import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import institutionMcpEn from './i18n-mcp/en.json';
import institutionMcpFr from './i18n-mcp/fr.json';
import { institutionOverlay } from './i18n-institution';
import { learnerDashboardLocales } from './i18n-learner-dashboard-locales';
import { useLanguageStore, resolveInitialLanguage, type Language } from '@/stores/language.store';
import { arTranslations } from './i18n-ar';
import { arHelpTranslations } from './i18n-ar-help';
import { arPlayerTranslations } from './i18n-ar-player';
import { arGapTranslations } from './i18n-ar-gap';
import { deGapTranslations } from './i18n-de-gap';
import { esGapTranslations } from './i18n-es-gap';
import { itGapTranslations } from './i18n-it-gap';
import { lbGapTranslations } from './i18n-lb-gap';
import { ptGapTranslations } from './i18n-pt-gap';
import { ruGapTranslations } from './i18n-ru-gap';
import { ukGapTranslations } from './i18n-uk-gap';
import { trTranslations } from './i18n-tr';
import { trGapTranslations } from './i18n-tr-gap';
import { applyDocumentLocale } from './locale-direction';
import { eduflowBlockEditorsFr } from './i18n-eduflow-block-editors-fr';
import { eduflowBlockEditorsEn } from './i18n-eduflow-block-editors-en';
import { playerVisualParallelLocales } from './i18n-player-visual-parallel-locales';

function withInstitution(
  base: Record<string, string>,
  code: keyof typeof institutionOverlay,
): Record<string, string> {
  return {
    ...base,
    ...(institutionOverlay[code] ?? {}),
    ...(learnerDashboardLocales[code] ?? {}),
  };
}

/** Remplit les clés manquantes des bundles partiels (de, it, es, …) avec l’anglais avant la langue cible. */
function withEnglishFallback(overrides: Record<string, string>): Record<string, string> {
  return { ...enTranslations, ...overrides };
}

const frTranslations = {
  // Navigation
  'nav.dashboard': 'Tableau de bord',
  'nav.projects': 'Projets',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Livres audio',
  'nav.audio_studio': 'Studio Audio',
  'nav.store': 'Boutique',
  'nav.analytics': 'Analytiques',
  'nav.users': 'Utilisateurs',
  'nav.settings': 'Paramètres',
  'nav.profile': 'Profil',
  'profile.institution': 'Établissement',
  'profile.institutionHint':
    'Votre compte LyrraStudio est rattaché à cet établissement. Ce rattachement est géré par votre administrateur.',
  'profile.institutionUnknownName': 'Établissement (nom indisponible)',
  'nav.notifications': 'Notifications',
  'nav.help': 'Aide',
  'nav.community': 'Communauté',
  'nav.library': 'Bibliothèque',
  'nav.certificates': 'Certificats',
  'nav.institution': 'Établissement',
  'nav.groups': 'Groupes',
  'nav.learners': 'Apprenants',
  'nav.myLearning': 'Mon apprentissage',
  'nav.reports': 'Rapports',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'Plan de formation',
  'nav.catalog': 'Catalogue',
  'nav.competencies': 'Compétences',
  'nav.budget': 'Budget',
  'nav.campaigns': 'Campagnes',
  'nav.codir': 'CODIR',
  'nav.home': 'Accueil',
  'nav.producerAssignments': 'Missions de production',
  'nav.videos': 'Vidéos',
  'nav.sounds': 'Sons',
  'nav.images': 'Images',
  'pages.images.title': 'Images',
  'pages.images.pasteHint': 'Ou collez une capture d’écran (Ctrl+V / Cmd+V)',
  'pages.images.emptyHint':
    'Glisser-déposer, cliquer pour importer, ou coller une capture (Ctrl+V / Cmd+V).',
  'pages.images.viewLarge': 'Voir en grand',
  'pages.images.lightboxTitle': 'Aperçu de l’image',
  'pages.images.lightboxClose': 'Fermer',
  'pages.images.lightboxBackdropClose': 'Fermer l’aperçu',
  'pages.images.lightboxPrev': 'Image précédente',
  'pages.images.lightboxNext': 'Image suivante',
  'nav.documents': 'Documents',
  'nav.podcasts': 'Podcasts',
  'nav.webinars': 'Webinaires',
  'nav.webinarsEnterprise': 'Webinaires (entreprise)',
  'webinar.room.capacityFull':
    'Cette salle a atteint sa capacité maximale ({{max}} personnes connectées en même temps). Réessayez plus tard.',
  'webinar.room.participants': 'Participants',
  'webinar.room.chat': 'Chat',
  'webinar.room.speechTab': 'Parole',
  'webinar.room.speechPanelHint':
    'Texte reconnu depuis le micro des animateurs (reconnaissance vocale du navigateur). Le nom affiché est celui de la personne qui parle.',
  'webinar.room.speechEmpty':
    'Quand un animateur parle avec le micro ouvert, le texte apparaît ici avec son nom. La ponctuation suit celle proposée par le navigateur.',
  'webinar.room.speechUnknownSpeaker': 'Animateur',
  'webinar.room.speechPresenterActive':
    'Votre voix est reconnue par le navigateur et envoyée aux participants (onglet Parole).',
  'webinar.room.speechUnsupportedBrowser':
    'La transcription automatique des animateurs n’est pas disponible sur ce navigateur (essayez Chrome ou Edge).',
  'webinar.room.sidePanelTabs':
    'Participants, parole des animateurs, sondage et chat',
  'webinar.room.you': 'vous',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.leave': 'Quitter la salle',
  'webinar.room.contentFocusExpand': 'Contenu sur tout l’écran',
  'webinar.room.contentFocusCollapse': 'Afficher décor et intervenants',
  'webinar.room.contentFocusAriaExpand':
    'Agrandir la formation, le document partagé ou le partage d’écran sur tout l’espace sans fond ni intervenants',
  'webinar.room.liveDocumentLabel': 'Fichier (PDF ou PowerPoint)',
  'webinar.room.liveDocumentUploadChoice': 'Choisir un fichier à afficher',
  'webinar.room.liveDocumentUploading': 'Envoi…',
  'webinar.room.liveDocumentUploadError':
    'Impossible d’envoyer le fichier. Vérifiez le format (PDF ou PowerPoint) et la taille (max. 40 Mo).',
  'webinar.room.liveDocumentClose': 'Retirer le document',
  'webinar.room.liveDocumentBehindScreen':
    'Le document reste disponible — arrêtez le partage d’écran pour le réafficher.',
  'webinar.room.waitingSharedContentExtended':
    'En attente du contenu partagé par l’animateur : formation catalogue, fichier (PDF ou PowerPoint) ou partage d’écran.',
  'webinar.room.liveDocumentOfficeHint':
    'Si l’aperçu ne charge pas (HTTP local ou fichier trop lourd), ouvrez le lien ci-dessus.',
  'webinar.room.liveDocumentOpenOrDownload': 'Ouvrir ou télécharger le fichier',
  'webinar.room.liveDocumentNoInlinePreview':
    'Ce fichier ne peut pas être affiché ici automatiquement. Ouvrez-le dans un nouvel onglet.',
  'webinar.room.liveDocumentOpenTab': 'Ouvrir',
  'webinar.room.pollTab': 'Sondage',
  'webinar.room.poll.regionTitle': 'Sondage du webinaire',
  'webinar.room.poll.presenterHint':
    'Créez une question et 2 à 8 réponses. Le sondage s’affiche ici pour toute la salle ; seuls les animateurs voient les résultats en direct.',
  'webinar.room.poll.questionLabel': 'Question',
  'webinar.room.poll.questionPlaceholder': 'Ex. : Quel thème préférez-vous pour la suite ?',
  'webinar.room.poll.optionsLabel': 'Réponses possibles',
  'webinar.room.poll.optionNumber': 'Réponse',
  'webinar.room.poll.choiceLabel': 'Choix',
  'webinar.room.poll.addOption': 'Ajouter une réponse',
  'webinar.room.poll.removeLastOption': 'Retirer la dernière',
  'webinar.room.poll.launch': 'Lancer le sondage',
  'webinar.room.poll.waitingParticipant': 'Aucun sondage en cours. L’animateur peut en lancer un.',
  'webinar.room.poll.voteHint':
    'Une réponse par personne sur le serveur : vous pouvez changer de choix en cliquant une autre option.',
  'webinar.room.poll.yourVote': 'votre choix',
  'webinar.room.poll.resultsHeading': 'Résultats (animateurs)',
  'webinar.room.poll.noVotesYet': 'Aucun vote pour l’instant.',
  'webinar.room.poll.totalVotes': 'Nombre de votes : {{n}}',
  'webinar.room.poll.closePoll': 'Fermer le sondage',
  'webinar.room.contentFocusAriaCollapse': 'Réafficher le décor de la scène et la colonne des intervenants',
  'webinar.room.exitDirectTitle': 'Quitter le webinaire ?',
  'webinar.room.exitDirectMessage':
    'Vous avez ouvert cette salle depuis un lien partagé (sans passer par le tableau de bord). Vous pouvez fermer cet onglet. Si le navigateur refuse la fermeture, vous serez renvoyé vers votre espace.',
  'webinar.room.exitDirectConfirm': 'Fermer l’onglet',
  'webinar.room.publicGateTitle': 'Inscription au webinaire',
  'webinar.room.publicGateHint':
    'L’organisateur a activé l’inscription publique : indiquez votre e-mail, choisissez un mot de passe (8 caractères min.) et complétez les champs demandés.',
  'webinar.room.publicGateEmail': 'E-mail',
  'webinar.room.publicGateLogin': 'Identifiant',
  'webinar.room.publicGatePassword': 'Mot de passe',
  'webinar.room.publicGatePasswordHintParticipant':
    'Première visite : inscription. Visites suivantes : même e-mail et mot de passe. Minimum 8 caractères.',
  'webinar.room.publicGateSubmit': 'Entrer dans la salle',
  'webinar.room.publicGateSubmitting': 'Vérification…',
  'webinar.room.publicGateDenied': 'E-mail ou mot de passe incorrect.',
  'webinar.room.socketGateDenied':
    'Impossible de rejoindre la salle en temps réel : session d’inscription publique manquante ou expirée. Rechargez la page et reconnectez-vous avec le même e-mail et mot de passe.',
  'nav.parcours': 'Parcours',
  'nav.eduflowList': 'Formations',
  'nav.section.pilotage': 'Pilotage',
  'nav.section.creation': 'Création',
  'nav.section.medias': 'Médias',
  'nav.section.aide': 'Aide',
  'sidebar.brandTagline': 'Formation',
  'sidebar.logoInstitutionAlt': "Logo de l'établissement",
  'sidebar.expand': 'Agrandir le menu',
  'sidebar.collapse': 'Réduire le menu',
  'sidebar.expandAria': 'Agrandir la barre latérale',
  'sidebar.collapseAria': 'Réduire la barre latérale',
  'dashboard.kindPodcast': 'Podcast',
  'pages.learners.title': 'Apprenants',
  'pages.learners.description': 'Suivez la progression de vos apprenants',
  'pages.learners.enrolledFormations': 'Formations inscrites',
  'pages.learners.notStarted': 'Non commencé',
  'pages.learners.startedOn': 'Début {{date}}',
  'pages.learners.detailedTraces': 'Traces détaillées · {{flowTitle}}',
  'pages.learners.statsEvents': 'Événements : {{count}}',
  'pages.learners.statsResponses': 'Réponses : {{count}}',
  'pages.learners.statsBlockSessions': 'Sessions blocs : {{count}}',
  'pages.learners.timePerBlock': 'Temps passé par bloc',
  'pages.learners.lastEvents': 'Derniers événements',
  'pages.learners.blockSessionInProgress': '(en cours)',
  'pages.learners.blockTimeOnBlock': '({{duration}} sur ce bloc)',
  'pages.learners.trackingEvent.unknown': 'Événement',
  'pages.learners.trackingEvent.training_enter': 'Ouverture de la formation',
  'pages.learners.trackingEvent.training_exit': 'Fermeture ou sortie de la formation',
  'pages.learners.trackingEvent.training_completed': 'Formation terminée',
  'pages.learners.trackingEvent.block_enter': 'Entrée dans un bloc',
  'pages.learners.trackingEvent.block_exit': 'Sortie d’un bloc',
  'pages.learners.trackingEvent.response_submitted': 'Réponse ou validation envoyée',
  'pages.learners.trackingEvent.progress_sync': 'Synchronisation de la progression',
  'pages.learners.refresh': 'Actualiser',
  'pages.learners.exportCsv': 'Exporter CSV',
  'pages.learners.statTotal': 'Total apprenants',
  'pages.learners.statInProgress': 'En cours',
  'pages.learners.statCompleted': 'Terminé',
  'pages.learners.statAvgProgress': 'Progression moy.',
  'pages.learners.searchPlaceholder': 'Rechercher par nom ou email…',
  'pages.learners.filterAllFlows': 'Toutes les formations',
  'pages.learners.sortRecent': 'Plus récents',
  'pages.learners.sortName': 'Nom A-Z',
  'pages.learners.sortProgress': 'Progression',
  'pages.learners.colLearner': 'Apprenant',
  'pages.learners.colFormations': 'Formations',
  'pages.learners.colProgress': 'Progression',
  'pages.learners.colCompleted': 'Complétées',
  'pages.learners.colTime': 'Temps',
  'pages.learners.colLastAccess': 'Dernier accès',
  'pages.learners.colActions': 'Actions',
  'pages.learners.openProgressDashboard': 'Tableau de bord d’avancement',
  'pages.learners.dashboardTitle': 'Avancement de l’apprenant',
  'pages.learners.dashboardBack': 'Retour à la liste',
  'pages.learners.dashboardInvalidId': 'Identifiant apprenant invalide',
  'pages.learners.dashboardNotFound': 'Apprenant introuvable ou accès refusé',
  'pages.learners.dashboardOverviewHint':
    'Vue consolidée : formations EduFlow auxquelles ce participant est inscrit, parcours et podcasts assignés au compte Lyrra qui partage le même e-mail.',
  'pages.learners.tabOverview': 'Synthèse',
  'pages.learners.tabFormations': 'Formations',
  'pages.learners.tabParcours': 'Parcours',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Formations',
  'pages.learners.sectionParcours': 'Parcours',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'Aucun parcours assigné à un compte Lyrra avec cet e-mail.',
  'pages.learners.emptyPodcasts': 'Aucun podcast assigné à un compte Lyrra avec cet e-mail.',
  'pages.learners.linkedAccountHint':
    'Les parcours et podcasts apparaissent lorsqu’un utilisateur Lyrra existe avec le même e-mail que ce participant.',
  'pages.learners.kpiEduflows': 'Formations',
  'pages.learners.kpiAvgProgress': 'Progression moy.',
  'pages.learners.kpiPaths': 'Parcours',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} terminée(s)',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} activités',
  'pages.learners.pathAssignedOn': 'Ajout le {{date}}',
  'pages.learners.podcastCompleted': 'Terminé',
  'pages.learners.podcastInProgress': 'En cours',
  'pages.learners.pathStatusPublished': 'Publié',
  'pages.learners.pathStatusDraft': 'Brouillon',
  'pages.learners.openInDesigner': 'Ouvrir dans le concepteur',
  'pages.learners.openParcoursEditor': 'Ouvrir le parcours',
  'pages.learners.viewPodcasts': 'Voir les podcasts',
  'pages.learners.loading': 'Chargement des apprenants…',
  'pages.learners.loadError': 'Impossible de charger les apprenants',
  'pages.learners.retry': 'Réessayer',
  'pages.learners.emptyFiltered': 'Aucun apprenant trouvé',
  'pages.learners.emptyFilteredHint': 'Essayez de modifier vos critères de recherche',
  'pages.learners.emptyNone': 'Aucun apprenant inscrit',
  'pages.learners.emptyNoneHint':
    "Les apprenants apparaîtront ici lorsqu'ils s'inscriront à vos formations publiées",
  'pages.learners.pagination':
    'Page {{page}} sur {{totalPages}} · {{total}} apprenant(s)',
  'pages.learners.prev': 'Précédent',
  'pages.learners.next': 'Suivant',
  'pages.learners.csvLastName': 'Nom',
  'pages.learners.csvFirstName': 'Prénom',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Formations',
  'pages.learners.csvAvgProgress': 'Progression moyenne',
  'pages.learners.csvTotalTime': 'Temps total',
  'pages.learners.csvLastActivity': 'Dernière activité',
  'eduflowList.status.draft': 'Brouillon',
  'eduflowList.status.published': 'Publié',
  'eduflowList.status.archived': 'Archivé',
  'eduflowList.badge.enterpriseShell': 'Besoin entreprise',
  'eduflowList.badge.enterpriseLinked': 'Entreprise',
  'eduflowList.badge.toProduce': 'À produire',
  'eduflow.list.blocksCount': '{{count}} blocs',
  'eduflow.list.noContent': 'Sans contenu',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Ouvrir Mes missions',
  'eduflow.list.tooltip.edit': 'Modifier',
  'eduflow.list.shellHint':
    'Fiche définie par votre organisation — créez l’EduFlow depuis « Mes missions ».',
  'breadcrumb.label': "Fil d'Ariane",
  'breadcrumb.home': 'Accueil',
  'breadcrumb.dashboard': 'Tableau de bord',
  'breadcrumb.projects': 'Projets',
  'breadcrumb.eduflow': 'Formations',
  'breadcrumb.learners': 'Apprenants',
  'breadcrumb.analytics': 'Analytiques',
  'breadcrumb.videos': 'Vidéos',
  'breadcrumb.sounds': 'Sons',
  'breadcrumb.images': 'Images',
  'breadcrumb.documents': 'Documents',
  'breadcrumb.store': 'Boutique',
  'breadcrumb.help': 'Aide',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.settings': 'Paramètres',
  'breadcrumb.profile': 'Profil',
  'breadcrumb.groups': 'Groupes',
  'breadcrumb.branding': 'Image de marque',
  'breadcrumb.landingPage': "Page d'accueil",
  'breadcrumb.webhooks': 'Webhooks',
  'breadcrumb.campaigns': 'Campagnes',
  'breadcrumb.competencies': 'Compétences',
  'breadcrumb.compliance': 'Conformité',
  'breadcrumb.reports': 'Rapports',
  'breadcrumb.budget': 'Budget',
  'breadcrumb.catalog': 'Catalogue',
  'breadcrumb.integrations': 'Intégrations',
  'breadcrumb.requests': 'Demandes',
  'breadcrumb.team': 'Équipe',
  'breadcrumb.reviews': 'Évaluations',
  'breadcrumb.training': 'Formations',
  'breadcrumb.skills': 'Compétences',
  'breadcrumb.certificates': 'Certificats',
  'breadcrumb.new': 'Nouveau',
  'breadcrumb.edit': 'Modifier',
  'breadcrumb.podcasts': 'Podcasts',
  'breadcrumb.episodes': 'Épisodes',
  'breadcrumb.parcours': 'Parcours',
  'breadcrumb.producerAssignments': 'Missions de production',
  'header.toggleMenu': 'Ouvrir ou fermer le menu',
  'header.search': 'Rechercher…',
  'header.toggleTheme': 'Changer le thème clair ou sombre',
  'header.profile': 'Profil',
  'header.settings': 'Paramètres',
  'header.backToInstitutionAdmin': 'Administration de l’établissement',
  'header.openTrainerWorkspace': 'Espace formateur et création',
  'header.logout': 'Déconnexion',
  'footer.madeWith': 'Fait avec',
  'footer.forEducation': "pour l'éducation",
  'footer.legal': 'Liens légaux',
  'footer.terms': 'CGU',
  'footer.privacy': 'Confidentialité',
  'footer.accessibility': 'Accessibilité',

  // Common buttons
  'btn.save': 'Enregistrer',
  'btn.cancel': 'Annuler',
  'btn.delete': 'Supprimer',
  'btn.edit': 'Modifier',
  'btn.create': 'Créer',
  'btn.add': 'Ajouter',
  'btn.close': 'Fermer',
  'btn.confirm': 'Confirmer',
  'btn.back': 'Retour',
  'btn.next': 'Suivant',
  'btn.previous': 'Précédent',
  'btn.submit': 'Soumettre',
  'btn.search': 'Rechercher',
  'btn.filter': 'Filtrer',
  'btn.export': 'Exporter',
  'btn.import': 'Importer',
  'btn.download': 'Télécharger',
  'btn.upload': 'Téléverser',
  'btn.refresh': 'Actualiser',
  'btn.retry': 'Réessayer',
  'btn.view': 'Voir',
  'btn.details': 'Détails',
  'btn.publish': 'Publier',
  'btn.unpublish': 'Dépublier',
  'btn.duplicate': 'Dupliquer',
  'btn.archive': 'Archiver',
  'btn.restore': 'Restaurer',
  'btn.send': 'Envoyer',
  'btn.apply': 'Appliquer',
  'btn.reset': 'Réinitialiser',
  'btn.select_all': 'Tout sélectionner',
  'btn.deselect_all': 'Tout désélectionner',
  'btn.load_more': 'Charger plus',
  'btn.show_more': 'Voir plus',
  'btn.show_less': 'Voir moins',
  'btn.copy': 'Copier',
  'btn.share': 'Partager',
  'btn.preview': 'Aperçu',
  'btn.continue': 'Continuer',
  'btn.skip': 'Passer',

  // Form labels
  'form.email': 'Adresse e-mail',
  'form.password': 'Mot de passe',
  'form.password_confirm': 'Confirmer le mot de passe',
  'form.first_name': 'Prénom',
  'form.last_name': 'Nom',
  'form.name': 'Nom',
  'form.phone': 'Téléphone',
  'form.address': 'Adresse',
  'form.city': 'Ville',
  'form.zip': 'Code postal',
  'form.country': 'Pays',
  'form.title': 'Titre',
  'form.description': 'Description',
  'form.category': 'Catégorie',
  'form.tags': 'Tags',
  'form.status': 'Statut',
  'form.role': 'Rôle',
  'form.language': 'Langue',
  'form.date': 'Date',
  'form.start_date': 'Date de début',
  'form.end_date': 'Date de fin',
  'form.duration': 'Durée',
  'form.price': 'Prix',
  'form.avatar': 'Photo de profil',
  'form.required': 'Obligatoire',
  'form.optional': 'Optionnel',
  'form.select_placeholder': 'Sélectionner...',
  'form.search_placeholder': 'Rechercher...',
  'form.no_results': 'Aucun résultat',
  'form.file_drop': 'Glissez-déposez un fichier ici ou cliquez pour parcourir',
  'form.max_size': 'Taille maximale : {{size}}',
  'form.institution': 'Établissement',
  'form.department': 'Département',
  'form.group': 'Groupe',
  'form.level': 'Niveau',
  'form.notes': 'Notes',
  'form.url': 'URL',
  'form.content': 'Contenu',
  'form.thumbnail': 'Vignette',

  // Validation errors
  'error.required': 'Ce champ est obligatoire',
  'error.email_invalid': 'Adresse e-mail invalide',
  'error.password_min': 'Le mot de passe doit contenir au moins {{min}} caractères',
  'error.password_match': 'Les mots de passe ne correspondent pas',
  'error.min_length': 'Minimum {{min}} caractères',
  'error.max_length': 'Maximum {{max}} caractères',
  'error.min_value': 'La valeur minimale est {{min}}',
  'error.max_value': 'La valeur maximale est {{max}}',
  'error.invalid_url': 'URL invalide',
  'error.file_too_large': 'Le fichier est trop volumineux',
  'error.file_type': 'Type de fichier non supporté',
  'error.generic': 'Une erreur est survenue',
  'error.network': 'Erreur de connexion au serveur',
  'error.unauthorized': 'Vous devez être connecté',
  'error.forbidden': 'Accès interdit',
  'error.not_found': 'Ressource introuvable',
  'error.server': 'Erreur serveur interne',
  'error.conflict': 'Cette ressource existe déjà',
  'error.rate_limit': 'Trop de requêtes, veuillez patienter',

  // Auth
  'auth.login': 'Se connecter',
  'auth.logout': 'Se déconnecter',
  'auth.register': 'Créer un compte',
  'auth.forgot_password': 'Mot de passe oublié ?',
  'auth.reset_password': 'Réinitialiser le mot de passe',
  'auth.otp_title': 'Connexion par code',
  'auth.otp_sent': 'Un code de vérification a été envoyé à {{email}}',
  'auth.otp_placeholder': 'Entrez le code à 6 chiffres',
  'auth.google_login': 'Continuer avec Google',
  'auth.remember_me': 'Se souvenir de moi',
  'auth.no_account': 'Pas encore de compte ?',
  'auth.has_account': 'Déjà un compte ?',
  'auth.welcome_back': 'Bon retour, {{name}} !',
  'auth.login_success': 'Connexion réussie',
  'auth.register_success': 'Compte créé avec succès',
  'auth.logout_success': 'Déconnexion réussie',

  // Roles
  'role.teacher': 'Formateur',
  'role.institution': 'Établissement',
  'role.institution_admin': 'Administrateur entreprise',
  'role.admin': 'Administrateur',
  'role.drh': 'DRH',
  'role.rh': 'RH',
  'role.resp_formation': 'Responsable formation',
  'role.manager': 'Manager',
  'role.collaborator': 'Collaborateur',
  'role.learner': 'Apprenant',

  // Dashboard
  'dashboard.title': 'Tableau de bord',
  'dashboard.welcome': 'Bienvenue, {{name}}',
  'dashboard.recent_projects': 'Projets récents',
  'dashboard.recent_activity': 'Activité récente',
  'dashboard.quick_actions': 'Actions rapides',
  'dashboard.stats': 'Statistiques',
  'dashboard.total_projects': 'Total projets',
  'dashboard.total_learners': 'Total apprenants',
  'dashboard.total_eduflows': 'Total formations',
  'dashboard.completion_rate': 'Taux de complétion',
  'dashboard.active_users': 'Utilisateurs actifs',
  'dashboard.new_registrations': 'Nouvelles inscriptions',
  'dashboard.revenue': 'Chiffre d\'affaires',
  'dashboard.subtitle': 'Vue d\'ensemble de votre activité',
  'dashboard.home.greeting': 'Bonjour {{name}}',
  'dashboard.home.greetingSubtitle':
    'Pilotez vos formations, parcours et podcasts depuis un seul espace.',
  'dashboard.home.primaryCta': 'Créer une formation',
  'dashboard.home.continueEditing': 'Reprendre la dernière modification',
  'dashboard.home.publishRate': 'Taux de publication',
  'dashboard.home.totalFormations': 'Formations créées',
  'dashboard.home.uniqueLearners': 'Apprenants touchés',
  'dashboard.home.calendarKicker': 'Activité ce mois-ci',
  'dashboard.home.recentWork': 'Dernières modifications',
  'dashboard.home.viewAll': 'Tout voir',
  'dashboard.home.quickAccess': 'Accès rapides',
  'dashboard.home.loading': 'Chargement du tableau de bord…',
  'dashboard.home.defaultName': 'formateur·rice',
  'dashboard.welcomeSub': 'Voici ce qui se passe sur vos projets.',
  'dashboard.projects': 'Projets',
  'dashboard.eduflows': 'Formations',
  'dashboard.publishedFlows': 'Parcours publiés',
  'dashboard.certificatesIssued': 'Certificats délivrés',
  'dashboard.activityEmpty': 'Aucune activité récente sur vos parcours ou podcasts.',
  'dashboard.loadError': 'Impossible de charger les données du tableau de bord.',
  'dashboard.learners': 'Apprenants',
  'dashboard.newProject': 'Nouveau projet',
  'dashboard.newEduFlow': 'Nouvelle formation',
  'dashboard.uploadMedia': 'Importer un média',
  'dashboard.viewDocs': 'Documentation',
  'dashboard.recentProjects': 'Projets récents',
  'dashboard.noRecentProjects': 'Aucun projet récent',
  'dashboard.activity': 'Activité',
  'dashboard.searchProjects': 'Rechercher des projets...',
  'dashboard.noProjects': 'Aucun projet pour le moment',
  'dashboard.createFirst': 'Créez votre premier projet pour commencer',
  'dashboard.createProject': 'Créer un projet',
  'dashboard.projectTitle': 'Titre du projet',
  'dashboard.recentActivity': 'Activité récente',
  'dashboard.roleSpecific': 'Votre espace est personnalisé selon votre rôle.',
  'dashboard.calendar.mon': 'Lun',
  'dashboard.calendar.wed': 'Mer',
  'dashboard.calendar.fri': 'Ven',
  'dashboard.calendar.less': 'Moins',
  'dashboard.calendar.more': 'Plus',
  'dashboard.calendar.tooltip': '{{count}} activite(s) le {{date}}',
  'dashboard.mock.updated2h': 'Il y a 2 h',
  'dashboard.mock.updated5h': 'Il y a 5 h',
  'dashboard.mock.updatedYesterday': 'Hier',
  'dashboard.home': 'Accueil',
  'dashboard.analytics': 'Analytique',
  'dashboard.settings': 'Paramètres',
  'dashboard.analyticsComingSoon': 'Analytique bientôt disponible',
  'dashboard.settingsComingSoon': 'Paramètres bientôt disponibles',
  'pages.analytics.title': 'Analytiques',
  'pages.analytics.description': 'Tableaux de bord et statistiques',
  'pages.analytics.subtitle':
    'Filtrez les données, réorganisez les graphiques par glisser-déposer et adaptez la disposition à votre besoin.',
  'pages.analytics.filters.period': 'Période',
  'pages.analytics.filters.flow': 'Formation',
  'pages.analytics.filters.allFlows': 'Toutes les formations',
  'pages.analytics.period.7d': '7 derniers jours',
  'pages.analytics.period.30d': '30 derniers jours',
  'pages.analytics.period.90d': '90 derniers jours',
  'pages.analytics.period.365d': '12 derniers mois',
  'pages.analytics.addChart': 'Ajouter un graphique',
  'pages.analytics.resetLayout': 'Réinitialiser la disposition',
  'pages.analytics.dragHint': 'Glisser pour réorganiser',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Retirer ce graphique',
  'pages.analytics.kind.completion_line': 'Complétion dans le temps',
  'pages.analytics.kind.learners_bar': 'Apprenants par module',
  'pages.analytics.kind.status_pie': 'Répartition des statuts',
  'pages.analytics.kind.engagement_area': 'Temps d’engagement',
  'pages.analytics.series.completion': 'Taux de complétion (%)',
  'pages.analytics.series.learners': 'Apprenants',
  'pages.analytics.series.hours': 'Heures',
  'pages.analytics.status.completed': 'Complété',
  'pages.analytics.status.in_progress': 'En cours',
  'pages.analytics.status.dropped': 'Abandonné',
  'pages.analytics.status.not_started': 'Non commencé',
  'pages.analytics.loadError': 'Impossible de charger les données analytiques.',
  'pages.analytics.retry': 'Réessayer',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Lun',
  'pages.analytics.mock.wd1': 'Mar',
  'pages.analytics.mock.wd2': 'Mer',
  'pages.analytics.mock.wd3': 'Jeu',
  'pages.analytics.mock.wd4': 'Ven',
  'pages.analytics.mock.wd5': 'Sam',
  'pages.analytics.mock.wd6': 'Dim',
  'pages.analytics.emptyCharts': 'Ajoutez au moins un graphique pour visualiser les données.',
  'pages.analytics.comingSoonDesc':
    'Visualisez les statistiques de vos formations, le taux de complétion, l\'engagement des apprenants et les performances globales.',
  'pages.help.title': 'Centre d\'aide',
  'pages.help.description': 'Documentation et support',
  'pages.help.hubIntro':
    'Thématiques couvrant l’usage de Lyrra : vous pourrez bientôt lier chaque fiche à votre documentation, tutoriels ou tickets support.',
  'pages.help.searchPlaceholder': 'Rechercher une thématique…',
  'pages.help.noResults': 'Aucun résultat pour cette recherche.',
  'pages.help.configHint':
    'Contenu de démonstration : branchez vos URLs, articles ou base de connaissances quand vous serez prêt.',
  'pages.help.comingSoonDesc':
    'Accédez à la documentation, aux tutoriels et au support technique pour utiliser la plateforme.',
  'pages.help.modal.steps': 'En pratique',
  'pages.help.modal.tips': 'Conseils',
  'pages.help.modal.related': 'Voir aussi',
  'pages.help.openArticle': 'Ouvrir la fiche',
  'pages.help.onboardingFlowMissing': 'Formation introuvable',
  'pages.help.onboardingFlowMissingDesc':
    "Lancez les seeds backend (db:seed:onboarding-eduflow, db:seed:onboarding-navigation-eduflow, db:seed:onboarding-profile-eduflow, db:seed:onboarding-roles-eduflow, db:seed:onboarding-block-types-eduflow, db:seed:onboarding-designer-overview-eduflow, db:seed:onboarding-branches-eduflow, db:seed:onboarding-validation-eduflow, db:seed:onboarding-objectives-eduflow, db:seed:onboarding-ai-designer-eduflow, db:seed:onboarding-publication-status-eduflow, db:seed:onboarding-publication-links-eduflow, db:seed:onboarding-catalog-visibility-eduflow, db:seed:onboarding-enrollments-eduflow, db:seed:onboarding-groups-cohorts-eduflow, db:seed:onboarding-progress-eduflow, db:seed:onboarding-certificates-eduflow et db:seed:onboarding-media-library-eduflow) ou renseignez VITE_ONBOARDING_DASHBOARD_FLOW_ID / VITE_ONBOARDING_NAVIGATION_FLOW_ID / VITE_ONBOARDING_PROFILE_FLOW_ID / VITE_ONBOARDING_ROLES_FLOW_ID / VITE_ONBOARDING_BLOCK_TYPES_FLOW_ID / VITE_ONBOARDING_DESIGNER_OVERVIEW_FLOW_ID / VITE_ONBOARDING_BRANCHES_FLOW_ID / VITE_ONBOARDING_VALIDATION_FLOW_ID / VITE_ONBOARDING_OBJECTIVES_FLOW_ID / VITE_ONBOARDING_AI_DESIGNER_FLOW_ID / VITE_ONBOARDING_PUBLICATION_STATUS_FLOW_ID / VITE_ONBOARDING_PUBLICATION_LINKS_FLOW_ID / VITE_ONBOARDING_CATALOG_VISIBILITY_FLOW_ID / VITE_ONBOARDING_ENROLLMENTS_FLOW_ID / VITE_ONBOARDING_GROUPS_COHORTS_FLOW_ID / VITE_ONBOARDING_PROGRESS_FLOW_ID / VITE_ONBOARDING_CERTIFICATES_FLOW_ID / VITE_ONBOARDING_MEDIA_LIBRARY_FLOW_ID.",
  'pages.help.onboardingDashboardPlayerTitle': 'Formation — Tableau de bord Lyrra (onboarding)',
  'pages.help.onboardingNavigationPlayerTitle':
    'Formation — Navigation & menu latéral Lyrra (onboarding)',
  'pages.help.onboardingProfilePlayerTitle': 'Formation — Profil & préférences Lyrra (onboarding)',
  'pages.help.onboardingRolesPlayerTitle':
    "Formation — Rôles et droits d'accès Lyrra (onboarding)",
  'pages.help.onboardingBlockTypesPlayerTitle':
    'Formation — Types de blocs EduFlow (onboarding)',
  'pages.help.onboardingDesignerOverviewPlayerTitle':
    'Formation — Vue d’ensemble du concepteur EduFlow (onboarding)',
  'pages.help.onboardingBranchesPlayerTitle':
    'Formation — Branches & conditions EduFlow (onboarding)',
  'pages.help.onboardingValidationPlayerTitle':
    'Formation — Validation du parcours EduFlow (onboarding)',
  'pages.help.onboardingObjectivesPlayerTitle':
    'Formation — Objectifs pédagogiques EduFlow (onboarding)',
  'pages.help.onboardingAiDesignerPlayerTitle':
    'Formation — Assistant IA du designer LyrraStudio (onboarding)',
  'pages.help.onboardingPublicationStatusPlayerTitle':
    'Formation — Brouillon, publication et mises à jour LyrraStudio (onboarding)',
  'pages.help.onboardingPublicationLinksPlayerTitle':
    'Formation — Liens d’accès & partage LyrraStudio (onboarding)',
  'pages.help.onboardingCatalogVisibilityPlayerTitle':
    'Formation — Catalogue & visibilité LyrraStudio (onboarding)',
  'pages.help.onboardingEnrollmentsPlayerTitle':
    'Formation — Inscriptions & participants LyrraStudio (onboarding)',
  'pages.help.onboardingGroupsCohortsPlayerTitle':
    'Formation — Groupes & cohortes LyrraStudio (onboarding)',
  'pages.help.onboardingProgressPlayerTitle':
    'Formation — Progression & temps passé LyrraStudio (onboarding)',
  'pages.help.onboardingCertificatesPlayerTitle':
    'Formation — Certificats délivrés LyrraStudio (onboarding)',
  'pages.help.onboardingMediaLibraryPlayerTitle':
    'Formation — Bibliothèque média LyrraStudio (onboarding)',
  'dashboard.period.week': 'Semaine',
  'dashboard.period.month': 'Mois',
  'dashboard.period.quarter': 'Trimestre',
  'dashboard.vsLastPeriod': 'par rapport à la période précédente ({{period}})',
  'dashboard.completionPercent': 'Complétion %',
  'dashboard.activeLearners': 'Apprenants actifs',
  'dashboard.sessions': 'Sessions',
  'dashboard.activityType.completion': 'Terminé',
  'dashboard.activityType.enrollment': 'Inscription',
  'dashboard.activityType.milestone': 'Étape',
  'dashboard.activityType.feedback': 'Feedback',
  'dashboard.activityType.start': 'Démarrage',
  'dashboard.totalLearners': 'Total apprenants',
  'dashboard.activeEduFlows': 'Formations actives',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.completionRate': 'Taux de complétion',
  'dashboard.npsScore': 'Score NPS',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.topEduFlowsHint':
    'Taux de complétion des formations les plus suivies (tri par nombre d’inscrits).',
  'dashboard.engagement': 'Engagement des apprenants',
  'dashboard.xp_earned': 'XP gagnés',
  'dashboard.streak': 'Série en cours',
  'dashboard.level': 'Niveau {{level}}',
  'dashboard.badges_earned': 'Badges obtenus',
  'dashboard.next_level': 'Prochain niveau',
  'dashboard.no_projects': 'Aucun projet pour le moment',
  'dashboard.create_first': 'Créez votre premier projet',
  'dashboard.pending_reviews': 'Évaluations en attente',
  'dashboard.upcoming_deadlines': 'Échéances à venir',

  // Podcast — liste & création d’épisodes
  'podcast.episodes.pageTitle': 'Épisodes',
  'podcast.episodes.count': '{{count}} épisode(s)',
  'podcast.episodes.newEpisode': 'Nouvel épisode',
  'podcast.episodes.searchPlaceholder': 'Rechercher par titre…',
  'podcast.episodes.filterAllStatuses': 'Tous les statuts',
  'podcast.episodes.filterAllPodcasts': 'Tous les podcasts',
  'podcast.episodes.filterDraft': 'Brouillon',
  'podcast.episodes.filterPublished': 'Publié',
  'podcast.episodes.unassignedPodcast': 'Sans podcast',
  'podcast.episodes.generateVoiceOff': 'Générer la voix off',
  'podcast.episodes.generateVoiceConfirmTitle': 'Générer la voix off ?',
  'podcast.episodes.generateVoiceConfirmMessage':
    'L’épisode « {{title}} » sera synthétisé ({{count}} segment(s)). Cela peut prendre plusieurs minutes.',
  'podcast.episodes.generateVoiceConfirmButton': 'Générer',
  'podcast.episodes.generateNoBlocks':
    'Aucun texte ni enregistrement micro sur cet épisode — ouvrez l’éditeur pour ajouter du contenu.',
  'podcast.episodes.generateSuccess': 'Voix off générée pour « {{title}} » ({{count}} segment(s)).',
  'podcast.episodes.generateError': 'Échec de la génération de la voix off.',
  'podcast.episodes.publish': 'Publier l’épisode',
  'podcast.episodes.publishConfirmTitle': 'Publier cet épisode ?',
  'podcast.episodes.publishConfirmMessage':
    '« {{title}} » sera visible pour les apprenants assignés au podcast (statut publié).',
  'podcast.episodes.publishConfirmButton': 'Publier',
  'podcast.episodes.publishSuccess': '« {{title}} » est publié.',
  'podcast.episodes.publishError': 'Échec de la publication.',
  'podcast.episodes.emptyTitle': 'Aucun épisode trouvé',
  'podcast.episodes.emptyHint': 'Créez votre premier épisode pour commencer',
  'podcast.episodes.emptyCreateCta': 'Créer un épisode',
  'podcast.episodes.backPodcasts': 'Podcasts',
  'podcast.episodes.paginationTotal': '{{total}} épisode(s) au total',
  'podcast.episodes.pageOf': 'Page {{page}} / {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Supprimer l’épisode ?',
  'podcast.episodes.deleteHint': '« {{title}} » sera supprimé définitivement.',
  'podcast.episodes.deleteCancel': 'Annuler',
  'podcast.episodes.deleteConfirm': 'Supprimer',
  'podcast.episodes.statusPublished': 'Publié',
  'podcast.episodes.statusDraft': 'Brouillon',
  'podcast.episodes.noDescription': 'Aucune description',
  'podcast.episodes.noAudio': 'Pas d’audio',
  'podcast.episodes.audioReady': 'Audio prêt',
  'podcast.episodes.retry': 'Réessayer',
  'podcast.createEpisode.panelTitle': 'Nouvel épisode',
  'podcast.createEpisode.panelSubtitle':
    'Indiquez le titre et, si vous le souhaitez, une description et une image. Après validation, l’épisode est créé et l’éditeur s’ouvre pour concevoir le contenu.',
  'podcast.createEpisode.titleLabel': 'Titre',
  'podcast.createEpisode.descriptionLabel': 'Description',
  'podcast.createEpisode.coverLabel': 'Image de couverture (facultatif)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP ou GIF — max 5 Mo.',
  'podcast.createEpisode.coverChoose': 'Choisir une image',
  'podcast.createEpisode.coverRemove': 'Retirer l’image',
  'podcast.createEpisode.titlePlaceholder': 'Ex. : Introduction au module sécurité',
  'podcast.createEpisode.descriptionPlaceholder': 'Résumé, objectifs… (facultatif)',
  'podcast.createEpisode.validate': 'Créer et ouvrir l’éditeur',
  'podcast.createEpisode.titleRequired': 'Le titre est obligatoire.',
  'podcast.createEpisode.error': 'Impossible de créer l’épisode.',
  'podcast.createEpisode.uploadCoverError': 'L’épisode a été créé, mais l’envoi de l’image a échoué.',
  'podcast.createEpisode.coverTooLarge': 'L’image ne doit pas dépasser 5 Mo.',
  'podcast.editor.defaultNewTitle': 'Nouvel épisode',

  // EduFlow
  'eduflow.title': 'Éditeur EduFlow',
  'eduflow.new_flow': 'Nouvelle formation',
  'eduflow.list.title': 'Formations',
  'eduflow.list.subtitle': 'Concevez vos parcours pédagogiques interactifs',
  'eduflow.list.searchPlaceholder': 'Rechercher une formation…',
  'eduflow.list.import': 'Importer',
  'eduflow.list.importHint': 'Fichier .lyrra (médias inclus) ou export JSON historique Lyrra V1.',
  'eduflow.list.importSuccess': 'Formation importée. Le designer s’ouvre.',
  'eduflow.list.importError':
    'Import impossible — vérifiez le fichier (.lyrra ou export JSON valide, au moins un bloc).',
  'eduflow.list.filterAll': 'Tous',
  'eduflow.list.filterDraft': 'Brouillons',
  'eduflow.list.filterPublished': 'Publiés',
  'eduflow.list.filterArchived': 'Archivés',
  'eduflow.list.archiveFormation': 'Archiver la formation',
  'eduflow.list.restoreFromArchive': 'Restaurer depuis les archives (brouillon)',
  'eduflow.list.archiveSuccess': 'Formation archivée',
  'eduflow.list.archiveError': 'Impossible d’archiver la formation.',
  'eduflow.list.restoreSuccess': 'Formation restaurée en brouillon',
  'eduflow.list.restoreError': 'Impossible de restaurer la formation.',
  'eduflow.list.viewModeGrid': 'Vue mosaïque',
  'eduflow.list.viewModeList': 'Vue liste',
  'eduflow.list.viewByCategory': 'Vue catégories',
  'eduflow.list.categoryBack': 'Toutes les catégories',
  'eduflow.list.categoryBrowseHint': 'Choisissez une catégorie pour afficher ses formations.',
  'eduflow.list.categoryFormationCount': '{{count}} formation(s)',
  'eduflow.list.categoryEmptyFiltered': 'Aucune formation dans cette vue (filtre ou recherche actifs).',
  'eduflow.list.uncategorizedCardHint': 'Formations sans étiquette de catégorie.',
  'eduflow.list.uncategorized': 'Sans catégorie',
  'eduflow.list.manageCategories': 'Gérer les catégories',
  'eduflow.list.categoriesPanelTitle': 'Catégories de la formation',
  'eduflow.list.noCategoryAvailable': 'Aucune catégorie disponible.',
  'eduflow.list.categoriesSaveError': 'Impossible de sauvegarder les catégories.',
  'eduflow.list.emptyFilteredTitle': 'Aucun résultat trouvé',
  'eduflow.list.emptyFilteredHint': 'Essayez de modifier vos critères de recherche ou de filtre.',
  'eduflow.list.emptyFirstTitle': 'Créez votre première formation',
  'eduflow.list.emptyFirstHint':
    "Concevez des expériences d'apprentissage interactives avec le designer visuel.",
  'eduflow.list.emptyCreateCta': 'Créer ma première formation',
  'eduflow.list.deleteToTrashTitle': 'Mettre la formation en corbeille ?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » sera retirée de votre liste (suppression logique). Votre établissement pourra la restaurer depuis la corbeille institution.',
  'eduflow.list.deleteToTrashConfirm': 'Mettre en corbeille',
  'eduflow.createFormation.panelTitle': 'Nouvelle formation',
  'eduflow.createFormation.panelSubtitle':
    'Renseignez le titre et la description. Après validation, la formation est créée puis le designer s’ouvre pour composer le flux.',
  'eduflow.createFormation.titleLabel': 'Titre',
  'eduflow.createFormation.descriptionLabel': 'Description',
  'eduflow.createFormation.titlePlaceholder': 'Ex. : Introduction au module sécurité',
  'eduflow.createFormation.descriptionPlaceholder': 'Objectifs, public visé, etc. (facultatif)',
  'eduflow.createFormation.validate': 'Créer et ouvrir le designer',
  'eduflow.createFormation.titleRequired': 'Le titre est obligatoire.',
  'eduflow.createFormation.error': 'Impossible de créer la formation.',
  'eduflow.shareFormation.title': 'Partager la formation',
  'eduflow.shareFormation.publicLinkTitle': 'Lien public (sans connexion)',
  'eduflow.shareFormation.publicLinkCopy': 'Copier le lien',
  'eduflow.shareFormation.publicLinkCopied': 'Copié',
  'eduflow.shareFormation.publishRequired':
    'Publiez la formation pour obtenir un lien accessible sans connexion.',
  'eduflow.shareFormation.embedTitle': 'Bouton intégré et lecteur en modale',
  'eduflow.shareFormation.embedHint':
    'Collez ce bloc sur votre site. Le script ne doit figurer qu’une fois par page ; pour d’autres formations, ajoutez uniquement des boutons en dupliquant la première ligne et en changeant l’URL (data-lyrra-eduflow-url). L’URL du lecteur inclut sidebar=0 pour afficher la formation avec le sommaire replié dans la modale.',
  'eduflow.shareFormation.embedCopy': 'Copier le code HTML',
  'eduflow.shareFormation.embedCopied': 'Copié',
  'eduflow.shareFormation.embedCloseAria': 'Fermer',
  'eduflow.shareFormation.embedButtonLabel': 'Ouvrir : {{title}}',
  'eduflow.shareFormation.embedToggle': 'Intégration site web',
  'eduflow.shareFormation.removeAccessSuccess': "Accès retiré pour l'apprenant.",
  'eduflow.shareFormation.removeAccessError': "Impossible de retirer l'apprenant.",
  'eduflow.shareFormation.assignSuccess': "Formation partagée et email d'invitation envoyé.",
  'eduflow.shareFormation.assignError': "Impossible d'assigner cet apprenant.",
  'eduflow.shareFormation.createAssignSuccess': "Compte créé/lié, formation assignée et email d'invitation envoyé.",
  'eduflow.shareFormation.createLearnerError': "Impossible de créer l'apprenant.",
  'eduflow.shareFormation.selectExistingLearner': 'Sélectionner un apprenant existant',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'eduflow.shareFormation.loadingLearners': 'Chargement des apprenants...',
  'eduflow.shareFormation.noLearnerAvailable': 'Aucun apprenant disponible.',
  'eduflow.shareFormation.hideQuickCreate': 'Masquer la création rapide',
  'eduflow.shareFormation.createNewLearner': 'Créer un nouvel apprenant',
  'eduflow.shareFormation.firstNameRequired': 'Prénom *',
  'eduflow.shareFormation.lastNameRequired': 'Nom *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': "Créer, partager et envoyer l'invitation",
  'eduflow.shareFormation.assignedLearnersCount_one': '{{count}} apprenant assigné',
  'eduflow.shareFormation.assignedLearnersCount_other': '{{count}} apprenants assignés',
  'eduflow.shareFormation.noAssignedLearner': 'Aucun apprenant assigné pour cette formation.',
  'eduflow.shareFormation.removeAccessTitle': "Retirer l'accès",
  'eduflow.shareFormation.inviteEmailsTitle': 'Inviter par e-mail (compte existant)',
  'eduflow.shareFormation.inviteEmailsHint':
    'Une ou plusieurs adresses : comptes LyrraStudio uniquement (y compris d’une autre institution). Les personnes sans compte ne sont pas ajoutées — utilisez « Créer un nouvel apprenant » pour les inviter.',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'Une adresse par ligne ou séparées par des virgules\nexemple@lyrra.fr, autre@entreprise.com',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Ajouter ces comptes à la formation',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Saisissez au moins une adresse e-mail valide.',
  'eduflow.shareFormation.inviteEmailsError': 'Impossible d’ajouter les adresses.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} compte(s) ajouté(s).',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} sans compte LyrraStudio.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} déjà assigné(s).',
  'eduflow.participant.flowTitle': 'Parcours de formation',
  'eduflow.participant.stepWelcome': 'Bienvenue',
  'eduflow.participant.stepModule1': 'Module 1',
  'eduflow.participant.stepEvaluation': 'Évaluation',
  'eduflow.participant.stepContentPlaceholder': "Contenu interactif de l'étape affiché pour le participant.",
  'eduflow.edit_flow': 'Modifier le parcours',
  'eduflow.flow_name': 'Nom du parcours',
  'eduflow.block_palette': 'Palette de blocs',
  'eduflow.canvas': 'Canvas',
  'eduflow.properties': 'Propriétés',
  'eduflow.publish': 'Publier',
  'eduflow.publish_confirm': 'Êtes-vous sûr de vouloir publier ce parcours ?',
  'eduflow.save_draft': 'Sauvegarder le brouillon',
  'eduflow.preview': 'Aperçu',
  'eduflow.versions': 'Versions',
  'eduflow.versionHistory': 'Historique des versions',
  'eduflow.versionHistory.count': '{{count}} version(s)',
  'eduflow.versionHistory.create': 'Créer une version',
  'eduflow.versionHistory.createVersion': 'Créer v{{version}}',
  'eduflow.versionHistory.minor': 'Mineure',
  'eduflow.versionHistory.major': 'Majeure',
  'eduflow.versionHistory.minorBadge': 'Version mineure',
  'eduflow.versionHistory.majorBadge': 'Version majeure',
  'eduflow.versionHistory.latest': 'Dernière',
  'eduflow.versionHistory.public': 'Version publique',
  'eduflow.versionHistory.publicActive': 'En production',
  'eduflow.versionHistory.publish': 'Publier cette version',
  'eduflow.versionHistory.publishCta': 'Publier',
  'eduflow.versionHistory.openPublicPlayer': 'Ouvrir le player public',
  'eduflow.versionHistory.copyPublicPlayer': 'Copier le lien public',
  'eduflow.versionHistory.copyPublicPlayerError': 'Impossible de copier le lien public',
  'eduflow.versionHistory.systemPublished': 'Publication de {{version}}',
  'eduflow.versionHistory.systemSaved': 'Sauvegarde {{version}}',
  'eduflow.versionHistory.systemAutosaveRestore': 'Sauvegarde automatique avant restauration de {{version}}',
  'eduflow.versionHistory.descriptionPlaceholder': 'Description des changements…',
  'eduflow.versionHistory.empty': 'Aucune version créée.',
  'eduflow.versionHistory.emptyHint': 'Sauvegardez une version pour y accéder ici.',
  'eduflow.versionHistory.blocks': '{{count}} blocs',
  'eduflow.versionHistory.restoreTitle': 'Restaurer la version v{{version}} ?',
  'eduflow.versionHistory.restoreWarning': 'Le contenu actuel sera remplacé. Une sauvegarde automatique sera créée avant la restauration.',
  'eduflow.versionHistory.createSuccess': 'Version {{version}} créée',
  'eduflow.versionHistory.createError': 'Erreur lors de la création de la version',
  'eduflow.versionHistory.restoreSuccess': 'Version {{version}} restaurée',
  'eduflow.versionHistory.restoreError': 'Erreur lors de la restauration',
  'eduflow.versionHistory.publishSuccess': 'Version {{version}} publiée',
  'eduflow.versionHistory.publishError': 'Erreur lors de la publication',
  'eduflow.versionHistory.now': 'à l\'instant',
  'eduflow.versionHistory.minutesAgo': 'il y a {{count}} min',
  'eduflow.versionHistory.hoursAgo': 'il y a {{count}} h',
  'eduflow.versionHistory.yesterday': 'hier',
  'eduflow.versionHistory.daysAgo': 'il y a {{count}} j',
  'eduflow.add_block': 'Ajouter un bloc',
  'eduflow.remove_block': 'Supprimer le bloc',
  'eduflow.connect_blocks': 'Connecter les blocs',
  'eduflow.undo': 'Annuler',
  'eduflow.redo': 'Rétablir',
  'eduflow.zoom_in': 'Zoomer',
  'eduflow.zoom_out': 'Dézoomer',
  'eduflow.fit_view': 'Ajuster la vue',
  'eduflow.auto_layout': 'Disposition automatique',
  'eduflow.unsaved_changes': 'Modifications non sauvegardées',
  'eduflow.blocks.quiz_mcq': 'QCM',
  'eduflow.blocks.quiz_open': 'Question ouverte',
  'eduflow.blocks.video': 'Vidéo',
  'eduflow.blocks.audio': 'Audio',
  'eduflow.blocks.text': 'Texte',
  'eduflow.blocks.evaluation': 'Évaluation',
  'eduflow.blocks.form': 'Formulaire',
  'eduflow.blocks.mindmap': 'Carte mentale',
  'eduflow.blocks.poll': 'Sondage',
  'eduflow.blocks.flashcard': 'Flashcard',
  'eduflow.blocks.timeline': 'Frise chronologique',
  'eduflow.blocks.pdf': 'Document PDF',
  'eduflow.blocks.image': 'Image',
  'eduflow.blocks.code': 'Code',
  'eduflow.blocks.whiteboard': 'Tableau blanc',
  'eduflow.blocks.ai_chat': 'Chat IA',
  'eduflow.blocks.certificate': 'Certificat',
  'eduflow.blocks.attendance': 'Émargement',
  'eduflow.blocks.sortable': 'Classement',
  'eduflow.blocks.pair_matching': 'Appariement',
  'eduflow.blocks.drawing': 'Dessin',
  'eduflow.blocks.embed': 'Contenu embarqué',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Condition',
  'eduflow.blocks.timer': 'Minuteur',
  'eduflow.blocks.randomizer': 'Aléatoire',
  'eduflow.blocks.grouping': 'Regroupement',
  'eduflow.blocks.debate': 'Débat',
  'eduflow.blocks.roleplay': 'Jeu de rôle',
  'eduflow.blocks.simulation': 'Simulation',
  'eduflow.blocks.scenario': 'Scénario',
  'eduflow.blocks.checklist': 'Checklist',
  'eduflow.blocks.kanban': 'Kanban',
  'eduflow.blocks.peer_review': 'Évaluation par les pairs',

  // EduFlow designer (palette, canvas, barre d’outils)
  'eduflow.designer.paletteCategory.structure': 'Parcours & structure',
  'eduflow.designer.paletteCategory.content': 'Contenu & médias',
  'eduflow.designer.paletteCategory.interactive': 'Activités & évaluation',
  'eduflow.designer.paletteCategory.accessibility': 'Accessibilité (DYS)',
  'eduflow.designer.paletteCategory.enterprise': 'Conformité & avancé',
  'eduflow.designer.flowSettings.toolbarTitle': 'Paramètres formation & lecteur',
  'eduflow.designer.flowSettings.panelTitle': 'Paramètres de la formation',
  'eduflow.designer.flowSettings.intro':
    'Ces informations sont affichées côté lecteur (titre, description, visuel). Le bouton « Précédent » peut être masqué pour un parcours strictement linéaire.',
  'eduflow.designer.flowSettings.titleLabel': 'Titre de la formation',
  'eduflow.designer.flowSettings.descriptionLabel': 'Description de la formation',
  'eduflow.designer.flowSettings.coverLabel': 'Image de couverture',
  'eduflow.designer.flowSettings.coverUpload': 'Importer une image',
  'eduflow.designer.flowSettings.coverUrlPlaceholder': 'Ou coller une URL d’image (https://…)',
  'eduflow.designer.flowSettings.coverRemove': 'Retirer la couverture',
  'eduflow.designer.flowSettings.allowPreviousLabel': 'Bouton « Précédent » dans le lecteur',
  'eduflow.designer.flowSettings.allowPreviousHint':
    'Désactivé : l’apprenant ne peut pas revenir au bloc précédent (ni par raccourci clavier).',
  'eduflow.designer.flowSettings.focusGuard.title': 'Vérification de perte de focus',
  'eduflow.designer.flowSettings.focusGuard.hint':
    'Si l’apprenant quitte l’onglet ou la page, la formation se met en pause (écran grisé, reprise au clic).',
  'eduflow.designer.flowSettings.focusGuard.limitLabel': 'Sorties autorisées',
  'eduflow.designer.flowSettings.focusGuard.unlimited': 'Illimité',
  'eduflow.designer.flowSettings.focusGuard.limited': 'Limité',
  'eduflow.designer.flowSettings.focusGuard.lossesUnit': 'perte(s) de focus autorisée(s)',
  'eduflow.designer.flowSettings.focusGuard.blockWarning':
    'Au-delà de ce nombre, l’apprenant ne peut plus continuer la formation.',
  'eduflow.designer.flowSettings.coverUploadError': "Impossible d'envoyer la couverture.",
  'eduflow.designer.flowSettings.certificate.uploadError': 'Réponse serveur invalide.',
  'eduflow.designer.flowSettings.certificate.uploadFailed': "Impossible d'envoyer le modèle Word.",
  'eduflow.designer.blockLabel.start': 'Départ',
  'eduflow.designer.blockLabel.end': 'Fin',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.summary.editorHint':
    'Liste les notions ou étapes vues dans la formation. Chaque ligne apparaît comme une carte numérotée dans le lecteur.',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.designer.blockLabel.form': 'Formulaire',
  'eduflow.designer.blockLabel.loop': 'Boucle',
  'eduflow.designer.blockLabel.merge': 'Convergence',
  'eduflow.designer.blockLabel.split': 'Embranchement',
  'eduflow.designer.blockLabel.subflow': 'Sous-formation',
  'eduflow.designer.blockLabel.timer': 'Minuteur',
  'eduflow.designer.blockLabel.email': 'Email automatique',
  'eduflow.designer.blockLabel.text': 'Texte',
  'eduflow.designer.blockLabel.podcast': 'PodCast',
  'eduflow.designer.blockLabel.video': 'Vidéo',
  'eduflow.designer.blockLabel.audio': 'Audio',
  'eduflow.designer.blockLabel.image': 'Image annotée',
  'eduflow.designer.blockLabel.pdf': 'Document PDF',
  'eduflow.designer.blockLabel.browser': 'Page web intégrée',
  'eduflow.designer.blockLabel.presentation': 'Diaporama',
  'eduflow.designer.blockLabel.timeline': 'Frise chronologique',
  'eduflow.designer.blockLabel.chart': 'Graphique interactif',
  'eduflow.designer.blockLabel.mindmap': 'Carte mentale',
  'eduflow.designer.blockLabel.visual_ai': 'Visuel IA',
  'eduflow.designer.blockLabel.quiz_mcq': 'Quiz (QCM / V-F / Ordre)',
  'eduflow.designer.blockLabel.poll': 'Sondage en direct',
  'eduflow.designer.blockLabel.self_assessment': 'Auto-évaluation',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.panelHint':
    'Définissez chaque dimension à noter : intitulé court et, si besoin, une description pour guider l’apprenant.',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.emptyCriteria':
    'Aucun critère. Ajoutez au moins une ligne pour que l’apprenant puisse s’auto-évaluer.',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.criterionTitlePh': 'Ex. Communication, Autonomie…',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionDescriptionPh':
    'Précisez ce que l’apprenant doit observer ou comment se positionner…',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Badges (mots) ou texte libre par critère',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.badgeOptionsHint':
    'Mots ou courtes expressions : l’apprenant les choisit comme pastilles. Sans proposition → champ texte libre.',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.textModeHint':
    'L’apprenant rédige une réponse pour chaque critère. Le commentaire global reste disponible sous les critères dans le lecteur.',
  'eduflow.designer.blockLabel.evaluation': 'Évaluation multi-questions',
  'eduflow.designer.blockLabel.phrase_reveal': 'Révélation progressive',
  'eduflow.designer.blockLabel.voice_assessment': 'Évaluation vocale',
  'eduflow.designer.blockLabel.dys_reader': 'Lecteur DYS (syllabes + TTS)',
  'eduflow.designer.blockLabel.dys_image_zones': 'Image zones interactives',
  'eduflow.designer.blockLabel.dys_clock': 'Horloge colorée DYS',
  'eduflow.designer.blockLabel.dys_reading_practice': 'Exercice lecture adaptée',
  'eduflow.designer.blockLabel.glossary': 'Glossaire + Quiz intégré',
  'eduflow.designer.blockLabel.html': 'HTML / JavaScript personnalisé',
  'eduflow.designer.blockLabel.certification': 'Certificat (template Word)',
  'eduflow.designer.blockLabel.signature': 'Signature apprenant',
  'eduflow.designer.blockLabel.facial_recognition': 'Reconnaissance faciale',
  'eduflow.designer.blockLabel.attendance': 'Émargement numérique',
  'eduflow.designer.startBlockTitle': 'Départ',
  'eduflow.designer.placeholderUntitled': 'Sans titre',
  'eduflow.designer.placeholderFlowDescription': 'Description de la formation (255 caractères max)',
  'eduflow.designer.saving': 'Enregistrement…',
  'eduflow.designer.unsaved': 'Non enregistré',
  'eduflow.designer.saved': 'Enregistré',
  'eduflow.designer.blocksPanelTitle': 'Blocs',
  'eduflow.designer.paletteSearchLabel': 'Rechercher un bloc',
  'eduflow.designer.paletteSearchPlaceholder': 'Rechercher un bloc…',
  'eduflow.designer.paletteSearchEmpty': 'Aucun bloc ne correspond à votre recherche.',
  'eduflow.designer.autoLayoutShort': 'Auto-layout',
  'eduflow.designer.validate': 'Valider',
  'eduflow.designer.validateTitle': 'Valider le parcours',
  'eduflow.designer.validationBeforePreview': 'Corrigez les erreurs du parcours avant l’aperçu.',
  'eduflow.designer.validationBeforePublish': 'Corrigez les erreurs du parcours avant de publier.',
  'eduflow.designer.aiShort': 'IA',
  'eduflow.designer.aiTitle': 'Assistant IA',
  'eduflow.designer.ragChatbotTitle': 'Chatbot formation',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.needDoc': 'Ajoutez au moins un document avant d’activer le chatbot.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.ragChatbot.uploadSavedNotIndexed':
    'Document enregistré. Il ne sera pas utilisé par le RAG tant que la base vectorielle n’est pas configurée sur le serveur.',
  'eduflow.ragChatbot.ragIndexFailed':
    'Document enregistré, mais non indexé. Vérifiez pgvector (DATABASE_VECTOR_URL) et Mistral, puis réimportez si besoin.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.vectorHint':
    'Pour indexer des PDF / TXT / MD : configurez DATABASE_VECTOR_URL ou PGVECTOR_HOST (PostgreSQL + extension pgvector) et Mistral dans Administration → Intégrations plateforme (clé + modèle d’embeddings). Sans cela, l’upload et l’indexation échouent.',
  'eduflow.ragChatbot.chatHint':
    'Les réponses et les embeddings RAG utilisent Mistral. Les documents ajoutés ici sont cherchés via embeddings Mistral + pgvector ; sans document, seul le texte des blocs du parcours sert de contexte.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerToggleHint':
    'Les apprenants posent des questions uniquement sur le contenu de cette formation : texte des blocs du parcours, et extraits des documents indexés si vous en avez ajouté.',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.docCount': 'Documents indexés : {{count}}',
  'eduflow.ragChatbot.emptyDocs':
    'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.playerDisclaimer':
    'Réponses basées sur les documents et le contenu de cette formation uniquement.',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.designer.aiPanel.welcome':
    'Décrivez ce que vous voulez faire. Le bloc ouvert dans **Propriétés** est la cible par défaut. Utilisez la **viseur** pour capturer l’écran (canevas + formulaire) : l’IA analyse et remplit les champs via le canevas.',
  'eduflow.designer.aiPanel.askBeforeAct': 'Demander avant d’agir',
  'eduflow.designer.aiPanel.askBeforeActHint':
    'Si activé, les modifications sont proposées dans le chat avant application sur le graphe.',
  'eduflow.designer.aiPanel.captureScreenHint':
    'Capturer le canevas et le panneau Propriétés (analyse visuelle)',
  'eduflow.designer.aiPanel.captureReady': 'Capture prête — envoyez votre consigne.',
  'eduflow.designer.aiPanel.captureFailed': 'Impossible de capturer l’écran.',
  'eduflow.designer.aiPanel.captureAttached': 'Capture d’écran jointe au prochain envoi',
  'eduflow.designer.aiPanel.removeCapture': 'Retirer',
  'eduflow.designer.aiPanel.captureInMessage': 'Capture écran',
  'eduflow.designer.aiPanel.statusCapturing': 'Capture de l’interface…',
  'eduflow.designer.aiPanel.statusAnalyzing': 'Analyse de l’écran et du bloc ouvert…',
  'eduflow.designer.aiPanel.statusWorking': 'Traitement de votre demande…',
  'eduflow.designer.aiPanel.statusApplying': 'Application sur le canevas…',
  'eduflow.designer.aiPanel.captureDisclaimer':
    'L’IA peut se tromper : vérifiez le contenu généré. Collez une image (Ctrl+V) ou utilisez la capture.',
  'eduflow.designer.resizeAiPanel': 'Redimensionner le panneau assistant IA',
  'eduflow.designer.aiPanel.subtitle': 'Copilote : contenu, graphe et objectifs.',
  'eduflow.designer.aiPanel.focusContextLabel': 'Cible :',
  'eduflow.designer.aiPanel.focusPrimary': '« {{label}} » ({{type}}) — Propriétés',
  'eduflow.designer.aiPanel.focusMulti':
    '{{count}} blocs sélectionnés — précisez lequel modifier ou un seul bloc au focus.',
  'eduflow.designer.aiPanel.focusNone':
    'Aucun bloc ciblé — ouvrez Propriétés sur un nœud ou nommez un bloc dans le message.',
  'eduflow.designer.aiPanel.followUpsLabel': 'Suite suggérée',
  'eduflow.designer.aiPanel.ideasToggle': 'Idées prêtes à l’emploi',
  'eduflow.designer.aiPanel.advancedToggle': 'Options (streaming, sources, raccourcis…)',
  'eduflow.designer.aiPanel.removeDocument': 'Retirer',
  'eduflow.designer.aiPanel.importDocumentTitle': 'Importer PDF, Word ou PowerPoint',
  'eduflow.designer.aiPanel.webSourcesLabel': 'Pages web (max 3, optionnel)',
  'eduflow.designer.aiPanel.webSourcesPlaceholder': 'https://exemple.fr/doc\nhttps://…',
  'eduflow.designer.aiPanel.idea.auditPath':
    'Audite tout le graphe : impasses, nœuds isolés et ordre du parcours — propose les corrections nécessaires',
  'eduflow.designer.aiPanel.idea.microLearning':
    'À partir du parcours actuel, propose une variante courte type micro-learning tout en gardant les idées',
  'eduflow.designer.aiPanel.idea.formativeGap':
    'Insère une médiation formative (activité courte) aux endroits les plus denses cognitivement',
  'eduflow.designer.aiPanel.idea.titleCoherence':
    'Aligne les titres des blocs sur le titre du projet et le ton général décrit dans la formation',
  'eduflow.designer.aiPanel.idea.remediation':
    'Ajoute un chemin de remédiation après le principal QCM (branche échec / score bas)',
  'eduflow.designer.aiPanel.idea.mobileWording':
    'Raccourcis et clarifie les textes HTML des blocs trop longs pour lecture mobile',
  'eduflow.designer.aiPanel.idea.quizWording':
    'Unifie le style des consignes (« Choisis », « Sélectionnez », niveau formel…) sur tous les quiz',
  'eduflow.designer.aiPanel.idea.summaryBeforeEnd':
    'Insère ou rédige un bloc de synthèse juste avant le bloc Fin',
  'eduflow.designer.aiPanel.streamMode': 'Streaming',
  'eduflow.designer.aiPanel.streamModeHint':
    'Réponse affichée au fil de l’eau ; le résultat final est traité comme sans streaming.',
  'eduflow.designer.aiPanel.streamError': 'Flux IA interrompu ou refusé.',
  'eduflow.designer.aiPanel.streamIncomplete': 'Réponse incomplète depuis le serveur. Réessayez ou désactivez le streaming.',
  'eduflow.designer.aiPanel.skillPath': 'Audit du parcours (serveur)',
  'eduflow.designer.aiPanel.skillPathHint': 'Analyse structurelle du graphe ajoutée au prompt.',
  'eduflow.designer.aiPanel.skillQuiz': 'Schéma QCM strict (serveur)',
  'eduflow.designer.aiPanel.skillQuizHint': 'Gabarit officiel quiz_mcq dans le prompt.',
  'eduflow.designer.aiPanel.thinking': 'L\'assistant réfléchit…',
  'eduflow.designer.aiPanel.serverSkills': 'Renforts côté serveur',
  'eduflow.designer.aiPanel.quickActions': 'Actions rapides',
  'eduflow.designer.aiPanel.placeholder':
    'Question, idée, relecture, structure du parcours, contenu d’un bloc…',
  'eduflow.designer.aiPanel.apply': 'Appliquer au canevas',
  'eduflow.designer.aiPanel.autoApply': 'Appliquer tout seul au graphe',
  'eduflow.designer.aiPanel.autoApplyHint':
    'Les opérations structurées de l’assistant modifient le parcours sans clic sur « Appliquer ».',
  'eduflow.designer.aiPanel.dismissOps': 'Ignorer',
  'eduflow.designer.aiPanel.applied': 'Modifications appliquées au parcours.',
  'eduflow.designer.aiPanel.applyPartial': 'Appliqué avec avertissements : {{details}}',
  'eduflow.designer.aiPanel.applyFailed':
    'Aucune opération n’a pu être appliquée sur le canevas (format des opérations ou types de blocs invalides).',
  'eduflow.designer.aiPanel.objectivesSaved': 'Objectifs pédagogiques enregistrés (panneau Objectifs).',
  'eduflow.designer.aiPanel.objectivesSaveError': 'Impossible d’enregistrer les objectifs. Réessayez.',
  'eduflow.designer.aiPanel.error': 'Impossible de contacter l’assistant. Vérifiez la configuration IA (clé API) et le réseau.',
  'eduflow.designer.aiPanel.newConversation': 'Nouvelle conversation',
  'eduflow.designer.aiPanel.clearConversation': 'Vider la conversation',
  'eduflow.designer.aiPanel.conversationHistory': 'Historique',
  'eduflow.designer.aiPanel.deleteConversation': 'Supprimer',
  'eduflow.designer.aiPanel.maxConversations':
    'Vous avez atteint la limite de {{max}} conversations enregistrées. Supprimez-en une pour en créer une nouvelle.',
  'eduflow.designer.aiPanel.unnamedConversation': 'Conversation',
  'eduflow.designer.aiQuick.plan': 'Plan de parcours',
  'eduflow.designer.aiQuick.expand': 'Blocs interactifs',
  'eduflow.designer.aiQuick.quiz': 'Module QCM',
  'eduflow.designer.aiQuick.objectives': 'Objectifs (panneau)',
  'eduflow.designer.aiQuick.rewriteFocus': 'Réécrire le bloc Propriétés',
  'eduflow.designer.aiQuick.translateFocus': 'Traduire le bloc Propriétés (EN)',
  'eduflow.designer.voiceShort': 'Voix',
  'eduflow.designer.voiceTitle': 'Configuration vocale',
  'eduflow.designer.objectivesShort': 'Objectifs',
  'eduflow.designer.objectivesTitle': 'Objectifs pédagogiques',
  'eduflow.designer.flowVariablesTitle': 'Variables globales',
  'eduflow.designer.flowVariables.subtitle':
    'Définissez des paires clé / valeur. Utilisez @suivi du nom de la clé dans les titres et descriptions des blocs (ex. @nom_formation). Les valeurs sont substituées pour les apprenants.',
  'eduflow.designer.flowVariables.empty': 'Aucune variable. Ajoutez-en pour personnaliser les textes du parcours.',
  'eduflow.designer.flowVariables.keyLabel': 'Clé',
  'eduflow.designer.flowVariables.keyPlaceholder': 'ex. nom_formation',
  'eduflow.designer.flowVariables.keyHelp':
    'Lettres, chiffres, tiret et underscore uniquement. Sensible à la casse pour @cle.',
  'eduflow.designer.flowVariables.valueLabel': 'Valeur par défaut',
  'eduflow.designer.flowVariables.valuePlaceholder': 'Texte affiché à la place de @cle',
  'eduflow.designer.flowVariables.add': 'Ajouter une variable',
  'eduflow.designer.flowVariables.delete': 'Supprimer',
  'eduflow.designer.flowVariables.invalidKey': 'Chaque clé doit être renseignée et ne contenir que des caractères autorisés.',
  'eduflow.designer.flowVariables.duplicateKey': 'Deux clés identiques (sans tenir compte de la casse) ne sont pas autorisées.',
  'eduflow.designer.flowVariables.savedToast': 'Variables enregistrées',
  'eduflow.designer.flowVariables.errorToast': 'Impossible d’enregistrer les variables.',
  'eduflow.designer.flowVariables.saveNow': 'Enregistrer',
  'eduflow.designer.blockFlowVariablesHint':
    'Variables globales : utilisez @votre_cle dans le titre ou la description (configurez-les via l’icône { } dans la barre d’outils).',
  'eduflow.designer.blockFlowVariablesPreviewTitle': 'Aperçu (titres)',
  'eduflow.designer.blockFlowVariablesPreviewDescription': 'Aperçu (description)',
  'eduflow.designer.flowVariables.mentionNoMatch': 'Aucune variable ne correspond.',
  'eduflow.designer.formFieldsSlash.mentionNoMatch': 'Aucun champ ne correspond.',
  'eduflow.designer.onlineCount': '{{count}} en ligne',
  'eduflow.designer.editingBlocks_one': '{{count}} bloc en édition',
  'eduflow.designer.editingBlocks_other': '{{count}} blocs en édition',
  'eduflow.designer.invite': 'Inviter',
  'eduflow.designer.inviteTitle': 'Inviter un collaborateur',
  'eduflow.designer.exportTitle': 'Exporter le parcours',
  'eduflow.designer.importTitle': 'Importer un parcours (fichier ou collage JSON)',
  'eduflow.designer.exportNeedSavedFlow': 'Enregistrez la formation avant d’exporter un fichier .lyrra.',
  'eduflow.designer.canvasUntitled': 'Sans titre',
  'eduflow.designer.selectBlock': 'Sélectionnez un bloc',
  'eduflow.designer.selectBlockHint': 'Cliquez sur un bloc du canvas pour modifier ses propriétés',
  'eduflow.designer.blockPropertiesSubtitle': 'Propriétés du bloc',
  'eduflow.designer.textVoiceoverHint':
    'La voix off lit la description et le contenu du bloc.',
  'eduflow.designer.blockLockedReadOnly':
    'Ce bloc est verrouillé : les propriétés sont affichées en lecture seule.',
  'eduflow.designer.blockTitleField': 'Titre',
  'eduflow.designer.blockTitlePlaceholder': 'Titre du bloc',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.you': 'Vous',
  'eduflow.designer.blockInfoPosition': 'ID: {{id}} | Position: ({{x}}, {{y}})',
  'eduflow.designer.toastBlockCopied': 'Bloc copié',
  'eduflow.designer.toastCollaboratorInvited': 'Collaborateur invité',
  'eduflow.designer.toastInviteError': 'Impossible d’inviter ce collaborateur',
  'eduflow.designer.createFlowError': 'Impossible de créer le parcours',
  'eduflow.designer.loadFlowError': 'Impossible de charger cette formation.',
  'eduflow.designer.loadFlowForbidden': 'Vous n’avez pas accès à cette formation.',
  'eduflow.designer.loadFlowNotFound': 'Formation introuvable ou supprimée.',
  'eduflow.duplicateFlowError': 'Impossible de dupliquer la formation.',
  'eduflow.designer.editConnection': 'Modifier la connexion',
  'eduflow.designer.deleteConnection': 'Supprimer la connexion',

  'eduflow.translation.toolbarTitle': 'Traduction du contenu',
  'eduflow.translation.toolbarShort': 'Traduction',
  'eduflow.translation.title': 'Traduction multilingue',
  'eduflow.translation.subtitle':
    'Traduisez les textes des blocs (IA) et régénérez la voix off des descriptions si besoin. Les résultats sont enregistrés par langue.',
  'eduflow.translation.subtitleV2':
    'Choisissez une voix Mistral par langue : elles servent aux voix off générées dans le player (traduction à la demande). Les jobs de traduction ci-dessous restent optionnels.',
  'eduflow.translation.voicesSection': 'Voix TTS par langue',
  'eduflow.translation.voicesSectionHint':
    'Aperçu dans la langue concernée. Sans voix choisie : « Marie - sad » pour le français, l’italien et l’espagnol ; « Paul - sad » pour les autres langues.',
  'eduflow.translation.voicesLangCount':
    '{{count}} langues (FR, EN, ES, DE, IT, PT, LB, UK, RU, AR) : choisissez une voix dans la liste, puis « Écouter » pour un court extrait.',
  'eduflow.translation.voicesLoadError':
    'Impossible de charger les voix. Vérifiez votre connexion ou réessayez.',
  'eduflow.translation.voicePreview': 'Écouter',
  'eduflow.translation.previewStop': 'Stop',
  'eduflow.translation.voicePlaceholder': '— Choisir une voix —',
  'eduflow.translation.voiceSelectAria': 'Voix pour {{lang}}',
  'eduflow.translation.voicesSaveError': 'Impossible de sauvegarder les voix.',
  'eduflow.translation.selectVoiceFirst': 'Choisissez une voix pour cette langue.',
  'eduflow.translation.previewError': 'Échec de la lecture audio.',
  'eduflow.translation.batchSection': 'Traductions précalculées (optionnel)',
  'eduflow.translation.batchSectionHint':
    'Le player peut traduire à la volée ; lancez un job seulement si vous voulez figer texte + audio en base à l’avance.',
  'eduflow.translation.sourceLang': 'Langue source du parcours',
  'eduflow.translation.bulkSection': 'Toutes les langues',
  'eduflow.translation.includeText': 'Texte',
  'eduflow.translation.includeAudio': 'Audio (voix off description)',
  'eduflow.translation.runAll': 'Traduire toutes les langues (sauf la source)',
  'eduflow.translation.runAllHint':
    'Lance un job par langue cible parmi les 11 langues supportées, en excluant la langue source.',
  'eduflow.translation.perLanguage': 'Par langue',
  'eduflow.translation.sourceBadge': 'Source',
  'eduflow.translation.runOne': 'Traduire',
  'eduflow.translation.history': 'Jobs récents',
  'eduflow.translation.noHistory': 'Aucun job pour l’instant.',
  'eduflow.translation.saveFirst': 'Enregistrez le parcours avant de lancer une traduction.',
  'eduflow.translation.prefsSaveError':
    'Impossible d’enregistrer les préférences de traduction (cases Texte / Audio par langue).',
  'eduflow.translation.jobStarted': 'Traduction lancée vers {{lang}}.',
  'eduflow.translation.jobError': 'Impossible de lancer la traduction.',
  'eduflow.translation.bulkStarted': '{{count}} traduction(s) lancée(s).',
  'eduflow.translation.bulkNone':
    'Aucun nouveau job : des traductions sont déjà en cours pour toutes les langues cibles, ou les options sont vides.',
  'eduflow.translation.bulkError': 'Impossible de lancer les traductions groupées.',
  'eduflow.translation.statusPending': 'En attente',
  'eduflow.translation.statusProcessing': 'En cours',
  'eduflow.translation.statusCompleted': 'Terminé',
  'eduflow.translation.statusFailed': 'Échec',
  'eduflow.translation.missingSourceAudioHint':
    'Traduction avec audio : générez d’abord la voix off (langue {{lang}}) pour chaque description longue. Bloc(s) concerné(s) ({{count}}) : {{blocks}}.',
  'eduflow.translation.missingSourceAudioToast':
    '{{count}} bloc(s) sans voix off dans la langue du parcours : {{blocks}}. Générez l’audio depuis les propriétés du bloc, puis relancez.',

  // Projects
  'project.title': 'Projets',
  'project.new': 'Nouveau projet',
  'project.name': 'Nom du projet',
  'project.description': 'Description du projet',
  'project.status.draft': 'Brouillon',
  'project.status.published': 'Publié',
  'project.status.archived': 'Archivé',
  'project.status.in_review': 'En révision',
  'project.visibility.public': 'Public',
  'project.visibility.private': 'Privé',
  'project.visibility.institution': 'Établissement',
  'project.no_projects': 'Aucun projet trouvé',
  'project.delete_confirm': 'Êtes-vous sûr de vouloir supprimer ce projet ?',

  // Store / Boutique
  'store.title': 'Boutique',
  'store.search': 'Rechercher dans la boutique',
  'store.categories': 'Catégories',
  'store.price': 'Prix',
  'store.free': 'Gratuit',
  'store.buy': 'Acheter',
  'store.add_to_cart': 'Ajouter au panier',
  'store.cart': 'Panier',
  'store.checkout': 'Commander',
  'store.reviews': 'Avis',
  'store.rating': 'Note',
  'store.my_purchases': 'Mes achats',
  'store.no_items': 'Aucun article trouvé',

  // Audio
  'audio.title': 'Studio Audio',
  'audio.audiobooks': 'Livres audio',
  'audio.new_audiobook': 'Nouveau livre audio',
  'audio.record': 'Enregistrer',
  'audio.stop': 'Arrêter',
  'audio.play': 'Lecture',
  'audio.pause': 'Pause',
  'audio.volume': 'Volume',
  'audio.tracks': 'Pistes',
  'audio.add_track': 'Ajouter une piste',
  'audio.remove_track': 'Supprimer la piste',
  'audio.effects': 'Effets',
  'audio.export': 'Exporter',
  'audio.export_format': 'Format d\'export',
  'audio.duration': 'Durée',
  'audio.narrator': 'Narrateur',
  'audio.chapter': 'Chapitre',

  // Notifications
  'notification.title': 'Notifications',
  'notification.mark_all_read': 'Tout marquer comme lu',
  'notification.no_notifications': 'Aucune notification',
  'notification.new': 'Nouvelle notification',
  'notification.type.info': 'Information',
  'notification.type.success': 'Succès',
  'notification.type.warning': 'Avertissement',
  'notification.type.error': 'Erreur',

  // Settings
  'settings.title': 'Paramètres',
  'settings.general': 'Général',
  'settings.account': 'Compte',
  'settings.security': 'Sécurité',
  'settings.appearance': 'Apparence',
  'settings.notifications': 'Notifications',
  'settings.language': 'Langue',
  'settings.theme': 'Thème',
  'settings.theme.light': 'Clair',
  'settings.theme.dark': 'Sombre',
  'settings.theme.system': 'Système',
  'settings.accessibility': 'Accessibilité',
  'settings.font_size': 'Taille de police',
  'settings.high_contrast': 'Contraste élevé',
  'settings.reduced_motion': 'Mouvements réduits',
  'settings.dyslexia_mode': 'Mode dyslexie',

  // Analytics
  'analytics.title': 'Analytiques',
  'analytics.overview': 'Vue d\'ensemble',
  'analytics.learner_progress': 'Progression des apprenants',
  'analytics.engagement': 'Engagement',
  'analytics.completion': 'Complétion',
  'analytics.time_spent': 'Temps passé',
  'analytics.average_score': 'Score moyen',
  'analytics.export_report': 'Exporter le rapport',

  // Gamification
  'gamification.xp': 'XP',
  'gamification.level': 'Niveau',
  'gamification.streak': 'Série',
  'gamification.badges': 'Badges',
  'gamification.leaderboard': 'Classement',
  'gamification.achievements': 'Réalisations',
  'gamification.daily_goal': 'Objectif quotidien',
  'gamification.congratulations': 'Félicitations !',
  'gamification.new_badge': 'Nouveau badge débloqué !',
  'gamification.level_up': 'Niveau supérieur !',

  // Qualiopi
  'qualiopi.title': 'Conformité Qualiopi',
  'qualiopi.dashboard': 'Tableau de bord Qualiopi',
  'qualiopi.criteria': 'Critère {{number}}',
  'qualiopi.indicator': 'Indicateur {{number}}',
  'qualiopi.compliant': 'Conforme',
  'qualiopi.non_compliant': 'Non conforme',
  'qualiopi.partial': 'Partiellement conforme',
  'qualiopi.not_applicable': 'Non applicable',
  'qualiopi.evidence': 'Preuves',
  'qualiopi.upload_evidence': 'Téléverser une preuve',
  'qualiopi.audit_date': 'Date d\'audit',
  'qualiopi.next_audit': 'Prochain audit',
  'qualiopi.compliance_score': 'Score de conformité',
  'qualiopi.action_plan': 'Plan d\'actions',

  // V2 Training Management
  'training.plan': 'Plan de formation',
  'training.request': 'Demande de formation',
  'training.requests': 'Demandes de formation',
  'training.catalog': 'Catalogue de formations',
  'training.budget': 'Budget formation',
  'training.campaign': 'Campagne d\'entretiens',
  'training.competency': 'Référentiel de compétences',
  'training.gap_analysis': 'Analyse des écarts',
  'training.roi': 'Retour sur investissement',

  // Missions producteur (pipeline RH → formateur)
  'producerAssignments.intro':
    'Ici figurent uniquement les contenus que la RH vous a explicitement assignés : fiches catalogue (« coquilles ») à transformer en parcours dans le designer, et podcasts dont vous êtes le producteur désigné. Si les listes sont vides, aucune mission ne vous est attribuée pour l’instant.',
  'producerAssignments.errorForbidden':
    'Accès refusé : votre rôle ne permet pas d’utiliser cette page. Si vous êtes bien formateur ou RH d’établissement, contactez l’administrateur.',
  'producerAssignments.errorNoInstitution':
    'Votre compte n’a pas d’établissement rattaché dans l’application (données en base). Un administrateur doit associer votre utilisateur à l’organisation, puis vous reconnecter.',
  'producerAssignments.errorGeneric': 'Impossible de charger vos missions.',

  // Common
  'common.loading': 'Chargement...',
  'common.refresh': 'Actualiser',
  'common.saving': 'Enregistrement...',
  'common.saved': 'Enregistré',
  'common.error': 'Erreur',
  'common.success': 'Succès',
  'common.warning': 'Attention',
  'common.info': 'Information',
  'common.yes': 'Oui',
  'common.no': 'Non',
  'common.or': 'ou',
  'common.and': 'et',
  'common.all': 'Tous',
  'common.none': 'Aucun',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'common.created_at': 'Créé le',
  'common.updated_at': 'Modifié le',
  'common.sort_by': 'Trier par',
  'common.per_page': 'Par page',
  'common.page': 'Page',
  'common.of': 'sur',
  'common.results': '{{count}} résultat(s)',
  'common.no_data': 'Aucune donnée',
  'common.coming_soon': 'Bientôt disponible',
  'common.beta': 'Bêta',
  'common.new': 'Nouveau',
  'common.delete_confirm': 'Cette action est irréversible. Continuer ?',
  'common.copied': 'Copié !',
  'common.drag_drop': 'Glisser-déposer',
  'common.online': 'En ligne',
  'common.offline': 'Hors ligne',
  'common.version': 'Version',

  // Player
  'player.next': 'Suivant',
  'player.previous': 'Précédent',
  'player.scrollDownHint': 'Faites défiler pour la suite',
  'player.image.clickForAnnotations': 'Cliquez sur l’image pour voir les annotations',
  'player.image.annotationNoDescription': 'Aucune description pour cette annotation.',
  'player.image.noneAvailable': 'Aucune image disponible',
  'player.visualAi.emptyTitle': 'Aucun visuel IA configuré',
  'player.visualAi.emptyHint': 'Revenez dans le concepteur pour générer ou configurer ce visuel.',
  'player.visualAi.defaultTitle': 'Visuel IA',
  'player.visualAi.subtitle': 'Explorez le diagramme : zoomez, faites défiler ou utilisez le mode plein écran.',
  'player.visualAi.openFullscreen': 'Ouvrir en plein écran',
  'player.visualAi.closeFullscreen': 'Fermer le plein écran',
  'player.visualAi.badge': 'Visuel IA',
  'player.visualAi.viewportLabel': 'Diagramme interactif — zoom et déplacement',
  'player.visualAi.toolbarLabel': 'Contrôles du diagramme',
  'player.visualAi.zoomOut': 'Zoom arrière',
  'player.visualAi.zoomIn': 'Zoom avant',
  'player.visualAi.resetZoom': 'Réinitialiser le zoom à 100 %',
  'player.visualAi.reset': '100 %',
  'player.visualAi.fitView': 'Ajuster le diagramme à la fenêtre',
  'player.visualAi.fit': 'Ajuster',
  'player.visualAi.zoomLevel': 'Zoom {{percent}} %',
  'player.visualAi.viewportHint':
    'Molette + Ctrl (ou Cmd) pour zoomer. Flèches du clavier pour vous déplacer une fois la zone focalisée. Tab pour atteindre les contrôles.',
  'player.complete': 'Terminer',
  'player.exit': 'Quitter',
  'player.fullscreen': 'Plein écran',
  'player.exitFullscreen': 'Quitter le plein écran',
  'player.webinarFollowBanner':
    'Vous suivez l’animateur — la navigation est verrouillée. Vous pouvez changer la langue d’affichage via le bouton globe.',
  'player.textBlockTipsHeading': 'Conseils',
  'player.textBlockReadDone': 'Lecture terminée',
  'player.institutionLogo': "Logo de l'organisation",
  'player.exitConfirmTitle': 'Quitter la formation ?',
  'player.exitConfirmMessage': 'Votre progression sera sauvegardée. Êtes-vous sûr de vouloir quitter ?',
  'player.focusLostTitle': 'Formation en pause',
  'player.focusLostMessage': 'Vous avez quitté la fenêtre de formation. Le temps passé est mis en pause.',
  'player.focusLostResume': 'Cliquer pour reprendre',
  'player.focusLostRemaining': 'Sorties restantes avant blocage : {{remaining}}',
  'player.focusBlockedTitle': 'Formation bloquée',
  'player.focusBlockedMessage':
    'Vous avez dépassé le nombre de sorties autorisées ({{max}}). Vous ne pouvez plus continuer cette formation.',
  'player.unblockRequestButton': 'Demander un déblocage au formateur',
  'player.unblockRequestAgain': 'Demander à nouveau',
  'player.unblockPending': 'Demande envoyée — en attente de réponse de votre formateur.',
  'player.unblockRejected': 'Votre demande de déblocage a été refusée.',
  'player.unblockLoginRequired':
    'Connectez-vous à votre compte apprenant (ou utilisez le lien personnel reçu par e-mail) pour demander un déblocage.',
  'player.unblockPublicLoginRequired':
    'En mode public sans inscription, la demande de déblocage n’est pas disponible. Remplissez le formulaire d’inscription de la formation ou connectez-vous à LyrraStudio.',
  'player.unblockRequestSent': 'Demande envoyée à votre formateur.',
  'player.unblockRequestError': "Impossible d'envoyer la demande. Réessayez.",
  'player.unblockApprovedToast': 'Votre formateur a débloqué la formation. Bonne reprise !',
  'player.registrationFormHint':
    'Ce formulaire crée votre compte apprenant pour cette formation (e-mail, prénom et nom requis).',
  'player.registrationFormSuccess':
    'Inscription enregistrée. Vous pouvez poursuivre la formation.',
  'player.registrationFormError': "Impossible d'enregistrer l'inscription.",
  'notifications.unblock.title': 'Demande de déblocage',
  'notifications.unblock.message': '{{name}} — {{flow}} ({{count}} sortie(s))',
  'notifications.unblock.unknownLearner': 'Un apprenant',
  'notifications.unblock.unknownFlow': 'Formation',
  'notifications.unblock.approve': 'Débloquer',
  'notifications.unblock.reject': 'Refuser',
  'notifications.unblock.approvedToast': 'Formation débloquée pour cet apprenant.',
  'notifications.unblock.rejectedToast': 'Demande de déblocage refusée.',
  'notifications.unblock.resolveError': 'Impossible de traiter la demande. Réessayez.',
  'player.cancel': 'Annuler',
  'player.summary': 'Sommaire',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Traduction en cours…',
  'player.summary.blockSimplifying': 'Texte simplifié en cours…',
  'player.simplifiedTextLoadingTitle': 'Préparation du texte simplifié…',
  'player.simplifiedTextLoadingHint':
    'Nous simplifions d’abord le bloc affiché, puis le reste du parcours en arrière-plan.',
  'player.contents': 'Contenu',
  'player.progress': 'Progression',
  'player.block': 'Bloc',
  'player.parallelBranches': 'Branches parallèles',
  'player.parallelBranchesTitle': 'À suivre',
  'player.parallelBranchesHint': 'Les autres options seront proposées ensuite.',
  'player.parallelBranchShowFull': 'Voir plus',
  'player.parallelBranchShowLess': 'Voir moins',
  'player.parallelBranchChoose': 'Ouvrir',
  'player.parallelBranchDismiss': 'Fermer',
  'player.parallelBranchDismissHint':
    'Revenir au bloc. Utilisez « Suivant » pour choisir une branche.',
  'player.parallelBranchesPickTitle': 'Choisissez votre prochaine voie',
  'player.parallelBranchesPickHint':
    'Les voies terminées restent cliquables pour les revoir. Parcourez chaque branche au moins une fois avant la suite commune.',
  'player.parallelBranchActionReplay': 'Refaire cette voie',
  'player.parallelBranchFromBlock': 'Depuis : {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} voies complétées',
  'player.parallelBranchesOverview': 'Progression',
  'player.parallelBranchStatusDone': 'Terminée',
  'player.parallelBranchStatusTodo': 'En attente',
  'player.parallelBranchStatusNext': 'À faire maintenant',
  'player.parallelBranchAction': 'Choisir cette voie',
  'player.parallelBranchesGridLabel': 'Grille des voies parallèles',
  'player.parallelBranchesScrollHint':
    'Faites défiler vers la droite (ou utilisez les flèches) pour voir les autres voies.',
  'player.parallelBranchesPageNav': 'Pages de voies',
  'player.parallelBranchesPageIndicator': 'Page {{current}} / {{total}}',
  'player.parallelBranchesPrevPage': 'Voies précédentes',
  'player.parallelBranchesNextPage': 'Voies suivantes',
  'player.notes': 'Prendre des notes...',
  'player.noBlocks': 'Aucun bloc à afficher.',
  'player.contentTranslatingTitle': 'Adaptation du contenu à votre langue…',
  'player.contentTranslatingHint':
    'Traduction et mise en forme en cours. Cela peut prendre un peu de temps la première fois.',
  'player.contentLanguageReloadFailed':
    'Le contenu n’a pas pu être rechargé dans cette langue (délai ou erreur serveur). La version précédente reste affichée. Réessayez dans un instant ou précalculez les traductions dans le designer.',
  'player.contentLanguageSubflowHint':
    'Pour mettre à jour le texte et l’audio, terminez ou quittez la sous-partie en cours, puis choisissez à nouveau la langue.',
  'player.defaultFlowTitle': 'Formation',
  'player.docTitle.error': 'Formation indisponible',
  'player.docTitle.completed': 'Formation terminée',
  'player.docTitle.loading': 'Chargement de la formation',
  'player.docTitle.fallback': 'Formation',
  'player.initError.title': 'La formation est momentanément indisponible',
  'player.initError.loadFailed': "La formation n'a pas pu être chargée pour le moment.",
  'player.initError.loadFailedWithDetail':
    "La formation n'a pas pu être chargée pour le moment.{{detail}} Veuillez réessayer dans quelques instants.",
  'player.initError.hintsIntro': 'Vous pouvez :',
  'player.initError.hintRefresh': 'rafraîchir la page ;',
  'player.initError.hintConnection': 'vérifier votre connexion Internet ;',
  'player.initError.hintRetryLater': 'réessayer dans quelques minutes.',
  'api.error.network': 'Impossible de contacter le serveur. Vérifiez votre connexion.',
  'api.error.unexpected': 'Une erreur inattendue est survenue.',
  'api.error.badRequest': 'Requête invalide.',
  'api.error.forbidden': 'Accès interdit.',
  'api.error.notFound': 'Ressource introuvable.',
  'api.error.conflict': 'Conflit — cette ressource existe déjà.',
  'api.error.unprocessable': 'Les données envoyées ne sont pas valides.',
  'api.error.tooManyRequests': 'Trop de requêtes. Veuillez réessayer dans un instant.',
  'api.error.server': 'Erreur serveur interne.',
  'api.error.badGateway': 'Service temporairement indisponible.',
  'api.error.maintenance': 'Service en maintenance.',
  'api.error.generic': 'Erreur {{status}}',
  'player.textBlockReadMinutes': '{{count}} min de lecture',
  'player.textBlockWordCount_one': '{{count}} mot',
  'player.textBlockWordCount_other': '{{count}} mots',
  'player.textBlockNoContent': 'Aucun contenu disponible.',
  'player.blockUnknown': 'Bloc inconnu',
  'player.objectivesPanelTitle': 'Objectifs pédagogiques',
  'player.objectivesOpenLabel': 'Voir les objectifs pédagogiques',
  'player.objectivesCloseLabel': 'Fermer le panneau des objectifs',
  'player.objectivesAchievementRate': "Taux d'atteinte",
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objectifs atteints',
  'player.objectivesNoneDefined': 'Aucun objectif défini pour cette formation.',
  'player.objectiveCategory.savoir': 'Savoir',
  'player.objectiveCategory.savoirFaire': 'Savoir-faire',
  'player.objectiveCategory.savoirEtre': 'Savoir-être',
  'player.objectiveLevel.connaissance': 'Connaissance',
  'player.objectiveLevel.comprehension': 'Compréhension',
  'player.objectiveLevel.application': 'Application',
  'player.objectiveLevel.analyse': 'Analyse',
  'player.objectiveLevel.synthese': 'Synthèse',
  'player.objectiveLevel.evaluation': 'Évaluation',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obligatoire',
  'player.objectiveLinkedBlocks': 'Blocs associés',
  'player.objectiveCriterionScore': 'Score ≥ {{min}} %',
  'player.objectiveCriterionCompletion': 'Complétion du bloc',
  'player.objectiveNoLinkedBlocks':
    'Aucun bloc lié — la progression globale ne calcule pas cet objectif.',
  'player.completion.congratulations': '🎉 Félicitations !',
  'player.completion.finished': '📋 Parcours terminé',
  'player.completion.youCompleted': 'Vous avez terminé',
  'player.completion.passed': 'Réussi ✓',
  'player.completion.failed': 'Non validé ✗',
  'player.completion.requiredScore': 'Seuil requis : {{score}}%',
  'player.completion.totalTime': 'Temps total',
  'player.completion.blocksCompleted': 'Blocs complétés',
  'player.completion.quizPassed': 'Quiz réussis',
  'player.completion.successRate': 'Taux de réussite',
  'player.completion.blockCompleted': 'Complété ✓',
  'player.completion.blockFailed': 'Échoué ✗',
  'player.completion.blockSkipped': 'Ignoré',
  'player.completion.detail': '📊 Détail par bloc',
  'player.completion.type': 'Type',
  'player.completion.timeSpent': 'Temps passé',
  'player.completion.downloadCertificate': '📄 Télécharger le certificat',
  'player.completion.certificateSoon': 'Le certificat sera disponible prochainement.',
  'player.completion.restart': '🔄 Recommencer',
  'player.completion.viewDetails': '📊 Voir les détails',
  'player.completion.backToDashboard': '← Retour au tableau de bord',
  'player.completion.embedDismissHint':
    'Vous avez terminé ce module. Pour quitter, utilisez la croix en haut à droite de la fenêtre ou le bouton ci-dessous.',
  'player.completion.embedDismissButton': 'Fermer la fenêtre',
  'player.previewBanner': 'Mode aperçu — cette vue reflète les vraies données de votre formation',
  'player.closePreview': "Fermer l'aperçu",
  'player.flowPreviewMap': 'Schéma du parcours',
  'player.flowPreviewTitle': 'Aperçu du flux',
  'player.flowPreviewHint':
    'Vue statique (comme dans le designer). Le cadre orange indique le bloc affiché dans le lecteur.',
  'player.flowPreviewLegendCurrent': 'Bloc affiché',
  'player.flowPreviewClose': 'Fermer',
  'player.toggleSidebarShow': 'Afficher le panneau',
  'player.toggleSidebarHide': 'Masquer le panneau',
  'player.mobileSummary': 'Sommaire',
  'player.mobileObjectives': 'Objectifs',
  'player.mobileMoreOptions': "Plus d'options",
  'player.sidebarExpand': 'Développer le panneau',
  'player.sidebarCollapse': 'Réduire le panneau',
  'player.keyboardShortcuts': 'Raccourcis clavier',
  'player.keyboardShortcutPrev': 'Bloc précédent',
  'player.keyboardShortcutNext': 'Bloc suivant',
  'player.keyboardShortcutsHint':
    'Activez « Navigation au clavier » dans le centre d’accessibilité (bouton en bas à droite). Inactif dans les champs de saisie.',
  'player.a11yCenter': "Centre d'accessibilité",
  'player.reviewSummary': 'Voir le récapitulatif',
  'player.viewResults': 'Voir les résultats',
  'player.submitFirst': "Soumettre d'abord",
  'player.phraseRevealCompleteFirst': 'Parcourez toutes les phrases',
  'player.phraseReveal.emptyBlock': 'Aucune phrase configurée pour ce bloc.',
  'player.phraseReveal.revealNextAria':
    'Cliquez ou appuyez sur Espace pour révéler la phrase suivante',
  'player.phraseReveal.lastPhraseHint':
    'Dernière phrase — utilisez « Suivant » pour continuer le parcours.',
  'player.phraseReveal.morePhrasesHint': 'Clic, Espace ou flèche droite pour la suite',
  'player.phraseReveal.previousPhrases': 'Phrases précédentes ({{count}})',
  'player.certificateUnavailable': 'Le certificat sera disponible prochainement.',
  'player.browser.embeddedContentTitle': 'Contenu intégré',
  'player.browser.noValidUrl': 'Aucune URL valide',
  'player.browser.enterHttpsHint':
    'Saisissez une adresse commençant par https:// (ou http://) dans les paramètres du bloc.',
  'player.browser.loadingContent': 'Chargement du contenu…',
  'player.browser.defaultPageTitle': 'Page web intégrée',
  'player.browser.continue': 'Continuer',
  'player.browser.stepSaved': 'Étape enregistrée',
  'player.browser.fullscreen': 'Plein écran',
  'player.browser.exitFullscreen': 'Quitter le plein écran',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Épisode',
  'player.podcast.loadError': 'Impossible de charger le podcast.',
  'player.podcast.loadingEpisodes': 'Chargement des épisodes…',
  'player.podcast.playAll': 'Tout écouter',
  'player.podcast.nextEpisode': 'Épisode suivant',
  'player.podcast.episodesHeading': 'Épisodes',
  'player.podcast.noEpisodes': 'Aucun épisode.',
  'player.podcast.noAudio': "Pas d'audio",
  'player.podcast.chainPlayback': 'Lecture en chaîne · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Sélectionnez un épisode dans la liste ou lancez « Tout écouter ».',
  'player.podcast.audioUnavailable': 'Audio non disponible pour cet épisode.',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.episodeCount_one': '{{count}} épisode',
  'player.podcast.episodeCount_other': '{{count}} épisodes',
  'player.podcast.showChapters': 'Afficher les chapitres',
  'player.podcast.hideChapters': 'Masquer les chapitres',
  'player.podcast.coverDialogLabel': 'Couverture : {{title}}',
  'player.podcast.coverEnlargePodcast': 'Agrandir la pochette du podcast',
  'player.podcast.coverEnlargeEpisode': "Agrandir la pochette de l'épisode",
  'player.podcast.coverEnlarge': 'Agrandir la pochette',
  'player.audio.play': 'Lecture',
  'player.audio.pause': 'Pause',
  'player.audio.mute': 'Couper le son',
  'player.audio.unmute': 'Réactiver le son',
  'player.audio.seek': 'Position dans la piste audio',
  'player.audio.playbackSpeed': 'Vitesse de lecture',
  'player.audio.completed': 'Piste audio terminée',
  'player.audio.subtitlesAria': 'Texte synchronisé avec la lecture',
  'player.audio.subtitlesWaiting':
    'Le texte défilera ici pendant la lecture (segment selon la position).',
  'player.audio.subtitlesNeedText':
    'Ajoutez du texte (TTS ou contenu du bloc) pour afficher les segments ici.',
  'player.audio.subtitlesHintSr':
    'Synchronisation approximative : le texte est réparti sur la durée audio, sans timecodes mot à mot.',
  'player.timeline.defaultTitle': 'Frise chronologique',
  'player.timeline.subtitle': 'Frise chronologique à parcourir.',
  'player.timeline.eventFallback': 'Événement',
  'player.timeline.voiceoverLabel': 'Voix off',
  'player.timeline.playVoiceover': 'Lire la voix off',
  'player.timeline.pauseVoiceover': 'Pause voix off',
  'player.timeline.generateVoiceover': 'Générer la voix off',
  'player.timeline.showDetail': 'Voir le détail',
  'player.timeline.hideDetail': 'Masquer le détail',
  'player.timeline.emptyTitle': 'Aucun événement sur cette frise',
  'player.timeline.emptyHint': 'Ajoutez des étapes dans le panneau de configuration du bloc.',
  'player.timeline.voiceoverUnavailableTitle': 'Voix off indisponible',
  'player.timeline.voiceoverUnavailableDesc':
    'Ouvrez ce parcours depuis une formation connectée pour générer l’audio.',
  'player.timeline.voiceoverTextTooShortTitle': 'Texte trop court',
  'player.timeline.voiceoverTextTooShortDesc':
    'Ajoutez une description (au moins 20 caractères) ou un titre plus détaillé pour la voix off.',
  'player.timeline.voiceoverGenerationErrorTitle': 'Génération voix off impossible',
  'player.timeline.voiceoverSlowTitle': 'Voix off',
  'player.timeline.voiceoverSlowDesc':
    'La génération prend plus de temps que prévu. Réessayez dans un instant.',
  'player.timeline.retryLater': 'Réessayez plus tard.',
  'player.timeline.openFullscreen': 'Ouvrir en grand',
  'player.timeline.openFullscreenAria': 'Ouvrir la frise en plein écran',
  'player.timeline.closeFullscreenAria': 'Fermer le plein écran',
  'player.timeline.reviewed': 'Chronologie consultée',
  'player.video.seek': 'Position dans la vidéo',
  'player.video.subtitlesShow': 'Afficher les sous-titres',
  'player.video.subtitlesHide': 'Masquer les sous-titres',
  'player.video.trackLabel': 'Sous-titres',
  'player.video.completed': 'Vidéo terminée',
  'player.video.markWatched': 'J’ai regardé la vidéo',
  'player.video.defaultIframeTitle': 'Vidéo intégrée',
  'player.podcast.playAllAria': 'Tout écouter « {{title}} » — {{count}} épisode(s) avec audio',
  'player.podcast.skipNextQueuedAria': 'Épisode suivant dans la file de lecture',
  'player.podcast.playEpisodeAria': 'Lecture de l’épisode : {{title}}',
  'player.podcast.episodeNoAudioAria': 'Épisode sans fichier audio : {{title}}',
  'player.renderError': 'Erreur lors du rendu du bloc : {{blockType}}',
  'player.blocks.defaultWelcome': 'Bienvenue dans cette formation',
  'player.blocks.defaultEndTitle': 'Félicitations !',
  'player.quiz.selectCorrectAnswer': 'Sélectionnez la bonne réponse :',
  'player.quiz.correctAnswer': '✓ Bonne réponse !',
  'player.quiz.incorrectAnswer': '✗ Mauvaise réponse.',
  'player.blockTimer.aria': 'Temps pour répondre à ce bloc',
  'player.blockTimer.modeShortCountup': 'Minuteur',
  'player.blockTimer.modeShortCountdown': 'Décompte',
  'player.form.successMessage': 'Merci pour votre réponse !',
  'player.form.noFieldsConfigured': "Ce formulaire n'a pas encore de champs configurés.",
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.commentPlaceholder': 'Réflexions personnelles, axes de progrès…',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.textAnswerPlaceholder': 'Votre réponse…',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.evaluation.commentsLabel': 'Commentaires (optionnel)',
  'player.evaluation.commentsPlaceholder': 'Partagez votre avis...',
  'player.evaluation.submitted': 'Évaluation soumise',
  'player.evaluation.submit': "Soumettre l'évaluation",
  'player.time.lessThanMinute': "Moins d'une minute",
  'player.time.oneMinute': '1 minute',
  'player.time.minutes': '{{count}} minutes',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}}min',
  'player.time.shortMinutesSeconds': '{{minutes}}min {{seconds}}s',
  'player.completion.scoreLabel': 'Score',
  'accessibility.openCenter': "Ouvrir le centre d'accessibilité",
  'accessibility.center': 'Accessibilité',
  'accessibility.modalTitle': 'Centre de lecture adaptée',
  'accessibility.modalDescription': "Ajustez l'interface pour la malvoyance, les troubles dys et la lecture confortable.",
  'accessibility.closeCenter': "Fermer le centre d'accessibilité",
  'accessibility.accessPad.connected': 'Lyrra Access Pad connecté',
  'accessibility.accessPad.playGuide': 'Écouter le guide LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Mettre en pause le guide LyrraPad',
  'accessibility.presets': 'Profils préconfigurés',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': "Rendu par défaut de l'application.",
  'accessibility.preset.lowVision.label': 'Malvoyance',
  'accessibility.preset.lowVision.desc': 'Contraste renforcé, texte agrandi et repères visuels plus nets.',
  'accessibility.preset.dys.label': 'Dys',
  'accessibility.preset.dys.desc': "Lecture facilitée avec plus d'espace et une police plus lisible.",
  'accessibility.preset.cumulHint': 'Combinez plusieurs profils si besoin',
  'accessibility.preset.clickToAdd': 'Cliquer pour ajouter',
  'accessibility.preset.clickToRemove': 'Cliquer pour retirer',
  'accessibility.typography': 'Réglages typographiques',
  'accessibility.fontScale': 'Taille du texte',
  'accessibility.lineHeight': 'Interlignage',
  'accessibility.letterSpacing': 'Espacement des lettres',
  'accessibility.wordSpacing': 'Espacement des mots',
  'accessibility.visualAids': 'Aides visuelles',
  'accessibility.highContrast': 'Contraste renforcé',
  'accessibility.highContrastDesc': 'Renforce les contrastes de lecture, les bordures et les repères visuels.',
  'accessibility.underlineLinks': 'Liens soulignés',
  'accessibility.underlineLinksDesc': 'Affiche plus clairement les liens et zones interactives.',
  'accessibility.dyslexiaFont': 'Police de lecture facilitée',
  'accessibility.dyslexiaFontDesc': 'Utilise une police plus confortable pour les personnes dys et les lecteurs fatigués.',
  'accessibility.reducedMotion': 'Mouvements réduits',
  'accessibility.reducedMotionDesc': "Diminue les animations et transitions pour limiter l'inconfort visuel.",
  'accessibility.mouseMagnifier': 'Loupe de lecture',
  'accessibility.mouseMagnifierDesc':
    'Affiche une loupe circulaire qui suit le curseur et grossit le contenu du parcours sous la zone pointée (affichage agrandi en lecture seule).',
  'accessibility.readingMask': 'Masque de lecture',
  'accessibility.readingMaskDesc':
    'Une bande opaque suit le curseur pour isoler la ligne en cours — très utile pour la dyslexie et les TDA/H. Astuce : Option ⌥ + touche =/+ ou Option ⌥ + ↑/↓ (macOS) ; Alt + + / Alt − (Windows/Linux) pour ajuster la hauteur.',
  'accessibility.speakOnSelection': 'Synthèse vocale sur sélection',
  'accessibility.speakOnSelectionDesc':
    "Sélectionnez du texte sur la page : un bouton apparaît pour le lire à voix haute à la demande (différent du mode lecture audio automatique).",
  'accessibility.speakSelection.listen': 'Écouter la sélection',
  'accessibility.speakSelection.stop': 'Arrêter la lecture',
  'accessibility.speakSelection.loading': 'Génération…',
  'accessibility.speakSelection.retry': 'Réessayer',
  'accessibility.speakSelection.error': 'Lecture indisponible, réessayez dans quelques instants.',
  'accessibility.speakSelection.region': 'Lire la sélection à voix haute',
  'a11y.voiceGuide.title': 'Assistant vocal accessibilité',
  'a11y.help.shortcuts': 'Raccourcis clavier',
  'a11y.help.kbdEsc': 'Fermer le panneau / arrêter la synthèse vocale',
  'a11y.help.kbdMask': 'Ajuster la hauteur du masque de lecture (masque activé)',
  'a11y.help.kbdMaskMac': 'Touche = ou + (au-dessus des chiffres), ou flèches ↑ ↓ avec Option ⌥',
  'a11y.help.kbdMaskOther': 'Windows / Linux : Alt + + / Alt + −',
  'a11y.help.kbdTab': 'Naviguer entre les éléments interactifs',
  'a11y.help.tips': 'Conseils',
  'a11y.voiceGuide.subtitle': "Décrivez vos besoins, j'active les bonnes options pour vous.",
  'a11y.voiceGuide.cta.title': 'Décrivez vos besoins',
  'a11y.voiceGuide.cta.hint': "Parlez : l'IA active automatiquement les bonnes options pour vous.",
  'a11y.voiceGuide.cta.openLabel': "Ouvrir l'assistant vocal — décrire mes besoins d'accessibilité à voix haute",
  'a11y.voiceGuide.cta.start': "Démarrer l'assistant vocal",
  'a11y.voiceGuide.cta.stop': "Arrêter d'écouter",
  'a11y.voiceGuide.status': 'Statut',
  'a11y.voiceGuide.statusActive': 'En cours',
  'a11y.voiceGuide.youSaid': 'Vous avez dit',
  'a11y.voiceGuide.reformulation': "Voici ce que j'ai compris",
  'a11y.voiceGuide.applied': 'Réglages appliqués',
  'a11y.voiceGuide.resetButton': 'Remettre en normal',
  'a11y.voiceGuide.resetExplanation': "Tous les réglages d'accessibilité ont été remis par défaut.",
  'a11y.voiceGuide.problem': 'Problème',
  'a11y.voiceGuide.micReady': 'Micro prêt',
  'a11y.voiceGuide.micUnsupported': 'Reconnaissance vocale non supportée',
  'a11y.voiceGuide.stage.idle': 'Prêt à vous écouter',
  'a11y.voiceGuide.stage.intro': 'Je vous explique…',
  'a11y.voiceGuide.stage.listening': 'Je vous écoute. Parlez librement.',
  'a11y.voiceGuide.stage.thinking': 'Analyse de vos besoins…',
  'a11y.voiceGuide.stage.confirmPlaying': 'Je résume…',
  'a11y.voiceGuide.stage.confirmListening': 'Dites « oui » pour confirmer ou « non » pour recommencer.',
  'a11y.voiceGuide.stage.applying': 'Application de vos réglages…',
  'a11y.voiceGuide.stage.done': "C'est fait !",
  'a11y.voiceGuide.stage.error': 'Une erreur est survenue.',
  'a11y.voiceGuide.error.mic': "Impossible d'accéder au micro.",
  'a11y.voiceGuide.error.micDenied': "L'accès au microphone a été refusé. Autorisez-le dans les paramètres du navigateur.",
  'a11y.voiceGuide.error.stt': 'Reconnaissance vocale non disponible sur ce navigateur.',
  'a11y.voiceGuide.error.ai': 'Analyse IA indisponible.',
  'a11y.voiceGuide.error.serverUnavailable':
    'Le serveur Lyrra ne répond pas correctement (souvent : backend non déployé ou erreur de build). Vérifiez le déploiement, puis réessayez.',
  'a11y.voiceGuide.error.generic': "Quelque chose s'est mal passé. Réessayez ou ajustez les réglages manuellement.",
  'accessibility.alwaysOn': 'Améliorations actives en continu',
  'accessibility.alwaysOnFocus': 'Focus clavier plus visible',
  'accessibility.alwaysOnLinks': 'Liens et éléments interactifs plus détectables',
  'accessibility.alwaysOnSaved': 'Réglages sauvegardés automatiquement sur cet appareil',
  'accessibility.navigation': 'Navigation',
  'accessibility.keyboardNavigation': 'Navigation au clavier',
  'accessibility.keyboardNavigationDesc':
    'Utilisez les flèches gauche / haut pour le bloc précédent et droite / bas pour le bloc suivant (comme les boutons du bas).',
  // ── Learner Dashboard ──────────────────────────────────
  'learner.header.workspace': 'Espace apprenant',
  'learner.tabs.home': 'Accueil',
  'learner.tabs.parcours': 'Mes Parcours',
  'learner.tabs.formations': 'Mes Formations',
  'learner.tabs.publicFormations': 'Formations publiques',
  'learner.tabs.webinars': 'Webinaires',
  'learner.tabs.podcasts': 'Podcasts',
  'learner.tabs.documents': 'Documents',
  'learner.tabs.progress': 'Ma Progression',
  'learner.tabs.profile': 'Profil',
  'learner.sidebar.tagline': 'Apprenant',
  'learner.sidebar.webinars': 'Webinaires',
  'learner.webinars.title': 'Mes webinaires',
  'learner.webinars.loading': 'Chargement de vos webinaires…',
  'learner.webinars.empty':
    'Aucun webinaire pour le moment. Les invitations reçues et les sessions où vous êtes animateur apparaîtront ici.',
  'learner.webinars.sectionInvited': 'Invitations reçues',
  'learner.webinars.sectionPresenting': 'Mes sessions en tant qu’animateur',
  'learner.webinars.emptyInvited': 'Aucune invitation pour le moment.',
  'learner.webinars.emptyPresenting': 'Vous n’êtes animateur sur aucun webinaire.',
  'learner.webinars.rolePresenter': 'Animateur',
  'learner.webinars.joinAsPresenter': 'Ouvrir la salle (animateur)',
  'learner.webinars.join': 'Rejoindre la salle',
  'learner.webinars.invitedOn': 'Invité le',
  'learner.webinars.scheduledFor': 'Prévu le',
  'learner.webinars.duration': 'Durée : {{duration}}',
  'learner.webinars.loadError': 'Impossible de charger les webinaires.',
  'learner.publicFormations.title': 'Formations publiques',
  'learner.publicFormations.subtitle':
    'Formations en accès public proposées par votre établissement.',
  'learner.publicFormations.loading': 'Chargement des formations publiques…',
  'learner.publicFormations.loadError': 'Impossible de charger les formations publiques.',
  'learner.publicFormations.empty': 'Aucune formation ne correspond à votre recherche.',
  'learner.publicFormations.emptyNoInstitution':
    'Aucune formation publique disponible pour votre établissement pour le moment.',
  'learner.publicFormations.searchPlaceholder': 'Rechercher une formation…',
  'learner.publicFormations.open': 'Ouvrir la formation',
  'learner.webinars.status.draft': 'Brouillon',
  'learner.webinars.status.scheduled': 'Planifié',
  'learner.webinars.status.live': 'En direct',
  'learner.webinars.status.ended': 'Terminé',
  'learner.sidebar.myProfile': 'Mon profil',
  'learner.sidebar.logout': 'Déconnexion',
  'learner.sidebar.collapse': 'Réduire le menu',
  'learner.sidebar.expandAria': 'Agrandir la barre latérale',
  'learner.sidebar.collapseAria': 'Réduire la barre latérale',
  'learner.sidebar.backToTrainer': 'Tableau de bord formateur',
  'learner.sidebar.backToTrainerAria': 'Retour au tableau de bord formateur',
  'learner.home.defaultName': 'apprenant·e',
  'learner.home.greeting': 'Bonjour {{name}},',
  'learner.home.greetingSubtitle':
    'Reprenez votre apprentissage là où vous vous êtes arrêté·e.',
  'learner.home.resumeMyFormation': 'Reprendre ma formation',
  'learner.home.globalProgress': 'Progression globale',
  'learner.home.learningTime': "Temps d'apprentissage",
  'learner.home.badges': 'Badges obtenus',
  'learner.home.calendarKicker': 'Ma progression',
  'learner.home.quickAccess': 'Accès rapides',
  'learner.learningPath': 'Parcours',
  'learner.home.resumeTraining': 'Reprendre vos formations',
  'learner.home.viewAll': 'Voir tout →',
  'learner.home.trainingDomains': 'Domaines de formation',
  'learner.home.courses': 'cours',
  'learner.home.episodes': 'épisodes',
  'learner.home.recommendedPodcasts': 'Podcasts recommandés',
  'learner.home.explore': 'Explorer →',
  'learner.formations.title': 'Mes Formations',
  'learner.formations.searchPlaceholder': 'Rechercher une formation…',
  'learner.formations.all': 'Tous',
  'learner.formations.formation': 'Formation',
  'learner.formations.inProgress': 'En cours',
  'learner.formations.completed': 'Terminé',
  'learner.formations.notStarted': 'Non commencé',
  'learner.formations.loading': 'Chargement de vos formations...',
  'learner.formations.empty': 'Aucune formation trouvée.',
  'learner.formations.progress': 'Progression',
  'learner.formations.instructor': 'Formateur',
  'learner.formations.opening': 'Ouverture...',
  'learner.formations.review': 'Revoir',
  'learner.formations.start': 'Commencer',
  'learner.formations.continue': 'Continuer',
  'learner.podcasts.title': 'Explorer les Podcasts',
  'learner.podcasts.searchPlaceholder': 'Rechercher un podcast…',
  'learner.podcasts.all': 'Tous',
  'learner.podcasts.assigned': 'Podcast assigné',
  'learner.podcasts.defaultCategory': 'Podcast',
  'learner.podcasts.loading': 'Chargement de vos podcasts...',
  'learner.podcasts.empty': 'Aucun podcast assigné.',
  'learner.podcasts.episodes': 'épisodes',
  'learner.podcasts.opening': 'Ouverture...',
  'learner.podcasts.listen': 'Écouter',
  'learner.documents.title': 'Documents partagés',
  'learner.documents.loading': 'Chargement de vos documents...',
  'learner.documents.view': 'Consulter le document',
  'learner.documents.empty': 'Aucun document partagé.',
  'learner.documents.personalTitle': 'Mes documents personnels',
  'learner.documents.personalHint': 'Vos documents personnels ne sont visibles que par vous. Cliquez sur un document pour le consulter et poser des questions à l\'assistant IA.',
  'learner.documents.personalEmpty': 'Aucun document personnel. Déposez un PDF pour commencer.',
  'learner.documents.upload': 'Déposer un document',
  'learner.documents.delete': 'Supprimer',
  'learner.documents.deleteConfirmTitle': 'Supprimer ce document ?',
  'learner.documents.deleteConfirmDesc': 'Cette action est irréversible. Le document et son analyse IA seront supprimés.',
  'learner.documents.cancel': 'Annuler',
  'learner.documents.confirmDelete': 'Supprimer',
  'learner.documents.chatReady': 'Chat disponible',
  'learner.documents.processing': 'Analyse en cours…',
  'learner.documents.backToList': 'Retour aux documents',
  'learner.documents.chatTitle': 'Posez vos questions sur ce document',
  'learner.documents.chatPlaceholder': 'Posez une question sur ce document…',
  'learner.documents.chatPlaceholderHint': 'Posez n\'importe quelle question sur ce document. L\'assistant ne répondra qu\'à partir de son contenu.',
  'learner.documents.chatError': 'Une erreur est survenue.',
  'learner.documents.preview': 'Aperçu',
  'learner.documents.indexing': 'Analyse de votre document en cours…',
  'learner.progress.title': 'Ma Progression',
  'learner.progress.completedFormations': 'Formations terminées',
  'learner.progress.trainingHours': 'Heures de formation',
  'learner.progress.listenedPodcasts': 'Podcasts écoutés',
  'learner.progress.earnedBadges': 'Badges obtenus',
  'learner.progress.weeklyActivity': 'Activité de la semaine',
  'learner.progress.badges': 'Badges',
  'learner.progress.recentActivity': 'Activité récente',
  'learner.progress.rarity.common': 'Commun',
  'learner.progress.rarity.rare': 'Rare',
  'learner.progress.rarity.epic': 'Épique',
  'learner.progress.rarity.legendary': 'Légendaire',
  'learner.progress.days.mon': 'Lun',
  'learner.progress.days.tue': 'Mar',
  'learner.progress.days.wed': 'Mer',
  'learner.progress.days.thu': 'Jeu',
  'learner.progress.days.fri': 'Ven',
  'learner.progress.days.sat': 'Sam',
  'learner.progress.days.sun': 'Dim',
  'learner.profile.title': 'Mon Profil',
  'learner.profile.defaultName': 'Apprenant',
  'learner.profile.defaultEmail': 'apprenant@exemple.com',
  'learner.profile.memberSince': 'Membre depuis janvier 2024',
  'learner.profile.editProfile': 'Modifier le profil',
  'learner.profile.formations': 'Formations',
  'learner.profile.hours': 'Heures',
  'learner.profile.podcasts': 'Podcasts',
  'learner.profile.badges': 'Badges',
  'learner.profile.preferences': 'Préférences',
  'learner.profile.theme': 'Thème',
  'learner.profile.themeLight': 'Clair',
  'learner.profile.themeDark': 'Sombre',
  'learner.profile.themeSystem': 'Système',
  'learner.profile.language': 'Langue',
  'learner.profile.notifications': 'Notifications',
  'learner.profile.fontSize': 'Taille du texte',
  'learner.profile.fontNormal': 'Normal',
  'learner.profile.fontLarge': 'Grand',
  'learner.profile.highContrast': 'Contraste élevé',
  'learner.profile.a11ySection': 'Accessibilité',
  'learner.profile.dangerZone': 'Zone de danger',
  'learner.profile.logoutBtn': 'Se déconnecter',
  'learner.parcours.title': 'Mes Parcours',
  'learner.parcours.assigned': 'parcours assigné(s)',
  'learner.parcours.loading': 'Chargement de vos parcours…',
  'learner.parcours.loadError': 'Impossible de charger vos parcours.',
  'learner.parcours.noAssigned': 'Aucun parcours assigné',
  'learner.parcours.noAssignedDesc': 'Votre formateur vous assignera des parcours ici.',
  'learner.parcours.completedBadge': 'Terminé ✓',
  'learner.parcours.by': 'Par',
  'learner.parcours.contents': 'contenu(s)',
  'learner.parcours.completedItems': 'complété(s)',
  'learner.parcours.emptyPath': 'Aucun contenu dans ce parcours.',
  'learner.parcours.locked': 'Terminez le précédent d\'abord',
  'learner.parcours.review': 'Revoir',
  'learner.parcours.listen': 'Écouter',
  'learner.parcours.open': 'Ouvrir',
  'learner.modal.loading': 'Chargement…',
  'learner.modal.noEpisodes': 'Aucun épisode disponible.',
  'learner.modal.markedDone': '✓ Marqué comme terminé',
  'learner.modal.listenThenMark': 'Écoutez puis marquez comme terminé',
  'learner.modal.alreadyDone': 'Déjà terminé',
  'learner.modal.markDone': 'Marquer comme terminé',
  'learner.modal.formationDone': '✓ Formation terminée',
  'learner.modal.completeThenMark': 'Complétez la formation puis marquez-la comme terminée',
  'learner.modal.formationAlreadyDone': 'Déjà terminée',
  'learner.modal.markFormationDone': 'Marquer comme terminée',
  'learner.modal.formationInstruction': 'Suivez les modules de cette formation dans l\'ordre, puis marquez-la comme terminée pour progresser dans votre parcours.',
  'learner.modal.launchFormation': 'Lancer la formation',
  'learner.modal.openFormationHint':
    'Ouvrez la formation dans un nouvel onglet, puis marquez-la comme terminée dans le parcours.',
  'learner.home.loadingHome': 'Chargement de votre accueil…',
  'learner.home.noFormationsAssigned':
    'Aucune formation assignée pour le moment. Elles apparaîtront ici dès qu’un formateur vous en attribuera.',
  'learner.home.loadingPaths': 'Chargement de vos parcours…',
  'learner.home.noPathsAssigned':
    'Aucun parcours assigné pour le moment. Ils apparaîtront ici dès qu’un formateur vous en attribuera.',
  'learner.home.byTheme': 'Vos contenus par thème',
  'learner.home.themesHint':
    'Les thèmes apparaissent à partir des catégories de vos formations et podcasts assignés.',
  'learner.home.categoryOther': 'Autres',
  'learner.home.contentsCount_one': '{{count}} contenu',
  'learner.home.contentsCount_other': '{{count}} contenus',
  'learner.home.viewThemeFormations': 'Voir les {{count}} formations du thème',
  'learner.home.viewThemePodcasts': 'Voir les {{count}} podcasts du thème',
  'learner.home.resumeSubtitleProgress': 'Progression {{percent}} %',
  'learner.home.resumeSubtitleNotStarted': 'Pas encore commencé',
  'learner.home.podcastsAssigned': 'Podcasts assignés',
  'learner.home.viewAllPodcasts': 'Tout voir →',
  'learner.home.gamificationHomeHint':
    'Le détail des badges et de l’activité est dans l’onglet « Ma progression ».',
  'learner.home.gamificationHomeCta': 'Ouvrir ma progression',
  'learner.podcasts.mineTitle': 'Mes podcasts',
  'learner.podcasts.episodeCount_one': '{{count}} épisode',
  'learner.podcasts.episodeCount_other': '{{count}} épisodes',
  'learner.progress.loading': 'Chargement de votre progression…',
  'learner.progress.statPodcastsCompleted': 'Podcasts terminés',
  'learner.progress.statTimeFormationsHours': 'Temps sur les formations (h)',
  'learner.progress.statAssignedContents': 'Contenus assignés',
  'learner.progress.weeklyEstimated': 'Activité des 7 derniers jours (estimée)',
  'learner.progress.badgesSoon':
    'Les badges et récompenses seront bientôt disponibles dans votre espace.',
  'learner.progress.gamificationSection': 'Niveau et expérience',
  'learner.progress.gamificationLevelLine': 'Niveau {{level}} — {{name}}',
  'learner.progress.gamificationXpToNext': 'Encore {{xp}} XP pour le niveau suivant',
  'learner.progress.rewardFormation': 'Formation terminée',
  'learner.progress.rewardPathItem': 'Étape de parcours',
  'learner.progress.rewardPathComplete': 'Parcours terminé',
  'learner.progress.rewardDesignerBlocks': 'Points formation (blocs)',
  'learner.progress.rewardDesignerPerfect': 'Bonus score parfait',
  'learner.progress.rewardDesignerBadge': 'Badge formation',
  'learner.progress.rewardDesignerBadgeNoXp': 'Sans points XP (récompense visuelle)',
  'learner.progress.badgesEmpty':
    'Complétez une formation ou un parcours pour gagner des points et voir vos récompenses ici.',
  'learner.progress.plusXp': '+{{xp}} XP',
  'learner.progress.noRecentHint':
    'Aucune activité récente enregistrée. Ouvrez une formation ou un podcast pour alimenter ce fil.',
  'learner.progress.hoursShort': '{{hours}} h',
  'learner.profile.statFormationsRatio': 'Formations (terminées / total)',
  'learner.profile.statTimeFormations': 'Temps formations (h)',
  'learner.profile.statPodcastsAssigned': 'Podcasts assignés',
  'learner.profile.statPathsAssigned': 'Parcours assignés',
  'learner.profile.memberSincePrefix': 'Membre depuis {{date}}',
  'learner.parcours.playPath': 'Suivre ce parcours',
  'learner.parcours.playPathTitle': 'Ouvre le parcours en plein écran dans un nouvel onglet',
  'learner.parcours.steps_zero': 'Aucune étape',
  'learner.parcours.steps_one': '1 étape',
  'learner.parcours.steps_other': '{{count}} étapes',
  'learner.parcours.doneBadge': 'Terminé',
  'learner.parcours.completedSingle': '{{done}}/{{total}} complétée',
  'learner.parcours.completedPlural': '{{done}}/{{total}} complétées',
  'learner.parcours.byAuthor': 'Par {{firstName}} {{lastName}}',
  'learner.parcours.pathListAria': 'Liste de vos parcours',
  'learner.parcours.assignedCount_one': '{{count}} parcours assigné',
  'learner.parcours.assignedCount_other': '{{count}} parcours assignés',

  'learner.pathPlayer.loading': 'Chargement du parcours…',
  'learner.pathPlayer.errorGeneric': 'Erreur',
  'learner.pathPlayer.pathNotFound': 'Parcours introuvable',
  'learner.pathPlayer.loadError': 'Impossible de charger le parcours.',
  'learner.pathPlayer.backToPaths': 'Retour aux parcours',
  'learner.pathPlayer.showTocAria': 'Afficher le sommaire',
  'learner.pathPlayer.prevActivityAria': 'Activité précédente',
  'learner.pathPlayer.prev': 'Précédent',
  'learner.pathPlayer.nextActivityAria': 'Activité suivante',
  'learner.pathPlayer.next': 'Suivant',
  'learner.pathPlayer.closePlayerTitle': 'Fermer et retourner aux parcours',
  'learner.pathPlayer.close': 'Fermer',
  'learner.pathPlayer.congrats': 'Félicitations',
  'learner.pathPlayer.pathCompleteTitle': 'Parcours terminé',
  'learner.pathPlayer.pathCompleteBody':
    'Vous avez terminé toutes les activités du parcours « {{title}} ».',
  'learner.pathPlayer.activitiesCompleted_one': '{{count}} activité accomplie',
  'learner.pathPlayer.activitiesCompleted_other': '{{count}} activités accomplies',
  'learner.pathPlayer.seeMyPaths': 'Voir mes parcours',
  'learner.pathPlayer.closeTocAria': 'Fermer le sommaire',
  'learner.pathPlayer.tocNavAria': 'Sommaire du parcours',
  'learner.pathPlayer.pathTypeLabel': 'Parcours de formation',
  'learner.pathPlayer.noDescription': 'Aucune description.',
  'learner.pathPlayer.activityCount_zero': 'Aucune activité',
  'learner.pathPlayer.activityCount_one': '1 activité',
  'learner.pathPlayer.activityCount_other': '{{count}} activités',
  'learner.pathPlayer.progressPercent': 'Progression {{percent}}%',
  'learner.pathPlayer.quit': 'Quitter',
  'learner.pathPlayer.summary': 'Sommaire',
  'learner.pathPlayer.outsideModule': 'Hors module',
  'learner.pathPlayer.moduleContents': 'Contenus du module',
  'learner.pathPlayer.step': 'Étape',
  'learner.pathPlayer.podcastHint':
    'Écoutez l’épisode, puis marquez l’activité comme terminée.',
  'learner.pathPlayer.showChapters': 'Afficher les chapitres',
  'learner.pathPlayer.hideChapters': 'Masquer les chapitres',
  'learner.pathPlayer.showText': 'Afficher le texte',
  'learner.pathPlayer.hideText': 'Masquer le texte',
  'learner.pathPlayer.selectEpisode': 'Sélectionnez un épisode pour commencer.',
  'learner.pathPlayer.loadingShort': 'Chargement…',
  'learner.pathPlayer.noEpisodes': 'Aucun épisode disponible.',
  'learner.pathPlayer.listenThenMark': 'Écoutez puis marquez comme terminé',
  'learner.pathPlayer.updating': 'Mise à jour…',
  'learner.pathPlayer.alreadyDone': 'Déjà terminé',
  'learner.pathPlayer.markComplete': 'Marquer comme terminé',
  'learner.pathPlayer.nextStepBtn': 'Étape suivante',
  'learner.pathPlayer.embeddedFormationHint':
    'La formation s’affiche ci-dessous dans le parcours. Utilisez le sommaire à gauche pour les étapes détaillées.',
  'learner.pathPlayer.loadingFormation': 'Chargement de la formation…',
  'learner.pathPlayer.loadingReader': 'Chargement du lecteur…',
  'learner.pathPlayer.emptyPathTitle': 'Ce parcours ne contient pas encore d’activités.',
  'learner.pathPlayer.emptyPathDesc':
    'Le contenu sera ajouté par votre organisme de formation. Vous pouvez fermer cette fenêtre et revenir plus tard.',
  'learner.pathPlayer.pickActivity': 'Choisissez une activité dans le sommaire pour commencer.',
  'learner.pathPlayer.footerStep': 'Étape {{current}} / {{total}}',
  'learner.pathPlayer.unmute': 'Activer le son',
  'learner.pathPlayer.mute': 'Couper le son',

  'learner.activity.flowCompleted': 'Formation « {{title}} » terminée',
  'learner.activity.flowProgress': 'Formation « {{title}} » — {{progress}} %',
  'learner.activity.podcast': 'Podcast « {{title}} »',
  'learner.webinars.durationMin': '{{m}} min',
  'learner.webinars.durationH': '{{h}} h',
  'learner.webinars.durationHM': '{{h}} h {{m}}',
  'learner.formations.durationHours': '{{hours}} h',

  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.dashboard.title': 'Tableau de bord RH',
  'rh.dashboard.subtitle': 'Vue d’ensemble formation & développement des compétences',
  'rh.dashboard.activeCampaigns': 'Campagnes actives',
  'rh.dashboard.pendingAssignmentsKpi': 'Assignations (en attente / acceptées)',
  'rh.dashboard.pendingRequestsCount': 'Demandes en attente',
  'rh.dashboard.completionRateLabel': 'Taux de complétion (parcours)',
  'rh.dashboard.budgetUsed': 'Budget campagnes utilisé',
  'rh.dashboard.budgetNotSet': 'Non renseigné',
  'rh.dashboard.progressLabel': 'Progression (%)',
  'rh.dashboard.chartCampaignsTitle': 'Progression des campagnes',
  'rh.dashboard.chartFormationsTitle': 'Progression des parcours (inscriptions)',
  'rh.dashboard.campaignProgress': 'Avancement des campagnes',
  'rh.dashboard.chartCampaignsHint': 'Taux moyen d’avancement des assignations par campagne.',
  'rh.dashboard.chartFormationsHint': 'Part d’apprenants ayant terminé le parcours (sur inscriptions en cours).',
  'rh.dashboard.upcomingDeadlines': 'Échéances à venir',
  'rh.dashboard.recentActivity': 'Activité récente',
  'rh.dashboard.recentActivityHint': 'Assignations de campagnes et parcours terminés.',
  'rh.dashboard.viewAll': 'Tout voir',
  'rh.dashboard.exportReport': 'Exporter le rapport',
  'rh.dashboard.exportSoon': 'Export disponible prochainement.',
  'rh.dashboard.newCampaign': 'Nouvelle campagne',
  'rh.dashboard.createCampaign': 'Créer une campagne',
  'rh.dashboard.reviewRequests': 'Traiter les demandes',
  'rh.dashboard.skillsMap': 'Référentiel compétences',
  'rh.dashboard.budgetOverview': 'Vue budget',
  'rh.dashboard.loadError': 'Impossible de charger le pilotage RH.',
  'rh.dashboard.emptyChart': 'Aucune donnée à afficher pour cette période.',
  'rh.dashboard.emptyDeadlines': 'Aucune échéance de campagne à venir.',
  'rh.dashboard.emptyRecent': 'Aucune activité récente.',
  'rh.dashboard.kindAssignment': 'Campagne',
  'rh.dashboard.kindCompletion': 'Parcours terminé',
  'rh.dashboard.justNow': "À l'instant",
  'rh.dashboard.learnerInsightTitle': 'Pilotage formations (données réelles)',
  'rh.dashboard.learnerInsightHint':
    'Inscriptions aux formations agrégées : scores issus des blocs notés, friction par bloc, dernières évaluations.',
  'rh.dashboard.enrollmentsTotal': 'Inscriptions',
  'rh.dashboard.completionGlobal': 'Complétés',
  'rh.dashboard.avgTimePerEnrollment': 'Temps moyen / inscription',
  'rh.dashboard.notStarted': 'Non démarrés',
  'rh.dashboard.stalled': 'En pause (>30 j. sans activité après début)',
  'rh.dashboard.topFlowsDetail': 'Parcours les plus suivis',
  'rh.dashboard.colFlow': 'Parcours',
  'rh.dashboard.colEnrolled': 'Inscrits',
  'rh.dashboard.colCompletedPct': 'Complétion',
  'rh.dashboard.colAvgTime': 'Temps moyen',
  'rh.dashboard.colAvgScore': 'Score moy.',
  'rh.dashboard.blockFrictionTitle': 'Blocs les plus frictionnels (sessions ≥3)',
  'rh.dashboard.colBlock': 'Bloc',
  'rh.dashboard.colSessions': 'Sessions',
  'rh.dashboard.colBlockCompletion': 'Complétion bloc',
  'rh.dashboard.recentEvalTitle': 'Dernières évaluations',
  'rh.dashboard.evalComment': 'Commentaire',
  'rh.dashboard.exportOk': 'Export CSV téléchargé.',
  'rh.dashboard.exportFail': 'Échec du téléchargement.',
  'institution.dashboard.trainingCardTitle': 'Suivi formations (inscriptions)',
  'institution.dashboard.trainingCardHint': 'Vue agrégée sur tous les parcours de l’établissement.',
  'institution.dashboard.trainingCompleted': 'parcours terminés',
  'institution.dashboard.trainingAvgMinutes': 'min moy. / inscription',
  'rh.status.urgent': 'Urgent',
  'rh.status.soon': 'Proche',
  'rh.status.upcoming': 'À venir',
  'rh.status.pending': 'En cours',
  'rh.status.approved': 'Terminé',
  'rh.status.rejected': 'Annulé',
  'admin.nav.stats': 'Statistiques',
  'admin.nav.users': 'Utilisateurs',
  'admin.nav.support': 'Support et chat',
  'admin.nav.institutions': 'Établissements',
  'admin.nav.contracts': 'Contrats',
  'admin.nav.editors': 'Éditeurs',
  'admin.nav.podcasts': 'Podcasts',
  'admin.nav.sounds': 'Sons Lyrra',
  'admin.nav.templates': 'Modèles vidéo',
  'admin.nav.templatesImages': 'Modèles d’image',
  'admin.nav.tools': 'Outils',
  'admin.nav.files': 'Fichiers',
  'admin.nav.logs': 'Journaux',
  'admin.nav.webhooks': 'Webhooks',
  'admin.nav.email': 'Email',
  'admin.nav.docusign': 'DocuSign',
  'admin.docusign.title': 'Intégration DocuSign',
  'admin.docusign.subtitle': 'Clé d’intégration, identifiants API et clé privée RSA (JWT). Stockés en base, plus dans Docker.',
  'admin.docusign.statusOk': 'Configuré',
  'admin.docusign.statusIncomplete': 'Incomplet',
  'admin.docusign.keyStored': 'Clé privée enregistrée',
  'admin.docusign.consentHint':
    'Pour la première utilisation JWT, le compte DocuSign doit avoir accepté le consentement d’application (URL fournie par DocuSign). En cas d’erreur 401/403, vérifiez la clé PEM, la clé d’intégration et ce consentement.',
  'admin.docusign.loading': 'Chargement…',
  'admin.docusign.loadError': 'Impossible de charger la configuration DocuSign',
  'admin.docusign.saved': 'Paramètres DocuSign enregistrés',
  'admin.docusign.saveError': 'Enregistrement impossible',
  'admin.docusign.save': 'Enregistrer',
  'admin.docusign.reload': 'Recharger',
  'admin.docusign.fieldIntegrationKey': 'Clé d’intégration (ID client / client ID)',
  'admin.docusign.fieldUserId': 'Identifiant utilisateur DocuSign (GUID)',
  'admin.docusign.fieldAccountId': 'Identifiant de compte (account ID)',
  'admin.docusign.fieldBasePath': 'URL de base de l’API REST',
  'admin.docusign.fieldOauthHost': 'Hôte OAuth (sans https://)',
  'admin.docusign.fieldRsa': 'Clé privée RSA (PEM)',
  'admin.docusign.oauthPlaceholder': 'account-d.docusign.com',
  'admin.docusign.rsaPlaceholderKeep': 'Laisser vide pour conserver la clé actuelle',
  'admin.docusign.rsaPlaceholderNew': 'Collez le fichier .pem complet (BEGIN … END)',
  'admin.smtp.cardTitle': 'Envoi des emails (SMTP)',
  'admin.smtp.subtitle':
    'Hôte, port, authentification et expéditeur. Stocké en base — plus de variables Docker SMTP_* ni EMAIL_FROM.',
  'admin.smtp.host': 'Serveur SMTP (hôte)',
  'admin.smtp.port': 'Port',
  'admin.smtp.secure': 'TLS/SSL (port 465)',
  'admin.smtp.user': 'Utilisateur',
  'admin.smtp.password': 'Mot de passe',
  'admin.smtp.passwordPlaceholder': 'Laisser vide pour conserver le mot de passe actuel',
  'admin.smtp.passwordPlaceholderNew': 'Mot de passe SMTP',
  'admin.smtp.fromEmail': 'Email expéditeur',
  'admin.smtp.fromName': 'Nom affiché (From)',
  'admin.smtp.hint': 'La plupart des fournisseurs exigent utilisateur + mot de passe. Testez via les outils admin (email de test) après enregistrement.',
  'admin.smtp.save': 'Enregistrer SMTP',
  'admin.smtp.saved': 'Paramètres SMTP enregistrés',
  'admin.smtp.saveError': 'Enregistrement impossible',
  'admin.smtp.loadError': 'Impossible de charger SMTP',
  'admin.smtp.loading': 'Chargement…',
  'admin.smtp.configured': 'Paramètres enregistrés',
  'admin.smtp.notConfigured': 'À compléter',
  'admin.nav.smtp': 'Configuration SMTP',
  'admin.nav.helpCenter': "Centre d'aide",
  'admin.nav.platform': 'Intégrations plateforme',
  'admin.users.adminScopeTitle': 'Périmètre du tableau d’administration',
  'admin.users.adminScopeFull': 'Accès complet (tous les onglets)',
  'admin.users.adminScopeRestricted': 'Limiter aux onglets cochés',
  'admin.users.adminScopeHint':
    'Un compte restreint ne voit que les sections sélectionnées. Après modification des onglets, la personne doit se reconnecter (ou rafraîchir sa session) pour que le menu et les appels API reflètent le changement.',
  'admin.users.adminScopeNeedOne': 'Cochez au moins un onglet, ou choisissez l’accès complet.',
  'admin.helpCenter.title': "Centre d'aide",
  'admin.helpCenter.subtitle':
    'Rubriques affichées en tête sur /aide et /help : catégorie, titre, texte d’introduction et lien (chemin /… ou URL https) ouvert au clic sur la fiche.',
  'admin.helpCenter.reload': 'Actualiser',
  'admin.helpCenter.loadError': 'Chargement impossible.',
  'admin.helpCenter.saveError': 'Enregistrement impossible.',
  'admin.helpCenter.deleteError': 'Suppression impossible.',
  'admin.helpCenter.saved': 'Enregistré',
  'admin.helpCenter.deleted': 'Supprimé',
  'admin.helpCenter.catCreated': 'Catégorie créée',
  'admin.helpCenter.topicCreated': 'Fiche créée',
  'admin.helpCenter.catTitleRequired': 'Titre de catégorie obligatoire.',
  'admin.helpCenter.topicFieldsRequired': 'Titre et lien formation obligatoires.',
  'admin.helpCenter.newCategory': 'Nouvelle catégorie',
  'admin.helpCenter.createCategory': 'Nouvelle catégorie',
  'admin.helpCenter.editCategory': 'Modifier la catégorie',
  'admin.helpCenter.createTopic': 'Nouvelle fiche',
  'admin.helpCenter.editTopic': 'Modifier la fiche',
  'admin.helpCenter.fieldTitle': 'Titre',
  'admin.helpCenter.fieldDescription': 'Description',
  'admin.helpCenter.fieldIconKey': 'Icône Lucide',
  'admin.helpCenter.fieldIconKeyHint':
    'Choisissez une icône ; un aperçu s’affiche dans la liste et sur le bouton.',
  'admin.helpCenter.fieldFormationUrl': 'Lien formation',
  'admin.helpCenter.formationPickPlaceholder': '— Choisir une formation publique —',
  'admin.helpCenter.formationPickHint':
    'Seuls les parcours publiés avec l’accès public sans compte activé sont proposés.',
  'admin.helpCenter.formationPickLoading': 'Chargement des parcours…',
  'admin.helpCenter.publicFlowsLoadError': 'Impossible de charger la liste des parcours publics.',
  'admin.helpCenter.legacyFormationPickLabel': 'Lien enregistré (hors liste)',
  'admin.helpCenter.fieldSort': 'Ordre',
  'admin.helpCenter.fieldActive': 'Visible',
  'admin.helpCenter.save': 'Enregistrer',
  'admin.helpCenter.edit': 'Modifier',
  'admin.helpCenter.addTopic': 'Fiche',
  'admin.helpCenter.empty': 'Aucune catégorie. Créez une catégorie puis des fiches.',
  'admin.helpCenter.noTopics': 'Aucune fiche dans cette catégorie.',
  'admin.helpCenter.badgeActive': 'Actif',
  'admin.helpCenter.badgeInactive': 'Masqué',
  'admin.helpCenter.iconLabel': 'Icône',
  'admin.helpCenter.confirmDeleteCategory': 'Supprimer cette catégorie et toutes ses fiches ?',
  'admin.helpCenter.confirmDeleteTopic': 'Supprimer cette fiche ?',
  'admin.nav.formationMigration': 'Migration formations',
  'admin.nav.formationDuplicate': 'Duplication formations',
  'admin.nav.lyrraStudioFormations': 'Formations LyrraStudio',
  'admin.lyrraStudioFormations.title': 'Formations gérées par LyrraStudio',
  'admin.lyrraStudioFormations.subtitle':
    'Assignez un parcours à LyrraStudio : le propriétaire technique est réassigné (compte défini par PLATFORM_EDUFLOW_OWNER_USER_ID ou premier administrateur plateforme), les collaborateurs sont retirés, et seuls les administrateurs peuvent modifier le parcours dans le designer.',
  'admin.lyrraStudioFormations.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.lyrraStudioFormations.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.lyrraStudioFormations.search': 'Rechercher',
  'admin.lyrraStudioFormations.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.lyrraStudioFormations.results': 'Résultats',
  'admin.lyrraStudioFormations.noResults': 'Aucun parcours trouvé.',
  'admin.lyrraStudioFormations.badge': 'LyrraStudio',
  'admin.lyrraStudioFormations.detailHint':
    'Le créateur et les anciens collaborateurs perdent l’accès en édition. Les apprenants et le lecteur public ne sont pas impactés.',
  'admin.lyrraStudioFormations.assign': 'Assigner à LyrraStudio',
  'admin.lyrraStudioFormations.alreadyAssigned': 'Déjà assignée à LyrraStudio',
  'admin.lyrraStudioFormations.confirm':
    'Confirmer l’assignation à LyrraStudio ? Cette action est irréversible depuis cet écran : le parcours ne pourra plus être modifié que par les administrateurs plateforme.',
  'admin.lyrraStudioFormations.success': 'Parcours « {{title}} » : assignation LyrraStudio effectuée.',
  'admin.lyrraStudioFormations.assignedListTitle': 'Parcours déjà assignés à LyrraStudio',
  'admin.lyrraStudioFormations.assignedListEmpty':
    'Aucun parcours pour l’instant. Utilisez la recherche ci-dessous pour en assigner un.',
  'admin.lyrraStudioFormations.refreshList': 'Actualiser la liste',
  'admin.lyrraStudioFormations.openDesigner': 'Designer',
  'admin.lyrraStudioFormations.assignSectionTitle': 'Rechercher et assigner un autre parcours',
  'admin.lyrraStudioFormations.publicPolicyHint':
    'Les parcours assignés à LyrraStudio sont automatiquement publiés, accessibles sans compte (lien /f/…) et adaptés au partage (QR, iframe centre d’aide).',
  'admin.lyrraStudioFormations.drawerTitle': 'Lien public & QR code',
  'admin.lyrraStudioFormations.publicShortLink': 'Lien court (recommandé)',
  'admin.lyrraStudioFormations.publicLongLink': 'Lien lecteur public (UUID)',
  'admin.lyrraStudioFormations.copy': 'Copier',
  'admin.lyrraStudioFormations.copied': 'Copié dans le presse-papiers.',
  'admin.lyrraStudioFormations.copyFailed': 'Impossible de copier (navigateur ou permissions).',
  'admin.lyrraStudioFormations.publicLinksUnavailable':
    'Liens indisponibles : publiez l’accès public ou utilisez « Mettre à jour l’accès public » si le parcours date d’avant cette règle.',
  'admin.lyrraStudioFormations.publicIncomplete': 'Accès public incomplet',
  'admin.lyrraStudioFormations.publicRepairHint':
    'Ce parcours est géré LyrraStudio mais n’a pas encore les réglages publics complets (publication, accès anonyme, code court).',
  'admin.lyrraStudioFormations.publicRepairCta': 'Mettre à jour l’accès public',
  'admin.lyrraStudioFormations.publicRepairOk': 'Accès public appliqué pour « {{title}} ».',
  'admin.formationDuplicate.title': 'Dupliquer une formation vers un autre établissement',
  'admin.formationDuplicate.subtitle':
    'Recherchez un parcours, choisissez l’établissement et le compte propriétaire de la copie. Le parcours d’origine reste inchangé. La copie est un brouillon (sans catalogue, sessions ni collaborateurs).',
  'admin.formationDuplicate.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.formationDuplicate.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.formationDuplicate.search': 'Rechercher',
  'admin.formationDuplicate.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.formationDuplicate.results': 'Résultats',
  'admin.formationDuplicate.noResults': 'Aucun parcours trouvé.',
  'admin.formationDuplicate.currentOwner': 'Propriétaire actuel',
  'admin.formationDuplicate.currentInstitution': 'Établissement d’origine',
  'admin.formationDuplicate.targetInstitution': 'Établissement de la copie',
  'admin.formationDuplicate.pickInstitution': 'Choisir un établissement…',
  'admin.formationDuplicate.newOwner': 'Propriétaire de la copie (formateur / RH)',
  'admin.formationDuplicate.pickOwner': 'Choisir un compte…',
  'admin.formationDuplicate.noCandidates': 'Aucun compte éligible dans cet établissement.',
  'admin.formationDuplicate.duplicate': 'Dupliquer la formation',
  'admin.formationDuplicate.confirm':
    'Créer une copie de cette formation pour le compte sélectionné ? Le parcours source ne sera pas modifié.',
  'admin.formationDuplicate.success':
    'Copie créée : « {{title}} » pour {{email}}. Identifiant du nouveau parcours : {{newFlowId}}.',
  'admin.formationDuplicate.loadInstitutionsError': 'Impossible de charger la liste des établissements.',
  'admin.formationDuplicate.loadCandidatesError': 'Impossible de charger les comptes éligibles.',
  'admin.formationMigration.title': 'Migrer une formation entre établissements',
  'admin.formationMigration.subtitle':
    'Recherchez un parcours EduFlow, choisissez l’établissement cible et un compte pouvant en être propriétaire (formateur, RH, etc.). Les collaborateurs et catégories du parcours sont retirés ; les liens catalogue, campagnes et compétences de l’ancien établissement sont coupés.',
  'admin.formationMigration.searchLabel': 'Recherche (titre ou ID du parcours)',
  'admin.formationMigration.searchPlaceholder': 'Ex. onboarding ou UUID…',
  'admin.formationMigration.search': 'Rechercher',
  'admin.formationMigration.searchMin': 'Saisissez au moins 2 caractères.',
  'admin.formationMigration.results': 'Résultats',
  'admin.formationMigration.noResults': 'Aucun parcours trouvé.',
  'admin.formationMigration.currentOwner': 'Propriétaire actuel',
  'admin.formationMigration.currentInstitution': 'Établissement d’origine',
  'admin.formationMigration.targetInstitution': 'Établissement cible',
  'admin.formationMigration.pickInstitution': 'Choisir un établissement…',
  'admin.formationMigration.newOwner': 'Nouveau propriétaire (formateur / RH)',
  'admin.formationMigration.pickOwner': 'Choisir un compte…',
  'admin.formationMigration.noCandidates': 'Aucun compte éligible dans cet établissement.',
  'admin.formationMigration.migrate': 'Migrer la formation',
  'admin.formationMigration.confirm':
    'Confirmer la migration ? Les collaborateurs et catégories seront retirés, les liens RH/catalogue incohérents seront supprimés ou déliés.',
  'admin.formationMigration.success': 'Migration effectuée : « {{title}} » est désormais attribuée à {{email}}.',
  'admin.formationMigration.clearedCatalog': 'Lien fiche catalogue retiré (fiche d’un autre établissement).',
  'admin.formationMigration.loadInstitutionsError': 'Impossible de charger la liste des établissements.',
  'admin.formationMigration.loadCandidatesError': 'Impossible de charger les comptes éligibles.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels (invitations, réinitialisations, etc.).',
  'support.chat.title': 'Support Lyrra',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'admin.support.title': 'Conversations support',
  'admin.support.loadError': 'Impossible de charger les conversations.',
  'admin.support.replyError': "Impossible d'envoyer la réponse.",
  'admin.support.selectThread': 'Sélectionnez une conversation pour voir les messages.',
  'admin.support.replyPlaceholder': 'Votre réponse à l’utilisateur…',
  'admin.support.sendReply': 'Envoyer',
  'admin.support.refresh': 'Actualiser',
  'admin.support.user': 'Utilisateur',
  'admin.support.subject': 'Sujet',
  'admin.support.status': 'Statut',
  'admin.support.priority': 'Priorité',
  'admin.support.updated': 'Mise à jour',
  'admin.support.messages': 'Fil de discussion',
  'admin.support.newestFirst': 'plus récent en haut',
  'admin.support.allStatuses': 'Tous les statuts',
  'admin.support.linearCreate': 'Créer un ticket Linear',
  'admin.support.linearOpen': 'Ouvrir dans Linear',
  'admin.support.linearError': 'Impossible de créer le ticket Linear. Vérifiez LINEAR_API_KEY sur le serveur.',
  // ── Injected from locales/fr.json ──
  'producerAssignments.createInDesigner': 'Créer / ouvrir formation',
  'producerAssignments.draft': 'Brouillon',
  'producerAssignments.enterprise': 'Entreprise',
  'producerAssignments.enterprisePaths': 'Parcours entreprise',
  'producerAssignments.episodes': 'ép.',
  'producerAssignments.formations': 'Fiches formation',
  'producerAssignments.markCompleted': 'Marquer terminé',
  'producerAssignments.markInProgress': 'En production',
  'producerAssignments.markReady': 'Prêt',
  'producerAssignments.modulesCatalog': '{{count}} module(s) catalogue · mis à jour {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Aucune demande de parcours entreprise.',
  'producerAssignments.noneFormations': 'Aucune fiche assignée.',
  'producerAssignments.nonePodcasts': 'Aucun podcast assigné.',
  'producerAssignments.openInDesigner': 'Ouvrir dans le designer',
  'producerAssignments.openPodcasts': 'Studio podcast',
  'producerAssignments.planningDeadline': 'Échéance {{date}}',
  'producerAssignments.planningFrom': 'À partir du {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts à produire',
  'producerAssignments.productionStatus.completed': 'Terminé',
  'producerAssignments.productionStatus.in_progress': 'En cours',
  'producerAssignments.productionStatus.not_started': 'Pas commencé',
  'producerAssignments.productionStatus.published': 'Publié',
  'producerAssignments.publishedCatalog': 'Publié catalogue',
  'producerAssignments.rhPlanning': 'Planning RH',
  'producerAssignments.subtitle': 'Formations à créer dans le designer et podcasts à produire, assignés par la RH.',
  'producerAssignments.title': 'Mes missions de production',
  'podcasts.actions.cancel': 'Annuler',
  'podcasts.actions.close': 'Fermer',
  'podcasts.actions.create': 'Créer',
  'podcasts.actions.edit': 'Modifier',
  'podcasts.actions.retry': 'Réessayer',
  'podcasts.actions.save': 'Enregistrer',
  'podcasts.actions.saving': 'Sauvegarde…',
  'podcasts.actions.share': 'Partager',
  'podcasts.category.ariaGroup': 'Catégorie du podcast',
  'podcasts.category.emptyBanner': 'Ajoutez des catégories dans l\'espace Institution pour mieux organiser les podcasts.',
  'podcasts.category.emptyInstitutionHint': 'Aucune catégorie institution pour l\'instant — vous pouvez publier sans.',
  'podcasts.category.noneChip': 'Sans catégorie',
  'podcasts.category.sectionTitle': 'Catégorie',
  'podcasts.category.withCategoriesHint': 'Classement dans le catalogue et filtres côté institution.',
  'podcasts.cover.ariaDropzone': 'Choisir ou déposer une image de couverture',
  'podcasts.cover.browseFiles': 'parcourir les fichiers',
  'podcasts.cover.cancelNewFile': 'Annuler le nouveau fichier',
  'podcasts.cover.dragDrop': 'Glissez-déposez une image',
  'podcasts.cover.dropHere': 'Déposez l\'image ici',
  'podcasts.cover.errorMaxSize': 'L\'image ne doit pas dépasser 5 Mo.',
  'podcasts.cover.errorUnsupportedFormat': 'Format d\'image non supporté.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max 5 Mo',
  'podcasts.cover.help': 'Format carré recommandé. Glissez-déposez un fichier ou cliquez pour parcourir.',
  'podcasts.cover.or': 'ou',
  'podcasts.cover.replaceHint': 'Remplacer l\'image',
  'podcasts.cover.sectionTitle': 'Image de couverture',
  'podcasts.deleteDialog.body': '« {{title}} » sera mis en corbeille. Les épisodes rattachés uniquement à ce podcast seront supprimés (audio inclus). Les épisodes partagés avec d’autres podcasts seront conservés. Un administrateur peut restaurer le podcast depuis la corbeille Institution.',
  'podcasts.deleteDialog.confirm': 'Mettre en corbeille',
  'podcasts.deleteDialog.title': 'Mettre le podcast en corbeille ?',
  'podcasts.editPanel.createdOn': 'Créé le ',
  'podcasts.editPanel.headerTitle': 'Modifier le podcast',
  'podcasts.editPanel.saving': 'Enregistrement…',
  'podcasts.editPanel.statsEpisodes': 'Épisodes : ',
  'podcasts.editPanel.statsSubscribers': 'Abonnés : ',
  'podcasts.editPanel.successSaved': 'Modifications enregistrées',
  'podcasts.episodeStatus.draft': 'Brouillon',
  'podcasts.episodeStatus.published': 'Publié',
  'podcasts.episodesPanel.addEpisode': 'Ajouter un épisode',
  'podcasts.episodesPanel.emptySubtitle': 'Ajoutez des épisodes à ce podcast',
  'podcasts.episodesPanel.emptyTitle': 'Aucun épisode',
  'podcasts.episodesPanel.episodeCount': '{{count}} épisode(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Aucun épisode disponible',
  'podcasts.episodesPanel.remove': 'Retirer',
  'podcasts.episodesPanel.searchPlaceholder': 'Rechercher un épisode…',
  'podcasts.episodesPanel.title': 'Épisodes du podcast',
  'podcasts.pdfImport.openButton': 'Importer un document',
  'podcasts.pdfImport.pickPodcastTitle': 'Choisir le podcast cible',
  'podcasts.pdfImport.noPodcast': 'Créez d’abord un podcast non archivé pour importer un document.',
  'podcasts.pdfImport.noPodcastMatch': 'Aucun podcast ne correspond à la recherche.',
  'podcasts.list.actionImportDocument': 'Importer un document (PDF ou Markdown)',
  'podcasts.pdfImport.title': 'Importer un document (PDF ou Markdown)',
  'podcasts.pdfImport.subtitle': 'Créer les épisodes du podcast « {{podcast}} » à partir de la structure du document',
  'podcasts.pdfImport.hint':
    'Le document est découpé automatiquement : Titre 1 / # → épisode, Titre 2 / ## → chapitre, paragraphes → blocs. Les PDF sont d’abord convertis en Markdown pour relecture et validation. Formats : PDF ou .md.',
  'podcasts.pdfImport.convertingPdf': 'Conversion du PDF en Markdown…',
  'podcasts.pdfImport.markdownReviewTitle': 'Valider le Markdown généré',
  'podcasts.pdfImport.markdownReviewSubtitle':
    'Podcast « {{podcast}} » — Markdown généré depuis votre PDF. Relisez, corrigez les titres (# / ##) puis validez.',
  'podcasts.pdfImport.markdownReviewHint':
    'Vérifiez que chaque épisode commence par # et chaque chapitre par ##. Vous pouvez corriger le texte avant de continuer.',
  'podcasts.pdfImport.markdownEditorLabel': 'Contenu Markdown',
  'podcasts.pdfImport.markdownEditorHint': '# Épisode · ## Chapitre · paragraphes = blocs de script',
  'podcasts.pdfImport.validateMarkdown': 'Valider et voir la structure',
  'podcasts.pdfImport.editMarkdown': 'Modifier le Markdown',
  'podcasts.pdfImport.markdownEmpty': 'Le Markdown est vide.',
  'podcasts.pdfImport.dropLabel': 'Déposez un PDF ou un fichier .md, ou choisissez un fichier',
  'podcasts.pdfImport.chooseFile': 'Choisir un fichier',
  'podcasts.pdfImport.analyzing': 'Analyse du document…',
  'podcasts.pdfImport.pageCount': '{{count}} page(s)',
  'podcasts.pdfImport.lineCount': '{{count}} ligne(s)',
  'podcasts.pdfImport.stats': '{{episodes}} épisode(s) · {{chapters}} chapitre(s) · {{blocks}} section(s)',
  'podcasts.pdfImport.episodeLabel': 'Épisode {{index}} — {{title}}',
  'podcasts.pdfImport.sectionsCount': '{{count}} section(s)',
  'podcasts.pdfImport.confirm': 'Créer les épisodes',
  'podcasts.pdfImport.importing': 'Création en cours…',
  'podcasts.pdfImport.previewError': 'Impossible d’analyser ce document.',
  'podcasts.pdfImport.importError': 'Échec de l’import.',
  'podcasts.pdfImport.invalidFile': 'Seuls les fichiers PDF et Markdown (.md) sont acceptés.',
  'podcasts.pdfImport.success': '{{episodes}} épisode(s), {{chapters}} chapitre(s) et {{blocks}} section(s) créés.',
  'podcasts.pdfImport.hintEditorPage':
    'Après sélection du fichier, vous serez redirigé vers l’éditeur Markdown avec un panneau d’aide à la structure (épisodes, chapitres, alertes).',
  'podcasts.pdfImport.editorPageTitle': 'Préparer l’import du document',
  'podcasts.pdfImport.editorPageSubtitle': 'Podcast « {{podcast}} » — {{file}}',
  'podcasts.pdfImport.editorEmptyHint':
    'Choisissez un PDF ou un fichier Markdown pour commencer. Vous pourrez corriger la structure avant de créer les épisodes.',
  'podcasts.pdfImport.backToPodcasts': 'Retour aux podcasts',
  'podcasts.pdfImport.replaceFile': 'Changer de fichier',
  'podcasts.pdfImport.documentTitleLabel': 'Titre du document',
  'podcasts.pdfImport.untitledDocument': 'Sans titre',
  'podcasts.pdfImport.invalidRoute': 'Podcast introuvable.',
  'podcasts.pdfImport.fixErrorsBeforeImport': 'Corrigez les erreurs de structure (panneau de droite) avant de créer les épisodes.',
  'podcasts.pdfImport.structurePanelTitle': 'Aide à la conception',
  'podcasts.pdfImport.structurePanelSubtitle': 'Estimation et alertes en temps réel pendant l’édition.',
  'podcasts.pdfImport.estimatedStructure': 'Structure estimée',
  'podcasts.pdfImport.statEpisodes': 'Épisodes',
  'podcasts.pdfImport.statChapters': 'Chapitres',
  'podcasts.pdfImport.statBlocks': 'Blocs',
  'podcasts.pdfImport.noPreviewYet': 'Saisissez ou importez du contenu pour analyser la structure.',
  'podcasts.pdfImport.markdownMeta': '{{lines}} lignes · {{h1}} titre(s) # · {{h2}} titre(s) ##',
  'podcasts.pdfImport.structureBlocked': 'Des erreurs bloquent l’import — corrigez le Markdown.',
  'podcasts.pdfImport.structureReady': 'Structure exploitable — vous pouvez créer les épisodes.',
  'podcasts.pdfImport.structureIssues': 'Alertes de structure',
  'podcasts.pdfImport.goToLine': 'Aller à la ligne {{line}} dans l’éditeur',
  'podcasts.pdfImport.suggestions': 'Suggestions',
  'podcasts.pdfImport.outlinePreview': 'Aperçu de l’arborescence',
  'podcasts.pdfImport.conceptionGuide': 'Guide de structuration',
  'podcasts.pdfImport.guideH1': '# Titre 1 = un épisode du podcast (ex. chapitre de livre, partie de cours).',
  'podcasts.pdfImport.guideH2': '## Titre 2 = chapitre à l’intérieur de l’épisode (sections, sous-parties).',
  'podcasts.pdfImport.guideBlocks': 'Paragraphes et listes = blocs de narration lus à la voix.',
  'podcasts.pdfImport.guidePdf':
    'Chaque PDF est différent : relisez toujours le Markdown généré et ajustez les # / ## avant l’import.',
  'podcasts.error.retry': 'Réessayer',
  'podcasts.filters.allCategories': 'Toutes catégories',
  'podcasts.filters.categoryLabel': 'Catégorie',
  'podcasts.filters.searchLabel': 'Recherche texte',
  'podcasts.filters.searchPlaceholder': 'Titre, description…',
  'podcasts.filters.viewCategories': 'Vue catégories',
  'podcasts.filters.viewList': 'Vue liste',
  'podcasts.form.descriptionLabel': 'Description',
  'podcasts.form.descriptionPlaceholder': 'Décrivez votre podcast…',
  'podcasts.form.namePlaceholder': 'Nom du podcast',
  'podcasts.form.titleLabel': 'Titre',
  'podcasts.episodeOrder.label': 'Ordre des épisodes',
  'podcasts.episodeOrder.help': 'Ordre de lecture dans le podcast (suit l’ordre du document à l’import).',
  'podcasts.episodeOrder.ariaGroup': 'Ordre des épisodes',
  'podcasts.episodeOrder.newestFirst': 'Plus récent → plus ancien',
  'podcasts.episodeOrder.oldestFirst': 'Plus ancien → plus récent',
  'podcasts.language.ariaGroup': 'Langue du podcast',
  'podcasts.language.help': 'Parmi les 11 langues de la plateforme — pour cadrer le podcast avec la même locale que le lecteur et l\'interface apprenant.',
  'podcasts.language.label': 'Langue',
  'podcasts.lightbox.ariaClose': 'Fermer',
  'podcasts.lightbox.ariaDialog': 'Aperçu cover podcast',
  'podcasts.list.actionActivate': 'Activer',
  'podcasts.list.actionDeactivate': 'Désactiver',
  'podcasts.list.actionDelete': 'Supprimer',
  'podcasts.list.filterAll': 'Tous',
  'podcasts.list.filterArchived': 'Archivés',
  'podcasts.list.archivePodcast': 'Archiver le podcast',
  'podcasts.list.restoreFromArchive': 'Restaurer depuis les archives',
  'podcasts.list.archiveSuccess': 'Podcast archivé',
  'podcasts.list.archiveError': 'Impossible d’archiver le podcast.',
  'podcasts.list.restoreSuccess': 'Podcast restauré',
  'podcasts.list.restoreError': 'Impossible de restaurer le podcast.',
  'podcasts.list.badgeArchived': 'Archivé',
  'podcasts.list.emptyArchivedNone': 'Aucun podcast archivé.',
  'podcasts.list.emptyAllArchived': 'Tous les podcasts sont archivés. Ouvrez l’onglet « Archivés » pour les retrouver.',
  'podcasts.list.actionManageEpisodes': 'Gérer les épisodes',
  'podcasts.list.actionPreview': 'Aperçu & écoute',
  'podcasts.list.coverPreviewAria': 'Aperçu cover du podcast {{title}}',
  'podcasts.list.createFirst': 'Créer un podcast',
  'podcasts.list.emptySubtitle': 'Créez votre premier podcast pour commencer',
  'podcasts.list.emptyTitle': 'Aucun podcast',
  'podcasts.list.loadError': 'Impossible de charger les podcasts.',
  'podcasts.list.enterpriseBadge': 'Entreprise',
  'podcasts.list.enterpriseNeedTitle': 'Besoin défini par votre entreprise',
  'podcasts.list.episodesShort': '{{count}} ép.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} abon.',
  'podcasts.list.uncategorized': 'Sans catégorie',
  'podcasts.modal.errorRequiredFields': 'Le titre et la description sont requis.',
  'podcasts.modal.errorSaveFailed': 'Erreur lors de la sauvegarde.',
  'podcasts.modal.subtitleNew': 'Renseignez titre, description et options. Vous pourrez ajouter des épisodes ensuite.',
  'podcasts.modal.titleEdit': 'Modifier le podcast',
  'podcasts.modal.titleNew': 'Nouveau podcast',
  'podcasts.page.episodesLink': 'Épisodes',
  'podcasts.page.newButton': 'Nouveau',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Page {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) au total',
  'podcasts.player.listen': 'Écouter',
  'podcasts.player.noAudio': 'Pas d\'audio',
  'podcasts.player.pause': 'Pause',
  'podcasts.learner.pageTitle': 'Écouter le podcast',
  'podcasts.learner.invalidLink': 'Lien invalide ou expiré',
  'podcasts.learner.loadError': 'Impossible de charger le podcast',
  'podcasts.learner.loading': 'Chargement du podcast…',
  'podcasts.learner.noEpisodes': 'Aucun épisode publié disponible.',
  'podcasts.learner.listen': 'Écouter',
  'podcasts.learner.pause': 'Pause',
  'podcasts.preview.emptySubtitle': 'Ce podcast n\'a pas encore d\'épisodes',
  'podcasts.preview.emptyTitle': 'Aucun épisode',
  'podcasts.preview.episodeCount': '{{count}} épisode(s)',
  'podcasts.preview.fallbackDescription': 'Écoutez les épisodes audio disponibles.',
  'podcasts.preview.playAll': 'Tout écouter',
  'podcasts.preview.title': 'Aperçu du podcast',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'S’abonner aux nouveautés',
  'podcasts.public.subscribeModalTitle': 'Recevoir les nouveaux épisodes par email',
  'podcasts.public.subscribeModalIntro': 'Indiquez vos coordonnées. Nous vous enverrons un email lors de la publication d’un nouvel épisode.',
  'podcasts.public.fieldFirstName': 'Prénom',
  'podcasts.public.fieldLastName': 'Nom',
  'podcasts.public.fieldEmail': 'Email',
  'podcasts.public.fieldCompany': 'Société (facultatif)',
  'podcasts.public.submitSubscribe': 'Valider l’abonnement',
  'podcasts.public.cancel': 'Annuler',
  'podcasts.public.done': 'Terminé',
  'podcasts.public.subscribeSuccess': 'Merci ! Vous serez informé(e) à chaque nouvel épisode.',
  'podcasts.public.subscribeError': 'Impossible de finaliser l’abonnement. Réessayez plus tard.',
  'podcasts.public.subscribeValidationRequired': 'Le prénom, le nom et l’email sont obligatoires.',
  'podcasts.public.totalDuration': '{{duration}} au total',
  'podcasts.public.listenExcerpt': "Écouter l'extrait",
  'podcasts.playback.resumeAt': 'Reprise à {{time}}',
  'podcasts.playback.listened': 'Écouté',
  'podcasts.playback.listenedCount': '{{count}} écouté(s)',
  'podcasts.share.assignedLearnersHeading': '{{count}} apprenant(s) assigné(s)',
  'podcasts.share.copied': 'Copié',
  'podcasts.share.copyLink': 'Copier le lien',
  'podcasts.share.emailPlaceholder': 'Email *',
  'podcasts.share.errorCreateLearner': 'Impossible de créer l\'apprenant.',
  'podcasts.share.errorPublicLink': 'Impossible de générer le lien public.',
  'podcasts.share.errorRemoveLearner': 'Impossible de retirer l\'apprenant.',
  'podcasts.share.errorShare': 'Impossible de partager ce podcast.',
  'podcasts.share.firstNamePlaceholder': 'Prénom *',
  'podcasts.share.generatingLink': 'Génération du lien…',
  'podcasts.share.hideQuickCreate': 'Masquer la création rapide',
  'podcasts.share.lastNamePlaceholder': 'Nom *',
  'podcasts.share.loadingAssigned': 'Chargement…',
  'podcasts.share.loadingLearners': 'Chargement des apprenants...',
  'podcasts.share.messageAccessRemoved': 'Accès retiré pour l\'apprenant.',
  'podcasts.share.messageCreatedAndShared': 'Compte créé/lié, podcast partagé et email d\'invitation envoyé.',
  'podcasts.share.messageShared': 'Podcast partagé et invitation envoyée à l\'apprenant.',
  'podcasts.share.noAssignedLearners': 'Aucun apprenant assigné pour ce podcast.',
  'podcasts.share.noLearnerAvailable': 'Aucun apprenant disponible.',
  'podcasts.share.panelTitle': 'Partager le podcast',
  'podcasts.share.publicLinkSection': 'Lien public (sans connexion)',
  'podcasts.share.removeAccessTitle': 'Retirer l\'accès',
  'podcasts.share.searchLearnerPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'podcasts.share.selectExistingLearnerLabel': 'Sélectionner un apprenant existant',
  'podcasts.share.showQuickCreate': 'Créer un nouvel apprenant',
  'podcasts.share.submitCreateAndInvite': 'Créer, partager et envoyer l\'invitation',
  'podcasts.share.socialNetworksHeading': 'Partager sur les réseaux',
  'podcasts.share.shareLinkedIn': 'Partager sur LinkedIn',
  'podcasts.share.shareFacebook': 'Partager sur Facebook',
  'podcasts.share.shareTwitter': 'Partager sur X (Twitter)',
  'podcasts.share.publicShareStatsHeading': 'Statistiques page publique',
  'podcasts.share.publicShareStatsVisits': 'Visites (page publique)',
  'podcasts.share.publicShareStatsLoading': 'Chargement des statistiques…',
  'podcasts.share.publicShareStatsUnavailable': 'Statistiques indisponibles pour le moment.',
  'podcasts.share.publicShareStatsLinkedIn': 'LinkedIn',
  'podcasts.share.publicShareStatsFacebook': 'Facebook',
  'podcasts.share.publicShareStatsTwitter': 'X (Twitter)',
  'podcasts.share.publicSubscribersHeading': 'Abonnés (notifications email)',
  'podcasts.share.publicSubscribersEmpty': 'Aucun abonné pour l’instant.',
  'podcasts.share.publicSubscribersCompany': 'Entreprise',
  'share.publicQrTitle': 'QR code du lien public',
  'share.publicQrError': 'Impossible de générer le QR code.',
  'share.downloadQrPng': 'Télécharger en PNG',
  'podcasts.status.active': 'Actif',
  'podcasts.status.inactive': 'Inactif',
  'learningPaths.card.contents': '{{count}} contenu(s)',
  'learningPaths.card.delete': 'Supprimer',
  'learningPaths.card.edit': 'Modifier',
  'learningPaths.card.learners': '{{count}} apprenant(s)',
  'learningPaths.card.publish': 'Publier',
  'learningPaths.card.unpublish': 'Dépublier',
  'learningPaths.card.updatedAt': 'Mis à jour {{date}}',
  'learningPaths.createPanel.cancel': 'Annuler',
  'learningPaths.createPanel.description': 'Donnez un nom à votre parcours ; vous pourrez y ajouter podcasts et formations.',
  'learningPaths.createPanel.descriptionLabel': 'Description',
  'learningPaths.createPanel.descriptionPlaceholder': 'Objectif du parcours, public visé…',
  'learningPaths.createPanel.nameLabel': 'Nom du parcours',
  'learningPaths.createPanel.namePlaceholder': 'Ex. : Parcours d\'onboarding RH…',
  'learningPaths.createPanel.submit': 'Créer le parcours',
  'learningPaths.createPanel.title': 'Nouveau parcours',
  'learningPaths.deleteDialog.body': 'Vous allez supprimer « {{title}} ». Cette action est irréversible.',
  'learningPaths.deleteDialog.cancel': 'Annuler',
  'learningPaths.deleteDialog.confirm': 'Supprimer',
  'learningPaths.deleteDialog.title': 'Supprimer le parcours',
  'learningPaths.empty.createFirst': 'Créer un parcours',
  'learningPaths.empty.noPathsHint': 'Créez votre premier parcours en combinant podcasts et formations.',
  'learningPaths.empty.noPathsYet': 'Aucun parcours pour l\'instant',
  'learningPaths.empty.noResults': 'Aucun résultat',
  'learningPaths.empty.noResultsHint': 'Modifiez vos filtres ou créez un nouveau parcours.',
  'learningPaths.error.loadFailed': 'Impossible de charger les parcours',
  'learningPaths.error.retry': 'Réessayer',
  'learningPaths.filters.allStatuses': 'Tous les statuts',
  'learningPaths.filters.draft': 'Brouillon',
  'learningPaths.filters.published': 'Publié',
  'learningPaths.filters.searchPlaceholder': 'Rechercher un parcours…',
  'learningPaths.page.newButton': 'Nouveau parcours',
  'learningPaths.page.subtitle': 'Créez des parcours de formation ordonnés',
  'learningPaths.page.title': 'Parcours',
  'learningPaths.page.totalCount': '{{count}} parcours',
  'learningPaths.pagination.next': 'Suivant',
  'learningPaths.pagination.pageOf': 'Page {{page}} / {{total}} · {{count}} parcours',
  'learningPaths.pagination.previous': 'Précédent',
  'learningPaths.status.draft': 'Brouillon',
  'learningPaths.status.published': 'Publié',
  'learningPaths.editor.backToList': 'Retour aux parcours',
  'learningPaths.editor.pathNameLabel': 'Nom du parcours *',
  'learningPaths.editor.idCopyHint': 'Identifiant du parcours (pour le support)',
  'learningPaths.editor.sectionNavAria': 'Sections de l’éditeur de parcours',
  'learningPaths.editor.tabContents': 'Contenus',
  'learningPaths.editor.tabLearners': 'Apprenants',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Copier le lien',
  'webinars.card.exportAttendance': 'Télécharger la présence (CSV)',
  'webinars.card.attendanceExportError': 'Impossible de télécharger le fichier de présence.',
  'webinars.card.delete': 'Supprimer',
  'webinars.card.edit': 'Modifier',
  'webinars.card.openRoom': 'Ouvrir la salle',
  'webinars.card.publicSignupBadge': 'Inscription publique',
  'webinars.deleteDialog.body': 'Supprimer « {{title}} » ? Cette action est irréversible.',
  'webinars.deleteDialog.cancel': 'Annuler',
  'webinars.deleteDialog.confirm': 'Supprimer',
  'webinars.deleteDialog.title': 'Supprimer le webinaire',
  'webinars.eligibility.default': 'Les webinaires ne sont pas disponibles pour votre compte (vérifiez la connexion à l\'API ou contactez votre administrateur).',
  'webinars.eligibility.moduleDisabled': 'Le module webinaires n\'est pas encore activé pour votre établissement. Un administrateur doit cocher « Activer les webinaires » dans Établissement → Webinaires.',
  'webinars.eligibility.noInstitution': 'Aucun établissement n\'est associé à votre compte.',
  'webinars.eligibility.notAuthorized': 'Votre compte n\'est pas dans la liste des animateurs autorisés. Demandez à l\'établissement de vous ajouter (Pilotage webinaires → Animateurs autorisés).',
  'webinars.eligibility.roleNotAllowed': 'Votre rôle ne permet pas d\'animer des webinaires avec ce compte.',
  'webinars.empty.createFirst': 'Créer un webinaire',
  'webinars.empty.noResults': 'Aucun résultat',
  'webinars.empty.noResultsHint': 'Modifiez votre recherche.',
  'webinars.empty.noWebinarsHint': 'Créez une salle et invitez vos participants depuis le panneau latéral.',
  'webinars.empty.noWebinarsYet': 'Aucun webinaire pour l\'instant',
  'webinars.externalInvite.closeButton': 'Fermer',
  'webinars.externalInvite.description': 'Personnes qui ne figurent pas dans la liste des apprenants : chaque adresse reçoit un e-mail avec le lien de la salle et une pièce jointe pour le calendrier.',
  'webinars.externalInvite.done': 'Terminé.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} déjà invité',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} déjà invités',
  'webinars.externalInvite.emailsHint': 'Une adresse par ligne, ou séparées par des virgules. Les doublons et les invitations déjà envoyées sont ignorés.',
  'webinars.externalInvite.emailsLabel': 'Adresses e-mail',
  'webinars.externalInvite.emailsPlaceholder': 'une.adresse@exemple.fr\nautre@domaine.org',
  'webinars.externalInvite.errorMax200': 'Maximum 200 adresses par envoi.',
  'webinars.externalInvite.errorMinOne': 'Saisissez au moins une adresse e-mail valide.',
  'webinars.externalInvite.errorsLabel': 'erreurs : {{errors}}',
  'webinars.externalInvite.sendButton': 'Envoyer les invitations',
  'webinars.externalInvite.sendFailed': 'Échec d\'envoi',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail envoyé',
  'webinars.externalInvite.sentCount_other': '{{count}} e-mails envoyés',
  'webinars.externalInvite.skippedCount_one': '{{count}} ignorée',
  'webinars.externalInvite.skippedCount_other': '{{count}} ignorées',
  'webinars.externalInvite.title': 'Invitations par e-mail',
  'webinars.invite.duplicatesCount_one': '{{count}} déjà invité',
  'webinars.invite.duplicatesCount_other': '{{count}} déjà invités',
  'webinars.invite.errorsLabel': 'erreurs : {{errors}}',
  'webinars.invite.externalButton': 'Hors liste (e-mail)',
  'webinars.invite.failed': 'Échec',
  'webinars.invite.groupSuccess': 'Invitations groupe traitées (e-mails envoyés aux membres).',
  'webinars.invite.inviteButton': 'Inviter',
  'webinars.invite.learnersHelp': 'Recherchez un apprenant de votre établissement : un <strong>e-mail d\'invitation</strong> (lien + agenda) est envoyé à chaque invitation.',
  'webinars.invite.learnersTitle': 'Apprenants',
  'webinars.invite.noCandidates': 'Aucun apprenant à afficher (essayez une autre recherche ou utilisez « Hors liste » pour des e-mails externes).',
  'webinars.invite.noInvitations': 'Aucune invitation enregistrée.',
  'webinars.invite.searchLabel': 'Rechercher un apprenant',
  'webinars.invite.searchPlaceholder': 'Nom ou e-mail…',
  'webinars.invite.searching': 'Recherche…',
  'webinars.invite.sentCount_one': '{{count}} invitation envoyée (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} invitations envoyées (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Invitations envoyées',
  'webinars.page.newButton': 'Nouveau webinaire',
  'webinars.page.searchPlaceholder': 'Rechercher…',
  'webinars.page.subtitle': 'Liste de vos salles — ouvrez le panneau pour créer, modifier ou inviter des participants.',
  'webinars.page.title': 'Webinaires',
  'webinars.panel.backgroundHint':
    'Image derrière le contenu partagé pour les participants : saisissez une URL (https ou http), importez un fichier (enregistré dans Mes images) ou choisissez une image déjà importée. Laissez vide pour le visuel Lyrra intégré.',
  'webinars.panel.backgroundLabel': 'Image de fond (vue participant)',
  'webinars.panel.backgroundPlaceholder': 'https://… ou chemin Mes images',
  'webinars.panel.backgroundUrlLabel': 'Adresse de l’image (URL)',
  'webinars.panel.backgroundUploadTitle': 'Importer une image (Mes images)',
  'webinars.panel.backgroundLibraryTitle': 'Mes images',
  'webinars.panel.backgroundLibraryEmpty':
    'Aucune image pour l’instant — importez ci-dessus ou depuis la page Images du tableau de bord.',
  'webinars.panel.backgroundOpenImagesPage': 'Ouvrir la page Images',
  'webinars.panel.backgroundUseDefault': 'Utiliser le fond Lyrra par défaut',
  'webinars.panel.backgroundUploading': 'Envoi en cours…',
  'webinars.panel.backgroundUploadError': 'Échec de l’envoi de l’image.',
  'webinars.panel.copyLink': 'Copier le lien',
  'webinars.panel.createButton': 'Créer',
  'webinars.panel.createDescription': 'Renseignez les informations puis enregistrez. Vous pourrez inviter des participants ensuite.',
  'webinars.panel.createTitle': 'Nouveau webinaire',
  'webinars.panel.dateLabel': 'Jour (optionnel)',
  'webinars.panel.deleteButton': 'Supprimer',
  'webinars.panel.descriptionLabel': 'Description',
  'webinars.panel.durationError': 'Indiquez une durée au format heures:minutes (ex. 1:00 ou 0:45).',
  'webinars.panel.durationHint': 'Obligatoire si un jour est renseigné. Ex. 1:30 pour 1 h 30, 0:45 pour 45 min.',
  'webinars.panel.durationLabel': 'Durée (h:mm)',
  'webinars.panel.editTitle': 'Webinaire',
  'webinars.panel.loading': 'Chargement…',
  'webinars.panel.openRoom': 'Ouvrir la salle',
  'webinars.panel.presentersLabel': 'Co-présentateurs',
  'webinars.panel.roomCode': 'Code salle :',
  'webinars.panel.timeHint': 'Si vide : 9:00.',
  'webinars.panel.timeLabel': 'Heure de début',
  'webinars.panel.titleLabel': 'Titre',
  'webinars.panel.waitingHtmlLabel': 'HTML salle d\'attente (optionnel)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Bienvenue…</p>',
  'webinars.panel.publicAccessTitle': 'Inscription publique (formulaire)',
  'webinars.panel.publicAccessHelp':
    'Les participants non co-présentateurs remplissent un formulaire après connexion Lyrra : e-mail, mot de passe (qu’ils choisissent) et les champs supplémentaires que vous ajoutez ci-dessous.',
  'webinars.panel.publicFormFixedFieldsHint':
    'E-mail et mot de passe (8 caractères min.) sont toujours demandés aux participants ; vous ajoutez ici d’autres champs optionnels ou obligatoires.',
  'webinars.panel.publicFormAddFieldButton': 'Ajouter un champ',
  'webinars.panel.publicFormFieldNumber': 'Champ {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Libellé affiché',
  'webinars.panel.publicFormFieldType': 'Type',
  'webinars.panel.publicFormFieldTypeText': 'Texte court',
  'webinars.panel.publicFormFieldTypeTextarea': 'Texte long',
  'webinars.panel.publicFormFieldRequired': 'Obligatoire',
  'webinars.panel.publicFormFieldRemove': 'Retirer',
  'webinars.panel.publicFormFieldIdHint': 'Identifiant technique :',
  'webinars.panel.publicFormFieldLabelRequired': 'Chaque champ doit avoir un libellé.',
  'webinars.panel.publicAccessLoginLabel': 'Identifiant du formulaire d’inscription',
  'webinars.panel.publicAccessPasswordLabel': 'Mot de passe du formulaire',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Au moins 8 caractères',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Laisser vide pour conserver le mot de passe actuel',
  'webinars.panel.publicAccessPasswordHint':
    'À la création ou à la première activation : mot de passe obligatoire (8 caractères min.). Ensuite : laissez vide pour ne pas le changer.',
  'webinars.panel.publicAccessLoginRequired': 'Indiquez un identifiant pour l’inscription publique.',
  'webinars.panel.publicAccessPasswordRequired': 'Définissez un mot de passe d’au moins 8 caractères.',
  'webinars.panel.publicAccessPasswordMin': 'Le mot de passe public doit contenir au moins 8 caractères.',
  'webinars.status.draft': 'Brouillon',
  'webinars.status.ended': 'Terminé',
  'webinars.status.live': 'En direct',
  'webinars.status.scheduled': 'Planifié',
  'videos.addPanel.cancel': 'Annuler',
  'videos.addPanel.continue': 'Continuer',
  'videos.addPanel.description': 'Importez MP4, WebM ou MOV pour les utiliser dans vos formations.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Cliquer pour choisir une vidéo',
  'videos.addPanel.title': 'Ajouter une vidéo',
  'videos.addPanel.titleLabel': 'Titre',
  'videos.addPanel.titlePlaceholder': 'Titre de la vidéo…',
  'videos.card.delete': 'Supprimer',
  'videos.card.edit': 'Modifier',
  'videos.card.noThumbnail': 'Aucune miniature',
  'videos.card.publishedStudio': 'Publié (modifiable Studio)',
  'videos.card.draft': 'Brouillon',
  'videos.deleteDialog.body': '« {{title}} » sera supprimée définitivement.',
  'videos.deleteDialog.cancel': 'Annuler',
  'videos.deleteDialog.confirm': 'Supprimer',
  'videos.deleteDialog.title': 'Supprimer la vidéo ?',
  'videos.empty.addButton': 'Ajouter une vidéo',
  'videos.empty.description': 'Importez votre première vidéo pour la gérer et l\'utiliser dans vos formations.',
  'videos.empty.title': 'Aucune vidéo',
  'videos.page.addButton': 'Ajouter une vidéo',
  'videos.page.count_one': '{{count}} vidéo dans votre bibliothèque',
  'videos.page.count_other': '{{count}} vidéos dans votre bibliothèque',
  'videos.page.title': 'Vidéos',
  'videos.page.studioButton': 'Studio',
  'videos.studio.title': 'Studio vidéo',
  'videos.studio.rail.prep': 'Prépa',
  'videos.studio.rail.capture': 'Capture',
  'videos.studio.rail.post': 'Montage',
  'videos.studio.rail.aria': 'Étapes du studio vidéo',
  'videos.studio.badge.setup': 'Préparation',
  'videos.studio.badge.capture': 'Capture',
  'videos.studio.badge.recording': 'Enregistrement',
  'videos.studio.badge.post': 'Montage',
  'videos.studio.cannotLeaveRecord': 'Arrêtez l’enregistrement avant de changer d’étape.',
  'videos.studio.railNeedRecording': 'Enregistrez une vidéo pour accéder au montage.',
  'videos.studio.sidebar.captureHelp':
    'La capture enregistre l’intégralité du flux partagé (écran, onglet ou fenêtre). Le rognage pour ne garder qu’une zone utile se fera à l’étape Montage.',
  'videos.studio.sidebar.sources': 'Sources',
  'videos.studio.sidebar.recordingLive': 'Enregistrement en cours',
  'videos.studio.sidebar.finalizeHint':
    'Pour enregistrer dans la bibliothèque, utilisez le bouton dans la barre supérieure.',
  'videos.studio.canvas.setupEmpty':
    'Consultez les indications dans le panneau latéral, puis passez à l’étape Capture.',
  'videos.studio.stepSetup': 'Préparation',
  'videos.studio.stepCapture': 'Capture',
  'videos.studio.stepPost': 'Montage',
  'videos.studio.titleLabel': 'Titre de la vidéo',
  'videos.studio.titlePlaceholder': 'Ma capture…',
  'videos.studio.saveSetup': 'Continuer vers la capture',
  'videos.studio.fieldDescription': 'Description',
  'videos.studio.fieldCategories': 'Catégories',
  'videos.studio.categoriesHint':
    'Une ou plusieurs catégories de votre établissement (optionnel).',
  'videos.studio.categoriesEmptyInstitution':
    'Aucune catégorie n’est configurée pour votre établissement.',
  'videos.studio.audioTrackPlanned': 'Piste audio prévue',
  'videos.studio.voiceHumanPlan': 'Voix humaine (micro enregistré)',
  'videos.studio.voiceAiPlan': 'Voix IA (choix pour le montage)',
  'videos.studio.postVoiceHumanKeep':
    'Vous avez choisi la voix humaine : la vidéo conserve l’audio du micro. Passez à la finalisation lorsque vous êtes prêt.',
  'videos.studio.titleUntitled': 'Sans titre',
  'videos.studio.voiceAiNoVoices': 'Aucune voix IA disponible pour le moment.',
  'videos.studio.postVoiceAiPipeline':
    'Voix IA : préparez le texte, générez la piste audio puis appliquez-la à la vidéo.',
  'videos.studio.allowMedia': 'Autoriser écran et microphone',
  'videos.studio.controlsHint':
    'Astuce : pour éviter d’enregistrer cette barre, partagez un onglet ou une fenêtre plutôt que tout l’écran, ou utilisez un second écran. Espace = pause / reprise lorsque cette page a le focus.',
  'videos.studio.warnTabOnly':
    'Vous avez sélectionné un seul onglet : seul cet onglet sera enregistré, même si vous en ouvrez d’autres. Pour capturer plusieurs onglets ou fenêtres, relancez la capture et choisissez « Écran entier ».',
  'videos.studio.templateMenuTitle': 'Menu des modèles',
  'videos.studio.templateEmpty': 'Aucun modèle enregistré',
  'videos.studio.templatesHintCompact': 'Cliquez sur + pour enregistrer, appliquer ou supprimer un modèle.',
  'videos.studio.overlayAddMusic': 'Musique de fond',
  'videos.studio.musicInvalidType': 'Format invalide. Utilisez un audio (MP3, WAV, M4A, OGG).',
  'videos.studio.musicTooLarge': 'Le fichier audio ne doit pas dépasser 50 Mo.',
  'videos.studio.musicDefault': 'Musique',
  'videos.studio.musicAdded': 'Musique de fond ajoutée à la timeline.',
  'videos.studio.musicUploadError': "Impossible d'ajouter la musique.",
  'videos.studio.layerScreenBorder': 'Bordure extérieure',
  'videos.studio.layerScreenBorderSize': 'Taille',
  'videos.studio.layerScreenBorderColor': 'Couleur',
  'videos.studio.layerScreenBorderRadius': 'Arrondi des angles',
  'videos.studio.segmentToolsTitle': 'Outils de section',
  'videos.studio.segmentZoom': 'Zoom',
  'videos.studio.segmentResetView': 'Réinit. vue',
  'videos.studio.segmentTransition': 'Transition',
  'videos.studio.transitionNone': 'Aucune',
  'videos.studio.transitionFade': 'Fondu',
  'videos.studio.transitionWipeLeft': 'Balayage →',
  'videos.studio.transitionSlideUp': 'Glissé ↑',
  'videos.studio.textEditor.toolbar': 'Mise en forme',
  'videos.studio.textEditor.bold': 'Gras',
  'videos.studio.textEditor.italic': 'Italique',
  'videos.studio.textEditor.underline': 'Souligné',
  'videos.studio.textEditor.textColor': 'Couleur du texte (sélection)',
  'videos.studio.textEditor.size': 'Taille',
  'videos.studio.textEditor.font': 'Police',
  'videos.studio.textEditor.fontFamily.system': 'Système',
  'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
  'videos.studio.textEditor.fontFamily.serif': 'Serif',
  'videos.studio.textEditor.fontFamily.mono': 'Mono',
  'videos.studio.textEditor.fontFamily.script': 'Script',
  'videos.studio.controlsHintTab':
    'Vous partagez un autre onglet : la vidéo ne montre pas cette page Studio (les boutons n’y figurent donc pas). Gardez le Studio dans une autre fenêtre du navigateur ou sur un second écran, à côté de votre contenu. Pour Pause, Arrêter ou Terminer, basculez vers la fenêtre où est ouvert le Studio (Alt+Tab ou Cmd+Tab). La touche Espace ne fonctionne que lorsque le focus est sur la page Studio.',
  'videos.studio.controlsHintMonitor':
    'Vous partagez tout l’écran : cette barre peut apparaître dans l’enregistrement. Pour l’éviter, choisissez plutôt « Onglet » ou « Fenêtre » dans le partage, ou placez les contrôles sur un autre moniteur.',
  'videos.studio.controlsHintWindow':
    'Vous partagez une fenêtre : le reste du bureau (et les autres fenêtres) n’est en principe pas enregistré. Utilisez les boutons ci-dessous pour Pause, Arrêt ou Terminer.',
  'videos.studio.startRecord': 'Enregistrer',
  'videos.studio.pause': 'Pause',
  'videos.studio.resume': 'Reprendre',
  'videos.studio.stop': 'Arrêter',
  'videos.studio.terminer': 'Terminer',
  'videos.studio.processingUpload': 'Envoi et transcription…',
  'videos.studio.timeline': 'Lecture',
  'videos.studio.transcript': 'Texte reconnu',
  'videos.studio.transcriptRefining': 'Amélioration de la transcription en cours…',
  'videos.studio.transcriptPlaceholder': 'Le texte reconnu depuis votre enregistrement apparaîtra ici…',
  'videos.studio.voiceMode': 'Piste audio',
  'videos.studio.voiceOriginal': 'Ma voix (micro enregistré)',
  'videos.studio.voiceAi': 'Voix IA (Mistral TTS)',
  'videos.studio.voiceSelect': 'Voix',
  'videos.studio.polish': 'Préparer le texte pour la voix IA',
  'videos.studio.generateTts': 'Générer la voix off',
  'videos.studio.applyVoiceover': 'Appliquer la voix IA à la vidéo',
  'videos.studio.preview': 'Prévisualiser',
  'videos.studio.preview.noFile': 'Aucun fichier vidéo à prévisualiser.',
  'videos.studio.bgImage': 'Image de fond',
  'videos.studio.bgImageChoose': 'Choisir',
  'videos.studio.bgImageInvalidType': 'Format invalide. Utilisez une image (PNG, JPG, WebP).',
  'videos.studio.bgImageTooLarge': "L'image ne doit pas dépasser 10 Mo.",
  'videos.studio.bgImageLoadError': 'Impossible de charger l’image de fond.',
  'videos.studio.bgImageUploading': 'Envoi…',
  'videos.studio.bgImageUploadError': "Impossible d'envoyer l'image. Réessayez.",
  'videos.studio.bgImageLibraryTitle': 'Fond studio vidéo',
  'videos.studio.postLayoutTitle': 'Cadre & fond',
  'videos.studio.postLayoutHint':
    'Aperçu aligné sur la vidéo publiée : enregistrez le brouillon puis publiez pour appliquer fond, taille et position.',
  'videos.studio.bgImageVideoSize': 'Taille vidéo',
  'videos.studio.dragToMove': 'Glisser pour déplacer',
  'videos.studio.finalize': 'Finaliser et enregistrer',
  'videos.studio.saveDraft': 'Brouillon',
  'videos.studio.publish': 'Publier',
  'videos.studio.successDraft': 'Vidéo enregistrée en brouillon',
  'videos.studio.successPublish': 'Vidéo publiée dans votre bibliothèque',
  'videos.studio.processingPublish': 'Traitement de la vidéo en cours…',
  'videos.studio.back': 'Retour aux vidéos',
  'videos.studio.errorMedia': 'Impossible d’accéder à l’écran ou au micro.',
  'videos.studio.errorInsecureContext':
    'La capture nécessite HTTPS ou localhost. Ouvrez l’application via https:// ou http://127.0.0.1 / localhost.',
  'videos.studio.errorNotSupported':
    'Ce navigateur ou appareil ne prend pas en charge la capture d’écran ici. Utilisez Chrome ou Edge à jour sur ordinateur.',
  'videos.studio.errorMicDenied':
    'Micro refusé : autorisez le micro pour ce site (icône cadenas ou ⓘ dans la barre d’adresse), puis réessayez.',
  'videos.studio.errorScreenDenied':
    'Partage d’écran refusé : dans la fenêtre du navigateur, choisissez un écran ou un onglet et validez « Partager ».',
  'videos.studio.errorMicMissing': 'Aucun micro détecté. Vérifiez le branchement et les réglages système.',
  'videos.studio.errorScreenMissing': 'Partage d’écran indisponible sur cet appareil ou ce navigateur.',
  'videos.studio.errorMicBusy': 'Micro déjà utilisé par une autre application. Fermez-la puis réessayez.',
  'videos.studio.errorScreenBusy': 'Capture d’écran impossible (ressource occupée). Réessayez ou redémarrez le navigateur.',
  'videos.studio.errorScreenCancelled': 'Partage d’écran annulé.',
  'videos.studio.errorMicCancelled': 'Accès au micro annulé.',
  'videos.studio.errorMicGeneric': 'Impossible d’accéder au micro.',
  'videos.studio.errorScreenGeneric': 'Impossible de capturer l’écran.',
  'videos.studio.errorScreenEnded': 'Partage d’écran arrêté.',
  'videos.studio.mediaHints':
    'Chrome ou Edge sur ordinateur. Si l’URL est en http:// avec une IP type 192.168.x.x, le navigateur bloque souvent la capture : utilisez http://localhost:5173 ou un HTTPS valide.',
  'videos.studio.errorRecord': 'Enregistrement impossible sur ce navigateur.',
  'videos.studio.errorUpload': 'Échec de l’envoi.',
  'videos.studio.errorLoadDraft': 'Impossible de charger le brouillon.',
  'videos.studio.errorTranscribe': 'Transcription indisponible.',
  'videos.studio.errorPolish': 'Reformulation IA impossible.',
  'videos.studio.errorTts': 'Synthèse vocale impossible.',
  'videos.studio.errorMux': 'Montage audio impossible.',
  'videos.studio.muxSuccess': 'Voix IA appliquée à la vidéo',
  'videos.studio.successFinal': 'Vidéo enregistrée dans votre bibliothèque',
  'videos.studio.generatingTimeline': 'Génération de la timeline…',
  'videos.studio.crop': 'Recadrer',
  'videos.studio.cropReset': 'Réinitialiser le recadrage',
  'videos.studio.cropActive': 'Recadrage actif — déplacez ou redimensionnez la zone sur la vidéo',
  'videos.studio.cropDone': 'Terminer',
  'videos.studio.cropApplied': 'Rognage appliqué',
  'videos.studio.cropEdit': 'Modifier',
  'videos.studio.cropRemove': 'Supprimer',
  'videos.studio.trimIn': 'IN',
  'videos.studio.trimOut': 'OUT',
  'videos.studio.trimDuration': 'durée',
  'videos.preview.noFile': 'Aucun fichier disponible.',
  'videos.status.draft': 'Brouillon',
  'videos.status.published': 'Publié',
  'sounds.card.delete': 'Supprimer',
  'sounds.card.download': 'Télécharger',
  'sounds.card.mute': 'Couper le son',
  'sounds.deleteDialog.body': '« {{title}} » sera supprimé définitivement du serveur et de votre bibliothèque.',
  'sounds.deleteDialog.cancel': 'Annuler',
  'sounds.deleteDialog.confirm': 'Supprimer',
  'sounds.deleteDialog.title': 'Supprimer ce son ?',
  'sounds.empty.description': 'Importez vos fichiers : ils sont enregistrés en base et copiés sur le serveur pour ne rien perdre.',
  'sounds.empty.importButton': 'Importer un son',
  'sounds.empty.title': 'Aucun son importé',
  'sounds.fileSize.bytes': '{{size}} o',
  'sounds.fileSize.kb': '{{size}} Ko',
  'sounds.fileSize.mb': '{{size}} Mo',
  'sounds.importPanel.cancel': 'Annuler',
  'sounds.importPanel.changeFile': 'Changer de fichier',
  'sounds.importPanel.createTag': 'Créer « {{tag}} »',
  'sounds.importPanel.description': 'Ajoutez un fichier audio à votre bibliothèque pour vos parcours.',
  'sounds.importPanel.descriptionLabel': 'Description',
  'sounds.importPanel.descriptionPlaceholder': 'Décrivez ce son…',
  'sounds.importPanel.dropzoneBrowse': 'parcourez',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Glissez un fichier audio ou',
  'sounds.importPanel.optional': '(optionnel)',
  'sounds.importPanel.submit': 'Importer',
  'sounds.importPanel.submitting': 'Envoi…',
  'sounds.importPanel.tagsHint': 'Tapez et appuyez sur Entrée · Les nouveaux tags sont créés automatiquement',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Ajouter des tags…',
  'sounds.importPanel.title': 'Importer un son',
  'sounds.importPanel.titleLabel': 'Titre',
  'sounds.importPanel.titlePlaceholder': 'Nom du son…',
  'sounds.importPanel.titleRequired': 'Le titre est obligatoire',
  'sounds.noResults': 'Aucun son ne correspond à « {{query}} »',
  'sounds.page.count_one': '{{count}} fichier — sauvegardé en base et sur le serveur',
  'sounds.page.count_other': '{{count}} fichiers — sauvegardés en base et sur le serveur',
  'sounds.page.importButton': 'Importer',
  'sounds.page.searchPlaceholder': 'Titre, description, tag…',
  'sounds.page.title': 'Sons',
  'documents.count_one': '{{count}} document',
  'documents.count_other': '{{count}} documents',
  'documents.delete.body': 'Le document « {{filename}} » sera retiré de votre bibliothèque.',
  'documents.delete.cancel': 'Annuler',
  'documents.delete.confirm': 'Supprimer',
  'documents.delete.irreversible': 'Cette action est irréversible.',
  'documents.delete.title': 'Supprimer le document ?',
  'documents.importPanel.cancel': 'Annuler',
  'documents.importPanel.chooseFile': 'Choisir un document PDF',
  'documents.importPanel.description': 'Le document sera disponible dans vos blocs PDF et dans cette bibliothèque.',
  'documents.importPanel.descriptionLabel': 'Description',
  'documents.importPanel.descriptionPlaceholder': 'Description (optionnelle)',
  'documents.importPanel.fileHint': 'Le document sera disponible dans votre bloc PDF et dans cette bibliothèque.',
  'documents.importPanel.submit': 'Importer',
  'documents.importPanel.title': 'Importer un PDF',
  'documents.importPanel.titleLabel': 'Titre',
  'documents.importPanel.titlePlaceholder': 'Titre du document',
  'documents.importPanel.uploading': 'Upload en cours',
  'documents.importPdf': 'Importer un PDF',
  'documents.loading': 'Chargement de la bibliothèque…',
  'documents.newDocument': 'Nouveau document',
  'documents.noDocuments': 'Aucun document',
  'documents.noDocumentsHint': 'Importez vos PDFs ici pour les réutiliser ensuite dans le bloc PDF.',
  'documents.noResults': 'Aucun document ne correspond à votre recherche.',
  'documents.pages_one': '{{count}} page',
  'documents.pages_other': '{{count}} pages',
  'documents.previewTitle': 'Aperçu du document',
  'documents.searchPlaceholder': 'Rechercher un PDF...',
  'documents.share.accessRemoved': 'Accès retiré pour l\'apprenant.',
  'documents.share.assignedTitle': 'Apprenants ayant accès',
  'documents.share.close': 'Fermer',
  'documents.share.createAndShare': 'Créer, partager et envoyer l\'invitation',
  'documents.share.createError': 'Impossible de créer l\'apprenant.',
  'documents.share.createLearner': 'Créer un nouvel apprenant',
  'documents.share.createdSuccess': 'Compte créé/lié, document partagé et email envoyé.',
  'documents.share.creating': 'Envoi...',
  'documents.share.emailPlaceholder': 'Email *',
  'documents.share.firstNamePlaceholder': 'Prénom *',
  'documents.share.hideCreateLearner': 'Masquer la création rapide',
  'documents.share.lastNamePlaceholder': 'Nom *',
  'documents.share.loadingAssigned': 'Chargement…',
  'documents.share.loadingLearners': 'Chargement des apprenants...',
  'documents.share.noAssigned': 'Aucun apprenant assigné pour ce document.',
  'documents.share.noLearners': 'Aucun apprenant disponible.',
  'documents.share.removeButton': 'Retirer',
  'documents.share.removeError': 'Impossible de retirer l\'accès.',
  'documents.share.searchPlaceholder': 'Rechercher un apprenant (nom, prénom, email)',
  'documents.share.selectLearner': 'Sélectionner un apprenant existant',
  'documents.share.shareButton': 'Partager',
  'documents.share.shareError': 'Impossible de partager le document.',
  'documents.share.sharedSuccess': 'Document partagé et invitation envoyée.',
  'documents.share.title': 'Partager le document',
  'documents.subtitle': 'Bibliothèque de documents PDF réutilisable dans le designer.',
  'documents.title': 'Documents',
  'documents.toast.importError': 'Import impossible',
  'documents.toast.importErrorDefault': 'L\'import a échoué.',
  'documents.toast.importSuccess': 'Document importé',
  'documents.toast.importSuccessDesc': '{{filename}} a été ajouté à votre bibliothèque documents.',
  'documents.updating': 'Mise à jour…',
  'help.categories.analytics.description': 'Tableaux de bord, filtres et exports pour piloter l\'impact des formations.',
  'help.categories.analytics.title': 'Analytiques & rapports',
  'help.categories.apprenants.description': 'Suivre les inscriptions, les cohortes et la progression des participants.',
  'help.categories.apprenants.title': 'Apprenants, groupes & suivi',
  'help.categories.boutique.description': 'Vendre ou attribuer des formations lorsque la boutique est configurée.',
  'help.categories.boutique.title': 'Boutique & monétisation',
  'help.categories.demarrage.description': 'Prendre en main l\'interface, votre profil et la navigation au quotidien dans Lyrra.',
  'help.categories.demarrage.title': 'Démarrage & compte',
  'help.categories.donnees.description': 'RGPD, conservation, droits des personnes et bonnes pratiques de sécurité.',
  'help.categories.donnees.title': 'Données personnelles & sécurité',
  'help.categories.eduflow.description': 'Concevoir des parcours pédagogiques avec blocs, connexions et logique de navigation.',
  'help.categories.eduflow.title': 'Parcours & designer EduFlow',
  'help.categories.evaluation.description': 'Quiz, évaluations, sondages et retours pour mesurer les acquis.',
  'help.categories.evaluation.title': 'Évaluation & pédagogie active',
  'help.categories.institution.description': 'Paramétrer votre structure, les collaborateurs et l\'organisation des contenus.',
  'help.categories.institution.title': 'Établissement & équipe',
  'help.categories.marque.description': 'Aligner couleurs, libellés et parcours apprenant sur votre identité.',
  'help.categories.marque.title': 'Marque & expérience utilisateur',
  'help.categories.medias.description': 'Gérer bibliothèque, imports et outils audio / vidéo / image intégrés à Lyrra.',
  'help.categories.medias.title': 'Médias & studio créatif',
  'help.categories.parcours-v2.description': 'Campagnes, compétences, intégrations : tout ce qui étoffe le pilotage formation.',
  'help.categories.parcours-v2.title': 'Fonctions avancées & feuille de route',
  'help.categories.podcasts.description': 'Créer et diffuser des contenus audio longs format hors du parcours bloc par bloc.',
  'help.categories.podcasts.title': 'Podcasts & livres audio',
  'help.categories.publication.description': 'Rendre un parcours disponible aux apprenants et partager les bons accès.',
  'help.categories.publication.title': 'Publication & diffusion',
  'help.categories.qualite.description': 'Cadres qualité français : traçabilité, objectifs et preuves pour les audits.',
  'help.categories.qualite.title': 'Qualité, Qualiopi & conformité pédagogique',
  'help.categories.support.description': 'En cas de blocage technique ou fonctionnel, savoir où chercher et qui contacter.',
  'help.categories.support.title': 'Dépannage & support',
  'help.configHint': 'Contenu éditorial : branchez vos URLs, articles ou base de connaissances quand vous serez prêt.',
  'help.description': 'Documentation et support',
  'help.hubIntro': 'Thématiques couvrant l\'usage de Lyrra : vous pourrez bientôt lier chaque fiche à votre documentation, tutoriels ou tickets support.',
  'help.modal.expectedAnswer': 'Réponse attendue :',
  'help.modal.fallbackHighlight1': 'Fiche',
  'help.modal.fallbackHighlight2': 'Plateforme formation',
  'help.modal.fallbackHighlight3': 'À compléter',
  'help.modal.fallbackSectionHeading': 'À retenir',
  'help.modal.fallbackSectionParagraph': 'Le contenu détaillé de cette fiche sera enrichi ou relié à votre documentation interne.',
  'help.modal.fallbackStep1Desc': 'Utilisez le menu latéral ou le tableau de bord pour accéder au module concerné.',
  'help.modal.fallbackStep1Title': 'Ouvrir la fonctionnalité',
  'help.modal.fallbackStep2Desc': 'Les écrans vous guident : enregistrez souvent et testez en prévisualisation lorsque c\'est disponible.',
  'help.modal.fallbackStep2Title': 'Suivre le flux métier',
  'help.modal.fallbackStep3Desc': 'Notez les spécificités de votre établissement (process RH, nommage, charte) pour votre équipe.',
  'help.modal.fallbackStep3Title': 'Documenter en interne',
  'help.modal.fallbackTip': 'En cas de doute, vérifiez votre rôle (formateur, admin, RH) : les menus affichés en dépendent.',
  'help.modal.quiz': 'Quiz de validation',
  'help.modal.related': 'Voir aussi',
  'help.modal.resources': 'Ressources',
  'help.modal.steps': 'En pratique',
  'help.modal.tips': 'Conseils',
  'help.noResults': 'Aucun résultat pour cette recherche.',
  'help.onboarding.ai-designer': 'Formation — Assistant IA du designer LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Formation — Types de blocs EduFlow (onboarding)',
  'help.onboarding.branches': 'Formation — Branches & conditions EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Formation — Catalogue & visibilité LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Formation — Certificats délivrés LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Formation — Tableau de bord Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Formation — Vue d\'ensemble du concepteur EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Formation — Inscriptions & participants LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Formation — Groupes & cohortes LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Formation — Bibliothèque média LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Formation — Navigation & menu latéral Lyrra (onboarding)',
  'help.onboarding.objectives': 'Formation — Objectifs pédagogiques EduFlow (onboarding)',
  'help.onboarding.profile': 'Formation — Profil & préférences Lyrra (onboarding)',
  'help.onboarding.progress': 'Formation — Progression & temps passé LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Formation — Liens d\'accès & partage LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Formation — Brouillon, publication et mises à jour LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Formation — Rôles et droits d\'accès Lyrra (onboarding)',
  'help.onboarding.validation': 'Formation — Validation du parcours EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Formation introuvable',
  'help.onboardingFlowMissingDesc': 'Exécutez les scripts db:seed:onboarding-eduflow et db:seed:onboarding-navigation-eduflow (backend), ou les variables VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Ouvrir la fiche',
  'help.searchPlaceholder': 'Rechercher une thématique…',
  'help.title': 'Centre d\'aide',
  'help.topics.analytics-exports.description': 'Préparer les extractions pour le reporting interne ou réglementaire.',
  'help.topics.analytics-exports.title': 'Exports & reporting RH',
  'help.topics.analytics-indicateurs.description': 'Taux de complétion, engagement, répartition par statut et temps sur les blocs.',
  'help.topics.analytics-indicateurs.title': 'Indicateurs clés',
  'help.topics.analytics-vue.description': 'Personnaliser graphiques, périodes et filtre par formation pour analyser les données.',
  'help.topics.analytics-vue.title': 'Vue d\'ensemble analytique',
  'help.topics.app-certificats.description': 'Vérifier les attestations générées et les conditions de réussite associées.',
  'help.topics.app-certificats.title': 'Certificats délivrés',
  'help.topics.app-groupes.description': 'Utiliser les groupes pour segmenter les publics et faciliter les campagnes ou rapports.',
  'help.topics.app-groupes.title': 'Groupes & cohortes',
  'help.topics.app-inscriptions.description': 'Consulter la liste des apprenants sur un parcours, leurs statuts et historiques d\'accès.',
  'help.topics.app-inscriptions.title': 'Inscriptions & participants',
  'help.topics.app-progression.description': 'Interpréter la progression, les complétions et le temps d\'engagement dans les blocs.',
  'help.topics.app-progression.title': 'Progression & temps passé',
  'help.topics.boutique-offres.description': 'Lier des parcours à des produits ou codes d\'accès selon votre modèle économique.',
  'help.topics.boutique-offres.title': 'Offres & tarification',
  'help.topics.boutique-paiement.description': 'Comprendre le flux d\'achat côté apprenant et les confirmations côté organisme.',
  'help.topics.boutique-paiement.title': 'Paiements & facturation',
  'help.topics.demarrage-navigation.description': 'Retrouver les espaces Projets, Formations, Médias, Analytiques et Paramètres selon votre rôle.',
  'help.topics.demarrage-navigation.title': 'Navigation & menu latéral',
  'help.topics.demarrage-profil.description': 'Mettre à jour vos informations, la langue d\'interface et les notifications.',
  'help.topics.demarrage-profil.title': 'Profil & préférences',
  'help.topics.demarrage-roles.description': 'Comprendre les différences entre formateur, administrateur d\'établissement, RH et apprenant.',
  'help.topics.demarrage-roles.title': 'Rôles et droits d\'accès',
  'help.topics.demarrage-tableau-bord.description': 'Lire les indicateurs clés, accéder rapidement aux parcours récents et aux raccourcis utiles.',
  'help.topics.demarrage-tableau-bord.title': 'Tableau de bord formation',
  'help.topics.donnees-conservation.description': 'Principes de durée d\'archivage des progressions et des preuves de formation.',
  'help.topics.donnees-conservation.title': 'Durée de conservation',
  'help.topics.donnees-mots-de-passe.description': 'Bonnes pratiques de mots de passe, sessions et délégation d\'accès.',
  'help.topics.donnees-mots-de-passe.title': 'Comptes & authentification',
  'help.topics.donnees-rgpd.description': 'Finalités des données collectées dans Lyrra et responsabilité du responsable de formation.',
  'help.topics.donnees-rgpd.title': 'RGPD & registre des traitements',
  'help.topics.eduflow-blocs.description': 'Choisir et paramétrer les blocs adaptés : texte, vidéo, audio, évaluation, branchement, etc.',
  'help.topics.eduflow-blocs.title': 'Types de blocs (contenu, quiz, médias…)',
  'help.topics.eduflow-branches.description': 'Gérer plusieurs sorties, la convergence des chemins et les règles de navigation.',
  'help.topics.eduflow-branches.title': 'Branches & conditions',
  'help.topics.eduflow-concept.description': 'Canvas, palette de blocs, sélection, zoom et organisation visuelle du parcours.',
  'help.topics.eduflow-concept.title': 'Vue d\'ensemble du concepteur',
  'help.topics.eduflow-ia.description': 'Suggérer ou modifier des blocs à l\'aide de l\'assistant, avec relecture humaine systématique.',
  'help.topics.eduflow-ia.title': 'Assistant IA dans le designer',
  'help.topics.eduflow-objectifs.description': 'Lier des objectifs aux blocs pour structurer le référentiel de votre formation.',
  'help.topics.eduflow-objectifs.title': 'Objectifs pédagogiques',
  'help.topics.eduflow-validation.description': 'Utiliser le panneau Validation pour détecter erreurs et avertissements avant publication.',
  'help.topics.eduflow-validation.title': 'Validation du parcours',
  'help.topics.eval-auto-eval.description': 'Proposer des parcours adaptatifs en fonction des résultats.',
  'help.topics.eval-auto-eval.title': 'Auto-évaluation & remédiation',
  'help.topics.eval-ouverte.description': 'Organiser la collecte de réponses libres et la notation manuelle ou semi-automatisée.',
  'help.topics.eval-ouverte.title': 'Questions ouvertes & correction',
  'help.topics.eval-quiz.description': 'Configurer barèmes, feedbacks et tentatives pour un QCM efficace.',
  'help.topics.eval-quiz.title': 'Quiz & questions à choix',
  'help.topics.inst-collaborateurs.description': 'Ajouter des formateurs, administrateurs ou RH et gérer les permissions.',
  'help.topics.inst-collaborateurs.title': 'Utilisateurs & invitations',
  'help.topics.inst-structure.description': 'Définir ce qui est partagé au niveau établissement versus projets personnels.',
  'help.topics.inst-structure.title': 'Structure & périmètre',
  'help.topics.inst-trash.description': 'Retrouver formations ou podcasts supprimés et les restaurer si besoin.',
  'help.topics.inst-trash.title': 'Corbeille & restauration',
  'help.topics.marque-accessibilite.description': 'Contrastes, alternatives textuelles et navigation clavier dans les contenus.',
  'help.topics.marque-accessibilite.title': 'Accessibilité & lisibilité',
  'help.topics.marque-charte.description': 'Comment la marque se reflète dans le player et les espaces apprenants.',
  'help.topics.marque-charte.title': 'Charte & couleurs institutionnelles',
  'help.topics.marque-landing.description': 'Présenter votre offre de formation côté visiteur lorsque le module est activé.',
  'help.topics.marque-landing.title': 'Pages publiques & catalogue',
  'help.topics.medias-bibliotheque.description': 'Importer, organiser et réutiliser vidéos, sons, images et documents dans vos blocs.',
  'help.topics.medias-bibliotheque.title': 'Bibliothèque média',
  'help.topics.medias-documents.description': 'Joindre des ressources téléchargeables et lier la documentation au parcours.',
  'help.topics.medias-documents.title': 'Documents & PDF',
  'help.topics.medias-studio-audio.description': 'Enregistrer, mixer ou utiliser la synthèse vocale pour enrichir vos contenus.',
  'help.topics.medias-studio-audio.title': 'Studio audio & voix',
  'help.topics.medias-video.description': 'Bonnes pratiques de format, poids et accessibilité pour les lecteurs intégrés.',
  'help.topics.medias-video.title': 'Vidéos & intégrations',
  'help.topics.podcasts-creation.description': 'Structurer des épisodes, métadonnées et couvertures pour votre offre audio.',
  'help.topics.podcasts-creation.title': 'Création d\'un podcast',
  'help.topics.podcasts-diffusion.description': 'Paramètres d\'écoute, partage et suivi d\'usage côté institution.',
  'help.topics.podcasts-diffusion.title': 'Diffusion & apprenants',
  'help.topics.pub-catalogue.description': 'Rattacher une formation au catalogue institutionnel ou à une offre lorsque c\'est activé.',
  'help.topics.pub-catalogue.title': 'Catalogue & visibilité',
  'help.topics.pub-liens.description': 'Générer ou copier les liens pour les apprenants, présentateurs ou prévisualisation.',
  'help.topics.pub-liens.title': 'Liens d\'accès & partage',
  'help.topics.pub-statuts.description': 'Cycle de vie d\'une formation : enregistrer, tester, publier et versionner les changements.',
  'help.topics.pub-statuts.title': 'Brouillon, publication et mises à jour',
  'help.topics.qualite-qualiopi.description': 'Utiliser les espaces dédiés pour préparer les critères et le dossier qualité.',
  'help.topics.qualite-qualiopi.title': 'Pré-audit & indicateurs Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Relier objectifs, contenus et évaluations pour documenter le parcours certifiant.',
  'help.topics.qualite-tracabilite.title': 'Traçabilité pédagogique',
  'help.topics.support-contact.description': 'Canal support, création de ticket et informations à fournir pour un diagnostic rapide.',
  'help.topics.support-contact.title': 'Contacter le support Lyrra',
  'help.topics.support-faq.description': 'Lecteur qui ne charge pas, média manquant, erreur de publication : premiers réflexes.',
  'help.topics.support-faq.title': 'Problèmes fréquents',
  'help.topics.support-navigateur.description': 'Versions recommandées, cookies et extensions qui peuvent interférer.',
  'help.topics.support-navigateur.title': 'Navigateur & compatibilité',
  'help.topics.v2-campagnes.description': 'Planifier le déploiement de parcours auprès de populations ciblées dans le temps.',
  'help.topics.v2-campagnes.title': 'Campagnes & plans de formation',
  'help.topics.v2-competences.description': 'Mapper les blocs ou parcours aux compétences attendues pour le reporting métiers.',
  'help.topics.v2-competences.title': 'Référentiels de compétences',
  'help.topics.v2-integrations.description': 'Échanges avec votre SI-RH, LMS externe ou outils de BI lorsque connecteurs disponibles.',
  'help.topics.v2-integrations.title': 'Intégrations & API',
  'pages.images.cancel': 'Annuler',
  'pages.images.changeFile': 'Changer de fichier',
  'pages.images.copyUrl': 'Copier l\'URL',
  'pages.images.copyUrlSuccess': 'URL copiée dans le presse-papiers.',
  'pages.images.copyUrlError': 'Impossible de copier l’URL.',
  'pages.images.urlLabel': 'URL publique',
  'pages.images.download': 'Télécharger',
  'pages.images.downloadSuccess': 'Téléchargement lancé.',
  'pages.images.downloadError': 'Téléchargement impossible.',
  'pages.images.count_one': '{{count}} image dans votre bibliothèque',
  'pages.images.count_other': '{{count}} images dans votre bibliothèque',
  'pages.images.delete': 'Supprimer',
  'pages.images.deleteAction': 'Supprimer',
  'pages.images.deleteConfirmHint': 'sera supprimée définitivement.',
  'pages.images.deleteTitle': 'Supprimer cette image ?',
  'pages.images.descriptionLabel': 'Description (optionnel)',
  'pages.images.descriptionPlaceholder': 'Description rapide…',
  'pages.images.dropzoneBrowse': 'parcourez',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Glissez une image ou',
  'pages.images.fileSize.bytes': '{{size}} o',
  'pages.images.fileSize.kb': '{{size}} Ko',
  'pages.images.fileSize.mb': '{{size}} Mo',
  'pages.images.fileSizeKb': '{{size}} Ko',
  'pages.images.importButton': 'Importer',
  'pages.images.importSubtitle': 'Ajoutez une image à votre bibliothèque pour vos formations.',
  'pages.images.importTitle': 'Importer une image',
  'pages.images.noImages': 'Aucune image importée',
  'pages.images.noResults': 'Aucun résultat',
  'pages.images.searchPlaceholder': 'Rechercher une image…',
  'pages.images.submit': 'Importer',
  'pages.images.titleLabel': 'Titre (optionnel)',
  'pages.images.titlePlaceholder': 'Nom de l\'image…',
  'pages.images.viewGrid': 'Grille',
  'pages.images.viewList': 'Liste',
  'pages.images.editPanelTitle': 'Modifier l’image',
  'pages.images.editPanelSubtitle':
    'Titre, description et nom de fichier. Remplacer le fichier conserve la même URL (les contenus qui l’utilisent restent valides).',
  'pages.images.replaceFileLabel': 'Nouvelle image',
  'pages.images.replaceFileHint':
    'Le format est adapté à l’extension actuelle du fichier. L’URL publique ne change pas.',
  'pages.images.fileOriginalNameLabel': 'Nom du fichier d’origine',
  'pages.images.saveChanges': 'Enregistrer',
  'pages.images.saveSuccess': 'Image mise à jour.',
  'pages.images.saveError': 'Enregistrement impossible.',
  'pages.images.editAction': 'Modifier',
  // ── Auto-injected missing t() fallbacks ──
  'accessibility.simplifiedTextMode': 'Mode texte simplifié',
  'accessibility.simplifiedTextModeDesc':
    'Simplifie les textes de la page affichée par IA (mise en page conservée). Dans une formation, les textes du parcours sont traités bloc par bloc.',
  'accessibility.simplifiedTextPageLoading': 'Simplification du contenu de cette page par IA…',
  'accessibility.simplifiedTextPageError':
    "Impossible de simplifier le contenu de cette page pour le moment. Réessayez dans un instant.",
  'accessibility.imageHoverAudio': 'Mode malvoyant: lecture audio image au survol',
  'accessibility.imageHoverAudioDesc':
    "Quand une image est survolée, l'IA analyse son contenu visuel (envoi base64) et lit la description à voix haute.",
  'accessibility.imageHoverAudio.loading': 'Chargement de la description audio…',
  'accessibility.imageHoverAudio.loadingTts': 'Génération de la description audio…',
  'accessibility.imageHoverAudio.loadingVision': 'Analyse de l’image et génération audio…',
  'accessibility.imageHoverAudio.playing': 'Lecture de la description…',
  'accessibility.imageHoverAudio.error': 'Lecture audio impossible.',
  'accessibility.imageHoverAudio.unavailable': 'Description audio indisponible pour cette image.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.dashboard.activeToday': 'Actifs aujourd’hui',
  'admin.dashboard.quickActions': 'Actions rapides',
  'admin.shell.collapse': 'Réduire',
  'admin.shell.collapseSidebar': 'Réduire la barre latérale',
  'admin.shell.expandSidebar': 'Agrandir la barre latérale',
  'admin.shell.title': 'Administration',
  'admin.header.workspace': 'Administration plateforme',
  'admin.institutionDetail.pageTitle': 'Établissement — Administration',
  'admin.institutionDetail.backToList': 'Retour aux établissements',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Complétion',
  'campaigns.col.enrolled': 'Inscrits',
  'campaigns.col.name': 'Nom',
  'campaigns.col.period': 'Période',
  'campaigns.col.status': 'Statut',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Directions',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'Date de fin',
  'campaigns.create.error': 'Création impossible.',
  'campaigns.create.individualSearchPlaceholder': 'Rechercher des personnes…',
  'campaigns.create.individuals': 'Personnes',
  'campaigns.create.launch': 'Lancer la campagne',
  'campaigns.create.launchNote':
    'Le lancement préviendra tous les participants et démarrera la campagne à la date de début choisie.',
  'campaigns.create.name': 'Nom de la campagne',
  'campaigns.create.namePlaceholder': 'ex. Leadership 2025',
  'campaigns.create.review': 'Vérification',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Nom',
  'campaigns.create.reviewFieldPeriod': 'Période',
  'campaigns.create.reviewContentItems': 'Contenus',
  'campaigns.create.contentSelected': '{{count}} sélectionné(s)',
  'campaigns.create.selectContent': 'Sélectionner les formations',
  'campaigns.create.startDate': 'Date de début',
  'campaigns.create.steps.audience': 'Public cible',
  'campaigns.create.steps.basic': 'Informations générales',
  'campaigns.create.steps.content': 'Contenu',
  'campaigns.create.steps.review': 'Vérification et lancement',
  'campaigns.create.title': 'Créer une campagne',
  'campaigns.dept.engineering': 'Ingénierie',
  'campaigns.dept.product': 'Produit',
  'campaigns.dept.sales': 'Commerce',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Juridique',
  'campaigns.dept.hr': 'Ressources humaines',
  'campaigns.list.create': 'Nouvelle campagne',
  'campaigns.list.empty': 'Aucune campagne trouvée.',
  'campaigns.list.loadError': 'Impossible de charger les campagnes.',
  'campaigns.list.results': 'campagnes',
  'campaigns.list.search': 'Rechercher des campagnes…',
  'campaigns.list.title': 'Campagnes',
  'campaigns.page.documentTitle': 'Campagnes - LyrraStudio',
  'campaigns.sort.completion': 'Complétion',
  'campaigns.sort.enrolled': 'Inscrits',
  'campaigns.sort.name': 'Nom',
  'campaigns.sort.startDate': 'Date de début',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'Toutes',
  'campaigns.status.cancelled': 'Annulée',
  'campaigns.status.completed': 'Terminée',
  'campaigns.status.draft': 'Brouillon',
  'campaigns.status.paused': 'En pause',
  'campaigns.status.scheduled': 'Planifiée',
  'campaigns.validation.budgetRequired': 'Le budget est obligatoire.',
  'campaigns.validation.endDateRequired': 'La date de fin est obligatoire.',
  'campaigns.validation.nameRequired': 'Le nom de la campagne est obligatoire.',
  'campaigns.validation.startDateRequired': 'La date de début est obligatoire.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Demande de formation — contenus pédagogiques',
  'trainingRequest.intro': 'Décrivez le besoin. Les équipes RH valident ou non la demande ; en cas de validation, elle passe en préparation de contenu.',
  'trainingRequest.fieldTitle': 'Titre de la formation',
  'trainingRequest.fieldDescription': 'Description du besoin',
  'trainingRequest.fieldJustification': 'Justification / objectifs',
  'trainingRequest.fieldDate': 'Date souhaitée',
  'trainingRequest.datePlaceholder': 'ex. T2 2026',
  'trainingRequest.fieldFormateur': 'Formateur de l\'établissement',
  'trainingRequest.formateurPlaceholder': 'Choisir un formateur…',
  'trainingRequest.formateursEmpty': 'Aucun compte formateur listé pour votre établissement.',
  'trainingRequest.titleRequired': 'Indiquez un titre (2 caractères minimum).',
  'trainingRequest.sent': 'Demande enregistrée. Vous serez informé·e par e-mail de la décision.',
  'trainingRequest.sendError': 'Impossible d’enregistrer la demande. Vérifiez votre connexion ou vos droits.',
  'trainingRequest.submit': 'Envoyer la demande',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.collaborators': 'Collaborateurs',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Collaboratif',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.coverLabel': 'Image de couverture',
  'eduflow.list.coverRemove': 'Retirer la couverture',
  'eduflow.list.coverUpload': 'Choisir une image',
  'eduflow.list.flowProperties': 'Titre, description et couverture',
  'eduflow.list.propertiesPanelTitle': 'Propriétés de la formation',
  'eduflow.list.duplicatePanelTitle': 'Dupliquer la formation',
  'eduflow.list.duplicatePanelSubtitle': 'Formation d’origine : {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copie',
  'eduflow.list.duplicateSuccess': 'Formation dupliquée.',
  'eduflow.list.duplicateCoverHint':
    'La couverture et le contenu pédagogique de la formation d’origine seront repris.',
  'eduflow.list.propertiesSaveError': 'Impossible d’enregistrer les propriétés.',
  'eduflow.list.propertiesSaved': 'Propriétés enregistrées.',
  'eduflow.list.propertiesUploadError': 'Échec du téléversement de l’image.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Suivant',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.readComplete': 'Read complete',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory.activateSuccess': 'Version {{label}} activée (publique)',
  'eduflow.versionHistory.activateTitle': 'Activer {{label}} ?',
  'eduflow.versionHistory.confirmActivate': 'Activer',
  'eduflow.versionHistory.copyFailed': 'Copie impossible',
  'eduflow.versionHistory.copyHtml': 'Copier le HTML',
  'eduflow.versionHistory.copyPlain': 'Copier le texte seul',
  'eduflow.versionHistory.createMajor': 'Version majeure',
  'eduflow.versionHistory.createMajorSuccess': 'Version majeure créée',
  'eduflow.versionHistory.createMinor': 'Version mineure',
  'eduflow.versionHistory.createMinorSuccess': 'Version mineure créée',
  'eduflow.versionHistory.deleteError': 'Erreur lors de la suppression',
  'eduflow.versionHistory.deleteSuccess': 'Version supprimée',
  'eduflow.versionHistory.deleteTitle': 'Supprimer {{label}} ?',
  'eduflow.versionHistory.deleteWarning': 'Cette action est irréversible.',
  'eduflow.versionHistory.duplicateConfirm': 'Dupliquer',
  'eduflow.versionHistory.duplicateError': 'Erreur lors de la duplication',
  'eduflow.versionHistory.duplicateMinor': 'Dupliquer (mineure)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Dupliquer {{label}} ?',
  'eduflow.versionHistory.newFromCurrent': 'Nouvelle version à partir du contenu actuel',
  'eduflow.versionHistory.preview': 'Aperçu',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocs',
  'eduflow.versionHistory.previewEdges': '{{count}} connexions',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Aperçu {{label}}',
  'eduflow.versionHistory.setPublic': 'Activer',
  'eduflow.versionHistory.startDescModalTitle': 'Descriptif — bloc Démarrage ({{version}})',
  'eduflow.videoCompleted': 'Video watched',
  'gamification.active': 'actives',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.learners.statsAdaptiveChoice': 'Choix assistes - acceptes: {{accepted}} · refuses: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptations evaluees: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versions simplifiees utilisees: {{count}}',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.manualVoiceoverMic': 'Enregistrer la voix off pour cette langue',
  'player.manualVoiceoverStudioTitle': 'Studio d\u2019enregistrement voix off',
  'player.manualVoiceoverStudioLang': 'Langue de l\u2019enregistrement',
  'player.manualVoiceoverRecord': 'Enregistrer',
  'player.manualVoiceoverStop': 'Arrêter',
  'player.manualVoiceoverPlay': 'Écouter',
  'player.manualVoiceoverSave': 'Enregistrer sur le bloc',
  'player.manualVoiceoverClose': 'Fermer',
  'player.manualVoiceoverHint':
    'À droite : le texte ou l\u2019image du bloc dans la langue choisie. Enregistrez, puis enregistrez sur le bloc pour associer l\u2019audio à cette langue.',
  'player.manualVoiceoverPreviewLoading': 'Chargement du contenu…',
  'player.manualVoiceoverNoMic': 'Micro ou enregistrement indisponible dans ce navigateur.',
  'player.manualVoiceoverNoText': 'Aucun texte de description suffisant pour ce bloc.',
  'player.manualVoiceoverSaveError': 'Impossible d\u2019enregistrer la voix sur le bloc.',
  'player.manualVoiceoverUploadError': 'Échec du téléversement audio.',
  'player.manualVoiceoverSaved': 'Voix off enregistrée sur le bloc.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditDateUnknown': 'Non renseigné',
  'rh.auditOverdue': 'Audit en retard',
  'rh.auditScheduleHint': 'À définir dans les paramètres établissement',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Avancement',
  'rh.compliance': 'Conformité',
  'rh.complianceAuditAction.qualiopi_corrective_action_create': "Création d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_corrective_action_delete': "Suppression d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_corrective_action_update': "Mise à jour d'une action corrective Qualiopi",
  'rh.complianceAuditAction.qualiopi_evidence_create': "Ajout d'une preuve Qualiopi",
  'rh.complianceAuditAction.qualiopi_indicator_update': "Mise à jour d'un indicateur Qualiopi",
  'rh.complianceAuditAction.compliance_regulatory_watch_create': "Création d'un élément de veille réglementaire",
  'rh.complianceAuditAction.compliance_regulatory_watch_update': "Mise à jour d'un élément de veille réglementaire",
  'rh.complianceAuditAction.compliance_regulatory_watch_delete': "Suppression d'un élément de veille réglementaire",
  'rh.complianceCritical': 'Critique : plusieurs critères sont en dessous des seuils acceptables.',
  'rh.complianceGood': 'Votre organisme respecte les exigences Qualiopi. Continuez sur cette lancée.',
  'rh.compliancePage.documentTitle': 'Conformité & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Pilotage de la conformité réglementaire et de la certification Qualiopi',
  'rh.compliancePage.tabs.dashboard': 'Tableau de bord',
  'rh.compliancePage.tabs.difficulty': 'Difficultés',
  'rh.compliancePage.tabs.handicap': 'Référent handicap',
  'rh.compliancePage.tabs.learnerBilan': 'Bilan apprenants',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.tabs.regulatory': 'Veille réglementaire',
  'rh.regulatoryWatch.lyrraBadge': 'Lyrra Studio',
  'rh.regulatoryWatch.ofResponse': 'Notre réponse / positionnement',
  'rh.regulatoryWatch.ofResponsePlaceholder': 'Ce que nous avons fait ou prévu…',
  'rh.regulatoryWatch.ackStatus': 'Statut',
  'rh.regulatoryWatch.ack.pending': 'À traiter',
  'rh.regulatoryWatch.ack.read': 'Pris en compte (lu)',
  'rh.regulatoryWatch.ack.actionTaken': 'Action menée',
  'rh.regulatoryWatch.ack.notApplicable': 'Non applicable',
  'rh.regulatoryWatch.pdf.title': 'Registre de veille réglementaire',
  'rh.regulatoryWatch.pdf.subtitleFallback': 'Établissement',
  'rh.regulatoryWatch.pdf.generatedAt': 'Document généré le {{date}}',
  'rh.regulatoryWatch.pdf.colDate': 'Date',
  'rh.regulatoryWatch.pdf.colCategory': 'Cat.',
  'rh.regulatoryWatch.pdf.colTitle': 'Titre',
  'rh.regulatoryWatch.pdf.colDesc': 'Description (extrait)',
  'rh.regulatoryWatch.pdf.colImpact': 'Impact',
  'rh.regulatoryWatch.pdf.colOrigin': 'Origine',
  'rh.regulatoryWatch.pdf.colStatus': 'Statut',
  'rh.regulatoryWatch.pdf.colAction': 'Action',
  'rh.regulatoryWatch.pdf.colWho': 'Utilisateur',
  'rh.regulatoryWatch.pdf.colWhen': 'Horodatage',
  'rh.regulatoryWatch.pdf.originLyrra': 'Lyrra Studio',
  'rh.regulatoryWatch.pdf.originOf': 'OF',
  'rh.regulatoryWatch.pdf.readYes': 'Lu',
  'rh.regulatoryWatch.pdf.readNo': 'Non lu',
  'rh.regulatoryWatch.pdf.actionDone': 'Action faite',
  'rh.regulatoryWatch.pdf.actionOpen': 'Action en cours',
  'rh.regulatoryWatch.pdf.emptyWatch': 'Aucun élément enregistré',
  'rh.regulatoryWatch.pdf.auditSection': 'Journal interne (traçabilité)',
  'rh.regulatoryWatch.pdf.auditHint': 'Actions enregistrées automatiquement dans la plateforme.',
  'rh.regulatoryWatch.pdf.emptyAudit': 'Aucun événement récent',
  'rh.regulatoryWatch.exportPdf': 'Exporter PDF',
  'rh.regulatoryWatch.exportPdfBusy': 'Export PDF…',
  'rh.regulatoryWatch.exportPdfError': 'Export PDF impossible',
  'rh.compliancePage.tabs.satisfaction': 'Satisfaction',
  'rh.compliancePage.title': 'Conformité & Qualiopi',
  'rh.complianceWarning': 'Certains critères nécessitent une action avant le prochain audit.',
  'rh.compliant': 'conformes',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critiques',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'jours restants',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Dernière mise à jour',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'à renforcer',
  'rh.nextAudit': 'Prochain audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Score global de conformité',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Conformité Qualiopi',
  'rh.qualiopiEmpty': 'Aucune donnée Qualiopi disponible pour cet établissement.',
  'rh.qualiopiLoading': 'Chargement du référentiel Qualiopi…',
  'v2.compliance': 'Conformité & Qualiopi',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Chemin circulaire détecté',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} erreur(s)',
  'designer.validation.warningCount': '{{count}} avertissement(s)',
  'designer.validation.titleRequired': 'Le bloc « {{type}} » doit avoir un titre.',
  'designer.validation.summary': 'Parcours : {{blocks}} bloc(s), {{edges}} liaison(s).',
  'designer.validation.multipleStart': 'Plusieurs blocs Départ ({{count}}) : un seul est autorisé.',
  'designer.validation.noOutgoing': 'Le bloc « {{type}} » n’a pas de connexion sortante vers un bloc suivant.',
  'designer.validation.tooManyOutputs': '« {{label}} » : trop de liaisons sortantes ({{count}}, maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Branche sans chemin vers un bloc Fin (depuis « {{label}} »).',
  'designer.validation.moreDeadEnds': '… et {{count}} autre(s) branche(s) sans issue vers la fin.',
  'designer.validation.noConvergence': 'Les branches depuis « {{label}} » ne convergent pas vers un bloc commun.',
  'designer.validation.orphanBlock': 'Bloc isolé du graphe principal : « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Le bloc Fin « {{title}} » a encore une liaison sortante.',
  'designer.validation.noScoring': 'Paramètres de score non définis pour « {{title}} ».',
  'designer.validation.emptyText': 'Le bloc texte « {{title}} » est vide.',
  'designer.validation.textTooLong':
    'Le bloc texte « {{title}} » est estimé à {{lines}} lignes. Recommandé : {{min}} à {{max}} lignes.',
  'designer.validation.splitTextBlock': 'Séparer',
  'designer.validation.textSplitNotNeeded':
    'Ce bloc ne dépasse pas le seuil recommandé de lecture.',
  'designer.validation.textSplitUnable':
    'Impossible de proposer une séparation automatique fiable pour ce bloc.',
  'designer.validation.textSplitComplexFlow':
    'Séparation automatique non disponible pour ce bloc (branchements multiples/conditionnels).',
  'designer.validation.textSplitDone': '{{count}} blocs texte créés',
  'designer.validation.textSplitConfirmTitle':
    'Séparer automatiquement ce bloc texte ?',
  'designer.validation.textSplitConfirmMessage':
    'Le bloc « {{title}} » est estimé à {{lines}} lignes. Nous allons le découper en {{parts}} blocs (séparation guidée par les points) et les placer juste en dessous, dans l’ordre.',
  'designer.validation.textSplitConfirmAction': 'Séparer le bloc',
  'designer.validation.formFieldRefsMissing':
    '« {{title}} » : référence(s) vers champ(s) de formulaire inexistants : {{refs}}.',
  'designer.validation.formFieldRefsOrder':
    '« {{title}} » : variable(s) de formulaire incorrecte(s) {{refs}} — le bloc formulaire doit être atteint avant ce bloc dans le parcours.',
  'designer.confirmDeleteForm.title': 'Supprimer ce bloc formulaire ?',
  'designer.confirmDeleteForm.message':
    'D’autres blocs peuvent utiliser ses champs sous la forme /identifiant/. Ces références deviendront invalides. Vous pouvez annuler avec ⌘Z ou Ctrl+Z.',
  'designer.confirmDeleteForm.batchTitle': 'Supprimer la sélection ?',
  'designer.confirmDeleteForm.batchMessage':
    'La sélection contient au moins un bloc formulaire. Des références /identifiant/ ailleurs dans le parcours peuvent devenir invalides. Vous pouvez annuler avec ⌘Z ou Ctrl+Z.',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Voix par défaut',
  'voicePanel.provider': 'Fournisseur : Mistral AI',
  'voicePanel.hint': 'Sélectionnez la voix utilisée pour la narration des blocs.',
  'voicePanel.multiLangHint':
    'Pour une voix Mistral par langue (les 11 langues de l’interface), ouvrez le panneau Traduction (icône langues dans la barre du designer).',
  'voicePanel.translationFrenchFallback':
    'Traduction audio : sans voix enregistrée pour une langue, le moteur utilise « Marie - sad » (Mistral) pour le français, l’italien et l’espagnol, et « Paul - sad » pour les autres.',
  'voicePanel.search': 'Rechercher…',
  'voicePanel.allLanguages': 'Toutes les langues',
  'voicePanel.loading': 'Chargement des voix…',
  'voicePanel.loadError': 'Impossible de charger les voix',
  'voicePanel.empty': 'Aucune voix disponible.',
  'voicePanel.emptyHint': 'Vérifiez la clé API Mistral dans Administration → Intégrations.',
  'voicePanel.noResults': 'Aucune voix ne correspond à votre recherche.',
  'voicePanel.saved': 'Voix par défaut sauvegardée',
  'voicePanel.saveError': 'Erreur lors de la sauvegarde',
  'voicePanel.default': 'Par défaut',
  'voicePanel.female': 'Féminine',
  'voicePanel.male': 'Masculine',
  'voicePanel.playPreview': 'Tester la voix',
  'voicePanel.stopPreview': 'Arrêter',
  'voicePanel.noPreview': 'Aperçu non disponible',
  'voicePanel.previewLoading': 'Génération…',
  'voicePanel.previewGenerateError': 'Impossible de générer l’aperçu audio.',
  'voicePanel.previewPlayError': 'Échec de la lecture audio.',
  'voicePanel.filterNoVoicesForLang':
    'Aucune voix du catalogue ne correspond à cette langue — essayez « Toutes les langues ».',
  'episodeEditor.voicePicker.blockVoice': 'Voix pour ce bloc',
  'episodeEditor.voicePicker.defaultVoice': 'Voix par défaut',
  'episodeEditor.voicePicker.voices': 'voix',
  'episodeEditor.voicePicker.searchPlaceholder': 'Rechercher une voix…',
  'episodeEditor.voicePicker.noVoiceFound': 'Aucune voix trouvée',
  'episodeEditor.voicePicker.female': 'Féminin',
  'episodeEditor.voicePicker.male': 'Masculin',
  'episodeEditor.voicePicker.stop': 'Arrêter',
  'episodeEditor.voicePicker.preview': 'Tester la voix',
  'episodeEditor.voicePicker.stopPlayback': 'Arrêter la lecture',
  'episodeEditor.errors.notFound': 'Épisode introuvable',
  'episodeEditor.errors.titleRequired': 'Le titre est requis.',
  'episodeEditor.errors.saveFailed': 'Erreur lors de la sauvegarde.',
  'episodeEditor.errors.noVoicesAvailable': 'Aucune voix disponible. Renseignez la clé API dans Administration → Intégrations plateforme, puis rechargez la page.',
  'episodeEditor.errors.selectVoiceFirst': 'Choisissez une voix dans l\'onglet « Voix » avant de générer la voix off.',
  'episodeEditor.errors.invalidBlockVoice': 'Un ou plusieurs blocs ont une voix invalide. Ouvrez le sélecteur de voix par bloc ou rechargez la liste des voix.',
  'episodeEditor.errors.addTextFirst': 'Ajoutez du texte avant de générer l\'audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Génération audio impossible. {{message}} (durée affichée = estimation.)',
  'episodeEditor.errors.audioGenerationFailed': 'Erreur lors de la génération audio. Durée affichée = estimation.',
  'episodeEditor.success.published': 'Épisode publié !',
  'episodeEditor.success.draftSaved': 'Brouillon enregistré',
  'episodeEditor.success.audioGenerated': 'Audio généré avec {{count}} bloc(s)',
  'episodeEditor.tabs.edit': 'Édition',
  'episodeEditor.tabs.chapters': 'Chapitres',
  'episodeEditor.tabs.voice': 'Voix',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Annuler (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Rétablir (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Génération…',
  'episodeEditor.toolbar.generateAudio': 'Générer audio',
  'episodeEditor.toolbar.save': 'Enregistrer',
  'episodeEditor.toolbar.publish': 'Publier',
  'episodeEditor.titlePlaceholder': 'Titre de l\'épisode…',
  'episodeEditor.status.published': 'Publié',
  'episodeEditor.status.draft': 'Brouillon',
  'episodeEditor.warnings.mistralNotConfigured': 'La voix off des épisodes utilise Mistral Voxtral TTS. Ajoutez la clé Mistral dans Administration → Intégrations plateforme, puis rechargez cette page.',
  'episodeEditor.warnings.mistralNoVoices': 'La clé Mistral est reconnue mais l\'API n\'a renvoyé aucune voix. Consultez les logs du backend.',
  'episodeEditor.warnings.voiceLoadFailed': 'Impossible de charger la liste des voix. Vérifiez la configuration Mistral dans Administration → Intégrations plateforme.',
  'episodeEditor.warnings.voiceOffDisabled': 'Sans voix réelle, la génération de voix off reste désactivée.',
  'episodeEditor.edit.voiceOff': 'Voix off',
  'episodeEditor.edit.voiceDescription':
    'Par bloc : synthèse Mistral Voxtral (onglet Voix ou pastille à gauche) ou enregistrement micro. « Générer audio » assemble tous les segments dans l’ordre.',
  'episodeEditor.edit.coverImage': 'Image de couverture',
  'episodeEditor.edit.coverAlt': 'Couverture de l’épisode',
  'episodeEditor.edit.deleteCover': 'Supprimer',
  'episodeEditor.edit.dropOrClickImage': 'Cliquez ou déposez une image',
  'episodeEditor.edit.selectedVoice': 'Voix sélectionnée',
  'episodeEditor.edit.selectVoice': 'Sélectionner une voix',
  'episodeEditor.edit.defaultVoiceByLang': 'Voix par défaut (langue du podcast)',
  'episodeEditor.edit.lastAudioGenerated': 'Dernier audio généré',
  'episodeEditor.edit.audioNotFound': 'Fichier audio introuvable — regénérez la voix off.',
  'episodeEditor.edit.generatingInProgress': 'Génération en cours…',
  'episodeEditor.edit.generateVoiceOff': 'Générer la voix off',
  'episodeEditor.chapters.title': 'Chapitres',
  'episodeEditor.chapters.add': 'Ajouter',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapitre {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} bloc(s)',
  'episodeEditor.chapters.readByAi': 'Titre lu par l’IA',
  'episodeEditor.chapters.readByAiHint':
    "Si coché, l'IA lit le titre du chapitre avant le texte de ses blocs (même voix off). Les blocs restent toujours inclus.",
  'episodeEditor.voiceTab.availableVoices': 'Voix disponibles',
  'episodeEditor.podcasts.linked': 'Podcasts liés',
  'episodeEditor.podcasts.assign': 'Affecter',
  'episodeEditor.podcasts.noneAvailable': 'Aucun podcast disponible',
  'episodeEditor.podcasts.close': 'Fermer',
  'episodeEditor.podcasts.noneLinked': 'Aucun podcast lié',
  'episodeEditor.podcasts.assignHint': 'Affectez cet épisode à un ou plusieurs podcasts',
  'episodeEditor.podcasts.remove': 'Retirer',
  'episodeEditor.editor.addTextBlock': 'Ajouter un bloc de texte',
  'episodeEditor.editor.scriptPlaceholder': 'Commencez à écrire votre script…',
  'episodeEditor.editor.addBlock': 'Ajouter un bloc',
  'episodeEditor.block.source': 'Source',
  'episodeEditor.block.sourceTts': 'IA',
  'episodeEditor.block.sourceMic': 'Micro',
  'episodeEditor.block.pauseBefore': 'Pause avant',
  'episodeEditor.block.pauseBeforeHint': 'Silence avant de lire ce bloc (la suite).',
  'episodeEditor.block.pause0': '0 s',
  'episodeEditor.block.pause05': '0,5 s',
  'episodeEditor.block.pause1': '1 s',
  'episodeEditor.block.record': 'Enregistrer',
  'episodeEditor.block.stopRecording': 'Arrêter',
  'episodeEditor.block.microMode': 'Mode micro pour ce bloc',
  'episodeEditor.block.notesRecordingPlaceholder': 'Notes optionnelles (non lues automatiquement en micro)',
  'episodeEditor.block.removeRecording': 'Supprimer audio',
  'episodeEditor.edit.audioMixedHumanAndAi': 'Piste IA + voix micro',
  'episodeEditor.errors.addTextOrRecording': "Ajoutez du texte (synthèse IA) ou enregistrez votre voix sur au moins un bloc avant de générer l'audio.",
  'episodeEditor.errors.saveDraftBeforeRecording': 'Impossible de créer l’épisode pour le micro. Ajoutez un titre ou enregistrez depuis un épisode existant.',
  'episodeEditor.errors.microNotSupported': 'Micro non disponible dans ce navigateur.',
  'episodeEditor.errors.microPermission': 'Impossible d’accéder au micro (permission refusée).',
  'episodeEditor.errors.recordingUploadFailed': 'Échec de l’upload de l’enregistrement.',
  'episodeEditor.player.loop': 'Boucle',
  'episodeEditor.player.playbackSpeed': 'Vitesse de lecture',
  'episodeEditor.player.collapse': 'Réduire',
  'episodeEditor.player.expand': 'Agrandir',
  'episodeEditor.player.waveformHidden': 'Waveform masquée',
  'podcast.episodes.listen': 'Écouter',
  'podcast.episodes.noAudioShort': 'Pas d\'audio disponible',
  'podcast.episodes.edit': 'Modifier',
  'podcast.episodes.delete': 'Supprimer',
  'podcast.episodes.unmute': 'Activer le son',
  'podcast.episodes.mute': 'Couper le son',
  'podcast.episodes.totalEpisodes': '{{count}} épisode(s) au total',
  'eduflow.designer.toast.blockCopied': 'Block copié',
  'eduflow.designer.toast.blockPasted': 'Block collé',
  'eduflow.designer.toast.blockDuplicated': 'Block dupliqué',
  'eduflow.designer.toast.connectionUpdated': 'Connexion mise à jour',
  'eduflow.designer.toast.flowExported': 'Parcours exporté',
  'eduflow.designer.toast.flowImported': 'Parcours importé',
  'eduflow.designer.toast.flowPastedFromClipboard': 'Parcours créé à partir du presse-papiers',
  'eduflow.designer.toast.invalidFile': 'Fichier invalide',
  'eduflow.designer.toast.exportFailed': 'Export impossible.',
  'eduflow.designer.toast.importPackageFailed': 'Import du package .lyrra impossible.',
  'eduflow.designer.untitled': 'Sans titre',
  'eduflow.designer.importedFlow': 'Parcours importé',
  'eduflow.designer.collaborator.online': 'En ligne',
  'eduflow.designer.collaborator.offline': 'Hors ligne',
  'eduflow.designer.collaborator.defaultName': 'Utilisateur',
  'eduflow.designer.collaborator.editor': 'Editeur',
  'eduflow.designer.collaborator.viewer': 'Lecteur',
  'eduflow.designer.resizePanel': 'Redimensionner le panneau',
  'eduflow.designer.published': 'Publié',
  'eduflow.designer.draft': 'Brouillon',
  'eduflow.designer.quizOutput.success': 'Réussi',
  'eduflow.designer.quizOutput.failure': 'Échoué',
  'eduflow.designer.loopHandle.loop': 'Boucle',
  'eduflow.designer.loopHandle.end': 'Fin',
  'eduflow.designer.handle.default': 'Défaut',
  'eduflow.designer.genError': 'Erreur lors de la génération. Réessayez.',
  'trainerTour.welcome.title': 'Bienvenue sur votre espace formateur',
  'trainerTour.welcome.description':
    'Cette courte visite vous présente le tableau de bord et le menu. Ensuite, à chaque page que vous ouvrez pour la première fois, un rappel vous explique l’essentiel. Vous pouvez fermer une étape avec la croix : la visite ne se relancera plus.',
  'trainerTour.dashboard.header.title': 'Votre tableau de bord',
  'trainerTour.dashboard.header.description':
    'Vue d’ensemble de votre activité : indicateurs et raccourcis pour créer du contenu rapidement.',
  'trainerTour.dashboard.actions.title': 'Créer du contenu',
  'trainerTour.dashboard.actions.description':
    'Raccourcis vers une nouvelle formation EduFlow, un podcast ou un parcours. Utilisez-les pour démarrer un projet sans passer par le menu.',
  'trainerTour.dashboard.stats.title': 'Indicateurs',
  'trainerTour.dashboard.stats.description':
    'Formations, parcours publiés, apprenants concernés et podcasts : suivez l’ampleur de votre catalogue en un coup d’œil.',
  'trainerTour.dashboard.activity.title': 'Activité récente',
  'trainerTour.dashboard.activity.description':
    'Les derniers contenus modifiés. Cliquez une ligne pour rouvrir la formation, le podcast ou le parcours correspondant.',
  'trainerTour.sidebar.title': 'Menu latéral',
  'trainerTour.sidebar.description':
    'Pilotage (accueil, apprenants, analytiques, missions), création (formations, podcasts, parcours, webinaires si votre établissement les active), bibliothèque média et centre d’aide. Cliquez les entrées pour explorer : une aide contextuelle s’affiche la première fois sur chaque page.',
  'trainerTour.routes.learners.title': 'Apprenants',
  'trainerTour.routes.learners.description':
    'Liste des personnes inscrites à vos contenus, progression, exports. C’est votre cockpit pour le suivi pédagogique.',
  'trainerTour.routes.analytics.title': 'Analytiques',
  'trainerTour.routes.analytics.description':
    'Tableaux et graphiques sur l’engagement et la complétion. Personnalisez la disposition pour suivre ce qui compte pour vous.',
  'trainerTour.routes.eduflow.title': 'Formations (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Créez et gérez vos parcours interactifs, versions, publication et partage. C’est le cœur de la conception pédagogique.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Émissions audio, épisodes, visibilité et partage avec les groupes ou apprenants de votre établissement.',
  'trainerTour.routes.parcours.title': 'Parcours',
  'trainerTour.routes.parcours.description':
    'Enchaînements de formations et ressources : structurez des parcours longs et assignez-les à vos publics.',
  'trainerTour.routes.videos.title': 'Vidéos',
  'trainerTour.routes.videos.description':
    'Bibliothèque de vidéos réutilisables dans vos formations et documents.',
  'trainerTour.routes.sounds.title': 'Sons',
  'trainerTour.routes.sounds.description':
    'Fichiers audio et tags pour alimenter podcasts et blocs sonores des parcours.',
  'trainerTour.routes.images.title': 'Images',
  'trainerTour.routes.images.description':
    'Visuels centralisés : import, dossiers et réutilisation dans le contenu.',
  'trainerTour.routes.documents.title': 'Documents',
  'trainerTour.routes.documents.description':
    'PDF et pièces jointes partagées, avec partage ciblé vers les apprenants si besoin.',
  'trainerTour.routes.help.title': 'Aide',
  'trainerTour.routes.help.description':
    'Articles, tutoriels et parcours d’apprentissage sur LyrraStudio. Revenez-y quand vous bloquez sur une fonction.',
  'trainerTour.routes.producerAssignments.title': 'Missions de production',
  'trainerTour.routes.producerAssignments.description':
    'Lorsque votre RH vous confie des contenus à produire (catalogue établissement), elles apparaissent ici avec l’état d’avancement.',
  'trainerTour.routes.webinars.title': 'Webinaires',
  'trainerTour.routes.webinars.description':
    'Planifiez des sessions live, invitations et liens de diffusion pour vos groupes.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Suivant',
  'trainerTour.driver.prev': 'Précédent',
  'trainerTour.driver.done': 'OK',
  'trainerTour.driver.gotIt': 'Compris',
  'player.lightMode': 'Mode clair',
  'player.darkMode': 'Mode sombre',
  'a11y.skipToContent': 'Aller au contenu principal',
  'a11y.skipToNav': 'Aller à la navigation',
  'a11y.skipToPlayerMain': 'Aller au contenu de la formation',
  'a11y.playerMainLandmark': 'Contenu de la formation',
  'a11y.dialog': 'Dialogue',
  'a11y.closeDialog': 'Fermer la boîte de dialogue',
  'a11y.dismissNotification': 'Fermer la notification',
  'a11y.notificationsRegion': 'Notifications',
  'a11y.skipLinksLandmark': 'Liens d’accès rapide',
  ...eduflowBlockEditorsFr,
  ...institutionMcpFr,
};

const enTranslations: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Dashboard',
  'nav.projects': 'Projects',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Audiobooks',
  'nav.audio_studio': 'Audio Studio',
  'nav.store': 'Store',
  'nav.analytics': 'Analytics',
  'nav.users': 'Users',
  'nav.settings': 'Settings',
  'nav.profile': 'Profile',
  'profile.institution': 'Institution',
  'profile.institutionHint':
    'Your LyrraStudio account is linked to this institution. This link is managed by your administrator.',
  'profile.institutionUnknownName': 'Institution (name unavailable)',
  'nav.notifications': 'Notifications',
  'nav.help': 'Help',
  'nav.community': 'Community',
  'nav.library': 'Library',
  'nav.certificates': 'Certificates',
  'nav.institution': 'Institution',
  'nav.groups': 'Groups',
  'nav.learners': 'Learners',
  'nav.myLearning': 'My learning',
  'nav.reports': 'Reports',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'Training Plan',
  'nav.catalog': 'Catalog',
  'nav.competencies': 'Competencies',
  'nav.budget': 'Budget',
  'nav.campaigns': 'Campaigns',
  'nav.codir': 'CODIR',
  'nav.home': 'Home',
  'nav.producerAssignments': 'Production missions',
  'nav.videos': 'Videos',
  'nav.sounds': 'Sounds',
  'nav.images': 'Images',
  'pages.images.title': 'Images',
  'pages.images.pasteHint': 'Or paste a screenshot (Ctrl+V / Cmd+V)',
  'pages.images.emptyHint':
    'Drag and drop, click to import, or paste a screenshot (Ctrl+V / Cmd+V).',
  'pages.images.viewLarge': 'View full size',
  'pages.images.lightboxTitle': 'Image preview',
  'pages.images.lightboxClose': 'Close',
  'pages.images.lightboxBackdropClose': 'Close preview',
  'pages.images.lightboxPrev': 'Previous image',
  'pages.images.lightboxNext': 'Next image',
  'nav.documents': 'Documents',
  'nav.podcasts': 'Podcasts',
  'nav.webinars': 'Webinars',
  'nav.webinarsEnterprise': 'Webinars (organization)',
  'webinar.room.capacityFull':
    'This room has reached its maximum capacity ({{max}} people connected at once). Please try again later.',
  'webinar.room.participants': 'Participants',
  'webinar.room.chat': 'Chat',
  'webinar.room.speechTab': 'Speech',
  'webinar.room.speechPanelHint':
    'Text captured from presenters’ microphones (browser speech recognition). The name shown is the person currently speaking.',
  'webinar.room.speechEmpty':
    'When a presenter speaks with the microphone on, their words appear here with their name. Punctuation follows the browser’s transcript.',
  'webinar.room.speechUnknownSpeaker': 'Presenter',
  'webinar.room.speechPresenterActive':
    'Your voice is transcribed by the browser and sent to participants (Speech tab).',
  'webinar.room.speechUnsupportedBrowser':
    'Automatic presenter captions are not available in this browser (try Chrome or Edge).',
  'webinar.room.sidePanelTabs': 'Participants, presenter speech, poll, and chat',
  'webinar.room.you': 'you',
  'webinar.room.showParticipantsPanel': 'Show participants panel',
  'webinar.room.hideParticipantsPanel': 'Collapse panel',
  'webinar.room.leave': 'Leave room',
  'webinar.room.contentFocusExpand': 'Content fills the stage',
  'webinar.room.contentFocusCollapse': 'Show backdrop and presenters',
  'webinar.room.contentFocusAriaExpand':
    'Expand shared course, uploaded document or screen share full width without stage backdrop or presenter cameras',
  'webinar.room.liveDocumentLabel': 'Upload (PDF or PowerPoint)',
  'webinar.room.liveDocumentUploadChoice': 'Pick a file to display',
  'webinar.room.liveDocumentUploading': 'Uploading…',
  'webinar.room.liveDocumentUploadError':
    'Could not upload the file. Check the format (PDF or PowerPoint) and size (max 40 MB).',
  'webinar.room.liveDocumentClose': 'Remove document',
  'webinar.room.liveDocumentBehindScreen':
    'The document stays available — stop screen sharing to show it again.',
  'webinar.room.waitingSharedContentExtended':
    'Waiting for the presenter to share a catalog course, a file (PDF or PowerPoint), or their screen.',
  'webinar.room.liveDocumentOfficeHint':
    'If preview fails (plain HTTP locally or oversized file), use the link above.',
  'webinar.room.liveDocumentOpenOrDownload': 'Open or download the file',
  'webinar.room.liveDocumentNoInlinePreview':
    'This file cannot be embedded automatically. Open it in a new tab.',
  'webinar.room.liveDocumentOpenTab': 'Open',
  'webinar.room.pollTab': 'Poll',
  'webinar.room.poll.regionTitle': 'Webinar poll',
  'webinar.room.poll.presenterHint':
    'Add a question and 2–8 answers. Everyone sees the poll here; only hosts see live results.',
  'webinar.room.poll.questionLabel': 'Question',
  'webinar.room.poll.questionPlaceholder': 'E.g. Which topic should we cover next?',
  'webinar.room.poll.optionsLabel': 'Answer choices',
  'webinar.room.poll.optionNumber': 'Answer',
  'webinar.room.poll.choiceLabel': 'Choice',
  'webinar.room.poll.addOption': 'Add an answer',
  'webinar.room.poll.removeLastOption': 'Remove last',
  'webinar.room.poll.launch': 'Start poll',
  'webinar.room.poll.waitingParticipant': 'No active poll yet. The host can start one.',
  'webinar.room.poll.voteHint':
    'One vote saved per person: you can change your answer by picking another option.',
  'webinar.room.poll.yourVote': 'your pick',
  'webinar.room.poll.resultsHeading': 'Results (hosts)',
  'webinar.room.poll.noVotesYet': 'No votes yet.',
  'webinar.room.poll.totalVotes': 'Total votes: {{n}}',
  'webinar.room.poll.closePoll': 'Close poll',
  'webinar.room.contentFocusAriaCollapse': 'Restore stage backdrop and presenter column',
  'webinar.room.exitDirectTitle': 'Leave this webinar?',
  'webinar.room.exitDirectMessage':
    'You opened this room from a direct link (not from the dashboard). You can close this tab. If the browser blocks closing, you will be redirected to your home area.',
  'webinar.room.exitDirectConfirm': 'Close tab',
  'webinar.room.publicGateTitle': 'Webinar signup',
  'webinar.room.publicGateHint':
    'The organizer enabled public signup: enter your email, choose a password (min. 8 characters) and complete the requested fields.',
  'webinar.room.publicGateEmail': 'Email',
  'webinar.room.publicGateLogin': 'Login',
  'webinar.room.publicGatePassword': 'Password',
  'webinar.room.publicGatePasswordHintParticipant':
    'First visit: registration. Next visits: same email and password. Minimum 8 characters.',
  'webinar.room.publicGateSubmit': 'Enter room',
  'webinar.room.publicGateSubmitting': 'Verifying…',
  'webinar.room.publicGateDenied': 'Incorrect email or password.',
  'webinar.room.socketGateDenied':
    'Could not join the live room: public signup session missing or expired. Reload the page and sign in again with the same email and password.',
  'nav.parcours': 'Learning paths',
  'nav.eduflowList': 'Courses',
  'nav.section.pilotage': 'Overview',
  'nav.section.creation': 'Create',
  'nav.section.medias': 'Media',
  'nav.section.aide': 'Help',
  'sidebar.brandTagline': 'Training',
  'sidebar.logoInstitutionAlt': 'Organization logo',
  'sidebar.expand': 'Expand menu',
  'sidebar.collapse': 'Collapse menu',
  'sidebar.expandAria': 'Expand sidebar',
  'sidebar.collapseAria': 'Collapse sidebar',
  'dashboard.kindPodcast': 'Podcast',
  'pages.learners.title': 'Learners',
  'pages.learners.description': 'Track your learners’ progress',
  'pages.learners.enrolledFormations': 'Enrolled courses',
  'pages.learners.notStarted': 'Not started',
  'pages.learners.startedOn': 'Started {{date}}',
  'pages.learners.detailedTraces': 'Detailed activity · {{flowTitle}}',
  'pages.learners.statsEvents': 'Events: {{count}}',
  'pages.learners.statsResponses': 'Responses: {{count}}',
  'pages.learners.statsBlockSessions': 'Block sessions: {{count}}',
  'pages.learners.timePerBlock': 'Time per block',
  'pages.learners.lastEvents': 'Recent events',
  'pages.learners.blockSessionInProgress': '(in progress)',
  'pages.learners.blockTimeOnBlock': '({{duration}} on this block)',
  'pages.learners.trackingEvent.unknown': 'Event',
  'pages.learners.trackingEvent.training_enter': 'Opened the course',
  'pages.learners.trackingEvent.training_exit': 'Left or closed the course',
  'pages.learners.trackingEvent.training_completed': 'Course completed',
  'pages.learners.trackingEvent.block_enter': 'Entered a block',
  'pages.learners.trackingEvent.block_exit': 'Left a block',
  'pages.learners.trackingEvent.response_submitted': 'Submitted a response',
  'pages.learners.trackingEvent.progress_sync': 'Progress synced',
  'pages.learners.refresh': 'Refresh',
  'pages.learners.exportCsv': 'Export CSV',
  'pages.learners.statTotal': 'Total learners',
  'pages.learners.statInProgress': 'In progress',
  'pages.learners.statCompleted': 'Completed',
  'pages.learners.statAvgProgress': 'Avg. progress',
  'pages.learners.searchPlaceholder': 'Search by name or email…',
  'pages.learners.filterAllFlows': 'All courses',
  'pages.learners.sortRecent': 'Most recent',
  'pages.learners.sortName': 'Name A-Z',
  'pages.learners.sortProgress': 'Progress',
  'pages.learners.colLearner': 'Learner',
  'pages.learners.colFormations': 'Courses',
  'pages.learners.colProgress': 'Progress',
  'pages.learners.colCompleted': 'Completed',
  'pages.learners.colTime': 'Time',
  'pages.learners.colLastAccess': 'Last access',
  'pages.learners.colActions': 'Actions',
  'pages.learners.openProgressDashboard': 'Progress dashboard',
  'pages.learners.dashboardTitle': 'Learner progress',
  'pages.learners.dashboardBack': 'Back to list',
  'pages.learners.dashboardInvalidId': 'Invalid learner id',
  'pages.learners.dashboardNotFound': 'Learner not found or access denied',
  'pages.learners.dashboardOverviewHint':
    'Consolidated view: EduFlow courses this participant is enrolled in, plus learning paths and podcasts assigned to the Lyrra account with the same email.',
  'pages.learners.tabOverview': 'Overview',
  'pages.learners.tabFormations': 'Courses',
  'pages.learners.tabParcours': 'Paths',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Courses',
  'pages.learners.sectionParcours': 'Learning paths',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'No learning path assigned to a Lyrra user with this email.',
  'pages.learners.emptyPodcasts': 'No podcast assigned to a Lyrra user with this email.',
  'pages.learners.linkedAccountHint':
    'Paths and podcasts show when a Lyrra user exists with the same email as this participant.',
  'pages.learners.kpiEduflows': 'Courses',
  'pages.learners.kpiAvgProgress': 'Avg. progress',
  'pages.learners.kpiPaths': 'Paths',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} completed',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} activities',
  'pages.learners.pathAssignedOn': 'Added on {{date}}',
  'pages.learners.podcastCompleted': 'Completed',
  'pages.learners.podcastInProgress': 'In progress',
  'pages.learners.pathStatusPublished': 'Published',
  'pages.learners.pathStatusDraft': 'Draft',
  'pages.learners.openInDesigner': 'Open in designer',
  'pages.learners.openParcoursEditor': 'Open path',
  'pages.learners.viewPodcasts': 'View podcasts',
  'pages.learners.loading': 'Loading learners…',
  'pages.learners.loadError': 'Could not load learners',
  'pages.learners.retry': 'Retry',
  'pages.learners.emptyFiltered': 'No learners found',
  'pages.learners.emptyFilteredHint': 'Try changing your search or filters',
  'pages.learners.emptyNone': 'No enrolled learners yet',
  'pages.learners.emptyNoneHint': 'Learners will appear here when they enroll in your published courses',
  'pages.learners.pagination': 'Page {{page}} of {{totalPages}} · {{total}} learner(s)',
  'pages.learners.prev': 'Previous',
  'pages.learners.next': 'Next',
  'pages.learners.csvLastName': 'Last name',
  'pages.learners.csvFirstName': 'First name',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Courses',
  'pages.learners.csvAvgProgress': 'Average progress',
  'pages.learners.csvTotalTime': 'Total time',
  'pages.learners.csvLastActivity': 'Last activity',
  'eduflowList.status.draft': 'Draft',
  'eduflowList.status.published': 'Published',
  'eduflowList.status.archived': 'Archived',
  'eduflowList.badge.enterpriseShell': 'Enterprise request',
  'eduflowList.badge.enterpriseLinked': 'Enterprise',
  'eduflowList.badge.toProduce': 'To produce',
  'eduflow.list.blocksCount': '{{count}} blocks',
  'eduflow.list.noContent': 'No content',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Open My assignments',
  'eduflow.list.tooltip.edit': 'Edit',
  'eduflow.list.shellHint':
    'Defined by your organization — create the course from « My assignments ».',
  'breadcrumb.label': 'Breadcrumb',
  'breadcrumb.home': 'Home',
  'breadcrumb.dashboard': 'Dashboard',
  'breadcrumb.projects': 'Projects',
  'breadcrumb.eduflow': 'Courses',
  'breadcrumb.learners': 'Learners',
  'breadcrumb.analytics': 'Analytics',
  'breadcrumb.videos': 'Videos',
  'breadcrumb.sounds': 'Sounds',
  'breadcrumb.images': 'Images',
  'breadcrumb.documents': 'Documents',
  'breadcrumb.store': 'Store',
  'breadcrumb.help': 'Help',
  'breadcrumb.admin': 'Administration',
  'breadcrumb.settings': 'Settings',
  'breadcrumb.profile': 'Profile',
  'breadcrumb.groups': 'Groups',
  'breadcrumb.branding': 'Branding',
  'breadcrumb.landingPage': 'Landing page',
  'breadcrumb.webhooks': 'Webhooks',
  'breadcrumb.campaigns': 'Campaigns',
  'breadcrumb.competencies': 'Competencies',
  'breadcrumb.compliance': 'Compliance',
  'breadcrumb.reports': 'Reports',
  'breadcrumb.budget': 'Budget',
  'breadcrumb.catalog': 'Catalog',
  'breadcrumb.integrations': 'Integrations',
  'breadcrumb.requests': 'Requests',
  'breadcrumb.team': 'Team',
  'breadcrumb.reviews': 'Reviews',
  'breadcrumb.training': 'Training',
  'breadcrumb.skills': 'Skills',
  'breadcrumb.certificates': 'Certificates',
  'breadcrumb.new': 'New',
  'breadcrumb.edit': 'Edit',
  'breadcrumb.podcasts': 'Podcasts',
  'breadcrumb.episodes': 'Episodes',
  'breadcrumb.parcours': 'Learning paths',
  'breadcrumb.producerAssignments': 'Production missions',
  'header.toggleMenu': 'Toggle menu',
  'header.search': 'Search…',
  'header.toggleTheme': 'Toggle light or dark theme',
  'header.profile': 'Profile',
  'header.settings': 'Settings',
  'header.backToInstitutionAdmin': 'Institution administration',
  'header.openTrainerWorkspace': 'Trainer workspace & create content',
  'header.logout': 'Log out',
  'footer.madeWith': 'Made with',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.terms': 'Terms',
  'footer.privacy': 'Privacy',
  'footer.accessibility': 'Accessibility',
  // Common buttons
  'btn.save': 'Save',
  'btn.cancel': 'Cancel',
  'btn.delete': 'Delete',
  'btn.edit': 'Edit',
  'btn.create': 'Create',
  'btn.add': 'Add',
  'btn.close': 'Close',
  'btn.confirm': 'Confirm',
  'btn.back': 'Back',
  'btn.next': 'Next',
  'btn.previous': 'Previous',
  'btn.submit': 'Submit',
  'btn.search': 'Search',
  'btn.filter': 'Filter',
  'btn.export': 'Export',
  'btn.import': 'Import',
  'btn.download': 'Download',
  'btn.upload': 'Upload',
  'btn.refresh': 'Refresh',
  'btn.retry': 'Retry',
  'btn.view': 'View',
  'btn.details': 'Details',
  'btn.publish': 'Publish',
  'btn.unpublish': 'Unpublish',
  'btn.duplicate': 'Duplicate',
  'btn.archive': 'Archive',
  'btn.restore': 'Restore',
  'btn.send': 'Send',
  'btn.apply': 'Apply',
  'btn.reset': 'Reset',
  'btn.select_all': 'Select all',
  'btn.deselect_all': 'Deselect all',
  'btn.load_more': 'Load more',
  'btn.show_more': 'Show more',
  'btn.show_less': 'Show less',
  'btn.copy': 'Copy',
  'btn.share': 'Share',
  'btn.preview': 'Preview',
  'btn.continue': 'Continue',
  'btn.skip': 'Skip',
  // Form labels
  'form.email': 'Email address',
  'form.password': 'Password',
  'form.password_confirm': 'Confirm password',
  'form.first_name': 'First name',
  'form.last_name': 'Last name',
  'form.name': 'Name',
  'form.phone': 'Phone',
  'form.address': 'Address',
  'form.city': 'City',
  'form.zip': 'Zip code',
  'form.country': 'Country',
  'form.title': 'Title',
  'form.description': 'Description',
  'form.category': 'Category',
  'form.tags': 'Tags',
  'form.status': 'Status',
  'form.role': 'Role',
  'form.language': 'Language',
  'form.date': 'Date',
  'form.start_date': 'Start date',
  'form.end_date': 'End date',
  'form.duration': 'Duration',
  'form.price': 'Price',
  'form.avatar': 'Profile picture',
  'form.required': 'Required',
  'form.optional': 'Optional',
  'form.select_placeholder': 'Select...',
  'form.search_placeholder': 'Search...',
  'form.no_results': 'No results',
  'form.file_drop': 'Drag and drop a file here or click to browse',
  'form.max_size': 'Maximum size: {{size}}',
  'form.institution': 'Institution',
  'form.department': 'Department',
  'form.group': 'Group',
  'form.level': 'Level',
  'form.notes': 'Notes',
  'form.url': 'URL',
  'form.content': 'Content',
  'form.thumbnail': 'Thumbnail',
  // Validation errors
  'error.required': 'This field is required',
  'error.email_invalid': 'Invalid email address',
  'error.password_min': 'Password must be at least {{min}} characters',
  'error.password_match': 'Passwords do not match',
  'error.min_length': 'Minimum {{min}} characters',
  'error.max_length': 'Maximum {{max}} characters',
  'error.min_value': 'Minimum value is {{min}}',
  'error.max_value': 'Maximum value is {{max}}',
  'error.invalid_url': 'Invalid URL',
  'error.file_too_large': 'File is too large',
  'error.file_type': 'Unsupported file type',
  'error.generic': 'An error occurred',
  'error.network': 'Network connection error',
  'error.unauthorized': 'You must be logged in',
  'error.forbidden': 'Access denied',
  'error.not_found': 'Resource not found',
  'error.server': 'Internal server error',
  'error.conflict': 'This resource already exists',
  'error.rate_limit': 'Too many requests, please wait',
  // Auth
  'auth.login': 'Log in',
  'auth.logout': 'Log out',
  'auth.register': 'Sign up',
  'auth.forgot_password': 'Forgot password?',
  'auth.reset_password': 'Reset password',
  'auth.otp_title': 'Login with code',
  'auth.otp_sent': 'A verification code has been sent to {{email}}',
  'auth.otp_placeholder': 'Enter the 6-digit code',
  'auth.google_login': 'Continue with Google',
  'auth.remember_me': 'Remember me',
  'auth.no_account': 'No account yet?',
  'auth.has_account': 'Already have an account?',
  'auth.welcome_back': 'Welcome back, {{name}}!',
  'auth.login_success': 'Login successful',
  'auth.register_success': 'Account created successfully',
  'auth.logout_success': 'Logout successful',
  // Roles
  'role.teacher': 'Teacher',
  'role.institution': 'Institution',
  'role.admin': 'Administrator',
  'role.drh': 'HR Director',
  'role.rh': 'HR Manager',
  'role.manager': 'Manager',
  'role.collaborator': 'Collaborator',
  'role.learner': 'Learner',
  // Dashboard
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Welcome, {{name}}',
  'dashboard.subtitle': 'Overview of your activity',
  'dashboard.home.greeting': 'Hello {{name}}',
  'dashboard.home.greetingSubtitle':
    'Manage your courses, learning paths and podcasts from one place.',
  'dashboard.home.primaryCta': 'Create a course',
  'dashboard.home.continueEditing': 'Resume last edit',
  'dashboard.home.publishRate': 'Publication rate',
  'dashboard.home.totalFormations': 'Courses created',
  'dashboard.home.uniqueLearners': 'Learners reached',
  'dashboard.home.calendarKicker': 'Activity this month',
  'dashboard.home.recentWork': 'Recent updates',
  'dashboard.home.viewAll': 'View all',
  'dashboard.home.quickAccess': 'Quick access',
  'dashboard.home.loading': 'Loading dashboard…',
  'dashboard.home.defaultName': 'trainer',
  'dashboard.welcomeSub': 'Here is what is happening across your projects.',
  'dashboard.projects': 'Projects',
  'dashboard.eduflows': 'Training paths',
  'dashboard.publishedFlows': 'Published paths',
  'dashboard.certificatesIssued': 'Certificates issued',
  'dashboard.activityEmpty': 'No recent activity on your paths or podcasts.',
  'dashboard.loadError': 'Could not load dashboard data.',
  'dashboard.learners': 'Learners',
  'dashboard.revenue': 'Revenue',
  'dashboard.newProject': 'New project',
  'dashboard.newEduFlow': 'New training path',
  'dashboard.uploadMedia': 'Upload media',
  'dashboard.viewDocs': 'Documentation',
  'dashboard.recentProjects': 'Recent projects',
  'dashboard.noRecentProjects': 'No recent projects',
  'dashboard.activity': 'Activity',
  'dashboard.searchProjects': 'Search projects...',
  'dashboard.noProjects': 'No projects yet',
  'dashboard.createFirst': 'Create your first project to get started',
  'dashboard.createProject': 'Create project',
  'dashboard.projectTitle': 'Project title',
  'dashboard.recentActivity': 'Recent activity',
  'project.status.draft': 'Draft',
  'project.status.published': 'Published',
  'project.status.archived': 'Archived',
  'dashboard.roleSpecific': 'Your space is personalized according to your role.',
  'dashboard.calendar.mon': 'Mon',
  'dashboard.calendar.wed': 'Wed',
  'dashboard.calendar.fri': 'Fri',
  'dashboard.calendar.less': 'Less',
  'dashboard.calendar.more': 'More',
  'dashboard.calendar.tooltip': '{{count}} activit(ies) on {{date}}',
  'dashboard.mock.updated2h': '2h ago',
  'dashboard.mock.updated5h': '5h ago',
  'dashboard.mock.updatedYesterday': 'Yesterday',
  'dashboard.home': 'Home',
  'dashboard.analytics': 'Analytics',
  'dashboard.settings': 'Settings',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'pages.analytics.title': 'Analytics',
  'pages.analytics.description': 'Dashboards and statistics',
  'pages.analytics.subtitle':
    'Filter data, drag to reorder charts, and resize widgets to match how you work.',
  'pages.analytics.filters.period': 'Period',
  'pages.analytics.filters.flow': 'Course',
  'pages.analytics.filters.allFlows': 'All courses',
  'pages.analytics.period.7d': 'Last 7 days',
  'pages.analytics.period.30d': 'Last 30 days',
  'pages.analytics.period.90d': 'Last 90 days',
  'pages.analytics.period.365d': 'Last 12 months',
  'pages.analytics.addChart': 'Add chart',
  'pages.analytics.resetLayout': 'Reset layout',
  'pages.analytics.dragHint': 'Drag to reorder',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Remove this chart',
  'pages.analytics.kind.completion_line': 'Completion over time',
  'pages.analytics.kind.learners_bar': 'Learners by module',
  'pages.analytics.kind.status_pie': 'Status distribution',
  'pages.analytics.kind.engagement_area': 'Engagement time',
  'pages.analytics.series.completion': 'Completion rate (%)',
  'pages.analytics.series.learners': 'Learners',
  'pages.analytics.series.hours': 'Hours',
  'pages.analytics.status.completed': 'Completed',
  'pages.analytics.status.in_progress': 'In progress',
  'pages.analytics.status.dropped': 'Dropped',
  'pages.analytics.status.not_started': 'Not started',
  'pages.analytics.loadError': 'Could not load analytics data.',
  'pages.analytics.retry': 'Retry',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Mon',
  'pages.analytics.mock.wd1': 'Tue',
  'pages.analytics.mock.wd2': 'Wed',
  'pages.analytics.mock.wd3': 'Thu',
  'pages.analytics.mock.wd4': 'Fri',
  'pages.analytics.mock.wd5': 'Sat',
  'pages.analytics.mock.wd6': 'Sun',
  'pages.analytics.emptyCharts': 'Add at least one chart to see your data.',
  'pages.analytics.comingSoonDesc':
    'See training stats, completion rates, learner engagement, and overall performance.',
  'pages.help.title': 'Help center',
  'pages.help.description': 'Documentation and support',
  'pages.help.hubIntro':
    'Topics covering how to use Lyrra — you will soon be able to link each card to your docs, tutorials, or support tickets.',
  'pages.help.searchPlaceholder': 'Search topics…',
  'pages.help.noResults': 'No results for this search.',
  'pages.help.configHint':
    'Sample content: wire up your URLs, articles, or knowledge base when you are ready.',
  'pages.help.comingSoonDesc':
    'Access documentation, tutorials, and technical support to get the most out of the platform.',
  'pages.help.modal.steps': 'In practice',
  'pages.help.modal.tips': 'Tips',
  'pages.help.modal.related': 'See also',
  'pages.help.openArticle': 'Open article',
  'pages.help.onboardingFlowMissing': 'Training not found',
  'pages.help.onboardingFlowMissingDesc':
    'Run backend seeds (db:seed:onboarding-eduflow, db:seed:onboarding-navigation-eduflow, db:seed:onboarding-profile-eduflow, db:seed:onboarding-roles-eduflow, db:seed:onboarding-block-types-eduflow, db:seed:onboarding-designer-overview-eduflow, db:seed:onboarding-branches-eduflow, db:seed:onboarding-validation-eduflow, db:seed:onboarding-objectives-eduflow, db:seed:onboarding-ai-designer-eduflow, db:seed:onboarding-publication-status-eduflow, db:seed:onboarding-publication-links-eduflow, db:seed:onboarding-catalog-visibility-eduflow, db:seed:onboarding-enrollments-eduflow, db:seed:onboarding-groups-cohorts-eduflow, db:seed:onboarding-progress-eduflow, db:seed:onboarding-certificates-eduflow, and db:seed:onboarding-media-library-eduflow) or set VITE_ONBOARDING_DASHBOARD_FLOW_ID / VITE_ONBOARDING_NAVIGATION_FLOW_ID / VITE_ONBOARDING_PROFILE_FLOW_ID / VITE_ONBOARDING_ROLES_FLOW_ID / VITE_ONBOARDING_BLOCK_TYPES_FLOW_ID / VITE_ONBOARDING_DESIGNER_OVERVIEW_FLOW_ID / VITE_ONBOARDING_BRANCHES_FLOW_ID / VITE_ONBOARDING_VALIDATION_FLOW_ID / VITE_ONBOARDING_OBJECTIVES_FLOW_ID / VITE_ONBOARDING_AI_DESIGNER_FLOW_ID / VITE_ONBOARDING_PUBLICATION_STATUS_FLOW_ID / VITE_ONBOARDING_PUBLICATION_LINKS_FLOW_ID / VITE_ONBOARDING_CATALOG_VISIBILITY_FLOW_ID / VITE_ONBOARDING_ENROLLMENTS_FLOW_ID / VITE_ONBOARDING_GROUPS_COHORTS_FLOW_ID / VITE_ONBOARDING_PROGRESS_FLOW_ID / VITE_ONBOARDING_CERTIFICATES_FLOW_ID / VITE_ONBOARDING_MEDIA_LIBRARY_FLOW_ID.',
  'pages.help.onboardingDashboardPlayerTitle': 'Training — Lyrra creator dashboard (onboarding)',
  'pages.help.onboardingNavigationPlayerTitle':
    'Training — Lyrra navigation & sidebar (onboarding)',
  'pages.help.onboardingProfilePlayerTitle': 'Training — Lyrra profile & preferences (onboarding)',
  'pages.help.onboardingRolesPlayerTitle':
    'Training — Lyrra roles and access rights (onboarding)',
  'pages.help.onboardingBlockTypesPlayerTitle':
    'Training — EduFlow block types (onboarding)',
  'pages.help.onboardingDesignerOverviewPlayerTitle':
    'Training — EduFlow designer overview (onboarding)',
  'pages.help.onboardingBranchesPlayerTitle':
    'Training — EduFlow branches & conditions (onboarding)',
  'pages.help.onboardingValidationPlayerTitle':
    'Training — EduFlow parcours validation (onboarding)',
  'pages.help.onboardingObjectivesPlayerTitle':
    'Training — EduFlow learning objectives (onboarding)',
  'pages.help.onboardingAiDesignerPlayerTitle':
    'Training — LyrraStudio AI designer assistant (onboarding)',
  'pages.help.onboardingPublicationStatusPlayerTitle':
    'Training — LyrraStudio draft, publication and updates (onboarding)',
  'pages.help.onboardingPublicationLinksPlayerTitle':
    'Training — LyrraStudio access links and sharing (onboarding)',
  'pages.help.onboardingCatalogVisibilityPlayerTitle':
    'Training — LyrraStudio catalog and visibility (onboarding)',
  'pages.help.onboardingEnrollmentsPlayerTitle':
    'Training — LyrraStudio enrollments & participants (onboarding)',
  'pages.help.onboardingGroupsCohortsPlayerTitle':
    'Training — LyrraStudio groups & cohorts (onboarding)',
  'pages.help.onboardingProgressPlayerTitle':
    'Training — LyrraStudio progress & time spent (onboarding)',
  'pages.help.onboardingCertificatesPlayerTitle':
    'Training — LyrraStudio delivered certificates (onboarding)',
  'pages.help.onboardingMediaLibraryPlayerTitle':
    'Training — LyrraStudio media library (onboarding)',
  'dashboard.period.week': 'Week',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.vsLastPeriod': 'vs previous period ({{period}})',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.activeLearners': 'Active learners',
  'dashboard.sessions': 'Sessions',
  'dashboard.activityType.completion': 'Completed',
  'dashboard.activityType.enrollment': 'Enrollment',
  'dashboard.activityType.milestone': 'Milestone',
  'dashboard.activityType.feedback': 'Feedback',
  'dashboard.activityType.start': 'Start',
  'dashboard.totalLearners': 'Total learners',
  'dashboard.activeEduFlows': 'Active training paths',
  'dashboard.publishedTraining': 'Published courses',
  'dashboard.activeUsersPeriod': 'Active users (period)',
  'dashboard.completionRate': 'Completion rate',
  'dashboard.npsScore': 'NPS score',
  'dashboard.topEduFlows': 'Top training paths',
  'dashboard.topEduFlowsHint':
    'Completion rate among the most enrolled courses (sorted by learner count).',
  'dashboard.engagement': 'Learner engagement',
  'common.seeAll': 'See all',
  'common.startedOn': 'Started on',
  'admin.dashboard.totalUsers': 'Total users',
  'admin.dashboard.activeToday': 'Active today',
  'admin.dashboard.revenue': 'Revenue',
  'admin.dashboard.storageUsed': 'Storage used',
  'admin.dashboard.vsLastMonth': 'vs last month',
  'admin.dashboard.userGrowth': 'User growth',
  'admin.dashboard.monthlyRevenue': 'Monthly revenue (€)',
  'admin.dashboard.recentActivity': 'Recent activity',
  'admin.dashboard.systemHealth': 'System health',
  'admin.dashboard.quickActions': 'Quick actions',
  'admin.nav.stats': 'Statistics',
  'admin.nav.users': 'Users',
  'admin.nav.support': 'Support & chat',
  'support.chat.title': 'Lyrra support',
  'support.chat.subtitle': 'Ask your question — our team replies here.',
  'support.chat.empty': 'Write a message to start the conversation.',
  'support.chat.placeholder': 'Your message…',
  'support.chat.send': 'Send',
  'support.chat.open': 'Open support chat',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'You',
  'admin.support.title': 'Support conversations',
  'admin.support.loadError': 'Could not load conversations.',
  'admin.support.replyError': 'Could not send the reply.',
  'admin.support.selectThread': 'Select a conversation to view messages.',
  'admin.support.replyPlaceholder': 'Your reply to the user…',
  'admin.support.sendReply': 'Send',
  'admin.support.refresh': 'Refresh',
  'admin.support.user': 'User',
  'admin.support.subject': 'Subject',
  'admin.support.status': 'Status',
  'admin.support.priority': 'Priority',
  'admin.support.updated': 'Updated',
  'admin.support.messages': 'Thread',
  'admin.support.newestFirst': 'newest first',
  'admin.support.allStatuses': 'All statuses',
  'admin.support.linearCreate': 'Create Linear issue',
  'admin.support.linearOpen': 'Open in Linear',
  'admin.support.linearError': 'Could not create the Linear issue. Check LINEAR_API_KEY on the server.',
  'admin.nav.institutions': 'Institutions',
  'admin.nav.contracts': 'Contracts',
  'admin.nav.editors': 'Editors',
  'admin.nav.podcasts': 'Podcasts',
  'admin.nav.sounds': 'Lyrra sounds',
  'admin.nav.templates': 'Video templates',
  'admin.nav.templatesImages': 'Image templates',
  'admin.nav.tools': 'Tools',
  'admin.nav.files': 'Files',
  'admin.nav.logs': 'Logs',
  'admin.nav.webhooks': 'Webhooks',
  'admin.nav.email': 'Email',
  'admin.nav.docusign': 'DocuSign',
  'admin.docusign.title': 'DocuSign integration',
  'admin.docusign.subtitle':
    'Integration key, API identifiers and RSA private key (JWT). Stored in the database, not in Docker.',
  'admin.docusign.statusOk': 'Configured',
  'admin.docusign.statusIncomplete': 'Incomplete',
  'admin.docusign.keyStored': 'Private key on file',
  'admin.docusign.consentHint':
    'For first-time JWT use, the DocuSign account must grant application consent (URL from DocuSign). If you get 401/403, check the PEM key, integration key and consent.',
  'admin.docusign.loading': 'Loading…',
  'admin.docusign.loadError': 'Could not load DocuSign settings',
  'admin.docusign.saved': 'DocuSign settings saved',
  'admin.docusign.saveError': 'Could not save',
  'admin.docusign.save': 'Save',
  'admin.docusign.reload': 'Reload',
  'admin.docusign.fieldIntegrationKey': 'Integration key (client ID)',
  'admin.docusign.fieldUserId': 'User ID (GUID)',
  'admin.docusign.fieldAccountId': 'Account ID',
  'admin.docusign.fieldBasePath': 'REST API base URL',
  'admin.docusign.fieldOauthHost': 'OAuth host (without https://)',
  'admin.docusign.fieldRsa': 'RSA private key (PEM)',
  'admin.docusign.oauthPlaceholder': 'account-d.docusign.com',
  'admin.docusign.rsaPlaceholderKeep': 'Leave empty to keep the current key',
  'admin.docusign.rsaPlaceholderNew': 'Paste the full .pem file (BEGIN … END)',
  'admin.nav.smtp': 'SMTP configuration',
  'admin.nav.helpCenter': 'Help center',
  'admin.nav.platform': 'Platform integrations',
  'admin.users.adminScopeTitle': 'Admin dashboard scope',
  'admin.users.adminScopeFull': 'Full access (all tabs)',
  'admin.users.adminScopeRestricted': 'Restrict to selected tabs',
  'admin.users.adminScopeHint':
    'Restricted accounts only see the selected sections. After changing tabs, the user must sign in again (or refresh the session) for the menu and API access to update.',
  'admin.users.adminScopeNeedOne': 'Select at least one tab, or choose full access.',
  'admin.helpCenter.title': 'Help center',
  'admin.helpCenter.subtitle':
    'Sections shown first on /help and /aide: category, title, short description, and link (/… path or https URL) opened when the card is clicked.',
  'admin.helpCenter.reload': 'Refresh',
  'admin.helpCenter.loadError': 'Could not load.',
  'admin.helpCenter.saveError': 'Could not save.',
  'admin.helpCenter.deleteError': 'Could not delete.',
  'admin.helpCenter.saved': 'Saved',
  'admin.helpCenter.deleted': 'Deleted',
  'admin.helpCenter.catCreated': 'Category created',
  'admin.helpCenter.topicCreated': 'Article created',
  'admin.helpCenter.catTitleRequired': 'Category title is required.',
  'admin.helpCenter.topicFieldsRequired': 'Title and formation link are required.',
  'admin.helpCenter.newCategory': 'New category',
  'admin.helpCenter.createCategory': 'New category',
  'admin.helpCenter.editCategory': 'Edit category',
  'admin.helpCenter.createTopic': 'New article',
  'admin.helpCenter.editTopic': 'Edit article',
  'admin.helpCenter.fieldTitle': 'Title',
  'admin.helpCenter.fieldDescription': 'Description',
  'admin.helpCenter.fieldIconKey': 'Lucide icon',
  'admin.helpCenter.fieldIconKeyHint':
    'Pick an icon; a preview appears in the list and on the trigger button.',
  'admin.helpCenter.fieldFormationUrl': 'Formation link',
  'admin.helpCenter.formationPickPlaceholder': '— Pick a public course —',
  'admin.helpCenter.formationPickHint':
    'Only published courses with anonymous public access enabled are listed.',
  'admin.helpCenter.formationPickLoading': 'Loading courses…',
  'admin.helpCenter.publicFlowsLoadError': 'Could not load the list of public courses.',
  'admin.helpCenter.legacyFormationPickLabel': 'Saved link (not in list)',
  'admin.helpCenter.fieldSort': 'Order',
  'admin.helpCenter.fieldActive': 'Visible',
  'admin.helpCenter.save': 'Save',
  'admin.helpCenter.edit': 'Edit',
  'admin.helpCenter.addTopic': 'Article',
  'admin.helpCenter.empty': 'No categories yet. Create a category, then articles.',
  'admin.helpCenter.noTopics': 'No articles in this category.',
  'admin.helpCenter.badgeActive': 'Active',
  'admin.helpCenter.badgeInactive': 'Hidden',
  'admin.helpCenter.iconLabel': 'Icon',
  'admin.helpCenter.confirmDeleteCategory': 'Delete this category and all its articles?',
  'admin.helpCenter.confirmDeleteTopic': 'Delete this article?',
  'admin.nav.formationMigration': 'Course migration',
  'admin.nav.formationDuplicate': 'Duplicate courses',
  'admin.nav.lyrraStudioFormations': 'LyrraStudio courses',
  'admin.lyrraStudioFormations.title': 'LyrraStudio-managed courses',
  'admin.lyrraStudioFormations.subtitle':
    'Assign an EduFlow to LyrraStudio: the technical owner is reassigned (PLATFORM_EDUFLOW_OWNER_USER_ID or the first platform admin account), collaborators are removed, and only platform administrators can edit the course in the designer.',
  'admin.lyrraStudioFormations.searchLabel': 'Search (title or course ID)',
  'admin.lyrraStudioFormations.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.lyrraStudioFormations.search': 'Search',
  'admin.lyrraStudioFormations.searchMin': 'Enter at least 2 characters.',
  'admin.lyrraStudioFormations.results': 'Results',
  'admin.lyrraStudioFormations.noResults': 'No course found.',
  'admin.lyrraStudioFormations.badge': 'LyrraStudio',
  'admin.lyrraStudioFormations.detailHint':
    'The original author and former collaborators lose edit access. Learners and public playback are not affected.',
  'admin.lyrraStudioFormations.assign': 'Assign to LyrraStudio',
  'admin.lyrraStudioFormations.alreadyAssigned': 'Already LyrraStudio-managed',
  'admin.lyrraStudioFormations.confirm':
    'Confirm LyrraStudio assignment? This cannot be undone from here: only platform administrators will be able to edit the course.',
  'admin.lyrraStudioFormations.success': 'Course "{{title}}": LyrraStudio assignment completed.',
  'admin.lyrraStudioFormations.assignedListTitle': 'Courses already assigned to LyrraStudio',
  'admin.lyrraStudioFormations.assignedListEmpty': 'None yet. Use the search below to assign one.',
  'admin.lyrraStudioFormations.refreshList': 'Refresh list',
  'admin.lyrraStudioFormations.openDesigner': 'Designer',
  'admin.lyrraStudioFormations.assignSectionTitle': 'Search and assign another course',
  'admin.lyrraStudioFormations.publicPolicyHint':
    'Courses assigned to LyrraStudio are automatically published, anonymously accessible (/f/… short link), and suitable for sharing (QR, help-center iframe).',
  'admin.lyrraStudioFormations.drawerTitle': 'Public link & QR code',
  'admin.lyrraStudioFormations.publicShortLink': 'Short link (recommended)',
  'admin.lyrraStudioFormations.publicLongLink': 'Public player link (UUID)',
  'admin.lyrraStudioFormations.copy': 'Copy',
  'admin.lyrraStudioFormations.copied': 'Copied to clipboard.',
  'admin.lyrraStudioFormations.copyFailed': 'Could not copy (browser or permissions).',
  'admin.lyrraStudioFormations.publicLinksUnavailable':
    'Links unavailable: enable public access or use “Update public access” for courses created before this rule.',
  'admin.lyrraStudioFormations.publicIncomplete': 'Public access incomplete',
  'admin.lyrraStudioFormations.publicRepairHint':
    'This course is LyrraStudio-managed but is missing full public settings (published state, anonymous access, short code).',
  'admin.lyrraStudioFormations.publicRepairCta': 'Update public access',
  'admin.lyrraStudioFormations.publicRepairOk': 'Public access applied for “{{title}}”.',
  'admin.formationDuplicate.title': 'Duplicate a course to another institution',
  'admin.formationDuplicate.subtitle':
    'Search for a course, then pick the institution and account that will own the copy. The original course is left unchanged. The copy is a draft (no catalogue, sessions, or collaborators).',
  'admin.formationDuplicate.searchLabel': 'Search (title or course ID)',
  'admin.formationDuplicate.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.formationDuplicate.search': 'Search',
  'admin.formationDuplicate.searchMin': 'Enter at least 2 characters.',
  'admin.formationDuplicate.results': 'Results',
  'admin.formationDuplicate.noResults': 'No course found.',
  'admin.formationDuplicate.currentOwner': 'Current owner',
  'admin.formationDuplicate.currentInstitution': 'Source institution',
  'admin.formationDuplicate.targetInstitution': 'Copy destination institution',
  'admin.formationDuplicate.pickInstitution': 'Select an institution…',
  'admin.formationDuplicate.newOwner': 'Copy owner (trainer / HR)',
  'admin.formationDuplicate.pickOwner': 'Select an account…',
  'admin.formationDuplicate.noCandidates': 'No eligible account in this institution.',
  'admin.formationDuplicate.duplicate': 'Duplicate course',
  'admin.formationDuplicate.confirm':
    'Create a copy of this course for the selected account? The source course will not be modified.',
  'admin.formationDuplicate.success':
    'Copy created: "{{title}}" for {{email}}. New course ID: {{newFlowId}}.',
  'admin.formationDuplicate.loadInstitutionsError': 'Could not load institutions.',
  'admin.formationDuplicate.loadCandidatesError': 'Could not load eligible accounts.',
  'admin.formationMigration.title': 'Migrate a course between institutions',
  'admin.formationMigration.subtitle':
    'Search an EduFlow, pick the target institution and an account that can own it (trainer, HR, etc.). Collaborators and course categories are cleared; catalogue links, campaigns and competencies from the previous institution are removed.',
  'admin.formationMigration.searchLabel': 'Search (title or course ID)',
  'admin.formationMigration.searchPlaceholder': 'e.g. onboarding or UUID…',
  'admin.formationMigration.search': 'Search',
  'admin.formationMigration.searchMin': 'Enter at least 2 characters.',
  'admin.formationMigration.results': 'Results',
  'admin.formationMigration.noResults': 'No course found.',
  'admin.formationMigration.currentOwner': 'Current owner',
  'admin.formationMigration.currentInstitution': 'Source institution',
  'admin.formationMigration.targetInstitution': 'Target institution',
  'admin.formationMigration.pickInstitution': 'Select an institution…',
  'admin.formationMigration.newOwner': 'New owner (trainer / HR)',
  'admin.formationMigration.pickOwner': 'Select an account…',
  'admin.formationMigration.noCandidates': 'No eligible account in this institution.',
  'admin.formationMigration.migrate': 'Migrate course',
  'admin.formationMigration.confirm':
    'Confirm migration? Collaborators and categories will be cleared; inconsistent HR/catalogue links will be removed.',
  'admin.formationMigration.success': 'Done: "{{title}}" is now assigned to {{email}}.',
  'admin.formationMigration.clearedCatalog': 'Catalogue link removed (item belonged to another institution).',
  'admin.formationMigration.loadInstitutionsError': 'Could not load institutions.',
  'admin.formationMigration.loadCandidatesError': 'Could not load eligible accounts.',
  'admin.smtp.pageTitle': 'SMTP configuration',
  'admin.smtp.pageHint': 'Outgoing server for transactional platform email.',
  'admin.smtp.cardTitle': 'Outbound email (SMTP)',
  'admin.smtp.subtitle':
    'Host, port, credentials, and sender. Stored in the database — no Docker SMTP_* or EMAIL_FROM variables.',
  'admin.smtp.host': 'SMTP host',
  'admin.smtp.port': 'Port',
  'admin.smtp.secure': 'TLS/SSL (e.g. port 465)',
  'admin.smtp.user': 'Username',
  'admin.smtp.password': 'Password',
  'admin.smtp.passwordPlaceholder': 'Leave empty to keep the current password',
  'admin.smtp.passwordPlaceholderNew': 'SMTP password',
  'admin.smtp.fromEmail': 'From email',
  'admin.smtp.fromName': 'From display name',
  'admin.smtp.hint': 'Most providers require a username and password. Use the admin email test tool after saving.',
  'admin.smtp.save': 'Save SMTP',
  'admin.smtp.saved': 'SMTP settings saved',
  'admin.smtp.saveError': 'Could not save',
  'admin.smtp.loadError': 'Could not load SMTP settings',
  'admin.smtp.loading': 'Loading…',
  'admin.smtp.configured': 'Settings saved',
  'admin.smtp.notConfigured': 'Incomplete',
  'admin.shell.title': 'Administration',
  'admin.shell.expandSidebar': 'Expand sidebar',
  'admin.shell.collapseSidebar': 'Collapse sidebar',
  'admin.shell.collapse': 'Collapse menu',
  'admin.header.workspace': 'Platform administration',
  'admin.institutionDetail.pageTitle': 'Institution — Administration',
  'admin.institutionDetail.backToList': 'Back to institutions',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.title': 'Manager workspace',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.team': 'Team',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.title': 'My training',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.certificates': 'Certificates',
  'rh.page.documentTitle': 'HR dashboard - LyrraStudio',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.difficulty': 'Learning difficulties',
  'rh.compliancePage.tabs.handicap': 'Disability liaison',
  'rh.compliancePage.tabs.learnerBilan': 'Learner summary',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.tabs.regulatory': 'Regulatory watch',
  'rh.compliancePage.tabs.satisfaction': 'Satisfaction',
  'rh.regulatoryWatch.lyrraBadge': 'Lyrra Studio',
  'rh.regulatoryWatch.ofResponse': 'Our response / position',
  'rh.regulatoryWatch.ofResponsePlaceholder': 'What we did or plan to do…',
  'rh.regulatoryWatch.ackStatus': 'Status',
  'rh.regulatoryWatch.ack.pending': 'To process',
  'rh.regulatoryWatch.ack.read': 'Acknowledged (read)',
  'rh.regulatoryWatch.ack.actionTaken': 'Action taken',
  'rh.regulatoryWatch.ack.notApplicable': 'Not applicable',
  'rh.regulatoryWatch.pdf.title': 'Regulatory watch register',
  'rh.regulatoryWatch.pdf.subtitleFallback': 'Institution',
  'rh.regulatoryWatch.pdf.generatedAt': 'Generated on {{date}}',
  'rh.regulatoryWatch.pdf.colDate': 'Date',
  'rh.regulatoryWatch.pdf.colCategory': 'Cat.',
  'rh.regulatoryWatch.pdf.colTitle': 'Title',
  'rh.regulatoryWatch.pdf.colDesc': 'Description (excerpt)',
  'rh.regulatoryWatch.pdf.colImpact': 'Impact',
  'rh.regulatoryWatch.pdf.colOrigin': 'Origin',
  'rh.regulatoryWatch.pdf.colStatus': 'Status',
  'rh.regulatoryWatch.pdf.colAction': 'Action',
  'rh.regulatoryWatch.pdf.colWho': 'User',
  'rh.regulatoryWatch.pdf.colWhen': 'Timestamp',
  'rh.regulatoryWatch.pdf.originLyrra': 'Lyrra Studio',
  'rh.regulatoryWatch.pdf.originOf': 'Training org.',
  'rh.regulatoryWatch.pdf.readYes': 'Read',
  'rh.regulatoryWatch.pdf.readNo': 'Unread',
  'rh.regulatoryWatch.pdf.actionDone': 'Action done',
  'rh.regulatoryWatch.pdf.actionOpen': 'Action in progress',
  'rh.regulatoryWatch.pdf.emptyWatch': 'No items recorded',
  'rh.regulatoryWatch.pdf.auditSection': 'Internal audit log',
  'rh.regulatoryWatch.pdf.auditHint': 'Actions recorded automatically by the platform.',
  'rh.regulatoryWatch.pdf.emptyAudit': 'No recent events',
  'rh.regulatoryWatch.exportPdf': 'Export PDF',
  'rh.regulatoryWatch.exportPdfBusy': 'Exporting PDF…',
  'rh.regulatoryWatch.exportPdfError': 'PDF export failed',
  'rh.compliance': 'Compliance',
  'rh.complianceAuditAction.qualiopi_corrective_action_create': 'Qualiopi corrective action created',
  'rh.complianceAuditAction.qualiopi_corrective_action_delete': 'Qualiopi corrective action deleted',
  'rh.complianceAuditAction.qualiopi_corrective_action_update': 'Qualiopi corrective action updated',
  'rh.complianceAuditAction.qualiopi_evidence_create': 'Qualiopi evidence added',
  'rh.complianceAuditAction.qualiopi_indicator_update': 'Qualiopi indicator updated',
  'rh.complianceAuditAction.compliance_regulatory_watch_create': 'Regulatory watch item created',
  'rh.complianceAuditAction.compliance_regulatory_watch_update': 'Regulatory watch item updated',
  'rh.complianceAuditAction.compliance_regulatory_watch_delete': 'Regulatory watch item deleted',
  'rh.auditDateUnknown': 'Not set',
  'rh.auditScheduleHint': 'Set in institution settings',
  'rh.qualiopiEmpty': 'No Qualiopi data available for this institution.',
  'rh.qualiopiLoading': 'Loading Qualiopi reference data…',
  'v2.compliance': 'Compliance & Qualiopi',
  'rh.dashboard.title': 'HR dashboard',
  'rh.dashboard.subtitle': 'Training & development overview',
  'rh.dashboard.activeCampaigns': 'Active campaigns',
  'rh.dashboard.pendingAssignmentsKpi': 'Assignments (pending / accepted)',
  'rh.dashboard.pendingRequestsCount': 'Pending requests',
  'rh.dashboard.completionRateLabel': 'Completion rate (learning paths)',
  'rh.dashboard.budgetUsed': 'Campaign budget used',
  'rh.dashboard.budgetNotSet': 'Not set',
  'rh.dashboard.progressLabel': 'Progress (%)',
  'rh.dashboard.chartCampaignsTitle': 'Campaign progress',
  'rh.dashboard.chartFormationsTitle': 'Learning path progress',
  'rh.dashboard.chartCampaignsHint': 'Average assignment completion rate per campaign.',
  'rh.dashboard.chartFormationsHint': 'Share of learners who completed the path (of current enrollments).',
  'rh.dashboard.upcomingDeadlines': 'Upcoming deadlines',
  'rh.dashboard.recentActivity': 'Recent activity',
  'rh.dashboard.recentActivityHint': 'Campaign assignments and completed paths.',
  'rh.dashboard.viewAll': 'View all',
  'rh.dashboard.exportReport': 'Export report',
  'rh.dashboard.exportSoon': 'Export coming soon.',
  'rh.dashboard.newCampaign': 'New campaign',
  'rh.dashboard.campaignProgress': 'Campaign progress',
  'rh.dashboard.createCampaign': 'Create campaign',
  'rh.dashboard.reviewRequests': 'Review requests',
  'rh.dashboard.skillsMap': 'Skills framework',
  'rh.dashboard.budgetOverview': 'Budget overview',
  'rh.dashboard.loadError': 'Could not load HR dashboard.',
  'rh.dashboard.emptyChart': 'No data to display yet.',
  'rh.dashboard.emptyDeadlines': 'No upcoming campaign deadlines.',
  'rh.dashboard.emptyRecent': 'No recent activity.',
  'rh.dashboard.kindAssignment': 'Campaign',
  'rh.dashboard.kindCompletion': 'Path completed',
  'rh.dashboard.justNow': 'Just now',
  'rh.dashboard.learnerInsightTitle': 'Training operations (live data)',
  'rh.dashboard.learnerInsightHint':
    'Aggregated training enrollments: scores from graded blocks, block friction, latest evaluations.',
  'rh.dashboard.enrollmentsTotal': 'Enrollments',
  'rh.dashboard.completionGlobal': 'Completed',
  'rh.dashboard.avgTimePerEnrollment': 'Avg. time per enrollment',
  'rh.dashboard.notStarted': 'Not started',
  'rh.dashboard.stalled': 'Stalled (>30d inactive after start)',
  'rh.dashboard.topFlowsDetail': 'Most subscribed paths',
  'rh.dashboard.colFlow': 'Path',
  'rh.dashboard.colEnrolled': 'Enrolled',
  'rh.dashboard.colCompletedPct': 'Completion',
  'rh.dashboard.colAvgTime': 'Avg. time',
  'rh.dashboard.colAvgScore': 'Avg. score',
  'rh.dashboard.blockFrictionTitle': 'Highest-friction blocks (≥3 sessions)',
  'rh.dashboard.colBlock': 'Block',
  'rh.dashboard.colSessions': 'Sessions',
  'rh.dashboard.colBlockCompletion': 'Block completion',
  'rh.dashboard.recentEvalTitle': 'Latest evaluations',
  'rh.dashboard.evalComment': 'Has comment',
  'rh.dashboard.exportOk': 'CSV downloaded.',
  'rh.dashboard.exportFail': 'Download failed.',
  'institution.dashboard.trainingCardTitle': 'Training follow-up (enrollments)',
  'institution.dashboard.trainingCardHint': 'Aggregated across all institution learning paths.',
  'institution.dashboard.trainingCompleted': 'paths completed',
  'institution.dashboard.trainingAvgMinutes': 'avg min / enrollment',
  'rh.status.urgent': 'Urgent',
  'rh.status.soon': 'Soon',
  'rh.status.upcoming': 'Upcoming',
  'rh.status.pending': 'Pending',
  'rh.status.approved': 'Approved',
  'rh.status.rejected': 'Rejected',
  'producerAssignments.intro':
    'This page lists only content HR has assigned to you: catalog “shell” items to turn into EduFlow courses in the designer, and podcasts you are set as producer for. Empty lists mean nothing is assigned yet.',
  'producerAssignments.errorForbidden':
    'Access denied: your role cannot use this page. If you are a trainer or HR for your organization, contact an administrator.',
  'producerAssignments.errorNoInstitution':
    'Your account has no institution linked in the database. An administrator must attach your user to the organization, then sign in again.',
  'producerAssignments.errorGeneric': 'Could not load your assignments.',
  // Common
  'common.loading': 'Loading...',
  'common.refresh': 'Refresh',
  'common.saving': 'Saving...',
  'common.saved': 'Saved',
  'common.error': 'Error',
  'common.success': 'Success',
  'common.warning': 'Warning',
  'common.info': 'Information',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.or': 'or',
  'common.and': 'and',
  'common.all': 'All',
  'common.none': 'None',
  'common.total': 'Total',
  'common.actions': 'Actions',
  'common.created_at': 'Created at',
  'common.updated_at': 'Updated at',
  'common.sort_by': 'Sort by',
  'common.per_page': 'Per page',
  'common.page': 'Page',
  'common.of': 'of',
  'common.results': '{{count}} result(s)',
  'common.no_data': 'No data',
  'common.coming_soon': 'Coming soon',
  'common.beta': 'Beta',
  'common.new': 'New',
  'common.delete_confirm': 'This action is irreversible. Continue?',
  'common.copied': 'Copied!',
  'common.drag_drop': 'Drag and drop',
  'common.online': 'Online',
  'common.offline': 'Offline',
  'common.version': 'Version',
  // Player
  'player.next': 'Next',
  'player.previous': 'Previous',
  'player.scrollDownHint': 'Scroll down for more',
  'player.image.clickForAnnotations': 'Click the image to view annotations',
  'player.image.annotationNoDescription': 'No description for this annotation.',
  'player.image.noneAvailable': 'No image available',
  'player.visualAi.emptyTitle': 'No AI visual configured',
  'player.visualAi.emptyHint': 'Return to the designer to generate or configure this visual.',
  'player.visualAi.defaultTitle': 'AI visual',
  'player.visualAi.subtitle': 'Explore the diagram: zoom, scroll, or use full screen mode.',
  'player.visualAi.openFullscreen': 'Open full screen',
  'player.visualAi.closeFullscreen': 'Close full screen',
  'player.visualAi.badge': 'AI visual',
  'player.visualAi.viewportLabel': 'Interactive diagram — zoom and pan',
  'player.visualAi.toolbarLabel': 'Diagram controls',
  'player.visualAi.zoomOut': 'Zoom out',
  'player.visualAi.zoomIn': 'Zoom in',
  'player.visualAi.resetZoom': 'Reset zoom to 100%',
  'player.visualAi.reset': '100%',
  'player.visualAi.fitView': 'Fit diagram to window',
  'player.visualAi.fit': 'Fit',
  'player.visualAi.zoomLevel': 'Zoom {{percent}}%',
  'player.visualAi.viewportHint':
    'Ctrl (or Cmd) + scroll wheel to zoom. Arrow keys to pan when the viewport is focused. Tab to reach the controls.',
  'player.manualVoiceoverMic': 'Record voice-over for this language',
  'player.manualVoiceoverStudioTitle': 'Voice-over recording studio',
  'player.manualVoiceoverStudioLang': 'Recording language',
  'player.manualVoiceoverRecord': 'Record',
  'player.manualVoiceoverStop': 'Stop',
  'player.manualVoiceoverPlay': 'Listen',
  'player.manualVoiceoverSave': 'Save to block',
  'player.manualVoiceoverClose': 'Close',
  'player.manualVoiceoverHint':
    'On the right: block text or image in the selected language. Record, then save to attach the audio to this language.',
  'player.manualVoiceoverPreviewLoading': 'Loading content…',
  'player.manualVoiceoverNoMic': 'Microphone or recording is not available in this browser.',
  'player.manualVoiceoverNoText': 'No description text long enough for this block.',
  'player.manualVoiceoverSaveError': 'Could not save the voice-over to the block.',
  'player.manualVoiceoverUploadError': 'Audio upload failed.',
  'player.manualVoiceoverSaved': 'Voice-over saved to the block.',
  'eduflow.versionHistory': 'Version history',
  'eduflow.versionHistory.count': '{{count}} version(s)',
  'eduflow.versionHistory.create': 'Create a version',
  'eduflow.versionHistory.createVersion': 'Create v{{version}}',
  'eduflow.versionHistory.minor': 'Minor',
  'eduflow.versionHistory.major': 'Major',
  'eduflow.versionHistory.minorBadge': 'Minor version',
  'eduflow.versionHistory.majorBadge': 'Major version',
  'eduflow.versionHistory.latest': 'Latest',
  'eduflow.versionHistory.public': 'Public version',
  'eduflow.versionHistory.publicActive': 'Live',
  'eduflow.versionHistory.publish': 'Publish this version',
  'eduflow.versionHistory.publishCta': 'Publish',
  'eduflow.versionHistory.openPublicPlayer': 'Open public player',
  'eduflow.versionHistory.copyPublicPlayer': 'Copy public link',
  'eduflow.versionHistory.copyPublicPlayerError': 'Unable to copy the public link',
  'eduflow.versionHistory.systemPublished': 'Published {{version}}',
  'eduflow.versionHistory.systemSaved': 'Saved {{version}}',
  'eduflow.versionHistory.systemAutosaveRestore': 'Auto-save before restoring {{version}}',
  'eduflow.versionHistory.descriptionPlaceholder': 'Describe the changes…',
  'eduflow.versionHistory.empty': 'No version created yet.',
  'eduflow.versionHistory.emptyHint': 'Save a version to access it here.',
  'eduflow.versionHistory.blocks': '{{count}} blocks',
  'eduflow.versionHistory.restoreTitle': 'Restore version v{{version}}?',
  'eduflow.versionHistory.restoreWarning': 'The current content will be replaced. An automatic backup will be created before the restore.',
  'eduflow.versionHistory.createSuccess': 'Version {{version}} created',
  'eduflow.versionHistory.createError': 'Error while creating the version',
  'eduflow.versionHistory.restoreSuccess': 'Version {{version}} restored',
  'eduflow.versionHistory.restoreError': 'Error while restoring the version',
  'eduflow.versionHistory.publishSuccess': 'Version {{version}} published',
  'eduflow.versionHistory.publishError': 'Error while publishing the version',
  'eduflow.versionHistory.now': 'just now',
  'eduflow.versionHistory.minutesAgo': '{{count}} min ago',
  'eduflow.versionHistory.hoursAgo': '{{count}} h ago',
  'eduflow.versionHistory.yesterday': 'yesterday',
  'eduflow.versionHistory.daysAgo': '{{count}} days ago',
  // Podcast — episodes list & create
  'podcast.episodes.pageTitle': 'Episodes',
  'podcast.episodes.count': '{{count}} episode(s)',
  'podcast.episodes.newEpisode': 'New episode',
  'podcast.episodes.searchPlaceholder': 'Search by title…',
  'podcast.episodes.filterAllStatuses': 'All statuses',
  'podcast.episodes.filterAllPodcasts': 'All podcasts',
  'podcast.episodes.filterDraft': 'Draft',
  'podcast.episodes.filterPublished': 'Published',
  'podcast.episodes.unassignedPodcast': 'No podcast',
  'podcast.episodes.generateVoiceOff': 'Generate voice-over',
  'podcast.episodes.generateVoiceConfirmTitle': 'Generate voice-over?',
  'podcast.episodes.generateVoiceConfirmMessage':
    'Episode « {{title}} » will be synthesized ({{count}} segment(s)). This may take several minutes.',
  'podcast.episodes.generateVoiceConfirmButton': 'Generate',
  'podcast.episodes.generateNoBlocks':
    'No script text or microphone recording on this episode — open the editor to add content.',
  'podcast.episodes.generateSuccess': 'Voice-over generated for « {{title}} » ({{count}} segment(s)).',
  'podcast.episodes.generateError': 'Voice-over generation failed.',
  'podcast.episodes.publish': 'Publish episode',
  'podcast.episodes.publishConfirmTitle': 'Publish this episode?',
  'podcast.episodes.publishConfirmMessage':
    '« {{title}} » will be visible to learners assigned to the podcast (published status).',
  'podcast.episodes.publishConfirmButton': 'Publish',
  'podcast.episodes.publishSuccess': '« {{title}} » is published.',
  'podcast.episodes.publishError': 'Publication failed.',
  'podcast.episodes.emptyTitle': 'No episodes found',
  'podcast.episodes.emptyHint': 'Create your first episode to get started',
  'podcast.episodes.emptyCreateCta': 'Create an episode',
  'podcast.episodes.backPodcasts': 'Podcasts',
  'podcast.episodes.paginationTotal': '{{total}} episode(s) total',
  'podcast.episodes.pageOf': 'Page {{page}} / {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Delete this episode?',
  'podcast.episodes.deleteHint': '“{{title}}” will be permanently deleted.',
  'podcast.episodes.deleteCancel': 'Cancel',
  'podcast.episodes.deleteConfirm': 'Delete',
  'podcast.episodes.statusPublished': 'Published',
  'podcast.episodes.statusDraft': 'Draft',
  'podcast.episodes.noDescription': 'No description',
  'podcast.episodes.noAudio': 'No audio',
  'podcast.episodes.audioReady': 'Audio ready',
  'podcast.episodes.retry': 'Retry',
  'podcast.createEpisode.panelTitle': 'New episode',
  'podcast.createEpisode.panelSubtitle':
    'Enter a title and optionally a description and cover image. After you confirm, the episode is created and the editor opens to build the content.',
  'podcast.createEpisode.titleLabel': 'Title',
  'podcast.createEpisode.descriptionLabel': 'Description',
  'podcast.createEpisode.coverLabel': 'Cover image (optional)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP or GIF — max 5 MB.',
  'podcast.createEpisode.coverChoose': 'Choose image',
  'podcast.createEpisode.coverRemove': 'Remove image',
  'podcast.createEpisode.titlePlaceholder': 'e.g. Safety module introduction',
  'podcast.createEpisode.descriptionPlaceholder': 'Summary, goals… (optional)',
  'podcast.createEpisode.validate': 'Create and open editor',
  'podcast.createEpisode.titleRequired': 'Title is required.',
  'podcast.createEpisode.error': 'Could not create the episode.',
  'podcast.createEpisode.uploadCoverError': 'Episode created, but cover upload failed.',
  'podcast.createEpisode.coverTooLarge': 'Image must be 5 MB or smaller.',
  'podcast.editor.defaultNewTitle': 'New episode',
  'eduflow.title': 'EduFlow editor',
  'eduflow.new_flow': 'New course',
  'eduflow.list.title': 'Courses',
  'eduflow.list.subtitle': 'Design your interactive learning experiences',
  'eduflow.list.searchPlaceholder': 'Search a course…',
  'eduflow.list.import': 'Import',
  'eduflow.list.importHint': '.lyrra package (media included) or legacy Lyrra V1 JSON export.',
  'eduflow.list.importSuccess': 'Course imported. Opening the designer.',
  'eduflow.list.importError':
    'Could not import — check the file (valid .lyrra or JSON export, at least one block).',
  'eduflow.list.filterAll': 'All',
  'eduflow.list.filterDraft': 'Drafts',
  'eduflow.list.filterPublished': 'Published',
  'eduflow.list.filterArchived': 'Archived',
  'eduflow.list.archiveFormation': 'Archive course',
  'eduflow.list.restoreFromArchive': 'Restore from archive (draft)',
  'eduflow.list.archiveSuccess': 'Course archived',
  'eduflow.list.archiveError': 'Could not archive the course.',
  'eduflow.list.restoreSuccess': 'Course restored as draft',
  'eduflow.list.restoreError': 'Could not restore the course.',
  'eduflow.list.viewModeGrid': 'Grid view',
  'eduflow.list.viewModeList': 'List view',
  'eduflow.list.viewByCategory': 'Category view',
  'eduflow.list.categoryBack': 'All categories',
  'eduflow.list.categoryBrowseHint': 'Pick a category to see its courses.',
  'eduflow.list.categoryFormationCount': '{{count}} course(s)',
  'eduflow.list.categoryEmptyFiltered': 'No courses in this view (active filter or search).',
  'eduflow.list.uncategorizedCardHint': 'Courses without a category tag.',
  'eduflow.list.uncategorized': 'Uncategorized',
  'eduflow.list.manageCategories': 'Manage categories',
  'eduflow.list.categoriesPanelTitle': 'Course categories',
  'eduflow.list.noCategoryAvailable': 'No category available.',
  'eduflow.list.categoriesSaveError': 'Unable to save categories.',
  'eduflow.list.emptyFilteredTitle': 'No results',
  'eduflow.list.emptyFilteredHint': 'Try changing your search or filters.',
  'eduflow.list.emptyFirstTitle': 'Create your first course',
  'eduflow.list.emptyFirstHint': 'Build interactive learning with the visual designer.',
  'eduflow.list.emptyCreateCta': 'Create my first course',
  'eduflow.list.deleteToTrashTitle': 'Move this course to trash?',
  'eduflow.list.deleteToTrashBody':
    '“{{title}}” will be removed from your list (soft delete). Your organization can restore it from the institution trash.',
  'eduflow.list.deleteToTrashConfirm': 'Move to trash',
  'eduflow.createFormation.panelTitle': 'New course',
  'eduflow.createFormation.panelSubtitle':
    'Enter a title and description. After you confirm, the course is created and the designer opens to build the flow.',
  'eduflow.createFormation.titleLabel': 'Title',
  'eduflow.createFormation.descriptionLabel': 'Description',
  'eduflow.createFormation.titlePlaceholder': 'e.g. Safety module introduction',
  'eduflow.createFormation.descriptionPlaceholder': 'Goals, audience, etc. (optional)',
  'eduflow.createFormation.validate': 'Create and open designer',
  'eduflow.createFormation.titleRequired': 'Title is required.',
  'eduflow.createFormation.error': 'Could not create the course.',
  'eduflow.shareFormation.title': 'Share course',
  'eduflow.shareFormation.publicLinkTitle': 'Public link (no sign-in)',
  'eduflow.shareFormation.publicLinkCopy': 'Copy link',
  'eduflow.shareFormation.publicLinkCopied': 'Copied',
  'eduflow.shareFormation.publishRequired': 'Publish the course to get a link that works without sign-in.',
  'eduflow.shareFormation.embedTitle': 'Embed button and player modal',
  'eduflow.shareFormation.embedHint':
    'Paste this block on your site. Include the script only once per page; for more courses, add extra buttons by copying the button line and changing the URL in data-lyrra-eduflow-url. The player URL includes sidebar=0 so the outline starts collapsed in the modal embed.',
  'eduflow.shareFormation.embedCopy': 'Copy HTML code',
  'eduflow.shareFormation.embedCopied': 'Copied',
  'eduflow.shareFormation.embedCloseAria': 'Close',
  'eduflow.shareFormation.embedButtonLabel': 'Open: {{title}}',
  'eduflow.shareFormation.embedToggle': 'Website embed',
  'eduflow.shareFormation.removeAccessSuccess': 'Access removed for the learner.',
  'eduflow.shareFormation.removeAccessError': 'Unable to remove learner access.',
  'eduflow.shareFormation.assignSuccess': 'Course shared and invitation email sent.',
  'eduflow.shareFormation.assignError': 'Unable to assign this learner.',
  'eduflow.shareFormation.createAssignSuccess': 'Account created/linked, course assigned, and invitation email sent.',
  'eduflow.shareFormation.createLearnerError': 'Unable to create learner.',
  'eduflow.shareFormation.selectExistingLearner': 'Select an existing learner',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Search learner (first name, last name, email)',
  'eduflow.shareFormation.loadingLearners': 'Loading learners...',
  'eduflow.shareFormation.noLearnerAvailable': 'No learner available.',
  'eduflow.shareFormation.hideQuickCreate': 'Hide quick create',
  'eduflow.shareFormation.createNewLearner': 'Create a new learner',
  'eduflow.shareFormation.firstNameRequired': 'First name *',
  'eduflow.shareFormation.lastNameRequired': 'Last name *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': 'Create, share, and send invitation',
  'eduflow.shareFormation.assignedLearnersCount_one': '{{count}} learner assigned',
  'eduflow.shareFormation.assignedLearnersCount_other': '{{count}} learners assigned',
  'eduflow.shareFormation.noAssignedLearner': 'No learner assigned to this course.',
  'eduflow.shareFormation.removeAccessTitle': 'Remove access',
  'eduflow.shareFormation.inviteEmailsTitle': 'Invite by email (existing account)',
  'eduflow.shareFormation.inviteEmailsHint':
    'One or more addresses: LyrraStudio accounts only (including another institution). People without an account are skipped — use “Create new learner” to invite them.',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'One address per line or comma-separated\nexample@company.com, other@org.fr',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Add these accounts to the course',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Enter at least one valid email address.',
  'eduflow.shareFormation.inviteEmailsError': 'Could not add the addresses.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} account(s) added.',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} with no LyrraStudio account.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} already assigned.',
  'eduflow.participant.flowTitle': 'Learning path',
  'eduflow.participant.stepWelcome': 'Welcome',
  'eduflow.participant.stepModule1': 'Module 1',
  'eduflow.participant.stepEvaluation': 'Assessment',
  'eduflow.participant.stepContentPlaceholder': 'Interactive step content displayed for the learner.',
  'eduflow.edit_flow': 'Edit path',
  'eduflow.flow_name': 'Path name',
  'eduflow.block_palette': 'Block palette',
  'eduflow.canvas': 'Canvas',
  'eduflow.properties': 'Properties',
  'eduflow.publish': 'Publish',
  'eduflow.publish_confirm': 'Are you sure you want to publish this path?',
  'eduflow.save_draft': 'Save draft',
  'eduflow.preview': 'Preview',
  'eduflow.versions': 'Versions',
  'eduflow.add_block': 'Add block',
  'eduflow.remove_block': 'Remove block',
  'eduflow.connect_blocks': 'Connect blocks',
  'eduflow.undo': 'Undo',
  'eduflow.redo': 'Redo',
  'eduflow.zoom_in': 'Zoom in',
  'eduflow.zoom_out': 'Zoom out',
  'eduflow.fit_view': 'Fit view',
  'eduflow.auto_layout': 'Auto layout',
  'eduflow.unsaved_changes': 'Unsaved changes',
  'eduflow.blocks.quiz_mcq': 'MCQ',
  'eduflow.blocks.quiz_open': 'Open question',
  'eduflow.blocks.video': 'Video',
  'eduflow.blocks.audio': 'Audio',
  'eduflow.blocks.text': 'Text',
  'eduflow.blocks.evaluation': 'Assessment',
  'eduflow.blocks.form': 'Form',
  'eduflow.blocks.mindmap': 'Mind map',
  'eduflow.blocks.poll': 'Poll',
  'eduflow.blocks.flashcard': 'Flashcard',
  'eduflow.blocks.timeline': 'Timeline',
  'eduflow.blocks.pdf': 'PDF document',
  'eduflow.blocks.image': 'Image',
  'eduflow.blocks.code': 'Code',
  'eduflow.blocks.whiteboard': 'Whiteboard',
  'eduflow.blocks.ai_chat': 'AI chat',
  'eduflow.blocks.certificate': 'Certificate',
  'eduflow.blocks.attendance': 'Attendance',
  'eduflow.blocks.sortable': 'Ranking',
  'eduflow.blocks.pair_matching': 'Matching',
  'eduflow.blocks.drawing': 'Drawing',
  'eduflow.blocks.embed': 'Embedded content',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Condition',
  'eduflow.blocks.timer': 'Timer',
  'eduflow.blocks.randomizer': 'Random',
  'eduflow.blocks.grouping': 'Grouping',
  'eduflow.blocks.debate': 'Debate',
  'eduflow.blocks.roleplay': 'Role play',
  'eduflow.blocks.simulation': 'Simulation',
  'eduflow.blocks.scenario': 'Scenario',
  'eduflow.blocks.checklist': 'Checklist',
  'eduflow.blocks.kanban': 'Kanban',
  'eduflow.blocks.peer_review': 'Peer review',
  'eduflow.designer.paletteCategory.structure': 'Path & structure',
  'eduflow.designer.paletteCategory.content': 'Content & media',
  'eduflow.designer.paletteCategory.interactive': 'Activities & assessment',
  'eduflow.designer.paletteCategory.accessibility': 'Accessibility (DYS)',
  'eduflow.designer.paletteCategory.enterprise': 'Compliance & advanced',
  'eduflow.designer.flowSettings.toolbarTitle': 'Course & player settings',
  'eduflow.designer.flowSettings.panelTitle': 'Course settings',
  'eduflow.designer.flowSettings.intro':
    'Shown in the player (title, description, cover). You can hide the “Previous” button for a strictly linear path.',
  'eduflow.designer.flowSettings.titleLabel': 'Course title',
  'eduflow.designer.flowSettings.descriptionLabel': 'Course description',
  'eduflow.designer.flowSettings.coverLabel': 'Cover image',
  'eduflow.designer.flowSettings.coverUpload': 'Upload an image',
  'eduflow.designer.flowSettings.coverUrlPlaceholder': 'Or paste an image URL (https://…)',
  'eduflow.designer.flowSettings.coverRemove': 'Remove cover',
  'eduflow.designer.flowSettings.allowPreviousLabel': '“Previous” button in the player',
  'eduflow.designer.flowSettings.allowPreviousHint':
    'When off, learners cannot go back to the previous block (including keyboard shortcuts).',
  'eduflow.designer.flowSettings.focusGuard.title': 'Focus loss check',
  'eduflow.designer.flowSettings.focusGuard.hint':
    'If the learner leaves the tab or page, the training is paused (dimmed screen, click to resume).',
  'eduflow.designer.flowSettings.focusGuard.limitLabel': 'Allowed exits',
  'eduflow.designer.flowSettings.focusGuard.unlimited': 'Unlimited',
  'eduflow.designer.flowSettings.focusGuard.limited': 'Limited',
  'eduflow.designer.flowSettings.focusGuard.lossesUnit': 'allowed focus loss(es)',
  'eduflow.designer.flowSettings.focusGuard.blockWarning':
    'Beyond this number, the learner can no longer continue the training.',
  'eduflow.designer.flowSettings.coverUploadError': 'Could not upload the cover image.',
  'eduflow.designer.flowSettings.certificate.uploadError': 'Invalid server response.',
  'eduflow.designer.flowSettings.certificate.uploadFailed': 'Could not upload the Word template.',
  'eduflow.designer.blockLabel.start': 'Start',
  'eduflow.designer.blockLabel.end': 'End',
  'eduflow.designer.blockLabel.summary': 'Summary',
  'eduflow.designer.blockLabel.form': 'Form',
  'eduflow.designer.blockLabel.loop': 'Loop',
  'eduflow.designer.blockLabel.merge': 'Merge',
  'eduflow.designer.blockLabel.split': 'Branch',
  'eduflow.designer.blockLabel.subflow': 'Sub-training',
  'eduflow.designer.blockLabel.timer': 'Timer',
  'eduflow.designer.blockLabel.email': 'Automated email',
  'eduflow.designer.blockLabel.text': 'Text',
  'eduflow.designer.blockLabel.podcast': 'Podcast',
  'eduflow.designer.blockLabel.video': 'Video',
  'eduflow.designer.blockLabel.audio': 'Audio',
  'eduflow.designer.blockLabel.image': 'Annotated image',
  'eduflow.designer.blockLabel.pdf': 'PDF document',
  'eduflow.designer.blockLabel.browser': 'Embedded web page',
  'eduflow.designer.blockLabel.presentation': 'Slideshow',
  'eduflow.designer.blockLabel.timeline': 'Timeline',
  'eduflow.designer.blockLabel.chart': 'Interactive chart',
  'eduflow.designer.blockLabel.mindmap': 'Mind map',
  'eduflow.designer.blockLabel.visual_ai': 'AI visual',
  'eduflow.designer.blockLabel.quiz_mcq': 'Quiz (MCQ / T-F / order)',
  'eduflow.designer.blockLabel.poll': 'Live poll',
  'eduflow.designer.blockLabel.self_assessment': 'Self-assessment',
  'eduflow.selfAssessment.panelTitle': 'Self-assessment',
  'eduflow.selfAssessment.panelHint':
    'Define each dimension to rate: a short title and, if needed, a description to guide the learner.',
  'eduflow.selfAssessment.scaleLabel': 'Rating scale',
  'eduflow.selfAssessment.scaleHint': 'From 1 to N (e.g. 5 = Likert)',
  'eduflow.selfAssessment.criteriaSection': 'Criteria',
  'eduflow.selfAssessment.addCriterion': 'Add',
  'eduflow.selfAssessment.emptyCriteria':
    'No criteria yet. Add at least one so learners can self-assess.',
  'eduflow.selfAssessment.criterionTitle': 'Title',
  'eduflow.selfAssessment.criterionTitlePh': 'e.g. Communication, Autonomy…',
  'eduflow.selfAssessment.criterionDescription': 'Description (optional)',
  'eduflow.selfAssessment.criterionDescriptionPh':
    'Clarify what the learner should reflect on or observe…',
  'eduflow.selfAssessment.moveUp': 'Move up',
  'eduflow.selfAssessment.moveDown': 'Move down',
  'eduflow.selfAssessment.removeCriterion': 'Remove this criterion',
  'eduflow.selfAssessment.responseTypeLabel': 'Response type',
  'eduflow.selfAssessment.typeNumeric': 'Numbers',
  'eduflow.selfAssessment.typeNumericHint': 'Buttons 1 to N',
  'eduflow.selfAssessment.typeStars': 'Stars',
  'eduflow.selfAssessment.typeStarsHint': 'Visual rating',
  'eduflow.selfAssessment.typeText': 'Free text',
  'eduflow.selfAssessment.typeTextHint': 'Badges (short words) or free text per criterion',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Options (badges)',
  'eduflow.selfAssessment.badgeOptionsHint':
    'Short words or phrases — learners pick them as chips. If empty → free-text field.',
  'eduflow.selfAssessment.removeBadge': 'Remove',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'e.g. Often, Never, Sometimes…',
  'eduflow.selfAssessment.addBadge': 'Add',
  'eduflow.selfAssessment.starCountLabel': 'Number of stars',
  'eduflow.selfAssessment.starCountHint': 'Often 5',
  'eduflow.selfAssessment.textModeHint':
    'Learners write an answer for each criterion. The global comment field remains below in the player.',
  'eduflow.designer.blockLabel.evaluation': 'Multi-question assessment',
  'eduflow.designer.blockLabel.phrase_reveal': 'Progressive reveal',
  'eduflow.designer.blockLabel.voice_assessment': 'Voice assessment',
  'eduflow.designer.blockLabel.dys_reader': 'DYS reader (syllables + TTS)',
  'eduflow.designer.blockLabel.dys_image_zones': 'Interactive image zones',
  'eduflow.designer.blockLabel.dys_clock': 'DYS color clock',
  'eduflow.designer.blockLabel.dys_reading_practice': 'Adapted reading exercise',
  'eduflow.designer.blockLabel.glossary': 'Glossary + built-in quiz',
  'eduflow.designer.blockLabel.html': 'Custom HTML / JavaScript',
  'eduflow.designer.blockLabel.certification': 'Certificate (Word template)',
  'eduflow.designer.blockLabel.signature': 'Learner signature',
  'eduflow.designer.blockLabel.facial_recognition': 'Facial recognition',
  'eduflow.designer.blockLabel.attendance': 'Digital attendance',
  'eduflow.designer.startBlockTitle': 'Start',
  'eduflow.designer.placeholderUntitled': 'Untitled',
  'eduflow.designer.placeholderFlowDescription': 'Training description (max 255 characters)',
  'eduflow.designer.saving': 'Saving…',
  'eduflow.designer.unsaved': 'Not saved',
  'eduflow.designer.saved': 'Saved',
  'eduflow.designer.blocksPanelTitle': 'Blocks',
  'eduflow.designer.paletteSearchLabel': 'Search blocks',
  'eduflow.designer.paletteSearchPlaceholder': 'Search blocks…',
  'eduflow.designer.paletteSearchEmpty': 'No blocks match your search.',
  'eduflow.designer.autoLayoutShort': 'Auto-layout',
  'eduflow.designer.validate': 'Validate',
  'eduflow.designer.validateTitle': 'Validate path',
  'eduflow.designer.validationBeforePreview': 'Fix path errors before preview.',
  'eduflow.designer.validationBeforePublish': 'Fix path errors before publishing.',
  'eduflow.designer.aiShort': 'AI',
  'eduflow.designer.aiTitle': 'AI assistant',
  'eduflow.designer.ragChatbotTitle': 'Course chatbot',
  'eduflow.ragChatbot.panelTitle': 'Course chatbot (RAG)',
  'eduflow.ragChatbot.loadError': 'Could not load chatbot settings.',
  'eduflow.ragChatbot.needDoc': 'Add at least one document before enabling the chatbot.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot enabled for the player.',
  'eduflow.ragChatbot.disabledOk': 'Chatbot disabled.',
  'eduflow.ragChatbot.toggleError': 'Could not update.',
  'eduflow.ragChatbot.uploadOk': 'Document indexed.',
  'eduflow.ragChatbot.uploadSavedNotIndexed':
    'Document saved. It will not be used for RAG until the vector store is configured on the server.',
  'eduflow.ragChatbot.ragIndexFailed':
    'Document saved but not indexed. Check pgvector (DATABASE_VECTOR_URL) and Mistral, then re-upload if needed.',
  'eduflow.ragChatbot.uploadError': 'Upload or indexing failed.',
  'eduflow.ragChatbot.confirmDelete': 'Delete this document and its index?',
  'eduflow.ragChatbot.deleted': 'Document removed.',
  'eduflow.ragChatbot.deleteError': 'Could not delete.',
  'eduflow.ragChatbot.vectorHint':
    'To index PDF / TXT / MD: set DATABASE_VECTOR_URL or PGVECTOR_HOST (PostgreSQL + pgvector) and Mistral in Admin → Platform integrations (API key + embedding model). Otherwise upload and indexing fail.',
  'eduflow.ragChatbot.chatHint':
    'Player answers and RAG embeddings use Mistral. Uploaded documents are retrieved with Mistral embeddings + pgvector; with no documents, only block text is used as context.',
  'eduflow.ragChatbot.playerToggle': 'Show in player',
  'eduflow.ragChatbot.playerToggleHint':
    'Learners ask questions only about this course: block text in the path, plus indexed documents if you added any.',
  'eduflow.ragChatbot.addDocument': 'Add document (PDF, TXT, MD)',
  'eduflow.ragChatbot.docCount': 'Indexed documents: {{count}}',
  'eduflow.ragChatbot.emptyDocs':
    'No documents. The chatbot will use course block text only.',
  'eduflow.ragChatbot.open': 'Open assistant',
  'eduflow.ragChatbot.dialogTitle': 'Course assistant',
  'eduflow.ragChatbot.playerDisclaimer':
    'Answers are based on this course’s documents and content only.',
  'eduflow.ragChatbot.playerWelcome': 'Ask a question about the course or the provided documents.',
  'eduflow.ragChatbot.playerError': 'Could not get a response. Please try again later.',
  'eduflow.ragChatbot.placeholder': 'Your question…',
  'eduflow.ragChatbot.send': 'Send',
  'eduflow.designer.aiPanel.welcome':
    'Describe what you want. The block open in **Properties** is the default target. Use the **crosshair** to capture the screen (canvas + form) for visual analysis and direct edits.',
  'eduflow.designer.aiPanel.askBeforeAct': 'Ask before acting',
  'eduflow.designer.aiPanel.askBeforeActHint':
    'When on, changes are shown in chat before applying to the graph.',
  'eduflow.designer.aiPanel.captureScreenHint': 'Capture canvas and Properties panel',
  'eduflow.designer.aiPanel.captureReady': 'Screenshot ready — send your instruction.',
  'eduflow.designer.aiPanel.captureFailed': 'Could not capture the screen.',
  'eduflow.designer.aiPanel.captureAttached': 'Screenshot will be sent with your next message',
  'eduflow.designer.aiPanel.removeCapture': 'Remove',
  'eduflow.designer.aiPanel.captureInMessage': 'Screen capture',
  'eduflow.designer.aiPanel.statusCapturing': 'Capturing the interface…',
  'eduflow.designer.aiPanel.statusAnalyzing': 'Analyzing screen and focused block…',
  'eduflow.designer.aiPanel.statusWorking': 'Working on your request…',
  'eduflow.designer.aiPanel.statusApplying': 'Applying to the canvas…',
  'eduflow.designer.aiPanel.captureDisclaimer':
    'AI can make mistakes — review generated content. Paste an image (Ctrl+V) or use capture.',
  'eduflow.designer.resizeAiPanel': 'Resize AI assistant panel',
  'eduflow.designer.aiPanel.subtitle': 'Copilot: content, graph, and objectives.',
  'eduflow.designer.aiPanel.focusContextLabel': 'Target:',
  'eduflow.designer.aiPanel.focusPrimary': '"{{label}}" ({{type}}) — Properties',
  'eduflow.designer.aiPanel.focusMulti':
    '{{count}} blocks selected — say which to edit, or focus one block.',
  'eduflow.designer.aiPanel.focusNone':
    'No focused block — open Properties on a node or name a block in your message.',
  'eduflow.designer.aiPanel.followUpsLabel': 'Suggested next steps',
  'eduflow.designer.aiPanel.ideasToggle': 'Ready-made ideas',
  'eduflow.designer.aiPanel.advancedToggle': 'Options (stream, sources, shortcuts…)',
  'eduflow.designer.aiPanel.removeDocument': 'Remove',
  'eduflow.designer.aiPanel.importDocumentTitle': 'Import PDF, Word, or PowerPoint',
  'eduflow.designer.aiPanel.webSourcesLabel': 'Web pages (max 3, optional)',
  'eduflow.designer.aiPanel.webSourcesPlaceholder': 'https://example.org/doc\nhttps://…',
  'eduflow.designer.aiPanel.idea.auditPath':
    'Audit the graph: broken paths, isolated nodes and flow order — apply fixes',
  'eduflow.designer.aiPanel.idea.microLearning':
    'Design a concise micro‑learning variant from the current flow while preserving intent',
  'eduflow.designer.aiPanel.idea.formativeGap':
    'Insert short formative bridging activities wherever the pathway is cognitively heavy',
  'eduflow.designer.aiPanel.idea.titleCoherence':
    'Align block titles with the project title and the overall training tone',
  'eduflow.designer.aiPanel.idea.remediation':
    'Add a remediation path after the main MCQ quiz (failure / low-score branch)',
  'eduflow.designer.aiPanel.idea.mobileWording':
    'Shorten and clarify HTML text in blocks that are too long on mobile',
  'eduflow.designer.aiPanel.idea.quizWording':
    'Unify wording style across all quiz instructions (“Select…”, tone, formal register)',
  'eduflow.designer.aiPanel.idea.summaryBeforeEnd':
    'Add or rewrite a recap block immediately before the End node',
  'eduflow.designer.aiPanel.streamMode': 'Streaming',
  'eduflow.designer.aiPanel.streamModeHint':
    'Response streams as it generates; the final payload is normalized like non-streaming mode.',
  'eduflow.designer.aiPanel.streamError': 'Stream interrupted or refused.',
  'eduflow.designer.aiPanel.streamIncomplete': 'Incomplete response from server. Retry or disable streaming.',
  'eduflow.designer.aiPanel.skillPath': 'Path audit (server)',
  'eduflow.designer.aiPanel.skillPathHint': 'Structural graph analysis added to the prompt.',
  'eduflow.designer.aiPanel.skillQuiz': 'Strict MCQ schema (server)',
  'eduflow.designer.aiPanel.skillQuizHint': 'Official Lyrra quiz_mcq template in the prompt.',
  'eduflow.designer.aiPanel.thinking': 'The assistant is thinking…',
  'eduflow.designer.aiPanel.serverSkills': 'Server boosts',
  'eduflow.designer.aiPanel.quickActions': 'Quick actions',
  'eduflow.designer.aiPanel.placeholder': 'Ask a question, brainstorm, review content, or change the path…',
  'eduflow.designer.aiPanel.apply': 'Apply to canvas',
  'eduflow.designer.aiPanel.autoApply': 'Auto-apply to graph',
  'eduflow.designer.aiPanel.autoApplyHint':
    'Structured assistant operations update the path without clicking Apply.',
  'eduflow.designer.aiPanel.dismissOps': 'Dismiss',
  'eduflow.designer.aiPanel.applied': 'Changes applied to the path.',
  'eduflow.designer.aiPanel.applyPartial': 'Applied with warnings: {{details}}',
  'eduflow.designer.aiPanel.applyFailed':
    'No operations could be applied to the canvas (invalid operation format or block types).',
  'eduflow.designer.aiPanel.objectivesSaved': 'Learning objectives saved (Objectives panel).',
  'eduflow.designer.aiPanel.objectivesSaveError': 'Could not save objectives. Please try again.',
  'eduflow.designer.aiPanel.error': 'Could not reach the assistant. Check your AI configuration (API key) and network.',
  'eduflow.designer.aiPanel.newConversation': 'New conversation',
  'eduflow.designer.aiPanel.clearConversation': 'Clear conversation',
  'eduflow.designer.aiPanel.conversationHistory': 'History',
  'eduflow.designer.aiPanel.deleteConversation': 'Delete',
  'eduflow.designer.aiPanel.maxConversations':
    'You’ve reached the limit of {{max}} saved conversations. Delete one to start a new one.',
  'eduflow.designer.aiPanel.unnamedConversation': 'Conversation',
  'eduflow.designer.aiQuick.plan': 'Path plan',
  'eduflow.designer.aiQuick.expand': 'Interactive blocks',
  'eduflow.designer.aiQuick.quiz': 'Quiz module',
  'eduflow.designer.aiQuick.objectives': 'Objectives (panel)',
  'eduflow.designer.aiQuick.rewriteFocus': 'Rewrite Properties block',
  'eduflow.designer.aiQuick.translateFocus': 'Translate Properties block (FR)',
  'eduflow.designer.voiceShort': 'Voice',
  'eduflow.designer.voiceTitle': 'Voice settings',
  'eduflow.designer.objectivesShort': 'Objectives',
  'eduflow.designer.objectivesTitle': 'Learning objectives',
  'eduflow.designer.flowVariablesTitle': 'Global variables',
  'eduflow.designer.flowVariables.subtitle':
    'Set key / value pairs. Use @ followed by the key name in block titles and descriptions (e.g. @course_name). Values are substituted for learners.',
  'eduflow.designer.flowVariables.empty': 'No variables yet. Add some to personalize course wording.',
  'eduflow.designer.flowVariables.keyLabel': 'Key',
  'eduflow.designer.flowVariables.keyPlaceholder': 'e.g. course_name',
  'eduflow.designer.flowVariables.keyHelp':
    'Letters, numbers, hyphen and underscore only. @key matching is case-sensitive.',
  'eduflow.designer.flowVariables.valueLabel': 'Default value',
  'eduflow.designer.flowVariables.valuePlaceholder': 'Text shown instead of @key',
  'eduflow.designer.flowVariables.add': 'Add variable',
  'eduflow.designer.flowVariables.delete': 'Remove',
  'eduflow.designer.flowVariables.invalidKey': 'Each key must be set and use only allowed characters.',
  'eduflow.designer.flowVariables.duplicateKey': 'Duplicate keys (case-insensitive) are not allowed.',
  'eduflow.designer.flowVariables.savedToast': 'Variables saved',
  'eduflow.designer.flowVariables.errorToast': 'Could not save variables.',
  'eduflow.designer.flowVariables.saveNow': 'Save now',
  'eduflow.designer.blockFlowVariablesHint':
    'Global variables: use @your_key in the title or description (configure them with the { } icon in the toolbar).',
  'eduflow.designer.blockFlowVariablesPreviewTitle': 'Preview (title)',
  'eduflow.designer.blockFlowVariablesPreviewDescription': 'Preview (description)',
  'eduflow.designer.flowVariables.mentionNoMatch': 'No matching variable.',
  'eduflow.designer.formFieldsSlash.mentionNoMatch': 'No matching field.',
  'eduflow.designer.onlineCount': '{{count}} online',
  'eduflow.designer.editingBlocks_one': '{{count}} block being edited',
  'eduflow.designer.editingBlocks_other': '{{count}} blocks being edited',
  'eduflow.designer.invite': 'Invite',
  'eduflow.designer.inviteTitle': 'Invite a collaborator',
  'eduflow.designer.exportTitle': 'Export path',
  'eduflow.designer.importTitle': 'Import course (file or JSON paste)',
  'eduflow.designer.exportNeedSavedFlow': 'Save the course first to export a .lyrra package.',
  'eduflow.designer.canvasUntitled': 'Untitled',
  'eduflow.designer.selectBlock': 'Select a block',
  'eduflow.designer.selectBlockHint': 'Click a block on the canvas to edit its properties',
  'eduflow.designer.blockPropertiesSubtitle': 'Block properties',
  'eduflow.designer.textVoiceoverHint':
    'The voiceover reads both the block description and main content.',
  'eduflow.designer.blockLockedReadOnly':
    'This block is locked: properties are shown as read-only.',
  'eduflow.designer.blockTitleField': 'Title',
  'eduflow.designer.blockTitlePlaceholder': 'Block title',
  'eduflow.designer.newFlowDefaultTitle': 'New course',
  'eduflow.designer.you': 'You',
  'eduflow.designer.blockInfoPosition': 'ID: {{id}} | Position: ({{x}}, {{y}})',
  'eduflow.designer.toastBlockCopied': 'Block copied',
  'eduflow.designer.toastCollaboratorInvited': 'Collaborator invited',
  'eduflow.designer.toastInviteError': 'Could not invite this collaborator',
  'eduflow.designer.createFlowError': 'Could not create the path',
  'eduflow.designer.loadFlowError': 'Could not load this training.',
  'eduflow.designer.loadFlowForbidden': 'You do not have access to this training.',
  'eduflow.designer.loadFlowNotFound': 'Training not found or removed.',
  'eduflow.duplicateFlowError': 'Could not duplicate the training.',
  'eduflow.designer.editConnection': 'Edit connection',
  'eduflow.designer.deleteConnection': 'Delete connection',

  'eduflow.translation.toolbarTitle': 'Content translation',
  'eduflow.translation.toolbarShort': 'Translate',
  'eduflow.translation.title': 'Multilingual translation',
  'eduflow.translation.subtitle':
    'Translate block text (AI) and optionally regenerate description voiceovers. Results are stored per language.',
  'eduflow.translation.subtitleV2':
    'Pick a Mistral voice per language for description voiceovers in the player (on-demand translation). Batch translation jobs below are optional.',
  'eduflow.translation.voicesSection': 'TTS voices per language',
  'eduflow.translation.voicesSectionHint':
    'Preview uses sample text in that language. With no voice selected, “Marie - sad” is used for French, Italian and Spanish; “Paul - sad” for other languages.',
  'eduflow.translation.voicesLangCount':
    '{{count}} languages (FR, EN, ES, DE, IT, PT, LB, UK, RU, AR): pick a voice, then tap “Play sample”.',
  'eduflow.translation.voicesLoadError':
    'Could not load voices. Check your connection or try again.',
  'eduflow.translation.voicePreview': 'Play sample',
  'eduflow.translation.previewStop': 'Stop',
  'eduflow.translation.voicePlaceholder': '— Choose a voice —',
  'eduflow.translation.voiceSelectAria': 'Voice for {{lang}}',
  'eduflow.translation.voicesSaveError': 'Could not save voices.',
  'eduflow.translation.selectVoiceFirst': 'Choose a voice for this language first.',
  'eduflow.translation.previewError': 'Audio playback failed.',
  'eduflow.translation.batchSection': 'Precomputed translations (optional)',
  'eduflow.translation.batchSectionHint':
    'The player can translate on the fly; run a job only if you want text + audio stored ahead of time.',
  'eduflow.translation.sourceLang': 'Course source language',
  'eduflow.translation.bulkSection': 'All languages',
  'eduflow.translation.includeText': 'Text',
  'eduflow.translation.includeAudio': 'Audio (description voiceover)',
  'eduflow.translation.runAll': 'Translate all languages (except source)',
  'eduflow.translation.runAllHint':
    'Starts one job per target language among the 10 supported languages, excluding the source language.',
  'eduflow.translation.perLanguage': 'Per language',
  'eduflow.translation.sourceBadge': 'Source',
  'eduflow.translation.runOne': 'Translate',
  'eduflow.translation.history': 'Recent jobs',
  'eduflow.translation.noHistory': 'No jobs yet.',
  'eduflow.translation.saveFirst': 'Save the path before starting a translation.',
  'eduflow.translation.prefsSaveError': 'Could not save translation preferences (per-language Text / Audio toggles).',
  'eduflow.translation.jobStarted': 'Translation started for {{lang}}.',
  'eduflow.translation.jobError': 'Could not start translation.',
  'eduflow.translation.bulkStarted': '{{count}} translation job(s) started.',
  'eduflow.translation.bulkNone':
    'No new jobs: translations may already be running for all target languages, or no options were selected.',
  'eduflow.translation.bulkError': 'Could not start bulk translations.',
  'eduflow.translation.statusPending': 'Pending',
  'eduflow.translation.statusProcessing': 'In progress',
  'eduflow.translation.statusCompleted': 'Completed',
  'eduflow.translation.statusFailed': 'Failed',
  'eduflow.translation.missingSourceAudioHint':
    'Audio translation: generate the description voiceover in {{lang}} for every long description first. Block(s) missing audio ({{count}}): {{blocks}}.',
  'eduflow.translation.missingSourceAudioToast':
    '{{count}} block(s) without source-language voiceover: {{blocks}}. Generate audio from the block properties, then try again.',

  'player.complete': 'Complete',
  'player.exit': 'Exit',
  'player.fullscreen': 'Fullscreen',
  'player.exitFullscreen': 'Exit fullscreen',
  'player.webinarFollowBanner':
    'You are following the host — navigation is locked. You can change the display language using the globe button.',
  'player.textBlockTipsHeading': 'Tips',
  'player.textBlockReadDone': 'Reading complete',
  'player.institutionLogo': 'Organization logo',
  'player.exitConfirmTitle': 'Exit training?',
  'player.exitConfirmMessage': 'Your progress will be saved. Are you sure you want to exit?',
  'player.focusLostTitle': 'Training paused',
  'player.focusLostMessage': 'You left the training window. Your time is paused.',
  'player.focusLostResume': 'Click to resume',
  'player.focusLostRemaining': 'Exits remaining before lockout: {{remaining}}',
  'player.focusBlockedTitle': 'Training locked',
  'player.focusBlockedMessage':
    'You exceeded the allowed number of exits ({{max}}). You can no longer continue this training.',
  'player.unblockRequestButton': 'Request unlock from trainer',
  'player.unblockRequestAgain': 'Request again',
  'player.unblockPending': 'Request sent — waiting for your trainer’s response.',
  'player.unblockRejected': 'Your unlock request was declined.',
  'player.unblockLoginRequired':
    'Sign in to your learner account (or use the personal link from your email) to request an unlock.',
  'player.unblockPublicLoginRequired':
    'In public mode without registration, unlock requests are not available. Complete the training registration form or sign in to LyrraStudio.',
  'player.unblockRequestSent': 'Request sent to your trainer.',
  'player.unblockRequestError': 'Could not send the request. Please try again.',
  'player.unblockApprovedToast': 'Your trainer unlocked the training. You can continue!',
  'player.registrationFormHint':
    'This form creates your learner account for this training (email, first name and last name required).',
  'player.registrationFormSuccess': 'Registration saved. You can continue the training.',
  'player.registrationFormError': 'Could not save registration.',
  'notifications.unblock.title': 'Unlock request',
  'notifications.unblock.message': '{{name}} — {{flow}} ({{count}} exit(s))',
  'notifications.unblock.unknownLearner': 'A learner',
  'notifications.unblock.unknownFlow': 'Training',
  'notifications.unblock.approve': 'Unlock',
  'notifications.unblock.reject': 'Decline',
  'notifications.unblock.approvedToast': 'Training unlocked for this learner.',
  'notifications.unblock.rejectedToast': 'Unlock request declined.',
  'notifications.unblock.resolveError': 'Could not process the request. Please try again.',
  'player.cancel': 'Cancel',
  'player.summary': 'Summary',
  'player.summary.badge': 'Recap',
  'player.summary.noItems': 'No summary points were defined for this block.',
  'player.summary.blockTranslating': 'Translating…',
  'player.summary.blockSimplifying': 'Simplifying text…',
  'player.simplifiedTextLoadingTitle': 'Preparing simplified text…',
  'player.simplifiedTextLoadingHint':
    'We simplify the visible block first, then the rest of the course in the background.',
  'player.contents': 'Content',
  'player.progress': 'Progress',
  'player.block': 'Block',
  'player.parallelBranches': 'Parallel branches',
  'player.parallelBranchesTitle': 'What’s next?',
  'player.parallelBranchesHint': 'The other options will be offered later.',
  'player.parallelBranchShowFull': 'Show more',
  'player.parallelBranchShowLess': 'Show less',
  'player.parallelBranchChoose': 'Open',
  'player.parallelBranchDismiss': 'Close',
  'player.parallelBranchDismissHint':
    'Return to the block. Use “Next” to choose a branch.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Completed paths stay clickable to review. Finish each branch at least once before the shared section.',
  'player.parallelBranchActionReplay': 'Retake this path',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.parallelBranchesGridLabel': 'Parallel branch grid',
  'player.parallelBranchesScrollHint':
    'Scroll right (or use the arrows) to see more paths.',
  'player.parallelBranchesPageNav': 'Path pages',
  'player.parallelBranchesPageIndicator': 'Page {{current}} / {{total}}',
  'player.parallelBranchesPrevPage': 'Previous paths',
  'player.parallelBranchesNextPage': 'Next paths',
  'player.notes': 'Take notes...',
  'player.noBlocks': 'No blocks to display.',
  'player.contentTranslatingTitle': 'Adapting content to your language…',
  'player.contentTranslatingHint': 'Translation in progress. The first load may take a little longer.',
  'player.contentLanguageReloadFailed':
    'Could not reload content in this language (timeout or server error). The previous version is still shown. Try again shortly or precompute translations in the designer.',
  'player.contentLanguageSubflowHint':
    'To update text and audio, finish or leave the current sub-section, then pick the language again.',
  'player.defaultFlowTitle': 'Course',
  'player.docTitle.error': 'Course unavailable',
  'player.docTitle.completed': 'Course completed',
  'player.docTitle.loading': 'Loading course',
  'player.docTitle.fallback': 'Course',
  'player.initError.title': 'The course is temporarily unavailable',
  'player.initError.loadFailed': 'The course could not be loaded right now.',
  'player.initError.loadFailedWithDetail':
    'The course could not be loaded right now.{{detail}} Please try again in a moment.',
  'player.initError.hintsIntro': 'You can:',
  'player.initError.hintRefresh': 'refresh the page;',
  'player.initError.hintConnection': 'check your internet connection;',
  'player.initError.hintRetryLater': 'try again in a few minutes.',
  'api.error.network': 'Unable to reach the server. Check your connection.',
  'api.error.unexpected': 'An unexpected error occurred.',
  'api.error.badRequest': 'Invalid request.',
  'api.error.forbidden': 'Access denied.',
  'api.error.notFound': 'Resource not found.',
  'api.error.conflict': 'Conflict — this resource already exists.',
  'api.error.unprocessable': 'The submitted data is not valid.',
  'api.error.tooManyRequests': 'Too many requests. Please try again shortly.',
  'api.error.server': 'Internal server error.',
  'api.error.badGateway': 'Service temporarily unavailable.',
  'api.error.maintenance': 'Service under maintenance.',
  'api.error.generic': 'Error {{status}}',
  'player.textBlockReadMinutes': '{{count}} min read',
  'player.textBlockWordCount_one': '{{count}} word',
  'player.textBlockWordCount_other': '{{count}} words',
  'player.textBlockNoContent': 'No content available.',
  'player.blockUnknown': 'Unknown block',
  'player.objectivesPanelTitle': 'Learning objectives',
  'player.objectivesOpenLabel': 'View learning objectives',
  'player.objectivesCloseLabel': 'Close objectives panel',
  'player.objectivesAchievementRate': 'Achievement rate',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objectives achieved',
  'player.objectivesNoneDefined': 'No objectives defined for this course.',
  'player.objectiveCategory.savoir': 'Knowledge',
  'player.objectiveCategory.savoirFaire': 'Skills',
  'player.objectiveCategory.savoirEtre': 'Attitudes',
  'player.objectiveLevel.connaissance': 'Knowledge',
  'player.objectiveLevel.comprehension': 'Comprehension',
  'player.objectiveLevel.application': 'Application',
  'player.objectiveLevel.analyse': 'Analysis',
  'player.objectiveLevel.synthese': 'Synthesis',
  'player.objectiveLevel.evaluation': 'Evaluation',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Required',
  'player.objectiveLinkedBlocks': 'Linked blocks',
  'player.objectiveCriterionScore': 'Score ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Block completion',
  'player.objectiveNoLinkedBlocks':
    'No linked block — overall progress does not measure this objective.',
  'player.completion.congratulations': '🎉 Congratulations!',
  'player.completion.finished': '📋 Course completed',
  'player.completion.youCompleted': 'You have completed',
  'player.completion.passed': 'Passed ✓',
  'player.completion.failed': 'Not validated ✗',
  'player.completion.requiredScore': 'Required score: {{score}}%',
  'player.completion.totalTime': 'Total time',
  'player.completion.blocksCompleted': 'Blocks completed',
  'player.completion.quizPassed': 'Quizzes passed',
  'player.completion.successRate': 'Success rate',
  'player.completion.blockCompleted': 'Completed ✓',
  'player.completion.blockFailed': 'Failed ✗',
  'player.completion.blockSkipped': 'Skipped',
  'player.completion.detail': '📊 Detail by block',
  'player.completion.type': 'Type',
  'player.completion.timeSpent': 'Time spent',
  'player.completion.downloadCertificate': '📄 Download certificate',
  'player.completion.certificateSoon': 'The certificate will be available soon.',
  'player.completion.restart': '🔄 Restart',
  'player.completion.viewDetails': '📊 View details',
  'player.completion.backToDashboard': '← Back to dashboard',
  'player.completion.embedDismissHint':
    "You've finished this module. To leave, use the × at the top of the window or the button below.",
  'player.completion.embedDismissButton': 'Close window',
  'player.previewBanner': "Preview mode — this view reflects your training's real data",
  'player.closePreview': 'Close preview',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Show sidebar',
  'player.toggleSidebarHide': 'Hide sidebar',
  'player.mobileSummary': 'Outline',
  'player.mobileObjectives': 'Objectives',
  'player.mobileMoreOptions': 'More options',
  'player.sidebarExpand': 'Expand sidebar',
  'player.sidebarCollapse': 'Collapse sidebar',
  'player.keyboardShortcuts': 'Keyboard shortcuts',
  'player.keyboardShortcutPrev': 'Previous block',
  'player.keyboardShortcutNext': 'Next block',
  'player.keyboardShortcutsHint':
    'Turn on “Keyboard navigation” in the accessibility panel (bottom-right button). Disabled while typing in fields.',
  'player.a11yCenter': 'Accessibility center',
  'player.reviewSummary': 'View summary',
  'player.viewResults': 'View results',
  'player.submitFirst': 'Submit first',
  'player.phraseRevealCompleteFirst': 'View all phrases first',
  'player.phraseReveal.emptyBlock': 'No phrases are configured for this block.',
  'player.phraseReveal.revealNextAria': 'Click or press Space to reveal the next phrase',
  'player.phraseReveal.lastPhraseHint': 'Last phrase — use “Next” to continue the path.',
  'player.phraseReveal.morePhrasesHint': 'Click, Space, or right arrow for the next phrase',
  'player.phraseReveal.previousPhrases': 'Previous phrases ({{count}})',
  'player.certificateUnavailable': 'The certificate will be available soon.',
  'player.browser.embeddedContentTitle': 'Embedded content',
  'player.browser.noValidUrl': 'No valid URL',
  'player.browser.enterHttpsHint':
    'Enter an address starting with https:// (or http://) in the block settings.',
  'player.browser.loadingContent': 'Loading content…',
  'player.browser.defaultPageTitle': 'Embedded web page',
  'player.browser.continue': 'Continue',
  'player.browser.stepSaved': 'Step saved',
  'player.browser.fullscreen': 'Fullscreen',
  'player.browser.exitFullscreen': 'Exit fullscreen',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Episode',
  'player.podcast.loadError': 'Could not load the podcast.',
  'player.podcast.loadingEpisodes': 'Loading episodes…',
  'player.podcast.playAll': 'Play all',
  'player.podcast.nextEpisode': 'Next episode',
  'player.podcast.episodesHeading': 'Episodes',
  'player.podcast.noEpisodes': 'No episodes.',
  'player.podcast.noAudio': 'No audio',
  'player.podcast.chainPlayback': 'Playlist · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll': 'Select an episode from the list or tap “Play all”.',
  'player.podcast.audioUnavailable': 'Audio is not available for this episode.',
  'player.podcast.showEpisodeText': 'Show transcript',
  'player.podcast.hideEpisodeText': 'Hide transcript',
  'player.podcast.episodeCount_one': '{{count}} episode',
  'player.podcast.episodeCount_other': '{{count}} episodes',
  'player.podcast.showChapters': 'Show chapters',
  'player.podcast.hideChapters': 'Hide chapters',
  'player.podcast.coverDialogLabel': 'Cover artwork: {{title}}',
  'player.podcast.coverEnlargePodcast': 'Enlarge podcast cover artwork',
  'player.podcast.coverEnlargeEpisode': 'Enlarge episode cover artwork',
  'player.podcast.coverEnlarge': 'Enlarge cover artwork',
  'player.audio.play': 'Play',
  'player.audio.pause': 'Pause',
  'player.audio.mute': 'Mute',
  'player.audio.unmute': 'Unmute',
  'player.audio.seek': 'Audio track position',
  'player.audio.playbackSpeed': 'Playback speed',
  'player.audio.completed': 'Audio track finished',
  'player.audio.subtitlesAria': 'Text synced with playback',
  'player.audio.subtitlesWaiting': 'Text segments will appear here during playback.',
  'player.audio.subtitlesNeedText': 'Add text (TTS or block content) to show segments here.',
  'player.audio.subtitlesHintSr':
    'Approximate sync: text is spread over the audio duration, not word-level timecodes.',
  'player.timeline.defaultTitle': 'Timeline',
  'player.timeline.subtitle': 'Timeline to explore.',
  'player.timeline.eventFallback': 'Event',
  'player.timeline.voiceoverLabel': 'Voice-over',
  'player.timeline.playVoiceover': 'Play voice-over',
  'player.timeline.pauseVoiceover': 'Pause voice-over',
  'player.timeline.generateVoiceover': 'Generate voice-over',
  'player.timeline.showDetail': 'View details',
  'player.timeline.hideDetail': 'Hide details',
  'player.timeline.emptyTitle': 'No events on this timeline',
  'player.timeline.emptyHint': 'Add steps in the block configuration panel.',
  'player.timeline.voiceoverUnavailableTitle': 'Voice-over unavailable',
  'player.timeline.voiceoverUnavailableDesc':
    'Open this path from a connected training to generate audio.',
  'player.timeline.voiceoverTextTooShortTitle': 'Text too short',
  'player.timeline.voiceoverTextTooShortDesc':
    'Add a description (at least 20 characters) or a more detailed title for voice-over.',
  'player.timeline.voiceoverGenerationErrorTitle': 'Voice-over generation failed',
  'player.timeline.voiceoverSlowTitle': 'Voice-over',
  'player.timeline.voiceoverSlowDesc':
    'Generation is taking longer than expected. Try again in a moment.',
  'player.timeline.retryLater': 'Please try again later.',
  'player.timeline.openFullscreen': 'Open fullscreen',
  'player.timeline.openFullscreenAria': 'Open timeline in fullscreen',
  'player.timeline.closeFullscreenAria': 'Close fullscreen',
  'player.timeline.reviewed': 'Timeline reviewed',
  'player.video.seek': 'Video position',
  'player.video.subtitlesShow': 'Show subtitles',
  'player.video.subtitlesHide': 'Hide subtitles',
  'player.video.trackLabel': 'Subtitles',
  'player.video.completed': 'Video completed',
  'player.video.markWatched': 'I have watched the video',
  'player.video.defaultIframeTitle': 'Embedded video',
  'player.podcast.playAllAria': 'Play all “{{title}}” — {{count}} episode(s) with audio',
  'player.podcast.skipNextQueuedAria': 'Skip to next episode in queue',
  'player.podcast.playEpisodeAria': 'Play episode: {{title}}',
  'player.podcast.episodeNoAudioAria': 'Episode with no audio file: {{title}}',
  'player.renderError': 'Error rendering block: {{blockType}}',
  'player.blocks.defaultWelcome': 'Welcome to this training',
  'player.blocks.defaultEndTitle': 'Congratulations!',
  'player.quiz.selectCorrectAnswer': 'Select the correct answer:',
  'player.quiz.correctAnswer': '✓ Correct answer!',
  'player.quiz.incorrectAnswer': '✗ Incorrect answer.',
  'player.blockTimer.aria': 'Time remaining for this block',
  'player.blockTimer.modeShortCountup': 'Elapsed',
  'player.blockTimer.modeShortCountdown': 'Countdown',
  'player.form.successMessage': 'Thank you for your response!',
  'player.form.noFieldsConfigured': 'This form does not have any configured fields yet.',
  'player.selfAssessment.empty': 'No criteria are defined for this self-assessment.',
  'player.selfAssessment.continue': 'Continue',
  'player.selfAssessment.pick': 'Choose a score',
  'player.selfAssessment.rateAria': 'Score {{n}}: {{label}}',
  'player.selfAssessment.criterionFallback': 'criterion',
  'player.selfAssessment.commentLabel': 'Comment (optional)',
  'player.selfAssessment.commentPlaceholder': 'Personal reflections, areas for growth…',
  'player.selfAssessment.submitted': 'Responses saved.',
  'player.selfAssessment.submit': 'Submit self-assessment',
  'player.selfAssessment.textAnswerPlaceholder': 'Your answer…',
  'player.selfAssessment.pickBadges': 'Select one or more options:',
  'player.evaluation.commentsLabel': 'Comments (optional)',
  'player.evaluation.commentsPlaceholder': 'Share your feedback...',
  'player.evaluation.submitted': 'Evaluation submitted',
  'player.evaluation.submit': 'Submit evaluation',
  'player.time.lessThanMinute': 'Less than a minute',
  'player.time.oneMinute': '1 minute',
  'player.time.minutes': '{{count}} minutes',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}}min',
  'player.time.shortMinutesSeconds': '{{minutes}}min {{seconds}}s',
  'player.completion.scoreLabel': 'Score',
  'accessibility.openCenter': 'Open accessibility center',
  'accessibility.center': 'Accessibility',
  'accessibility.modalTitle': 'Adapted reading center',
  'accessibility.modalDescription': 'Adjust the interface for low vision, dyslexia and more comfortable reading.',
  'accessibility.closeCenter': 'Close accessibility center',
  'accessibility.accessPad.connected': 'Lyrra Access Pad connected',
  'accessibility.accessPad.playGuide': 'Play LyrraPad guide',
  'accessibility.accessPad.pauseGuide': 'Pause LyrraPad guide',
  'accessibility.presets': 'Preset profiles',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': 'Default application rendering.',
  'accessibility.preset.lowVision.label': 'Low vision',
  'accessibility.preset.lowVision.desc': 'Stronger contrast, larger text and clearer visual cues.',
  'accessibility.preset.dys.label': 'Dys',
  'accessibility.preset.dys.desc': 'Easier reading with more spacing and a more legible font.',
  'accessibility.preset.cumulHint': 'Combine several profiles if needed',
  'accessibility.preset.clickToAdd': 'Click to add',
  'accessibility.preset.clickToRemove': 'Click to remove',
  'accessibility.typography': 'Typography settings',
  'accessibility.fontScale': 'Text size',
  'accessibility.lineHeight': 'Line height',
  'accessibility.letterSpacing': 'Letter spacing',
  'accessibility.wordSpacing': 'Word spacing',
  'accessibility.visualAids': 'Visual aids',
  'accessibility.highContrast': 'High contrast',
  'accessibility.highContrastDesc': 'Strengthens reading contrast, borders and visual cues.',
  'accessibility.underlineLinks': 'Underlined links',
  'accessibility.underlineLinksDesc': 'Makes links and interactive areas easier to identify.',
  'accessibility.dyslexiaFont': 'Easier reading font',
  'accessibility.dyslexiaFontDesc': 'Uses a more comfortable font for dyslexic users and tired readers.',
  'accessibility.reducedMotion': 'Reduced motion',
  'accessibility.reducedMotionDesc': 'Reduces animations and transitions to limit visual discomfort.',
  'accessibility.mouseMagnifier': 'Reading magnifier',
  'accessibility.mouseMagnifierDesc':
    'Shows a circular lens that follows the pointer and enlarges course content under it (magnified view for reading only).',
  'accessibility.readingMask': 'Reading mask',
  'accessibility.readingMaskDesc':
    'An opaque band follows the cursor to isolate the current line — especially helpful for dyslexia and ADHD. Tip: Option ⌥ + =/+ or Option ⌥ + ↑/↓ (macOS); Alt + + / Alt − (Windows/Linux) to adjust the height.',
  'accessibility.speakOnSelection': 'Speak selected text',
  'accessibility.speakOnSelectionDesc':
    'Select any text on the page: a button appears to read it aloud on demand (different from automatic audio reading).',
  'accessibility.speakSelection.listen': 'Listen to selection',
  'accessibility.speakSelection.stop': 'Stop reading',
  'accessibility.speakSelection.loading': 'Generating…',
  'accessibility.speakSelection.retry': 'Try again',
  'accessibility.speakSelection.error': 'Read-aloud unavailable, please try again shortly.',
  'accessibility.speakSelection.region': 'Read selection aloud',
  'a11y.voiceGuide.title': 'Accessibility voice assistant',
  'a11y.help.shortcuts': 'Keyboard shortcuts',
  'a11y.help.kbdEsc': 'Close the panel / stop speech synthesis',
  'a11y.help.kbdMask': 'Adjust reading mask height (mask enabled)',
  'a11y.help.kbdMaskMac': 'Use = or + key (above numbers), or ↑ ↓ arrows with Option ⌥',
  'a11y.help.kbdMaskOther': 'Windows / Linux: Alt + + / Alt + −',
  'a11y.help.kbdTab': 'Move between interactive elements',
  'a11y.help.tips': 'Tips',
  'a11y.voiceGuide.subtitle': "Describe your needs and I'll turn on the right options for you.",
  'a11y.voiceGuide.cta.title': 'Describe your needs',
  'a11y.voiceGuide.cta.hint': 'Speak: AI automatically enables the right options for you.',
  'a11y.voiceGuide.cta.openLabel': 'Open the voice assistant to describe your accessibility needs',
  'a11y.voiceGuide.cta.start': 'Start the voice assistant',
  'a11y.voiceGuide.cta.stop': 'Stop listening',
  'a11y.voiceGuide.status': 'Status',
  'a11y.voiceGuide.statusActive': 'In progress',
  'a11y.voiceGuide.youSaid': 'You said',
  'a11y.voiceGuide.reformulation': 'Here is what I understood',
  'a11y.voiceGuide.applied': 'Settings applied',
  'a11y.voiceGuide.resetButton': 'Reset to normal',
  'a11y.voiceGuide.resetExplanation': 'All accessibility settings have been reset to default.',
  'a11y.voiceGuide.problem': 'Problem',
  'a11y.voiceGuide.micReady': 'Mic ready',
  'a11y.voiceGuide.micUnsupported': 'Speech recognition not supported',
  'a11y.voiceGuide.stage.idle': 'Ready to listen',
  'a11y.voiceGuide.stage.intro': 'Setting things up…',
  'a11y.voiceGuide.stage.listening': "I'm listening — speak freely.",
  'a11y.voiceGuide.stage.thinking': 'Analyzing your needs…',
  'a11y.voiceGuide.stage.confirmPlaying': 'Summarising…',
  'a11y.voiceGuide.stage.confirmListening': "Say 'yes' to confirm or 'no' to start over.",
  'a11y.voiceGuide.stage.applying': 'Applying your settings…',
  'a11y.voiceGuide.stage.done': 'Done!',
  'a11y.voiceGuide.stage.error': 'Something went wrong.',
  'a11y.voiceGuide.error.mic': 'Microphone access failed.',
  'a11y.voiceGuide.error.micDenied': 'Microphone permission was denied. Please enable it in your browser settings.',
  'a11y.voiceGuide.error.stt': 'Speech recognition is not available in this browser.',
  'a11y.voiceGuide.error.ai': 'AI analysis unavailable.',
  'a11y.voiceGuide.error.serverUnavailable':
    'The Lyrra server is not responding correctly (often: backend not deployed or build error). Check deployment, then try again.',
  'a11y.voiceGuide.error.generic': 'Something went wrong. Please try again or adjust settings manually.',
  'accessibility.alwaysOn': 'Always-on improvements',
  'accessibility.alwaysOnFocus': 'More visible keyboard focus',
  'accessibility.alwaysOnLinks': 'Links and interactive elements easier to detect',
  'accessibility.alwaysOnSaved': 'Settings automatically saved on this device',
  'accessibility.navigation': 'Navigation',
  'accessibility.keyboardNavigation': 'Keyboard navigation',
  'accessibility.keyboardNavigationDesc':
    'Use Left / Up arrow for the previous block and Right / Down arrow for the next block (same as the bottom buttons).',

  // ── Learner Dashboard ──────────────────────────────────
  'learner.header.workspace': 'Learner workspace',
  'learner.tabs.home': 'Home',
  'learner.tabs.parcours': 'My Paths',
  'learner.tabs.formations': 'My Courses',
  'learner.tabs.publicFormations': 'Public courses',
  'learner.tabs.webinars': 'Webinars',
  'learner.tabs.podcasts': 'Podcasts',
  'learner.tabs.documents': 'Documents',
  'learner.tabs.progress': 'My Progress',
  'learner.tabs.profile': 'Profile',
  'learner.sidebar.tagline': 'Learner',
  'learner.sidebar.webinars': 'Webinars',
  'learner.webinars.title': 'My webinars',
  'learner.webinars.loading': 'Loading your webinars…',
  'learner.webinars.empty': 'No webinars yet. Email invitations and sessions where you present will appear here.',
  'learner.webinars.sectionInvited': 'Invitations received',
  'learner.webinars.sectionPresenting': 'Sessions I present',
  'learner.webinars.emptyInvited': 'No invitations yet.',
  'learner.webinars.emptyPresenting': 'You are not a presenter on any webinar.',
  'learner.webinars.rolePresenter': 'Presenter',
  'learner.webinars.joinAsPresenter': 'Open room (presenter)',
  'learner.webinars.join': 'Join room',
  'learner.webinars.invitedOn': 'Invited on',
  'learner.webinars.scheduledFor': 'Scheduled for',
  'learner.webinars.duration': 'Duration: {{duration}}',
  'learner.webinars.loadError': 'Could not load webinars.',
  'learner.publicFormations.title': 'Public courses',
  'learner.publicFormations.subtitle': 'Publicly accessible courses offered by your organization.',
  'learner.publicFormations.loading': 'Loading public courses…',
  'learner.publicFormations.loadError': 'Could not load public courses.',
  'learner.publicFormations.empty': 'No course matches your search.',
  'learner.publicFormations.emptyNoInstitution':
    'No public courses are available for your organization at the moment.',
  'learner.publicFormations.searchPlaceholder': 'Search for a course…',
  'learner.publicFormations.open': 'Open course',
  'learner.webinars.status.draft': 'Draft',
  'learner.webinars.status.scheduled': 'Scheduled',
  'learner.webinars.status.live': 'Live',
  'learner.webinars.status.ended': 'Ended',
  'learner.sidebar.myProfile': 'My profile',
  'learner.sidebar.logout': 'Log out',
  'learner.sidebar.collapse': 'Collapse menu',
  'learner.sidebar.expandAria': 'Expand sidebar',
  'learner.sidebar.collapseAria': 'Collapse sidebar',
  'learner.sidebar.backToTrainer': 'Trainer dashboard',
  'learner.sidebar.backToTrainerAria': 'Go back to the trainer dashboard',
  'learner.home.defaultName': 'learner',
  'learner.home.greeting': 'Hello {{name}},',
  'learner.home.greetingSubtitle': 'Pick up your learning where you left off.',
  'learner.home.resumeMyFormation': 'Resume my course',
  'learner.home.globalProgress': 'Overall progress',
  'learner.home.learningTime': 'Learning time',
  'learner.home.badges': 'Badges earned',
  'learner.home.calendarKicker': 'My progress',
  'learner.home.quickAccess': 'Quick access',
  'learner.learningPath': 'Learning path',
  'learner.home.resumeTraining': 'Resume your courses',
  'learner.home.viewAll': 'View all →',
  'learner.home.trainingDomains': 'Training domains',
  'learner.home.courses': 'courses',
  'learner.home.episodes': 'episodes',
  'learner.home.recommendedPodcasts': 'Recommended podcasts',
  'learner.home.explore': 'Explore →',
  'learner.formations.title': 'My Courses',
  'learner.formations.searchPlaceholder': 'Search a course…',
  'learner.formations.all': 'All',
  'learner.formations.formation': 'Course',
  'learner.formations.inProgress': 'In progress',
  'learner.formations.completed': 'Completed',
  'learner.formations.notStarted': 'Not started',
  'learner.formations.loading': 'Loading your courses...',
  'learner.formations.empty': 'No course found.',
  'learner.formations.progress': 'Progress',
  'learner.formations.instructor': 'Instructor',
  'learner.formations.opening': 'Opening...',
  'learner.formations.review': 'Review',
  'learner.formations.start': 'Start',
  'learner.formations.continue': 'Continue',
  'learner.podcasts.title': 'Explore Podcasts',
  'learner.podcasts.searchPlaceholder': 'Search a podcast…',
  'learner.podcasts.all': 'All',
  'learner.podcasts.assigned': 'Assigned podcast',
  'learner.podcasts.defaultCategory': 'Podcast',
  'learner.podcasts.loading': 'Loading your podcasts...',
  'learner.podcasts.empty': 'No assigned podcast.',
  'learner.podcasts.episodes': 'episodes',
  'learner.podcasts.opening': 'Opening...',
  'learner.podcasts.listen': 'Listen',
  'learner.documents.title': 'Shared Documents',
  'learner.documents.loading': 'Loading your documents...',
  'learner.documents.view': 'View document',
  'learner.documents.empty': 'No shared document.',
  'learner.documents.personalTitle': 'My personal documents',
  'learner.documents.personalHint': 'Your personal documents are only visible to you. Click on a document to preview it and ask questions to the AI assistant.',
  'learner.documents.personalEmpty': 'No personal document yet. Upload a PDF to get started.',
  'learner.documents.upload': 'Upload a document',
  'learner.documents.delete': 'Delete',
  'learner.documents.deleteConfirmTitle': 'Delete this document?',
  'learner.documents.deleteConfirmDesc': 'This action is permanent. The document and its AI analysis will be deleted.',
  'learner.documents.cancel': 'Cancel',
  'learner.documents.confirmDelete': 'Delete',
  'learner.documents.chatReady': 'Chat ready',
  'learner.documents.processing': 'Processing…',
  'learner.documents.backToList': 'Back to documents',
  'learner.documents.chatTitle': 'Ask about this document',
  'learner.documents.chatPlaceholder': 'Ask a question about this document…',
  'learner.documents.chatPlaceholderHint': 'Ask any question about this document. The assistant will only answer based on its content.',
  'learner.documents.chatError': 'An error occurred.',
  'learner.documents.preview': 'Preview',
  'learner.documents.indexing': 'Analyzing your document…',
  'learner.progress.title': 'My Progress',
  'learner.progress.completedFormations': 'Completed courses',
  'learner.progress.trainingHours': 'Training hours',
  'learner.progress.listenedPodcasts': 'Podcasts listened',
  'learner.progress.earnedBadges': 'Badges earned',
  'learner.progress.weeklyActivity': 'Weekly activity',
  'learner.progress.badges': 'Badges',
  'learner.progress.recentActivity': 'Recent activity',
  'learner.progress.rarity.common': 'Common',
  'learner.progress.rarity.rare': 'Rare',
  'learner.progress.rarity.epic': 'Epic',
  'learner.progress.rarity.legendary': 'Legendary',
  'learner.progress.days.mon': 'Mon',
  'learner.progress.days.tue': 'Tue',
  'learner.progress.days.wed': 'Wed',
  'learner.progress.days.thu': 'Thu',
  'learner.progress.days.fri': 'Fri',
  'learner.progress.days.sat': 'Sat',
  'learner.progress.days.sun': 'Sun',
  'learner.profile.title': 'My Profile',
  'learner.profile.defaultName': 'Learner',
  'learner.profile.defaultEmail': 'learner@example.com',
  'learner.profile.memberSince': 'Member since January 2024',
  'learner.profile.editProfile': 'Edit profile',
  'learner.profile.formations': 'Courses',
  'learner.profile.hours': 'Hours',
  'learner.profile.podcasts': 'Podcasts',
  'learner.profile.badges': 'Badges',
  'learner.profile.preferences': 'Preferences',
  'learner.profile.theme': 'Theme',
  'learner.profile.themeLight': 'Light',
  'learner.profile.themeDark': 'Dark',
  'learner.profile.themeSystem': 'System',
  'learner.profile.language': 'Language',
  'learner.profile.notifications': 'Notifications',
  'learner.profile.fontSize': 'Text size',
  'learner.profile.fontNormal': 'Normal',
  'learner.profile.fontLarge': 'Large',
  'learner.profile.highContrast': 'High contrast',
  'learner.profile.a11ySection': 'Accessibility',
  'learner.profile.dangerZone': 'Danger zone',
  'learner.profile.logoutBtn': 'Log out',
  'learner.parcours.title': 'My Paths',
  'learner.parcours.assigned': 'assigned path(s)',
  'learner.parcours.loading': 'Loading your paths…',
  'learner.parcours.loadError': 'Unable to load your paths.',
  'learner.parcours.noAssigned': 'No assigned path',
  'learner.parcours.noAssignedDesc': 'Your instructor will assign paths here.',
  'learner.parcours.completedBadge': 'Completed ✓',
  'learner.parcours.by': 'By',
  'learner.parcours.contents': 'content(s)',
  'learner.parcours.completedItems': 'completed',
  'learner.parcours.emptyPath': 'No content in this path.',
  'learner.parcours.locked': 'Complete the previous one first',
  'learner.parcours.review': 'Review',
  'learner.parcours.listen': 'Listen',
  'learner.parcours.open': 'Open',
  'learner.modal.loading': 'Loading…',
  'learner.modal.noEpisodes': 'No episode available.',
  'learner.modal.markedDone': '✓ Marked as done',
  'learner.modal.listenThenMark': 'Listen then mark as done',
  'learner.modal.alreadyDone': 'Already done',
  'learner.modal.markDone': 'Mark as done',
  'learner.modal.formationDone': '✓ Course completed',
  'learner.modal.completeThenMark': 'Complete the course then mark it as done',
  'learner.modal.formationAlreadyDone': 'Already completed',
  'learner.modal.markFormationDone': 'Mark as completed',
  'learner.modal.formationInstruction': 'Follow the modules of this course in order, then mark it as completed to progress in your path.',
  'learner.modal.launchFormation': 'Launch course',
  'learner.modal.openFormationHint':
    'Open the course in a new tab, then mark it as completed from your path.',
  'learner.home.loadingHome': 'Loading your home…',
  'learner.home.noFormationsAssigned':
    'No course assigned yet. They will appear here when a trainer assigns one to you.',
  'learner.home.loadingPaths': 'Loading your paths…',
  'learner.home.noPathsAssigned':
    'No path assigned yet. They will appear here when a trainer assigns one to you.',
  'learner.home.byTheme': 'Your content by theme',
  'learner.home.themesHint': 'Themes come from the categories of your assigned courses and podcasts.',
  'learner.home.categoryOther': 'Other',
  'learner.home.contentsCount_one': '{{count}} item',
  'learner.home.contentsCount_other': '{{count}} items',
  'learner.home.viewThemeFormations': 'View all {{count}} courses in this theme',
  'learner.home.viewThemePodcasts': 'View all {{count}} podcasts in this theme',
  'learner.home.resumeSubtitleProgress': 'Progress {{percent}}%',
  'learner.home.resumeSubtitleNotStarted': 'Not started yet',
  'learner.home.podcastsAssigned': 'Assigned podcasts',
  'learner.home.viewAllPodcasts': 'See all →',
  'learner.home.gamificationHomeHint':
    'Badges and full activity are under the « My progress » tab.',
  'learner.home.gamificationHomeCta': 'Open my progress',
  'learner.podcasts.mineTitle': 'My podcasts',
  'learner.podcasts.episodeCount_one': '{{count}} episode',
  'learner.podcasts.episodeCount_other': '{{count}} episodes',
  'learner.progress.loading': 'Loading your progress…',
  'learner.progress.statPodcastsCompleted': 'Podcasts completed',
  'learner.progress.statTimeFormationsHours': 'Time on courses (h)',
  'learner.progress.statAssignedContents': 'Assigned content',
  'learner.progress.weeklyEstimated': 'Last 7 days activity (estimated)',
  'learner.progress.badgesSoon': 'Badges and rewards will be available in your space soon.',
  'learner.progress.gamificationSection': 'Level and experience',
  'learner.progress.gamificationLevelLine': 'Level {{level}} — {{name}}',
  'learner.progress.gamificationXpToNext': '{{xp}} XP until the next level',
  'learner.progress.rewardFormation': 'Course completed',
  'learner.progress.rewardPathItem': 'Learning path step',
  'learner.progress.rewardPathComplete': 'Learning path completed',
  'learner.progress.rewardDesignerBlocks': 'Course points (completed blocks)',
  'learner.progress.rewardDesignerPerfect': 'Perfect quiz score bonus',
  'learner.progress.rewardDesignerBadge': 'Course badge',
  'learner.progress.rewardDesignerBadgeNoXp': 'No XP points (visual reward)',
  'learner.progress.badgesEmpty':
    'Complete a course or a learning path to earn points and see your rewards here.',
  'learner.progress.plusXp': '+{{xp}} XP',
  'learner.progress.noRecentHint':
    'No recent activity yet. Open a course or a podcast to feed this list.',
  'learner.progress.hoursShort': '{{hours}}h',
  'learner.profile.statFormationsRatio': 'Courses (completed / total)',
  'learner.profile.statTimeFormations': 'Course time (h)',
  'learner.profile.statPodcastsAssigned': 'Assigned podcasts',
  'learner.profile.statPathsAssigned': 'Assigned paths',
  'learner.profile.memberSincePrefix': 'Member since {{date}}',
  'learner.parcours.playPath': 'Follow this path',
  'learner.parcours.playPathTitle': 'Opens the full-screen path in a new tab',
  'learner.parcours.steps_zero': 'No steps',
  'learner.parcours.steps_one': '1 step',
  'learner.parcours.steps_other': '{{count}} steps',
  'learner.parcours.doneBadge': 'Done',
  'learner.parcours.completedSingle': '{{done}}/{{total}} completed',
  'learner.parcours.completedPlural': '{{done}}/{{total}} completed',
  'learner.parcours.byAuthor': 'By {{firstName}} {{lastName}}',
  'learner.parcours.pathListAria': 'Your paths list',
  'learner.parcours.assignedCount_one': '{{count}} assigned path',
  'learner.parcours.assignedCount_other': '{{count}} assigned paths',

  'learner.pathPlayer.loading': 'Loading path…',
  'learner.pathPlayer.errorGeneric': 'Error',
  'learner.pathPlayer.pathNotFound': 'Path not found',
  'learner.pathPlayer.loadError': 'Unable to load path.',
  'learner.pathPlayer.backToPaths': 'Back to my paths',
  'learner.pathPlayer.showTocAria': 'Show outline',
  'learner.pathPlayer.prevActivityAria': 'Previous activity',
  'learner.pathPlayer.prev': 'Previous',
  'learner.pathPlayer.nextActivityAria': 'Next activity',
  'learner.pathPlayer.next': 'Next',
  'learner.pathPlayer.closePlayerTitle': 'Close and return to paths',
  'learner.pathPlayer.close': 'Close',
  'learner.pathPlayer.congrats': 'Congratulations',
  'learner.pathPlayer.pathCompleteTitle': 'Path completed',
  'learner.pathPlayer.pathCompleteBody': 'You have completed all activities in the path “{{title}}”.',
  'learner.pathPlayer.activitiesCompleted_one': '{{count}} activity completed',
  'learner.pathPlayer.activitiesCompleted_other': '{{count}} activities completed',
  'learner.pathPlayer.seeMyPaths': 'See my paths',
  'learner.pathPlayer.closeTocAria': 'Close outline',
  'learner.pathPlayer.tocNavAria': 'Path outline',
  'learner.pathPlayer.pathTypeLabel': 'Training path',
  'learner.pathPlayer.noDescription': 'No description.',
  'learner.pathPlayer.activityCount_zero': 'No activities',
  'learner.pathPlayer.activityCount_one': '1 activity',
  'learner.pathPlayer.activityCount_other': '{{count}} activities',
  'learner.pathPlayer.progressPercent': 'Progress {{percent}}%',
  'learner.pathPlayer.quit': 'Leave',
  'learner.pathPlayer.summary': 'Outline',
  'learner.pathPlayer.outsideModule': 'Outside module',
  'learner.pathPlayer.moduleContents': 'Module contents',
  'learner.pathPlayer.step': 'Step',
  'learner.pathPlayer.podcastHint': 'Listen to the episode, then mark the activity as complete.',
  'learner.pathPlayer.showChapters': 'Show chapters',
  'learner.pathPlayer.hideChapters': 'Hide chapters',
  'learner.pathPlayer.showText': 'Show transcript',
  'learner.pathPlayer.hideText': 'Hide transcript',
  'learner.pathPlayer.selectEpisode': 'Select an episode to start.',
  'learner.pathPlayer.loadingShort': 'Loading…',
  'learner.pathPlayer.noEpisodes': 'No episodes available.',
  'learner.pathPlayer.listenThenMark': 'Listen, then mark as complete',
  'learner.pathPlayer.updating': 'Updating…',
  'learner.pathPlayer.alreadyDone': 'Already completed',
  'learner.pathPlayer.markComplete': 'Mark as complete',
  'learner.pathPlayer.nextStepBtn': 'Next step',
  'learner.pathPlayer.embeddedFormationHint':
    'The course appears below in the path. Use the outline on the left for detailed steps.',
  'learner.pathPlayer.loadingFormation': 'Loading course…',
  'learner.pathPlayer.loadingReader': 'Loading player…',
  'learner.pathPlayer.emptyPathTitle': 'This path does not have any activities yet.',
  'learner.pathPlayer.emptyPathDesc':
    'Content will be added by your training organization. You can close this window and come back later.',
  'learner.pathPlayer.pickActivity': 'Choose an activity in the outline to get started.',
  'learner.pathPlayer.footerStep': 'Step {{current}} / {{total}}',
  'learner.pathPlayer.unmute': 'Unmute',
  'learner.pathPlayer.mute': 'Mute',

  'learner.activity.flowCompleted': 'Course “{{title}}” completed',
  'learner.activity.flowProgress': 'Course “{{title}}” — {{progress}}%',
  'learner.activity.podcast': 'Podcast “{{title}}”',
  'learner.webinars.durationMin': '{{m}} min',
  'learner.webinars.durationH': '{{h}} h',
  'learner.webinars.durationHM': '{{h}} h {{m}}',
  'learner.formations.durationHours': '{{hours}} h',
  // ── Injected from locales/en.json ──
  'producerAssignments.createInDesigner': 'Create / open course',
  'producerAssignments.draft': 'Draft',
  'producerAssignments.enterprise': 'Enterprise',
  'producerAssignments.enterprisePaths': 'Enterprise paths',
  'producerAssignments.episodes': 'ep.',
  'producerAssignments.formations': 'Course cards',
  'producerAssignments.markCompleted': 'Mark as completed',
  'producerAssignments.markInProgress': 'In production',
  'producerAssignments.markReady': 'Ready',
  'producerAssignments.modulesCatalog': '{{count}} catalog module(s) · updated {{date}}',
  'producerAssignments.noneEnterprisePaths': 'No enterprise path requests.',
  'producerAssignments.noneFormations': 'No cards assigned.',
  'producerAssignments.nonePodcasts': 'No podcasts assigned.',
  'producerAssignments.openInDesigner': 'Open in designer',
  'producerAssignments.openPodcasts': 'Podcast studio',
  'producerAssignments.planningDeadline': 'Deadline {{date}}',
  'producerAssignments.planningFrom': 'Starting {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts to produce',
  'producerAssignments.productionStatus.completed': 'Completed',
  'producerAssignments.productionStatus.in_progress': 'In progress',
  'producerAssignments.productionStatus.not_started': 'Not started',
  'producerAssignments.productionStatus.published': 'Published',
  'producerAssignments.publishedCatalog': 'Published to catalog',
  'producerAssignments.rhPlanning': 'HR planning',
  'producerAssignments.subtitle': 'Courses to create in the designer and podcasts to produce, assigned by HR.',
  'producerAssignments.title': 'My production assignments',
  'podcasts.actions.cancel': 'Cancel',
  'podcasts.actions.close': 'Close',
  'podcasts.actions.create': 'Create',
  'podcasts.actions.edit': 'Edit',
  'podcasts.actions.retry': 'Retry',
  'podcasts.actions.save': 'Save',
  'podcasts.actions.saving': 'Saving…',
  'podcasts.actions.share': 'Share',
  'podcasts.category.ariaGroup': 'Podcast category',
  'podcasts.category.emptyBanner': 'Add categories in the Institution space to better organize podcasts.',
  'podcasts.category.emptyInstitutionHint': 'No institution categories yet — you can publish without one.',
  'podcasts.category.noneChip': 'No category',
  'podcasts.category.sectionTitle': 'Category',
  'podcasts.category.withCategoriesHint': 'Catalog placement and institution-side filters.',
  'podcasts.cover.ariaDropzone': 'Choose or drop a cover image',
  'podcasts.cover.browseFiles': 'browse files',
  'podcasts.cover.cancelNewFile': 'Cancel new file',
  'podcasts.cover.dragDrop': 'Drag and drop an image',
  'podcasts.cover.dropHere': 'Drop image here',
  'podcasts.cover.errorMaxSize': 'Image must not exceed 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Unsupported image format.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max 5 MB',
  'podcasts.cover.help': 'Square format recommended. Drag and drop a file or click to browse.',
  'podcasts.cover.or': 'or',
  'podcasts.cover.replaceHint': 'Replace image',
  'podcasts.cover.sectionTitle': 'Cover image',
  'podcasts.deleteDialog.body': '"{{title}}" will be moved to trash. Episodes linked only to this podcast will be deleted (including audio). Episodes shared with other podcasts are kept. An administrator can restore the podcast from the Institution trash.',
  'podcasts.deleteDialog.confirm': 'Move to trash',
  'podcasts.deleteDialog.title': 'Move podcast to trash?',
  'podcasts.editPanel.createdOn': 'Created on ',
  'podcasts.editPanel.headerTitle': 'Edit podcast',
  'podcasts.editPanel.saving': 'Saving…',
  'podcasts.editPanel.statsEpisodes': 'Episodes: ',
  'podcasts.editPanel.statsSubscribers': 'Subscribers: ',
  'podcasts.editPanel.successSaved': 'Changes saved',
  'podcasts.episodeStatus.draft': 'Draft',
  'podcasts.episodeStatus.published': 'Published',
  'podcasts.episodesPanel.addEpisode': 'Add episode',
  'podcasts.episodesPanel.emptySubtitle': 'Add episodes to this podcast',
  'podcasts.episodesPanel.emptyTitle': 'No episodes',
  'podcasts.episodesPanel.episodeCount': '{{count}} episode(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'No episode available',
  'podcasts.episodesPanel.remove': 'Remove',
  'podcasts.episodesPanel.searchPlaceholder': 'Search an episode…',
  'podcasts.episodesPanel.title': 'Podcast episodes',
  'podcasts.pdfImport.openButton': 'Import document',
  'podcasts.pdfImport.pickPodcastTitle': 'Choose target podcast',
  'podcasts.pdfImport.noPodcast': 'Create a non-archived podcast before importing a document.',
  'podcasts.pdfImport.noPodcastMatch': 'No podcast matches your search.',
  'podcasts.list.actionImportDocument': 'Import document (PDF or Markdown)',
  'podcasts.pdfImport.title': 'Import document (PDF or Markdown)',
  'podcasts.pdfImport.subtitle': 'Create episodes for « {{podcast}} » from document structure',
  'podcasts.pdfImport.hint':
    'Content is split: # → episode, ## → chapter, paragraphs → blocks. PDFs are converted to Markdown first for review. Accepted: PDF or .md.',
  'podcasts.pdfImport.convertingPdf': 'Converting PDF to Markdown…',
  'podcasts.pdfImport.markdownReviewTitle': 'Review generated Markdown',
  'podcasts.pdfImport.markdownReviewSubtitle':
    'PDF converted for « {{podcast}} ». Edit headings (# / ##) then validate.',
  'podcasts.pdfImport.markdownReviewHint':
    'Each episode should start with # and each chapter with ##. Fix text before continuing.',
  'podcasts.pdfImport.markdownEditorLabel': 'Markdown content',
  'podcasts.pdfImport.markdownEditorHint': '# Episode · ## Chapter · paragraphs = script blocks',
  'podcasts.pdfImport.validateMarkdown': 'Validate and view structure',
  'podcasts.pdfImport.editMarkdown': 'Edit Markdown',
  'podcasts.pdfImport.markdownEmpty': 'Markdown is empty.',
  'podcasts.pdfImport.dropLabel': 'Drop a PDF or .md file, or choose a file',
  'podcasts.pdfImport.chooseFile': 'Choose file',
  'podcasts.pdfImport.analyzing': 'Analyzing document…',
  'podcasts.pdfImport.pageCount': '{{count}} page(s)',
  'podcasts.pdfImport.lineCount': '{{count}} line(s)',
  'podcasts.pdfImport.stats': '{{episodes}} episode(s) · {{chapters}} chapter(s) · {{blocks}} section(s)',
  'podcasts.pdfImport.episodeLabel': 'Episode {{index}} — {{title}}',
  'podcasts.pdfImport.sectionsCount': '{{count}} section(s)',
  'podcasts.pdfImport.confirm': 'Create episodes',
  'podcasts.pdfImport.importing': 'Creating…',
  'podcasts.pdfImport.previewError': 'Could not analyze this document.',
  'podcasts.pdfImport.importError': 'Import failed.',
  'podcasts.pdfImport.invalidFile': 'Only PDF and Markdown (.md) files are accepted.',
  'podcasts.pdfImport.success': 'Created {{episodes}} episode(s), {{chapters}} chapter(s), {{blocks}} section(s).',
  'podcasts.pdfImport.hintEditorPage':
    'After choosing a file, you will open the Markdown editor with a structure assistant (episodes, chapters, alerts).',
  'podcasts.pdfImport.editorPageTitle': 'Prepare document import',
  'podcasts.pdfImport.editorPageSubtitle': 'Podcast « {{podcast}} » — {{file}}',
  'podcasts.pdfImport.editorEmptyHint':
    'Choose a PDF or Markdown file to start. Fix structure before creating episodes.',
  'podcasts.pdfImport.backToPodcasts': 'Back to podcasts',
  'podcasts.pdfImport.replaceFile': 'Replace file',
  'podcasts.pdfImport.documentTitleLabel': 'Document title',
  'podcasts.pdfImport.untitledDocument': 'Untitled',
  'podcasts.pdfImport.invalidRoute': 'Invalid podcast.',
  'podcasts.pdfImport.fixErrorsBeforeImport': 'Fix structure errors (right panel) before creating episodes.',
  'podcasts.pdfImport.structurePanelTitle': 'Structure assistant',
  'podcasts.pdfImport.structurePanelSubtitle': 'Live estimates and alerts while you edit.',
  'podcasts.pdfImport.estimatedStructure': 'Estimated structure',
  'podcasts.pdfImport.statEpisodes': 'Episodes',
  'podcasts.pdfImport.statChapters': 'Chapters',
  'podcasts.pdfImport.statBlocks': 'Blocks',
  'podcasts.pdfImport.noPreviewYet': 'Add or import content to analyze structure.',
  'podcasts.pdfImport.markdownMeta': '{{lines}} lines · {{h1}} # heading(s) · {{h2}} ## heading(s)',
  'podcasts.pdfImport.structureBlocked': 'Errors block import — fix Markdown first.',
  'podcasts.pdfImport.structureReady': 'Structure looks good — you can create episodes.',
  'podcasts.pdfImport.structureIssues': 'Structure alerts',
  'podcasts.pdfImport.goToLine': 'Go to line {{line}} in the editor',
  'podcasts.pdfImport.suggestions': 'Suggestions',
  'podcasts.pdfImport.outlinePreview': 'Outline preview',
  'podcasts.pdfImport.conceptionGuide': 'Structuring guide',
  'podcasts.pdfImport.guideH1': '# Heading 1 = one podcast episode (book chapter, course part, etc.).',
  'podcasts.pdfImport.guideH2': '## Heading 2 = chapter inside the episode (sections, sub-parts).',
  'podcasts.pdfImport.guideBlocks': 'Paragraphs and lists = narration blocks for voice-over.',
  'podcasts.pdfImport.guidePdf':
    'Every PDF is different: always review generated Markdown and adjust # / ## before import.',
  'podcasts.error.retry': 'Retry',
  'podcasts.filters.allCategories': 'All categories',
  'podcasts.filters.categoryLabel': 'Category',
  'podcasts.filters.searchLabel': 'Text search',
  'podcasts.filters.searchPlaceholder': 'Title, description…',
  'podcasts.filters.viewCategories': 'Categories view',
  'podcasts.filters.viewList': 'List view',
  'podcasts.form.descriptionLabel': 'Description',
  'podcasts.form.descriptionPlaceholder': 'Describe your podcast…',
  'podcasts.form.namePlaceholder': 'Podcast name',
  'podcasts.form.titleLabel': 'Title',
  'podcasts.episodeOrder.label': 'Episode order',
  'podcasts.episodeOrder.help': 'Playback order in the podcast (matches document order on import).',
  'podcasts.episodeOrder.ariaGroup': 'Episode order',
  'podcasts.episodeOrder.newestFirst': 'Newest → oldest',
  'podcasts.episodeOrder.oldestFirst': 'Oldest → newest',
  'podcasts.language.ariaGroup': 'Podcast language',
  'podcasts.language.help': 'Among the platform\'s 11 languages — to align the podcast with the same locale as the learner player and interface.',
  'podcasts.language.label': 'Language',
  'podcasts.lightbox.ariaClose': 'Close',
  'podcasts.lightbox.ariaDialog': 'Podcast cover preview',
  'podcasts.list.actionActivate': 'Activate',
  'podcasts.list.actionDeactivate': 'Deactivate',
  'podcasts.list.actionDelete': 'Delete',
  'podcasts.list.filterAll': 'All',
  'podcasts.list.filterArchived': 'Archived',
  'podcasts.list.archivePodcast': 'Archive podcast',
  'podcasts.list.restoreFromArchive': 'Restore from archive',
  'podcasts.list.archiveSuccess': 'Podcast archived',
  'podcasts.list.archiveError': 'Could not archive the podcast.',
  'podcasts.list.restoreSuccess': 'Podcast restored',
  'podcasts.list.restoreError': 'Could not restore the podcast.',
  'podcasts.list.badgeArchived': 'Archived',
  'podcasts.list.emptyArchivedNone': 'No archived podcasts.',
  'podcasts.list.emptyAllArchived': 'All podcasts are archived. Open the « Archived » tab to see them.',
  'podcasts.list.actionManageEpisodes': 'Manage episodes',
  'podcasts.list.actionPreview': 'Preview & listen',
  'podcasts.list.coverPreviewAria': 'Podcast cover preview {{title}}',
  'podcasts.list.createFirst': 'Create a podcast',
  'podcasts.list.emptySubtitle': 'Create your first podcast to get started',
  'podcasts.list.emptyTitle': 'No podcasts',
  'podcasts.list.loadError': 'Unable to load podcasts.',
  'podcasts.list.enterpriseBadge': 'Company',
  'podcasts.list.enterpriseNeedTitle': 'Need defined by your company',
  'podcasts.list.episodesShort': '{{count}} ep.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} sub.',
  'podcasts.list.uncategorized': 'Uncategorized',
  'podcasts.modal.errorRequiredFields': 'Title and description are required.',
  'podcasts.modal.errorSaveFailed': 'Error while saving.',
  'podcasts.modal.subtitleNew': 'Enter title, description and options. You can add episodes afterwards.',
  'podcasts.modal.titleEdit': 'Edit podcast',
  'podcasts.modal.titleNew': 'New podcast',
  'podcasts.page.episodesLink': 'Episodes',
  'podcasts.page.newButton': 'New',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Page {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) in total',
  'podcasts.player.listen': 'Listen',
  'podcasts.player.noAudio': 'No audio',
  'podcasts.player.pause': 'Pause',
  'podcasts.learner.pageTitle': 'Listen to podcast',
  'podcasts.learner.invalidLink': 'Invalid or expired link',
  'podcasts.learner.loadError': 'Unable to load podcast',
  'podcasts.learner.loading': 'Loading podcast…',
  'podcasts.learner.noEpisodes': 'No published episode available.',
  'podcasts.learner.listen': 'Listen',
  'podcasts.learner.pause': 'Pause',
  'podcasts.preview.emptySubtitle': 'This podcast has no episodes yet',
  'podcasts.preview.emptyTitle': 'No episodes',
  'podcasts.preview.episodeCount': '{{count}} episode(s)',
  'podcasts.preview.fallbackDescription': 'Listen to available audio episodes.',
  'podcasts.preview.playAll': 'Play all',
  'podcasts.preview.title': 'Podcast preview',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'Subscribe for updates',
  'podcasts.public.subscribeModalTitle': 'Get new episodes by email',
  'podcasts.public.subscribeModalIntro': 'Enter your details. We will email you when a new episode is published.',
  'podcasts.public.fieldFirstName': 'First name',
  'podcasts.public.fieldLastName': 'Last name',
  'podcasts.public.fieldEmail': 'Email',
  'podcasts.public.fieldCompany': 'Company (optional)',
  'podcasts.public.submitSubscribe': 'Confirm subscription',
  'podcasts.public.cancel': 'Cancel',
  'podcasts.public.done': 'Done',
  'podcasts.public.subscribeSuccess': 'Thank you! You will be notified for each new episode.',
  'podcasts.public.subscribeError': 'Could not complete subscription. Please try again later.',
  'podcasts.public.subscribeValidationRequired': 'First name, last name and email are required.',
  'podcasts.public.totalDuration': '{{duration}} total',
  'podcasts.public.listenExcerpt': 'Listen to excerpt',
  'podcasts.playback.resumeAt': 'Resume at {{time}}',
  'podcasts.playback.listened': 'Listened',
  'podcasts.playback.listenedCount': '{{count}} listened',
  'podcasts.share.assignedLearnersHeading': '{{count}} learner(s) assigned',
  'podcasts.share.copied': 'Copied',
  'podcasts.share.copyLink': 'Copy link',
  'podcasts.share.emailPlaceholder': 'Email *',
  'podcasts.share.errorCreateLearner': 'Unable to create learner.',
  'podcasts.share.errorPublicLink': 'Unable to generate public link.',
  'podcasts.share.errorRemoveLearner': 'Unable to remove learner.',
  'podcasts.share.errorShare': 'Unable to share this podcast.',
  'podcasts.share.firstNamePlaceholder': 'First name *',
  'podcasts.share.generatingLink': 'Generating link…',
  'podcasts.share.hideQuickCreate': 'Hide quick create',
  'podcasts.share.lastNamePlaceholder': 'Last name *',
  'podcasts.share.loadingAssigned': 'Loading…',
  'podcasts.share.loadingLearners': 'Loading learners...',
  'podcasts.share.messageAccessRemoved': 'Access removed for the learner.',
  'podcasts.share.messageCreatedAndShared': 'Account created/linked, podcast shared and invitation email sent.',
  'podcasts.share.messageShared': 'Podcast shared and invitation sent to the learner.',
  'podcasts.share.noAssignedLearners': 'No learner assigned to this podcast.',
  'podcasts.share.noLearnerAvailable': 'No learner available.',
  'podcasts.share.panelTitle': 'Share podcast',
  'podcasts.share.publicLinkSection': 'Public link (no login)',
  'podcasts.share.removeAccessTitle': 'Remove access',
  'podcasts.share.searchLearnerPlaceholder': 'Search a learner (last name, first name, email)',
  'podcasts.share.selectExistingLearnerLabel': 'Select an existing learner',
  'podcasts.share.showQuickCreate': 'Create new learner',
  'podcasts.share.submitCreateAndInvite': 'Create, share and send invitation',
  'podcasts.share.socialNetworksHeading': 'Share on social',
  'podcasts.share.shareLinkedIn': 'Share on LinkedIn',
  'podcasts.share.shareFacebook': 'Share on Facebook',
  'podcasts.share.shareTwitter': 'Share on X (Twitter)',
  'podcasts.share.publicShareStatsHeading': 'Public page stats',
  'podcasts.share.publicShareStatsVisits': 'Visits (public page)',
  'podcasts.share.publicShareStatsLoading': 'Loading stats…',
  'podcasts.share.publicShareStatsUnavailable': 'Stats unavailable right now.',
  'podcasts.share.publicShareStatsLinkedIn': 'LinkedIn',
  'podcasts.share.publicShareStatsFacebook': 'Facebook',
  'podcasts.share.publicShareStatsTwitter': 'X (Twitter)',
  'podcasts.share.publicSubscribersHeading': 'Subscribers (email notifications)',
  'podcasts.share.publicSubscribersEmpty': 'No subscribers yet.',
  'podcasts.share.publicSubscribersCompany': 'Company',
  'share.publicQrTitle': 'Public link QR code',
  'share.publicQrError': 'Could not generate the QR code.',
  'share.downloadQrPng': 'Download as PNG',
  'podcasts.status.active': 'Active',
  'podcasts.status.inactive': 'Inactive',
  'learningPaths.card.contents': '{{count}} content items',
  'learningPaths.card.delete': 'Delete',
  'learningPaths.card.edit': 'Edit',
  'learningPaths.card.learners': '{{count}} learners',
  'learningPaths.card.publish': 'Publish',
  'learningPaths.card.unpublish': 'Unpublish',
  'learningPaths.card.updatedAt': 'Updated {{date}}',
  'learningPaths.createPanel.cancel': 'Cancel',
  'learningPaths.createPanel.description': 'Name your path; you can add podcasts and courses to it.',
  'learningPaths.createPanel.descriptionLabel': 'Description',
  'learningPaths.createPanel.descriptionPlaceholder': 'Path goal, target audience…',
  'learningPaths.createPanel.nameLabel': 'Path name',
  'learningPaths.createPanel.namePlaceholder': 'e.g. HR onboarding path…',
  'learningPaths.createPanel.submit': 'Create path',
  'learningPaths.createPanel.title': 'New path',
  'learningPaths.deleteDialog.body': 'You are about to delete "{{title}}". This action cannot be undone.',
  'learningPaths.deleteDialog.cancel': 'Cancel',
  'learningPaths.deleteDialog.confirm': 'Delete',
  'learningPaths.deleteDialog.title': 'Delete path',
  'learningPaths.empty.createFirst': 'Create a path',
  'learningPaths.empty.noPathsHint': 'Create your first path by combining podcasts and courses.',
  'learningPaths.empty.noPathsYet': 'No paths yet',
  'learningPaths.empty.noResults': 'No results',
  'learningPaths.empty.noResultsHint': 'Adjust your filters or create a new path.',
  'learningPaths.error.loadFailed': 'Could not load paths',
  'learningPaths.error.retry': 'Retry',
  'learningPaths.filters.allStatuses': 'All statuses',
  'learningPaths.filters.draft': 'Draft',
  'learningPaths.filters.published': 'Published',
  'learningPaths.filters.searchPlaceholder': 'Search for a path…',
  'learningPaths.page.newButton': 'New path',
  'learningPaths.page.subtitle': 'Create ordered training paths',
  'learningPaths.page.title': 'Paths',
  'learningPaths.page.totalCount': '{{count}} paths',
  'learningPaths.pagination.next': 'Next',
  'learningPaths.pagination.pageOf': 'Page {{page}} / {{total}} · {{count}} paths',
  'learningPaths.pagination.previous': 'Previous',
  'learningPaths.status.draft': 'Draft',
  'learningPaths.status.published': 'Published',
  'learningPaths.editor.backToList': 'Back to paths',
  'learningPaths.editor.pathNameLabel': 'Path name *',
  'learningPaths.editor.idCopyHint': 'Path ID (for support)',
  'learningPaths.editor.sectionNavAria': 'Path editor sections',
  'learningPaths.editor.tabContents': 'Contents',
  'learningPaths.editor.tabLearners': 'Learners',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Copy link',
  'webinars.card.exportAttendance': 'Download attendance (CSV)',
  'webinars.card.attendanceExportError': 'Could not download the attendance file.',
  'webinars.card.delete': 'Delete',
  'webinars.card.edit': 'Edit',
  'webinars.card.openRoom': 'Open room',
  'webinars.card.publicSignupBadge': 'Public signup',
  'webinars.deleteDialog.body': 'Delete "{{title}}"? This action cannot be undone.',
  'webinars.deleteDialog.cancel': 'Cancel',
  'webinars.deleteDialog.confirm': 'Delete',
  'webinars.deleteDialog.title': 'Delete webinar',
  'webinars.eligibility.default': 'Webinars are not available for your account (check the API connection or contact your administrator).',
  'webinars.eligibility.moduleDisabled': 'The webinars module is not yet enabled for your institution. An administrator must enable \'Enable webinars\' in Institution → Webinars.',
  'webinars.eligibility.noInstitution': 'No institution is linked to your account.',
  'webinars.eligibility.notAuthorized': 'Your account is not on the list of authorized hosts. Ask your institution to add you (Webinar management → Authorized hosts).',
  'webinars.eligibility.roleNotAllowed': 'Your role does not allow you to host webinars with this account.',
  'webinars.empty.createFirst': 'Create a webinar',
  'webinars.empty.noResults': 'No results',
  'webinars.empty.noResultsHint': 'Change your search.',
  'webinars.empty.noWebinarsHint': 'Create a room and invite your participants from the side panel.',
  'webinars.empty.noWebinarsYet': 'No webinars yet',
  'webinars.externalInvite.closeButton': 'Close',
  'webinars.externalInvite.description': 'People who are not on the learner list: each address receives an email with the room link and a calendar attachment.',
  'webinars.externalInvite.done': 'Done.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} already invited',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} already invited',
  'webinars.externalInvite.emailsHint': 'One address per line, or separated by commas. Duplicates and invitations already sent are ignored.',
  'webinars.externalInvite.emailsLabel': 'Email addresses',
  'webinars.externalInvite.emailsPlaceholder': 'john.doe@example.com\njane@example.org',
  'webinars.externalInvite.errorMax200': 'Maximum 200 addresses per send.',
  'webinars.externalInvite.errorMinOne': 'Enter at least one valid email address.',
  'webinars.externalInvite.errorsLabel': 'errors: {{errors}}',
  'webinars.externalInvite.sendButton': 'Send invitations',
  'webinars.externalInvite.sendFailed': 'Send failed',
  'webinars.externalInvite.sentCount_one': '{{count}} email sent',
  'webinars.externalInvite.sentCount_other': '{{count}} emails sent',
  'webinars.externalInvite.skippedCount_one': '{{count}} skipped',
  'webinars.externalInvite.skippedCount_other': '{{count}} skipped',
  'webinars.externalInvite.title': 'Email invitations',
  'webinars.invite.duplicatesCount_one': '{{count}} already invited',
  'webinars.invite.duplicatesCount_other': '{{count}} already invited',
  'webinars.invite.errorsLabel': 'errors: {{errors}}',
  'webinars.invite.externalButton': 'Off-list (email)',
  'webinars.invite.failed': 'Failed',
  'webinars.invite.groupSuccess': 'Group invitations processed (emails sent to members).',
  'webinars.invite.inviteButton': 'Invite',
  'webinars.invite.learnersHelp': 'Search for a learner at your institution: an <strong>invitation email</strong> (link + calendar) is sent with each invitation.',
  'webinars.invite.learnersTitle': 'Learners',
  'webinars.invite.noCandidates': 'No learners to show (try another search or use \'Off-list\' for external emails).',
  'webinars.invite.noInvitations': 'No invitations recorded.',
  'webinars.invite.searchLabel': 'Search for a learner',
  'webinars.invite.searchPlaceholder': 'Name or email…',
  'webinars.invite.searching': 'Searching…',
  'webinars.invite.sentCount_one': '{{count}} invitation sent (email)',
  'webinars.invite.sentCount_other': '{{count}} invitations sent (email)',
  'webinars.invite.sentInvitationsToggle': 'Invitations sent',
  'webinars.page.newButton': 'New webinar',
  'webinars.page.searchPlaceholder': 'Search…',
  'webinars.page.subtitle': 'List of your rooms — open the panel to create, edit or invite participants.',
  'webinars.page.title': 'Webinars',
  'webinars.panel.backgroundHint':
    'Image behind shared content for participants: enter a URL (https or http), upload a file (saved to My images), or pick an existing one from your library. Leave empty for the built-in Lyrra visual.',
  'webinars.panel.backgroundLabel': 'Background image (participant view)',
  'webinars.panel.backgroundPlaceholder': 'https://… or My images path',
  'webinars.panel.backgroundUrlLabel': 'Image address (URL)',
  'webinars.panel.backgroundUploadTitle': 'Upload an image (My images)',
  'webinars.panel.backgroundLibraryTitle': 'My images',
  'webinars.panel.backgroundLibraryEmpty': 'No images yet — upload above or from the Images page in your dashboard.',
  'webinars.panel.backgroundOpenImagesPage': 'Open Images page',
  'webinars.panel.backgroundUseDefault': 'Use default Lyrra background',
  'webinars.panel.backgroundUploading': 'Uploading…',
  'webinars.panel.backgroundUploadError': 'Image upload failed.',
  'webinars.panel.copyLink': 'Copy link',
  'webinars.panel.createButton': 'Create',
  'webinars.panel.createDescription': 'Fill in the information then save. You can invite participants afterwards.',
  'webinars.panel.createTitle': 'New webinar',
  'webinars.panel.dateLabel': 'Day (optional)',
  'webinars.panel.deleteButton': 'Delete',
  'webinars.panel.descriptionLabel': 'Description',
  'webinars.panel.durationError': 'Enter a duration in hours:minutes format (e.g. 1:00 or 0:45).',
  'webinars.panel.durationHint': 'Required if a day is set. E.g. 1:30 for 1 h 30, 0:45 for 45 min.',
  'webinars.panel.durationLabel': 'Duration (h:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Loading…',
  'webinars.panel.openRoom': 'Open room',
  'webinars.panel.presentersLabel': 'Co-presenters',
  'webinars.panel.roomCode': 'Room code:',
  'webinars.panel.timeHint': 'If empty: 9:00.',
  'webinars.panel.timeLabel': 'Start time',
  'webinars.panel.titleLabel': 'Title',
  'webinars.panel.waitingHtmlLabel': 'Waiting room HTML (optional)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Welcome…</p>',
  'webinars.panel.publicAccessTitle': 'Public signup (form)',
  'webinars.panel.publicAccessHelp':
    'Participants who are not co-presenters fill in a form after signing in to Lyrra: email, password (they choose it) and any extra fields you add below.',
  'webinars.panel.publicFormFixedFieldsHint':
    'Email and password (min. 8 characters) are always required for participants; add optional or required extra fields here.',
  'webinars.panel.publicFormAddFieldButton': 'Add field',
  'webinars.panel.publicFormFieldNumber': 'Field {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Label shown',
  'webinars.panel.publicFormFieldType': 'Type',
  'webinars.panel.publicFormFieldTypeText': 'Short text',
  'webinars.panel.publicFormFieldTypeTextarea': 'Long text',
  'webinars.panel.publicFormFieldRequired': 'Required',
  'webinars.panel.publicFormFieldRemove': 'Remove',
  'webinars.panel.publicFormFieldIdHint': 'Technical id:',
  'webinars.panel.publicFormFieldLabelRequired': 'Each field must have a label.',
  'webinars.panel.publicAccessLoginLabel': 'Signup form login',
  'webinars.panel.publicAccessPasswordLabel': 'Signup form password',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'At least 8 characters',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Leave blank to keep the current password',
  'webinars.panel.publicAccessPasswordHint':
    'On create or first activation: password required (min. 8). Later: leave blank to keep the current password.',
  'webinars.panel.publicAccessLoginRequired': 'Enter a login for public signup.',
  'webinars.panel.publicAccessPasswordRequired': 'Set a password of at least 8 characters.',
  'webinars.panel.publicAccessPasswordMin': 'The public password must be at least 8 characters.',
  'webinars.status.draft': 'Draft',
  'webinars.status.ended': 'Ended',
  'webinars.status.live': 'Live',
  'webinars.status.scheduled': 'Scheduled',
  'videos.addPanel.cancel': 'Cancel',
  'videos.addPanel.continue': 'Continue',
  'videos.addPanel.description': 'Import MP4, WebM, or MOV to use them in your training.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Click to choose a video',
  'videos.addPanel.title': 'Add a video',
  'videos.addPanel.titleLabel': 'Title',
  'videos.addPanel.titlePlaceholder': 'Video title…',
  'videos.card.delete': 'Delete',
  'videos.card.edit': 'Edit',
  'videos.card.noThumbnail': 'No thumbnail',
  'videos.card.publishedStudio': 'Published (editable in Studio)',
  'videos.card.draft': 'Draft',
  'videos.deleteDialog.body': '« {{title}} » will be permanently deleted.',
  'videos.deleteDialog.cancel': 'Cancel',
  'videos.deleteDialog.confirm': 'Delete',
  'videos.deleteDialog.title': 'Delete video?',
  'videos.empty.addButton': 'Add a video',
  'videos.empty.description': 'Import your first video to manage it and use it in your training.',
  'videos.empty.title': 'No videos',
  'videos.page.addButton': 'Add a video',
  'videos.page.count_one': '{{count}} video in your library',
  'videos.page.count_other': '{{count}} videos in your library',
  'videos.page.title': 'Videos',
  'videos.page.studioButton': 'Studio',
  'videos.studio.title': 'Video studio',
  'videos.studio.rail.prep': 'Prep',
  'videos.studio.rail.capture': 'Capture',
  'videos.studio.rail.post': 'Edit',
  'videos.studio.rail.aria': 'Video studio steps',
  'videos.studio.badge.setup': 'Setup',
  'videos.studio.badge.capture': 'Capture',
  'videos.studio.badge.recording': 'Recording',
  'videos.studio.badge.post': 'Edit',
  'videos.studio.cannotLeaveRecord': 'Stop recording before switching step.',
  'videos.studio.railNeedRecording': 'Record a video to open the edit step.',
  'videos.studio.sidebar.captureHelp':
    'Recording captures the entire shared feed (screen, tab, or window). Cropping to the useful area will be done in the Edit step.',
  'videos.studio.sidebar.sources': 'Sources',
  'videos.studio.sidebar.recordingLive': 'Recording in progress',
  'videos.studio.sidebar.finalizeHint': 'Save to your library using the button in the top bar.',
  'videos.studio.canvas.setupEmpty': 'Read the side panel, then go to the Capture step.',
  'videos.studio.stepSetup': 'Setup',
  'videos.studio.stepCapture': 'Capture',
  'videos.studio.stepPost': 'Edit',
  'videos.studio.titleLabel': 'Video title',
  'videos.studio.titlePlaceholder': 'My recording…',
  'videos.studio.saveSetup': 'Continue to capture',
  'videos.studio.fieldDescription': 'Description',
  'videos.studio.fieldCategories': 'Categories',
  'videos.studio.categoriesHint': 'One or more categories from your institution (optional).',
  'videos.studio.categoriesEmptyInstitution': 'No categories are set up for your institution.',
  'videos.studio.audioTrackPlanned': 'Planned audio track',
  'videos.studio.voiceHumanPlan': 'Human voice (recorded microphone)',
  'videos.studio.voiceAiPlan': 'AI voice (choose for the edit step)',
  'videos.studio.postVoiceHumanKeep':
    'You chose human voice: the video keeps the microphone audio. Finalize when you are ready.',
  'videos.studio.titleUntitled': 'Untitled',
  'videos.studio.voiceAiNoVoices': 'No AI voices are available right now.',
  'videos.studio.postVoiceAiPipeline':
    'AI voice: polish the text, generate the audio track, then apply it to the video.',
  'videos.studio.allowMedia': 'Allow screen and microphone',
  'videos.studio.controlsHint':
    'Tip: to avoid recording this bar, share a tab or a window instead of the full screen, or use a second display. Space = pause / resume when this page is focused.',
  'videos.studio.warnTabOnly':
    'You selected a single tab: only that tab will be recorded, even if you open other tabs. To capture multiple tabs or windows, restart capture and choose "Entire Screen".',
  'videos.studio.templateMenuTitle': 'Templates menu',
  'videos.studio.templateEmpty': 'No saved template',
  'videos.studio.templatesHintCompact': 'Click + to save, apply, or delete a template.',
  'videos.studio.overlayAddMusic': 'Background music',
  'videos.studio.musicInvalidType': 'Invalid format. Use an audio file (MP3, WAV, M4A, OGG).',
  'videos.studio.musicTooLarge': 'Audio file must not exceed 50 MB.',
  'videos.studio.musicDefault': 'Music',
  'videos.studio.musicAdded': 'Background music added to timeline.',
  'videos.studio.musicUploadError': 'Unable to add background music.',
  'videos.studio.layerScreenBorder': 'Outer border',
  'videos.studio.layerScreenBorderSize': 'Size',
  'videos.studio.layerScreenBorderColor': 'Color',
  'videos.studio.layerScreenBorderRadius': 'Corner radius',
  'videos.studio.segmentToolsTitle': 'Segment tools',
  'videos.studio.segmentZoom': 'Zoom',
  'videos.studio.segmentResetView': 'Reset view',
  'videos.studio.segmentTransition': 'Transition',
  'videos.studio.transitionNone': 'None',
  'videos.studio.transitionFade': 'Fade',
  'videos.studio.transitionWipeLeft': 'Wipe →',
  'videos.studio.transitionSlideUp': 'Slide ↑',
  'videos.studio.textEditor.toolbar': 'Text formatting',
  'videos.studio.textEditor.bold': 'Bold',
  'videos.studio.textEditor.italic': 'Italic',
  'videos.studio.textEditor.underline': 'Underline',
  'videos.studio.textEditor.textColor': 'Text color (selection)',
  'videos.studio.textEditor.size': 'Size',
  'videos.studio.textEditor.font': 'Font',
  'videos.studio.textEditor.fontFamily.system': 'System',
  'videos.studio.textEditor.fontFamily.sansInter': 'Sans (Inter)',
  'videos.studio.textEditor.fontFamily.serif': 'Serif',
  'videos.studio.textEditor.fontFamily.mono': 'Mono',
  'videos.studio.textEditor.fontFamily.script': 'Script',
  'videos.studio.controlsHintTab':
    'You are sharing another tab: the recording does not include this Studio page (so these buttons are not in the video). Keep Studio open in another browser window or on a second screen next to your content. For Pause, Stop, or Done, switch to the window where Studio is open (Alt+Tab or Cmd+Tab). Space only works when the Studio page is focused.',
  'videos.studio.controlsHintMonitor':
    'You are sharing the full screen: this bar may appear in the recording. Prefer sharing a Tab or Window, or move the controls to another monitor.',
  'videos.studio.controlsHintWindow':
    'You are sharing one window: the rest of the desktop (and other windows) is usually not recorded. Use the buttons below for Pause, Stop, or Done.',
  'videos.studio.startRecord': 'Record',
  'videos.studio.pause': 'Pause',
  'videos.studio.resume': 'Resume',
  'videos.studio.stop': 'Stop',
  'videos.studio.terminer': 'Done',
  'videos.studio.processingUpload': 'Uploading and transcribing…',
  'videos.studio.timeline': 'Playback',
  'videos.studio.transcript': 'Recognized text',
  'videos.studio.transcriptRefining': 'Refining transcription…',
  'videos.studio.transcriptPlaceholder': 'Recognized text from your recording will appear here…',
  'videos.studio.voiceMode': 'Audio track',
  'videos.studio.voiceOriginal': 'My voice (recorded mic)',
  'videos.studio.voiceAi': 'AI voice (Mistral TTS)',
  'videos.studio.voiceSelect': 'Voice',
  'videos.studio.polish': 'Prepare text for AI voice',
  'videos.studio.generateTts': 'Generate voiceover',
  'videos.studio.applyVoiceover': 'Apply AI voice to video',
  'videos.studio.preview': 'Preview',
  'videos.studio.preview.noFile': 'No video file to preview.',
  'videos.studio.bgImage': 'Background image',
  'videos.studio.bgImageChoose': 'Choose',
  'videos.studio.bgImageInvalidType': 'Invalid format. Use an image (PNG, JPG, WebP).',
  'videos.studio.bgImageTooLarge': 'Image must not exceed 10 MB.',
  'videos.studio.bgImageLoadError': 'Could not load the background image.',
  'videos.studio.bgImageUploading': 'Uploading…',
  'videos.studio.bgImageUploadError': 'Could not upload the image. Try again.',
  'videos.studio.bgImageLibraryTitle': 'Video studio background',
  'videos.studio.postLayoutTitle': 'Frame & background',
  'videos.studio.postLayoutHint':
    'Preview matches the published video: save the draft then publish to apply background, size, and position.',
  'videos.studio.bgImageVideoSize': 'Video size',
  'videos.studio.dragToMove': 'Drag to move',
  'videos.studio.finalize': 'Save to library',
  'videos.studio.saveDraft': 'Draft',
  'videos.studio.publish': 'Publish',
  'videos.studio.successDraft': 'Video saved as draft',
  'videos.studio.successPublish': 'Video published to your library',
  'videos.studio.processingPublish': 'Processing video…',
  'videos.studio.back': 'Back to videos',
  'videos.studio.errorMedia': 'Could not access screen or microphone.',
  'videos.studio.errorInsecureContext':
    'Screen capture requires HTTPS or localhost. Open the app via https:// or http://127.0.0.1 / localhost.',
  'videos.studio.errorNotSupported':
    'This browser or device does not support screen capture here. Use an up-to-date Chrome or Edge on desktop.',
  'videos.studio.errorMicDenied':
    'Microphone blocked: allow microphone for this site (lock or info icon in the address bar), then try again.',
  'videos.studio.errorScreenDenied':
    'Screen sharing denied: pick a screen or tab in the browser dialog and confirm « Share ».',
  'videos.studio.errorMicMissing': 'No microphone found. Check hardware and system settings.',
  'videos.studio.errorScreenMissing': 'Screen sharing is not available on this device or browser.',
  'videos.studio.errorMicBusy': 'Microphone is in use by another app. Close it and try again.',
  'videos.studio.errorScreenBusy': 'Could not capture the screen (resource busy). Retry or restart the browser.',
  'videos.studio.errorScreenCancelled': 'Screen sharing cancelled.',
  'videos.studio.errorMicCancelled': 'Microphone access cancelled.',
  'videos.studio.errorMicGeneric': 'Could not access the microphone.',
  'videos.studio.errorScreenGeneric': 'Could not capture the screen.',
  'videos.studio.errorScreenEnded': 'Screen sharing stopped.',
  'videos.studio.mediaHints':
    'Chrome or Edge on desktop. If you use http:// with a LAN IP (e.g. 192.168.x.x), capture is often blocked: use http://localhost:5173 or valid HTTPS.',
  'videos.studio.errorRecord': 'Recording is not supported in this browser.',
  'videos.studio.errorUpload': 'Upload failed.',
  'videos.studio.errorLoadDraft': 'Could not load draft.',
  'videos.studio.errorTranscribe': 'Transcription unavailable.',
  'videos.studio.errorPolish': 'AI polish failed.',
  'videos.studio.errorTts': 'Text-to-speech failed.',
  'videos.studio.errorMux': 'Audio merge failed.',
  'videos.studio.muxSuccess': 'AI voice applied to the video',
  'videos.studio.successFinal': 'Video saved to your library',
  'videos.studio.generatingTimeline': 'Generating timeline…',
  'videos.studio.crop': 'Crop',
  'videos.studio.cropReset': 'Reset crop',
  'videos.studio.cropActive': 'Crop active — drag or resize the region on the video',
  'videos.studio.cropDone': 'Done',
  'videos.studio.cropApplied': 'Crop applied',
  'videos.studio.cropEdit': 'Edit',
  'videos.studio.cropRemove': 'Remove',
  'videos.studio.trimIn': 'IN',
  'videos.studio.trimOut': 'OUT',
  'videos.studio.trimDuration': 'duration',
  'videos.preview.noFile': 'No file available.',
  'videos.status.draft': 'Draft',
  'videos.status.published': 'Published',
  'sounds.card.delete': 'Delete',
  'sounds.card.download': 'Download',
  'sounds.card.mute': 'Mute',
  'sounds.deleteDialog.body': '« {{title}} » will be permanently removed from the server and your library.',
  'sounds.deleteDialog.cancel': 'Cancel',
  'sounds.deleteDialog.confirm': 'Delete',
  'sounds.deleteDialog.title': 'Delete this sound?',
  'sounds.empty.description': 'Import your files: they are stored in the database and copied to the server so nothing is lost.',
  'sounds.empty.importButton': 'Import a sound',
  'sounds.empty.title': 'No sounds imported',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Cancel',
  'sounds.importPanel.changeFile': 'Change file',
  'sounds.importPanel.createTag': 'Create « {{tag}} »',
  'sounds.importPanel.description': 'Add an audio file to your library for your learning paths.',
  'sounds.importPanel.descriptionLabel': 'Description',
  'sounds.importPanel.descriptionPlaceholder': 'Describe this sound…',
  'sounds.importPanel.dropzoneBrowse': 'browse',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Drop an audio file or',
  'sounds.importPanel.optional': '(optional)',
  'sounds.importPanel.submit': 'Import',
  'sounds.importPanel.submitting': 'Sending…',
  'sounds.importPanel.tagsHint': 'Type and press Enter · New tags are created automatically',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Add tags…',
  'sounds.importPanel.title': 'Import a sound',
  'sounds.importPanel.titleLabel': 'Title',
  'sounds.importPanel.titlePlaceholder': 'Sound name…',
  'sounds.importPanel.titleRequired': 'Title is required',
  'sounds.noResults': 'No sound matches « {{query}} »',
  'sounds.page.count_one': '{{count}} file — saved in the database and on the server',
  'sounds.page.count_other': '{{count}} files — saved in the database and on the server',
  'sounds.page.importButton': 'Import',
  'sounds.page.searchPlaceholder': 'Title, description, tag…',
  'sounds.page.title': 'Sounds',
  'documents.count_one': '{{count}} document',
  'documents.count_other': '{{count}} documents',
  'documents.delete.body': 'The document "{{filename}}" will be removed from your library.',
  'documents.delete.cancel': 'Cancel',
  'documents.delete.confirm': 'Delete',
  'documents.delete.irreversible': 'This action cannot be undone.',
  'documents.delete.title': 'Delete document?',
  'documents.importPanel.cancel': 'Cancel',
  'documents.importPanel.chooseFile': 'Choose a PDF document',
  'documents.importPanel.description': 'The document will be available in your PDF blocks and in this library.',
  'documents.importPanel.descriptionLabel': 'Description',
  'documents.importPanel.descriptionPlaceholder': 'Description (optional)',
  'documents.importPanel.fileHint': 'The document will be available in your PDF block and in this library.',
  'documents.importPanel.submit': 'Import',
  'documents.importPanel.title': 'Import PDF',
  'documents.importPanel.titleLabel': 'Title',
  'documents.importPanel.titlePlaceholder': 'Document title',
  'documents.importPanel.uploading': 'Uploading',
  'documents.importPdf': 'Import PDF',
  'documents.loading': 'Loading library…',
  'documents.newDocument': 'New document',
  'documents.noDocuments': 'No documents',
  'documents.noDocumentsHint': 'Import your PDFs here to reuse them later in the PDF block.',
  'documents.noResults': 'No documents match your search.',
  'documents.pages_one': '{{count}} page',
  'documents.pages_other': '{{count}} pages',
  'documents.previewTitle': 'Document preview',
  'documents.searchPlaceholder': 'Search for a PDF...',
  'documents.share.accessRemoved': 'Access removed for the learner.',
  'documents.share.assignedTitle': 'Learners with access',
  'documents.share.close': 'Close',
  'documents.share.createAndShare': 'Create, share, and send invitation',
  'documents.share.createError': 'Could not create the learner.',
  'documents.share.createLearner': 'Create new learner',
  'documents.share.createdSuccess': 'Account created/linked, document shared, and email sent.',
  'documents.share.creating': 'Sending...',
  'documents.share.emailPlaceholder': 'Email *',
  'documents.share.firstNamePlaceholder': 'First name *',
  'documents.share.hideCreateLearner': 'Hide quick create',
  'documents.share.lastNamePlaceholder': 'Last name *',
  'documents.share.loadingAssigned': 'Loading…',
  'documents.share.loadingLearners': 'Loading learners...',
  'documents.share.noAssigned': 'No learners assigned to this document.',
  'documents.share.noLearners': 'No learners available.',
  'documents.share.removeButton': 'Remove',
  'documents.share.removeError': 'Could not remove access.',
  'documents.share.searchPlaceholder': 'Search for a learner (first name, last name, email)',
  'documents.share.selectLearner': 'Select an existing learner',
  'documents.share.shareButton': 'Share',
  'documents.share.shareError': 'Could not share the document.',
  'documents.share.sharedSuccess': 'Document shared and invitation sent.',
  'documents.share.title': 'Share document',
  'documents.subtitle': 'Reusable PDF document library for the designer.',
  'documents.title': 'Documents',
  'documents.toast.importError': 'Import failed',
  'documents.toast.importErrorDefault': 'The import failed.',
  'documents.toast.importSuccess': 'Document imported',
  'documents.toast.importSuccessDesc': '{{filename}} has been added to your document library.',
  'documents.updating': 'Updating…',
  'help.categories.analytics.description': 'Dashboards, filters and exports to steer the impact of training.',
  'help.categories.analytics.title': 'Analytics & reports',
  'help.categories.apprenants.description': 'Track enrollments, cohorts and participant progress.',
  'help.categories.apprenants.title': 'Learners, groups & tracking',
  'help.categories.boutique.description': 'Sell or assign training when the store is enabled.',
  'help.categories.boutique.title': 'Store & monetization',
  'help.categories.demarrage.description': 'Learn the interface, your profile and daily navigation in Lyrra.',
  'help.categories.demarrage.title': 'Getting started & account',
  'help.categories.donnees.description': 'GDPR, retention, data subject rights and security best practices.',
  'help.categories.donnees.title': 'Personal data & security',
  'help.categories.eduflow.description': 'Design learning paths with blocks, connections and navigation logic.',
  'help.categories.eduflow.title': 'Paths & EduFlow designer',
  'help.categories.evaluation.description': 'Quizzes, assessments, surveys and feedback to measure learning outcomes.',
  'help.categories.evaluation.title': 'Assessment & active learning',
  'help.categories.institution.description': 'Configure your organization, collaborators and content structure.',
  'help.categories.institution.title': 'Institution & team',
  'help.categories.marque.description': 'Align colors, labels and the learner experience with your identity.',
  'help.categories.marque.title': 'Branding & user experience',
  'help.categories.medias.description': 'Manage library, imports and integrated audio / video / image tools in Lyrra.',
  'help.categories.medias.title': 'Media & creative studio',
  'help.categories.parcours-v2.description': 'Campaigns, skills, integrations: everything that enriches training management.',
  'help.categories.parcours-v2.title': 'Advanced features & roadmap',
  'help.categories.podcasts.description': 'Create and distribute long-form audio content outside the block-by-block path.',
  'help.categories.podcasts.title': 'Podcasts & audiobooks',
  'help.categories.publication.description': 'Make a path available to learners and share the right access.',
  'help.categories.publication.title': 'Publication & distribution',
  'help.categories.qualite.description': 'French quality frameworks: traceability, objectives and evidence for audits.',
  'help.categories.qualite.title': 'Quality, Qualiopi & pedagogical compliance',
  'help.categories.support.description': 'When facing a technical or functional issue, know where to look and who to contact.',
  'help.categories.support.title': 'Troubleshooting & support',
  'help.configHint': 'Editorial content: connect your URLs, articles or knowledge base when you are ready.',
  'help.description': 'Documentation and support',
  'help.hubIntro': 'Topics covering Lyrra usage: you will soon be able to link each article to your documentation, tutorials or support tickets.',
  'help.modal.expectedAnswer': 'Expected answer:',
  'help.modal.fallbackHighlight1': 'Article',
  'help.modal.fallbackHighlight2': 'Training platform',
  'help.modal.fallbackHighlight3': 'To be completed',
  'help.modal.fallbackSectionHeading': 'Key takeaways',
  'help.modal.fallbackSectionParagraph': 'The detailed content of this article will be enriched or linked to your internal documentation.',
  'help.modal.fallbackStep1Desc': 'Use the sidebar or the dashboard to access the relevant module.',
  'help.modal.fallbackStep1Title': 'Open the feature',
  'help.modal.fallbackStep2Desc': 'The screens guide you: save often and test in preview mode when available.',
  'help.modal.fallbackStep2Title': 'Follow the business flow',
  'help.modal.fallbackStep3Desc': 'Note your institution\'s specifics (HR processes, naming, brand guidelines) for your team.',
  'help.modal.fallbackStep3Title': 'Document internally',
  'help.modal.fallbackTip': 'If in doubt, check your role (trainer, admin, HR): the displayed menus depend on it.',
  'help.modal.quiz': 'Validation quiz',
  'help.modal.related': 'See also',
  'help.modal.resources': 'Resources',
  'help.modal.steps': 'In practice',
  'help.modal.tips': 'Tips',
  'help.noResults': 'No results for this search.',
  'help.onboarding.ai-designer': 'Training — LyrraStudio AI designer assistant (onboarding)',
  'help.onboarding.blocks': 'Training — EduFlow block types (onboarding)',
  'help.onboarding.branches': 'Training — EduFlow branches & conditions (onboarding)',
  'help.onboarding.catalog-visibility': 'Training — LyrraStudio catalog & visibility (onboarding)',
  'help.onboarding.certificates': 'Training — LyrraStudio issued certificates (onboarding)',
  'help.onboarding.dashboard': 'Training — Lyrra dashboard (onboarding)',
  'help.onboarding.designer-overview': 'Training — EduFlow designer overview (onboarding)',
  'help.onboarding.enrollments': 'Training — LyrraStudio enrollments & participants (onboarding)',
  'help.onboarding.groups-cohorts': 'Training — LyrraStudio groups & cohorts (onboarding)',
  'help.onboarding.media-library': 'Training — LyrraStudio media library (onboarding)',
  'help.onboarding.navigation': 'Training — Lyrra navigation & sidebar (onboarding)',
  'help.onboarding.objectives': 'Training — EduFlow learning objectives (onboarding)',
  'help.onboarding.profile': 'Training — Lyrra profile & preferences (onboarding)',
  'help.onboarding.progress': 'Training — LyrraStudio progress & time spent (onboarding)',
  'help.onboarding.publication-links': 'Training — LyrraStudio access links & sharing (onboarding)',
  'help.onboarding.publication-status': 'Training — LyrraStudio draft, publication and updates (onboarding)',
  'help.onboarding.roles': 'Training — Lyrra roles and access rights (onboarding)',
  'help.onboarding.validation': 'Training — EduFlow path validation (onboarding)',
  'help.onboardingFlowMissing': 'Training not found',
  'help.onboardingFlowMissingDesc': 'Run the db:seed:onboarding-eduflow and db:seed:onboarding-navigation-eduflow scripts (backend), or set the VITE_ONBOARDING_*_FLOW_ID variables.',
  'help.openArticle': 'Open article',
  'help.searchPlaceholder': 'Search a topic…',
  'help.title': 'Help Center',
  'help.topics.analytics-exports.description': 'Prepare extractions for internal or regulatory reporting.',
  'help.topics.analytics-exports.title': 'Exports & HR reporting',
  'help.topics.analytics-indicateurs.description': 'Completion rate, engagement, distribution by status and time on blocks.',
  'help.topics.analytics-indicateurs.title': 'Key indicators',
  'help.topics.analytics-vue.description': 'Customize charts, periods and filter by training to analyze data.',
  'help.topics.analytics-vue.title': 'Analytics overview',
  'help.topics.app-certificats.description': 'Check generated certificates and associated completion conditions.',
  'help.topics.app-certificats.title': 'Issued certificates',
  'help.topics.app-groupes.description': 'Use groups to segment audiences and facilitate campaigns or reports.',
  'help.topics.app-groupes.title': 'Groups & cohorts',
  'help.topics.app-inscriptions.description': 'View the list of learners on a path, their statuses and access history.',
  'help.topics.app-inscriptions.title': 'Enrollments & participants',
  'help.topics.app-progression.description': 'Interpret progress, completions and engagement time within blocks.',
  'help.topics.app-progression.title': 'Progress & time spent',
  'help.topics.boutique-offres.description': 'Link paths to products or access codes based on your business model.',
  'help.topics.boutique-offres.title': 'Offers & pricing',
  'help.topics.boutique-paiement.description': 'Understand the purchase flow on the learner side and confirmations on the institution side.',
  'help.topics.boutique-paiement.title': 'Payments & billing',
  'help.topics.demarrage-navigation.description': 'Find the Projects, Training, Media, Analytics and Settings spaces based on your role.',
  'help.topics.demarrage-navigation.title': 'Navigation & sidebar',
  'help.topics.demarrage-profil.description': 'Update your information, interface language and notifications.',
  'help.topics.demarrage-profil.title': 'Profile & preferences',
  'help.topics.demarrage-roles.description': 'Understand the differences between trainer, institution admin, HR and learner.',
  'help.topics.demarrage-roles.title': 'Roles and access rights',
  'help.topics.demarrage-tableau-bord.description': 'Read key indicators, quickly access recent paths and useful shortcuts.',
  'help.topics.demarrage-tableau-bord.title': 'Training dashboard',
  'help.topics.donnees-conservation.description': 'Archiving duration principles for progress and training evidence.',
  'help.topics.donnees-conservation.title': 'Retention period',
  'help.topics.donnees-mots-de-passe.description': 'Password best practices, sessions and access delegation.',
  'help.topics.donnees-mots-de-passe.title': 'Accounts & authentication',
  'help.topics.donnees-rgpd.description': 'Purposes of data collected in Lyrra and the training manager\'s responsibility.',
  'help.topics.donnees-rgpd.title': 'GDPR & processing registry',
  'help.topics.eduflow-blocs.description': 'Choose and configure the right blocks: text, video, audio, assessment, branching, etc.',
  'help.topics.eduflow-blocs.title': 'Block types (content, quiz, media…)',
  'help.topics.eduflow-branches.description': 'Manage multiple outputs, path convergence and navigation rules.',
  'help.topics.eduflow-branches.title': 'Branches & conditions',
  'help.topics.eduflow-concept.description': 'Canvas, block palette, selection, zoom and visual organization of the path.',
  'help.topics.eduflow-concept.title': 'Designer overview',
  'help.topics.eduflow-ia.description': 'Suggest or edit blocks with the assistant, with systematic human review.',
  'help.topics.eduflow-ia.title': 'AI assistant in the designer',
  'help.topics.eduflow-objectifs.description': 'Link objectives to blocks to structure the framework of your training.',
  'help.topics.eduflow-objectifs.title': 'Learning objectives',
  'help.topics.eduflow-validation.description': 'Use the Validation panel to detect errors and warnings before publication.',
  'help.topics.eduflow-validation.title': 'Path validation',
  'help.topics.eval-auto-eval.description': 'Offer adaptive paths based on results.',
  'help.topics.eval-auto-eval.title': 'Self-assessment & remediation',
  'help.topics.eval-ouverte.description': 'Organize the collection of free-form answers and manual or semi-automated grading.',
  'help.topics.eval-ouverte.title': 'Open-ended questions & grading',
  'help.topics.eval-quiz.description': 'Configure scoring, feedback and attempts for effective MCQs.',
  'help.topics.eval-quiz.title': 'Quizzes & multiple-choice questions',
  'help.topics.inst-collaborateurs.description': 'Add trainers, administrators or HR staff and manage permissions.',
  'help.topics.inst-collaborateurs.title': 'Users & invitations',
  'help.topics.inst-structure.description': 'Define what is shared at institution level versus personal projects.',
  'help.topics.inst-structure.title': 'Structure & scope',
  'help.topics.inst-trash.description': 'Find deleted training or podcasts and restore them if needed.',
  'help.topics.inst-trash.title': 'Trash & restoration',
  'help.topics.marque-accessibilite.description': 'Contrast, text alternatives and keyboard navigation in content.',
  'help.topics.marque-accessibilite.title': 'Accessibility & readability',
  'help.topics.marque-charte.description': 'How branding is reflected in the player and learner spaces.',
  'help.topics.marque-charte.title': 'Brand & institutional colors',
  'help.topics.marque-landing.description': 'Showcase your training offering to visitors when the module is enabled.',
  'help.topics.marque-landing.title': 'Public pages & catalog',
  'help.topics.medias-bibliotheque.description': 'Import, organize and reuse videos, sounds, images and documents in your blocks.',
  'help.topics.medias-bibliotheque.title': 'Media library',
  'help.topics.medias-documents.description': 'Attach downloadable resources and link documentation to the path.',
  'help.topics.medias-documents.title': 'Documents & PDFs',
  'help.topics.medias-studio-audio.description': 'Record, mix or use text-to-speech to enhance your content.',
  'help.topics.medias-studio-audio.title': 'Audio & voice studio',
  'help.topics.medias-video.description': 'Best practices for format, size and accessibility for embedded players.',
  'help.topics.medias-video.title': 'Videos & integrations',
  'help.topics.podcasts-creation.description': 'Structure episodes, metadata and covers for your audio offering.',
  'help.topics.podcasts-creation.title': 'Creating a podcast',
  'help.topics.podcasts-diffusion.description': 'Listening settings, sharing and usage tracking for the institution.',
  'help.topics.podcasts-diffusion.title': 'Distribution & learners',
  'help.topics.pub-catalogue.description': 'Attach a training to the institutional catalog or an offer when enabled.',
  'help.topics.pub-catalogue.title': 'Catalog & visibility',
  'help.topics.pub-liens.description': 'Generate or copy links for learners, presenters or preview.',
  'help.topics.pub-liens.title': 'Access links & sharing',
  'help.topics.pub-statuts.description': 'Training lifecycle: save, test, publish and version changes.',
  'help.topics.pub-statuts.title': 'Draft, publication and updates',
  'help.topics.qualite-qualiopi.description': 'Use dedicated spaces to prepare criteria and the quality dossier.',
  'help.topics.qualite-qualiopi.title': 'Pre-audit & Qualiopi indicators',
  'help.topics.qualite-tracabilite.description': 'Link objectives, content and assessments to document the certified path.',
  'help.topics.qualite-tracabilite.title': 'Pedagogical traceability',
  'help.topics.support-contact.description': 'Support channel, ticket creation and information to provide for a quick diagnosis.',
  'help.topics.support-contact.title': 'Contact Lyrra support',
  'help.topics.support-faq.description': 'Player not loading, missing media, publication error: first steps.',
  'help.topics.support-faq.title': 'Common issues',
  'help.topics.support-navigateur.description': 'Recommended versions, cookies and extensions that may interfere.',
  'help.topics.support-navigateur.title': 'Browser & compatibility',
  'help.topics.v2-campagnes.description': 'Plan the deployment of paths to targeted audiences over time.',
  'help.topics.v2-campagnes.title': 'Campaigns & training plans',
  'help.topics.v2-competences.description': 'Map blocks or paths to expected skills for business reporting.',
  'help.topics.v2-competences.title': 'Skills frameworks',
  'help.topics.v2-integrations.description': 'Exchanges with your HR IS, external LMS or BI tools when connectors are available.',
  'help.topics.v2-integrations.title': 'Integrations & API',
  'pages.images.cancel': 'Cancel',
  'pages.images.changeFile': 'Change file',
  'pages.images.copyUrl': 'Copy URL',
  'pages.images.copyUrlSuccess': 'URL copied to clipboard.',
  'pages.images.copyUrlError': 'Could not copy the URL.',
  'pages.images.urlLabel': 'Public URL',
  'pages.images.download': 'Download',
  'pages.images.downloadSuccess': 'Download started.',
  'pages.images.downloadError': 'Download failed.',
  'pages.images.count_one': '{{count}} image in your library',
  'pages.images.count_other': '{{count}} images in your library',
  'pages.images.delete': 'Delete',
  'pages.images.deleteAction': 'Delete',
  'pages.images.deleteConfirmHint': 'will be permanently deleted.',
  'pages.images.deleteTitle': 'Delete this image?',
  'pages.images.descriptionLabel': 'Description (optional)',
  'pages.images.descriptionPlaceholder': 'Short description…',
  'pages.images.dropzoneBrowse': 'browse',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Drag an image or',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Import',
  'pages.images.importSubtitle': 'Add an image to your library for your training.',
  'pages.images.importTitle': 'Import an image',
  'pages.images.noImages': 'No images imported',
  'pages.images.noResults': 'No results',
  'pages.images.searchPlaceholder': 'Search for an image…',
  'pages.images.submit': 'Import',
  'pages.images.titleLabel': 'Title (optional)',
  'pages.images.titlePlaceholder': 'Image name…',
  'pages.images.viewGrid': 'Grid',
  'pages.images.viewList': 'List',
  'pages.images.editPanelTitle': 'Edit image',
  'pages.images.editPanelSubtitle':
    'Title, description and file name. Replacing the file keeps the same URL (existing references keep working).',
  'pages.images.replaceFileLabel': 'New image file',
  'pages.images.replaceFileHint':
    'The image is converted to match the current file extension. The public URL does not change.',
  'pages.images.fileOriginalNameLabel': 'Original file name',
  'pages.images.saveChanges': 'Save',
  'pages.images.saveSuccess': 'Image updated.',
  'pages.images.saveError': 'Could not save.',
  'pages.images.editAction': 'Edit',
  // ── Auto-injected missing t() fallbacks ──
  'accessibility.simplifiedTextMode': 'Simplified text mode',
  'accessibility.simplifiedTextModeDesc':
    'Simplifies the text on the current page with AI (layout preserved). In a course, course blocks are processed one by one.',
  'accessibility.simplifiedTextPageLoading': 'Simplifying this page with AI…',
  'accessibility.simplifiedTextPageError':
    'Unable to simplify this page right now. Please try again in a moment.',
  'accessibility.imageHoverAudio': 'Low-vision mode: play image audio on hover',
  'accessibility.imageHoverAudioDesc':
    'When an image is hovered, AI analyzes its visual content (base64 upload) and reads the description aloud.',
  'accessibility.imageHoverAudio.loading': 'Loading audio description…',
  'accessibility.imageHoverAudio.loadingTts': 'Generating audio description…',
  'accessibility.imageHoverAudio.loadingVision': 'Analyzing image and generating audio…',
  'accessibility.imageHoverAudio.playing': 'Playing description…',
  'accessibility.imageHoverAudio.error': 'Unable to play audio.',
  'accessibility.imageHoverAudio.unavailable': 'Audio description unavailable for this image.',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Training request — educational content',
  'trainingRequest.intro': 'Describe the need. HR teams approve or decline the request; if approved, it moves to content preparation.',
  'trainingRequest.fieldTitle': 'Training title',
  'trainingRequest.fieldDescription': 'Description of the need',
  'trainingRequest.fieldJustification': 'Justification / objectives',
  'trainingRequest.fieldDate': 'Preferred date',
  'trainingRequest.datePlaceholder': 'e.g. Q2 2026',
  'trainingRequest.fieldFormateur': 'Institution trainer',
  'trainingRequest.formateurPlaceholder': 'Choose a trainer…',
  'trainingRequest.formateursEmpty': 'No trainer account listed for your institution.',
  'trainingRequest.titleRequired': 'Please enter a title (minimum 2 characters).',
  'trainingRequest.sent': 'Request submitted. You will be notified by email of the decision.',
  'trainingRequest.sendError': 'Unable to submit the request. Check your connection or permissions.',
  'trainingRequest.submit': 'Submit request',
  'common.share': 'Partager',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.collaborators': 'Collaborateurs',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Collaboratif',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.coverLabel': 'Image de couverture',
  'eduflow.list.coverRemove': 'Retirer la couverture',
  'eduflow.list.coverUpload': 'Choisir une image',
  'eduflow.list.flowProperties': 'Title, description & cover',
  'eduflow.list.propertiesPanelTitle': 'Training properties',
  'eduflow.list.duplicatePanelTitle': 'Duplicate training',
  'eduflow.list.duplicatePanelSubtitle': 'Original training: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copy',
  'eduflow.list.duplicateSuccess': 'Training duplicated.',
  'eduflow.list.duplicateCoverHint':
    'Cover image and learning content from the original training will be copied.',
  'eduflow.list.propertiesSaveError': 'Could not save properties.',
  'eduflow.list.propertiesSaved': 'Properties saved.',
  'eduflow.list.propertiesUploadError': 'Image upload failed.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Suivant',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.readComplete': 'Read complete',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory.activateSuccess': 'Version {{label}} activated (public)',
  'eduflow.versionHistory.activateTitle': 'Activate {{label}}?',
  'eduflow.versionHistory.confirmActivate': 'Activate',
  'eduflow.versionHistory.copyFailed': 'Copy failed',
  'eduflow.versionHistory.copyHtml': 'Copy HTML',
  'eduflow.versionHistory.copyPlain': 'Copy plain text',
  'eduflow.versionHistory.createMajor': 'Major version',
  'eduflow.versionHistory.createMajorSuccess': 'Major version created',
  'eduflow.versionHistory.createMinor': 'Minor version',
  'eduflow.versionHistory.createMinorSuccess': 'Minor version created',
  'eduflow.versionHistory.deleteError': 'Error deleting version',
  'eduflow.versionHistory.deleteSuccess': 'Version deleted',
  'eduflow.versionHistory.deleteTitle': 'Delete {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'This action is irreversible.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplicate',
  'eduflow.versionHistory.duplicateError': 'Error duplicating version',
  'eduflow.versionHistory.duplicateMinor': 'Duplicate (minor)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Duplicate {{label}}?',
  'eduflow.versionHistory.newFromCurrent': 'New version from current content',
  'eduflow.versionHistory.preview': 'Preview',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocks',
  'eduflow.versionHistory.previewEdges': '{{count}} connections',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Preview {{label}}',
  'eduflow.versionHistory.setPublic': 'Activate',
  'eduflow.versionHistory.startDescModalTitle': 'Description — Start block ({{version}})',
  'eduflow.videoCompleted': 'Video watched',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.learners.statsAdaptiveChoice': 'Choix assistes - acceptes: {{accepted}} · refuses: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptations evaluees: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versions simplifiees utilisees: {{count}}',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Circular path detected',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} error(s)',
  'designer.validation.warningCount': '{{count}} warning(s)',
  'designer.validation.titleRequired': 'The « {{type}} » block must have a title.',
  'designer.validation.summary': 'Flow: {{blocks}} block(s), {{edges}} connection(s).',
  'designer.validation.multipleStart': 'Multiple Start blocks ({{count}}): only one is allowed.',
  'designer.validation.noOutgoing': 'The « {{type}} » block has no outgoing connection to a next block.',
  'designer.validation.tooManyOutputs': '« {{label}} »: too many outgoing connections ({{count}}, maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Branch with no path to an End block (from « {{label}} »).',
  'designer.validation.moreDeadEnds': '… and {{count}} more branch(es) with no path to the end.',
  'designer.validation.noConvergence': 'Branches from « {{label}} » do not converge to a common block.',
  'designer.validation.orphanBlock': 'Block isolated from the main graph: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'The End block « {{title}} » still has an outgoing connection.',
  'designer.validation.noScoring': 'Scoring settings not defined for « {{title}} ».',
  'designer.validation.emptyText': 'The text block « {{title}} » is empty.',
  'designer.validation.textTooLong':
    'Text block « {{title}} » is estimated at {{lines}} lines. Recommended: {{min}} to {{max}} lines.',
  'designer.validation.splitTextBlock': 'Split',
  'designer.validation.textSplitNotNeeded':
    'This block does not exceed the recommended reading threshold.',
  'designer.validation.textSplitUnable':
    'Unable to provide a reliable automatic split for this block.',
  'designer.validation.textSplitComplexFlow':
    'Automatic split is unavailable for this block (multiple/conditional branches).',
  'designer.validation.textSplitDone': '{{count}} text blocks created',
  'designer.validation.textSplitConfirmTitle': 'Automatically split this text block?',
  'designer.validation.textSplitConfirmMessage':
    'Block « {{title}} » is estimated at {{lines}} lines. We will split it into {{parts}} blocks (sentence-based on punctuation) and place them just below, in order.',
  'designer.validation.textSplitConfirmAction': 'Split block',
  'designer.validation.formFieldRefsMissing':
    '« {{title}} »: reference(s) to missing form field(s): {{refs}}.',
  'designer.validation.formFieldRefsOrder':
    '« {{title}} »: invalid form field variable(s) {{refs}} — the form block must be reached before this block in the flow.',
  'designer.confirmDeleteForm.title': 'Delete this form block?',
  'designer.confirmDeleteForm.message':
    'Other blocks may reference its fields as /fieldId/. Those references will break. You can undo with ⌘Z or Ctrl+Z.',
  'designer.confirmDeleteForm.batchTitle': 'Delete selection?',
  'designer.confirmDeleteForm.batchMessage':
    'The selection includes at least one form block. Slash references elsewhere may break. You can undo with ⌘Z or Ctrl+Z.',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Default voice',
  'voicePanel.provider': 'Provider: Mistral AI',
  'voicePanel.hint': 'Select the voice used for block narration.',
  'voicePanel.multiLangHint':
    'For one Mistral voice per UI language (all 10), open the Translation panel (languages icon in the designer toolbar).',
  'voicePanel.translationFrenchFallback':
    'Translated audio: with no voice saved for a language, the engine uses “Marie - sad” (Mistral) for French, Italian and Spanish, and “Paul - sad” for other languages.',
  'voicePanel.search': 'Search…',
  'voicePanel.allLanguages': 'All languages',
  'voicePanel.loading': 'Loading voices…',
  'voicePanel.loadError': 'Unable to load voices',
  'voicePanel.empty': 'No voices available.',
  'voicePanel.emptyHint': 'Check the Mistral API key in Administration → Integrations.',
  'voicePanel.noResults': 'No voices match your search.',
  'voicePanel.saved': 'Default voice saved',
  'voicePanel.saveError': 'Error saving voice',
  'voicePanel.default': 'Default',
  'voicePanel.female': 'Female',
  'voicePanel.male': 'Male',
  'voicePanel.playPreview': 'Preview voice',
  'voicePanel.stopPreview': 'Stop',
  'voicePanel.noPreview': 'Preview not available',
  'voicePanel.previewLoading': 'Generating…',
  'voicePanel.previewGenerateError': 'Could not generate the audio preview.',
  'voicePanel.previewPlayError': 'Audio playback failed.',
  'voicePanel.filterNoVoicesForLang':
    'No catalog voice matches this language — try “All languages”.',
  'episodeEditor.voicePicker.blockVoice': 'Voice for this block',
  'episodeEditor.voicePicker.defaultVoice': 'Default voice',
  'episodeEditor.voicePicker.voices': 'voices',
  'episodeEditor.voicePicker.searchPlaceholder': 'Search a voice…',
  'episodeEditor.voicePicker.noVoiceFound': 'No voice found',
  'episodeEditor.voicePicker.female': 'Female',
  'episodeEditor.voicePicker.male': 'Male',
  'episodeEditor.voicePicker.stop': 'Stop',
  'episodeEditor.voicePicker.preview': 'Preview voice',
  'episodeEditor.voicePicker.stopPlayback': 'Stop playback',
  'episodeEditor.errors.notFound': 'Episode not found',
  'episodeEditor.errors.titleRequired': 'Title is required.',
  'episodeEditor.errors.saveFailed': 'Error saving.',
  'episodeEditor.errors.noVoicesAvailable': 'No voices available. Enter the API key in Administration → Platform integrations, then reload the page.',
  'episodeEditor.errors.selectVoiceFirst': 'Select a voice in the "Voice" tab before generating voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'One or more blocks have an invalid voice. Open the per-block voice selector or reload the voice list.',
  'episodeEditor.errors.addTextFirst': 'Add text before generating audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audio generation failed. {{message}} (displayed duration = estimate.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error during audio generation. Displayed duration = estimate.',
  'episodeEditor.success.published': 'Episode published!',
  'episodeEditor.success.draftSaved': 'Draft saved',
  'episodeEditor.success.audioGenerated': 'Audio generated with {{count}} block(s)',
  'episodeEditor.tabs.edit': 'Edit',
  'episodeEditor.tabs.chapters': 'Chapters',
  'episodeEditor.tabs.voice': 'Voice',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Undo (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Redo (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generating…',
  'episodeEditor.toolbar.generateAudio': 'Generate audio',
  'episodeEditor.toolbar.save': 'Save',
  'episodeEditor.toolbar.publish': 'Publish',
  'episodeEditor.titlePlaceholder': 'Episode title…',
  'episodeEditor.status.published': 'Published',
  'episodeEditor.status.draft': 'Draft',
  'episodeEditor.warnings.mistralNotConfigured': 'Episode voiceover uses Mistral Voxtral TTS. Add the Mistral key in Administration → Platform integrations, then reload this page.',
  'episodeEditor.warnings.mistralNoVoices': 'The Mistral key is recognized but the API returned no voices. Check backend logs.',
  'episodeEditor.warnings.voiceLoadFailed': 'Unable to load voice list. Check Mistral configuration in Administration → Platform integrations.',
  'episodeEditor.warnings.voiceOffDisabled': 'Without a real voice, voiceover generation remains disabled.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthesis via Mistral Voxtral TTS. Choose the voice in the "Voice" tab or per block.',
  'episodeEditor.edit.coverImage': 'Cover image',
  'episodeEditor.edit.coverAlt': 'Episode cover',
  'episodeEditor.edit.deleteCover': 'Delete',
  'episodeEditor.edit.dropOrClickImage': 'Click or drop an image',
  'episodeEditor.edit.selectedVoice': 'Selected voice',
  'episodeEditor.edit.selectVoice': 'Select a voice',
  'episodeEditor.edit.defaultVoiceByLang': 'Default voice (podcast language)',
  'episodeEditor.edit.lastAudioGenerated': 'Last generated audio',
  'episodeEditor.edit.audioNotFound': 'Audio file not found — regenerate the voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generating…',
  'episodeEditor.edit.generateVoiceOff': 'Generate voiceover',
  'episodeEditor.chapters.title': 'Chapters',
  'episodeEditor.chapters.add': 'Add',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapter {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} block(s)',
  'episodeEditor.chapters.readByAi': 'Read chapter title',
  'episodeEditor.chapters.readByAiHint':
    'When off, the AI does not read this chapter title aloud. The chapter blocks stay included in the voiceover.',
  'episodeEditor.voiceTab.availableVoices': 'Available voices',
  'episodeEditor.podcasts.linked': 'Linked podcasts',
  'episodeEditor.podcasts.assign': 'Assign',
  'episodeEditor.podcasts.noneAvailable': 'No podcast available',
  'episodeEditor.podcasts.close': 'Close',
  'episodeEditor.podcasts.noneLinked': 'No linked podcast',
  'episodeEditor.podcasts.assignHint': 'Assign this episode to one or more podcasts',
  'episodeEditor.podcasts.remove': 'Remove',
  'episodeEditor.editor.addTextBlock': 'Add a text block',
  'episodeEditor.editor.scriptPlaceholder': 'Start writing your script…',
  'episodeEditor.editor.addBlock': 'Add a block',
  'episodeEditor.player.loop': 'Loop',
  'episodeEditor.player.playbackSpeed': 'Playback speed',
  'episodeEditor.player.collapse': 'Collapse',
  'episodeEditor.player.expand': 'Expand',
  'episodeEditor.player.waveformHidden': 'Waveform hidden',
  'podcast.episodes.listen': 'Listen',
  'podcast.episodes.noAudioShort': 'No audio available',
  'podcast.episodes.edit': 'Edit',
  'podcast.episodes.delete': 'Delete',
  'podcast.episodes.unmute': 'Unmute',
  'podcast.episodes.mute': 'Mute',
  'podcast.episodes.totalEpisodes': '{{count}} episode(s) total',
  'eduflow.designer.toast.blockCopied': 'Block copied',
  'eduflow.designer.toast.blockPasted': 'Block pasted',
  'eduflow.designer.toast.blockDuplicated': 'Block duplicated',
  'eduflow.designer.toast.connectionUpdated': 'Connection updated',
  'eduflow.designer.toast.flowExported': 'Course exported',
  'eduflow.designer.toast.flowImported': 'Course imported',
  'eduflow.designer.toast.flowPastedFromClipboard': 'Course created from clipboard',
  'eduflow.designer.toast.invalidFile': 'Invalid file',
  'eduflow.designer.toast.exportFailed': 'Export failed.',
  'eduflow.designer.toast.importPackageFailed': 'Could not import the .lyrra package.',
  'eduflow.designer.untitled': 'Untitled',
  'eduflow.designer.importedFlow': 'Imported course',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'User',
  'eduflow.designer.collaborator.editor': 'Editor',
  'eduflow.designer.collaborator.viewer': 'Viewer',
  'eduflow.designer.resizePanel': 'Resize panel',
  'eduflow.designer.published': 'Published',
  'eduflow.designer.draft': 'Draft',
  'eduflow.designer.quizOutput.success': 'Passed',
  'eduflow.designer.quizOutput.failure': 'Failed',
  'eduflow.designer.loopHandle.loop': 'Loop',
  'eduflow.designer.loopHandle.end': 'End',
  'eduflow.designer.handle.default': 'Default',
  'eduflow.designer.genError': 'Error during generation. Please try again.',
  'trainerTour.welcome.title': 'Welcome to your trainer workspace',
  'trainerTour.welcome.description':
    'This short tour introduces the dashboard and menu. Then, the first time you open each page, a tip explains the essentials. You can close a step with the X — the tour will not start again.',
  'trainerTour.dashboard.header.title': 'Your dashboard',
  'trainerTour.dashboard.header.description':
    'Overview of your activity: key numbers and shortcuts to create content quickly.',
  'trainerTour.dashboard.actions.title': 'Create content',
  'trainerTour.dashboard.actions.description':
    'Shortcuts to a new EduFlow course, a podcast, or a learning path. Use them to start a project without going through the menu.',
  'trainerTour.dashboard.stats.title': 'Metrics',
  'trainerTour.dashboard.stats.description':
    'Courses, published paths, learners reached, and podcasts: see the scope of your catalog at a glance.',
  'trainerTour.dashboard.activity.title': 'Recent activity',
  'trainerTour.dashboard.activity.description':
    'Latest updated content. Click a row to reopen the matching course, podcast, or path.',
  'trainerTour.sidebar.title': 'Side menu',
  'trainerTour.sidebar.description':
    'Overview (home, learners, analytics, missions), creation (courses, podcasts, paths, webinars if enabled), media library, and help. Open each section — a contextual tip appears the first time on every page.',
  'trainerTour.routes.learners.title': 'Learners',
  'trainerTour.routes.learners.description':
    'People enrolled in your content, progress, exports — your hub for teaching follow-up.',
  'trainerTour.routes.analytics.title': 'Analytics',
  'trainerTour.routes.analytics.description':
    'Charts on engagement and completion. Customize the layout to track what matters to you.',
  'trainerTour.routes.eduflow.title': 'Courses (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Create and manage interactive courses, versions, publishing, and sharing — the core of instructional design.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Shows, episodes, visibility, and sharing with your organization’s groups or learners.',
  'trainerTour.routes.parcours.title': 'Learning paths',
  'trainerTour.routes.parcours.description':
    'Sequences of courses and resources: structure long journeys and assign them to your audiences.',
  'trainerTour.routes.videos.title': 'Videos',
  'trainerTour.routes.videos.description':
    'Reusable video library for your courses and documents.',
  'trainerTour.routes.sounds.title': 'Audio',
  'trainerTour.routes.sounds.description':
    'Audio files and tags for podcasts and sound blocks in courses.',
  'trainerTour.routes.images.title': 'Images',
  'trainerTour.routes.images.description':
    'Centralized visuals: import, folders, and reuse across content.',
  'trainerTour.routes.documents.title': 'Documents',
  'trainerTour.routes.documents.description':
    'PDFs and attachments, with optional targeted sharing to learners.',
  'trainerTour.routes.help.title': 'Help',
  'trainerTour.routes.help.description':
    'Articles, tutorials, and walkthroughs about LyrraStudio. Come back when you are stuck on a feature.',
  'trainerTour.routes.producerAssignments.title': 'Production missions',
  'trainerTour.routes.producerAssignments.description':
    'When HR assigns catalog items for you to produce, they show up here with progress.',
  'trainerTour.routes.webinars.title': 'Webinars',
  'trainerTour.routes.webinars.description':
    'Schedule live sessions, invitations, and broadcast links for your groups.',
  'trainerTour.driver.progress': '{{current}} of {{total}}',
  'trainerTour.driver.next': 'Next',
  'trainerTour.driver.prev': 'Previous',
  'trainerTour.driver.done': 'Done',
  'trainerTour.driver.gotIt': 'Got it',
  'player.lightMode': 'Light mode',
  'player.darkMode': 'Dark mode',
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'a11y.skipToPlayerMain': 'Skip to course content',
  'a11y.playerMainLandmark': 'Course content',
  'a11y.dialog': 'Dialog',
  'a11y.closeDialog': 'Close dialog',
  'a11y.dismissNotification': 'Dismiss notification',
  'a11y.notificationsRegion': 'Notifications',
  'a11y.skipLinksLandmark': 'Quick access links',

  // ── Parity with fr (dashboard / roles / projects / store / audio / notifications / settings / analytics / gamification / Qualiopi / training / player) ──
  'role.institution_admin': 'Organization administrator',
  'role.resp_formation': 'Training manager',
  'dashboard.recent_projects': 'Recent projects',
  'dashboard.recent_activity': 'Recent activity',
  'dashboard.quick_actions': 'Quick actions',
  'dashboard.stats': 'Statistics',
  'dashboard.total_projects': 'Total projects',
  'dashboard.total_learners': 'Total learners',
  'dashboard.total_eduflows': 'Total courses',
  'dashboard.completion_rate': 'Completion rate',
  'dashboard.active_users': 'Active users',
  'dashboard.new_registrations': 'New registrations',
  'dashboard.xp_earned': 'XP earned',
  'dashboard.streak': 'Current streak',
  'dashboard.level': 'Level {{level}}',
  'dashboard.badges_earned': 'Badges earned',
  'dashboard.next_level': 'Next level',
  'dashboard.no_projects': 'No projects yet',
  'dashboard.create_first': 'Create your first project',
  'dashboard.pending_reviews': 'Pending reviews',
  'dashboard.upcoming_deadlines': 'Upcoming deadlines',
  'eduflow.summary.editorHint':
    'List the concepts or steps covered in the course. Each line appears as a numbered card in the player.',
  'project.title': 'Projects',
  'project.new': 'New project',
  'project.name': 'Project name',
  'project.description': 'Project description',
  'project.status.in_review': 'In review',
  'project.visibility.public': 'Public',
  'project.visibility.private': 'Private',
  'project.visibility.institution': 'Organization',
  'project.no_projects': 'No projects found',
  'project.delete_confirm': 'Are you sure you want to delete this project?',
  'store.categories': 'Categories',
  'store.price': 'Price',
  'store.free': 'Free',
  'store.buy': 'Buy',
  'store.add_to_cart': 'Add to cart',
  'store.cart': 'Cart',
  'store.checkout': 'Checkout',
  'store.reviews': 'Reviews',
  'store.rating': 'Rating',
  'store.my_purchases': 'My purchases',
  'store.no_items': 'No items found',
  'audio.title': 'Audio Studio',
  'audio.audiobooks': 'Audiobooks',
  'audio.new_audiobook': 'New audiobook',
  'audio.record': 'Record',
  'audio.stop': 'Stop',
  'audio.play': 'Play',
  'audio.pause': 'Pause',
  'audio.volume': 'Volume',
  'audio.tracks': 'Tracks',
  'audio.add_track': 'Add track',
  'audio.remove_track': 'Remove track',
  'audio.effects': 'Effects',
  'audio.export': 'Export',
  'audio.export_format': 'Export format',
  'audio.duration': 'Duration',
  'audio.narrator': 'Narrator',
  'audio.chapter': 'Chapter',
  'notification.title': 'Notifications',
  'notification.mark_all_read': 'Mark all as read',
  'notification.no_notifications': 'No notifications',
  'notification.new': 'New notification',
  'notification.type.info': 'Information',
  'notification.type.success': 'Success',
  'notification.type.warning': 'Warning',
  'notification.type.error': 'Error',
  'settings.title': 'Settings',
  'settings.general': 'General',
  'settings.account': 'Account',
  'settings.security': 'Security',
  'settings.appearance': 'Appearance',
  'settings.notifications': 'Notifications',
  'settings.language': 'Language',
  'settings.theme': 'Theme',
  'settings.theme.light': 'Light',
  'settings.theme.dark': 'Dark',
  'settings.theme.system': 'System',
  'settings.accessibility': 'Accessibility',
  'settings.font_size': 'Font size',
  'settings.high_contrast': 'High contrast',
  'settings.reduced_motion': 'Reduced motion',
  'settings.dyslexia_mode': 'Dyslexia mode',
  'analytics.title': 'Analytics',
  'analytics.overview': 'Overview',
  'analytics.learner_progress': 'Learner progress',
  'analytics.engagement': 'Engagement',
  'analytics.completion': 'Completion',
  'analytics.time_spent': 'Time spent',
  'analytics.average_score': 'Average score',
  'analytics.export_report': 'Export report',
  'gamification.xp': 'XP',
  'gamification.streak': 'Streak',
  'gamification.achievements': 'Achievements',
  'gamification.daily_goal': 'Daily goal',
  'gamification.congratulations': 'Congratulations!',
  'gamification.new_badge': 'New badge unlocked!',
  'gamification.level_up': 'Level up!',
  'qualiopi.title': 'Qualiopi compliance',
  'qualiopi.dashboard': 'Qualiopi dashboard',
  'qualiopi.criteria': 'Criterion {{number}}',
  'qualiopi.indicator': 'Indicator {{number}}',
  'qualiopi.compliant': 'Compliant',
  'qualiopi.non_compliant': 'Non-compliant',
  'qualiopi.partial': 'Partially compliant',
  'qualiopi.not_applicable': 'Not applicable',
  'qualiopi.upload_evidence': 'Upload evidence',
  'qualiopi.audit_date': 'Audit date',
  'qualiopi.next_audit': 'Next audit',
  'qualiopi.compliance_score': 'Compliance score',
  'qualiopi.action_plan': 'Action plan',
  'training.plan': 'Training plan',
  'training.request': 'Training request',
  'training.requests': 'Training requests',
  'training.catalog': 'Training catalog',
  'training.budget': 'Training budget',
  'training.campaign': 'Interview campaign',
  'training.competency': 'Skills framework',
  'training.gap_analysis': 'Gap analysis',
  'training.roi': 'Return on investment',
  ...eduflowBlockEditorsEn,
  ...institutionMcpEn,
};

const deTranslations: Record<string, string> = {
  'nav.home': 'Startseite',
  'nav.producerAssignments': 'Produktionsaufgaben',
  'nav.videos': 'Videos',
  'nav.sounds': 'Audio',
  'nav.images': 'Bilder',
  'nav.documents': 'Dokumente',
  'nav.podcasts': 'Podcasts',
  'nav.parcours': 'Lernpfade',
  'nav.eduflowList': 'Schulungen',
  'nav.myLearning': 'Mein Lernen',
  'nav.section.pilotage': 'Steuerung',
  'nav.section.creation': 'Erstellung',
  'nav.section.medias': 'Medien',
  'nav.section.aide': 'Hilfe',
  'sidebar.brandTagline': 'Schulung',
  'sidebar.logoInstitutionAlt': 'Logo der Organisation',
  'sidebar.expand': 'Menü ausklappen',
  'sidebar.collapse': 'Menü einklappen',
  'sidebar.expandAria': 'Seitenleiste erweitern',
  'sidebar.collapseAria': 'Seitenleiste einklappen',
  'learner.sidebar.collapse': 'Einklappen',
  'learner.sidebar.expandAria': 'Seitenleiste erweitern',
  'learner.sidebar.collapseAria': 'Seitenleiste einklappen',
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Willkommen, {{name}}!',
  'dashboard.welcomeSub': 'Hier ist ein Überblick über Ihren Arbeitsbereich.',
  'dashboard.projects': 'Projekte',
  'dashboard.subtitle': 'Verwalten Sie Ihre Schulungen und verfolgen Sie die Lernenden.',
  'dashboard.newProject': 'Neue Schulung',
  'dashboard.newEduFlow': 'Neuen Lernpfad',
  'dashboard.uploadMedia': 'Medien hochladen',
  'dashboard.viewDocs': 'Dokumentation',
  'dashboard.revenue': 'Umsatz',
  'dashboard.eduflows': 'Schulungen',
  'dashboard.learners': 'Lernende',
  'dashboard.recentActivity': 'Aktuelle Aktivitäten',
  'dashboard.recentProjects': 'Aktuelle Projekte',
  'dashboard.noRecentProjects': 'Keine aktuellen Projekte',
  'dashboard.activity': 'Aktivität',
  'dashboard.activityEmpty': 'Keine aktuellen Aktivitäten zu Schulungen oder Podcasts.',
  'dashboard.loadError': 'Dashboard-Daten konnten nicht geladen werden.',
  'dashboard.publishedFlows': 'Veröffentlichte Lernpfade',
  'dashboard.roleSpecific': 'Für diese Rolle ist ein spezielles Dashboard verfügbar.',
  'dashboard.kindPodcast': 'Podcast',
  'nav.analytics': 'Analysen',
  'pages.analytics.title': 'Analysen',
  'pages.analytics.description': 'Dashboards und Statistiken',
  'pages.analytics.subtitle':
    'Daten filtern, Diagramme per Drag & Drop sortieren und die Größe der Widgets anpassen.',
  'pages.analytics.filters.period': 'Zeitraum',
  'pages.analytics.filters.flow': 'Kurs',
  'pages.analytics.filters.allFlows': 'Alle Kurse',
  'pages.analytics.period.7d': 'Letzte 7 Tage',
  'pages.analytics.period.30d': 'Letzte 30 Tage',
  'pages.analytics.period.90d': 'Letzte 90 Tage',
  'pages.analytics.period.365d': 'Letzte 12 Monate',
  'pages.analytics.addChart': 'Diagramm hinzufügen',
  'pages.analytics.resetLayout': 'Layout zurücksetzen',
  'pages.analytics.dragHint': 'Ziehen zum Sortieren',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Dieses Diagramm entfernen',
  'pages.analytics.kind.completion_line': 'Abschluss im Zeitverlauf',
  'pages.analytics.kind.learners_bar': 'Lernende pro Modul',
  'pages.analytics.kind.status_pie': 'Statusverteilung',
  'pages.analytics.kind.engagement_area': 'Engagement-Zeit',
  'pages.analytics.series.completion': 'Abschlussquote (%)',
  'pages.analytics.series.learners': 'Lernende',
  'pages.analytics.series.hours': 'Stunden',
  'pages.analytics.status.completed': 'Abgeschlossen',
  'pages.analytics.status.in_progress': 'In Bearbeitung',
  'pages.analytics.status.dropped': 'Abgebrochen',
  'pages.analytics.status.not_started': 'Nicht begonnen',
  'pages.analytics.loadError': 'Analysedaten konnten nicht geladen werden.',
  'pages.analytics.retry': 'Erneut versuchen',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Mo',
  'pages.analytics.mock.wd1': 'Di',
  'pages.analytics.mock.wd2': 'Mi',
  'pages.analytics.mock.wd3': 'Do',
  'pages.analytics.mock.wd4': 'Fr',
  'pages.analytics.mock.wd5': 'Sa',
  'pages.analytics.mock.wd6': 'So',
  'pages.analytics.emptyCharts': 'Fügen Sie mindestens ein Diagramm hinzu, um Daten zu sehen.',
  'pages.analytics.comingSoonDesc':
    'Statistiken zu Kursen, Abschlussquoten, Engagement der Lernenden und Gesamtleistung einsehen.',
  'project.status.draft': 'Entwurf',
  'project.status.published': 'Veröffentlicht',
  'project.status.archived': 'Archiviert',
  'eduflowList.status.draft': 'Entwurf',
  'eduflowList.status.published': 'Veröffentlicht',
  'eduflowList.status.archived': 'Archiviert',
  'eduflowList.badge.enterpriseShell': 'Unternehmensanfrage',
  'eduflowList.badge.enterpriseLinked': 'Unternehmen',
  'eduflowList.badge.toProduce': 'Zu produzieren',
  'learner.sidebar.tagline': 'Lernende',
  'player.next': 'Weiter',
  'player.image.clickForAnnotations': 'Klicken Sie auf das Bild, um die Anmerkungen anzuzeigen',
  'player.previous': 'Zurück',
  'player.complete': 'Abschließen',
  'player.exit': 'Beenden',
  'player.exitConfirmTitle': 'Training beenden?',
  'player.exitConfirmMessage': 'Ihr Fortschritt wird gespeichert. Möchten Sie wirklich beenden?',
  'player.focusLostTitle': 'Schulung pausiert',
  'player.focusLostMessage': 'Sie haben das Schulungsfenster verlassen. Die Zeit ist pausiert.',
  'player.focusLostResume': 'Zum Fortfahren klicken',
  'player.focusLostRemaining': 'Verbleibende Ausstiege vor der Sperrung: {{remaining}}',
  'player.focusBlockedTitle': 'Schulung gesperrt',
  'player.focusBlockedMessage':
    'Sie haben die zulässige Anzahl an Ausstiegen ({{max}}) überschritten. Sie können diese Schulung nicht mehr fortsetzen. Wenden Sie sich an Ihre Trainerin oder Ihren Trainer.',
  'player.cancel': 'Abbrechen',
  'player.summary': 'Zusammenfassung',
  'player.contents': 'Inhalt',
  'player.progress': 'Fortschritt',
  'player.block': 'Block',
  'player.parallelBranches': 'Parallele Zweige',
  'player.parallelBranchesTitle': 'Wie geht es weiter?',
  'player.parallelBranchesHint': 'Die anderen Optionen folgen danach.',
  'player.parallelBranchShowFull': 'Mehr anzeigen',
  'player.parallelBranchShowLess': 'Weniger anzeigen',
  'player.parallelBranchChoose': 'Öffnen',
  'player.parallelBranchDismiss': 'Schließen',
  'player.parallelBranchDismissHint':
    'Zurück zum Block. Mit „Weiter“ eine Verzweigung wählen.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Notizen machen...',
  'player.noBlocks': 'Keine Blöcke anzuzeigen.',
  'player.contentTranslatingTitle': 'Inhalt wird an Ihre Sprache angepasst…',
  'player.contentTranslatingHint':
    'Übersetzung läuft. Beim ersten Laden kann es etwas länger dauern.',
  'player.textBlockTipsHeading': 'Tipps',
  'player.textBlockReadDone': 'Lesen abgeschlossen',
  'player.defaultFlowTitle': 'Schulung',
  'player.docTitle.error': 'Schulung nicht verfügbar',
  'player.docTitle.completed': 'Schulung abgeschlossen',
  'player.docTitle.loading': 'Schulung wird geladen',
  'player.docTitle.fallback': 'Schulung',
  'player.initError.title': 'Die Schulung ist vorübergehend nicht verfügbar',
  'player.initError.loadFailed': 'Die Schulung konnte gerade nicht geladen werden.',
  'player.initError.loadFailedWithDetail':
    'Die Schulung konnte gerade nicht geladen werden.{{detail}} Bitte versuchen Sie es in Kürze erneut.',
  'player.initError.hintsIntro': 'Sie können:',
  'player.initError.hintRefresh': 'die Seite aktualisieren;',
  'player.initError.hintConnection': 'Ihre Internetverbindung prüfen;',
  'player.initError.hintRetryLater': 'es in einigen Minuten erneut versuchen.',
  'api.error.network': 'Server nicht erreichbar. Prüfen Sie Ihre Verbindung.',
  'api.error.unexpected': 'Ein unerwarteter Fehler ist aufgetreten.',
  'api.error.badRequest': 'Ungültige Anfrage.',
  'api.error.forbidden': 'Zugriff verweigert.',
  'api.error.notFound': 'Ressource nicht gefunden.',
  'api.error.conflict': 'Konflikt — diese Ressource existiert bereits.',
  'api.error.unprocessable': 'Die gesendeten Daten sind ungültig.',
  'api.error.tooManyRequests': 'Zu viele Anfragen. Bitte versuchen Sie es gleich erneut.',
  'api.error.server': 'Interner Serverfehler.',
  'api.error.badGateway': 'Dienst vorübergehend nicht verfügbar.',
  'api.error.maintenance': 'Wartungsarbeiten.',
  'api.error.generic': 'Fehler {{status}}',
  'player.textBlockReadMinutes': '{{count}} Min. Lesezeit',
  'player.textBlockWordCount_one': '{{count}} Wort',
  'player.textBlockWordCount_other': '{{count}} Wörter',
  'player.textBlockNoContent': 'Kein Inhalt verfügbar.',
  'player.blockUnknown': 'Unbekannter Block',
  'player.objectivesPanelTitle': 'Lernziele',
  'player.objectivesOpenLabel': 'Lernziele anzeigen',
  'player.objectivesCloseLabel': 'Lernziele schließen',
  'player.objectivesAchievementRate': 'Erreichungsgrad',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} Lernziele erreicht',
  'player.objectivesNoneDefined': 'Für diese Schulung sind keine Lernziele definiert.',
  'player.objectiveCategory.savoir': 'Wissen',
  'player.objectiveCategory.savoirFaire': 'Können',
  'player.objectiveCategory.savoirEtre': 'Haltung',
  'player.objectiveLevel.connaissance': 'Erinnern',
  'player.objectiveLevel.comprehension': 'Verstehen',
  'player.objectiveLevel.application': 'Anwenden',
  'player.objectiveLevel.analyse': 'Analysieren',
  'player.objectiveLevel.synthese': 'Bewerten',
  'player.objectiveLevel.evaluation': 'Erschaffen',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Pflicht',
  'player.objectiveLinkedBlocks': 'Verknüpfte Blöcke',
  'player.objectiveCriterionScore': 'Punktzahl ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Block abgeschlossen',
  'player.objectiveNoLinkedBlocks':
    'Kein verknüpfter Block — der Gesamtfortschritt bewertet dieses Lernziel nicht.',
  'player.completion.congratulations': '🎉 Glückwunsch!',
  'player.completion.finished': '📋 Kurs abgeschlossen',
  'player.completion.youCompleted': 'Sie haben abgeschlossen',
  'player.completion.passed': 'Bestanden ✓',
  'player.completion.failed': 'Nicht bestanden ✗',
  'player.completion.requiredScore': 'Erforderliche Punktzahl: {{score}}%',
  'player.completion.totalTime': 'Gesamtzeit',
  'player.completion.blocksCompleted': 'Abgeschlossene Blöcke',
  'player.completion.quizPassed': 'Bestandene Quiz',
  'player.completion.successRate': 'Erfolgsquote',
  'player.completion.blockCompleted': 'Abgeschlossen ✓',
  'player.completion.blockFailed': 'Nicht bestanden ✗',
  'player.completion.blockSkipped': 'Übersprungen',
  'player.completion.detail': '📊 Details pro Block',
  'player.completion.type': 'Typ',
  'player.completion.timeSpent': 'Verbrachte Zeit',
  'player.completion.downloadCertificate': '📄 Zertifikat herunterladen',
  'player.completion.certificateSoon': 'Das Zertifikat wird in Kürze verfügbar sein.',
  'player.completion.restart': '🔄 Neustart',
  'player.completion.viewDetails': '📊 Details anzeigen',
  'player.completion.backToDashboard': '← Zurück zum Dashboard',
  'player.completion.embedDismissHint':
    'Sie haben dieses Modul abgeschlossen. Schließen Sie das Fenster mit dem X oben oder der Schaltfläche unten.',
  'player.completion.embedDismissButton': 'Fenster schließen',
  'player.previewBanner': 'Vorschaumodus — diese Ansicht spiegelt die echten Daten Ihrer Schulung wider',
  'player.closePreview': 'Vorschau schließen',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Seitenleiste anzeigen',
  'player.toggleSidebarHide': 'Seitenleiste ausblenden',
  'player.sidebarExpand': 'Seitenleiste erweitern',
  'player.sidebarCollapse': 'Seitenleiste einklappen',
  'player.keyboardShortcuts': 'Tastenkürzel',
  'player.keyboardShortcutPrev': 'Vorheriger Block',
  'player.keyboardShortcutNext': 'Nächster Block',
  'player.keyboardShortcutsHint':
    'Aktivieren Sie „Tastaturnavigation“ im Barrierefreiheitsbereich (Schaltfläche unten rechts). In Eingabefeldern deaktiviert.',
  'player.a11yCenter': 'Barrierefreiheitszentrum',
  'player.reviewSummary': 'Zusammenfassung ansehen',
  'player.viewResults': 'Ergebnisse ansehen',
  'player.submitFirst': 'Bitte zuerst absenden',
  'player.phraseRevealCompleteFirst': 'Sehen Sie zuerst alle Sätze',
  'player.phraseReveal.emptyBlock': 'Für diesen Block sind keine Sätze konfiguriert.',
  'player.phraseReveal.revealNextAria':
    'Klicken oder Leertaste drücken, um den nächsten Satz anzuzeigen',
  'player.phraseReveal.lastPhraseHint':
    'Letzter Satz — verwenden Sie „Weiter“, um den Pfad fortzusetzen.',
  'player.phraseReveal.morePhrasesHint': 'Klick, Leertaste oder Pfeil rechts für den nächsten Satz',
  'player.phraseReveal.previousPhrases': 'Vorherige Sätze ({{count}})',
  'player.certificateUnavailable': 'Das Zertifikat wird in Kürze verfügbar sein.',
  'player.browser.embeddedContentTitle': 'Eingebetteter Inhalt',
  'player.browser.noValidUrl': 'Keine gültige URL',
  'player.browser.enterHttpsHint':
    'Geben Sie im Block eine Adresse ein, die mit https:// (oder http://) beginnt.',
  'player.browser.loadingContent': 'Inhalt wird geladen…',
  'player.browser.defaultPageTitle': 'Eingebettete Webseite',
  'player.browser.continue': 'Weiter',
  'player.browser.stepSaved': 'Schritt gespeichert',
  'player.browser.fullscreen': 'Vollbild',
  'player.browser.exitFullscreen': 'Vollbild beenden',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Folge',
  'player.podcast.loadError': 'Podcast konnte nicht geladen werden.',
  'player.podcast.loadingEpisodes': 'Folgen werden geladen…',
  'player.podcast.playAll': 'Alle abspielen',
  'player.podcast.nextEpisode': 'Nächste Folge',
  'player.podcast.episodesHeading': 'Folgen',
  'player.podcast.noEpisodes': 'Keine Folgen.',
  'player.podcast.noAudio': 'Kein Audio',
  'player.podcast.chainPlayback': 'Wiedergabeliste · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Wählen Sie eine Folge aus der Liste oder starten Sie „Alle abspielen“.',
  'player.podcast.audioUnavailable': 'Für diese Folge ist kein Audio verfügbar.',
  'player.podcast.episodeCount_one': '{{count}} Folge',
  'player.podcast.episodeCount_other': '{{count}} Folgen',
  'player.renderError': 'Fehler beim Rendern des Blocks: {{blockType}}',
  'player.blocks.defaultWelcome': 'Willkommen zu dieser Schulung',
  'player.blocks.defaultEndTitle': 'Glückwunsch!',
  'player.quiz.selectCorrectAnswer': 'Wählen Sie die richtige Antwort:',
  'player.quiz.correctAnswer': '✓ Richtige Antwort!',
  'player.quiz.incorrectAnswer': '✗ Falsche Antwort.',
  'player.blockTimer.aria': 'Zeit für diesen Block',
  'player.blockTimer.modeShortCountup': 'Hochzählen',
  'player.blockTimer.modeShortCountdown': 'Countdown',
  'player.form.successMessage': 'Vielen Dank für Ihre Antwort!',
  'player.form.noFieldsConfigured': 'Dieses Formular hat noch keine konfigurierten Felder.',
  'player.evaluation.commentsLabel': 'Kommentare (optional)',
  'player.evaluation.commentsPlaceholder': 'Teilen Sie Ihr Feedback...',
  'player.evaluation.submitted': 'Bewertung gesendet',
  'player.evaluation.submit': 'Bewertung absenden',
  'player.time.lessThanMinute': 'Weniger als eine Minute',
  'player.time.oneMinute': '1 Minute',
  'player.time.minutes': '{{count}} Minuten',
  'player.time.shortSeconds': '{{count}} Sek.',
  'player.time.shortMinutes': '{{count}} Min.',
  'player.time.shortMinutesSeconds': '{{minutes}} Min. {{seconds}} Sek.',
  'player.completion.scoreLabel': 'Punktzahl',
  'accessibility.openCenter': 'Barrierefreiheitszentrum öffnen',
  'accessibility.center': 'Barrierefreiheit',
  'accessibility.modalTitle': 'Zentrum für angepasstes Lesen',
  'accessibility.modalDescription': 'Passen Sie die Oberfläche für Sehschwäche, Legasthenie und komfortableres Lesen an.',
  'accessibility.closeCenter': 'Barrierefreiheitszentrum schließen',
  'accessibility.accessPad.connected': 'Lyrra Access Pad verbunden',
  'accessibility.accessPad.playGuide': 'LyrraPad-Anleitung abspielen',
  'accessibility.accessPad.pauseGuide': 'LyrraPad-Anleitung pausieren',
  'accessibility.presets': 'Voreingestellte Profile',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': 'Standarddarstellung der Anwendung.',
  'accessibility.preset.lowVision.label': 'Sehschwäche',
  'accessibility.preset.lowVision.desc': 'Stärkerer Kontrast, größerer Text und klarere visuelle Hinweise.',
  'accessibility.preset.dys.label': 'Legasthenie',
  'accessibility.preset.dys.desc': 'Leichteres Lesen mit mehr Abstand und besser lesbarer Schrift.',
  'accessibility.typography': 'Typografie',
  'accessibility.fontScale': 'Textgröße',
  'accessibility.lineHeight': 'Zeilenabstand',
  'accessibility.letterSpacing': 'Buchstabenabstand',
  'accessibility.wordSpacing': 'Wortabstand',
  'accessibility.visualAids': 'Visuelle Hilfen',
  'accessibility.highContrast': 'Hoher Kontrast',
  'accessibility.highContrastDesc': 'Verstärkt Lesekontrast, Ränder und visuelle Hinweise.',
  'accessibility.underlineLinks': 'Unterstrichene Links',
  'accessibility.underlineLinksDesc': 'Macht Links und interaktive Bereiche leichter erkennbar.',
  'accessibility.dyslexiaFont': 'Lesefreundliche Schrift',
  'accessibility.dyslexiaFontDesc': 'Nutzt eine angenehmere Schrift für Menschen mit Legasthenie und ermüdete Leser.',
  'accessibility.reducedMotion': 'Reduzierte Bewegung',
  'accessibility.reducedMotionDesc': 'Verringert Animationen und Übergänge, um visuelle Beschwerden zu mindern.',
  'accessibility.mouseMagnifier': 'Leselupe',
  'accessibility.mouseMagnifierDesc':
    'Zeigt eine runde Lupe, die dem Mauszeiger folgt und den Kursinhalt darunter vergrößert (nur lesen).',
  'accessibility.alwaysOn': 'Dauerhaft aktive Verbesserungen',
  'accessibility.alwaysOnFocus': 'Sichtbarerer Tastaturfokus',
  'accessibility.alwaysOnLinks': 'Links und interaktive Elemente leichter erkennbar',
  'accessibility.alwaysOnSaved': 'Einstellungen werden auf diesem Gerät automatisch gespeichert',
  'accessibility.navigation': 'Navigation',
  'accessibility.keyboardNavigation': 'Tastaturnavigation',
  'accessibility.keyboardNavigationDesc':
    'Pfeil links/oben für den vorherigen Block, Pfeil rechts/unten für den nächsten Block (wie die unteren Schaltflächen).',
  // ── Injected from locales/de.json ──
  'producerAssignments.createInDesigner': 'Schulung erstellen / öffnen',
  'producerAssignments.draft': 'Entwurf',
  'producerAssignments.enterprise': 'Unternehmen',
  'producerAssignments.enterprisePaths': 'Unternehmenspfade',
  'producerAssignments.episodes': 'Ep.',
  'producerAssignments.errorForbidden': 'Sie haben keinen Zugriff auf diese Seite.',
  'producerAssignments.errorGeneric': 'Ihre Aufträge konnten nicht geladen werden.',
  'producerAssignments.errorNoInstitution': 'Sie sind keiner Einrichtung zugeordnet.',
  'producerAssignments.formations': 'Schulungskarten',
  'producerAssignments.intro': 'Diese Seite enthält die Schulungskarten und Podcasts, die Ihnen von der Personalabteilung Ihrer Einrichtung zugewiesen wurden. Erstellen Sie Ihre Inhalte im EduFlow-Designer oder im Podcast-Studio und aktualisieren Sie den Produktionsstatus.',
  'producerAssignments.markCompleted': 'Als abgeschlossen markieren',
  'producerAssignments.markInProgress': 'In Produktion',
  'producerAssignments.markReady': 'Bereit',
  'producerAssignments.modulesCatalog': '{{count}} Katalogmodul(e) · aktualisiert {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Keine Unternehmenspfad-Anfragen.',
  'producerAssignments.noneFormations': 'Keine Karten zugewiesen.',
  'producerAssignments.nonePodcasts': 'Keine Podcasts zugewiesen.',
  'producerAssignments.openInDesigner': 'Im Designer öffnen',
  'producerAssignments.openPodcasts': 'Podcast-Studio',
  'producerAssignments.planningDeadline': 'Frist {{date}}',
  'producerAssignments.planningFrom': 'Ab {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Zu produzierende Podcasts',
  'producerAssignments.productionStatus.completed': 'Abgeschlossen',
  'producerAssignments.productionStatus.in_progress': 'In Bearbeitung',
  'producerAssignments.productionStatus.not_started': 'Nicht begonnen',
  'producerAssignments.productionStatus.published': 'Veröffentlicht',
  'producerAssignments.publishedCatalog': 'Im Katalog veröffentlicht',
  'producerAssignments.rhPlanning': 'HR-Planung',
  'producerAssignments.subtitle': 'Schulungen, die im Designer erstellt, und Podcasts, die produziert werden sollen – zugewiesen von der Personalabteilung.',
  'producerAssignments.title': 'Meine Produktionsaufträge',
  'podcasts.actions.cancel': 'Abbrechen',
  'podcasts.actions.close': 'Schließen',
  'podcasts.actions.create': 'Erstellen',
  'podcasts.actions.edit': 'Bearbeiten',
  'podcasts.actions.retry': 'Erneut versuchen',
  'podcasts.actions.save': 'Speichern',
  'podcasts.actions.saving': 'Wird gespeichert…',
  'podcasts.actions.share': 'Teilen',
  'podcasts.category.ariaGroup': 'Podcast-Kategorie',
  'podcasts.category.emptyBanner': 'Fügen Sie im Einrichtungsbereich Kategorien hinzu, um Podcasts besser zu organisieren.',
  'podcasts.category.emptyInstitutionHint': 'Noch keine Kategorie der Einrichtung — Sie können ohne veröffentlichen.',
  'podcasts.category.noneChip': 'Ohne Kategorie',
  'podcasts.category.sectionTitle': 'Kategorie',
  'podcasts.category.withCategoriesHint': 'Einordnung im Katalog und Filter auf Einrichtungsebene.',
  'podcasts.cover.ariaDropzone': 'Titelbild auswählen oder ablegen',
  'podcasts.cover.browseFiles': 'Dateien durchsuchen',
  'podcasts.cover.cancelNewFile': 'Neue Datei abbrechen',
  'podcasts.cover.dragDrop': 'Bild per Drag & Drop',
  'podcasts.cover.dropHere': 'Bild hier ablegen',
  'podcasts.cover.errorMaxSize': 'Das Bild darf 5 MB nicht überschreiten.',
  'podcasts.cover.errorUnsupportedFormat': 'Bildformat wird nicht unterstützt.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max. 5 MB',
  'podcasts.cover.help': 'Quadratisches Format empfohlen. Datei per Drag & Drop ablegen oder klicken zum Durchsuchen.',
  'podcasts.cover.or': 'oder',
  'podcasts.cover.replaceHint': 'Bild ersetzen',
  'podcasts.cover.sectionTitle': 'Titelbild',
  'podcasts.deleteDialog.body': '„{{title}}“ wird aus der Liste entfernt (logische Löschung). Episoden werden nicht gelöscht. Wenn Ihr Konto einer Einrichtung zugeordnet ist, kann ein Administrator den Podcast aus dem Papierkorb des Einrichtungsbereichs wiederherstellen.',
  'podcasts.deleteDialog.confirm': 'In den Papierkorb',
  'podcasts.deleteDialog.title': 'Podcast in den Papierkorb verschieben?',
  'podcasts.editPanel.createdOn': 'Erstellt am ',
  'podcasts.editPanel.headerTitle': 'Podcast bearbeiten',
  'podcasts.editPanel.saving': 'Wird gespeichert…',
  'podcasts.editPanel.statsEpisodes': 'Episoden: ',
  'podcasts.editPanel.statsSubscribers': 'Abonnenten: ',
  'podcasts.editPanel.successSaved': 'Änderungen gespeichert',
  'podcasts.episodeStatus.draft': 'Entwurf',
  'podcasts.episodeStatus.published': 'Veröffentlicht',
  'podcasts.episodesPanel.addEpisode': 'Episode hinzufügen',
  'podcasts.episodesPanel.emptySubtitle': 'Fügen Sie diesem Podcast Episoden hinzu',
  'podcasts.episodesPanel.emptyTitle': 'Keine Episoden',
  'podcasts.episodesPanel.episodeCount': '{{count}} Episode(n)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Keine Episode verfügbar',
  'podcasts.episodesPanel.remove': 'Entfernen',
  'podcasts.episodesPanel.searchPlaceholder': 'Episode suchen…',
  'podcasts.episodesPanel.title': 'Podcast-Episoden',
  'podcasts.error.retry': 'Erneut versuchen',
  'podcasts.filters.allCategories': 'Alle Kategorien',
  'podcasts.filters.categoryLabel': 'Kategorie',
  'podcasts.filters.searchLabel': 'Textsuche',
  'podcasts.filters.searchPlaceholder': 'Titel, Beschreibung…',
  'podcasts.filters.viewCategories': 'Kategorieansicht',
  'podcasts.filters.viewList': 'Listenansicht',
  'podcasts.form.descriptionLabel': 'Beschreibung',
  'podcasts.form.descriptionPlaceholder': 'Beschreiben Sie Ihren Podcast…',
  'podcasts.form.namePlaceholder': 'Name des Podcasts',
  'podcasts.form.titleLabel': 'Titel',
  'podcasts.language.ariaGroup': 'Podcast-Sprache',
  'podcasts.language.help': 'Aus den 10 Plattformsprachen — damit der Podcast dieselbe Locale wie der Player und die Lernenden-Oberfläche verwendet.',
  'podcasts.language.label': 'Sprache',
  'podcasts.lightbox.ariaClose': 'Schließen',
  'podcasts.lightbox.ariaDialog': 'Podcast-Cover-Vorschau',
  'podcasts.list.actionActivate': 'Aktivieren',
  'podcasts.list.actionDeactivate': 'Deaktivieren',
  'podcasts.list.actionDelete': 'Löschen',
  'podcasts.list.actionManageEpisodes': 'Episoden verwalten',
  'podcasts.list.actionPreview': 'Vorschau & Anhören',
  'podcasts.list.coverPreviewAria': 'Cover-Vorschau Podcast {{title}}',
  'podcasts.list.createFirst': 'Podcast erstellen',
  'podcasts.list.emptySubtitle': 'Erstellen Sie Ihren ersten Podcast, um zu beginnen',
  'podcasts.list.emptyTitle': 'Kein Podcast',
  'podcasts.list.loadError': 'Podcasts konnten nicht geladen werden.',
  'podcasts.list.enterpriseBadge': 'Unternehmen',
  'podcasts.list.enterpriseNeedTitle': 'Von Ihrem Unternehmen definierter Bedarf',
  'podcasts.list.episodesShort': '{{count}} Folg.',
  'podcasts.list.groupPodcastCount': '{{count}} Podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} Abo.',
  'podcasts.list.uncategorized': 'Ohne Kategorie',
  'podcasts.modal.errorRequiredFields': 'Titel und Beschreibung sind erforderlich.',
  'podcasts.modal.errorSaveFailed': 'Fehler beim Speichern.',
  'podcasts.modal.subtitleNew': 'Tragen Sie Titel, Beschreibung und Optionen ein. Episoden können Sie anschließend hinzufügen.',
  'podcasts.modal.titleEdit': 'Podcast bearbeiten',
  'podcasts.modal.titleNew': 'Neuer Podcast',
  'podcasts.page.episodesLink': 'Episoden',
  'podcasts.page.newButton': 'Neu',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} Podcast(s)',
  'podcasts.pagination.pageOf': 'Seite {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} Podcast(s) insgesamt',
  'podcasts.player.listen': 'Anhören',
  'podcasts.player.noAudio': 'Kein Audio',
  'podcasts.player.pause': 'Pause',
  'podcasts.preview.emptySubtitle': 'Dieser Podcast hat noch keine Episoden',
  'podcasts.preview.emptyTitle': 'Keine Episoden',
  'podcasts.preview.episodeCount': '{{count}} Episode(n)',
  'podcasts.preview.fallbackDescription': 'Hören Sie die verfügbaren Audioepisoden.',
  'podcasts.preview.playAll': 'Alles anhören',
  'podcasts.preview.title': 'Podcast-Vorschau',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'Updates abonnieren',
  'podcasts.public.subscribeModalTitle': 'Neue Folgen per E-Mail',
  'podcasts.public.subscribeModalIntro': 'Geben Sie Ihre Daten ein. Wir benachrichtigen Sie, wenn eine neue Folge veröffentlicht wird.',
  'podcasts.public.fieldFirstName': 'Vorname',
  'podcasts.public.fieldLastName': 'Nachname',
  'podcasts.public.fieldEmail': 'E-Mail',
  'podcasts.public.fieldCompany': 'Unternehmen (optional)',
  'podcasts.public.submitSubscribe': 'Abonnement bestätigen',
  'podcasts.public.cancel': 'Abbrechen',
  'podcasts.public.done': 'Fertig',
  'podcasts.public.subscribeSuccess': 'Danke! Sie werden über jede neue Folge informiert.',
  'podcasts.public.subscribeError': 'Abonnement fehlgeschlagen. Bitte später erneut versuchen.',
  'podcasts.public.subscribeValidationRequired': 'Vorname, Nachname und E-Mail sind Pflichtfelder.',
  'podcasts.share.assignedLearnersHeading': '{{count}} Lernende zugewiesen',
  'podcasts.share.copied': 'Kopiert',
  'podcasts.share.copyLink': 'Link kopieren',
  'podcasts.share.emailPlaceholder': 'E-Mail *',
  'podcasts.share.errorCreateLearner': 'Lernender konnte nicht erstellt werden.',
  'podcasts.share.errorPublicLink': 'Öffentlicher Link konnte nicht erstellt werden.',
  'podcasts.share.errorRemoveLearner': 'Lernender konnte nicht entfernt werden.',
  'podcasts.share.errorShare': 'Dieser Podcast konnte nicht geteilt werden.',
  'podcasts.share.firstNamePlaceholder': 'Vorname *',
  'podcasts.share.generatingLink': 'Link wird erstellt…',
  'podcasts.share.hideQuickCreate': 'Schnellerstellung ausblenden',
  'podcasts.share.lastNamePlaceholder': 'Nachname *',
  'podcasts.share.loadingAssigned': 'Wird geladen…',
  'podcasts.share.loadingLearners': 'Lernende werden geladen...',
  'podcasts.share.messageAccessRemoved': 'Zugriff für den Lernenden entfernt.',
  'podcasts.share.messageCreatedAndShared': 'Konto erstellt/verknüpft, Podcast geteilt und Einladungs-E-Mail gesendet.',
  'podcasts.share.messageShared': 'Podcast geteilt und Einladung an den Lernenden gesendet.',
  'podcasts.share.noAssignedLearners': 'Kein Lernender für diesen Podcast zugewiesen.',
  'podcasts.share.noLearnerAvailable': 'Kein Lernender verfügbar.',
  'podcasts.share.panelTitle': 'Podcast teilen',
  'podcasts.share.publicLinkSection': 'Öffentlicher Link (ohne Anmeldung)',
  'podcasts.share.removeAccessTitle': 'Zugriff entfernen',
  'podcasts.share.searchLearnerPlaceholder': 'Lernenden suchen (Vorname, Nachname, E-Mail)',
  'podcasts.share.selectExistingLearnerLabel': 'Bestehenden Lernenden auswählen',
  'podcasts.share.showQuickCreate': 'Neuen Lernenden erstellen',
  'podcasts.share.submitCreateAndInvite': 'Erstellen, teilen und Einladung senden',
  'podcasts.share.socialNetworksHeading': 'In sozialen Netzwerken teilen',
  'podcasts.share.shareLinkedIn': 'Auf LinkedIn teilen',
  'podcasts.share.shareFacebook': 'Auf Facebook teilen',
  'podcasts.share.shareTwitter': 'Auf X (Twitter) teilen',
  'podcasts.status.active': 'Aktiv',
  'podcasts.status.inactive': 'Inaktiv',
  'learningPaths.card.contents': '{{count}} Inhalt(e)',
  'learningPaths.card.delete': 'Löschen',
  'learningPaths.card.edit': 'Bearbeiten',
  'learningPaths.card.learners': '{{count}} Teilnehmer',
  'learningPaths.card.publish': 'Veröffentlichen',
  'learningPaths.card.unpublish': 'Veröffentlichung aufheben',
  'learningPaths.card.updatedAt': 'Aktualisiert am {{date}}',
  'learningPaths.createPanel.cancel': 'Abbrechen',
  'learningPaths.createPanel.description': 'Geben Sie Ihrem Pfad einen Namen; Sie können Podcasts und Schulungen hinzufügen.',
  'learningPaths.createPanel.descriptionLabel': 'Beschreibung',
  'learningPaths.createPanel.descriptionPlaceholder': 'Ziel des Pfads, Zielgruppe…',
  'learningPaths.createPanel.nameLabel': 'Pfadname',
  'learningPaths.createPanel.namePlaceholder': 'z. B. Onboarding-Pfad Personal…',
  'learningPaths.createPanel.submit': 'Pfad erstellen',
  'learningPaths.createPanel.title': 'Neuer Pfad',
  'learningPaths.deleteDialog.body': 'Sie löschen „{{title}}“. Diese Aktion kann nicht rückgängig gemacht werden.',
  'learningPaths.deleteDialog.cancel': 'Abbrechen',
  'learningPaths.deleteDialog.confirm': 'Löschen',
  'learningPaths.deleteDialog.title': 'Pfad löschen',
  'learningPaths.empty.createFirst': 'Pfad anlegen',
  'learningPaths.empty.noPathsHint': 'Erstellen Sie Ihren ersten Pfad aus Podcasts und Schulungen.',
  'learningPaths.empty.noPathsYet': 'Noch keine Pfade',
  'learningPaths.empty.noResults': 'Keine Ergebnisse',
  'learningPaths.empty.noResultsHint': 'Passen Sie die Filter an oder legen Sie einen neuen Pfad an.',
  'learningPaths.error.loadFailed': 'Pfade konnten nicht geladen werden',
  'learningPaths.error.retry': 'Erneut versuchen',
  'learningPaths.filters.allStatuses': 'Alle Status',
  'learningPaths.filters.draft': 'Entwurf',
  'learningPaths.filters.published': 'Veröffentlicht',
  'learningPaths.filters.searchPlaceholder': 'Pfad suchen…',
  'learningPaths.page.newButton': 'Neuer Pfad',
  'learningPaths.page.subtitle': 'Erstellen Sie geordnete Schulungspfade',
  'learningPaths.page.title': 'Lernpfade',
  'learningPaths.page.totalCount': '{{count}} Pfade',
  'learningPaths.pagination.next': 'Weiter',
  'learningPaths.pagination.pageOf': 'Seite {{page}} / {{total}} · {{count}} Pfade',
  'learningPaths.pagination.previous': 'Zurück',
  'learningPaths.status.draft': 'Entwurf',
  'learningPaths.status.published': 'Veröffentlicht',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Link kopieren',
  'webinars.card.delete': 'Löschen',
  'webinars.card.edit': 'Bearbeiten',
  'webinars.card.openRoom': 'Raum öffnen',
  'webinars.card.publicSignupBadge': 'Öffentliche Anmeldung',
  'webinars.deleteDialog.body': '„{{title}}“ löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
  'webinars.deleteDialog.cancel': 'Abbrechen',
  'webinars.deleteDialog.confirm': 'Löschen',
  'webinars.deleteDialog.title': 'Webinar löschen',
  'webinars.eligibility.default': 'Webinare sind für Ihr Konto nicht verfügbar (prüfen Sie die API-Verbindung oder wenden Sie sich an Ihren Administrator).',
  'webinars.eligibility.moduleDisabled': 'Das Webinar-Modul ist für Ihre Einrichtung noch nicht aktiviert. Ein Administrator muss unter Einrichtung → Webinare die Option „Webinare aktivieren“ aktivieren.',
  'webinars.eligibility.noInstitution': 'Ihrem Konto ist keine Einrichtung zugeordnet.',
  'webinars.eligibility.notAuthorized': 'Ihr Konto steht nicht auf der Liste der berechtigten Moderatoren. Bitten Sie die Einrichtung, Sie hinzuzufügen (Webinar-Steuerung → Berechtigte Moderatoren).',
  'webinars.eligibility.roleNotAllowed': 'Ihre Rolle erlaubt es nicht, Webinare mit diesem Konto zu moderieren.',
  'webinars.empty.createFirst': 'Webinar erstellen',
  'webinars.empty.noResults': 'Keine Ergebnisse',
  'webinars.empty.noResultsHint': 'Passen Sie Ihre Suche an.',
  'webinars.empty.noWebinarsHint': 'Erstellen Sie einen Raum und laden Sie Teilnehmer über die Seitenleiste ein.',
  'webinars.empty.noWebinarsYet': 'Noch keine Webinare',
  'webinars.externalInvite.closeButton': 'Schließen',
  'webinars.externalInvite.description': 'Personen, die nicht in der Lernendenliste stehen: jede Adresse erhält eine E-Mail mit Raumlink und Kalenderanhang.',
  'webinars.externalInvite.done': 'Fertig.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} bereits eingeladen',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} bereits eingeladen',
  'webinars.externalInvite.emailsHint': 'Eine Adresse pro Zeile oder durch Kommas getrennt. Duplikate und bereits gesendete Einladungen werden ignoriert.',
  'webinars.externalInvite.emailsLabel': 'E-Mail-Adressen',
  'webinars.externalInvite.emailsPlaceholder': 'max.mustermann@beispiel.de\nanna@beispiel.de',
  'webinars.externalInvite.errorMax200': 'Maximal 200 Adressen pro Versand.',
  'webinars.externalInvite.errorMinOne': 'Geben Sie mindestens eine gültige E-Mail-Adresse ein.',
  'webinars.externalInvite.errorsLabel': 'Fehler: {{errors}}',
  'webinars.externalInvite.sendButton': 'Einladungen senden',
  'webinars.externalInvite.sendFailed': 'Versand fehlgeschlagen',
  'webinars.externalInvite.sentCount_one': '{{count}} E-Mail gesendet',
  'webinars.externalInvite.sentCount_other': '{{count}} E-Mails gesendet',
  'webinars.externalInvite.skippedCount_one': '{{count}} übersprungen',
  'webinars.externalInvite.skippedCount_other': '{{count}} übersprungen',
  'webinars.externalInvite.title': 'Einladungen per E-Mail',
  'webinars.invite.duplicatesCount_one': '{{count}} bereits eingeladen',
  'webinars.invite.duplicatesCount_other': '{{count}} bereits eingeladen',
  'webinars.invite.errorsLabel': 'Fehler: {{errors}}',
  'webinars.invite.externalButton': 'Nicht in der Liste (E-Mail)',
  'webinars.invite.failed': 'Fehlgeschlagen',
  'webinars.invite.groupSuccess': 'Gruppeneinladungen verarbeitet (E-Mails an Mitglieder gesendet).',
  'webinars.invite.inviteButton': 'Einladen',
  'webinars.invite.learnersHelp': 'Suchen Sie einen Lernenden Ihrer Einrichtung: bei jeder Einladung wird eine <strong>Einladungs-E-Mail</strong> (Link + Kalender) versendet.',
  'webinars.invite.learnersTitle': 'Lernende',
  'webinars.invite.noCandidates': 'Keine Lernenden anzuzeigen (andere Suche versuchen oder „Nicht in der Liste“ für externe E-Mails).',
  'webinars.invite.noInvitations': 'Keine Einladungen erfasst.',
  'webinars.invite.searchLabel': 'Lernenden suchen',
  'webinars.invite.searchPlaceholder': 'Name oder E-Mail…',
  'webinars.invite.searching': 'Suche läuft…',
  'webinars.invite.sentCount_one': '{{count}} Einladung versendet (E-Mail)',
  'webinars.invite.sentCount_other': '{{count}} Einladungen versendet (E-Mail)',
  'webinars.invite.sentInvitationsToggle': 'Gesendete Einladungen',
  'webinars.page.newButton': 'Neues Webinar',
  'webinars.page.searchPlaceholder': 'Suchen…',
  'webinars.page.subtitle': 'Liste Ihrer Räume — öffnen Sie das Panel, um zu erstellen, zu bearbeiten oder Teilnehmer einzuladen.',
  'webinars.page.title': 'Webinare',
  'webinars.panel.backgroundHint': 'HTTPS- (oder HTTP-)URL hinter dem geteilten Inhalt für Teilnehmer. Leer lassen für das integrierte Lyrra-Motiv.',
  'webinars.panel.backgroundLabel': 'Hintergrundbild (Teilnehmeransicht)',
  'webinars.panel.backgroundPlaceholder': 'https://… (leer = Standard-Lyrra-Hintergrund)',
  'webinars.panel.copyLink': 'Link kopieren',
  'webinars.panel.createButton': 'Erstellen',
  'webinars.panel.createDescription': 'Tragen Sie die Angaben ein und speichern Sie. Anschließend können Sie Teilnehmer einladen.',
  'webinars.panel.createTitle': 'Neues Webinar',
  'webinars.panel.dateLabel': 'Tag (optional)',
  'webinars.panel.deleteButton': 'Löschen',
  'webinars.panel.descriptionLabel': 'Beschreibung',
  'webinars.panel.durationError': 'Geben Sie eine Dauer im Format Stunden:Minuten an (z. B. 1:00 oder 0:45).',
  'webinars.panel.durationHint': 'Pflicht, wenn ein Tag angegeben ist. Z. B. 1:30 für 1 h 30, 0:45 für 45 Min.',
  'webinars.panel.durationLabel': 'Dauer (h:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Wird geladen…',
  'webinars.panel.openRoom': 'Raum öffnen',
  'webinars.panel.presentersLabel': 'Co-Moderatoren',
  'webinars.panel.roomCode': 'Raumcode:',
  'webinars.panel.timeHint': 'Wenn leer: 9:00.',
  'webinars.panel.timeLabel': 'Startzeit',
  'webinars.panel.titleLabel': 'Titel',
  'webinars.panel.waitingHtmlLabel': 'HTML Warteraum (optional)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Willkommen…</p>',
  'webinars.panel.publicAccessTitle': 'Öffentliche Anmeldung (Formular)',
  'webinars.panel.publicAccessHelp':
    'Teilnehmer ohne Co-Moderation füllen nach der Lyrra-Anmeldung ein Formular aus: E-Mail, Passwort (eigenes Passwort) und zusätzliche Felder, die Sie unten hinzufügen.',
  'webinars.panel.publicFormFixedFieldsHint':
    'E-Mail und Passwort (mind. 8 Zeichen) sind immer erforderlich; hier fügen Sie weitere optionale oder Pflichtfelder hinzu.',
  'webinars.panel.publicFormAddFieldButton': 'Feld hinzufügen',
  'webinars.panel.publicFormFieldNumber': 'Feld {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Angezeigte Bezeichnung',
  'webinars.panel.publicFormFieldType': 'Typ',
  'webinars.panel.publicFormFieldTypeText': 'Kurztext',
  'webinars.panel.publicFormFieldTypeTextarea': 'Langtext',
  'webinars.panel.publicFormFieldRequired': 'Pflichtfeld',
  'webinars.panel.publicFormFieldRemove': 'Entfernen',
  'webinars.panel.publicFormFieldIdHint': 'Technische Kennung:',
  'webinars.panel.publicFormFieldLabelRequired': 'Jedes Feld braucht eine Bezeichnung.',
  'webinars.panel.publicAccessLoginLabel': 'Login im Anmeldeformular',
  'webinars.panel.publicAccessPasswordLabel': 'Passwort im Anmeldeformular',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Mindestens 8 Zeichen',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Leer lassen, um das aktuelle Passwort beizubehalten',
  'webinars.panel.publicAccessPasswordHint':
    'Bei Erstellung oder erster Aktivierung: Passwort erforderlich (mind. 8). Später: leer lassen, um das Passwort nicht zu ändern.',
  'webinars.panel.publicAccessLoginRequired': 'Geben Sie ein Login für die öffentliche Anmeldung an.',
  'webinars.panel.publicAccessPasswordRequired': 'Legen Sie ein Passwort mit mindestens 8 Zeichen fest.',
  'webinars.panel.publicAccessPasswordMin': 'Das öffentliche Passwort muss mindestens 8 Zeichen lang sein.',
  'webinars.status.draft': 'Entwurf',
  'webinars.status.ended': 'Beendet',
  'webinars.status.live': 'Live',
  'webinars.status.scheduled': 'Geplant',
  'videos.addPanel.cancel': 'Abbrechen',
  'videos.addPanel.continue': 'Weiter',
  'videos.addPanel.description': 'Importieren Sie MP4, WebM oder MOV, um sie in Ihren Schulungen zu verwenden.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Klicken, um ein Video auszuwählen',
  'videos.addPanel.title': 'Video hinzufügen',
  'videos.addPanel.titleLabel': 'Titel',
  'videos.addPanel.titlePlaceholder': 'Videotitel…',
  'videos.card.delete': 'Löschen',
  'videos.card.edit': 'Bearbeiten',
  'videos.card.noThumbnail': 'Kein Vorschaubild',
  'videos.card.publishedStudio': 'Veröffentlicht (im Studio bearbeitbar)',
  'videos.deleteDialog.body': '« {{title}} » wird dauerhaft gelöscht.',
  'videos.deleteDialog.cancel': 'Abbrechen',
  'videos.deleteDialog.confirm': 'Löschen',
  'videos.deleteDialog.title': 'Video löschen?',
  'videos.empty.addButton': 'Video hinzufügen',
  'videos.empty.description': 'Importieren Sie Ihr erstes Video, um es zu verwalten und in Ihren Schulungen zu verwenden.',
  'videos.empty.title': 'Keine Videos',
  'videos.page.addButton': 'Video hinzufügen',
  'videos.page.count_one': '{{count}} Video in Ihrer Bibliothek',
  'videos.page.count_other': '{{count}} Videos in Ihrer Bibliothek',
  'videos.page.title': 'Videos',
  'videos.preview.noFile': 'Keine Datei verfügbar.',
  'videos.status.draft': 'Entwurf',
  'videos.status.published': 'Veröffentlicht',
  'sounds.card.delete': 'Löschen',
  'sounds.card.mute': 'Stumm schalten',
  'sounds.deleteDialog.body': '« {{title}} » wird dauerhaft vom Server und aus Ihrer Bibliothek entfernt.',
  'sounds.deleteDialog.cancel': 'Abbrechen',
  'sounds.deleteDialog.confirm': 'Löschen',
  'sounds.deleteDialog.title': 'Diesen Sound löschen?',
  'sounds.empty.description': 'Importieren Sie Ihre Dateien: Sie werden in der Datenbank gespeichert und auf den Server kopiert, damit nichts verloren geht.',
  'sounds.empty.importButton': 'Sound importieren',
  'sounds.empty.title': 'Keine Sounds importiert',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Abbrechen',
  'sounds.importPanel.changeFile': 'Datei wechseln',
  'sounds.importPanel.createTag': '« {{tag}} » erstellen',
  'sounds.importPanel.description': 'Fügen Sie eine Audiodatei Ihrer Bibliothek für Ihre Lernpfade hinzu.',
  'sounds.importPanel.descriptionLabel': 'Beschreibung',
  'sounds.importPanel.descriptionPlaceholder': 'Beschreiben Sie diesen Sound…',
  'sounds.importPanel.dropzoneBrowse': 'durchsuchen',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Audiodatei hierher ziehen oder',
  'sounds.importPanel.optional': '(optional)',
  'sounds.importPanel.submit': 'Importieren',
  'sounds.importPanel.submitting': 'Wird gesendet…',
  'sounds.importPanel.tagsHint': 'Tippen und Enter drücken · Neue Tags werden automatisch erstellt',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Tags hinzufügen…',
  'sounds.importPanel.title': 'Sound importieren',
  'sounds.importPanel.titleLabel': 'Titel',
  'sounds.importPanel.titlePlaceholder': 'Name des Sounds…',
  'sounds.importPanel.titleRequired': 'Titel ist erforderlich',
  'sounds.noResults': 'Kein Sound entspricht « {{query}} »',
  'sounds.page.count_one': '{{count}} Datei — in der Datenbank und auf dem Server gespeichert',
  'sounds.page.count_other': '{{count}} Dateien — in der Datenbank und auf dem Server gespeichert',
  'sounds.page.importButton': 'Importieren',
  'sounds.page.searchPlaceholder': 'Titel, Beschreibung, Tag…',
  'sounds.page.title': 'Sounds',
  'documents.count_one': '{{count}} Dokument',
  'documents.count_other': '{{count}} Dokumente',
  'documents.delete.body': 'Das Dokument „{{filename}}“ wird aus Ihrer Bibliothek entfernt.',
  'documents.delete.cancel': 'Abbrechen',
  'documents.delete.confirm': 'Löschen',
  'documents.delete.irreversible': 'Diese Aktion kann nicht rückgängig gemacht werden.',
  'documents.delete.title': 'Dokument löschen?',
  'documents.importPanel.cancel': 'Abbrechen',
  'documents.importPanel.chooseFile': 'PDF-Dokument auswählen',
  'documents.importPanel.description': 'Das Dokument steht in Ihren PDF-Blöcken und in dieser Bibliothek zur Verfügung.',
  'documents.importPanel.descriptionLabel': 'Beschreibung',
  'documents.importPanel.descriptionPlaceholder': 'Beschreibung (optional)',
  'documents.importPanel.fileHint': 'Das Dokument steht in Ihrem PDF-Block und in dieser Bibliothek zur Verfügung.',
  'documents.importPanel.submit': 'Importieren',
  'documents.importPanel.title': 'PDF importieren',
  'documents.importPanel.titleLabel': 'Titel',
  'documents.importPanel.titlePlaceholder': 'Dokumenttitel',
  'documents.importPanel.uploading': 'Wird hochgeladen',
  'documents.importPdf': 'PDF importieren',
  'documents.loading': 'Bibliothek wird geladen…',
  'documents.newDocument': 'Neues Dokument',
  'documents.noDocuments': 'Keine Dokumente',
  'documents.noDocumentsHint': 'Importieren Sie Ihre PDFs hier, um sie später im PDF-Block wiederzuverwenden.',
  'documents.noResults': 'Kein Dokument entspricht Ihrer Suche.',
  'documents.pages_one': '{{count}} Seite',
  'documents.pages_other': '{{count}} Seiten',
  'documents.previewTitle': 'Dokumentvorschau',
  'documents.searchPlaceholder': 'PDF suchen...',
  'documents.share.accessRemoved': 'Zugriff für den Lernenden entzogen.',
  'documents.share.assignedTitle': 'Lernende mit Zugriff',
  'documents.share.close': 'Schließen',
  'documents.share.createAndShare': 'Erstellen, teilen und Einladung senden',
  'documents.share.createError': 'Lernender konnte nicht erstellt werden.',
  'documents.share.createLearner': 'Neuen Lernenden erstellen',
  'documents.share.createdSuccess': 'Konto erstellt/verknüpft, Dokument geteilt und E-Mail gesendet.',
  'documents.share.creating': 'Wird gesendet...',
  'documents.share.emailPlaceholder': 'E-Mail *',
  'documents.share.firstNamePlaceholder': 'Vorname *',
  'documents.share.hideCreateLearner': 'Schnellerstellung ausblenden',
  'documents.share.lastNamePlaceholder': 'Nachname *',
  'documents.share.loadingAssigned': 'Wird geladen…',
  'documents.share.loadingLearners': 'Lernende werden geladen...',
  'documents.share.noAssigned': 'Kein Lernender für dieses Dokument zugewiesen.',
  'documents.share.noLearners': 'Keine Lernenden verfügbar.',
  'documents.share.removeButton': 'Entfernen',
  'documents.share.removeError': 'Zugriff konnte nicht entfernt werden.',
  'documents.share.searchPlaceholder': 'Lernenden suchen (Vorname, Nachname, E-Mail)',
  'documents.share.selectLearner': 'Vorhandenen Lernenden auswählen',
  'documents.share.shareButton': 'Teilen',
  'documents.share.shareError': 'Dokument konnte nicht geteilt werden.',
  'documents.share.sharedSuccess': 'Dokument geteilt und Einladung gesendet.',
  'documents.share.title': 'Dokument teilen',
  'documents.subtitle': 'Wiederverwendbare PDF-Dokumentenbibliothek für den Designer.',
  'documents.title': 'Dokumente',
  'documents.toast.importError': 'Import nicht möglich',
  'documents.toast.importErrorDefault': 'Der Import ist fehlgeschlagen.',
  'documents.toast.importSuccess': 'Dokument importiert',
  'documents.toast.importSuccessDesc': '{{filename}} wurde Ihrer Dokumentenbibliothek hinzugefügt.',
  'documents.updating': 'Aktualisierung…',
  'help.categories.analytics.description': 'Dashboards, Filter und Exporte zur Steuerung der Schulungswirkung.',
  'help.categories.analytics.title': 'Analytik & Berichte',
  'help.categories.apprenants.description': 'Einschreibungen, Kohorten und den Fortschritt der Teilnehmer verfolgen.',
  'help.categories.apprenants.title': 'Lernende, Gruppen & Nachverfolgung',
  'help.categories.boutique.description': 'Schulungen verkaufen oder zuweisen, wenn der Shop konfiguriert ist.',
  'help.categories.boutique.title': 'Shop & Monetarisierung',
  'help.categories.demarrage.description': 'Die Oberfläche, Ihr Profil und die tägliche Navigation in Lyrra kennenlernen.',
  'help.categories.demarrage.title': 'Einstieg & Konto',
  'help.categories.donnees.description': 'DSGVO, Aufbewahrung, Betroffenenrechte und bewährte Sicherheitspraktiken.',
  'help.categories.donnees.title': 'Personenbezogene Daten & Sicherheit',
  'help.categories.eduflow.description': 'Lernpfade mit Blöcken, Verbindungen und Navigationslogik gestalten.',
  'help.categories.eduflow.title': 'Pfade & EduFlow-Designer',
  'help.categories.evaluation.description': 'Quiz, Bewertungen, Umfragen und Feedback zur Messung der Lernergebnisse.',
  'help.categories.evaluation.title': 'Bewertung & aktives Lernen',
  'help.categories.institution.description': 'Ihre Organisation, Mitarbeiter und Inhaltsstruktur konfigurieren.',
  'help.categories.institution.title': 'Einrichtung & Team',
  'help.categories.marque.description': 'Farben, Bezeichnungen und die Lernerfahrung an Ihre Identität anpassen.',
  'help.categories.marque.title': 'Marke & Benutzererfahrung',
  'help.categories.medias.description': 'Bibliothek, Importe und integrierte Audio-/Video-/Bildwerkzeuge in Lyrra verwalten.',
  'help.categories.medias.title': 'Medien & Kreativstudio',
  'help.categories.parcours-v2.description': 'Kampagnen, Kompetenzen, Integrationen: alles, was das Schulungsmanagement bereichert.',
  'help.categories.parcours-v2.title': 'Erweiterte Funktionen & Roadmap',
  'help.categories.podcasts.description': 'Langform-Audioinhalte außerhalb des Block-für-Block-Pfads erstellen und verbreiten.',
  'help.categories.podcasts.title': 'Podcasts & Hörbücher',
  'help.categories.publication.description': 'Einen Pfad für Lernende verfügbar machen und die richtigen Zugänge teilen.',
  'help.categories.publication.title': 'Veröffentlichung & Verbreitung',
  'help.categories.qualite.description': 'Französische Qualitätsrahmen: Rückverfolgbarkeit, Ziele und Nachweise für Audits.',
  'help.categories.qualite.title': 'Qualität, Qualiopi & pädagogische Konformität',
  'help.categories.support.description': 'Bei einem technischen oder funktionalen Problem wissen, wo man suchen und wen man kontaktieren kann.',
  'help.categories.support.title': 'Fehlerbehebung & Support',
  'help.configHint': 'Redaktionelle Inhalte: Verknüpfen Sie Ihre URLs, Artikel oder Wissensdatenbank, sobald Sie bereit sind.',
  'help.description': 'Dokumentation und Support',
  'help.hubIntro': 'Themen rund um die Nutzung von Lyrra: Sie werden bald jeden Artikel mit Ihrer Dokumentation, Tutorials oder Support-Tickets verknüpfen können.',
  'help.modal.expectedAnswer': 'Erwartete Antwort:',
  'help.modal.fallbackHighlight1': 'Artikel',
  'help.modal.fallbackHighlight2': 'Schulungsplattform',
  'help.modal.fallbackHighlight3': 'Zu vervollständigen',
  'help.modal.fallbackSectionHeading': 'Wichtige Punkte',
  'help.modal.fallbackSectionParagraph': 'Der detaillierte Inhalt dieses Artikels wird angereichert oder mit Ihrer internen Dokumentation verknüpft.',
  'help.modal.fallbackStep1Desc': 'Verwenden Sie das Seitenmenü oder das Dashboard, um auf das betreffende Modul zuzugreifen.',
  'help.modal.fallbackStep1Title': 'Funktion öffnen',
  'help.modal.fallbackStep2Desc': 'Die Bildschirme leiten Sie an: Speichern Sie regelmäßig und testen Sie in der Vorschau, wenn verfügbar.',
  'help.modal.fallbackStep2Title': 'Dem Geschäftsprozess folgen',
  'help.modal.fallbackStep3Desc': 'Notieren Sie die Besonderheiten Ihrer Einrichtung (HR-Prozesse, Benennung, Markenrichtlinien) für Ihr Team.',
  'help.modal.fallbackStep3Title': 'Intern dokumentieren',
  'help.modal.fallbackTip': 'Im Zweifelsfall überprüfen Sie Ihre Rolle (Trainer, Admin, HR): Die angezeigten Menüs hängen davon ab.',
  'help.modal.quiz': 'Validierungsquiz',
  'help.modal.related': 'Siehe auch',
  'help.modal.resources': 'Ressourcen',
  'help.modal.steps': 'In der Praxis',
  'help.modal.tips': 'Tipps',
  'help.noResults': 'Keine Ergebnisse für diese Suche.',
  'help.onboarding.ai-designer': 'Schulung — LyrraStudio-KI-Designer-Assistent (onboarding)',
  'help.onboarding.blocks': 'Schulung — EduFlow-Blocktypen (onboarding)',
  'help.onboarding.branches': 'Schulung — EduFlow-Verzweigungen & Bedingungen (onboarding)',
  'help.onboarding.catalog-visibility': 'Schulung — LyrraStudio-Katalog & Sichtbarkeit (onboarding)',
  'help.onboarding.certificates': 'Schulung — LyrraStudio-ausgestellte Zertifikate (onboarding)',
  'help.onboarding.dashboard': 'Schulung — Lyrra-Dashboard (onboarding)',
  'help.onboarding.designer-overview': 'Schulung — EduFlow-Designer-Übersicht (onboarding)',
  'help.onboarding.enrollments': 'Schulung — LyrraStudio-Einschreibungen & Teilnehmer (onboarding)',
  'help.onboarding.groups-cohorts': 'Schulung — LyrraStudio-Gruppen & Kohorten (onboarding)',
  'help.onboarding.media-library': 'Schulung — LyrraStudio-Mediathek (onboarding)',
  'help.onboarding.navigation': 'Schulung — Lyrra-Navigation & Seitenmenü (onboarding)',
  'help.onboarding.objectives': 'Schulung — EduFlow-Lernziele (onboarding)',
  'help.onboarding.profile': 'Schulung — Lyrra-Profil & Einstellungen (onboarding)',
  'help.onboarding.progress': 'Schulung — LyrraStudio-Fortschritt & Zeitaufwand (onboarding)',
  'help.onboarding.publication-links': 'Schulung — LyrraStudio-Zugangslinks & Teilen (onboarding)',
  'help.onboarding.publication-status': 'Schulung — LyrraStudio-Entwurf, Veröffentlichung und Aktualisierungen (onboarding)',
  'help.onboarding.roles': 'Schulung — Lyrra-Rollen und Zugriffsrechte (onboarding)',
  'help.onboarding.validation': 'Schulung — EduFlow-Pfadvalidierung (onboarding)',
  'help.onboardingFlowMissing': 'Schulung nicht gefunden',
  'help.onboardingFlowMissingDesc': 'Führen Sie die Skripte db:seed:onboarding-eduflow und db:seed:onboarding-navigation-eduflow (Backend) aus oder setzen Sie die Variablen VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Artikel öffnen',
  'help.searchPlaceholder': 'Thema suchen…',
  'help.title': 'Hilfezentrum',
  'help.topics.analytics-exports.description': 'Extraktionen für internes oder regulatorisches Reporting vorbereiten.',
  'help.topics.analytics-exports.title': 'Exporte & HR-Reporting',
  'help.topics.analytics-indicateurs.description': 'Abschlussrate, Engagement, Verteilung nach Status und Zeit in den Blöcken.',
  'help.topics.analytics-indicateurs.title': 'Schlüsselindikatoren',
  'help.topics.analytics-vue.description': 'Diagramme, Zeiträume anpassen und nach Schulung filtern, um Daten zu analysieren.',
  'help.topics.analytics-vue.title': 'Analytik-Übersicht',
  'help.topics.app-certificats.description': 'Generierte Zertifikate und die zugehörigen Abschlussbedingungen überprüfen.',
  'help.topics.app-certificats.title': 'Ausgestellte Zertifikate',
  'help.topics.app-groupes.description': 'Gruppen nutzen, um Zielgruppen zu segmentieren und Kampagnen oder Berichte zu erleichtern.',
  'help.topics.app-groupes.title': 'Gruppen & Kohorten',
  'help.topics.app-inscriptions.description': 'Die Liste der Lernenden eines Pfads, ihre Status und Zugangshistorie einsehen.',
  'help.topics.app-inscriptions.title': 'Einschreibungen & Teilnehmer',
  'help.topics.app-progression.description': 'Fortschritt, Abschlüsse und Engagement-Zeit in den Blöcken interpretieren.',
  'help.topics.app-progression.title': 'Fortschritt & Zeitaufwand',
  'help.topics.boutique-offres.description': 'Pfade mit Produkten oder Zugangscodes je nach Geschäftsmodell verknüpfen.',
  'help.topics.boutique-offres.title': 'Angebote & Preisgestaltung',
  'help.topics.boutique-paiement.description': 'Den Kaufablauf auf Lernendenseite und die Bestätigungen auf Einrichtungsseite verstehen.',
  'help.topics.boutique-paiement.title': 'Zahlungen & Rechnungsstellung',
  'help.topics.demarrage-navigation.description': 'Die Bereiche Projekte, Schulungen, Medien, Analytik und Einstellungen je nach Rolle finden.',
  'help.topics.demarrage-navigation.title': 'Navigation & Seitenmenü',
  'help.topics.demarrage-profil.description': 'Ihre Informationen, die Oberflächensprache und die Benachrichtigungen aktualisieren.',
  'help.topics.demarrage-profil.title': 'Profil & Einstellungen',
  'help.topics.demarrage-roles.description': 'Die Unterschiede zwischen Trainer, Einrichtungsadministrator, HR und Lernendem verstehen.',
  'help.topics.demarrage-roles.title': 'Rollen und Zugriffsrechte',
  'help.topics.demarrage-tableau-bord.description': 'Schlüsselindikatoren lesen, schnell auf aktuelle Pfade und nützliche Verknüpfungen zugreifen.',
  'help.topics.demarrage-tableau-bord.title': 'Schulungs-Dashboard',
  'help.topics.donnees-conservation.description': 'Grundsätze zur Archivierungsdauer von Fortschritten und Schulungsnachweisen.',
  'help.topics.donnees-conservation.title': 'Aufbewahrungsdauer',
  'help.topics.donnees-mots-de-passe.description': 'Bewährte Passwortpraktiken, Sitzungen und Zugriffsdelegation.',
  'help.topics.donnees-mots-de-passe.title': 'Konten & Authentifizierung',
  'help.topics.donnees-rgpd.description': 'Zwecke der in Lyrra erhobenen Daten und Verantwortung des Schulungsverantwortlichen.',
  'help.topics.donnees-rgpd.title': 'DSGVO & Verarbeitungsverzeichnis',
  'help.topics.eduflow-blocs.description': 'Die passenden Blöcke auswählen und konfigurieren: Text, Video, Audio, Bewertung, Verzweigung usw.',
  'help.topics.eduflow-blocs.title': 'Blocktypen (Inhalt, Quiz, Medien…)',
  'help.topics.eduflow-branches.description': 'Mehrere Ausgänge, Pfadkonvergenz und Navigationsregeln verwalten.',
  'help.topics.eduflow-branches.title': 'Verzweigungen & Bedingungen',
  'help.topics.eduflow-concept.description': 'Canvas, Blockpalette, Auswahl, Zoom und visuelle Organisation des Pfads.',
  'help.topics.eduflow-concept.title': 'Designer-Übersicht',
  'help.topics.eduflow-ia.description': 'Blöcke mit dem Assistenten vorschlagen oder bearbeiten, mit systematischer menschlicher Überprüfung.',
  'help.topics.eduflow-ia.title': 'KI-Assistent im Designer',
  'help.topics.eduflow-objectifs.description': 'Ziele mit Blöcken verknüpfen, um den Rahmen Ihrer Schulung zu strukturieren.',
  'help.topics.eduflow-objectifs.title': 'Lernziele',
  'help.topics.eduflow-validation.description': 'Das Validierungspanel nutzen, um Fehler und Warnungen vor der Veröffentlichung zu erkennen.',
  'help.topics.eduflow-validation.title': 'Pfadvalidierung',
  'help.topics.eval-auto-eval.description': 'Adaptive Pfade basierend auf Ergebnissen anbieten.',
  'help.topics.eval-auto-eval.title': 'Selbstbewertung & Nachbesserung',
  'help.topics.eval-ouverte.description': 'Die Sammlung von Freitextantworten und die manuelle oder halbautomatische Bewertung organisieren.',
  'help.topics.eval-ouverte.title': 'Offene Fragen & Korrektur',
  'help.topics.eval-quiz.description': 'Bewertungsmaßstäbe, Feedback und Versuche für effektive QCM konfigurieren.',
  'help.topics.eval-quiz.title': 'Quiz & Multiple-Choice-Fragen',
  'help.topics.inst-collaborateurs.description': 'Trainer, Administratoren oder HR-Personal hinzufügen und Berechtigungen verwalten.',
  'help.topics.inst-collaborateurs.title': 'Benutzer & Einladungen',
  'help.topics.inst-structure.description': 'Festlegen, was auf Einrichtungsebene geteilt wird gegenüber persönlichen Projekten.',
  'help.topics.inst-structure.title': 'Struktur & Geltungsbereich',
  'help.topics.inst-trash.description': 'Gelöschte Schulungen oder Podcasts finden und bei Bedarf wiederherstellen.',
  'help.topics.inst-trash.title': 'Papierkorb & Wiederherstellung',
  'help.topics.marque-accessibilite.description': 'Kontraste, Textalternativen und Tastaturnavigation in den Inhalten.',
  'help.topics.marque-accessibilite.title': 'Barrierefreiheit & Lesbarkeit',
  'help.topics.marque-charte.description': 'Wie sich das Branding im Player und in den Lernbereichen widerspiegelt.',
  'help.topics.marque-charte.title': 'Marke & institutionelle Farben',
  'help.topics.marque-landing.description': 'Ihr Schulungsangebot für Besucher präsentieren, wenn das Modul aktiviert ist.',
  'help.topics.marque-landing.title': 'Öffentliche Seiten & Katalog',
  'help.topics.medias-bibliotheque.description': 'Videos, Sounds, Bilder und Dokumente in Ihre Blöcke importieren, organisieren und wiederverwenden.',
  'help.topics.medias-bibliotheque.title': 'Mediathek',
  'help.topics.medias-documents.description': 'Herunterladbare Ressourcen anhängen und Dokumentation mit dem Pfad verknüpfen.',
  'help.topics.medias-documents.title': 'Dokumente & PDFs',
  'help.topics.medias-studio-audio.description': 'Aufnehmen, mischen oder Sprachsynthese nutzen, um Ihre Inhalte zu bereichern.',
  'help.topics.medias-studio-audio.title': 'Audio- & Sprachstudio',
  'help.topics.medias-video.description': 'Bewährte Praktiken für Format, Größe und Barrierefreiheit bei integrierten Playern.',
  'help.topics.medias-video.title': 'Videos & Integrationen',
  'help.topics.podcasts-creation.description': 'Episoden, Metadaten und Cover für Ihr Audioangebot strukturieren.',
  'help.topics.podcasts-creation.title': 'Einen Podcast erstellen',
  'help.topics.podcasts-diffusion.description': 'Höreinstellungen, Teilen und Nutzungsverfolgung seitens der Einrichtung.',
  'help.topics.podcasts-diffusion.title': 'Verbreitung & Lernende',
  'help.topics.pub-catalogue.description': 'Eine Schulung dem institutionellen Katalog oder einem Angebot zuordnen, wenn aktiviert.',
  'help.topics.pub-catalogue.title': 'Katalog & Sichtbarkeit',
  'help.topics.pub-liens.description': 'Links für Lernende, Präsentatoren oder Vorschau generieren oder kopieren.',
  'help.topics.pub-liens.title': 'Zugangslinks & Teilen',
  'help.topics.pub-statuts.description': 'Lebenszyklus einer Schulung: speichern, testen, veröffentlichen und Änderungen versionieren.',
  'help.topics.pub-statuts.title': 'Entwurf, Veröffentlichung und Aktualisierungen',
  'help.topics.qualite-qualiopi.description': 'Dedizierte Bereiche nutzen, um Kriterien und das Qualitätsdossier vorzubereiten.',
  'help.topics.qualite-qualiopi.title': 'Vor-Audit & Qualiopi-Indikatoren',
  'help.topics.qualite-tracabilite.description': 'Ziele, Inhalte und Bewertungen verknüpfen, um den zertifizierten Pfad zu dokumentieren.',
  'help.topics.qualite-tracabilite.title': 'Pädagogische Rückverfolgbarkeit',
  'help.topics.support-contact.description': 'Support-Kanal, Ticket-Erstellung und Informationen für eine schnelle Diagnose.',
  'help.topics.support-contact.title': 'Lyrra-Support kontaktieren',
  'help.topics.support-faq.description': 'Player lädt nicht, fehlendes Medium, Veröffentlichungsfehler: erste Schritte.',
  'help.topics.support-faq.title': 'Häufige Probleme',
  'help.topics.support-navigateur.description': 'Empfohlene Versionen, Cookies und Erweiterungen, die Störungen verursachen können.',
  'help.topics.support-navigateur.title': 'Browser & Kompatibilität',
  'help.topics.v2-campagnes.description': 'Den Einsatz von Pfaden für Zielgruppen über die Zeit hinweg planen.',
  'help.topics.v2-campagnes.title': 'Kampagnen & Schulungspläne',
  'help.topics.v2-competences.description': 'Blöcke oder Pfade den erwarteten Kompetenzen für das Geschäftsreporting zuordnen.',
  'help.topics.v2-competences.title': 'Kompetenzrahmen',
  'help.topics.v2-integrations.description': 'Austausch mit Ihrem HR-IS, externem LMS oder BI-Tools, wenn Konnektoren verfügbar sind.',
  'help.topics.v2-integrations.title': 'Integrationen & API',
  'pages.images.cancel': 'Abbrechen',
  'pages.images.changeFile': 'Datei wechseln',
  'pages.images.copyUrl': 'URL kopieren',
  'pages.images.count_one': '{{count}} Bild in Ihrer Bibliothek',
  'pages.images.count_other': '{{count}} Bilder in Ihrer Bibliothek',
  'pages.images.delete': 'Löschen',
  'pages.images.deleteAction': 'Löschen',
  'pages.images.deleteConfirmHint': 'wird dauerhaft gelöscht.',
  'pages.images.deleteTitle': 'Dieses Bild löschen?',
  'pages.images.descriptionLabel': 'Beschreibung (optional)',
  'pages.images.descriptionPlaceholder': 'Kurze Beschreibung…',
  'pages.images.dropzoneBrowse': 'durchsuchen',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Bild ziehen oder',
  'pages.images.emptyHint': 'Ziehen und ablegen, klicken zum Importieren oder Screenshot einfügen (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importieren',
  'pages.images.importSubtitle': 'Fügen Sie ein Bild zu Ihrer Bibliothek für Ihre Schulungen hinzu.',
  'pages.images.importTitle': 'Bild importieren',
  'pages.images.lightboxBackdropClose': 'Schließen',
  'pages.images.lightboxClose': 'Schließen',
  'pages.images.lightboxNext': 'Nächstes Bild',
  'pages.images.lightboxPrev': 'Vorheriges Bild',
  'pages.images.lightboxTitle': 'Bildvorschau',
  'pages.images.noImages': 'Keine Bilder importiert',
  'pages.images.noResults': 'Keine Ergebnisse',
  'pages.images.pasteHint': 'Oder Screenshot einfügen (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Nach einem Bild suchen…',
  'pages.images.submit': 'Importieren',
  'pages.images.title': 'Bilder',
  'pages.images.titleLabel': 'Titel (optional)',
  'pages.images.titlePlaceholder': 'Bildname…',
  'pages.images.viewLarge': 'Groß anzeigen',
  'pages.learners.blockSessionInProgress': '(laufend)',
  'pages.learners.blockTimeOnBlock': '({{duration}} in diesem Block)',
  'pages.learners.description': 'Fortschritt Ihrer Lernenden verfolgen',
  'pages.learners.detailedTraces': 'Detaillierte Aktivität · {{flowTitle}}',
  'pages.learners.enrolledFormations': 'Eingeschriebene Kurse',
  'pages.learners.lastEvents': 'Letzte Ereignisse',
  'pages.learners.notStarted': 'Nicht begonnen',
  'pages.learners.startedOn': 'Beginn {{date}}',
  'pages.learners.statsBlockSessions': 'Block-Sitzungen: {{count}}',
  'pages.learners.statsEvents': 'Ereignisse: {{count}}',
  'pages.learners.statsResponses': 'Antworten: {{count}}',
  'pages.learners.timePerBlock': 'Zeit pro Block',
  'pages.learners.title': 'Lernende',
  'pages.learners.trackingEvent.block_enter': 'Block betreten',
  'pages.learners.trackingEvent.block_exit': 'Block verlassen',
  'pages.learners.trackingEvent.progress_sync': 'Fortschritt synchronisiert',
  'pages.learners.trackingEvent.response_submitted': 'Antwort gesendet',
  'pages.learners.trackingEvent.training_completed': 'Kurs abgeschlossen',
  'pages.learners.trackingEvent.training_enter': 'Kurs geöffnet',
  'pages.learners.trackingEvent.training_exit': 'Kurs verlassen oder geschlossen',
  'pages.learners.trackingEvent.unknown': 'Ereignis',
  'pages.learners.refresh': 'Aktualisieren',
  'pages.learners.exportCsv': 'CSV exportieren',
  'pages.learners.statTotal': 'Lernende gesamt',
  'pages.learners.statInProgress': 'In Bearbeitung',
  'pages.learners.statCompleted': 'Abgeschlossen',
  'pages.learners.statAvgProgress': 'Ø Fortschritt',
  'pages.learners.searchPlaceholder': 'Nach Name oder E-Mail suchen…',
  'pages.learners.filterAllFlows': 'Alle Kurse',
  'pages.learners.sortRecent': 'Neueste zuerst',
  'pages.learners.sortName': 'Name A–Z',
  'pages.learners.sortProgress': 'Fortschritt',
  'pages.learners.colLearner': 'Lernende(r)',
  'pages.learners.colFormations': 'Kurse',
  'pages.learners.colProgress': 'Fortschritt',
  'pages.learners.colCompleted': 'Abgeschlossen',
  'pages.learners.colTime': 'Zeit',
  'pages.learners.colLastAccess': 'Letzter Zugriff',
  'pages.learners.colActions': 'Aktionen',
  'pages.learners.openProgressDashboard': 'Fortschrittsübersicht',
  'pages.learners.dashboardTitle': 'Fortschritt des Lernenden',
  'pages.learners.dashboardBack': 'Zurück zur Liste',
  'pages.learners.dashboardInvalidId': 'Ungültige Lernenden-ID',
  'pages.learners.dashboardNotFound': 'Lernende(r) nicht gefunden oder Zugriff verweigert',
  'pages.learners.dashboardOverviewHint':
    'Gebündelte Ansicht: EduFlow-Kurse dieses Teilnehmers sowie Lernpfade und Podcasts für dasselbe E-Mail in Ihrem Konto.',
  'pages.learners.tabOverview': 'Übersicht',
  'pages.learners.tabFormations': 'Kurse',
  'pages.learners.tabParcours': 'Pfade',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Kurse',
  'pages.learners.sectionParcours': 'Lernpfade',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'Kein Lernpfad für ein Lyrra-Konto mit dieser E-Mail.',
  'pages.learners.emptyPodcasts': 'Kein Podcast für ein Lyrra-Konto mit dieser E-Mail.',
  'pages.learners.linkedAccountHint':
    'Pfade und Podcasts erscheinen, wenn ein Lyrra-Benutzer mit derselben E-Mail wie dieser Teilnehmer existiert.',
  'pages.learners.kpiEduflows': 'Kurse',
  'pages.learners.kpiAvgProgress': 'Ø Fortschritt',
  'pages.learners.kpiPaths': 'Pfade',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} abgeschlossen',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} Aktivitäten',
  'pages.learners.pathAssignedOn': 'Hinzugefügt am {{date}}',
  'pages.learners.podcastCompleted': 'Abgeschlossen',
  'pages.learners.podcastInProgress': 'In Bearbeitung',
  'pages.learners.pathStatusPublished': 'Veröffentlicht',
  'pages.learners.pathStatusDraft': 'Entwurf',
  'pages.learners.openInDesigner': 'Im Designer öffnen',
  'pages.learners.openParcoursEditor': 'Pfad öffnen',
  'pages.learners.viewPodcasts': 'Podcasts anzeigen',
  'pages.learners.loading': 'Lernende werden geladen…',
  'pages.learners.loadError': 'Lernende konnten nicht geladen werden',
  'pages.learners.retry': 'Erneut versuchen',
  'pages.learners.emptyFiltered': 'Keine Lernenden gefunden',
  'pages.learners.emptyFilteredHint': 'Passen Sie Suche oder Filter an',
  'pages.learners.emptyNone': 'Noch keine eingeschriebenen Lernenden',
  'pages.learners.emptyNoneHint':
    'Lernende erscheinen hier, sobald sie sich für Ihre veröffentlichten Kurse einschreiben',
  'pages.learners.pagination': 'Seite {{page}} von {{totalPages}} · {{total}} Lernende',
  'pages.learners.prev': 'Zurück',
  'pages.learners.next': 'Weiter',
  'pages.learners.csvLastName': 'Nachname',
  'pages.learners.csvFirstName': 'Vorname',
  'pages.learners.csvEmail': 'E-Mail',
  'pages.learners.csvFormations': 'Kurse',
  'pages.learners.csvAvgProgress': 'Durchschnittlicher Fortschritt',
  'pages.learners.csvTotalTime': 'Gesamtzeit',
  'pages.learners.csvLastActivity': 'Letzte Aktivität',
  'pages.learners.statsAdaptiveChoice':
    'Geführte Auswahl — akzeptiert: {{accepted}} · abgelehnt: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptive Anpassungen ausgewertet: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Vereinfachte Versionen genutzt: {{count}}',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Vereinfachter Textmodus',
  'accessibility.simplifiedTextModeDesc':
    'Vereinfacht nur die Texte im Kurs, um Lesen und Verstehen zu erleichtern.',
  'accessibility.imageHoverAudio': 'Modus für Sehbeeinträchtigung: Audiobeschreibung bei Bild-Hover',
  'accessibility.imageHoverAudioDesc':
    'Wenn ein Bild im Player überfahren wird, startet die Audiobeschreibung automatisch.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Einklappen',
  'admin.shell.collapseSidebar': 'Seitenleiste einklappen',
  'admin.shell.expandSidebar': 'Seitenleiste erweitern',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.login': 'Sign in',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.logout': 'Log out',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.register': 'Create one',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'btn.cancel': 'Annuler',
  'btn.close': 'Fermer',
  'btn.save': 'Enregistrer',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.copied': 'Copié !',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.loading': 'Chargement…',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Schulungsanfrage — pädagogische Inhalte',
  'trainingRequest.intro': 'Beschreiben Sie den Bedarf. Das HR-Team genehmigt oder lehnt die Anfrage ab; bei Genehmigung wird sie zur Inhaltsvorbereitung weitergeleitet.',
  'trainingRequest.fieldTitle': 'Schulungstitel',
  'trainingRequest.fieldDescription': 'Bedarfsbeschreibung',
  'trainingRequest.fieldJustification': 'Begründung / Ziele',
  'trainingRequest.fieldDate': 'Gewünschtes Datum',
  'trainingRequest.datePlaceholder': 'z.B. Q2 2026',
  'trainingRequest.fieldFormateur': 'Trainer der Einrichtung',
  'trainingRequest.formateurPlaceholder': 'Trainer auswählen…',
  'trainingRequest.formateursEmpty': 'Kein Trainerkonto für Ihre Einrichtung hinterlegt.',
  'trainingRequest.titleRequired': 'Bitte geben Sie einen Titel ein (mindestens 2 Zeichen).',
  'trainingRequest.sent': 'Anfrage gespeichert. Sie werden per E-Mail über die Entscheidung informiert.',
  'trainingRequest.sendError': 'Die Anfrage konnte nicht gespeichert werden. Prüfen Sie Ihre Verbindung oder Berechtigungen.',
  'trainingRequest.submit': 'Anfrage senden',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.total': 'au total',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Kollaborativ',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.createFormation.error': 'Schulung konnte nicht erstellt werden.',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duplicateFlowError': 'Schulung konnte nicht dupliziert werden.',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.title': 'Schulungen',
  'eduflow.list.subtitle': 'Gestalten Sie interaktive Lernerfahrungen',
  'eduflow.list.searchPlaceholder': 'Schulung suchen…',
  'eduflow.list.import': 'Importieren',
  'eduflow.list.importHint':
    '.lyrra-Datei (Medien enthalten) oder älteren Lyrra-V1-JSON-Export.',
  'eduflow.list.importSuccess': 'Schulung importiert. Der Designer wird geöffnet.',
  'eduflow.list.importError':
    'Import nicht möglich — prüfen Sie die Datei (.lyrra oder gültiger JSON-Export, mindestens ein Block).',
  'eduflow.list.filterAll': 'Alle',
  'eduflow.list.filterDraft': 'Entwürfe',
  'eduflow.list.filterPublished': 'Veröffentlicht',
  'eduflow.list.filterArchived': 'Archiviert',
  'eduflow.list.viewModeGrid': 'Kachelansicht',
  'eduflow.list.viewModeList': 'Listenansicht',
  'eduflow.list.viewByCategory': 'Kategorieansicht',
  'eduflow.list.categoryBack': 'Alle Kategorien',
  'eduflow.list.categoryBrowseHint': 'Wählen Sie eine Kategorie, um deren Schulungen anzuzeigen.',
  'eduflow.list.categoryFormationCount': '{{count}} Schulung(en)',
  'eduflow.list.categoryEmptyFiltered': 'Keine Schulungen in dieser Ansicht (aktiver Filter oder Suche).',
  'eduflow.list.uncategorizedCardHint': 'Schulungen ohne Kategorie.',
  'eduflow.list.uncategorized': 'Ohne Kategorie',
  'eduflow.list.manageCategories': 'Kategorien verwalten',
  'eduflow.list.categoriesPanelTitle': 'Kurskategorien',
  'eduflow.list.noCategoryAvailable': 'Keine Kategorie verfügbar.',
  'eduflow.list.categoriesSaveError': 'Kategorien konnten nicht gespeichert werden.',
  'eduflow.list.emptyFilteredTitle': 'Keine Ergebnisse',
  'eduflow.list.emptyFilteredHint': 'Ändern Sie Ihre Such- oder Filterkriterien.',
  'eduflow.list.emptyFirstTitle': 'Erstellen Sie Ihre erste Schulung',
  'eduflow.list.emptyFirstHint': 'Gestalten Sie interaktives Lernen mit dem visuellen Designer.',
  'eduflow.list.emptyCreateCta': 'Meine erste Schulung erstellen',
  'eduflow.list.deleteToTrashTitle': 'Diese Schulung in den Papierkorb legen?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » wird aus Ihrer Liste entfernt (logische Löschung). Ihre Einrichtung kann sie aus dem Papierkorb der Organisation wiederherstellen.',
  'eduflow.list.deleteToTrashConfirm': 'In den Papierkorb',
  'eduflow.list.coverLabel': 'Titelbild',
  'eduflow.list.coverRemove': 'Titelbild entfernen',
  'eduflow.list.coverUpload': 'Bild auswählen',
  'eduflow.list.flowProperties': 'Titel, Beschreibung und Titelbild',
  'eduflow.list.propertiesPanelTitle': 'Eigenschaften der Schulung',
  'eduflow.list.duplicatePanelTitle': 'Schulung duplizieren',
  'eduflow.list.duplicatePanelSubtitle': 'Ursprüngliche Schulung: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'Kopie',
  'eduflow.list.duplicateSuccess': 'Schulung dupliziert.',
  'eduflow.list.duplicateCoverHint':
    'Titelbild und Lerninhalt der ursprünglichen Schulung werden übernommen.',
  'eduflow.list.propertiesSaveError': 'Eigenschaften konnten nicht gespeichert werden.',
  'eduflow.list.propertiesSaved': 'Eigenschaften gespeichert.',
  'eduflow.list.propertiesUploadError': 'Bild-Upload fehlgeschlagen.',
  'eduflow.list.blocksCount': '{{count}} Blöcke',
  'eduflow.list.noContent': 'Kein Inhalt',
  'eduflow.list.minutesShort': '{{count}} Min.',
  'eduflow.list.tooltip.openMissions': '„Meine Produktionsaufträge“ öffnen',
  'eduflow.list.tooltip.edit': 'Bearbeiten',
  'eduflow.list.shellHint':
    'Eintrag Ihrer Organisation — erstellen Sie den EduFlow über „Meine Produktionsaufträge“.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Suivant',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Version {{label}} aktiviert (öffentlich)',
  'eduflow.versionHistory.activateTitle': '{{label}} aktivieren?',
  'eduflow.versionHistory.blocks': '{{count}} Blöcke',
  'eduflow.versionHistory.confirmActivate': 'Aktivieren',
  'eduflow.versionHistory.copyFailed': 'Kopieren fehlgeschlagen',
  'eduflow.versionHistory.copyHtml': 'HTML kopieren',
  'eduflow.versionHistory.copyPlain': 'Nur Text kopieren',
  'eduflow.versionHistory.copyPublicPlayer': 'Öffentlichen Link kopieren',
  'eduflow.versionHistory.copyPublicPlayerError': 'Öffentlicher Link konnte nicht kopiert werden',
  'eduflow.versionHistory.count': '{{count}} Version(en)',
  'eduflow.versionHistory.createError': 'Fehler beim Erstellen der Version',
  'eduflow.versionHistory.createMajor': 'Hauptversion',
  'eduflow.versionHistory.createMajorSuccess': 'Hauptversion erstellt',
  'eduflow.versionHistory.createMinor': 'Nebenversion',
  'eduflow.versionHistory.createMinorSuccess': 'Nebenversion erstellt',
  'eduflow.versionHistory.daysAgo': 'vor {{count}} Tagen',
  'eduflow.versionHistory.deleteError': 'Fehler beim Löschen',
  'eduflow.versionHistory.deleteSuccess': 'Version gelöscht',
  'eduflow.versionHistory.deleteTitle': '{{label}} löschen?',
  'eduflow.versionHistory.deleteWarning': 'Diese Aktion kann nicht rückgängig gemacht werden.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplizieren',
  'eduflow.versionHistory.duplicateError': 'Fehler beim Duplizieren',
  'eduflow.versionHistory.duplicateMinor': 'Duplizieren (Neben-)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': '{{label}} duplizieren?',
  'eduflow.versionHistory.empty': 'Keine Versionen erstellt.',
  'eduflow.versionHistory.emptyHint': 'Speichern Sie eine Haupt- oder Nebenversion, um sie hier zu finden.',
  'eduflow.versionHistory.hoursAgo': 'vor {{count}} Std.',
  'eduflow.versionHistory.latest': 'Neueste',
  'eduflow.versionHistory.minutesAgo': 'vor {{count}} Min.',
  'eduflow.versionHistory.newFromCurrent': 'Neue Version aus aktuellem Inhalt',
  'eduflow.versionHistory.openPublicPlayer': 'Öffentlichen Player öffnen',
  'eduflow.versionHistory.preview': 'Vorschau',
  'eduflow.versionHistory.previewBlocks': '{{count}} Blöcke',
  'eduflow.versionHistory.previewEdges': '{{count}} Verbindungen',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Vorschau {{label}}',
  'eduflow.versionHistory.public': 'Öffentliche Version',
  'eduflow.versionHistory.publicActive': 'Aktiv für Lernende',
  'eduflow.versionHistory.setPublic': 'Aktivieren',
  'eduflow.versionHistory.startDescModalTitle': 'Beschreibung — Startblock ({{version}})',
  'eduflow.versionHistory.yesterday': 'gestern',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.settings': 'Settings',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Übersetzung läuft…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Zirkulärer Pfad erkannt',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} Fehler',
  'designer.validation.warningCount': '{{count}} Warnung(en)',
  'designer.validation.titleRequired': 'Der Block « {{type}} » benötigt einen Titel.',
  'designer.validation.summary': 'Ablauf: {{blocks}} Block/Blöcke, {{edges}} Verbindung(en).',
  'designer.validation.multipleStart': 'Mehrere Start-Blöcke ({{count}}): nur einer ist erlaubt.',
  'designer.validation.noOutgoing': 'Der Block « {{type}} » hat keine ausgehende Verbindung zu einem Folgeblock.',
  'designer.validation.tooManyOutputs': '« {{label}} »: zu viele ausgehende Verbindungen ({{count}}, Maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Zweig ohne Pfad zu einem End-Block (von « {{label}} »).',
  'designer.validation.moreDeadEnds': '… und {{count}} weitere(r) Zweig(e) ohne Ausgang zum Ende.',
  'designer.validation.noConvergence': 'Die Zweige von « {{label}} » laufen nicht in einem gemeinsamen Block zusammen.',
  'designer.validation.orphanBlock': 'Block vom Hauptgraphen isoliert: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Der End-Block « {{title}} » hat noch eine ausgehende Verbindung.',
  'designer.validation.noScoring': 'Bewertungseinstellungen für « {{title}} » nicht definiert.',
  'designer.validation.emptyText': 'Der Textblock « {{title}} » ist leer.',
  'designer.validation.textTooLong':
    'Textblock « {{title}} » wird auf {{lines}} Zeilen geschätzt. Empfohlen: {{min}} bis {{max}} Zeilen.',
  'designer.validation.splitTextBlock': 'Aufteilen',
  'designer.validation.textSplitNotNeeded':
    'Dieser Block überschreitet die empfohlene Leseschwelle nicht.',
  'designer.validation.textSplitUnable':
    'Für diesen Block konnte keine zuverlässige automatische Aufteilung ermittelt werden.',
  'designer.validation.textSplitComplexFlow':
    'Automatische Aufteilung für diesen Block nicht verfügbar (mehrere/bedingte Verzweigungen).',
  'designer.validation.textSplitDone': '{{count}} Textblöcke erstellt',
  'designer.validation.textSplitConfirmTitle':
    'Diesen Textblock automatisch aufteilen?',
  'designer.validation.textSplitConfirmMessage':
    'Der Block « {{title}} » wird auf {{lines}} Zeilen geschätzt. Er wird in {{parts}} Blöcke aufgeteilt (satzbasiert über Satzzeichen) und direkt darunter in Reihenfolge eingefügt.',
  'designer.validation.textSplitConfirmAction': 'Block aufteilen',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Standardstimme',
  'voicePanel.provider': 'Anbieter: Mistral AI',
  'voicePanel.hint': 'Wählen Sie die Stimme für die Blockerzählung.',
  'voicePanel.translationFrenchFallback':
    'Übersetztes Audio: Ohne hier gespeicherte Standardstimme wird für Französisch die Stimme „Marie - sad“ (Mistral) verwendet.',
  'voicePanel.search': 'Suchen…',
  'voicePanel.allLanguages': 'Alle Sprachen',
  'voicePanel.loading': 'Stimmen werden geladen…',
  'voicePanel.loadError': 'Stimmen konnten nicht geladen werden',
  'voicePanel.empty': 'Keine Stimmen verfügbar.',
  'voicePanel.emptyHint': 'Überprüfen Sie den Mistral-API-Schlüssel unter Administration → Integrationen.',
  'voicePanel.noResults': 'Keine Stimmen entsprechen Ihrer Suche.',
  'voicePanel.saved': 'Standardstimme gespeichert',
  'voicePanel.saveError': 'Fehler beim Speichern',
  'voicePanel.default': 'Standard',
  'voicePanel.female': 'Weiblich',
  'voicePanel.male': 'Männlich',
  'voicePanel.playPreview': 'Stimme testen',
  'voicePanel.stopPreview': 'Stoppen',
  'voicePanel.noPreview': 'Vorschau nicht verfügbar',
  'voicePanel.previewLoading': 'Wird erzeugt…',
  'voicePanel.previewGenerateError': 'Audiovorschau konnte nicht erzeugt werden.',
  'voicePanel.previewPlayError': 'Wiedergabe fehlgeschlagen.',
  'voicePanel.filterNoVoicesForLang':
    'Keine Stimme in diesem Katalog passt zu dieser Sprache — versuchen Sie „Alle Sprachen“.',
  'episodeEditor.voicePicker.blockVoice': 'Stimme für diesen Block',
  'episodeEditor.voicePicker.defaultVoice': 'Standardstimme',
  'episodeEditor.voicePicker.voices': 'Stimmen',
  'episodeEditor.voicePicker.searchPlaceholder': 'Stimme suchen…',
  'episodeEditor.voicePicker.noVoiceFound': 'Keine Stimme gefunden',
  'episodeEditor.voicePicker.female': 'Weiblich',
  'episodeEditor.voicePicker.male': 'Männlich',
  'episodeEditor.voicePicker.stop': 'Stopp',
  'episodeEditor.voicePicker.preview': 'Stimme testen',
  'episodeEditor.voicePicker.stopPlayback': 'Wiedergabe stoppen',
  'episodeEditor.errors.notFound': 'Episode nicht gefunden',
  'episodeEditor.errors.titleRequired': 'Der Titel ist erforderlich.',
  'episodeEditor.errors.saveFailed': 'Fehler beim Speichern.',
  'episodeEditor.errors.noVoicesAvailable': 'Keine Stimmen verfügbar. API-Schlüssel unter Administration → Plattform-Integrationen eingeben, dann Seite neu laden.',
  'episodeEditor.errors.selectVoiceFirst': 'Wählen Sie eine Stimme im Tab „Stimme", bevor Sie das Voiceover generieren.',
  'episodeEditor.errors.invalidBlockVoice': 'Blöcke mit ungültiger Stimme vorhanden. Öffnen Sie den Stimmenauswahl pro Block oder laden Sie die Liste neu.',
  'episodeEditor.errors.addTextFirst': 'Fügen Sie Text hinzu, bevor Sie Audio generieren.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audiogenerierung fehlgeschlagen. {{message}} (angezeigte Dauer = Schätzung.)',
  'episodeEditor.errors.audioGenerationFailed': 'Fehler bei der Audiogenerierung. Angezeigte Dauer = Schätzung.',
  'episodeEditor.success.published': 'Episode veröffentlicht!',
  'episodeEditor.success.draftSaved': 'Entwurf gespeichert',
  'episodeEditor.success.audioGenerated': 'Audio generiert mit {{count}} Block/Blöcken',
  'episodeEditor.tabs.edit': 'Bearbeiten',
  'episodeEditor.tabs.chapters': 'Kapitel',
  'episodeEditor.tabs.voice': 'Stimme',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Rückgängig (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Wiederherstellen (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generierung…',
  'episodeEditor.toolbar.generateAudio': 'Audio generieren',
  'episodeEditor.toolbar.save': 'Speichern',
  'episodeEditor.toolbar.publish': 'Veröffentlichen',
  'episodeEditor.titlePlaceholder': 'Episodentitel…',
  'episodeEditor.status.published': 'Veröffentlicht',
  'episodeEditor.status.draft': 'Entwurf',
  'episodeEditor.warnings.mistralNotConfigured': 'Episoden-Voiceover nutzt Mistral Voxtral TTS. Mistral-Schlüssel unter Administration → Plattform-Integrationen hinzufügen und Seite neu laden.',
  'episodeEditor.warnings.mistralNoVoices': 'Mistral-Schlüssel erkannt, aber API lieferte keine Stimmen. Backend-Logs prüfen.',
  'episodeEditor.warnings.voiceLoadFailed': 'Stimmenliste konnte nicht geladen werden. Mistral-Konfiguration prüfen.',
  'episodeEditor.warnings.voiceOffDisabled': 'Ohne echte Stimme bleibt die Voiceover-Generierung deaktiviert.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthese über Mistral Voxtral TTS. Wählen Sie die Stimme im Tab „Stimme" oder pro Block.',
  'episodeEditor.edit.coverImage': 'Titelbild',
  'episodeEditor.edit.coverAlt': 'Episoden-Cover',
  'episodeEditor.edit.deleteCover': 'Löschen',
  'episodeEditor.edit.dropOrClickImage': 'Klicken oder Bild ablegen',
  'episodeEditor.edit.selectedVoice': 'Ausgewählte Stimme',
  'episodeEditor.edit.selectVoice': 'Eine Stimme auswählen',
  'episodeEditor.edit.defaultVoiceByLang': 'Standardstimme (Podcast-Sprache)',
  'episodeEditor.edit.lastAudioGenerated': 'Zuletzt generiertes Audio',
  'episodeEditor.edit.audioNotFound': 'Audiodatei nicht gefunden — Voiceover erneut generieren.',
  'episodeEditor.edit.generatingInProgress': 'Generierung läuft…',
  'episodeEditor.edit.generateVoiceOff': 'Voiceover generieren',
  'episodeEditor.chapters.title': 'Kapitel',
  'episodeEditor.chapters.add': 'Hinzufügen',
  'episodeEditor.chapters.defaultChapterTitle': 'Einführung',
  'episodeEditor.chapters.newChapterTitle': 'Kapitel {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} Block/Blöcke',
  'episodeEditor.voiceTab.availableVoices': 'Verfügbare Stimmen',
  'episodeEditor.podcasts.linked': 'Verknüpfte Podcasts',
  'episodeEditor.podcasts.assign': 'Zuweisen',
  'episodeEditor.podcasts.noneAvailable': 'Kein Podcast verfügbar',
  'episodeEditor.podcasts.close': 'Schließen',
  'episodeEditor.podcasts.noneLinked': 'Kein verknüpfter Podcast',
  'episodeEditor.podcasts.assignHint': 'Weisen Sie diese Episode einem oder mehreren Podcasts zu',
  'episodeEditor.podcasts.remove': 'Entfernen',
  'episodeEditor.editor.addTextBlock': 'Textblock hinzufügen',
  'episodeEditor.editor.scriptPlaceholder': 'Beginnen Sie Ihr Skript…',
  'episodeEditor.editor.addBlock': 'Block hinzufügen',
  'episodeEditor.player.loop': 'Schleife',
  'episodeEditor.player.playbackSpeed': 'Wiedergabegeschwindigkeit',
  'episodeEditor.player.collapse': 'Verkleinern',
  'episodeEditor.player.expand': 'Vergrößern',
  'episodeEditor.player.waveformHidden': 'Wellenform ausgeblendet',
  'podcast.episodes.listen': 'Anhören',
  'podcast.episodes.noAudioShort': 'Kein Audio verfügbar',
  'podcast.episodes.edit': 'Bearbeiten',
  'podcast.episodes.delete': 'Löschen',
  'podcast.episodes.unmute': 'Ton aktivieren',
  'podcast.episodes.mute': 'Stummschalten',
  'podcast.episodes.totalEpisodes': '{{count}} Episode(n) insgesamt',
  'eduflow.designer.toast.blockCopied': 'Block kopiert',
  'eduflow.designer.toast.blockPasted': 'Block eingefügt',
  'eduflow.designer.toast.blockDuplicated': 'Block dupliziert',
  'eduflow.designer.toast.connectionUpdated': 'Verbindung aktualisiert',
  'eduflow.designer.toast.flowExported': 'Kurs exportiert',
  'eduflow.designer.toast.flowImported': 'Kurs importiert',
  'eduflow.designer.toast.invalidFile': 'Ungültige Datei',
  'eduflow.designer.untitled': 'Ohne Titel',
  'eduflow.designer.importedFlow': 'Importierter Kurs',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'Benutzer',
  'eduflow.designer.collaborator.editor': 'Bearbeiter',
  'eduflow.designer.collaborator.viewer': 'Betrachter',
  'eduflow.designer.resizePanel': 'Panel-Größe ändern',
  'eduflow.designer.published': 'Veröffentlicht',
  'eduflow.designer.draft': 'Entwurf',
  'eduflow.designer.quizOutput.success': 'Bestanden',
  'eduflow.designer.quizOutput.failure': 'Nicht bestanden',
  'eduflow.designer.handle.default': 'Standard',
  'eduflow.designer.genError': 'Fehler bei der Generierung. Bitte versuchen Sie es erneut.',
  'trainerTour.welcome.title': 'Willkommen in Ihrem Trainer-Bereich',
  'trainerTour.welcome.description':
    'Diese kurze Führung zeigt Dashboard und Menü. Beim ersten Besuch jeder Seite erscheint ein Hinweis mit dem Wichtigsten. Sie können einen Schritt mit dem X schließen — die Führung startet nicht erneut.',
  'trainerTour.dashboard.header.title': 'Ihr Dashboard',
  'trainerTour.dashboard.header.description':
    'Überblick über Ihre Aktivität: Kennzahlen und Kurzbefehle, um schnell Inhalte zu erstellen.',
  'trainerTour.dashboard.actions.title': 'Inhalte erstellen',
  'trainerTour.dashboard.actions.description':
    'Direktlinks zu neuem EduFlow-Kurs, Podcast oder Lernpfad — ohne Umweg über das Menü.',
  'trainerTour.dashboard.stats.title': 'Kennzahlen',
  'trainerTour.dashboard.stats.description':
    'Kurse, veröffentlichte Pfade, erreichte Lernende und Podcasts: den Umfang Ihres Katalogs auf einen Blick.',
  'trainerTour.dashboard.activity.title': 'Letzte Aktivität',
  'trainerTour.dashboard.activity.description':
    'Zuletzt bearbeitete Inhalte. Klicken Sie eine Zeile an, um Kurs, Podcast oder Pfad wieder zu öffnen.',
  'trainerTour.sidebar.title': 'Seitenmenü',
  'trainerTour.sidebar.description':
    'Übersicht (Start, Lernende, Analysen, Aufträge), Erstellung (Kurse, Podcasts, Pfade, Webinare falls aktiv), Medienbibliothek und Hilfe. Jeden Punkt einmal öffnen — beim ersten Mal erscheint ein Kontexthinweis.',
  'trainerTour.routes.learners.title': 'Lernende',
  'trainerTour.routes.learners.description':
    'Eingeschriebene Personen, Fortschritt, Exporte — Ihr Hub für die pädagogische Begleitung.',
  'trainerTour.routes.analytics.title': 'Analysen',
  'trainerTour.routes.analytics.description':
    'Diagramme zu Engagement und Abschluss. Passen Sie die Anordnung an, was für Sie zählt.',
  'trainerTour.routes.eduflow.title': 'Kurse (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Interaktive Kurse erstellen und verwalten: Versionen, Veröffentlichung und Freigabe — Kern der didaktischen Gestaltung.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Sendungen, Folgen, Sichtbarkeit und Freigabe für Gruppen oder Lernende Ihrer Einrichtung.',
  'trainerTour.routes.parcours.title': 'Lernpfade',
  'trainerTour.routes.parcours.description':
    'Abläufe aus Kursen und Ressourcen: längere Lernreisen strukturieren und Zielgruppen zuweisen.',
  'trainerTour.routes.videos.title': 'Videos',
  'trainerTour.routes.videos.description':
    'Wiederverwendbare Videobibliothek für Kurse und Dokumente.',
  'trainerTour.routes.sounds.title': 'Audio',
  'trainerTour.routes.sounds.description':
    'Audiodateien und Tags für Podcasts und Ton-Blöcke in Kursen.',
  'trainerTour.routes.images.title': 'Bilder',
  'trainerTour.routes.images.description':
    'Zentrale Bilder: Import, Ordner und Wiederverwendung im Content.',
  'trainerTour.routes.documents.title': 'Dokumente',
  'trainerTour.routes.documents.description':
    'PDFs und Anhänge, optional gezielt für Lernende freigeben.',
  'trainerTour.routes.help.title': 'Hilfe',
  'trainerTour.routes.help.description':
    'Artikel, Tutorials und Anleitungen zu LyrraStudio. Nutzen Sie die Hilfe, wenn eine Funktion unklar ist.',
  'trainerTour.routes.producerAssignments.title': 'Produktionsaufträge',
  'trainerTour.routes.producerAssignments.description':
    'Wenn die Personalabteilung Kataloginhalte zur Produktion zuweist, erscheinen sie hier mit Fortschritt.',
  'trainerTour.routes.webinars.title': 'Webinare',
  'trainerTour.routes.webinars.description':
    'Live-Sessions planen, Einladungen und Stream-Links für Ihre Gruppen.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Weiter',
  'trainerTour.driver.prev': 'Zurück',
  'trainerTour.driver.done': 'Fertig',
  'trainerTour.driver.gotIt': 'Verstanden',
  'player.lightMode': 'Heller Modus',
  'player.darkMode': 'Dunkler Modus',
};

const itTranslations: Record<string, string> = {
  'nav.home': 'Home',
  'nav.producerAssignments': 'Missioni di produzione',
  'nav.videos': 'Video',
  'nav.sounds': 'Audio',
  'nav.images': 'Immagini',
  'nav.documents': 'Documenti',
  'nav.podcasts': 'Podcast',
  'nav.parcours': 'Percorsi',
  'nav.eduflowList': 'Formazioni',
  'nav.myLearning': 'Il mio apprendimento',
  'nav.section.pilotage': 'Controllo',
  'nav.section.creation': 'Creazione',
  'nav.section.medias': 'Media',
  'nav.section.aide': 'Aiuto',
  'sidebar.brandTagline': 'Formazione',
  'sidebar.logoInstitutionAlt': 'Logo dell’organizzazione',
  'sidebar.expand': 'Espandi menu',
  'sidebar.collapse': 'Comprimi menu',
  'sidebar.expandAria': 'Espandi barra laterale',
  'sidebar.collapseAria': 'Comprimi barra laterale',
  'learner.sidebar.collapse': 'Comprimi',
  'learner.sidebar.expandAria': 'Espandi barra laterale',
  'learner.sidebar.collapseAria': 'Comprimi barra laterale',
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Benvenuto, {{name}}!',
  'dashboard.welcomeSub': 'Ecco una panoramica del tuo spazio di lavoro.',
  'dashboard.projects': 'Progetti',
  'dashboard.subtitle': 'Gestisci le tue formazioni e monitora i learner.',
  'dashboard.newProject': 'Nuova formazione',
  'dashboard.newEduFlow': 'Nuovo percorso',
  'dashboard.uploadMedia': 'Carica media',
  'dashboard.viewDocs': 'Documentazione',
  'dashboard.revenue': 'Ricavi',
  'dashboard.eduflows': 'Formazioni',
  'dashboard.learners': 'Studenti',
  'dashboard.recentActivity': 'Attività recenti',
  'dashboard.recentProjects': 'Progetti recenti',
  'dashboard.noRecentProjects': 'Nessun progetto recente',
  'dashboard.activity': 'Attività',
  'dashboard.activityEmpty': 'Nessuna attività recente su percorsi o podcast.',
  'dashboard.loadError': 'Impossibile caricare i dati della dashboard.',
  'dashboard.publishedFlows': 'Percorsi pubblicati',
  'dashboard.roleSpecific': 'Per questo ruolo è disponibile una dashboard dedicata.',
  'dashboard.kindPodcast': 'Podcast',
  'nav.analytics': 'Analisi',
  'pages.analytics.title': 'Analisi',
  'pages.analytics.description': 'Dashboard e statistiche',
  'pages.analytics.subtitle':
    'Filtra i dati, riordina i grafici trascinando e ridimensiona i widget secondo le tue esigenze.',
  'pages.analytics.filters.period': 'Periodo',
  'pages.analytics.filters.flow': 'Corso',
  'pages.analytics.filters.allFlows': 'Tutti i corsi',
  'pages.analytics.period.7d': 'Ultimi 7 giorni',
  'pages.analytics.period.30d': 'Ultimi 30 giorni',
  'pages.analytics.period.90d': 'Ultimi 90 giorni',
  'pages.analytics.period.365d': 'Ultimi 12 mesi',
  'pages.analytics.addChart': 'Aggiungi grafico',
  'pages.analytics.resetLayout': 'Ripristina layout',
  'pages.analytics.dragHint': 'Trascina per riordinare',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Rimuovi questo grafico',
  'pages.analytics.kind.completion_line': 'Completamento nel tempo',
  'pages.analytics.kind.learners_bar': 'Studenti per modulo',
  'pages.analytics.kind.status_pie': 'Distribuzione degli stati',
  'pages.analytics.kind.engagement_area': 'Tempo di coinvolgimento',
  'pages.analytics.series.completion': 'Tasso di completamento (%)',
  'pages.analytics.series.learners': 'Studenti',
  'pages.analytics.series.hours': 'Ore',
  'pages.analytics.status.completed': 'Completato',
  'pages.analytics.status.in_progress': 'In corso',
  'pages.analytics.status.dropped': 'Abbandonato',
  'pages.analytics.status.not_started': 'Non iniziato',
  'pages.analytics.loadError': 'Impossibile caricare i dati di analisi.',
  'pages.analytics.retry': 'Riprova',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'lun',
  'pages.analytics.mock.wd1': 'mar',
  'pages.analytics.mock.wd2': 'mer',
  'pages.analytics.mock.wd3': 'gio',
  'pages.analytics.mock.wd4': 'ven',
  'pages.analytics.mock.wd5': 'sab',
  'pages.analytics.mock.wd6': 'dom',
  'pages.analytics.emptyCharts': 'Aggiungi almeno un grafico per visualizzare i dati.',
  'pages.analytics.comingSoonDesc':
    'Visualizza statistiche sui corsi, tassi di completamento, coinvolgimento e prestazioni complessive.',
  'project.status.draft': 'Bozza',
  'project.status.published': 'Pubblicato',
  'project.status.archived': 'Archiviato',
  'eduflowList.status.draft': 'Bozza',
  'eduflowList.status.published': 'Pubblicato',
  'eduflowList.status.archived': 'Archiviato',
  'eduflowList.badge.enterpriseShell': 'Richiesta azienda',
  'eduflowList.badge.enterpriseLinked': 'Azienda',
  'eduflowList.badge.toProduce': 'Da produrre',
  'learner.sidebar.tagline': 'Studente',
  'player.next': 'Avanti',
  'player.image.clickForAnnotations': "Clicca sull'immagine per vedere le annotazioni",
  'player.previous': 'Indietro',
  'player.complete': 'Completa',
  'player.exit': 'Esci',
  'player.exitConfirmTitle': 'Uscire dalla formazione?',
  'player.exitConfirmMessage': 'Il tuo progresso sarà salvato. Sei sicuro di voler uscire?',
  'player.focusLostTitle': 'Formazione in pausa',
  'player.focusLostMessage': 'Hai lasciato la finestra della formazione. Il tempo è in pausa.',
  'player.focusLostResume': 'Clicca per riprendere',
  'player.focusLostRemaining': 'Uscite rimanenti prima del blocco: {{remaining}}',
  'player.focusBlockedTitle': 'Formazione bloccata',
  'player.focusBlockedMessage':
    'Hai superato il numero di uscite consentite ({{max}}). Non puoi più continuare questa formazione. Contatta il tuo formatore o referente.',
  'player.cancel': 'Annulla',
  'player.summary': 'Sommario',
  'player.contents': 'Contenuto',
  'player.progress': 'Progresso',
  'player.block': 'Blocco',
  'player.parallelBranches': 'Rami paralleli',
  'player.parallelBranchesTitle': 'Cosa segue?',
  'player.parallelBranchesHint': 'Le altre opzioni saranno disponibili dopo.',
  'player.parallelBranchShowFull': 'Mostra di più',
  'player.parallelBranchShowLess': 'Mostra meno',
  'player.parallelBranchChoose': 'Apri',
  'player.parallelBranchDismiss': 'Chiudi',
  'player.parallelBranchDismissHint':
    'Torna al blocco. Usa « Avanti » per scegliere un ramo.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Prendere appunti...',
  'player.noBlocks': 'Nessun blocco da visualizzare.',
  'player.contentTranslatingTitle': 'Adattamento dei contenuti alla tua lingua…',
  'player.contentTranslatingHint':
    'Traduzione in corso. Il primo caricamento può richiedere qualche istante in più.',
  'player.textBlockTipsHeading': 'Suggerimenti',
  'player.textBlockReadDone': 'Lettura completata',
  'player.defaultFlowTitle': 'Corso',
  'player.docTitle.error': 'Corso non disponibile',
  'player.docTitle.completed': 'Corso completato',
  'player.docTitle.loading': 'Caricamento del corso',
  'player.docTitle.fallback': 'Corso',
  'player.initError.title': 'Il corso è temporaneamente non disponibile',
  'player.initError.loadFailed': 'Impossibile caricare il corso al momento.',
  'player.initError.loadFailedWithDetail':
    'Impossibile caricare il corso al momento.{{detail}} Riprovare tra un attimo.',
  'player.initError.hintsIntro': 'Puoi:',
  'player.initError.hintRefresh': 'aggiornare la pagina;',
  'player.initError.hintConnection': 'controllare la connessione a Internet;',
  'player.initError.hintRetryLater': 'riprovare tra qualche minuto.',
  'api.error.network': 'Impossibile contattare il server. Controlla la connessione.',
  'api.error.unexpected': 'Si è verificato un errore imprevisto.',
  'api.error.badRequest': 'Richiesta non valida.',
  'api.error.forbidden': 'Accesso negato.',
  'api.error.notFound': 'Risorsa non trovata.',
  'api.error.conflict': 'Conflitto — questa risorsa esiste già.',
  'api.error.unprocessable': 'I dati inviati non sono validi.',
  'api.error.tooManyRequests': 'Troppe richieste. Riprova tra poco.',
  'api.error.server': 'Errore interno del server.',
  'api.error.badGateway': 'Servizio temporaneamente non disponibile.',
  'api.error.maintenance': 'Servizio in manutenzione.',
  'api.error.generic': 'Errore {{status}}',
  'player.textBlockReadMinutes': '{{count}} min di lettura',
  'player.textBlockWordCount_one': '{{count}} parola',
  'player.textBlockWordCount_other': '{{count}} parole',
  'player.textBlockNoContent': 'Nessun contenuto disponibile.',
  'player.blockUnknown': 'Blocco sconosciuto',
  'player.objectivesPanelTitle': 'Obiettivi didattici',
  'player.objectivesOpenLabel': 'Vedi obiettivi didattici',
  'player.objectivesCloseLabel': 'Chiudi pannello obiettivi',
  'player.objectivesAchievementRate': 'Tasso di conseguimento',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} obiettivi raggiunti',
  'player.objectivesNoneDefined': 'Nessun obiettivo definito per questo corso.',
  'player.objectiveCategory.savoir': 'Sapere',
  'player.objectiveCategory.savoirFaire': 'Saper fare',
  'player.objectiveCategory.savoirEtre': 'Saper essere',
  'player.objectiveLevel.connaissance': 'Conoscenza',
  'player.objectiveLevel.comprehension': 'Comprensione',
  'player.objectiveLevel.application': 'Applicazione',
  'player.objectiveLevel.analyse': 'Analisi',
  'player.objectiveLevel.synthese': 'Sintesi',
  'player.objectiveLevel.evaluation': 'Valutazione',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obbligatorio',
  'player.objectiveLinkedBlocks': 'Blocchi collegati',
  'player.objectiveCriterionScore': 'Punteggio ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Completamento blocco',
  'player.objectiveNoLinkedBlocks':
    'Nessun blocco collegato — il progresso globale non misura questo obiettivo.',
  'player.completion.congratulations': '🎉 Congratulazioni!',
  'player.completion.finished': '📋 Percorso completato',
  'player.completion.youCompleted': 'Hai completato',
  'player.completion.passed': 'Superato ✓',
  'player.completion.failed': 'Non superato ✗',
  'player.completion.requiredScore': 'Punteggio richiesto: {{score}}%',
  'player.completion.totalTime': 'Tempo totale',
  'player.completion.blocksCompleted': 'Blocchi completati',
  'player.completion.quizPassed': 'Quiz superati',
  'player.completion.successRate': 'Tasso di successo',
  'player.completion.blockCompleted': 'Completato ✓',
  'player.completion.blockFailed': 'Non superato ✗',
  'player.completion.blockSkipped': 'Saltato',
  'player.completion.detail': '📊 Dettaglio per blocco',
  'player.completion.type': 'Tipo',
  'player.completion.timeSpent': 'Tempo trascorso',
  'player.completion.downloadCertificate': '📄 Scarica il certificato',
  'player.completion.certificateSoon': 'Il certificato sarà disponibile a breve.',
  'player.completion.restart': '🔄 Ricomincia',
  'player.completion.viewDetails': '📊 Visualizza i dettagli',
  'player.completion.backToDashboard': '← Torna alla dashboard',
  'player.completion.embedDismissHint':
    'Hai completato il modulo. Per uscire usa la X in alto sulla finestra o il pulsante qui sotto.',
  'player.completion.embedDismissButton': 'Chiudi la finestra',
  'player.previewBanner': 'Modalità anteprima — questa vista riflette i dati reali della tua formazione',
  'player.closePreview': 'Chiudi anteprima',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Mostra pannello',
  'player.toggleSidebarHide': 'Nascondi pannello',
  'player.sidebarExpand': 'Espandi pannello',
  'player.sidebarCollapse': 'Comprimi pannello',
  'player.keyboardShortcuts': 'Scorciatoie da tastiera',
  'player.keyboardShortcutPrev': 'Blocco precedente',
  'player.keyboardShortcutNext': 'Blocco successivo',
  'player.keyboardShortcutsHint':
    'Attiva « Navigazione da tastiera » nel centro accessibilità (pulsante in basso a destra). Disattivata nei campi di testo.',
  'player.a11yCenter': 'Centro accessibilità',
  'player.reviewSummary': 'Vedi riepilogo',
  'player.viewResults': 'Vedi risultati',
  'player.submitFirst': 'Invia prima',
  'player.phraseRevealCompleteFirst': 'Visualizza prima tutte le frasi',
  'player.phraseReveal.emptyBlock': 'Nessuna frase configurata per questo blocco.',
  'player.phraseReveal.revealNextAria':
    'Fai clic o premi Spazio per mostrare la frase successiva',
  'player.phraseReveal.lastPhraseHint':
    'Ultima frase — usa « Avanti » per continuare il percorso.',
  'player.phraseReveal.morePhrasesHint': 'Clic, Spazio o freccia destra per la frase successiva',
  'player.phraseReveal.previousPhrases': 'Frasi precedenti ({{count}})',
  'player.certificateUnavailable': 'Il certificato sarà disponibile a breve.',
  'player.browser.embeddedContentTitle': 'Contenuto incorporato',
  'player.browser.noValidUrl': 'URL non valido',
  'player.browser.enterHttpsHint':
    "Inserisci un indirizzo che inizi con https:// (o http://) nelle impostazioni del blocco.",
  'player.browser.loadingContent': 'Caricamento contenuto…',
  'player.browser.defaultPageTitle': 'Pagina web incorporata',
  'player.browser.continue': 'Continua',
  'player.browser.stepSaved': 'Passaggio salvato',
  'player.browser.fullscreen': 'Schermo intero',
  'player.browser.exitFullscreen': 'Esci da schermo intero',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Episodio',
  'player.podcast.loadError': 'Impossibile caricare il podcast.',
  'player.podcast.loadingEpisodes': 'Caricamento episodi…',
  'player.podcast.playAll': 'Ascolta tutti',
  'player.podcast.nextEpisode': 'Episodio successivo',
  'player.podcast.episodesHeading': 'Episodi',
  'player.podcast.noEpisodes': 'Nessun episodio.',
  'player.podcast.noAudio': 'Nessun audio',
  'player.podcast.chainPlayback': 'Riproduzione in sequenza · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Seleziona un episodio dall’elenco o avvia «Ascolta tutti».',
  'player.podcast.audioUnavailable': 'Audio non disponibile per questo episodio.',
  'player.podcast.episodeCount_one': '{{count}} episodio',
  'player.podcast.episodeCount_other': '{{count}} episodi',
  'player.renderError': 'Errore durante il rendering del blocco: {{blockType}}',
  'player.blocks.defaultWelcome': 'Benvenuto in questa formazione',
  'player.blocks.defaultEndTitle': 'Complimenti!',
  'player.quiz.selectCorrectAnswer': 'Seleziona la risposta corretta:',
  'player.quiz.correctAnswer': '✓ Risposta corretta!',
  'player.quiz.incorrectAnswer': '✗ Risposta errata.',
  'player.blockTimer.aria': 'Tempo per questo blocco',
  'player.blockTimer.modeShortCountup': 'Cronometro',
  'player.blockTimer.modeShortCountdown': 'Conto alla rovescia',
  'player.form.successMessage': 'Grazie per la tua risposta!',
  'player.form.noFieldsConfigured': 'Questo modulo non ha ancora campi configurati.',
  'player.evaluation.commentsLabel': 'Commenti (opzionale)',
  'player.evaluation.commentsPlaceholder': 'Condividi il tuo feedback...',
  'player.evaluation.submitted': 'Valutazione inviata',
  'player.evaluation.submit': 'Invia valutazione',
  'player.time.lessThanMinute': 'Meno di un minuto',
  'player.time.oneMinute': '1 minuto',
  'player.time.minutes': '{{count}} minuti',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}} min',
  'player.time.shortMinutesSeconds': '{{minutes}} min {{seconds}}s',
  'player.completion.scoreLabel': 'Punteggio',
  'accessibility.openCenter': 'Apri il centro accessibilità',
  'accessibility.center': 'Accessibilità',
  'accessibility.modalTitle': 'Centro di lettura adattata',
  'accessibility.modalDescription': "Adatta l'interfaccia per ipovedenti, dislessia e una lettura più comoda.",
  'accessibility.closeCenter': 'Chiudi il centro accessibilità',
  'accessibility.accessPad.connected': 'Lyrra Access Pad connesso',
  'accessibility.accessPad.playGuide': 'Ascolta la guida LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Metti in pausa la guida LyrraPad',
  'accessibility.presets': 'Profili predefiniti',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': "Visualizzazione predefinita dell'applicazione.",
  'accessibility.preset.lowVision.label': 'Ipovedenti',
  'accessibility.preset.lowVision.desc': 'Contrasto più forte, testo più grande e segnali visivi più chiari.',
  'accessibility.preset.dys.label': 'Dislessia',
  'accessibility.preset.dys.desc': 'Lettura più facile con più spazio e un carattere più leggibile.',
  'accessibility.typography': 'Impostazioni tipografiche',
  'accessibility.fontScale': 'Dimensione del testo',
  'accessibility.lineHeight': 'Interlinea',
  'accessibility.letterSpacing': 'Spaziatura tra le lettere',
  'accessibility.wordSpacing': 'Spaziatura tra le parole',
  'accessibility.visualAids': 'Ausili visivi',
  'accessibility.highContrast': 'Alto contrasto',
  'accessibility.highContrastDesc': 'Rafforza il contrasto di lettura, i bordi e i segnali visivi.',
  'accessibility.underlineLinks': 'Link sottolineati',
  'accessibility.underlineLinksDesc': 'Rende più evidenti i link e le aree interattive.',
  'accessibility.dyslexiaFont': 'Carattere di lettura facilitata',
  'accessibility.dyslexiaFontDesc': 'Usa un carattere più comodo per chi ha dislessia e per lettori affaticati.',
  'accessibility.reducedMotion': 'Movimento ridotto',
  'accessibility.reducedMotionDesc': 'Riduce animazioni e transizioni per limitare il disagio visivo.',
  'accessibility.mouseMagnifier': 'Lente di ingrandimento',
  'accessibility.mouseMagnifierDesc':
    'Mostra una lente circolare che segue il puntatore e ingrandisce il contenuto del percorso (solo lettura).',
  'accessibility.alwaysOn': 'Miglioramenti sempre attivi',
  'accessibility.alwaysOnFocus': 'Focus da tastiera più visibile',
  'accessibility.alwaysOnLinks': 'Link ed elementi interattivi più facili da individuare',
  'accessibility.alwaysOnSaved': 'Impostazioni salvate automaticamente su questo dispositivo',
  'accessibility.navigation': 'Navigazione',
  'accessibility.keyboardNavigation': 'Navigazione da tastiera',
  'accessibility.keyboardNavigationDesc':
    'Freccia sinistra/su per il blocco precedente, destra/giù per il successivo (come i pulsanti in basso).',
  // ── Injected from locales/it.json ──
  'producerAssignments.createInDesigner': 'Crea / apri formazione',
  'producerAssignments.draft': 'Bozza',
  'producerAssignments.enterprise': 'Azienda',
  'producerAssignments.enterprisePaths': 'Percorsi aziendali',
  'producerAssignments.episodes': 'ep.',
  'producerAssignments.errorForbidden': 'Non hai accesso a questa pagina.',
  'producerAssignments.errorGeneric': 'Impossibile caricare le tue missioni.',
  'producerAssignments.errorNoInstitution': 'Non sei associato a nessun istituto.',
  'producerAssignments.formations': 'Schede formazione',
  'producerAssignments.intro': 'Questa pagina elenca le schede formazione e i podcast assegnati dalla direzione risorse umane del tuo istituto. Crea i tuoi contenuti nel designer EduFlow o nello studio podcast, poi aggiorna lo stato di produzione.',
  'producerAssignments.markCompleted': 'Segna come completato',
  'producerAssignments.markInProgress': 'In produzione',
  'producerAssignments.markReady': 'Pronto',
  'producerAssignments.modulesCatalog': '{{count}} modulo/i catalogo · aggiornato {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Nessuna richiesta di percorso aziendale.',
  'producerAssignments.noneFormations': 'Nessuna scheda assegnata.',
  'producerAssignments.nonePodcasts': 'Nessun podcast assegnato.',
  'producerAssignments.openInDesigner': 'Apri nel designer',
  'producerAssignments.openPodcasts': 'Studio podcast',
  'producerAssignments.planningDeadline': 'Scadenza {{date}}',
  'producerAssignments.planningFrom': 'A partire dal {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcast da produrre',
  'producerAssignments.productionStatus.completed': 'Completato',
  'producerAssignments.productionStatus.in_progress': 'In corso',
  'producerAssignments.productionStatus.not_started': 'Non iniziato',
  'producerAssignments.productionStatus.published': 'Pubblicato',
  'producerAssignments.publishedCatalog': 'Pubblicato nel catalogo',
  'producerAssignments.rhPlanning': 'Pianificazione HR',
  'producerAssignments.subtitle': 'Formazioni da creare nel designer e podcast da produrre, assegnati dalle risorse umane.',
  'producerAssignments.title': 'Le mie missioni di produzione',
  'podcasts.actions.cancel': 'Annulla',
  'podcasts.actions.close': 'Chiudi',
  'podcasts.actions.create': 'Crea',
  'podcasts.actions.edit': 'Modifica',
  'podcasts.actions.retry': 'Riprova',
  'podcasts.actions.save': 'Salva',
  'podcasts.actions.saving': 'Salvataggio…',
  'podcasts.actions.share': 'Condividi',
  'podcasts.category.ariaGroup': 'Categoria del podcast',
  'podcasts.category.emptyBanner': 'Aggiungi categorie nello spazio Istituzione per organizzare meglio i podcast.',
  'podcasts.category.emptyInstitutionHint': 'Nessuna categoria istituzione per ora — puoi pubblicare senza.',
  'podcasts.category.noneChip': 'Senza categoria',
  'podcasts.category.sectionTitle': 'Categoria',
  'podcasts.category.withCategoriesHint': 'Classificazione nel catalogo e filtri lato istituzione.',
  'podcasts.cover.ariaDropzone': 'Scegli o trascina un\'immagine di copertina',
  'podcasts.cover.browseFiles': 'sfoglia i file',
  'podcasts.cover.cancelNewFile': 'Annulla il nuovo file',
  'podcasts.cover.dragDrop': 'Trascina un\'immagine',
  'podcasts.cover.dropHere': 'Rilascia l\'immagine qui',
  'podcasts.cover.errorMaxSize': 'L\'immagine non deve superare i 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Formato immagine non supportato.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max 5 MB',
  'podcasts.cover.help': 'Formato quadrato consigliato. Trascina un file o clicca per sfogliare.',
  'podcasts.cover.or': 'o',
  'podcasts.cover.replaceHint': 'Sostituisci l\'immagine',
  'podcasts.cover.sectionTitle': 'Immagine di copertina',
  'podcasts.deleteDialog.body': '« {{title}} » verrà rimosso dall\'elenco (eliminazione logica). Gli episodi non vengono cancellati. Se il tuo account è collegato a un ente, un amministratore può ripristinare il podcast dal cestino dello spazio Istituzione.',
  'podcasts.deleteDialog.confirm': 'Sposta nel cestino',
  'podcasts.deleteDialog.title': 'Spostare il podcast nel cestino?',
  'podcasts.editPanel.createdOn': 'Creato il ',
  'podcasts.editPanel.headerTitle': 'Modifica podcast',
  'podcasts.editPanel.saving': 'Salvataggio…',
  'podcasts.editPanel.statsEpisodes': 'Episodi: ',
  'podcasts.editPanel.statsSubscribers': 'Abbonati: ',
  'podcasts.editPanel.successSaved': 'Modifiche salvate',
  'podcasts.episodeStatus.draft': 'Bozza',
  'podcasts.episodeStatus.published': 'Pubblicato',
  'podcasts.episodesPanel.addEpisode': 'Aggiungi un episodio',
  'podcasts.episodesPanel.emptySubtitle': 'Aggiungi episodi a questo podcast',
  'podcasts.episodesPanel.emptyTitle': 'Nessun episodio',
  'podcasts.episodesPanel.episodeCount': '{{count}} episodi',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Nessun episodio disponibile',
  'podcasts.episodesPanel.remove': 'Rimuovi',
  'podcasts.episodesPanel.searchPlaceholder': 'Cerca un episodio…',
  'podcasts.episodesPanel.title': 'Episodi del podcast',
  'podcasts.error.retry': 'Riprova',
  'podcasts.filters.allCategories': 'Tutte le categorie',
  'podcasts.filters.categoryLabel': 'Categoria',
  'podcasts.filters.searchLabel': 'Ricerca testuale',
  'podcasts.filters.searchPlaceholder': 'Titolo, descrizione…',
  'podcasts.filters.viewCategories': 'Vista categorie',
  'podcasts.filters.viewList': 'Vista elenco',
  'podcasts.form.descriptionLabel': 'Descrizione',
  'podcasts.form.descriptionPlaceholder': 'Descrivi il tuo podcast…',
  'podcasts.form.namePlaceholder': 'Nome del podcast',
  'podcasts.form.titleLabel': 'Titolo',
  'podcasts.language.ariaGroup': 'Lingua del podcast',
  'podcasts.language.help': 'Tra le 11 lingue della piattaforma — per allineare il podcast alla stessa locale del lettore e dell\'interfaccia dell\'apprendente.',
  'podcasts.language.label': 'Lingua',
  'podcasts.lightbox.ariaClose': 'Chiudi',
  'podcasts.lightbox.ariaDialog': 'Anteprima copertina podcast',
  'podcasts.list.actionActivate': 'Attiva',
  'podcasts.list.actionDeactivate': 'Disattiva',
  'podcasts.list.actionDelete': 'Elimina',
  'podcasts.list.actionManageEpisodes': 'Gestisci episodi',
  'podcasts.list.actionPreview': 'Anteprima e ascolto',
  'podcasts.list.coverPreviewAria': 'Anteprima copertina podcast {{title}}',
  'podcasts.list.createFirst': 'Crea un podcast',
  'podcasts.list.emptySubtitle': 'Crea il tuo primo podcast per iniziare',
  'podcasts.list.emptyTitle': 'Nessun podcast',
  'podcasts.list.loadError': 'Impossibile caricare i podcast.',
  'podcasts.list.enterpriseBadge': 'Azienda',
  'podcasts.list.enterpriseNeedTitle': 'Bisogno definito dalla tua azienda',
  'podcasts.list.episodesShort': '{{count}} ep.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast',
  'podcasts.list.subscribersShort': '{{count}} abb.',
  'podcasts.list.uncategorized': 'Senza categoria',
  'podcasts.modal.errorRequiredFields': 'Titolo e descrizione sono obbligatori.',
  'podcasts.modal.errorSaveFailed': 'Errore durante il salvataggio.',
  'podcasts.modal.subtitleNew': 'Inserisci titolo, descrizione e opzioni. Potrai aggiungere episodi in seguito.',
  'podcasts.modal.titleEdit': 'Modifica podcast',
  'podcasts.modal.titleNew': 'Nuovo podcast',
  'podcasts.page.episodesLink': 'Episodi',
  'podcasts.page.newButton': 'Nuovo',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast',
  'podcasts.pagination.pageOf': 'Pagina {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast in totale',
  'podcasts.player.listen': 'Ascolta',
  'podcasts.player.noAudio': 'Nessun audio',
  'podcasts.player.pause': 'Pausa',
  'podcasts.preview.emptySubtitle': 'Questo podcast non ha ancora episodi',
  'podcasts.preview.emptyTitle': 'Nessun episodio',
  'podcasts.preview.episodeCount': '{{count}} episodi',
  'podcasts.preview.fallbackDescription': 'Ascolta gli episodi audio disponibili.',
  'podcasts.preview.playAll': 'Ascolta tutto',
  'podcasts.preview.title': 'Anteprima del podcast',
  'podcasts.public': 'Podcast',
  'podcasts.public.subscribeButton': 'Iscriviti agli aggiornamenti',
  'podcasts.public.subscribeModalTitle': 'Ricevi i nuovi episodi via email',
  'podcasts.public.subscribeModalIntro': 'Inserisci i tuoi dati. Ti invieremo un’email quando sarà pubblicato un nuovo episodio.',
  'podcasts.public.fieldFirstName': 'Nome',
  'podcasts.public.fieldLastName': 'Cognome',
  'podcasts.public.fieldEmail': 'Email',
  'podcasts.public.fieldCompany': 'Azienda (facoltativo)',
  'podcasts.public.submitSubscribe': 'Conferma iscrizione',
  'podcasts.public.cancel': 'Annulla',
  'podcasts.public.done': 'Fatto',
  'podcasts.public.subscribeSuccess': 'Grazie! Sarai informato per ogni nuovo episodio.',
  'podcasts.public.subscribeError': 'Impossibile completare l’iscrizione. Riprova più tardi.',
  'podcasts.public.subscribeValidationRequired': 'Nome, cognome ed email sono obbligatori.',
  'podcasts.share.assignedLearnersHeading': '{{count}} apprendenti assegnati',
  'podcasts.share.copied': 'Copiato',
  'podcasts.share.copyLink': 'Copia il link',
  'podcasts.share.emailPlaceholder': 'Email *',
  'podcasts.share.errorCreateLearner': 'Impossibile creare l\'apprendente.',
  'podcasts.share.errorPublicLink': 'Impossibile generare il link pubblico.',
  'podcasts.share.errorRemoveLearner': 'Impossibile rimuovere l\'apprendente.',
  'podcasts.share.errorShare': 'Impossibile condividere questo podcast.',
  'podcasts.share.firstNamePlaceholder': 'Nome *',
  'podcasts.share.generatingLink': 'Generazione del link…',
  'podcasts.share.hideQuickCreate': 'Nascondi creazione rapida',
  'podcasts.share.lastNamePlaceholder': 'Cognome *',
  'podcasts.share.loadingAssigned': 'Caricamento…',
  'podcasts.share.loadingLearners': 'Caricamento apprendenti...',
  'podcasts.share.messageAccessRemoved': 'Accesso rimosso per l\'apprendente.',
  'podcasts.share.messageCreatedAndShared': 'Account creato/collegato, podcast condiviso e email di invito inviata.',
  'podcasts.share.messageShared': 'Podcast condiviso e invito inviato all\'apprendente.',
  'podcasts.share.noAssignedLearners': 'Nessun apprendente assegnato a questo podcast.',
  'podcasts.share.noLearnerAvailable': 'Nessun apprendente disponibile.',
  'podcasts.share.panelTitle': 'Condividi il podcast',
  'podcasts.share.publicLinkSection': 'Link pubblico (senza autenticazione)',
  'podcasts.share.removeAccessTitle': 'Rimuovi l\'accesso',
  'podcasts.share.searchLearnerPlaceholder': 'Cerca un apprendente (nome, cognome, email)',
  'podcasts.share.selectExistingLearnerLabel': 'Seleziona un apprendente esistente',
  'podcasts.share.showQuickCreate': 'Crea un nuovo apprendente',
  'podcasts.share.submitCreateAndInvite': 'Crea, condividi e invia l\'invito',
  'podcasts.share.socialNetworksHeading': 'Condividi sui social',
  'podcasts.share.shareLinkedIn': 'Condividi su LinkedIn',
  'podcasts.share.shareFacebook': 'Condividi su Facebook',
  'podcasts.share.shareTwitter': 'Condividi su X (Twitter)',
  'podcasts.status.active': 'Attivo',
  'podcasts.status.inactive': 'Inattivo',
  'learningPaths.card.contents': '{{count}} contenuto/i',
  'learningPaths.card.delete': 'Elimina',
  'learningPaths.card.edit': 'Modifica',
  'learningPaths.card.learners': '{{count}} partecipante/i',
  'learningPaths.card.publish': 'Pubblica',
  'learningPaths.card.unpublish': 'Annulla pubblicazione',
  'learningPaths.card.updatedAt': 'Aggiornato il {{date}}',
  'learningPaths.createPanel.cancel': 'Annulla',
  'learningPaths.createPanel.description': 'Assegna un nome al percorso; potrai aggiungere podcast e formazioni.',
  'learningPaths.createPanel.descriptionLabel': 'Descrizione',
  'learningPaths.createPanel.descriptionPlaceholder': 'Obiettivo del percorso, pubblico di riferimento…',
  'learningPaths.createPanel.nameLabel': 'Nome del percorso',
  'learningPaths.createPanel.namePlaceholder': 'Es.: Percorso di onboarding HR…',
  'learningPaths.createPanel.submit': 'Crea percorso',
  'learningPaths.createPanel.title': 'Nuovo percorso',
  'learningPaths.deleteDialog.body': 'Stai per eliminare « {{title}} ». Questa azione è irreversibile.',
  'learningPaths.deleteDialog.cancel': 'Annulla',
  'learningPaths.deleteDialog.confirm': 'Elimina',
  'learningPaths.deleteDialog.title': 'Elimina percorso',
  'learningPaths.empty.createFirst': 'Crea un percorso',
  'learningPaths.empty.noPathsHint': 'Crea il tuo primo percorso combinando podcast e formazioni.',
  'learningPaths.empty.noPathsYet': 'Nessun percorso al momento',
  'learningPaths.empty.noResults': 'Nessun risultato',
  'learningPaths.empty.noResultsHint': 'Modifica i filtri o crea un nuovo percorso.',
  'learningPaths.error.loadFailed': 'Impossibile caricare i percorsi',
  'learningPaths.error.retry': 'Riprova',
  'learningPaths.filters.allStatuses': 'Tutti gli stati',
  'learningPaths.filters.draft': 'Bozza',
  'learningPaths.filters.published': 'Pubblicato',
  'learningPaths.filters.searchPlaceholder': 'Cerca un percorso…',
  'learningPaths.page.newButton': 'Nuovo percorso',
  'learningPaths.page.subtitle': 'Crea percorsi di formazione ordinati',
  'learningPaths.page.title': 'Percorsi',
  'learningPaths.page.totalCount': '{{count}} percorsi',
  'learningPaths.pagination.next': 'Successivo',
  'learningPaths.pagination.pageOf': 'Pagina {{page}} / {{total}} · {{count}} percorsi',
  'learningPaths.pagination.previous': 'Precedente',
  'learningPaths.status.draft': 'Bozza',
  'learningPaths.status.published': 'Pubblicato',
  'webinars.card.code': 'Codice {{code}}',
  'webinars.card.copyLink': 'Copia link',
  'webinars.card.delete': 'Elimina',
  'webinars.card.edit': 'Modifica',
  'webinars.card.openRoom': 'Apri la sala',
  'webinars.card.publicSignupBadge': 'Iscrizione pubblica',
  'webinars.deleteDialog.body': 'Eliminare «{{title}}»? Questa azione è irreversibile.',
  'webinars.deleteDialog.cancel': 'Annulla',
  'webinars.deleteDialog.confirm': 'Elimina',
  'webinars.deleteDialog.title': 'Elimina il webinar',
  'webinars.eligibility.default': 'I webinar non sono disponibili per il tuo account (verifica la connessione all\'API o contatta l\'amministratore).',
  'webinars.eligibility.moduleDisabled': 'Il modulo webinar non è ancora attivato per il tuo ente. Un amministratore deve selezionare «Attiva webinar» in Ente → Webinar.',
  'webinars.eligibility.noInstitution': 'Nessun ente è associato al tuo account.',
  'webinars.eligibility.notAuthorized': 'Il tuo account non è nell\'elenco degli animatori autorizzati. Chiedi all\'ente di aggiungerti (Gestione webinar → Animatori autorizzati).',
  'webinars.eligibility.roleNotAllowed': 'Il tuo ruolo non consente di condurre webinar con questo account.',
  'webinars.empty.createFirst': 'Crea un webinar',
  'webinars.empty.noResults': 'Nessun risultato',
  'webinars.empty.noResultsHint': 'Modifica la ricerca.',
  'webinars.empty.noWebinarsHint': 'Crea una sala e invita i partecipanti dal pannello laterale.',
  'webinars.empty.noWebinarsYet': 'Nessun webinar per ora',
  'webinars.externalInvite.closeButton': 'Chiudi',
  'webinars.externalInvite.description': 'Persone non presenti nell\'elenco allievi: ogni indirizzo riceve un\'e-mail con il link della sala e un allegato per il calendario.',
  'webinars.externalInvite.done': 'Fatto.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} già invitato',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} già invitati',
  'webinars.externalInvite.emailsHint': 'Un indirizzo per riga, o separati da virgole. Duplicati e inviti già inviati vengono ignorati.',
  'webinars.externalInvite.emailsLabel': 'Indirizzi e-mail',
  'webinars.externalInvite.emailsPlaceholder': 'mario.rossi@esempio.it\nlaura@dominio.org',
  'webinars.externalInvite.errorMax200': 'Massimo 200 indirizzi per invio.',
  'webinars.externalInvite.errorMinOne': 'Inserisci almeno un indirizzo e-mail valido.',
  'webinars.externalInvite.errorsLabel': 'errori: {{errors}}',
  'webinars.externalInvite.sendButton': 'Invia inviti',
  'webinars.externalInvite.sendFailed': 'Invio non riuscito',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail inviata',
  'webinars.externalInvite.sentCount_other': '{{count}} e-mail inviate',
  'webinars.externalInvite.skippedCount_one': '{{count}} ignorata',
  'webinars.externalInvite.skippedCount_other': '{{count}} ignorate',
  'webinars.externalInvite.title': 'Inviti via e-mail',
  'webinars.invite.duplicatesCount_one': '{{count}} già invitato',
  'webinars.invite.duplicatesCount_other': '{{count}} già invitati',
  'webinars.invite.errorsLabel': 'errori: {{errors}}',
  'webinars.invite.externalButton': 'Fuori elenco (e-mail)',
  'webinars.invite.failed': 'Operazione non riuscita',
  'webinars.invite.groupSuccess': 'Inviti di gruppo elaborati (e-mail inviate ai membri).',
  'webinars.invite.inviteButton': 'Invita',
  'webinars.invite.learnersHelp': 'Cerca un allievo del tuo ente: viene inviata un\'<strong>e-mail di invito</strong> (link + agenda) a ogni invito.',
  'webinars.invite.learnersTitle': 'Allievi',
  'webinars.invite.noCandidates': 'Nessun allievo da mostrare (prova un\'altra ricerca o usa «Fuori elenco» per e-mail esterne).',
  'webinars.invite.noInvitations': 'Nessun invito registrato.',
  'webinars.invite.searchLabel': 'Cerca un allievo',
  'webinars.invite.searchPlaceholder': 'Nome o e-mail…',
  'webinars.invite.searching': 'Ricerca…',
  'webinars.invite.sentCount_one': '{{count}} invito inviato (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} inviti inviati (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Inviti inviati',
  'webinars.page.newButton': 'Nuovo webinar',
  'webinars.page.searchPlaceholder': 'Cerca…',
  'webinars.page.subtitle': 'Elenco delle tue sale — apri il pannello per creare, modificare o invitare i partecipanti.',
  'webinars.page.title': 'Webinar',
  'webinars.panel.backgroundHint': 'URL https (o http) mostrato dietro il contenuto condiviso per i partecipanti. Lascia vuoto per usare il visual Lyrra integrato.',
  'webinars.panel.backgroundLabel': 'Immagine di sfondo (vista partecipante)',
  'webinars.panel.backgroundPlaceholder': 'https://… (vuoto = sfondo Lyrra predefinito)',
  'webinars.panel.copyLink': 'Copia link',
  'webinars.panel.createButton': 'Crea',
  'webinars.panel.createDescription': 'Compila le informazioni e salva. Potrai invitare i partecipanti in seguito.',
  'webinars.panel.createTitle': 'Nuovo webinar',
  'webinars.panel.dateLabel': 'Giorno (opzionale)',
  'webinars.panel.deleteButton': 'Elimina',
  'webinars.panel.descriptionLabel': 'Descrizione',
  'webinars.panel.durationError': 'Indica una durata nel formato ore:minuti (es. 1:00 o 0:45).',
  'webinars.panel.durationHint': 'Obbligatorio se è indicato un giorno. Es. 1:30 per 1 h 30, 0:45 per 45 min.',
  'webinars.panel.durationLabel': 'Durata (h:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Caricamento…',
  'webinars.panel.openRoom': 'Apri la sala',
  'webinars.panel.presentersLabel': 'Co-presentatori',
  'webinars.panel.roomCode': 'Codice sala:',
  'webinars.panel.timeHint': 'Se vuoto: 9:00.',
  'webinars.panel.timeLabel': 'Ora di inizio',
  'webinars.panel.titleLabel': 'Titolo',
  'webinars.panel.waitingHtmlLabel': 'HTML sala d\'attesa (opzionale)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Benvenuto…</p>',
  'webinars.panel.publicAccessTitle': 'Iscrizione pubblica (modulo)',
  'webinars.panel.publicAccessHelp':
    'I partecipanti non co-presentatori compilano un modulo dopo l’accesso a Lyrra: e-mail, password (a loro scelta) e campi aggiuntivi che aggiungi qui sotto.',
  'webinars.panel.publicFormFixedFieldsHint':
    'E-mail e password (min. 8 caratteri) sono sempre richieste; qui aggiungi altri campi facoltativi o obbligatori.',
  'webinars.panel.publicFormAddFieldButton': 'Aggiungi campo',
  'webinars.panel.publicFormFieldNumber': 'Campo {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Etichetta mostrata',
  'webinars.panel.publicFormFieldType': 'Tipo',
  'webinars.panel.publicFormFieldTypeText': 'Testo breve',
  'webinars.panel.publicFormFieldTypeTextarea': 'Testo lungo',
  'webinars.panel.publicFormFieldRequired': 'Obbligatorio',
  'webinars.panel.publicFormFieldRemove': 'Rimuovi',
  'webinars.panel.publicFormFieldIdHint': 'Identificativo tecnico:',
  'webinars.panel.publicFormFieldLabelRequired': 'Ogni campo deve avere un’etichetta.',
  'webinars.panel.publicAccessLoginLabel': 'Accesso nel modulo di iscrizione',
  'webinars.panel.publicAccessPasswordLabel': 'Password del modulo di iscrizione',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Almeno 8 caratteri',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Lascia vuoto per mantenere la password attuale',
  'webinars.panel.publicAccessPasswordHint':
    'In creazione o alla prima attivazione: password obbligatoria (min. 8). Poi: lascia vuoto per non modificarla.',
  'webinars.panel.publicAccessLoginRequired': 'Indica un accesso per l’iscrizione pubblica.',
  'webinars.panel.publicAccessPasswordRequired': 'Imposta una password di almeno 8 caratteri.',
  'webinars.panel.publicAccessPasswordMin': 'La password pubblica deve contenere almeno 8 caratteri.',
  'webinars.status.draft': 'Bozza',
  'webinars.status.ended': 'Terminato',
  'webinars.status.live': 'In diretta',
  'webinars.status.scheduled': 'Programmato',
  'videos.addPanel.cancel': 'Annulla',
  'videos.addPanel.continue': 'Continua',
  'videos.addPanel.description': 'Importa MP4, WebM o MOV per usarli nelle tue formazioni.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Clic per scegliere un video',
  'videos.addPanel.title': 'Aggiungi un video',
  'videos.addPanel.titleLabel': 'Titolo',
  'videos.addPanel.titlePlaceholder': 'Titolo del video…',
  'videos.card.delete': 'Elimina',
  'videos.card.edit': 'Modifica',
  'videos.card.noThumbnail': 'Nessuna miniatura',
  'videos.card.publishedStudio': 'Pubblicato (modificabile in Studio)',
  'videos.deleteDialog.body': '« {{title}} » verrà eliminato definitivamente.',
  'videos.deleteDialog.cancel': 'Annulla',
  'videos.deleteDialog.confirm': 'Elimina',
  'videos.deleteDialog.title': 'Eliminare il video?',
  'videos.empty.addButton': 'Aggiungi un video',
  'videos.empty.description': 'Importa il tuo primo video per gestirlo e usarlo nelle tue formazioni.',
  'videos.empty.title': 'Nessun video',
  'videos.page.addButton': 'Aggiungi un video',
  'videos.page.count_one': '{{count}} video nella tua libreria',
  'videos.page.count_other': '{{count}} video nella tua libreria',
  'videos.page.title': 'Video',
  'videos.preview.noFile': 'Nessun file disponibile.',
  'videos.status.draft': 'Bozza',
  'videos.status.published': 'Pubblicato',
  'sounds.card.delete': 'Elimina',
  'sounds.card.mute': 'Silenzia',
  'sounds.deleteDialog.body': '« {{title}} » verrà eliminato definitivamente dal server e dalla tua libreria.',
  'sounds.deleteDialog.cancel': 'Annulla',
  'sounds.deleteDialog.confirm': 'Elimina',
  'sounds.deleteDialog.title': 'Eliminare questo suono?',
  'sounds.empty.description': 'Importa i tuoi file: vengono registrati nel database e copiati sul server così non perdi nulla.',
  'sounds.empty.importButton': 'Importa un suono',
  'sounds.empty.title': 'Nessun suono importato',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Annulla',
  'sounds.importPanel.changeFile': 'Cambia file',
  'sounds.importPanel.createTag': 'Crea « {{tag}} »',
  'sounds.importPanel.description': 'Aggiungi un file audio alla tua libreria per i tuoi percorsi.',
  'sounds.importPanel.descriptionLabel': 'Descrizione',
  'sounds.importPanel.descriptionPlaceholder': 'Descrivi questo suono…',
  'sounds.importPanel.dropzoneBrowse': 'sfoglia',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Trascina un file audio o',
  'sounds.importPanel.optional': '(facoltativo)',
  'sounds.importPanel.submit': 'Importa',
  'sounds.importPanel.submitting': 'Invio in corso…',
  'sounds.importPanel.tagsHint': 'Digita e premi Invio · I nuovi tag vengono creati automaticamente',
  'sounds.importPanel.tagsLabel': 'Tag',
  'sounds.importPanel.tagsPlaceholder': 'Aggiungi tag…',
  'sounds.importPanel.title': 'Importa un suono',
  'sounds.importPanel.titleLabel': 'Titolo',
  'sounds.importPanel.titlePlaceholder': 'Nome del suono…',
  'sounds.importPanel.titleRequired': 'Il titolo è obbligatorio',
  'sounds.noResults': 'Nessun suono corrisponde a « {{query}} »',
  'sounds.page.count_one': '{{count}} file — salvato nel database e sul server',
  'sounds.page.count_other': '{{count}} file — salvati nel database e sul server',
  'sounds.page.importButton': 'Importa',
  'sounds.page.searchPlaceholder': 'Titolo, descrizione, tag…',
  'sounds.page.title': 'Suoni',
  'documents.count_one': '{{count}} documento',
  'documents.count_other': '{{count}} documenti',
  'documents.delete.body': 'Il documento «{{filename}}» verrà rimosso dalla tua libreria.',
  'documents.delete.cancel': 'Annulla',
  'documents.delete.confirm': 'Elimina',
  'documents.delete.irreversible': 'Questa azione è irreversibile.',
  'documents.delete.title': 'Eliminare il documento?',
  'documents.importPanel.cancel': 'Annulla',
  'documents.importPanel.chooseFile': 'Scegli un documento PDF',
  'documents.importPanel.description': 'Il documento sarà disponibile nei blocchi PDF e in questa libreria.',
  'documents.importPanel.descriptionLabel': 'Descrizione',
  'documents.importPanel.descriptionPlaceholder': 'Descrizione (facoltativa)',
  'documents.importPanel.fileHint': 'Il documento sarà disponibile nel blocco PDF e in questa libreria.',
  'documents.importPanel.submit': 'Importa',
  'documents.importPanel.title': 'Importa un PDF',
  'documents.importPanel.titleLabel': 'Titolo',
  'documents.importPanel.titlePlaceholder': 'Titolo del documento',
  'documents.importPanel.uploading': 'Caricamento in corso',
  'documents.importPdf': 'Importa un PDF',
  'documents.loading': 'Caricamento della libreria…',
  'documents.newDocument': 'Nuovo documento',
  'documents.noDocuments': 'Nessun documento',
  'documents.noDocumentsHint': 'Importa i tuoi PDF qui per riutilizzarli nel blocco PDF.',
  'documents.noResults': 'Nessun documento corrisponde alla ricerca.',
  'documents.pages_one': '{{count}} pagina',
  'documents.pages_other': '{{count}} pagine',
  'documents.previewTitle': 'Anteprima del documento',
  'documents.searchPlaceholder': 'Cerca un PDF...',
  'documents.share.accessRemoved': 'Accesso rimosso per il discente.',
  'documents.share.assignedTitle': 'Discenti con accesso',
  'documents.share.close': 'Chiudi',
  'documents.share.createAndShare': 'Crea, condividi e invia l\'invito',
  'documents.share.createError': 'Impossibile creare il discente.',
  'documents.share.createLearner': 'Crea un nuovo discente',
  'documents.share.createdSuccess': 'Account creato/collegato, documento condiviso ed email inviata.',
  'documents.share.creating': 'Invio in corso...',
  'documents.share.emailPlaceholder': 'Email *',
  'documents.share.firstNamePlaceholder': 'Nome *',
  'documents.share.hideCreateLearner': 'Nascondi creazione rapida',
  'documents.share.lastNamePlaceholder': 'Cognome *',
  'documents.share.loadingAssigned': 'Caricamento…',
  'documents.share.loadingLearners': 'Caricamento discenti...',
  'documents.share.noAssigned': 'Nessun discente assegnato a questo documento.',
  'documents.share.noLearners': 'Nessun discente disponibile.',
  'documents.share.removeButton': 'Rimuovi',
  'documents.share.removeError': 'Impossibile rimuovere l\'accesso.',
  'documents.share.searchPlaceholder': 'Cerca un discente (nome, cognome, email)',
  'documents.share.selectLearner': 'Seleziona un discente esistente',
  'documents.share.shareButton': 'Condividi',
  'documents.share.shareError': 'Impossibile condividere il documento.',
  'documents.share.sharedSuccess': 'Documento condiviso e invito inviato.',
  'documents.share.title': 'Condividi il documento',
  'documents.subtitle': 'Libreria di documenti PDF riutilizzabili nel designer.',
  'documents.title': 'Documenti',
  'documents.toast.importError': 'Impossibile importare',
  'documents.toast.importErrorDefault': 'L\'importazione non è riuscita.',
  'documents.toast.importSuccess': 'Documento importato',
  'documents.toast.importSuccessDesc': '{{filename}} è stato aggiunto alla tua libreria documenti.',
  'documents.updating': 'Aggiornamento…',
  'help.categories.analytics.description': 'Dashboard, filtri ed esportazioni per guidare l\'impatto delle formazioni.',
  'help.categories.analytics.title': 'Analisi e report',
  'help.categories.apprenants.description': 'Seguire le iscrizioni, le coorti e il progresso dei partecipanti.',
  'help.categories.apprenants.title': 'Discenti, gruppi e monitoraggio',
  'help.categories.boutique.description': 'Vendere o assegnare formazioni quando il negozio è configurato.',
  'help.categories.boutique.title': 'Negozio e monetizzazione',
  'help.categories.demarrage.description': 'Familiarizzare con l\'interfaccia, il tuo profilo e la navigazione quotidiana in Lyrra.',
  'help.categories.demarrage.title': 'Primi passi e account',
  'help.categories.donnees.description': 'GDPR, conservazione, diritti degli interessati e buone pratiche di sicurezza.',
  'help.categories.donnees.title': 'Dati personali e sicurezza',
  'help.categories.eduflow.description': 'Progettare percorsi pedagogici con blocchi, connessioni e logica di navigazione.',
  'help.categories.eduflow.title': 'Percorsi e designer EduFlow',
  'help.categories.evaluation.description': 'Quiz, valutazioni, sondaggi e feedback per misurare gli apprendimenti.',
  'help.categories.evaluation.title': 'Valutazione e pedagogia attiva',
  'help.categories.institution.description': 'Configurare la tua organizzazione, i collaboratori e la struttura dei contenuti.',
  'help.categories.institution.title': 'Istituto e team',
  'help.categories.marque.description': 'Allineare colori, etichette e l\'esperienza del discente alla tua identità.',
  'help.categories.marque.title': 'Marchio e esperienza utente',
  'help.categories.medias.description': 'Gestire libreria, importazioni e strumenti audio / video / immagine integrati in Lyrra.',
  'help.categories.medias.title': 'Media e studio creativo',
  'help.categories.parcours-v2.description': 'Campagne, competenze, integrazioni: tutto ciò che arricchisce la gestione della formazione.',
  'help.categories.parcours-v2.title': 'Funzioni avanzate e roadmap',
  'help.categories.podcasts.description': 'Creare e diffondere contenuti audio di lunga durata al di fuori del percorso blocco per blocco.',
  'help.categories.podcasts.title': 'Podcast e audiolibri',
  'help.categories.publication.description': 'Rendere un percorso disponibile ai discenti e condividere gli accessi corretti.',
  'help.categories.publication.title': 'Pubblicazione e diffusione',
  'help.categories.qualite.description': 'Quadri qualitativi francesi: tracciabilità, obiettivi e prove per gli audit.',
  'help.categories.qualite.title': 'Qualità, Qualiopi e conformità pedagogica',
  'help.categories.support.description': 'Di fronte a un problema tecnico o funzionale, sapere dove cercare e chi contattare.',
  'help.categories.support.title': 'Risoluzione problemi e supporto',
  'help.configHint': 'Contenuto editoriale: collega i tuoi URL, articoli o knowledge base quando sei pronto.',
  'help.description': 'Documentazione e supporto',
  'help.hubIntro': 'Temi riguardanti l\'uso di Lyrra: presto potrai collegare ogni scheda alla tua documentazione, tutorial o ticket di supporto.',
  'help.modal.expectedAnswer': 'Risposta attesa:',
  'help.modal.fallbackHighlight1': 'Scheda',
  'help.modal.fallbackHighlight2': 'Piattaforma di formazione',
  'help.modal.fallbackHighlight3': 'Da completare',
  'help.modal.fallbackSectionHeading': 'Punti chiave',
  'help.modal.fallbackSectionParagraph': 'Il contenuto dettagliato di questa scheda sarà arricchito o collegato alla tua documentazione interna.',
  'help.modal.fallbackStep1Desc': 'Usa il menu laterale o la dashboard per accedere al modulo interessato.',
  'help.modal.fallbackStep1Title': 'Aprire la funzionalità',
  'help.modal.fallbackStep2Desc': 'Le schermate ti guidano: salva spesso e testa in anteprima quando disponibile.',
  'help.modal.fallbackStep2Title': 'Seguire il flusso di lavoro',
  'help.modal.fallbackStep3Desc': 'Annota le specificità del tuo istituto (processi HR, nomenclatura, linee guida del marchio) per il tuo team.',
  'help.modal.fallbackStep3Title': 'Documentare internamente',
  'help.modal.fallbackTip': 'In caso di dubbio, verifica il tuo ruolo (formatore, admin, HR): i menu visualizzati dipendono da esso.',
  'help.modal.quiz': 'Quiz di validazione',
  'help.modal.related': 'Vedi anche',
  'help.modal.resources': 'Risorse',
  'help.modal.steps': 'In pratica',
  'help.modal.tips': 'Consigli',
  'help.noResults': 'Nessun risultato per questa ricerca.',
  'help.onboarding.ai-designer': 'Formazione — Assistente IA del designer LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Formazione — Tipi di blocchi EduFlow (onboarding)',
  'help.onboarding.branches': 'Formazione — Rami e condizioni EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Formazione — Catalogo e visibilità LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Formazione — Certificati rilasciati LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Formazione — Dashboard Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Formazione — Panoramica del designer EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Formazione — Iscrizioni e partecipanti LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Formazione — Gruppi e coorti LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Formazione — Mediateca LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Formazione — Navigazione e menu laterale Lyrra (onboarding)',
  'help.onboarding.objectives': 'Formazione — Obiettivi pedagogici EduFlow (onboarding)',
  'help.onboarding.profile': 'Formazione — Profilo e preferenze Lyrra (onboarding)',
  'help.onboarding.progress': 'Formazione — Progresso e tempo dedicato LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Formazione — Link di accesso e condivisione LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Formazione — Bozza, pubblicazione e aggiornamenti LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Formazione — Ruoli e diritti di accesso Lyrra (onboarding)',
  'help.onboarding.validation': 'Formazione — Validazione del percorso EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Formazione non trovata',
  'help.onboardingFlowMissingDesc': 'Esegui gli script db:seed:onboarding-eduflow e db:seed:onboarding-navigation-eduflow (backend), oppure imposta le variabili VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Apri la scheda',
  'help.searchPlaceholder': 'Cerca un tema…',
  'help.title': 'Centro assistenza',
  'help.topics.analytics-exports.description': 'Preparare le estrazioni per il reporting interno o regolamentare.',
  'help.topics.analytics-exports.title': 'Esportazioni e reporting HR',
  'help.topics.analytics-indicateurs.description': 'Tasso di completamento, coinvolgimento, distribuzione per stato e tempo sui blocchi.',
  'help.topics.analytics-indicateurs.title': 'Indicatori chiave',
  'help.topics.analytics-vue.description': 'Personalizzare grafici, periodi e filtrare per formazione per analizzare i dati.',
  'help.topics.analytics-vue.title': 'Panoramica analitica',
  'help.topics.app-certificats.description': 'Verificare i certificati generati e le condizioni di superamento associate.',
  'help.topics.app-certificats.title': 'Certificati rilasciati',
  'help.topics.app-groupes.description': 'Usare i gruppi per segmentare il pubblico e facilitare le campagne o i report.',
  'help.topics.app-groupes.title': 'Gruppi e coorti',
  'help.topics.app-inscriptions.description': 'Consultare la lista dei discenti su un percorso, i loro stati e lo storico degli accessi.',
  'help.topics.app-inscriptions.title': 'Iscrizioni e partecipanti',
  'help.topics.app-progression.description': 'Interpretare il progresso, i completamenti e il tempo di coinvolgimento nei blocchi.',
  'help.topics.app-progression.title': 'Progresso e tempo dedicato',
  'help.topics.boutique-offres.description': 'Collegare percorsi a prodotti o codici di accesso in base al tuo modello di business.',
  'help.topics.boutique-offres.title': 'Offerte e tariffe',
  'help.topics.boutique-paiement.description': 'Comprendere il flusso di acquisto lato discente e le conferme lato istituto.',
  'help.topics.boutique-paiement.title': 'Pagamenti e fatturazione',
  'help.topics.demarrage-navigation.description': 'Trovare gli spazi Progetti, Formazioni, Media, Analisi e Impostazioni in base al tuo ruolo.',
  'help.topics.demarrage-navigation.title': 'Navigazione e menu laterale',
  'help.topics.demarrage-profil.description': 'Aggiornare le tue informazioni, la lingua dell\'interfaccia e le notifiche.',
  'help.topics.demarrage-profil.title': 'Profilo e preferenze',
  'help.topics.demarrage-roles.description': 'Comprendere le differenze tra formatore, amministratore di istituto, HR e discente.',
  'help.topics.demarrage-roles.title': 'Ruoli e diritti di accesso',
  'help.topics.demarrage-tableau-bord.description': 'Leggere gli indicatori chiave, accedere rapidamente ai percorsi recenti e alle scorciatoie utili.',
  'help.topics.demarrage-tableau-bord.title': 'Dashboard formazione',
  'help.topics.donnees-conservation.description': 'Principi sulla durata di archiviazione dei progressi e delle prove di formazione.',
  'help.topics.donnees-conservation.title': 'Durata di conservazione',
  'help.topics.donnees-mots-de-passe.description': 'Buone pratiche per le password, sessioni e delega di accesso.',
  'help.topics.donnees-mots-de-passe.title': 'Account e autenticazione',
  'help.topics.donnees-rgpd.description': 'Finalità dei dati raccolti in Lyrra e responsabilità del responsabile della formazione.',
  'help.topics.donnees-rgpd.title': 'GDPR e registro dei trattamenti',
  'help.topics.eduflow-blocs.description': 'Scegliere e configurare i blocchi adatti: testo, video, audio, valutazione, ramificazione, ecc.',
  'help.topics.eduflow-blocs.title': 'Tipi di blocchi (contenuto, quiz, media…)',
  'help.topics.eduflow-branches.description': 'Gestire uscite multiple, la convergenza dei percorsi e le regole di navigazione.',
  'help.topics.eduflow-branches.title': 'Rami e condizioni',
  'help.topics.eduflow-concept.description': 'Canvas, palette dei blocchi, selezione, zoom e organizzazione visiva del percorso.',
  'help.topics.eduflow-concept.title': 'Panoramica del designer',
  'help.topics.eduflow-ia.description': 'Suggerire o modificare blocchi con l\'assistente, con revisione umana sistematica.',
  'help.topics.eduflow-ia.title': 'Assistente IA nel designer',
  'help.topics.eduflow-objectifs.description': 'Collegare obiettivi ai blocchi per strutturare il quadro della tua formazione.',
  'help.topics.eduflow-objectifs.title': 'Obiettivi pedagogici',
  'help.topics.eduflow-validation.description': 'Usare il pannello Validazione per rilevare errori e avvertimenti prima della pubblicazione.',
  'help.topics.eduflow-validation.title': 'Validazione del percorso',
  'help.topics.eval-auto-eval.description': 'Proporre percorsi adattivi in base ai risultati.',
  'help.topics.eval-auto-eval.title': 'Autovalutazione e rimedio',
  'help.topics.eval-ouverte.description': 'Organizzare la raccolta di risposte libere e la valutazione manuale o semi-automatica.',
  'help.topics.eval-ouverte.title': 'Domande aperte e correzione',
  'help.topics.eval-quiz.description': 'Configurare punteggi, feedback e tentativi per un QCM efficace.',
  'help.topics.eval-quiz.title': 'Quiz e domande a scelta multipla',
  'help.topics.inst-collaborateurs.description': 'Aggiungere formatori, amministratori o personale HR e gestire i permessi.',
  'help.topics.inst-collaborateurs.title': 'Utenti e inviti',
  'help.topics.inst-structure.description': 'Definire ciò che è condiviso a livello di istituto rispetto ai progetti personali.',
  'help.topics.inst-structure.title': 'Struttura e perimetro',
  'help.topics.inst-trash.description': 'Trovare formazioni o podcast eliminati e ripristinarli se necessario.',
  'help.topics.inst-trash.title': 'Cestino e ripristino',
  'help.topics.marque-accessibilite.description': 'Contrasti, alternative testuali e navigazione da tastiera nei contenuti.',
  'help.topics.marque-accessibilite.title': 'Accessibilità e leggibilità',
  'help.topics.marque-charte.description': 'Come il branding si riflette nel player e negli spazi per i discenti.',
  'help.topics.marque-charte.title': 'Marchio e colori istituzionali',
  'help.topics.marque-landing.description': 'Presentare la tua offerta formativa ai visitatori quando il modulo è attivato.',
  'help.topics.marque-landing.title': 'Pagine pubbliche e catalogo',
  'help.topics.medias-bibliotheque.description': 'Importare, organizzare e riutilizzare video, suoni, immagini e documenti nei tuoi blocchi.',
  'help.topics.medias-bibliotheque.title': 'Mediateca',
  'help.topics.medias-documents.description': 'Allegare risorse scaricabili e collegare la documentazione al percorso.',
  'help.topics.medias-documents.title': 'Documenti e PDF',
  'help.topics.medias-studio-audio.description': 'Registrare, mixare o usare la sintesi vocale per arricchire i tuoi contenuti.',
  'help.topics.medias-studio-audio.title': 'Studio audio e voce',
  'help.topics.medias-video.description': 'Buone pratiche di formato, peso e accessibilità per i lettori integrati.',
  'help.topics.medias-video.title': 'Video e integrazioni',
  'help.topics.podcasts-creation.description': 'Strutturare episodi, metadati e copertine per la tua offerta audio.',
  'help.topics.podcasts-creation.title': 'Creazione di un podcast',
  'help.topics.podcasts-diffusion.description': 'Impostazioni di ascolto, condivisione e monitoraggio dell\'uso da parte dell\'istituto.',
  'help.topics.podcasts-diffusion.title': 'Diffusione e discenti',
  'help.topics.pub-catalogue.description': 'Associare una formazione al catalogo istituzionale o a un\'offerta quando attivato.',
  'help.topics.pub-catalogue.title': 'Catalogo e visibilità',
  'help.topics.pub-liens.description': 'Generare o copiare i link per i discenti, presentatori o anteprima.',
  'help.topics.pub-liens.title': 'Link di accesso e condivisione',
  'help.topics.pub-statuts.description': 'Ciclo di vita di una formazione: salvare, testare, pubblicare e versionare le modifiche.',
  'help.topics.pub-statuts.title': 'Bozza, pubblicazione e aggiornamenti',
  'help.topics.qualite-qualiopi.description': 'Usare gli spazi dedicati per preparare i criteri e il dossier qualità.',
  'help.topics.qualite-qualiopi.title': 'Pre-audit e indicatori Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Collegare obiettivi, contenuti e valutazioni per documentare il percorso certificante.',
  'help.topics.qualite-tracabilite.title': 'Tracciabilità pedagogica',
  'help.topics.support-contact.description': 'Canale di supporto, creazione del ticket e informazioni da fornire per una diagnosi rapida.',
  'help.topics.support-contact.title': 'Contattare il supporto Lyrra',
  'help.topics.support-faq.description': 'Lettore che non carica, media mancante, errore di pubblicazione: primi passi.',
  'help.topics.support-faq.title': 'Problemi frequenti',
  'help.topics.support-navigateur.description': 'Versioni consigliate, cookie ed estensioni che possono interferire.',
  'help.topics.support-navigateur.title': 'Browser e compatibilità',
  'help.topics.v2-campagnes.description': 'Pianificare il dispiegamento di percorsi verso popolazioni mirate nel tempo.',
  'help.topics.v2-campagnes.title': 'Campagne e piani di formazione',
  'help.topics.v2-competences.description': 'Mappare i blocchi o percorsi alle competenze attese per il reporting aziendale.',
  'help.topics.v2-competences.title': 'Quadri di competenze',
  'help.topics.v2-integrations.description': 'Scambi con il tuo SI-HR, LMS esterno o strumenti BI quando i connettori sono disponibili.',
  'help.topics.v2-integrations.title': 'Integrazioni e API',
  'pages.images.cancel': 'Annulla',
  'pages.images.changeFile': 'Cambia file',
  'pages.images.copyUrl': 'Copia URL',
  'pages.images.count_one': '{{count}} immagine nella tua libreria',
  'pages.images.count_other': '{{count}} immagini nella tua libreria',
  'pages.images.delete': 'Elimina',
  'pages.images.deleteAction': 'Elimina',
  'pages.images.deleteConfirmHint': 'verrà eliminata definitivamente.',
  'pages.images.deleteTitle': 'Eliminare questa immagine?',
  'pages.images.descriptionLabel': 'Descrizione (opzionale)',
  'pages.images.descriptionPlaceholder': 'Breve descrizione…',
  'pages.images.dropzoneBrowse': 'sfoglia',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Trascina un\'immagine o',
  'pages.images.emptyHint': 'Trascina, clicca per importare o incolla uno screenshot (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importa',
  'pages.images.importSubtitle': 'Aggiungi un\'immagine alla tua libreria per i tuoi percorsi formativi.',
  'pages.images.importTitle': 'Importa un\'immagine',
  'pages.images.lightboxBackdropClose': 'Chiudi',
  'pages.images.lightboxClose': 'Chiudi',
  'pages.images.lightboxNext': 'Immagine successiva',
  'pages.images.lightboxPrev': 'Immagine precedente',
  'pages.images.lightboxTitle': 'Anteprima immagine',
  'pages.images.noImages': 'Nessuna immagine importata',
  'pages.images.noResults': 'Nessun risultato',
  'pages.images.pasteHint': 'Oppure incolla uno screenshot (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Cerca un\'immagine…',
  'pages.images.submit': 'Importa',
  'pages.images.title': 'Immagini',
  'pages.images.titleLabel': 'Titolo (opzionale)',
  'pages.images.titlePlaceholder': 'Nome dell\'immagine…',
  'pages.images.viewLarge': 'Vedi ingrandito',
  'pages.learners.blockSessionInProgress': '(in corso)',
  'pages.learners.blockTimeOnBlock': '({{duration}} su questo blocco)',
  'pages.learners.description': 'Segui l’avanzamento dei tuoi studenti',
  'pages.learners.detailedTraces': 'Attività dettagliata · {{flowTitle}}',
  'pages.learners.enrolledFormations': 'Corsi seguiti',
  'pages.learners.lastEvents': 'Eventi recenti',
  'pages.learners.notStarted': 'Non iniziato',
  'pages.learners.startedOn': 'Inizio {{date}}',
  'pages.learners.statsBlockSessions': 'Sessioni per blocco: {{count}}',
  'pages.learners.statsEvents': 'Eventi: {{count}}',
  'pages.learners.statsResponses': 'Risposte: {{count}}',
  'pages.learners.timePerBlock': 'Tempo per blocco',
  'pages.learners.title': 'Studenti',
  'pages.learners.trackingEvent.block_enter': 'Ingresso in un blocco',
  'pages.learners.trackingEvent.block_exit': 'Uscita da un blocco',
  'pages.learners.trackingEvent.progress_sync': 'Avanzamento sincronizzato',
  'pages.learners.trackingEvent.response_submitted': 'Risposta inviata',
  'pages.learners.trackingEvent.training_completed': 'Corso completato',
  'pages.learners.trackingEvent.training_enter': 'Apertura del corso',
  'pages.learners.trackingEvent.training_exit': 'Uscita o chiusura del corso',
  'pages.learners.trackingEvent.unknown': 'Evento',
  'pages.learners.refresh': 'Aggiorna',
  'pages.learners.exportCsv': 'Esporta CSV',
  'pages.learners.statTotal': 'Studenti totali',
  'pages.learners.statInProgress': 'In corso',
  'pages.learners.statCompleted': 'Completati',
  'pages.learners.statAvgProgress': 'Avanz. medio',
  'pages.learners.searchPlaceholder': 'Cerca per nome o email…',
  'pages.learners.filterAllFlows': 'Tutti i corsi',
  'pages.learners.sortRecent': 'Più recenti',
  'pages.learners.sortName': 'Nome A-Z',
  'pages.learners.sortProgress': 'Avanzamento',
  'pages.learners.colLearner': 'Studente',
  'pages.learners.colFormations': 'Corsi',
  'pages.learners.colProgress': 'Avanzamento',
  'pages.learners.colCompleted': 'Completati',
  'pages.learners.colTime': 'Tempo',
  'pages.learners.colLastAccess': 'Ultimo accesso',
  'pages.learners.colActions': 'Azioni',
  'pages.learners.openProgressDashboard': 'Cruscotto di avanzamento',
  'pages.learners.dashboardTitle': 'Avanzamento dello studente',
  'pages.learners.dashboardBack': 'Torna all’elenco',
  'pages.learners.dashboardInvalidId': 'ID studente non valido',
  'pages.learners.dashboardNotFound': 'Studente non trovato o accesso negato',
  'pages.learners.dashboardOverviewHint':
    'Vista unificata: corsi EduFlow del partecipante, percorsi e podcast assegnati all’account Lyrra con la stessa email.',
  'pages.learners.tabOverview': 'Panoramica',
  'pages.learners.tabFormations': 'Corsi',
  'pages.learners.tabParcours': 'Percorsi',
  'pages.learners.tabPodcasts': 'Podcast',
  'pages.learners.sectionFormations': 'Corsi',
  'pages.learners.sectionParcours': 'Percorsi',
  'pages.learners.sectionPodcasts': 'Podcast',
  'pages.learners.emptyParcours': 'Nessun percorso assegnato a un account Lyrra con questa email.',
  'pages.learners.emptyPodcasts': 'Nessun podcast assegnato a un account Lyrra con questa email.',
  'pages.learners.linkedAccountHint':
    'Percorsi e podcast compaiono se esiste un utente Lyrra con la stessa email del partecipante.',
  'pages.learners.kpiEduflows': 'Corsi',
  'pages.learners.kpiAvgProgress': 'Avanz. medio',
  'pages.learners.kpiPaths': 'Percorsi',
  'pages.learners.kpiPodcasts': 'Podcast',
  'pages.learners.kpiCompletedFormations': '{{count}} completati/e',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} attività',
  'pages.learners.pathAssignedOn': 'Aggiunto il {{date}}',
  'pages.learners.podcastCompleted': 'Completato',
  'pages.learners.podcastInProgress': 'In corso',
  'pages.learners.pathStatusPublished': 'Pubblicato',
  'pages.learners.pathStatusDraft': 'Bozza',
  'pages.learners.openInDesigner': 'Apri nel designer',
  'pages.learners.openParcoursEditor': 'Apri percorso',
  'pages.learners.viewPodcasts': 'Vai ai podcast',
  'pages.learners.loading': 'Caricamento studenti…',
  'pages.learners.loadError': 'Impossibile caricare gli studenti',
  'pages.learners.retry': 'Riprova',
  'pages.learners.emptyFiltered': 'Nessuno studente trovato',
  'pages.learners.emptyFilteredHint': 'Modifica criteri di ricerca o filtri',
  'pages.learners.emptyNone': 'Nessuno studente iscritto',
  'pages.learners.emptyNoneHint':
    'Gli studenti compariranno qui quando si iscrivono ai corsi pubblicati',
  'pages.learners.pagination': 'Pagina {{page}} di {{totalPages}} · {{total}} studente/i',
  'pages.learners.prev': 'Precedente',
  'pages.learners.next': 'Successivo',
  'pages.learners.csvLastName': 'Cognome',
  'pages.learners.csvFirstName': 'Nome',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Corsi',
  'pages.learners.csvAvgProgress': 'Avanzamento medio',
  'pages.learners.csvTotalTime': 'Tempo totale',
  'pages.learners.csvLastActivity': 'Ultima attività',
  'pages.learners.statsAdaptiveChoice':
    'Scelte assistite — accettate: {{accepted}} · rifiutate: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adattamenti valutati: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versioni semplificate usate: {{count}}',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Modalità testo semplificato',
  'accessibility.simplifiedTextModeDesc':
    'Semplifica solo i testi del percorso per facilitare lettura e comprensione.',
  'accessibility.imageHoverAudio': "Modalità ipovisione: avvio audio immagine al passaggio del mouse",
  'accessibility.imageHoverAudioDesc':
    "Quando l'immagine viene passata con il mouse nel player, la descrizione audio parte automaticamente.",
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Comprimi',
  'admin.shell.collapseSidebar': 'Comprimi barra laterale',
  'admin.shell.expandSidebar': 'Espandi barra laterale',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.login': 'Sign in',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.logout': 'Log out',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.register': 'Create one',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'btn.cancel': 'Annuler',
  'btn.close': 'Fermer',
  'btn.save': 'Enregistrer',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.copied': 'Copié !',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.loading': 'Chargement…',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Richiesta di formazione — contenuti didattici',
  'trainingRequest.intro': 'Descrivi il bisogno. I team HR approvano o rifiutano la richiesta; se approvata, passa alla preparazione dei contenuti.',
  'trainingRequest.fieldTitle': 'Titolo della formazione',
  'trainingRequest.fieldDescription': 'Descrizione del bisogno',
  'trainingRequest.fieldJustification': 'Giustificazione / obiettivi',
  'trainingRequest.fieldDate': 'Data preferita',
  'trainingRequest.datePlaceholder': 'es. T2 2026',
  'trainingRequest.fieldFormateur': 'Formatore dell\'istituto',
  'trainingRequest.formateurPlaceholder': 'Scegli un formatore…',
  'trainingRequest.formateursEmpty': 'Nessun account formatore registrato per il tuo istituto.',
  'trainingRequest.titleRequired': 'Inserisci un titolo (almeno 2 caratteri).',
  'trainingRequest.sent': 'Richiesta registrata. Riceverai una notifica via email sulla decisione.',
  'trainingRequest.sendError': 'Impossibile registrare la richiesta. Verifica la connessione o i permessi.',
  'trainingRequest.submit': 'Invia la richiesta',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.total': 'au total',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Collaborativo',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.createFormation.error': 'Impossibile creare il corso.',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duplicateFlowError': 'Impossibile duplicare il corso.',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.title': 'Corsi',
  'eduflow.list.subtitle': 'Progetta esperienze di apprendimento interattive',
  'eduflow.list.searchPlaceholder': 'Cerca un corso…',
  'eduflow.list.import': 'Importa',
  'eduflow.list.importHint':
    'File .lyrra (media inclusi) o export JSON legacy Lyrra V1.',
  'eduflow.list.importSuccess': 'Corso importato. Apertura del designer.',
  'eduflow.list.importError':
    'Importazione non riuscita — verifica il file (.lyrra o export JSON valido, almeno un blocco).',
  'eduflow.list.filterAll': 'Tutti',
  'eduflow.list.filterDraft': 'Bozze',
  'eduflow.list.filterPublished': 'Pubblicati',
  'eduflow.list.filterArchived': 'Archiviati',
  'eduflow.list.viewModeGrid': 'Vista griglia',
  'eduflow.list.viewModeList': 'Vista elenco',
  'eduflow.list.viewByCategory': 'Vista categorie',
  'eduflow.list.categoryBack': 'Tutte le categorie',
  'eduflow.list.categoryBrowseHint': 'Scegli una categoria per vedere i suoi corsi.',
  'eduflow.list.categoryFormationCount': '{{count}} corso/i',
  'eduflow.list.categoryEmptyFiltered': 'Nessun corso in questa vista (filtro o ricerca attivi).',
  'eduflow.list.uncategorizedCardHint': 'Corsi senza etichetta di categoria.',
  'eduflow.list.uncategorized': 'Senza categoria',
  'eduflow.list.manageCategories': 'Gestisci categorie',
  'eduflow.list.categoriesPanelTitle': 'Categorie del corso',
  'eduflow.list.noCategoryAvailable': 'Nessuna categoria disponibile.',
  'eduflow.list.categoriesSaveError': 'Impossibile salvare le categorie.',
  'eduflow.list.emptyFilteredTitle': 'Nessun risultato',
  'eduflow.list.emptyFilteredHint': 'Modifica i criteri di ricerca o i filtri.',
  'eduflow.list.emptyFirstTitle': 'Crea il tuo primo corso',
  'eduflow.list.emptyFirstHint': 'Costruisci apprendimento interattivo con il designer visuale.',
  'eduflow.list.emptyCreateCta': 'Crea il mio primo corso',
  'eduflow.list.deleteToTrashTitle': 'Spostare questo corso nel cestino?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » sarà rimosso dall’elenco (eliminazione logica). La tua organizzazione potrà ripristinarlo dal cestino istituzionale.',
  'eduflow.list.deleteToTrashConfirm': 'Nel cestino',
  'eduflow.list.coverLabel': 'Immagine di copertina',
  'eduflow.list.coverRemove': 'Rimuovi copertina',
  'eduflow.list.coverUpload': 'Scegli immagine',
  'eduflow.list.flowProperties': 'Titolo, descrizione e copertina',
  'eduflow.list.propertiesPanelTitle': 'Proprietà del corso',
  'eduflow.list.duplicatePanelTitle': 'Duplica corso',
  'eduflow.list.duplicatePanelSubtitle': 'Corso originale: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copia',
  'eduflow.list.duplicateSuccess': 'Corso duplicato.',
  'eduflow.list.duplicateCoverHint':
    'Copertina e contenuto didattico del corso originale saranno riutilizzati.',
  'eduflow.list.propertiesSaveError': 'Impossibile salvare le proprietà.',
  'eduflow.list.propertiesSaved': 'Proprietà salvate.',
  'eduflow.list.propertiesUploadError': 'Caricamento immagine non riuscito.',
  'eduflow.list.blocksCount': '{{count}} blocchi',
  'eduflow.list.noContent': 'Nessun contenuto',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Apri Le mie missioni di produzione',
  'eduflow.list.tooltip.edit': 'Modifica',
  'eduflow.list.shellHint':
    'Scheda definita dalla tua organizzazione — crea l’EduFlow da « Le mie missioni di produzione ».',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Avanti',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Versione {{label}} attivata (pubblica)',
  'eduflow.versionHistory.activateTitle': 'Attivare {{label}}?',
  'eduflow.versionHistory.blocks': '{{count}} blocchi',
  'eduflow.versionHistory.confirmActivate': 'Attiva',
  'eduflow.versionHistory.copyFailed': 'Copia non riuscita',
  'eduflow.versionHistory.copyHtml': 'Copia HTML',
  'eduflow.versionHistory.copyPlain': 'Copia solo testo',
  'eduflow.versionHistory.copyPublicPlayer': 'Copia link pubblico',
  'eduflow.versionHistory.copyPublicPlayerError': 'Impossibile copiare il link pubblico',
  'eduflow.versionHistory.count': '{{count}} versione/i',
  'eduflow.versionHistory.createError': 'Errore nella creazione della versione',
  'eduflow.versionHistory.createMajor': 'Versione principale',
  'eduflow.versionHistory.createMajorSuccess': 'Versione principale creata',
  'eduflow.versionHistory.createMinor': 'Versione secondaria',
  'eduflow.versionHistory.createMinorSuccess': 'Versione secondaria creata',
  'eduflow.versionHistory.daysAgo': '{{count}} giorni fa',
  'eduflow.versionHistory.deleteError': 'Errore nell\'eliminazione',
  'eduflow.versionHistory.deleteSuccess': 'Versione eliminata',
  'eduflow.versionHistory.deleteTitle': 'Eliminare {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'Questa azione è irreversibile.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplica',
  'eduflow.versionHistory.duplicateError': 'Errore nella duplicazione',
  'eduflow.versionHistory.duplicateMinor': 'Duplica (secondaria)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Duplicare {{label}}?',
  'eduflow.versionHistory.empty': 'Nessuna versione creata.',
  'eduflow.versionHistory.emptyHint': 'Salva una versione principale o secondaria per trovarla qui.',
  'eduflow.versionHistory.hoursAgo': '{{count}} h fa',
  'eduflow.versionHistory.latest': 'Più recente',
  'eduflow.versionHistory.minutesAgo': '{{count}} min fa',
  'eduflow.versionHistory.newFromCurrent': 'Nuova versione dal contenuto attuale',
  'eduflow.versionHistory.openPublicPlayer': 'Apri il player pubblico',
  'eduflow.versionHistory.preview': 'Anteprima',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocchi',
  'eduflow.versionHistory.previewEdges': '{{count}} connessioni',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Anteprima {{label}}',
  'eduflow.versionHistory.public': 'Versione pubblica',
  'eduflow.versionHistory.publicActive': 'Attiva per i discenti',
  'eduflow.versionHistory.setPublic': 'Attiva',
  'eduflow.versionHistory.startDescModalTitle': 'Descrizione — blocco Avvio ({{version}})',
  'eduflow.versionHistory.yesterday': 'ieri',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.settings': 'Settings',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Traduzione in corso…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Percorso circolare rilevato',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} errore/i',
  'designer.validation.warningCount': '{{count}} avviso/i',
  'designer.validation.titleRequired': 'Il blocco « {{type}} » deve avere un titolo.',
  'designer.validation.summary': 'Percorso: {{blocks}} blocco/i, {{edges}} collegamento/i.',
  'designer.validation.multipleStart': 'Più blocchi Inizio ({{count}}): ne è consentito solo uno.',
  'designer.validation.noOutgoing': 'Il blocco « {{type}} » non ha una connessione in uscita verso un blocco successivo.',
  'designer.validation.tooManyOutputs': '« {{label}} »: troppe connessioni in uscita ({{count}}, massimo {{max}}).',
  'designer.validation.deadEndBranch': 'Ramo senza percorso verso un blocco Fine (da « {{label}} »).',
  'designer.validation.moreDeadEnds': '… e altri {{count}} ramo/i senza uscita verso la fine.',
  'designer.validation.noConvergence': 'I rami da « {{label}} » non convergono verso un blocco comune.',
  'designer.validation.orphanBlock': 'Blocco isolato dal grafo principale: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Il blocco Fine « {{title}} » ha ancora una connessione in uscita.',
  'designer.validation.noScoring': 'Impostazioni di punteggio non definite per « {{title}} ».',
  'designer.validation.emptyText': 'Il blocco di testo « {{title}} » è vuoto.',
  'designer.validation.textTooLong':
    'Il blocco di testo « {{title}} » è stimato a {{lines}} righe. Consigliato: da {{min}} a {{max}} righe.',
  'designer.validation.splitTextBlock': 'Dividi',
  'designer.validation.textSplitNotNeeded':
    'Questo blocco non supera la soglia di lettura consigliata.',
  'designer.validation.textSplitUnable':
    'Impossibile proporre una suddivisione automatica affidabile per questo blocco.',
  'designer.validation.textSplitComplexFlow':
    'Suddivisione automatica non disponibile per questo blocco (diramazioni multiple/condizionali).',
  'designer.validation.textSplitDone': '{{count}} blocchi di testo creati',
  'designer.validation.textSplitConfirmTitle':
    'Dividere automaticamente questo blocco di testo?',
  'designer.validation.textSplitConfirmMessage':
    'Il blocco « {{title}} » è stimato a {{lines}} righe. Lo divideremo in {{parts}} blocchi (separazione guidata dalla punteggiatura) e li posizioneremo sotto, nell’ordine corretto.',
  'designer.validation.textSplitConfirmAction': 'Dividi il blocco',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Voce predefinita',
  'voicePanel.provider': 'Fornitore: Mistral AI',
  'voicePanel.hint': 'Seleziona la voce utilizzata per la narrazione dei blocchi.',
  'voicePanel.translationFrenchFallback':
    'Audio tradotto: se non è salvata una voce predefinita qui, per il francese si usa la voce « Marie - sad » (Mistral).',
  'voicePanel.search': 'Cerca…',
  'voicePanel.allLanguages': 'Tutte le lingue',
  'voicePanel.loading': 'Caricamento voci…',
  'voicePanel.loadError': 'Impossibile caricare le voci',
  'voicePanel.empty': 'Nessuna voce disponibile.',
  'voicePanel.emptyHint': 'Verifica la chiave API Mistral in Amministrazione → Integrazioni.',
  'voicePanel.noResults': 'Nessuna voce corrisponde alla ricerca.',
  'voicePanel.saved': 'Voce predefinita salvata',
  'voicePanel.saveError': 'Errore durante il salvataggio',
  'voicePanel.default': 'Predefinita',
  'voicePanel.female': 'Femminile',
  'voicePanel.male': 'Maschile',
  'voicePanel.playPreview': 'Testa la voce',
  'voicePanel.stopPreview': 'Ferma',
  'voicePanel.noPreview': 'Anteprima non disponibile',
  'voicePanel.previewLoading': 'Generazione…',
  'voicePanel.previewGenerateError': 'Impossibile generare l’anteprima audio.',
  'voicePanel.previewPlayError': 'Riproduzione audio non riuscita.',
  'voicePanel.filterNoVoicesForLang':
    'Nessuna voce del catalogo corrisponde a questa lingua — prova «Tutte le lingue».',
  'episodeEditor.voicePicker.blockVoice': 'Voce per questo blocco',
  'episodeEditor.voicePicker.defaultVoice': 'Voce predefinita',
  'episodeEditor.voicePicker.voices': 'voci',
  'episodeEditor.voicePicker.searchPlaceholder': 'Cerca una voce…',
  'episodeEditor.voicePicker.noVoiceFound': 'Nessuna voce trovata',
  'episodeEditor.voicePicker.female': 'Femminile',
  'episodeEditor.voicePicker.male': 'Maschile',
  'episodeEditor.voicePicker.stop': 'Ferma',
  'episodeEditor.voicePicker.preview': 'Anteprima voce',
  'episodeEditor.voicePicker.stopPlayback': 'Ferma riproduzione',
  'episodeEditor.errors.notFound': 'Episodio non trovato',
  'episodeEditor.errors.titleRequired': 'Il titolo è obbligatorio.',
  'episodeEditor.errors.saveFailed': 'Errore durante il salvataggio.',
  'episodeEditor.errors.noVoicesAvailable': 'Nessuna voce disponibile. Inserisci la chiave API in Amministrazione → Integrazioni piattaforma, poi ricarica.',
  'episodeEditor.errors.selectVoiceFirst': 'Seleziona una voce nella scheda « Voce » prima di generare il voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'Uno o più blocchi hanno una voce non valida. Apri il selettore voce per blocco o ricarica la lista.',
  'episodeEditor.errors.addTextFirst': 'Aggiungi del testo prima di generare l\'audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Generazione audio impossibile. {{message}} (durata = stima.)',
  'episodeEditor.errors.audioGenerationFailed': 'Errore durante la generazione audio. Durata = stima.',
  'episodeEditor.success.published': 'Episodio pubblicato!',
  'episodeEditor.success.draftSaved': 'Bozza salvata',
  'episodeEditor.success.audioGenerated': 'Audio generato con {{count}} blocco/i',
  'episodeEditor.tabs.edit': 'Modifica',
  'episodeEditor.tabs.chapters': 'Capitoli',
  'episodeEditor.tabs.voice': 'Voce',
  'episodeEditor.tabs.podcasts': 'Podcast',
  'episodeEditor.toolbar.undo': 'Annulla (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Ripristina (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generazione…',
  'episodeEditor.toolbar.generateAudio': 'Genera audio',
  'episodeEditor.toolbar.save': 'Salva',
  'episodeEditor.toolbar.publish': 'Pubblica',
  'episodeEditor.titlePlaceholder': 'Titolo dell\'episodio…',
  'episodeEditor.status.published': 'Pubblicato',
  'episodeEditor.status.draft': 'Bozza',
  'episodeEditor.warnings.mistralNotConfigured': 'Il voiceover degli episodi usa Mistral Voxtral TTS. Aggiungi la chiave Mistral in Amministrazione → Integrazioni piattaforma.',
  'episodeEditor.warnings.mistralNoVoices': 'Chiave Mistral riconosciuta ma l\'API non ha restituito voci. Controlla i log del backend.',
  'episodeEditor.warnings.voiceLoadFailed': 'Impossibile caricare le voci. Controlla la configurazione Mistral in Amministrazione → Integrazioni piattaforma.',
  'episodeEditor.warnings.voiceOffDisabled': 'Senza una voce reale, la generazione del voiceover resta disattivata.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Sintesi via Mistral Voxtral TTS. Scegli la voce nella scheda « Voce » o per blocco.',
  'episodeEditor.edit.coverImage': 'Immagine di copertina',
  'episodeEditor.edit.coverAlt': 'Copertina dell’episodio',
  'episodeEditor.edit.deleteCover': 'Elimina',
  'episodeEditor.edit.dropOrClickImage': 'Clicca o trascina un\'immagine',
  'episodeEditor.edit.selectedVoice': 'Voce selezionata',
  'episodeEditor.edit.selectVoice': 'Seleziona una voce',
  'episodeEditor.edit.defaultVoiceByLang': 'Voce predefinita (lingua del podcast)',
  'episodeEditor.edit.lastAudioGenerated': 'Ultimo audio generato',
  'episodeEditor.edit.audioNotFound': 'File audio non trovato — rigenera il voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generazione in corso…',
  'episodeEditor.edit.generateVoiceOff': 'Genera voiceover',
  'episodeEditor.chapters.title': 'Capitoli',
  'episodeEditor.chapters.add': 'Aggiungi',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduzione',
  'episodeEditor.chapters.newChapterTitle': 'Capitolo {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} blocco/i',
  'episodeEditor.voiceTab.availableVoices': 'Voci disponibili',
  'episodeEditor.podcasts.linked': 'Podcast collegati',
  'episodeEditor.podcasts.assign': 'Assegna',
  'episodeEditor.podcasts.noneAvailable': 'Nessun podcast disponibile',
  'episodeEditor.podcasts.close': 'Chiudi',
  'episodeEditor.podcasts.noneLinked': 'Nessun podcast collegato',
  'episodeEditor.podcasts.assignHint': 'Assegna questo episodio a uno o più podcast',
  'episodeEditor.podcasts.remove': 'Rimuovi',
  'episodeEditor.editor.addTextBlock': 'Aggiungi un blocco di testo',
  'episodeEditor.editor.scriptPlaceholder': 'Inizia a scrivere il tuo script…',
  'episodeEditor.editor.addBlock': 'Aggiungi un blocco',
  'episodeEditor.player.loop': 'Ripeti',
  'episodeEditor.player.playbackSpeed': 'Velocità di riproduzione',
  'episodeEditor.player.collapse': 'Riduci',
  'episodeEditor.player.expand': 'Espandi',
  'episodeEditor.player.waveformHidden': 'Forma d\'onda nascosta',
  'podcast.episodes.listen': 'Ascolta',
  'podcast.episodes.noAudioShort': 'Nessun audio disponibile',
  'podcast.episodes.edit': 'Modifica',
  'podcast.episodes.delete': 'Elimina',
  'podcast.episodes.unmute': 'Attiva audio',
  'podcast.episodes.mute': 'Disattiva audio',
  'podcast.episodes.totalEpisodes': '{{count}} episodio/i in totale',
  'eduflow.designer.toast.blockCopied': 'Blocco copiato',
  'eduflow.designer.toast.blockPasted': 'Blocco incollato',
  'eduflow.designer.toast.blockDuplicated': 'Blocco duplicato',
  'eduflow.designer.toast.connectionUpdated': 'Connessione aggiornata',
  'eduflow.designer.toast.flowExported': 'Percorso esportato',
  'eduflow.designer.toast.flowImported': 'Percorso importato',
  'eduflow.designer.toast.invalidFile': 'File non valido',
  'eduflow.designer.untitled': 'Senza titolo',
  'eduflow.designer.importedFlow': 'Percorso importato',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'Utente',
  'eduflow.designer.collaborator.editor': 'Editore',
  'eduflow.designer.collaborator.viewer': 'Lettore',
  'eduflow.designer.resizePanel': 'Ridimensiona pannello',
  'eduflow.designer.published': 'Pubblicato',
  'eduflow.designer.draft': 'Bozza',
  'eduflow.designer.quizOutput.success': 'Superato',
  'eduflow.designer.quizOutput.failure': 'Fallito',
  'eduflow.designer.handle.default': 'Predefinito',
  'eduflow.designer.genError': 'Errore durante la generazione. Riprovare.',
  'trainerTour.welcome.title': 'Benvenuto nel tuo spazio formatore',
  'trainerTour.welcome.description':
    'Questa breve visita presenta la dashboard e il menu. La prima volta che apri ogni pagina, un suggerimento spiega l’essenziale. Puoi chiudere un passo con la X — il tour non ripartirà.',
  'trainerTour.dashboard.header.title': 'La tua dashboard',
  'trainerTour.dashboard.header.description':
    'Panoramica dell’attività: numeri chiave e scorciatoie per creare contenuti rapidamente.',
  'trainerTour.dashboard.actions.title': 'Crea contenuti',
  'trainerTour.dashboard.actions.description':
    'Scorciatoie per un nuovo corso EduFlow, un podcast o un percorso. Per iniziare un progetto senza passare dal menu.',
  'trainerTour.dashboard.stats.title': 'Indicatori',
  'trainerTour.dashboard.stats.description':
    'Corsi, percorsi pubblicati, partecipanti e podcast: vedi l’ambito del catalogo a colpo d’occhio.',
  'trainerTour.dashboard.activity.title': 'Attività recente',
  'trainerTour.dashboard.activity.description':
    'Ultimi contenuti aggiornati. Clicca una riga per riaprire corso, podcast o percorso corrispondente.',
  'trainerTour.sidebar.title': 'Menu laterale',
  'trainerTour.sidebar.description':
    'Panoramica (home, partecipanti, analisi, missioni), creazione (corsi, podcast, percorsi, webinar se attivi), libreria media e aiuto. Apri ogni voce — alla prima visita compare un suggerimento contestuale.',
  'trainerTour.routes.learners.title': 'Partecipanti',
  'trainerTour.routes.learners.description':
    'Persone iscritte ai tuoi contenuti, avanzamento, esportazioni — il centro per il follow-up didattico.',
  'trainerTour.routes.analytics.title': 'Analisi',
  'trainerTour.routes.analytics.description':
    'Grafici su coinvolgimento e completamento. Personalizza il layout per ciò che conta per te.',
  'trainerTour.routes.eduflow.title': 'Corsi (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Crea e gestisci corsi interattivi, versioni, pubblicazione e condivisione — il cuore della progettazione.',
  'trainerTour.routes.podcasts.title': 'Podcast',
  'trainerTour.routes.podcasts.description':
    'Show, episodi, visibilità e condivisione con gruppi o partecipanti dell’ente.',
  'trainerTour.routes.parcours.title': 'Percorsi',
  'trainerTour.routes.parcours.description':
    'Sequenze di corsi e risorse: struttura percorsi lunghi e assegnali ai tuoi pubblici.',
  'trainerTour.routes.videos.title': 'Video',
  'trainerTour.routes.videos.description':
    'Libreria video riutilizzabile in corsi e documenti.',
  'trainerTour.routes.sounds.title': 'Audio',
  'trainerTour.routes.sounds.description':
    'File audio e tag per podcast e blocchi sonori nei corsi.',
  'trainerTour.routes.images.title': 'Immagini',
  'trainerTour.routes.images.description':
    'Visual centralizzati: import, cartelle e riuso nei contenuti.',
  'trainerTour.routes.documents.title': 'Documenti',
  'trainerTour.routes.documents.description':
    'PDF e allegati, con condivisione mirata ai partecipanti se necessario.',
  'trainerTour.routes.help.title': 'Aiuto',
  'trainerTour.routes.help.description':
    'Articoli, tutorial e guide su LyrraStudio. Torna qui se una funzione non ti è chiara.',
  'trainerTour.routes.producerAssignments.title': 'Missioni di produzione',
  'trainerTour.routes.producerAssignments.description':
    'Quando le Risorse Umane ti assegnano contenuti da produrre per il catalogo, compaiono qui con l’avanzamento.',
  'trainerTour.routes.webinars.title': 'Webinar',
  'trainerTour.routes.webinars.description':
    'Pianifica sessioni live, inviti e link di trasmissione per i tuoi gruppi.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Avanti',
  'trainerTour.driver.prev': 'Indietro',
  'trainerTour.driver.done': 'Fine',
  'trainerTour.driver.gotIt': 'Capito',
  'player.lightMode': 'Modalità chiara',
  'player.darkMode': 'Modalità scura',
};

const esTranslations: Record<string, string> = {
  'nav.home': 'Inicio',
  'nav.producerAssignments': 'Misiones de producción',
  'nav.videos': 'Vídeos',
  'nav.sounds': 'Audios',
  'nav.images': 'Imágenes',
  'nav.documents': 'Documentos',
  'nav.podcasts': 'Podcasts',
  'nav.parcours': 'Recorridos',
  'nav.eduflowList': 'Formaciones',
  'nav.myLearning': 'Mi aprendizaje',
  'nav.section.pilotage': 'Gestión',
  'nav.section.creation': 'Creación',
  'nav.section.medias': 'Medios',
  'nav.section.aide': 'Ayuda',
  'sidebar.brandTagline': 'Formación',
  'sidebar.logoInstitutionAlt': 'Logo de la organización',
  'sidebar.expand': 'Expandir menú',
  'sidebar.collapse': 'Contraer menú',
  'sidebar.expandAria': 'Expandir barra lateral',
  'sidebar.collapseAria': 'Contraer barra lateral',
  'learner.sidebar.collapse': 'Contraer',
  'learner.sidebar.expandAria': 'Expandir barra lateral',
  'learner.sidebar.collapseAria': 'Contraer barra lateral',
  'dashboard.title': 'Panel',
  'dashboard.welcome': '¡Bienvenido, {{name}}!',
  'dashboard.welcomeSub': 'Aquí tienes una visión general de tu espacio.',
  'dashboard.projects': 'Proyectos',
  'dashboard.subtitle': 'Gestiona tus formaciones y sigue a los aprendices.',
  'dashboard.newProject': 'Nueva formación',
  'dashboard.newEduFlow': 'Nuevo recorrido',
  'dashboard.uploadMedia': 'Subir medios',
  'dashboard.viewDocs': 'Documentación',
  'dashboard.revenue': 'Ingresos',
  'dashboard.eduflows': 'Formaciones',
  'dashboard.learners': 'Aprendices',
  'dashboard.recentActivity': 'Actividad reciente',
  'dashboard.recentProjects': 'Proyectos recientes',
  'dashboard.noRecentProjects': 'Sin proyectos recientes',
  'dashboard.activity': 'Actividad',
  'dashboard.activityEmpty': 'Sin actividad reciente en formaciones o podcasts.',
  'dashboard.loadError': 'No se pudieron cargar los datos del panel.',
  'dashboard.publishedFlows': 'Recorridos publicados',
  'dashboard.roleSpecific': 'Hay un panel específico para este rol.',
  'dashboard.kindPodcast': 'Podcast',
  'nav.analytics': 'Analítica',
  'pages.analytics.title': 'Analítica',
  'pages.analytics.description': 'Cuadros de mando y estadísticas',
  'pages.analytics.subtitle':
    'Filtre los datos, reordene gráficos arrastrando y ajuste el tamaño de los widgets.',
  'pages.analytics.filters.period': 'Periodo',
  'pages.analytics.filters.flow': 'Curso',
  'pages.analytics.filters.allFlows': 'Todos los cursos',
  'pages.analytics.period.7d': 'Últimos 7 días',
  'pages.analytics.period.30d': 'Últimos 30 días',
  'pages.analytics.period.90d': 'Últimos 90 días',
  'pages.analytics.period.365d': 'Últimos 12 meses',
  'pages.analytics.addChart': 'Añadir gráfico',
  'pages.analytics.resetLayout': 'Restablecer disposición',
  'pages.analytics.dragHint': 'Arrastrar para reordenar',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Quitar este gráfico',
  'pages.analytics.kind.completion_line': 'Finalización en el tiempo',
  'pages.analytics.kind.learners_bar': 'Estudiantes por módulo',
  'pages.analytics.kind.status_pie': 'Distribución de estados',
  'pages.analytics.kind.engagement_area': 'Tiempo de participación',
  'pages.analytics.series.completion': 'Tasa de finalización (%)',
  'pages.analytics.series.learners': 'Estudiantes',
  'pages.analytics.series.hours': 'Horas',
  'pages.analytics.status.completed': 'Completado',
  'pages.analytics.status.in_progress': 'En curso',
  'pages.analytics.status.dropped': 'Abandonado',
  'pages.analytics.status.not_started': 'No iniciado',
  'pages.analytics.loadError': 'No se pudieron cargar los datos de análisis.',
  'pages.analytics.retry': 'Reintentar',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'lun',
  'pages.analytics.mock.wd1': 'mar',
  'pages.analytics.mock.wd2': 'mié',
  'pages.analytics.mock.wd3': 'jue',
  'pages.analytics.mock.wd4': 'vie',
  'pages.analytics.mock.wd5': 'sáb',
  'pages.analytics.mock.wd6': 'dom',
  'pages.analytics.emptyCharts': 'Añada al menos un gráfico para ver los datos.',
  'pages.analytics.comingSoonDesc':
    'Vea estadísticas de formaciones, tasas de finalización, participación y rendimiento global.',
  'dashboard.searchProjects': 'Buscar proyectos...',
  'dashboard.noProjects': 'Todavía no hay proyectos',
  'dashboard.createFirst': 'Crea tu primer proyecto para empezar',
  'dashboard.createProject': 'Crear proyecto',
  'dashboard.projectTitle': 'Título del proyecto',
  'project.status.draft': 'Borrador',
  'project.status.published': 'Publicado',
  'project.status.archived': 'Archivado',
  'eduflowList.status.draft': 'Borrador',
  'eduflowList.status.published': 'Publicado',
  'eduflowList.status.archived': 'Archivado',
  'eduflowList.badge.enterpriseShell': 'Solicitud empresa',
  'eduflowList.badge.enterpriseLinked': 'Empresa',
  'eduflowList.badge.toProduce': 'Por producir',
  'learner.sidebar.tagline': 'Aprendiz',
  'player.next': 'Siguiente',
  'player.image.clickForAnnotations': 'Haz clic en la imagen para ver las anotaciones',
  'player.previous': 'Anterior',
  'player.complete': 'Completar',
  'player.exit': 'Salir',
  'player.exitConfirmTitle': '¿Salir de la formación?',
  'player.exitConfirmMessage': 'Tu progreso será guardado. ¿Estás seguro de que quieres salir?',
  'player.focusLostTitle': 'Formación en pausa',
  'player.focusLostMessage': 'Has salido de la ventana de formación. El tiempo está en pausa.',
  'player.focusLostResume': 'Haz clic para reanudar',
  'player.focusLostRemaining': 'Salidas restantes antes del bloqueo: {{remaining}}',
  'player.focusBlockedTitle': 'Formación bloqueada',
  'player.focusBlockedMessage':
    'Has superado el número de salidas permitidas ({{max}}). Ya no puedes continuar esta formación. Contacta con tu formador o referente.',
  'player.cancel': 'Cancelar',
  'player.summary': 'Resumen',
  'player.contents': 'Contenido',
  'player.progress': 'Progreso',
  'player.block': 'Bloque',
  'player.parallelBranches': 'Ramas paralelas',
  'player.parallelBranchesTitle': '¿Qué sigue?',
  'player.parallelBranchesHint': 'Las demás opciones se ofrecerán después.',
  'player.parallelBranchShowFull': 'Ver más',
  'player.parallelBranchShowLess': 'Ver menos',
  'player.parallelBranchChoose': 'Abrir',
  'player.parallelBranchDismiss': 'Cerrar',
  'player.parallelBranchDismissHint':
    'Volver al bloque. Usa « Siguiente » para elegir una rama.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Tomar notas...',
  'player.noBlocks': 'No hay bloques que mostrar.',
  'player.contentTranslatingTitle': 'Adaptando el contenido a tu idioma…',
  'player.contentTranslatingHint':
    'Traducción en curso. La primera carga puede tardar un poco más.',
  'player.textBlockTipsHeading': 'Consejos',
  'player.textBlockReadDone': 'Lectura completada',
  'player.defaultFlowTitle': 'Formación',
  'player.docTitle.error': 'Formación no disponible',
  'player.docTitle.completed': 'Formación completada',
  'player.docTitle.loading': 'Cargando la formación',
  'player.docTitle.fallback': 'Formación',
  'player.initError.title': 'La formación no está disponible temporalmente',
  'player.initError.loadFailed': 'No se pudo cargar la formación en este momento.',
  'player.initError.loadFailedWithDetail':
    'No se pudo cargar la formación en este momento.{{detail}} Vuelva a intentarlo en unos instantes.',
  'player.initError.hintsIntro': 'Puede:',
  'player.initError.hintRefresh': 'actualizar la página;',
  'player.initError.hintConnection': 'comprobar su conexión a Internet;',
  'player.initError.hintRetryLater': 'volver a intentarlo en unos minutos.',
  'api.error.network': 'No se puede contactar con el servidor. Compruebe su conexión.',
  'api.error.unexpected': 'Ha ocurrido un error inesperado.',
  'api.error.badRequest': 'Solicitud no válida.',
  'api.error.forbidden': 'Acceso denegado.',
  'api.error.notFound': 'Recurso no encontrado.',
  'api.error.conflict': 'Conflicto: este recurso ya existe.',
  'api.error.unprocessable': 'Los datos enviados no son válidos.',
  'api.error.tooManyRequests': 'Demasiadas solicitudes. Inténtelo de nuevo en un momento.',
  'api.error.server': 'Error interno del servidor.',
  'api.error.badGateway': 'Servicio temporalmente no disponible.',
  'api.error.maintenance': 'Servicio en mantenimiento.',
  'api.error.generic': 'Error {{status}}',
  'player.textBlockReadMinutes': '{{count}} min de lectura',
  'player.textBlockWordCount_one': '{{count}} palabra',
  'player.textBlockWordCount_other': '{{count}} palabras',
  'player.textBlockNoContent': 'No hay contenido disponible.',
  'player.blockUnknown': 'Bloque desconocido',
  'player.objectivesPanelTitle': 'Objetivos de aprendizaje',
  'player.objectivesOpenLabel': 'Ver objetivos de aprendizaje',
  'player.objectivesCloseLabel': 'Cerrar panel de objetivos',
  'player.objectivesAchievementRate': 'Tasa de logro',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objetivos alcanzados',
  'player.objectivesNoneDefined': 'No hay objetivos definidos para esta formación.',
  'player.objectiveCategory.savoir': 'Saber',
  'player.objectiveCategory.savoirFaire': 'Saber hacer',
  'player.objectiveCategory.savoirEtre': 'Saber ser',
  'player.objectiveLevel.connaissance': 'Conocimiento',
  'player.objectiveLevel.comprehension': 'Comprensión',
  'player.objectiveLevel.application': 'Aplicación',
  'player.objectiveLevel.analyse': 'Análisis',
  'player.objectiveLevel.synthese': 'Síntesis',
  'player.objectiveLevel.evaluation': 'Evaluación',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obligatorio',
  'player.objectiveLinkedBlocks': 'Bloques vinculados',
  'player.objectiveCriterionScore': 'Puntuación ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Finalización del bloque',
  'player.objectiveNoLinkedBlocks':
    'Sin bloque vinculado — el progreso global no mide este objetivo.',
  'player.completion.congratulations': '🎉 ¡Felicitaciones!',
  'player.completion.finished': '📋 Formación completada',
  'player.completion.youCompleted': 'Has completado',
  'player.completion.passed': 'Aprobado ✓',
  'player.completion.failed': 'No aprobado ✗',
  'player.completion.requiredScore': 'Puntuación requerida: {{score}}%',
  'player.completion.totalTime': 'Tiempo total',
  'player.completion.blocksCompleted': 'Bloques completados',
  'player.completion.quizPassed': 'Cuestionarios aprobados',
  'player.completion.successRate': 'Tasa de éxito',
  'player.completion.blockCompleted': 'Completado ✓',
  'player.completion.blockFailed': 'Fallado ✗',
  'player.completion.blockSkipped': 'Omitido',
  'player.completion.detail': '📊 Detalle por bloque',
  'player.completion.type': 'Tipo',
  'player.completion.timeSpent': 'Tiempo empleado',
  'player.completion.downloadCertificate': '📄 Descargar certificado',
  'player.completion.certificateSoon': 'El certificado estará disponible próximamente.',
  'player.completion.restart': '🔄 Reiniciar',
  'player.completion.viewDetails': '📊 Ver detalles',
  'player.completion.backToDashboard': '← Volver al tablero',
  'player.completion.embedDismissHint':
    'Has terminado el módulo. Para salir, usa la X arriba a la derecha o el botón de abajo.',
  'player.completion.embedDismissButton': 'Cerrar ventana',
  'player.previewBanner': 'Modo vista previa — esta vista refleja los datos reales de tu formación',
  'player.closePreview': 'Cerrar vista previa',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Mostrar panel',
  'player.toggleSidebarHide': 'Ocultar panel',
  'player.sidebarExpand': 'Expandir panel',
  'player.sidebarCollapse': 'Contraer panel',
  'player.keyboardShortcuts': 'Atajos de teclado',
  'player.keyboardShortcutPrev': 'Bloque anterior',
  'player.keyboardShortcutNext': 'Bloque siguiente',
  'player.keyboardShortcutsHint':
    'Active « Navegación con teclado » en el centro de accesibilidad (botón abajo a la derecha). Desactivado en campos de texto.',
  'player.a11yCenter': 'Centro de accesibilidad',
  'player.reviewSummary': 'Ver resumen',
  'player.viewResults': 'Ver resultados',
  'player.submitFirst': 'Enviar primero',
  'player.phraseRevealCompleteFirst': 'Vea todas las frases primero',
  'player.phraseReveal.emptyBlock': 'No hay frases configuradas para este bloque.',
  'player.phraseReveal.revealNextAria':
    'Haga clic o pulse Espacio para revelar la siguiente frase',
  'player.phraseReveal.lastPhraseHint':
    'Última frase — use « Siguiente » para continuar el recorrido.',
  'player.phraseReveal.morePhrasesHint': 'Clic, Espacio o flecha derecha para la siguiente frase',
  'player.phraseReveal.previousPhrases': 'Frases anteriores ({{count}})',
  'player.certificateUnavailable': 'El certificado estará disponible próximamente.',
  'player.browser.embeddedContentTitle': 'Contenido integrado',
  'player.browser.noValidUrl': 'URL no válida',
  'player.browser.enterHttpsHint':
    'Introduce una dirección que empiece por https:// (o http://) en los ajustes del bloque.',
  'player.browser.loadingContent': 'Cargando contenido…',
  'player.browser.defaultPageTitle': 'Página web integrada',
  'player.browser.continue': 'Continuar',
  'player.browser.stepSaved': 'Paso guardado',
  'player.browser.fullscreen': 'Pantalla completa',
  'player.browser.exitFullscreen': 'Salir de pantalla completa',
  'player.podcast.defaultShowTitle': 'Pódcast',
  'player.podcast.defaultEpisode': 'Episodio',
  'player.podcast.loadError': 'No se pudo cargar el pódcast.',
  'player.podcast.loadingEpisodes': 'Cargando episodios…',
  'player.podcast.playAll': 'Escuchar todo',
  'player.podcast.nextEpisode': 'Siguiente episodio',
  'player.podcast.episodesHeading': 'Episodios',
  'player.podcast.noEpisodes': 'No hay episodios.',
  'player.podcast.noAudio': 'Sin audio',
  'player.podcast.chainPlayback': 'Reproducción en cadena · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Selecciona un episodio de la lista o pulsa «Escuchar todo».',
  'player.podcast.audioUnavailable': 'Audio no disponible para este episodio.',
  'player.podcast.episodeCount_one': '{{count}} episodio',
  'player.podcast.episodeCount_other': '{{count}} episodios',
  'player.renderError': 'Error al renderizar el bloque: {{blockType}}',
  'player.blocks.defaultWelcome': 'Bienvenido a esta formación',
  'player.blocks.defaultEndTitle': '¡Felicidades!',
  'player.quiz.selectCorrectAnswer': 'Selecciona la respuesta correcta:',
  'player.quiz.correctAnswer': '✓ ¡Respuesta correcta!',
  'player.quiz.incorrectAnswer': '✗ Respuesta incorrecta.',
  'player.blockTimer.aria': 'Tiempo para este bloque',
  'player.blockTimer.modeShortCountup': 'Cronómetro',
  'player.blockTimer.modeShortCountdown': 'Cuenta atrás',
  'player.form.successMessage': '¡Gracias por tu respuesta!',
  'player.form.noFieldsConfigured': 'Este formulario aún no tiene campos configurados.',
  'player.evaluation.commentsLabel': 'Comentarios (opcional)',
  'player.evaluation.commentsPlaceholder': 'Comparte tu opinión...',
  'player.evaluation.submitted': 'Evaluación enviada',
  'player.evaluation.submit': 'Enviar evaluación',
  'player.time.lessThanMinute': 'Menos de un minuto',
  'player.time.oneMinute': '1 minuto',
  'player.time.minutes': '{{count}} minutos',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}} min',
  'player.time.shortMinutesSeconds': '{{minutes}} min {{seconds}}s',
  'player.completion.scoreLabel': 'Puntuación',
  'eduflow.versionHistory': 'Historial de versiones',
  'eduflow.versionHistory.count': '{{count}} versión(es)',
  'eduflow.versionHistory.create': 'Crear una versión',
  'eduflow.versionHistory.createVersion': 'Crear v{{version}}',
  'eduflow.versionHistory.minor': 'Menor',
  'eduflow.versionHistory.major': 'Mayor',
  'eduflow.versionHistory.minorBadge': 'Versión menor',
  'eduflow.versionHistory.majorBadge': 'Versión mayor',
  'eduflow.versionHistory.latest': 'Última',
  'eduflow.versionHistory.public': 'Versión pública',
  'eduflow.versionHistory.publicActive': 'En producción',
  'eduflow.versionHistory.publish': 'Publicar esta versión',
  'eduflow.versionHistory.publishCta': 'Publicar',
  'eduflow.versionHistory.openPublicPlayer': 'Abrir el reproductor público',
  'eduflow.versionHistory.copyPublicPlayer': 'Copiar el enlace público',
  'eduflow.versionHistory.copyPublicPlayerError': 'No se puede copiar el enlace público',
  'eduflow.versionHistory.systemPublished': 'Publicación de {{version}}',
  'eduflow.versionHistory.systemSaved': 'Guardado {{version}}',
  'eduflow.versionHistory.systemAutosaveRestore': 'Guardado automático antes de restaurar {{version}}',
  'eduflow.versionHistory.descriptionPlaceholder': 'Describe los cambios…',
  'eduflow.versionHistory.empty': 'Todavía no hay versiones creadas.',
  'eduflow.versionHistory.emptyHint': 'Guarda una versión para acceder a ella aquí.',
  'eduflow.versionHistory.blocks': '{{count}} bloques',
  'eduflow.versionHistory.restoreTitle': '¿Restaurar la versión v{{version}}?',
  'eduflow.versionHistory.restoreWarning': 'El contenido actual será reemplazado. Se creará una copia de seguridad automática antes de la restauración.',
  'eduflow.versionHistory.createSuccess': 'Versión {{version}} creada',
  'eduflow.versionHistory.createError': 'Error al crear la versión',
  'eduflow.versionHistory.restoreSuccess': 'Versión {{version}} restaurada',
  'eduflow.versionHistory.restoreError': 'Error al restaurar la versión',
  'eduflow.versionHistory.publishSuccess': 'Versión {{version}} publicada',
  'eduflow.versionHistory.publishError': 'Error al publicar la versión',
  'eduflow.versionHistory.now': 'ahora mismo',
  'eduflow.versionHistory.minutesAgo': 'hace {{count}} min',
  'eduflow.versionHistory.hoursAgo': 'hace {{count}} h',
  'eduflow.versionHistory.yesterday': 'ayer',
  'eduflow.versionHistory.daysAgo': 'hace {{count}} días',
  // Roles
  'role.teacher': 'Formador',
  'role.institution': 'Institución',
  'role.admin': 'Administrador',
  'role.drh': 'Director de RR. HH.',
  'role.rh': 'Responsable de RR. HH.',
  'role.manager': 'Manager',
  'role.collaborator': 'Colaborador',
  'role.learner': 'Aprendiz',
  'dashboard.calendar.mon': 'Lun',
  'dashboard.calendar.wed': 'Mié',
  'dashboard.calendar.fri': 'Vie',
  'dashboard.calendar.less': 'Menos',
  'dashboard.calendar.more': 'Más',
  'dashboard.calendar.tooltip': '{{count}} actividade(s) el {{date}}',
  'dashboard.mock.updated2h': 'Hace 2 h',
  'dashboard.mock.updated5h': 'Hace 5 h',
  'dashboard.mock.updatedYesterday': 'Ayer',
  'dashboard.home': 'Inicio',
  'dashboard.analytics': 'Analítica',
  'dashboard.settings': 'Ajustes',
  'dashboard.analyticsComingSoon': 'Analítica disponible próximamente',
  'dashboard.settingsComingSoon': 'Ajustes disponibles próximamente',
  'dashboard.period.week': 'Semana',
  'dashboard.period.month': 'Mes',
  'dashboard.period.quarter': 'Trimestre',
  'dashboard.vsLastPeriod': 'frente al período anterior ({{period}})',
  'dashboard.completionPercent': 'Finalización %',
  'dashboard.activeLearners': 'Aprendices activos',
  'dashboard.sessions': 'Sesiones',
  'dashboard.activityType.completion': 'Completado',
  'dashboard.activityType.enrollment': 'Inscripción',
  'dashboard.activityType.milestone': 'Hito',
  'dashboard.activityType.feedback': 'Feedback',
  'dashboard.activityType.start': 'Inicio',
  'dashboard.totalLearners': 'Total de aprendices',
  'dashboard.activeEduFlows': 'Formaciones activas',
  'dashboard.completionRate': 'Tasa de finalización',
  'dashboard.npsScore': 'Puntuación NPS',
  'dashboard.topEduFlows': 'Mejores formaciones',
  'dashboard.engagement': 'Participación de los aprendices',
  'common.seeAll': 'Ver todo',
  'common.startedOn': 'Empezó el',
  'admin.dashboard.totalUsers': 'Usuarios totales',
  'admin.dashboard.activeToday': 'Activos hoy',
  'admin.dashboard.revenue': 'Ingresos',
  'admin.dashboard.storageUsed': 'Almacenamiento usado',
  'admin.dashboard.vsLastMonth': 'vs el mes pasado',
  'admin.dashboard.userGrowth': 'Crecimiento de usuarios',
  'admin.dashboard.monthlyRevenue': 'Ingresos mensuales (€)',
  'admin.dashboard.recentActivity': 'Actividad reciente',
  'admin.dashboard.systemHealth': 'Salud del sistema',
  'admin.dashboard.quickActions': 'Acciones rápidas',
  'admin.nav.stats': 'Estadísticas',
  'admin.nav.users': 'Usuarios',
  'admin.nav.support': 'Soporte y chat',
  'admin.nav.institutions': 'Instituciones',
  'admin.nav.contracts': 'Contratos',
  'admin.nav.editors': 'Editores',
  'admin.nav.podcasts': 'Podcasts',
  'admin.nav.sounds': 'Sonidos de Lyrra',
  'admin.nav.templates': 'Plantillas de vídeo',
  'admin.nav.templatesImages': 'Plantillas de imagen',
  'admin.nav.tools': 'Herramientas',
  'admin.nav.files': 'Archivos',
  'admin.nav.logs': 'Registros',
  'admin.nav.webhooks': 'Webhooks',
  'admin.nav.email': 'Correo',
  'admin.nav.docusign': 'DocuSign',
  'admin.nav.smtp': 'Configuración SMTP',
  'admin.smtp.pageTitle': 'Configuración SMTP',
  'admin.smtp.pageHint': 'Servidor saliente para el correo transaccional de la plataforma.',
  'admin.docusign.title': 'Integración DocuSign',
  'admin.docusign.subtitle': 'Clave de integración, GUIDs de API y clave privada RSA (JWT). Guardados en base de datos, no en Docker.',
  'admin.docusign.statusOk': 'Configurado',
  'admin.docusign.statusIncomplete': 'Incompleto',
  'admin.docusign.keyStored': 'Clave privada guardada',
  'admin.docusign.consentHint':
    'Para el primer uso JWT, la cuenta DocuSign debe otorgar el consentimiento de la aplicación (URL de DocuSign). Si hay 401/403, revise el PEM, la clave de integración y el consentimiento.',
  'admin.docusign.loading': 'Cargando…',
  'admin.docusign.loadError': 'No se pudo cargar la configuración DocuSign',
  'admin.docusign.saved': 'Configuración DocuSign guardada',
  'admin.docusign.saveError': 'Error al guardar',
  'admin.docusign.save': 'Guardar',
  'admin.docusign.reload': 'Recargar',
  'admin.docusign.fieldIntegrationKey': 'Clave de integración (ID de cliente)',
  'admin.docusign.fieldUserId': 'ID de usuario DocuSign (GUID)',
  'admin.docusign.fieldAccountId': 'ID de cuenta (account ID)',
  'admin.docusign.fieldBasePath': 'URL base de la API REST',
  'admin.docusign.fieldOauthHost': 'Host OAuth (sin https://)',
  'admin.docusign.fieldRsa': 'Clave privada RSA (PEM)',
  'admin.docusign.oauthPlaceholder': 'account-d.docusign.com',
  'admin.docusign.rsaPlaceholderKeep': 'Dejar vacío para conservar la clave actual',
  'admin.docusign.rsaPlaceholderNew': 'Pegue el .pem completo (BEGIN … END)',
  'admin.smtp.cardTitle': 'Correo saliente (SMTP)',
  'admin.smtp.subtitle':
    'Servidor, puerto, credenciales y remitente. Guardado en base de datos — sin variables Docker SMTP_* ni EMAIL_FROM.',
  'admin.smtp.host': 'Servidor SMTP',
  'admin.smtp.port': 'Puerto',
  'admin.smtp.secure': 'TLS/SSL (p. ej. puerto 465)',
  'admin.smtp.user': 'Usuario',
  'admin.smtp.password': 'Contraseña',
  'admin.smtp.passwordPlaceholder': 'Dejar vacío para conservar la contraseña actual',
  'admin.smtp.passwordPlaceholderNew': 'Contraseña SMTP',
  'admin.smtp.fromEmail': 'Correo remitente',
  'admin.smtp.fromName': 'Nombre mostrado (From)',
  'admin.smtp.hint': 'La mayoría de proveedores requieren usuario y contraseña. Pruebe con la herramienta de email del admin tras guardar.',
  'admin.smtp.save': 'Guardar SMTP',
  'admin.smtp.saved': 'Configuración SMTP guardada',
  'admin.smtp.saveError': 'Error al guardar',
  'admin.smtp.loadError': 'No se pudo cargar SMTP',
  'admin.smtp.loading': 'Cargando…',
  'admin.smtp.configured': 'Configuración guardada',
  'admin.smtp.notConfigured': 'Incompleto',
  'admin.shell.title': 'Administración',
  'admin.shell.expandSidebar': 'Expandir barra lateral',
  'admin.shell.collapseSidebar': 'Contraer barra lateral',
  'admin.shell.collapse': 'Contraer',
  'manager.page.documentTitle': 'Espacio manager - LyrraStudio',
  'manager.page.title': 'Espacio manager',
  'manager.page.subtitle': 'Gestiona las formaciones y el progreso de tu equipo',
  'manager.page.tabs.dashboard': 'Panel',
  'manager.page.tabs.team': 'Equipo',
  'manager.page.tabs.requests': 'Solicitudes',
  'manager.page.tabs.reviews': 'Evaluaciones',
  'collab.page.documentTitle': 'Mis formaciones - LyrraStudio',
  'collab.page.title': 'Mis formaciones',
  'collab.page.subtitle': 'Sigue tu progreso y gestiona tus formaciones',
  'collab.page.tabs.dashboard': 'Panel',
  'collab.page.tabs.skills': 'Mis competencias',
  'collab.page.tabs.request': 'Hacer una solicitud',
  'collab.page.tabs.certificates': 'Certificados',
  'rh.page.documentTitle': 'Panel de RR. HH. - LyrraStudio',
  'rh.compliancePage.documentTitle': 'Conformidad y Qualiopi - LyrraStudio',
  'rh.compliancePage.title': 'Conformidad y Qualiopi',
  'rh.compliancePage.subtitle': 'Gestiona la conformidad reglamentaria y la certificación Qualiopi',
  'rh.compliancePage.tabs.dashboard': 'Panel',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.dashboard.activeCampaigns': 'Campañas activas',
  'rh.dashboard.pendingRequestsCount': 'Solicitudes pendientes',
  'rh.dashboard.completionRateLabel': 'Tasa de finalización',
  'rh.dashboard.budgetUsed': 'Presupuesto utilizado',
  'rh.dashboard.progressLabel': 'Progreso (%)',
  'rh.dashboard.justNow': 'Ahora mismo',
  'rh.status.urgent': 'Urgente',
  'rh.status.soon': 'Pronto',
  'rh.status.upcoming': 'Próximo',
  'rh.status.pending': 'Pendiente',
  'rh.status.approved': 'Aprobado',
  'rh.status.rejected': 'Rechazado',
  'accessibility.openCenter': 'Abrir el centro de accesibilidad',
  'accessibility.center': 'Accesibilidad',
  'accessibility.modalTitle': 'Centro de lectura adaptada',
  'accessibility.modalDescription': 'Ajusta la interfaz para baja visión, dislexia y una lectura más cómoda.',
  'accessibility.closeCenter': 'Cerrar el centro de accesibilidad',
  'accessibility.accessPad.connected': 'Lyrra Access Pad conectado',
  'accessibility.accessPad.playGuide': 'Escuchar la guía LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Pausar la guía LyrraPad',
  'accessibility.presets': 'Perfiles preconfigurados',
  'accessibility.preset.standard.label': 'Estándar',
  'accessibility.preset.standard.desc': 'Renderizado predeterminado de la aplicación.',
  'accessibility.preset.lowVision.label': 'Baja visión',
  'accessibility.preset.lowVision.desc': 'Mayor contraste, texto ampliado y señales visuales más claras.',
  'accessibility.preset.dys.label': 'Dislexia',
  'accessibility.preset.dys.desc': 'Lectura más fácil con más espaciado y una fuente más legible.',
  'accessibility.typography': 'Ajustes tipográficos',
  'accessibility.fontScale': 'Tamaño del texto',
  'accessibility.lineHeight': 'Interlineado',
  'accessibility.letterSpacing': 'Espaciado entre letras',
  'accessibility.wordSpacing': 'Espaciado entre palabras',
  'accessibility.visualAids': 'Ayudas visuales',
  'accessibility.highContrast': 'Alto contraste',
  'accessibility.highContrastDesc': 'Refuerza el contraste de lectura, los bordes y las señales visuales.',
  'accessibility.underlineLinks': 'Enlaces subrayados',
  'accessibility.underlineLinksDesc': 'Hace más visibles los enlaces y las zonas interactivas.',
  'accessibility.dyslexiaFont': 'Fuente de lectura facilitada',
  'accessibility.dyslexiaFontDesc': 'Usa una fuente más cómoda para personas con dislexia y lectores fatigados.',
  'accessibility.reducedMotion': 'Movimiento reducido',
  'accessibility.reducedMotionDesc': 'Reduce animaciones y transiciones para limitar la incomodidad visual.',
  'accessibility.mouseMagnifier': 'Lupa de lectura',
  'accessibility.mouseMagnifierDesc':
    'Muestra una lupa circular que sigue el cursor y amplía el contenido del curso bajo el puntero (solo lectura).',
  'accessibility.alwaysOn': 'Mejoras siempre activas',
  'accessibility.alwaysOnFocus': 'Foco de teclado más visible',
  'accessibility.alwaysOnLinks': 'Enlaces y elementos interactivos más fáciles de detectar',
  'accessibility.alwaysOnSaved': 'Ajustes guardados automáticamente en este dispositivo',
  'accessibility.navigation': 'Navegación',
  'accessibility.keyboardNavigation': 'Navegación con teclado',
  'accessibility.keyboardNavigationDesc':
    'Flecha izquierda/arriba para el bloque anterior, derecha/abajo para el siguiente (como los botones inferiores).',
  // ── Injected from locales/es.json ──
  'producerAssignments.createInDesigner': 'Crear / abrir formación',
  'producerAssignments.draft': 'Borrador',
  'producerAssignments.enterprise': 'Empresa',
  'producerAssignments.enterprisePaths': 'Itinerarios empresa',
  'producerAssignments.episodes': 'ep.',
  'producerAssignments.errorForbidden': 'No tiene acceso a esta página.',
  'producerAssignments.errorGeneric': 'No se pudieron cargar sus misiones.',
  'producerAssignments.errorNoInstitution': 'No está vinculado a ningún establecimiento.',
  'producerAssignments.formations': 'Fichas de formación',
  'producerAssignments.intro': 'Esta página agrupa las fichas de formación y podcasts que le ha asignado la dirección de RRHH de su establecimiento. Cree sus contenidos en el diseñador EduFlow o el estudio de podcasts, y actualice el estado de producción.',
  'producerAssignments.markCompleted': 'Marcar como completado',
  'producerAssignments.markInProgress': 'En producción',
  'producerAssignments.markReady': 'Listo',
  'producerAssignments.modulesCatalog': '{{count}} módulo(s) catálogo · actualizado {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Ninguna solicitud de itinerario empresa.',
  'producerAssignments.noneFormations': 'Ninguna ficha asignada.',
  'producerAssignments.nonePodcasts': 'Ningún podcast asignado.',
  'producerAssignments.openInDesigner': 'Abrir en el diseñador',
  'producerAssignments.openPodcasts': 'Estudio de podcasts',
  'producerAssignments.planningDeadline': 'Plazo {{date}}',
  'producerAssignments.planningFrom': 'A partir del {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts a producir',
  'producerAssignments.productionStatus.completed': 'Completado',
  'producerAssignments.productionStatus.in_progress': 'En curso',
  'producerAssignments.productionStatus.not_started': 'Sin empezar',
  'producerAssignments.productionStatus.published': 'Publicado',
  'producerAssignments.publishedCatalog': 'Publicado en catálogo',
  'producerAssignments.rhPlanning': 'Planificación RRHH',
  'producerAssignments.subtitle': 'Formaciones a crear en el diseñador y podcasts a producir, asignados por RRHH.',
  'producerAssignments.title': 'Mis misiones de producción',
  'podcasts.actions.cancel': 'Cancelar',
  'podcasts.actions.close': 'Cerrar',
  'podcasts.actions.create': 'Crear',
  'podcasts.actions.edit': 'Editar',
  'podcasts.actions.retry': 'Reintentar',
  'podcasts.actions.save': 'Guardar',
  'podcasts.actions.saving': 'Guardando…',
  'podcasts.actions.share': 'Compartir',
  'podcasts.category.ariaGroup': 'Categoría del podcast',
  'podcasts.category.emptyBanner': 'Añada categorías en el espacio Institución para organizar mejor los podcasts.',
  'podcasts.category.emptyInstitutionHint': 'Aún no hay categorías de la institución — puede publicar sin ellas.',
  'podcasts.category.noneChip': 'Sin categoría',
  'podcasts.category.sectionTitle': 'Categoría',
  'podcasts.category.withCategoriesHint': 'Clasificación en el catálogo y filtros en el área de la institución.',
  'podcasts.cover.ariaDropzone': 'Elegir o soltar una imagen de portada',
  'podcasts.cover.browseFiles': 'explorar archivos',
  'podcasts.cover.cancelNewFile': 'Cancelar el nuevo archivo',
  'podcasts.cover.dragDrop': 'Arrastre y suelte una imagen',
  'podcasts.cover.dropHere': 'Suelte la imagen aquí',
  'podcasts.cover.errorMaxSize': 'La imagen no debe superar 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Formato de imagen no admitido.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · máx. 5 MB',
  'podcasts.cover.help': 'Se recomienda formato cuadrado. Arrastre y suelte un archivo o haga clic para explorar.',
  'podcasts.cover.or': 'o',
  'podcasts.cover.replaceHint': 'Sustituir la imagen',
  'podcasts.cover.sectionTitle': 'Imagen de portada',
  'podcasts.deleteDialog.body': '« {{title}} » se retirará de la lista (eliminación lógica). Los episodios no se borran. Si su cuenta está vinculada a un centro, un administrador puede restaurar el podcast desde la papelera del espacio Institución.',
  'podcasts.deleteDialog.confirm': 'Mover a la papelera',
  'podcasts.deleteDialog.title': '¿Mover el podcast a la papelera?',
  'podcasts.editPanel.createdOn': 'Creado el ',
  'podcasts.editPanel.headerTitle': 'Editar podcast',
  'podcasts.editPanel.saving': 'Guardando…',
  'podcasts.editPanel.statsEpisodes': 'Episodios: ',
  'podcasts.editPanel.statsSubscribers': 'Suscriptores: ',
  'podcasts.editPanel.successSaved': 'Cambios guardados',
  'podcasts.episodeStatus.draft': 'Borrador',
  'podcasts.episodeStatus.published': 'Publicado',
  'podcasts.episodesPanel.addEpisode': 'Añadir episodio',
  'podcasts.episodesPanel.emptySubtitle': 'Añada episodios a este podcast',
  'podcasts.episodesPanel.emptyTitle': 'Ningún episodio',
  'podcasts.episodesPanel.episodeCount': '{{count}} episodio(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'No hay episodios disponibles',
  'podcasts.episodesPanel.remove': 'Quitar',
  'podcasts.episodesPanel.searchPlaceholder': 'Buscar un episodio…',
  'podcasts.episodesPanel.title': 'Episodios del podcast',
  'podcasts.error.retry': 'Reintentar',
  'podcasts.filters.allCategories': 'Todas las categorías',
  'podcasts.filters.categoryLabel': 'Categoría',
  'podcasts.filters.searchLabel': 'Búsqueda de texto',
  'podcasts.filters.searchPlaceholder': 'Título, descripción…',
  'podcasts.filters.viewCategories': 'Vista categorías',
  'podcasts.filters.viewList': 'Vista lista',
  'podcasts.form.descriptionLabel': 'Descripción',
  'podcasts.form.descriptionPlaceholder': 'Describa su podcast…',
  'podcasts.form.namePlaceholder': 'Nombre del podcast',
  'podcasts.form.titleLabel': 'Título',
  'podcasts.language.ariaGroup': 'Idioma del podcast',
  'podcasts.language.help': 'Entre los 11 idiomas de la plataforma — para alinear el podcast con la misma configuración regional que el reproductor y la interfaz del aprendiz.',
  'podcasts.language.label': 'Idioma',
  'podcasts.lightbox.ariaClose': 'Cerrar',
  'podcasts.lightbox.ariaDialog': 'Vista previa de portada del podcast',
  'podcasts.list.actionActivate': 'Activar',
  'podcasts.list.actionDeactivate': 'Desactivar',
  'podcasts.list.actionDelete': 'Eliminar',
  'podcasts.list.actionManageEpisodes': 'Gestionar episodios',
  'podcasts.list.actionPreview': 'Vista previa y escucha',
  'podcasts.list.coverPreviewAria': 'Vista previa de portada del podcast {{title}}',
  'podcasts.list.createFirst': 'Crear un podcast',
  'podcasts.list.emptySubtitle': 'Cree su primer podcast para empezar',
  'podcasts.list.emptyTitle': 'Ningún podcast',
  'podcasts.list.loadError': 'No se pudieron cargar los podcasts.',
  'podcasts.list.enterpriseBadge': 'Empresa',
  'podcasts.list.enterpriseNeedTitle': 'Necesidad definida por su empresa',
  'podcasts.list.episodesShort': '{{count}} ep.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} suscr.',
  'podcasts.list.uncategorized': 'Sin categoría',
  'podcasts.modal.errorRequiredFields': 'El título y la descripción son obligatorios.',
  'podcasts.modal.errorSaveFailed': 'Error al guardar.',
  'podcasts.modal.subtitleNew': 'Indique el título, la descripción y las opciones. Podrá añadir episodios después.',
  'podcasts.modal.titleEdit': 'Editar podcast',
  'podcasts.modal.titleNew': 'Nuevo podcast',
  'podcasts.page.episodesLink': 'Episodios',
  'podcasts.page.newButton': 'Nuevo',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Página {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) en total',
  'podcasts.player.listen': 'Escuchar',
  'podcasts.player.noAudio': 'Sin audio',
  'podcasts.player.pause': 'Pausa',
  'podcasts.preview.emptySubtitle': 'Este podcast aún no tiene episodios',
  'podcasts.preview.emptyTitle': 'Ningún episodio',
  'podcasts.preview.episodeCount': '{{count}} episodio(s)',
  'podcasts.preview.fallbackDescription': 'Escuche los episodios de audio disponibles.',
  'podcasts.preview.playAll': 'Escuchar todo',
  'podcasts.preview.title': 'Vista previa del podcast',
  'podcasts.share.assignedLearnersHeading': '{{count}} aprendiz(es) asignado(s)',
  'podcasts.share.copied': 'Copiado',
  'podcasts.share.copyLink': 'Copiar enlace',
  'podcasts.share.emailPlaceholder': 'Correo *',
  'podcasts.share.errorCreateLearner': 'No se pudo crear el aprendiz.',
  'podcasts.share.errorPublicLink': 'No se puede generar el enlace público.',
  'podcasts.share.errorRemoveLearner': 'No se pudo quitar el aprendiz.',
  'podcasts.share.errorShare': 'No se puede compartir este podcast.',
  'podcasts.share.firstNamePlaceholder': 'Nombre *',
  'podcasts.share.generatingLink': 'Generando enlace…',
  'podcasts.share.hideQuickCreate': 'Ocultar creación rápida',
  'podcasts.share.lastNamePlaceholder': 'Apellido *',
  'podcasts.share.loadingAssigned': 'Cargando…',
  'podcasts.share.loadingLearners': 'Cargando aprendices...',
  'podcasts.share.messageAccessRemoved': 'Acceso retirado al aprendiz.',
  'podcasts.share.messageCreatedAndShared': 'Cuenta creada/vinculada, podcast compartido y correo de invitación enviado.',
  'podcasts.share.messageShared': 'Podcast compartido e invitación enviada al aprendiz.',
  'podcasts.share.noAssignedLearners': 'No hay aprendices asignados a este podcast.',
  'podcasts.share.noLearnerAvailable': 'No hay aprendices disponibles.',
  'podcasts.share.panelTitle': 'Compartir podcast',
  'podcasts.share.publicLinkSection': 'Enlace público (sin inicio de sesión)',
  'podcasts.share.removeAccessTitle': 'Quitar acceso',
  'podcasts.share.searchLearnerPlaceholder': 'Buscar aprendiz (nombre, apellido, correo)',
  'podcasts.share.selectExistingLearnerLabel': 'Seleccionar un aprendiz existente',
  'podcasts.share.showQuickCreate': 'Crear nuevo aprendiz',
  'podcasts.share.submitCreateAndInvite': 'Crear, compartir y enviar invitación',
  'podcasts.status.active': 'Activo',
  'podcasts.status.inactive': 'Inactivo',
  'learningPaths.card.contents': '{{count}} contenido(s)',
  'learningPaths.card.delete': 'Eliminar',
  'learningPaths.card.edit': 'Editar',
  'learningPaths.card.learners': '{{count}} aprendiz(es)',
  'learningPaths.card.publish': 'Publicar',
  'learningPaths.card.unpublish': 'Despublicar',
  'learningPaths.card.updatedAt': 'Actualizado el {{date}}',
  'learningPaths.createPanel.cancel': 'Cancelar',
  'learningPaths.createPanel.description': 'Ponga nombre a su itinerario; podrá añadir pódcasts y formaciones.',
  'learningPaths.createPanel.descriptionLabel': 'Descripción',
  'learningPaths.createPanel.descriptionPlaceholder': 'Objetivo del itinerario, público objetivo…',
  'learningPaths.createPanel.nameLabel': 'Nombre del itinerario',
  'learningPaths.createPanel.namePlaceholder': 'Ej.: Itinerario de incorporación de RR. HH.…',
  'learningPaths.createPanel.submit': 'Crear itinerario',
  'learningPaths.createPanel.title': 'Nuevo itinerario',
  'learningPaths.deleteDialog.body': 'Va a eliminar « {{title}} ». Esta acción es irreversible.',
  'learningPaths.deleteDialog.cancel': 'Cancelar',
  'learningPaths.deleteDialog.confirm': 'Eliminar',
  'learningPaths.deleteDialog.title': 'Eliminar itinerario',
  'learningPaths.empty.createFirst': 'Crear un itinerario',
  'learningPaths.empty.noPathsHint': 'Cree su primer itinerario combinando pódcasts y formaciones.',
  'learningPaths.empty.noPathsYet': 'Aún no hay itinerarios',
  'learningPaths.empty.noResults': 'Sin resultados',
  'learningPaths.empty.noResultsHint': 'Modifique los filtros o cree un nuevo itinerario.',
  'learningPaths.error.loadFailed': 'No se pudieron cargar los itinerarios',
  'learningPaths.error.retry': 'Reintentar',
  'learningPaths.filters.allStatuses': 'Todos los estados',
  'learningPaths.filters.draft': 'Borrador',
  'learningPaths.filters.published': 'Publicado',
  'learningPaths.filters.searchPlaceholder': 'Buscar un itinerario…',
  'learningPaths.page.newButton': 'Nuevo itinerario',
  'learningPaths.page.subtitle': 'Cree itinerarios de formación ordenados',
  'learningPaths.page.title': 'Itinerarios',
  'learningPaths.page.totalCount': '{{count}} itinerarios',
  'learningPaths.pagination.next': 'Siguiente',
  'learningPaths.pagination.pageOf': 'Página {{page}} / {{total}} · {{count}} itinerarios',
  'learningPaths.pagination.previous': 'Anterior',
  'learningPaths.status.draft': 'Borrador',
  'learningPaths.status.published': 'Publicado',
  'webinars.card.code': 'Código {{code}}',
  'webinars.card.copyLink': 'Copiar enlace',
  'webinars.card.delete': 'Eliminar',
  'webinars.card.edit': 'Editar',
  'webinars.card.openRoom': 'Abrir la sala',
  'webinars.card.publicSignupBadge': 'Inscripción pública',
  'webinars.deleteDialog.body': '¿Eliminar «{{title}}»? Esta acción es irreversible.',
  'webinars.deleteDialog.cancel': 'Cancelar',
  'webinars.deleteDialog.confirm': 'Eliminar',
  'webinars.deleteDialog.title': 'Eliminar el webinario',
  'webinars.eligibility.default': 'Los webinarios no están disponibles para su cuenta (compruebe la conexión a la API o contacte con su administrador).',
  'webinars.eligibility.moduleDisabled': 'El módulo de webinarios aún no está activado para su centro. Un administrador debe marcar «Activar webinarios» en Centro → Webinarios.',
  'webinars.eligibility.noInstitution': 'Ningún centro está asociado a su cuenta.',
  'webinars.eligibility.notAuthorized': 'Su cuenta no figura en la lista de anfitriones autorizados. Pida al centro que le añada (Gestión de webinarios → Anfitriones autorizados).',
  'webinars.eligibility.roleNotAllowed': 'Su rol no permite animar webinarios con esta cuenta.',
  'webinars.empty.createFirst': 'Crear un webinario',
  'webinars.empty.noResults': 'Sin resultados',
  'webinars.empty.noResultsHint': 'Modifique su búsqueda.',
  'webinars.empty.noWebinarsHint': 'Cree una sala e invite a sus participantes desde el panel lateral.',
  'webinars.empty.noWebinarsYet': 'Aún no hay webinarios',
  'webinars.externalInvite.closeButton': 'Cerrar',
  'webinars.externalInvite.description': 'Personas que no figuran en la lista de aprendices: cada dirección recibe un correo con el enlace de la sala y un adjunto para el calendario.',
  'webinars.externalInvite.done': 'Hecho.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} ya invitado',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} ya invitados',
  'webinars.externalInvite.emailsHint': 'Una dirección por línea, o separadas por comas. Se ignoran duplicados e invitaciones ya enviadas.',
  'webinars.externalInvite.emailsLabel': 'Direcciones de correo',
  'webinars.externalInvite.emailsPlaceholder': 'juan.perez@ejemplo.es\nmaria@dominio.org',
  'webinars.externalInvite.errorMax200': 'Máximo 200 direcciones por envío.',
  'webinars.externalInvite.errorMinOne': 'Introduzca al menos una dirección de correo válida.',
  'webinars.externalInvite.errorsLabel': 'errores: {{errors}}',
  'webinars.externalInvite.sendButton': 'Enviar invitaciones',
  'webinars.externalInvite.sendFailed': 'Error al enviar',
  'webinars.externalInvite.sentCount_one': '{{count}} correo enviado',
  'webinars.externalInvite.sentCount_other': '{{count}} correos enviados',
  'webinars.externalInvite.skippedCount_one': '{{count}} omitida',
  'webinars.externalInvite.skippedCount_other': '{{count}} omitidas',
  'webinars.externalInvite.title': 'Invitaciones por correo',
  'webinars.invite.duplicatesCount_one': '{{count}} ya invitado',
  'webinars.invite.duplicatesCount_other': '{{count}} ya invitados',
  'webinars.invite.errorsLabel': 'errores: {{errors}}',
  'webinars.invite.externalButton': 'Fuera de lista (correo)',
  'webinars.invite.failed': 'Error',
  'webinars.invite.groupSuccess': 'Invitaciones de grupo procesadas (correos enviados a los miembros).',
  'webinars.invite.inviteButton': 'Invitar',
  'webinars.invite.learnersHelp': 'Busque un aprendiz de su centro: se envía un <strong>correo de invitación</strong> (enlace + agenda) con cada invitación.',
  'webinars.invite.learnersTitle': 'Aprendices',
  'webinars.invite.noCandidates': 'Ningún aprendiz que mostrar (pruebe otra búsqueda o use «Fuera de lista» para correos externos).',
  'webinars.invite.noInvitations': 'Ninguna invitación registrada.',
  'webinars.invite.searchLabel': 'Buscar un aprendiz',
  'webinars.invite.searchPlaceholder': 'Nombre o correo…',
  'webinars.invite.searching': 'Buscando…',
  'webinars.invite.sentCount_one': '{{count}} invitación enviada (correo)',
  'webinars.invite.sentCount_other': '{{count}} invitaciones enviadas (correo)',
  'webinars.invite.sentInvitationsToggle': 'Invitaciones enviadas',
  'webinars.page.newButton': 'Nuevo webinario',
  'webinars.page.searchPlaceholder': 'Buscar…',
  'webinars.page.subtitle': 'Lista de sus salas — abra el panel para crear, editar o invitar participantes.',
  'webinars.page.title': 'Webinarios',
  'webinars.panel.backgroundHint': 'URL https (o http) mostrada detrás del contenido compartido para los participantes. Déjela vacía para usar el visual Lyrra integrado.',
  'webinars.panel.backgroundLabel': 'Imagen de fondo (vista participante)',
  'webinars.panel.backgroundPlaceholder': 'https://… (vacío = fondo Lyrra predeterminado)',
  'webinars.panel.copyLink': 'Copiar enlace',
  'webinars.panel.createButton': 'Crear',
  'webinars.panel.createDescription': 'Complete la información y guarde. Podrá invitar participantes después.',
  'webinars.panel.createTitle': 'Nuevo webinario',
  'webinars.panel.dateLabel': 'Día (opcional)',
  'webinars.panel.deleteButton': 'Eliminar',
  'webinars.panel.descriptionLabel': 'Descripción',
  'webinars.panel.durationError': 'Indique una duración en formato horas:minutos (ej. 1:00 o 0:45).',
  'webinars.panel.durationHint': 'Obligatorio si se indica un día. Ej. 1:30 para 1 h 30, 0:45 para 45 min.',
  'webinars.panel.durationLabel': 'Duración (h:mm)',
  'webinars.panel.editTitle': 'Webinario',
  'webinars.panel.loading': 'Cargando…',
  'webinars.panel.openRoom': 'Abrir la sala',
  'webinars.panel.presentersLabel': 'Copresentadores',
  'webinars.panel.roomCode': 'Código de sala:',
  'webinars.panel.timeHint': 'Si está vacío: 9:00.',
  'webinars.panel.timeLabel': 'Hora de inicio',
  'webinars.panel.titleLabel': 'Título',
  'webinars.panel.waitingHtmlLabel': 'HTML de sala de espera (opcional)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Bienvenido…</p>',
  'webinars.panel.publicAccessTitle': 'Inscripción pública (formulario)',
  'webinars.panel.publicAccessHelp':
    'Los participantes que no son copresentadores rellenan un formulario tras iniciar sesión en Lyrra: correo, contraseña (la eligen ellos) y los campos adicionales que añada abajo.',
  'webinars.panel.publicFormFixedFieldsHint':
    'Correo y contraseña (mín. 8 caracteres) son siempre obligatorios; aquí añade otros campos opcionales u obligatorios.',
  'webinars.panel.publicFormAddFieldButton': 'Añadir campo',
  'webinars.panel.publicFormFieldNumber': 'Campo {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Etiqueta mostrada',
  'webinars.panel.publicFormFieldType': 'Tipo',
  'webinars.panel.publicFormFieldTypeText': 'Texto corto',
  'webinars.panel.publicFormFieldTypeTextarea': 'Texto largo',
  'webinars.panel.publicFormFieldRequired': 'Obligatorio',
  'webinars.panel.publicFormFieldRemove': 'Quitar',
  'webinars.panel.publicFormFieldIdHint': 'Identificador técnico:',
  'webinars.panel.publicFormFieldLabelRequired': 'Cada campo debe tener una etiqueta.',
  'webinars.panel.publicAccessLoginLabel': 'Usuario del formulario de inscripción',
  'webinars.panel.publicAccessPasswordLabel': 'Contraseña del formulario',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Al menos 8 caracteres',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Dejar en blanco para conservar la contraseña actual',
  'webinars.panel.publicAccessPasswordHint':
    'En la creación o en la primera activación: contraseña obligatoria (mín. 8). Después: deje en blanco para no cambiarla.',
  'webinars.panel.publicAccessLoginRequired': 'Indique un usuario para la inscripción pública.',
  'webinars.panel.publicAccessPasswordRequired': 'Defina una contraseña de al menos 8 caracteres.',
  'webinars.panel.publicAccessPasswordMin': 'La contraseña pública debe tener al menos 8 caracteres.',
  'webinars.status.draft': 'Borrador',
  'webinars.status.ended': 'Finalizado',
  'webinars.status.live': 'En directo',
  'webinars.status.scheduled': 'Programado',
  'videos.addPanel.cancel': 'Cancelar',
  'videos.addPanel.continue': 'Continuar',
  'videos.addPanel.description': 'Importa MP4, WebM o MOV para usarlos en tus formaciones.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Haz clic para elegir un vídeo',
  'videos.addPanel.title': 'Añadir un vídeo',
  'videos.addPanel.titleLabel': 'Título',
  'videos.addPanel.titlePlaceholder': 'Título del vídeo…',
  'videos.card.delete': 'Eliminar',
  'videos.card.edit': 'Editar',
  'videos.card.noThumbnail': 'Sin miniatura',
  'videos.card.publishedStudio': 'Publicado (editable en Studio)',
  'videos.deleteDialog.body': '« {{title}} » se eliminará de forma permanente.',
  'videos.deleteDialog.cancel': 'Cancelar',
  'videos.deleteDialog.confirm': 'Eliminar',
  'videos.deleteDialog.title': '¿Eliminar el vídeo?',
  'videos.empty.addButton': 'Añadir un vídeo',
  'videos.empty.description': 'Importa tu primer vídeo para gestionarlo y usarlo en tus formaciones.',
  'videos.empty.title': 'Sin vídeos',
  'videos.page.addButton': 'Añadir un vídeo',
  'videos.page.count_one': '{{count}} vídeo en tu biblioteca',
  'videos.page.count_other': '{{count}} vídeos en tu biblioteca',
  'videos.page.title': 'Vídeos',
  'videos.preview.noFile': 'No hay ningún archivo disponible.',
  'videos.status.draft': 'Borrador',
  'videos.status.published': 'Publicado',
  'sounds.card.delete': 'Eliminar',
  'sounds.card.mute': 'Silenciar',
  'sounds.deleteDialog.body': '« {{title}} » se eliminará de forma permanente del servidor y de su biblioteca.',
  'sounds.deleteDialog.cancel': 'Cancelar',
  'sounds.deleteDialog.confirm': 'Eliminar',
  'sounds.deleteDialog.title': '¿Eliminar este sonido?',
  'sounds.empty.description': 'Importe sus archivos: se guardan en la base de datos y se copian al servidor para no perder nada.',
  'sounds.empty.importButton': 'Importar un sonido',
  'sounds.empty.title': 'Ningún sonido importado',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Cancelar',
  'sounds.importPanel.changeFile': 'Cambiar archivo',
  'sounds.importPanel.createTag': 'Crear « {{tag}} »',
  'sounds.importPanel.description': 'Añada un archivo de audio a su biblioteca para sus itinerarios de formación.',
  'sounds.importPanel.descriptionLabel': 'Descripción',
  'sounds.importPanel.descriptionPlaceholder': 'Describa este sonido…',
  'sounds.importPanel.dropzoneBrowse': 'examine',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Arrastre un archivo de audio o',
  'sounds.importPanel.optional': '(opcional)',
  'sounds.importPanel.submit': 'Importar',
  'sounds.importPanel.submitting': 'Enviando…',
  'sounds.importPanel.tagsHint': 'Escriba y pulse Intro · Las etiquetas nuevas se crean automáticamente',
  'sounds.importPanel.tagsLabel': 'Etiquetas',
  'sounds.importPanel.tagsPlaceholder': 'Añadir etiquetas…',
  'sounds.importPanel.title': 'Importar un sonido',
  'sounds.importPanel.titleLabel': 'Título',
  'sounds.importPanel.titlePlaceholder': 'Nombre del sonido…',
  'sounds.importPanel.titleRequired': 'El título es obligatorio',
  'sounds.noResults': 'Ningún sonido coincide con « {{query}} »',
  'sounds.page.count_one': '{{count}} archivo — guardado en la base de datos y en el servidor',
  'sounds.page.count_other': '{{count}} archivos — guardados en la base de datos y en el servidor',
  'sounds.page.importButton': 'Importar',
  'sounds.page.searchPlaceholder': 'Título, descripción, etiqueta…',
  'sounds.page.title': 'Sonidos',
  'documents.count_one': '{{count}} documento',
  'documents.count_other': '{{count}} documentos',
  'documents.delete.body': 'El documento «{{filename}}» se eliminará de su biblioteca.',
  'documents.delete.cancel': 'Cancelar',
  'documents.delete.confirm': 'Eliminar',
  'documents.delete.irreversible': 'Esta acción es irreversible.',
  'documents.delete.title': '¿Eliminar el documento?',
  'documents.importPanel.cancel': 'Cancelar',
  'documents.importPanel.chooseFile': 'Elegir un documento PDF',
  'documents.importPanel.description': 'El documento estará disponible en sus bloques PDF y en esta biblioteca.',
  'documents.importPanel.descriptionLabel': 'Descripción',
  'documents.importPanel.descriptionPlaceholder': 'Descripción (opcional)',
  'documents.importPanel.fileHint': 'El documento estará disponible en su bloque PDF y en esta biblioteca.',
  'documents.importPanel.submit': 'Importar',
  'documents.importPanel.title': 'Importar un PDF',
  'documents.importPanel.titleLabel': 'Título',
  'documents.importPanel.titlePlaceholder': 'Título del documento',
  'documents.importPanel.uploading': 'Subiendo',
  'documents.importPdf': 'Importar un PDF',
  'documents.loading': 'Cargando la biblioteca…',
  'documents.newDocument': 'Nuevo documento',
  'documents.noDocuments': 'Sin documentos',
  'documents.noDocumentsHint': 'Importe sus PDF aquí para reutilizarlos después en el bloque PDF.',
  'documents.noResults': 'Ningún documento coincide con su búsqueda.',
  'documents.pages_one': '{{count}} página',
  'documents.pages_other': '{{count}} páginas',
  'documents.previewTitle': 'Vista previa del documento',
  'documents.searchPlaceholder': 'Buscar un PDF...',
  'documents.share.accessRemoved': 'Acceso retirado para el aprendiz.',
  'documents.share.assignedTitle': 'Aprendices con acceso',
  'documents.share.close': 'Cerrar',
  'documents.share.createAndShare': 'Crear, compartir y enviar la invitación',
  'documents.share.createError': 'No se pudo crear el aprendiz.',
  'documents.share.createLearner': 'Crear un nuevo aprendiz',
  'documents.share.createdSuccess': 'Cuenta creada/vinculada, documento compartido y correo enviado.',
  'documents.share.creating': 'Enviando...',
  'documents.share.emailPlaceholder': 'Correo electrónico *',
  'documents.share.firstNamePlaceholder': 'Nombre *',
  'documents.share.hideCreateLearner': 'Ocultar creación rápida',
  'documents.share.lastNamePlaceholder': 'Apellido *',
  'documents.share.loadingAssigned': 'Cargando…',
  'documents.share.loadingLearners': 'Cargando aprendices...',
  'documents.share.noAssigned': 'Ningún aprendiz asignado a este documento.',
  'documents.share.noLearners': 'No hay aprendices disponibles.',
  'documents.share.removeButton': 'Quitar',
  'documents.share.removeError': 'No se pudo retirar el acceso.',
  'documents.share.searchPlaceholder': 'Buscar un aprendiz (nombre, apellido, correo)',
  'documents.share.selectLearner': 'Seleccionar un aprendiz existente',
  'documents.share.shareButton': 'Compartir',
  'documents.share.shareError': 'No se pudo compartir el documento.',
  'documents.share.sharedSuccess': 'Documento compartido e invitación enviada.',
  'documents.share.title': 'Compartir el documento',
  'documents.subtitle': 'Biblioteca de documentos PDF reutilizables en el diseñador.',
  'documents.title': 'Documentos',
  'documents.toast.importError': 'No se puede importar',
  'documents.toast.importErrorDefault': 'La importación ha fallado.',
  'documents.toast.importSuccess': 'Documento importado',
  'documents.toast.importSuccessDesc': '{{filename}} se ha añadido a su biblioteca de documentos.',
  'documents.updating': 'Actualizando…',
  'help.categories.analytics.description': 'Paneles de control, filtros y exportaciones para dirigir el impacto de las formaciones.',
  'help.categories.analytics.title': 'Analíticas e informes',
  'help.categories.apprenants.description': 'Seguir las inscripciones, las cohortes y el progreso de los participantes.',
  'help.categories.apprenants.title': 'Aprendices, grupos y seguimiento',
  'help.categories.boutique.description': 'Vender o asignar formaciones cuando la tienda está configurada.',
  'help.categories.boutique.title': 'Tienda y monetización',
  'help.categories.demarrage.description': 'Familiarizarse con la interfaz, su perfil y la navegación diaria en Lyrra.',
  'help.categories.demarrage.title': 'Inicio y cuenta',
  'help.categories.donnees.description': 'RGPD, conservación, derechos de las personas y buenas prácticas de seguridad.',
  'help.categories.donnees.title': 'Datos personales y seguridad',
  'help.categories.eduflow.description': 'Diseñar recorridos pedagógicos con bloques, conexiones y lógica de navegación.',
  'help.categories.eduflow.title': 'Recorridos y diseñador EduFlow',
  'help.categories.evaluation.description': 'Cuestionarios, evaluaciones, encuestas y retroalimentación para medir los aprendizajes.',
  'help.categories.evaluation.title': 'Evaluación y pedagogía activa',
  'help.categories.institution.description': 'Configurar su organización, los colaboradores y la estructura de contenidos.',
  'help.categories.institution.title': 'Institución y equipo',
  'help.categories.marque.description': 'Alinear colores, etiquetas y la experiencia del aprendiz con su identidad.',
  'help.categories.marque.title': 'Marca y experiencia de usuario',
  'help.categories.medias.description': 'Gestionar biblioteca, importaciones y herramientas integradas de audio / vídeo / imagen en Lyrra.',
  'help.categories.medias.title': 'Medios y estudio creativo',
  'help.categories.parcours-v2.description': 'Campañas, competencias, integraciones: todo lo que enriquece la gestión de formación.',
  'help.categories.parcours-v2.title': 'Funciones avanzadas y hoja de ruta',
  'help.categories.podcasts.description': 'Crear y difundir contenidos de audio de larga duración fuera del recorrido bloque a bloque.',
  'help.categories.podcasts.title': 'Podcasts y audiolibros',
  'help.categories.publication.description': 'Hacer disponible un recorrido para los aprendices y compartir los accesos adecuados.',
  'help.categories.publication.title': 'Publicación y difusión',
  'help.categories.qualite.description': 'Marcos de calidad franceses: trazabilidad, objetivos y pruebas para las auditorías.',
  'help.categories.qualite.title': 'Calidad, Qualiopi y conformidad pedagógica',
  'help.categories.support.description': 'Ante un bloqueo técnico o funcional, saber dónde buscar y a quién contactar.',
  'help.categories.support.title': 'Solución de problemas y soporte',
  'help.configHint': 'Contenido editorial: conecte sus URLs, artículos o base de conocimientos cuando esté listo.',
  'help.description': 'Documentación y soporte',
  'help.hubIntro': 'Temas que cubren el uso de Lyrra: pronto podrá vincular cada ficha a su documentación, tutoriales o tickets de soporte.',
  'help.modal.expectedAnswer': 'Respuesta esperada:',
  'help.modal.fallbackHighlight1': 'Ficha',
  'help.modal.fallbackHighlight2': 'Plataforma de formación',
  'help.modal.fallbackHighlight3': 'Por completar',
  'help.modal.fallbackSectionHeading': 'Puntos clave',
  'help.modal.fallbackSectionParagraph': 'El contenido detallado de esta ficha será enriquecido o vinculado a su documentación interna.',
  'help.modal.fallbackStep1Desc': 'Use el menú lateral o el panel de control para acceder al módulo correspondiente.',
  'help.modal.fallbackStep1Title': 'Abrir la funcionalidad',
  'help.modal.fallbackStep2Desc': 'Las pantallas le guían: guarde a menudo y pruebe en vista previa cuando esté disponible.',
  'help.modal.fallbackStep2Title': 'Seguir el flujo de trabajo',
  'help.modal.fallbackStep3Desc': 'Anote las particularidades de su institución (procesos de RRHH, nomenclatura, marca) para su equipo.',
  'help.modal.fallbackStep3Title': 'Documentar internamente',
  'help.modal.fallbackTip': 'En caso de duda, verifique su rol (formador, admin, RRHH): los menús mostrados dependen de ello.',
  'help.modal.quiz': 'Quiz de validación',
  'help.modal.related': 'Ver también',
  'help.modal.resources': 'Recursos',
  'help.modal.steps': 'En la práctica',
  'help.modal.tips': 'Consejos',
  'help.noResults': 'Sin resultados para esta búsqueda.',
  'help.onboarding.ai-designer': 'Formación — Asistente IA del diseñador LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Formación — Tipos de bloques EduFlow (onboarding)',
  'help.onboarding.branches': 'Formación — Ramas y condiciones EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Formación — Catálogo y visibilidad LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Formación — Certificados emitidos LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Formación — Panel de control Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Formación — Vista general del diseñador EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Formación — Inscripciones y participantes LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Formación — Grupos y cohortes LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Formación — Biblioteca de medios LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Formación — Navegación y menú lateral Lyrra (onboarding)',
  'help.onboarding.objectives': 'Formación — Objetivos pedagógicos EduFlow (onboarding)',
  'help.onboarding.profile': 'Formación — Perfil y preferencias Lyrra (onboarding)',
  'help.onboarding.progress': 'Formación — Progreso y tiempo dedicado LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Formación — Enlaces de acceso y compartir LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Formación — Borrador, publicación y actualizaciones LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Formación — Roles y derechos de acceso Lyrra (onboarding)',
  'help.onboarding.validation': 'Formación — Validación del recorrido EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Formación no encontrada',
  'help.onboardingFlowMissingDesc': 'Ejecute los scripts db:seed:onboarding-eduflow y db:seed:onboarding-navigation-eduflow (backend), o configure las variables VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Abrir la ficha',
  'help.searchPlaceholder': 'Buscar un tema…',
  'help.title': 'Centro de ayuda',
  'help.topics.analytics-exports.description': 'Preparar las extracciones para el reporting interno o reglamentario.',
  'help.topics.analytics-exports.title': 'Exportaciones e informes de RRHH',
  'help.topics.analytics-indicateurs.description': 'Tasa de finalización, participación, distribución por estado y tiempo en los bloques.',
  'help.topics.analytics-indicateurs.title': 'Indicadores clave',
  'help.topics.analytics-vue.description': 'Personalizar gráficos, periodos y filtrar por formación para analizar los datos.',
  'help.topics.analytics-vue.title': 'Vista general analítica',
  'help.topics.app-certificats.description': 'Verificar las certificaciones generadas y las condiciones de aprobación asociadas.',
  'help.topics.app-certificats.title': 'Certificados emitidos',
  'help.topics.app-groupes.description': 'Usar los grupos para segmentar los públicos y facilitar las campañas o informes.',
  'help.topics.app-groupes.title': 'Grupos y cohortes',
  'help.topics.app-inscriptions.description': 'Consultar la lista de aprendices en un recorrido, sus estados e historiales de acceso.',
  'help.topics.app-inscriptions.title': 'Inscripciones y participantes',
  'help.topics.app-progression.description': 'Interpretar el progreso, las finalizaciones y el tiempo de participación en los bloques.',
  'help.topics.app-progression.title': 'Progreso y tiempo dedicado',
  'help.topics.boutique-offres.description': 'Vincular recorridos a productos o códigos de acceso según su modelo de negocio.',
  'help.topics.boutique-offres.title': 'Ofertas y tarifas',
  'help.topics.boutique-paiement.description': 'Comprender el flujo de compra del lado del aprendiz y las confirmaciones del lado de la institución.',
  'help.topics.boutique-paiement.title': 'Pagos y facturación',
  'help.topics.demarrage-navigation.description': 'Encontrar los espacios Proyectos, Formaciones, Medios, Analíticas y Configuración según su rol.',
  'help.topics.demarrage-navigation.title': 'Navegación y menú lateral',
  'help.topics.demarrage-profil.description': 'Actualizar su información, el idioma de la interfaz y las notificaciones.',
  'help.topics.demarrage-profil.title': 'Perfil y preferencias',
  'help.topics.demarrage-roles.description': 'Comprender las diferencias entre formador, administrador de institución, RRHH y aprendiz.',
  'help.topics.demarrage-roles.title': 'Roles y derechos de acceso',
  'help.topics.demarrage-tableau-bord.description': 'Leer los indicadores clave, acceder rápidamente a los recorridos recientes y los atajos útiles.',
  'help.topics.demarrage-tableau-bord.title': 'Panel de control de formación',
  'help.topics.donnees-conservation.description': 'Principios de duración de archivo de los progresos y las pruebas de formación.',
  'help.topics.donnees-conservation.title': 'Duración de conservación',
  'help.topics.donnees-mots-de-passe.description': 'Buenas prácticas de contraseñas, sesiones y delegación de acceso.',
  'help.topics.donnees-mots-de-passe.title': 'Cuentas y autenticación',
  'help.topics.donnees-rgpd.description': 'Finalidades de los datos recogidos en Lyrra y responsabilidad del responsable de formación.',
  'help.topics.donnees-rgpd.title': 'RGPD y registro de tratamientos',
  'help.topics.eduflow-blocs.description': 'Elegir y configurar los bloques adecuados: texto, vídeo, audio, evaluación, ramificación, etc.',
  'help.topics.eduflow-blocs.title': 'Tipos de bloques (contenido, quiz, medios…)',
  'help.topics.eduflow-branches.description': 'Gestionar múltiples salidas, la convergencia de caminos y las reglas de navegación.',
  'help.topics.eduflow-branches.title': 'Ramas y condiciones',
  'help.topics.eduflow-concept.description': 'Canvas, paleta de bloques, selección, zoom y organización visual del recorrido.',
  'help.topics.eduflow-concept.title': 'Vista general del diseñador',
  'help.topics.eduflow-ia.description': 'Sugerir o modificar bloques con el asistente, con revisión humana sistemática.',
  'help.topics.eduflow-ia.title': 'Asistente IA en el diseñador',
  'help.topics.eduflow-objectifs.description': 'Vincular objetivos a los bloques para estructurar el marco de su formación.',
  'help.topics.eduflow-objectifs.title': 'Objetivos pedagógicos',
  'help.topics.eduflow-validation.description': 'Usar el panel de Validación para detectar errores y advertencias antes de la publicación.',
  'help.topics.eduflow-validation.title': 'Validación del recorrido',
  'help.topics.eval-auto-eval.description': 'Proponer recorridos adaptativos en función de los resultados.',
  'help.topics.eval-auto-eval.title': 'Autoevaluación y remediación',
  'help.topics.eval-ouverte.description': 'Organizar la recopilación de respuestas libres y la calificación manual o semiautomática.',
  'help.topics.eval-ouverte.title': 'Preguntas abiertas y corrección',
  'help.topics.eval-quiz.description': 'Configurar baremos, retroalimentación e intentos para un QCM eficaz.',
  'help.topics.eval-quiz.title': 'Cuestionarios y preguntas de opción múltiple',
  'help.topics.inst-collaborateurs.description': 'Añadir formadores, administradores o personal de RRHH y gestionar los permisos.',
  'help.topics.inst-collaborateurs.title': 'Usuarios e invitaciones',
  'help.topics.inst-structure.description': 'Definir lo que se comparte a nivel institucional frente a proyectos personales.',
  'help.topics.inst-structure.title': 'Estructura y perímetro',
  'help.topics.inst-trash.description': 'Encontrar formaciones o podcasts eliminados y restaurarlos si es necesario.',
  'help.topics.inst-trash.title': 'Papelera y restauración',
  'help.topics.marque-accessibilite.description': 'Contrastes, alternativas textuales y navegación con teclado en los contenidos.',
  'help.topics.marque-accessibilite.title': 'Accesibilidad y legibilidad',
  'help.topics.marque-charte.description': 'Cómo la marca se refleja en el reproductor y los espacios de los aprendices.',
  'help.topics.marque-charte.title': 'Marca y colores institucionales',
  'help.topics.marque-landing.description': 'Presentar su oferta de formación a los visitantes cuando el módulo está activado.',
  'help.topics.marque-landing.title': 'Páginas públicas y catálogo',
  'help.topics.medias-bibliotheque.description': 'Importar, organizar y reutilizar vídeos, sonidos, imágenes y documentos en sus bloques.',
  'help.topics.medias-bibliotheque.title': 'Biblioteca de medios',
  'help.topics.medias-documents.description': 'Adjuntar recursos descargables y vincular la documentación al recorrido.',
  'help.topics.medias-documents.title': 'Documentos y PDF',
  'help.topics.medias-studio-audio.description': 'Grabar, mezclar o utilizar la síntesis de voz para enriquecer sus contenidos.',
  'help.topics.medias-studio-audio.title': 'Estudio de audio y voz',
  'help.topics.medias-video.description': 'Buenas prácticas de formato, peso y accesibilidad para los reproductores integrados.',
  'help.topics.medias-video.title': 'Vídeos e integraciones',
  'help.topics.podcasts-creation.description': 'Estructurar episodios, metadatos y portadas para su oferta de audio.',
  'help.topics.podcasts-creation.title': 'Creación de un podcast',
  'help.topics.podcasts-diffusion.description': 'Parámetros de escucha, compartición y seguimiento de uso por parte de la institución.',
  'help.topics.podcasts-diffusion.title': 'Difusión y aprendices',
  'help.topics.pub-catalogue.description': 'Vincular una formación al catálogo institucional o a una oferta cuando esté activado.',
  'help.topics.pub-catalogue.title': 'Catálogo y visibilidad',
  'help.topics.pub-liens.description': 'Generar o copiar los enlaces para los aprendices, presentadores o vista previa.',
  'help.topics.pub-liens.title': 'Enlaces de acceso y compartir',
  'help.topics.pub-statuts.description': 'Ciclo de vida de una formación: guardar, probar, publicar y versionar los cambios.',
  'help.topics.pub-statuts.title': 'Borrador, publicación y actualizaciones',
  'help.topics.qualite-qualiopi.description': 'Usar los espacios dedicados para preparar los criterios y el expediente de calidad.',
  'help.topics.qualite-qualiopi.title': 'Pre-auditoría e indicadores Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Vincular objetivos, contenidos y evaluaciones para documentar el recorrido certificante.',
  'help.topics.qualite-tracabilite.title': 'Trazabilidad pedagógica',
  'help.topics.support-contact.description': 'Canal de soporte, creación de ticket e información a proporcionar para un diagnóstico rápido.',
  'help.topics.support-contact.title': 'Contactar el soporte Lyrra',
  'help.topics.support-faq.description': 'Reproductor que no carga, medio faltante, error de publicación: primeros pasos.',
  'help.topics.support-faq.title': 'Problemas frecuentes',
  'help.topics.support-navigateur.description': 'Versiones recomendadas, cookies y extensiones que pueden interferir.',
  'help.topics.support-navigateur.title': 'Navegador y compatibilidad',
  'help.topics.v2-campagnes.description': 'Planificar el despliegue de recorridos a poblaciones objetivo a lo largo del tiempo.',
  'help.topics.v2-campagnes.title': 'Campañas y planes de formación',
  'help.topics.v2-competences.description': 'Mapear los bloques o recorridos a las competencias esperadas para el reporting de negocio.',
  'help.topics.v2-competences.title': 'Marcos de competencias',
  'help.topics.v2-integrations.description': 'Intercambios con su SI-RRHH, LMS externo o herramientas de BI cuando los conectores estén disponibles.',
  'help.topics.v2-integrations.title': 'Integraciones y API',
  'pages.images.cancel': 'Cancelar',
  'pages.images.changeFile': 'Cambiar archivo',
  'pages.images.copyUrl': 'Copiar URL',
  'pages.images.count_one': '{{count}} imagen en tu biblioteca',
  'pages.images.count_other': '{{count}} imágenes en tu biblioteca',
  'pages.images.delete': 'Eliminar',
  'pages.images.deleteAction': 'Eliminar',
  'pages.images.deleteConfirmHint': 'se eliminará definitivamente.',
  'pages.images.deleteTitle': '¿Eliminar esta imagen?',
  'pages.images.descriptionLabel': 'Descripción (opcional)',
  'pages.images.descriptionPlaceholder': 'Breve descripción…',
  'pages.images.dropzoneBrowse': 'explora',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Arrastra una imagen o',
  'pages.images.emptyHint': 'Arrastra y suelta, haz clic para importar o pega una captura (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importar',
  'pages.images.importSubtitle': 'Añade una imagen a tu biblioteca para tus formaciones.',
  'pages.images.importTitle': 'Importar una imagen',
  'pages.images.lightboxBackdropClose': 'Cerrar',
  'pages.images.lightboxClose': 'Cerrar',
  'pages.images.lightboxNext': 'Imagen siguiente',
  'pages.images.lightboxPrev': 'Imagen anterior',
  'pages.images.lightboxTitle': 'Vista previa de la imagen',
  'pages.images.noImages': 'No hay imágenes importadas',
  'pages.images.noResults': 'Sin resultados',
  'pages.images.pasteHint': 'O pega una captura de pantalla (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Buscar una imagen…',
  'pages.images.submit': 'Importar',
  'pages.images.title': 'Imágenes',
  'pages.images.titleLabel': 'Título (opcional)',
  'pages.images.titlePlaceholder': 'Nombre de la imagen…',
  'pages.images.viewLarge': 'Ver en grande',
  'pages.learners.blockSessionInProgress': '(en curso)',
  'pages.learners.blockTimeOnBlock': '({{duration}} en este bloque)',
  'pages.learners.description': 'Siga el progreso de sus estudiantes',
  'pages.learners.detailedTraces': 'Actividad detallada · {{flowTitle}}',
  'pages.learners.enrolledFormations': 'Formaciones inscritas',
  'pages.learners.lastEvents': 'Últimos eventos',
  'pages.learners.notStarted': 'No iniciado',
  'pages.learners.startedOn': 'Inicio {{date}}',
  'pages.learners.statsBlockSessions': 'Sesiones por bloque: {{count}}',
  'pages.learners.statsEvents': 'Eventos: {{count}}',
  'pages.learners.statsResponses': 'Respuestas: {{count}}',
  'pages.learners.timePerBlock': 'Tiempo por bloque',
  'pages.learners.title': 'Estudiantes',
  'pages.learners.trackingEvent.block_enter': 'Entrada en un bloque',
  'pages.learners.trackingEvent.block_exit': 'Salida de un bloque',
  'pages.learners.trackingEvent.progress_sync': 'Progreso sincronizado',
  'pages.learners.trackingEvent.response_submitted': 'Respuesta enviada',
  'pages.learners.trackingEvent.training_completed': 'Curso completado',
  'pages.learners.trackingEvent.training_enter': 'Apertura del curso',
  'pages.learners.trackingEvent.training_exit': 'Salida o cierre del curso',
  'pages.learners.trackingEvent.unknown': 'Evento',
  'pages.learners.refresh': 'Actualizar',
  'pages.learners.exportCsv': 'Exportar CSV',
  'pages.learners.statTotal': 'Total de estudiantes',
  'pages.learners.statInProgress': 'En curso',
  'pages.learners.statCompleted': 'Completados',
  'pages.learners.statAvgProgress': 'Progreso medio',
  'pages.learners.searchPlaceholder': 'Buscar por nombre o correo…',
  'pages.learners.filterAllFlows': 'Todos los cursos',
  'pages.learners.sortRecent': 'Más recientes',
  'pages.learners.sortName': 'Nombre A-Z',
  'pages.learners.sortProgress': 'Progreso',
  'pages.learners.colLearner': 'Estudiante',
  'pages.learners.colFormations': 'Formaciones',
  'pages.learners.colProgress': 'Progreso',
  'pages.learners.colCompleted': 'Completadas',
  'pages.learners.colTime': 'Tiempo',
  'pages.learners.colLastAccess': 'Último acceso',
  'pages.learners.colActions': 'Acciones',
  'pages.learners.openProgressDashboard': 'Panel de progreso',
  'pages.learners.dashboardTitle': 'Progreso del estudiante',
  'pages.learners.dashboardBack': 'Volver al listado',
  'pages.learners.dashboardInvalidId': 'Identificador de estudiante no válido',
  'pages.learners.dashboardNotFound': 'Estudiante no encontrado o acceso denegado',
  'pages.learners.dashboardOverviewHint':
    'Vista unificada: cursos EduFlow del participante, rutas y podcasts asignados a la cuenta Lyrra con el mismo correo.',
  'pages.learners.tabOverview': 'Resumen',
  'pages.learners.tabFormations': 'Formaciones',
  'pages.learners.tabParcours': 'Rutas',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Formaciones',
  'pages.learners.sectionParcours': 'Rutas',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'Ninguna ruta asignada a una cuenta Lyrra con este correo.',
  'pages.learners.emptyPodcasts': 'Ningún podcast asignado a una cuenta Lyrra con este correo.',
  'pages.learners.linkedAccountHint':
    'Las rutas y los podcasts aparecen si existe un usuario Lyrra con el mismo correo que el participante.',
  'pages.learners.kpiEduflows': 'Formaciones',
  'pages.learners.kpiAvgProgress': 'Progreso medio',
  'pages.learners.kpiPaths': 'Rutas',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} completadas',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} actividades',
  'pages.learners.pathAssignedOn': 'Añadido el {{date}}',
  'pages.learners.podcastCompleted': 'Completado',
  'pages.learners.podcastInProgress': 'En curso',
  'pages.learners.pathStatusPublished': 'Publicado',
  'pages.learners.pathStatusDraft': 'Borrador',
  'pages.learners.openInDesigner': 'Abrir en el diseñador',
  'pages.learners.openParcoursEditor': 'Abrir ruta',
  'pages.learners.viewPodcasts': 'Ver podcasts',
  'pages.learners.loading': 'Cargando estudiantes…',
  'pages.learners.loadError': 'No se pudieron cargar los estudiantes',
  'pages.learners.retry': 'Reintentar',
  'pages.learners.emptyFiltered': 'No se encontraron estudiantes',
  'pages.learners.emptyFilteredHint': 'Pruebe a cambiar la búsqueda o los filtros',
  'pages.learners.emptyNone': 'Ningún estudiante inscrito',
  'pages.learners.emptyNoneHint':
    'Los estudiantes aparecerán aquí al inscribirse en sus cursos publicados',
  'pages.learners.pagination': 'Página {{page}} de {{totalPages}} · {{total}} estudiante(s)',
  'pages.learners.prev': 'Anterior',
  'pages.learners.next': 'Siguiente',
  'pages.learners.csvLastName': 'Apellido',
  'pages.learners.csvFirstName': 'Nombre',
  'pages.learners.csvEmail': 'Correo electrónico',
  'pages.learners.csvFormations': 'Formaciones',
  'pages.learners.csvAvgProgress': 'Progreso medio',
  'pages.learners.csvTotalTime': 'Tiempo total',
  'pages.learners.csvLastActivity': 'Última actividad',
  'pages.learners.statsAdaptiveChoice':
    'Opciones asistidas — aceptadas: {{accepted}} · rechazadas: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptaciones evaluadas: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versiones simplificadas usadas: {{count}}',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Modo de texto simplificado',
  'accessibility.simplifiedTextModeDesc':
    'Simplifica únicamente los textos del recorrido para facilitar la lectura y la comprensión.',
  'accessibility.imageHoverAudio': 'Modo baja visión: audio de imagen al pasar el cursor',
  'accessibility.imageHoverAudioDesc':
    'Cuando se pasa el cursor por una imagen en el reproductor, la descripción de audio se inicia automáticamente.',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.login': 'Sign in',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.logout': 'Log out',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.register': 'Create one',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'btn.cancel': 'Annuler',
  'btn.close': 'Fermer',
  'btn.save': 'Enregistrer',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.copied': 'Copié !',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.loading': 'Chargement…',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Solicitud de formación — contenidos pedagógicos',
  'trainingRequest.intro': 'Describa la necesidad. Los equipos de RRHH aprueban o rechazan la solicitud; si se aprueba, pasa a preparación de contenidos.',
  'trainingRequest.fieldTitle': 'Título de la formación',
  'trainingRequest.fieldDescription': 'Descripción de la necesidad',
  'trainingRequest.fieldJustification': 'Justificación / objetivos',
  'trainingRequest.fieldDate': 'Fecha preferida',
  'trainingRequest.datePlaceholder': 'ej. T2 2026',
  'trainingRequest.fieldFormateur': 'Formador del centro',
  'trainingRequest.formateurPlaceholder': 'Elegir un formador…',
  'trainingRequest.formateursEmpty': 'No hay cuenta de formador registrada para su centro.',
  'trainingRequest.titleRequired': 'Indique un título (mínimo 2 caracteres).',
  'trainingRequest.sent': 'Solicitud registrada. Se le notificará por correo electrónico la decisión.',
  'trainingRequest.sendError': 'No se pudo registrar la solicitud. Verifique su conexión o permisos.',
  'trainingRequest.submit': 'Enviar la solicitud',
  'common.share': 'Partager',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.total': 'au total',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.publishedTraining': 'Formations publiées',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Colaborativo',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.createFormation.error': 'No se pudo crear el curso.',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duplicateFlowError': 'No se pudo duplicar el curso.',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.title': 'Cursos',
  'eduflow.list.subtitle': 'Diseña experiencias de aprendizaje interactivas',
  'eduflow.list.searchPlaceholder': 'Buscar un curso…',
  'eduflow.list.import': 'Importar',
  'eduflow.list.importHint':
    'Archivo .lyrra (medios incluidos) o exportación JSON heredada Lyrra V1.',
  'eduflow.list.importSuccess': 'Curso importado. Abriendo el diseñador.',
  'eduflow.list.importError':
    'Importación imposible — compruebe el archivo (.lyrra o JSON válido, al menos un bloque).',
  'eduflow.list.filterAll': 'Todos',
  'eduflow.list.filterDraft': 'Borradores',
  'eduflow.list.filterPublished': 'Publicados',
  'eduflow.list.filterArchived': 'Archivados',
  'eduflow.list.viewModeGrid': 'Vista de cuadrícula',
  'eduflow.list.viewModeList': 'Vista de lista',
  'eduflow.list.viewByCategory': 'Vista por categorías',
  'eduflow.list.categoryBack': 'Todas las categorías',
  'eduflow.list.categoryBrowseHint': 'Elija una categoría para ver sus cursos.',
  'eduflow.list.categoryFormationCount': '{{count}} curso(s)',
  'eduflow.list.categoryEmptyFiltered': 'Ningún curso en esta vista (filtro o búsqueda activos).',
  'eduflow.list.uncategorizedCardHint': 'Cursos sin etiqueta de categoría.',
  'eduflow.list.uncategorized': 'Sin categoría',
  'eduflow.list.manageCategories': 'Gestionar categorías',
  'eduflow.list.categoriesPanelTitle': 'Categorías del curso',
  'eduflow.list.noCategoryAvailable': 'No hay categorías disponibles.',
  'eduflow.list.categoriesSaveError': 'No se pudieron guardar las categorías.',
  'eduflow.list.emptyFilteredTitle': 'Sin resultados',
  'eduflow.list.emptyFilteredHint': 'Pruebe a cambiar la búsqueda o los filtros.',
  'eduflow.list.emptyFirstTitle': 'Cree su primer curso',
  'eduflow.list.emptyFirstHint': 'Construya aprendizaje interactivo con el diseñador visual.',
  'eduflow.list.emptyCreateCta': 'Crear mi primer curso',
  'eduflow.list.deleteToTrashTitle': '¿Mover este curso a la papelera?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » se quitará de su lista (eliminación lógica). Su organización puede restaurarlo desde la papelera institucional.',
  'eduflow.list.deleteToTrashConfirm': 'A la papelera',
  'eduflow.list.coverLabel': 'Imagen de portada',
  'eduflow.list.coverRemove': 'Quitar portada',
  'eduflow.list.coverUpload': 'Elegir imagen',
  'eduflow.list.flowProperties': 'Título, descripción y portada',
  'eduflow.list.propertiesPanelTitle': 'Propiedades del curso',
  'eduflow.list.duplicatePanelTitle': 'Duplicar curso',
  'eduflow.list.duplicatePanelSubtitle': 'Curso original: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'copia',
  'eduflow.list.duplicateSuccess': 'Curso duplicado.',
  'eduflow.list.duplicateCoverHint':
    'Se conservarán la portada y el contenido pedagógico del curso original.',
  'eduflow.list.propertiesSaveError': 'No se pudieron guardar las propiedades.',
  'eduflow.list.propertiesSaved': 'Propiedades guardadas.',
  'eduflow.list.propertiesUploadError': 'Error al subir la imagen.',
  'eduflow.list.blocksCount': '{{count}} bloques',
  'eduflow.list.noContent': 'Sin contenido',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Abrir Mis misiones de producción',
  'eduflow.list.tooltip.edit': 'Editar',
  'eduflow.list.shellHint':
    'Ficha definida por su organización — cree el EduFlow desde « Mis misiones de producción ».',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Siguiente',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory.activateSuccess': 'Versión {{label}} activada (pública)',
  'eduflow.versionHistory.activateTitle': '¿Activar {{label}}?',
  'eduflow.versionHistory.confirmActivate': 'Activar',
  'eduflow.versionHistory.copyFailed': 'Copia fallida',
  'eduflow.versionHistory.copyHtml': 'Copiar HTML',
  'eduflow.versionHistory.copyPlain': 'Copiar solo texto',
  'eduflow.versionHistory.createMajor': 'Versión mayor',
  'eduflow.versionHistory.createMajorSuccess': 'Versión mayor creada',
  'eduflow.versionHistory.createMinor': 'Versión menor',
  'eduflow.versionHistory.createMinorSuccess': 'Versión menor creada',
  'eduflow.versionHistory.deleteError': 'Error al eliminar',
  'eduflow.versionHistory.deleteSuccess': 'Versión eliminada',
  'eduflow.versionHistory.deleteTitle': '¿Eliminar {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'Esta acción es irreversible.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplicar',
  'eduflow.versionHistory.duplicateError': 'Error al duplicar',
  'eduflow.versionHistory.duplicateMinor': 'Duplicar (menor)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': '¿Duplicar {{label}}?',
  'eduflow.versionHistory.newFromCurrent': 'Nueva versión del contenido actual',
  'eduflow.versionHistory.preview': 'Vista previa',
  'eduflow.versionHistory.previewBlocks': '{{count}} bloques',
  'eduflow.versionHistory.previewEdges': '{{count}} conexiones',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Vista previa {{label}}',
  'eduflow.versionHistory.setPublic': 'Activar',
  'eduflow.versionHistory.startDescModalTitle': 'Descripción — bloque Inicio ({{version}})',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.settings': 'Settings',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Traduciendo…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Ruta circular detectada',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} error(es)',
  'designer.validation.warningCount': '{{count}} advertencia(s)',
  'designer.validation.titleRequired': 'El bloque « {{type}} » debe tener un título.',
  'designer.validation.summary': 'Recorrido: {{blocks}} bloque(s), {{edges}} enlace(s).',
  'designer.validation.multipleStart': 'Varios bloques Inicio ({{count}}): solo se permite uno.',
  'designer.validation.noOutgoing': 'El bloque « {{type}} » no tiene conexión saliente hacia un bloque siguiente.',
  'designer.validation.tooManyOutputs': '« {{label}} »: demasiadas conexiones salientes ({{count}}, máximo {{max}}).',
  'designer.validation.deadEndBranch': 'Rama sin camino hacia un bloque Fin (desde « {{label}} »).',
  'designer.validation.moreDeadEnds': '… y {{count}} rama(s) más sin salida hacia el final.',
  'designer.validation.noConvergence': 'Las ramas desde « {{label}} » no convergen hacia un bloque común.',
  'designer.validation.orphanBlock': 'Bloque aislado del grafo principal: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'El bloque Fin « {{title}} » aún tiene una conexión saliente.',
  'designer.validation.noScoring': 'Parámetros de puntuación no definidos para « {{title}} ».',
  'designer.validation.emptyText': 'El bloque de texto « {{title}} » está vacío.',
  'designer.validation.textTooLong':
    'El bloque de texto « {{title}} » se estima en {{lines}} líneas. Recomendado: entre {{min}} y {{max}} líneas.',
  'designer.validation.splitTextBlock': 'Separar',
  'designer.validation.textSplitNotNeeded':
    'Este bloque no supera el umbral de lectura recomendado.',
  'designer.validation.textSplitUnable':
    'No se pudo proponer una separación automática fiable para este bloque.',
  'designer.validation.textSplitComplexFlow':
    'Separación automática no disponible para este bloque (ramas múltiples/condicionales).',
  'designer.validation.textSplitDone': '{{count}} bloques de texto creados',
  'designer.validation.textSplitConfirmTitle':
    '¿Separar automáticamente este bloque de texto?',
  'designer.validation.textSplitConfirmMessage':
    'El bloque « {{title}} » se estima en {{lines}} líneas. Lo dividiremos en {{parts}} bloques (separación guiada por la puntuación) y los colocaremos justo debajo, en orden.',
  'designer.validation.textSplitConfirmAction': 'Separar bloque',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Voz predeterminada',
  'voicePanel.provider': 'Proveedor: Mistral AI',
  'voicePanel.hint': 'Seleccione la voz utilizada para la narración de bloques.',
  'voicePanel.translationFrenchFallback':
    'Audio traducido: si no hay voz predeterminada guardada aquí, para el francés se usa la voz « Marie - sad » (Mistral).',
  'voicePanel.search': 'Buscar…',
  'voicePanel.allLanguages': 'Todos los idiomas',
  'voicePanel.loading': 'Cargando voces…',
  'voicePanel.loadError': 'No se pudieron cargar las voces',
  'voicePanel.empty': 'No hay voces disponibles.',
  'voicePanel.emptyHint': 'Verifique la clave API de Mistral en Administración → Integraciones.',
  'voicePanel.noResults': 'Ninguna voz coincide con su búsqueda.',
  'voicePanel.saved': 'Voz predeterminada guardada',
  'voicePanel.saveError': 'Error al guardar',
  'voicePanel.default': 'Predeterminada',
  'voicePanel.female': 'Femenina',
  'voicePanel.male': 'Masculina',
  'voicePanel.playPreview': 'Probar la voz',
  'voicePanel.stopPreview': 'Detener',
  'voicePanel.noPreview': 'Vista previa no disponible',
  'voicePanel.previewLoading': 'Generando…',
  'voicePanel.previewGenerateError': 'No se pudo generar la vista previa de audio.',
  'voicePanel.previewPlayError': 'Error al reproducir el audio.',
  'voicePanel.filterNoVoicesForLang':
    'Ninguna voz del catálogo coincide con este idioma — prueba «Todos los idiomas».',
  'episodeEditor.voicePicker.blockVoice': 'Voz para este bloque',
  'episodeEditor.voicePicker.defaultVoice': 'Voz predeterminada',
  'episodeEditor.voicePicker.voices': 'voces',
  'episodeEditor.voicePicker.searchPlaceholder': 'Buscar una voz…',
  'episodeEditor.voicePicker.noVoiceFound': 'Ninguna voz encontrada',
  'episodeEditor.voicePicker.female': 'Femenino',
  'episodeEditor.voicePicker.male': 'Masculino',
  'episodeEditor.voicePicker.stop': 'Detener',
  'episodeEditor.voicePicker.preview': 'Probar voz',
  'episodeEditor.voicePicker.stopPlayback': 'Detener reproducción',
  'episodeEditor.errors.notFound': 'Episodio no encontrado',
  'episodeEditor.errors.titleRequired': 'El título es obligatorio.',
  'episodeEditor.errors.saveFailed': 'Error al guardar.',
  'episodeEditor.errors.noVoicesAvailable': 'No hay voces disponibles. Ingrese la clave API en Administración → Integraciones, luego recargue.',
  'episodeEditor.errors.selectVoiceFirst': 'Seleccione una voz en la pestaña « Voz » antes de generar la locución.',
  'episodeEditor.errors.invalidBlockVoice': 'Bloques con voz inválida. Abra el selector de voz por bloque o recargue la lista.',
  'episodeEditor.errors.addTextFirst': 'Añada texto antes de generar el audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Generación de audio imposible. {{message}} (duración = estimación.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error durante la generación de audio. Duración = estimación.',
  'episodeEditor.success.published': '¡Episodio publicado!',
  'episodeEditor.success.draftSaved': 'Borrador guardado',
  'episodeEditor.success.audioGenerated': 'Audio generado con {{count}} bloque(s)',
  'episodeEditor.tabs.edit': 'Edición',
  'episodeEditor.tabs.chapters': 'Capítulos',
  'episodeEditor.tabs.voice': 'Voz',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Deshacer (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Rehacer (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generando…',
  'episodeEditor.toolbar.generateAudio': 'Generar audio',
  'episodeEditor.toolbar.save': 'Guardar',
  'episodeEditor.toolbar.publish': 'Publicar',
  'episodeEditor.titlePlaceholder': 'Título del episodio…',
  'episodeEditor.status.published': 'Publicado',
  'episodeEditor.status.draft': 'Borrador',
  'episodeEditor.warnings.mistralNotConfigured': 'La locución usa Mistral Voxtral TTS. Añada la clave Mistral en Administración → Integraciones, luego recargue.',
  'episodeEditor.warnings.mistralNoVoices': 'Clave Mistral reconocida pero la API no devolvió voces. Consulte los logs del backend.',
  'episodeEditor.warnings.voiceLoadFailed': 'Imposible cargar la lista de voces. Verifique la configuración Mistral en Administración → Integraciones.',
  'episodeEditor.warnings.voiceOffDisabled': 'Sin una voz real, la generación de locución permanece desactivada.',
  'episodeEditor.edit.voiceOff': 'Locución',
  'episodeEditor.edit.voiceDescription': 'Síntesis vía Mistral Voxtral TTS. Elija la voz en « Voz » o por bloque.',
  'episodeEditor.edit.coverImage': 'Imagen de portada',
  'episodeEditor.edit.coverAlt': 'Portada del episodio',
  'episodeEditor.edit.deleteCover': 'Eliminar',
  'episodeEditor.edit.dropOrClickImage': 'Clic o suelte una imagen',
  'episodeEditor.edit.selectedVoice': 'Voz seleccionada',
  'episodeEditor.edit.selectVoice': 'Seleccionar una voz',
  'episodeEditor.edit.defaultVoiceByLang': 'Voz predeterminada (idioma del podcast)',
  'episodeEditor.edit.lastAudioGenerated': 'Último audio generado',
  'episodeEditor.edit.audioNotFound': 'Archivo de audio no encontrado — regenere la locución.',
  'episodeEditor.edit.generatingInProgress': 'Generando…',
  'episodeEditor.edit.generateVoiceOff': 'Generar locución',
  'episodeEditor.chapters.title': 'Capítulos',
  'episodeEditor.chapters.add': 'Añadir',
  'episodeEditor.chapters.defaultChapterTitle': 'Introducción',
  'episodeEditor.chapters.newChapterTitle': 'Capítulo {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} bloque(s)',
  'episodeEditor.voiceTab.availableVoices': 'Voces disponibles',
  'episodeEditor.podcasts.linked': 'Podcasts vinculados',
  'episodeEditor.podcasts.assign': 'Asignar',
  'episodeEditor.podcasts.noneAvailable': 'Ningún podcast disponible',
  'episodeEditor.podcasts.close': 'Cerrar',
  'episodeEditor.podcasts.noneLinked': 'Ningún podcast vinculado',
  'episodeEditor.podcasts.assignHint': 'Asigne este episodio a uno o más podcasts',
  'episodeEditor.podcasts.remove': 'Quitar',
  'episodeEditor.editor.addTextBlock': 'Añadir un bloque de texto',
  'episodeEditor.editor.scriptPlaceholder': 'Comience a escribir su guión…',
  'episodeEditor.editor.addBlock': 'Añadir un bloque',
  'episodeEditor.player.loop': 'Bucle',
  'episodeEditor.player.playbackSpeed': 'Velocidad de reproducción',
  'episodeEditor.player.collapse': 'Reducir',
  'episodeEditor.player.expand': 'Expandir',
  'episodeEditor.player.waveformHidden': 'Forma de onda oculta',
  'podcast.episodes.listen': 'Escuchar',
  'podcast.episodes.noAudioShort': 'Sin audio disponible',
  'podcast.episodes.edit': 'Editar',
  'podcast.episodes.delete': 'Eliminar',
  'podcast.episodes.unmute': 'Activar sonido',
  'podcast.episodes.mute': 'Silenciar',
  'podcast.episodes.totalEpisodes': '{{count}} episodio(s) en total',
  'eduflow.designer.toast.blockCopied': 'Bloque copiado',
  'eduflow.designer.toast.blockPasted': 'Bloque pegado',
  'eduflow.designer.toast.blockDuplicated': 'Bloque duplicado',
  'eduflow.designer.toast.connectionUpdated': 'Conexión actualizada',
  'eduflow.designer.toast.flowExported': 'Curso exportado',
  'eduflow.designer.toast.flowImported': 'Curso importado',
  'eduflow.designer.toast.invalidFile': 'Archivo inválido',
  'eduflow.designer.untitled': 'Sin título',
  'eduflow.designer.importedFlow': 'Curso importado',
  'eduflow.designer.collaborator.online': 'En línea',
  'eduflow.designer.collaborator.offline': 'Sin conexión',
  'eduflow.designer.collaborator.defaultName': 'Usuario',
  'eduflow.designer.collaborator.editor': 'Editor',
  'eduflow.designer.collaborator.viewer': 'Lector',
  'eduflow.designer.resizePanel': 'Redimensionar panel',
  'eduflow.designer.published': 'Publicado',
  'eduflow.designer.draft': 'Borrador',
  'eduflow.designer.quizOutput.success': 'Aprobado',
  'eduflow.designer.quizOutput.failure': 'Suspendido',
  'eduflow.designer.handle.default': 'Predeterminado',
  'eduflow.designer.genError': 'Error durante la generación. Inténtalo de nuevo.',
  'trainerTour.welcome.title': 'Bienvenido a tu espacio de formador',
  'trainerTour.welcome.description':
    'Esta breve visita presenta el panel y el menú. La primera vez que abres cada página, un aviso explica lo esencial. Puedes cerrar un paso con la X: la visita no se reiniciará.',
  'trainerTour.dashboard.header.title': 'Tu panel',
  'trainerTour.dashboard.header.description':
    'Resumen de tu actividad: cifras clave y accesos directos para crear contenido rápidamente.',
  'trainerTour.dashboard.actions.title': 'Crear contenido',
  'trainerTour.dashboard.actions.description':
    'Accesos directos a un nuevo curso EduFlow, un podcast o un recorrido. Para empezar un proyecto sin pasar por el menú.',
  'trainerTour.dashboard.stats.title': 'Indicadores',
  'trainerTour.dashboard.stats.description':
    'Cursos, recorridos publicados, aprendices alcanzados y podcasts: el alcance de tu catálogo de un vistazo.',
  'trainerTour.dashboard.activity.title': 'Actividad reciente',
  'trainerTour.dashboard.activity.description':
    'Últimos contenidos modificados. Haz clic en una fila para volver a abrir el curso, podcast o recorrido.',
  'trainerTour.sidebar.title': 'Menú lateral',
  'trainerTour.sidebar.description':
    'Gestión (inicio, aprendices, analíticas, misiones), creación (cursos, podcasts, recorridos, webinarios si están activos), biblioteca multimedia y ayuda. Abre cada sección: la primera vez aparece un consejo contextual.',
  'trainerTour.routes.learners.title': 'Aprendices',
  'trainerTour.routes.learners.description':
    'Personas inscritas en tus contenidos, progreso, exportaciones: tu centro de seguimiento pedagógico.',
  'trainerTour.routes.analytics.title': 'Analíticas',
  'trainerTour.routes.analytics.description':
    'Gráficos sobre participación y finalización. Personaliza la disposición para lo que te importa.',
  'trainerTour.routes.eduflow.title': 'Cursos (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Crea y gestiona cursos interactivos, versiones, publicación y uso compartido: el núcleo del diseño formativo.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Programas, episodios, visibilidad y uso compartido con grupos o aprendices de tu organización.',
  'trainerTour.routes.parcours.title': 'Recorridos',
  'trainerTour.routes.parcours.description':
    'Secuencias de cursos y recursos: estructura trayectos largos y asígnalos a tus audiencias.',
  'trainerTour.routes.videos.title': 'Vídeos',
  'trainerTour.routes.videos.description':
    'Biblioteca de vídeos reutilizables en cursos y documentos.',
  'trainerTour.routes.sounds.title': 'Audios',
  'trainerTour.routes.sounds.description':
    'Archivos de audio y etiquetas para podcasts y bloques de sonido en los cursos.',
  'trainerTour.routes.images.title': 'Imágenes',
  'trainerTour.routes.images.description':
    'Visuales centralizados: importación, carpetas y reutilización en el contenido.',
  'trainerTour.routes.documents.title': 'Documentos',
  'trainerTour.routes.documents.description':
    'PDFs y archivos adjuntos, con uso compartido dirigido a aprendices si hace falta.',
  'trainerTour.routes.help.title': 'Ayuda',
  'trainerTour.routes.help.description':
    'Artículos, tutoriales y guías sobre LyrraStudio. Vuelve aquí si te atascas con una función.',
  'trainerTour.routes.producerAssignments.title': 'Misiones de producción',
  'trainerTour.routes.producerAssignments.description':
    'Cuando RR. HH. te asigna contenidos del catálogo por producir, aparecen aquí con el avance.',
  'trainerTour.routes.webinars.title': 'Webinarios',
  'trainerTour.routes.webinars.description':
    'Planifica sesiones en directo, invitaciones y enlaces de difusión para tus grupos.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Siguiente',
  'trainerTour.driver.prev': 'Anterior',
  'trainerTour.driver.done': 'Listo',
  'trainerTour.driver.gotIt': 'Entendido',
  'player.lightMode': 'Modo claro',
  'player.darkMode': 'Modo oscuro',
};

const ptTranslations: Record<string, string> = {
  'nav.home': 'Início',
  'nav.producerAssignments': 'Missões de produção',
  'nav.videos': 'Vídeos',
  'nav.sounds': 'Áudios',
  'nav.images': 'Imagens',
  'nav.documents': 'Documentos',
  'nav.podcasts': 'Podcasts',
  'nav.parcours': 'Percursos',
  'nav.eduflowList': 'Formações',
  'nav.myLearning': 'Minha aprendizagem',
  'nav.section.pilotage': 'Gestão',
  'nav.section.creation': 'Criação',
  'nav.section.medias': 'Mídias',
  'nav.section.aide': 'Ajuda',
  'sidebar.brandTagline': 'Formação',
  'sidebar.logoInstitutionAlt': 'Logótipo da organização',
  'sidebar.expand': 'Expandir menu',
  'sidebar.collapse': 'Recolher menu',
  'sidebar.expandAria': 'Expandir barra lateral',
  'sidebar.collapseAria': 'Recolher barra lateral',
  'learner.sidebar.collapse': 'Recolher',
  'learner.sidebar.expandAria': 'Expandir barra lateral',
  'learner.sidebar.collapseAria': 'Recolher barra lateral',
  'dashboard.title': 'Painel',
  'dashboard.welcome': 'Bem-vindo, {{name}}!',
  'dashboard.welcomeSub': 'Aqui está uma visão geral do seu espaço.',
  'dashboard.projects': 'Projetos',
  'dashboard.subtitle': 'Gerencie suas formações e acompanhe os aprendizes.',
  'dashboard.newProject': 'Nova formação',
  'dashboard.newEduFlow': 'Novo percurso',
  'dashboard.uploadMedia': 'Enviar mídia',
  'dashboard.viewDocs': 'Documentação',
  'dashboard.revenue': 'Receita',
  'dashboard.eduflows': 'Formações',
  'dashboard.learners': 'Aprendizes',
  'dashboard.recentActivity': 'Atividade recente',
  'dashboard.recentProjects': 'Projetos recentes',
  'dashboard.noRecentProjects': 'Sem projetos recentes',
  'dashboard.activity': 'Atividade',
  'dashboard.activityEmpty': 'Sem atividade recente em formações ou podcasts.',
  'dashboard.loadError': 'Não foi possível carregar os dados do painel.',
  'dashboard.publishedFlows': 'Percursos publicados',
  'dashboard.roleSpecific': 'Há um painel específico para este papel.',
  'dashboard.kindPodcast': 'Podcast',
  'nav.analytics': 'Análise',
  'pages.analytics.title': 'Análise',
  'pages.analytics.description': 'Painéis e estatísticas',
  'pages.analytics.subtitle':
    'Filtre os dados, reorganize os gráficos por arrastar e ajuste o tamanho dos widgets.',
  'pages.analytics.filters.period': 'Período',
  'pages.analytics.filters.flow': 'Curso',
  'pages.analytics.filters.allFlows': 'Todos os cursos',
  'pages.analytics.period.7d': 'Últimos 7 dias',
  'pages.analytics.period.30d': 'Últimos 30 dias',
  'pages.analytics.period.90d': 'Últimos 90 dias',
  'pages.analytics.period.365d': 'Últimos 12 meses',
  'pages.analytics.addChart': 'Adicionar gráfico',
  'pages.analytics.resetLayout': 'Repor disposição',
  'pages.analytics.dragHint': 'Arrastar para reordenar',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Remover este gráfico',
  'pages.analytics.kind.completion_line': 'Conclusão ao longo do tempo',
  'pages.analytics.kind.learners_bar': 'Aprendizes por módulo',
  'pages.analytics.kind.status_pie': 'Distribuição de estados',
  'pages.analytics.kind.engagement_area': 'Tempo de envolvimento',
  'pages.analytics.series.completion': 'Taxa de conclusão (%)',
  'pages.analytics.series.learners': 'Aprendizes',
  'pages.analytics.series.hours': 'Horas',
  'pages.analytics.status.completed': 'Concluído',
  'pages.analytics.status.in_progress': 'Em curso',
  'pages.analytics.status.dropped': 'Abandonado',
  'pages.analytics.status.not_started': 'Não iniciado',
  'pages.analytics.loadError': 'Não foi possível carregar os dados de análise.',
  'pages.analytics.retry': 'Tentar novamente',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'seg',
  'pages.analytics.mock.wd1': 'ter',
  'pages.analytics.mock.wd2': 'qua',
  'pages.analytics.mock.wd3': 'qui',
  'pages.analytics.mock.wd4': 'sex',
  'pages.analytics.mock.wd5': 'sáb',
  'pages.analytics.mock.wd6': 'dom',
  'pages.analytics.emptyCharts': 'Adicione pelo menos um gráfico para ver os dados.',
  'pages.analytics.comingSoonDesc':
    'Veja estatísticas das formações, taxas de conclusão, envolvimento dos aprendizes e desempenho global.',
  'project.status.draft': 'Rascunho',
  'project.status.published': 'Publicado',
  'project.status.archived': 'Arquivado',
  'eduflowList.status.draft': 'Rascunho',
  'eduflowList.status.published': 'Publicado',
  'eduflowList.status.archived': 'Arquivado',
  'eduflowList.badge.enterpriseShell': 'Pedido empresa',
  'eduflowList.badge.enterpriseLinked': 'Empresa',
  'eduflowList.badge.toProduce': 'A produzir',
  'learner.sidebar.tagline': 'Aprendiz',
  'player.next': 'Próximo',
  'player.image.clickForAnnotations': 'Clique na imagem para ver as anotações',
  'player.previous': 'Anterior',
  'player.complete': 'Concluir',
  'player.exit': 'Sair',
  'player.exitConfirmTitle': 'Sair da formação?',
  'player.exitConfirmMessage': 'Seu progresso será salvo. Tem certeza de que deseja sair?',
  'player.focusLostTitle': 'Formação em pausa',
  'player.focusLostMessage': 'Você saiu da janela da formação. O tempo está em pausa.',
  'player.focusLostResume': 'Clique para retomar',
  'player.focusLostRemaining': 'Saídas restantes antes do bloqueio: {{remaining}}',
  'player.focusBlockedTitle': 'Formação bloqueada',
  'player.focusBlockedMessage':
    'Você excedeu o número de saídas permitidas ({{max}}). Você não pode mais continuar esta formação. Entre em contato com seu formador ou responsável.',
  'player.cancel': 'Cancelar',
  'player.summary': 'Resumo',
  'player.contents': 'Conteúdo',
  'player.progress': 'Progresso',
  'player.block': 'Bloco',
  'player.parallelBranches': 'Ramificações paralelas',
  'player.parallelBranchesTitle': 'E agora?',
  'player.parallelBranchesHint': 'As outras opções virão em seguida.',
  'player.parallelBranchShowFull': 'Ver mais',
  'player.parallelBranchShowLess': 'Ver menos',
  'player.parallelBranchChoose': 'Abrir',
  'player.parallelBranchDismiss': 'Fechar',
  'player.parallelBranchDismissHint':
    'Voltar ao bloco. Use « Seguinte » para escolher um ramo.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Tomar notas...',
  'player.noBlocks': 'Nenhum bloco para exibir.',
  'player.contentTranslatingTitle': 'A adaptar o conteúdo ao seu idioma…',
  'player.contentTranslatingHint':
    'Tradução em curso. A primeira carga pode demorar um pouco mais.',
  'player.textBlockTipsHeading': 'Dicas',
  'player.textBlockReadDone': 'Leitura concluída',
  'player.defaultFlowTitle': 'Formação',
  'player.docTitle.error': 'Formação indisponível',
  'player.docTitle.completed': 'Formação concluída',
  'player.docTitle.loading': 'A carregar a formação',
  'player.docTitle.fallback': 'Formação',
  'player.initError.title': 'A formação está temporariamente indisponível',
  'player.initError.loadFailed': 'Não foi possível carregar a formação de momento.',
  'player.initError.loadFailedWithDetail':
    'Não foi possível carregar a formação de momento.{{detail}} Tente novamente dentro de instantes.',
  'player.initError.hintsIntro': 'Pode:',
  'player.initError.hintRefresh': 'atualizar a página;',
  'player.initError.hintConnection': 'verificar a sua ligação à Internet;',
  'player.initError.hintRetryLater': 'tentar novamente dentro de alguns minutos.',
  'api.error.network': 'Não foi possível contactar o servidor. Verifique a sua ligação.',
  'api.error.unexpected': 'Ocorreu um erro inesperado.',
  'api.error.badRequest': 'Pedido inválido.',
  'api.error.forbidden': 'Acesso negado.',
  'api.error.notFound': 'Recurso não encontrado.',
  'api.error.conflict': 'Conflito — este recurso já existe.',
  'api.error.unprocessable': 'Os dados enviados não são válidos.',
  'api.error.tooManyRequests': 'Demasiados pedidos. Tente novamente dentro de momentos.',
  'api.error.server': 'Erro interno do servidor.',
  'api.error.badGateway': 'Serviço temporariamente indisponível.',
  'api.error.maintenance': 'Serviço em manutenção.',
  'api.error.generic': 'Erro {{status}}',
  'player.textBlockReadMinutes': '{{count}} min de leitura',
  'player.textBlockWordCount_one': '{{count}} palavra',
  'player.textBlockWordCount_other': '{{count}} palavras',
  'player.textBlockNoContent': 'Sem conteúdo disponível.',
  'player.blockUnknown': 'Bloco desconhecido',
  'player.objectivesPanelTitle': 'Objetivos de aprendizagem',
  'player.objectivesOpenLabel': 'Ver objetivos de aprendizagem',
  'player.objectivesCloseLabel': 'Fechar painel de objetivos',
  'player.objectivesAchievementRate': 'Taxa de alcance',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} objetivos alcançados',
  'player.objectivesNoneDefined': 'Nenhum objetivo definido para esta formação.',
  'player.objectiveCategory.savoir': 'Saber',
  'player.objectiveCategory.savoirFaire': 'Saber fazer',
  'player.objectiveCategory.savoirEtre': 'Saber ser',
  'player.objectiveLevel.connaissance': 'Conhecimento',
  'player.objectiveLevel.comprehension': 'Compreensão',
  'player.objectiveLevel.application': 'Aplicação',
  'player.objectiveLevel.analyse': 'Análise',
  'player.objectiveLevel.synthese': 'Síntese',
  'player.objectiveLevel.evaluation': 'Avaliação',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obrigatório',
  'player.objectiveLinkedBlocks': 'Blocos associados',
  'player.objectiveCriterionScore': 'Pontuação ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Conclusão do bloco',
  'player.objectiveNoLinkedBlocks':
    'Sem bloco associado — o progresso global não mede este objetivo.',
  'player.completion.congratulations': '🎉 Parabéns!',
  'player.completion.finished': '📋 Curso concluído',
  'player.completion.youCompleted': 'Você completou',
  'player.completion.passed': 'Aprovado ✓',
  'player.completion.failed': 'Não aprovado ✗',
  'player.completion.requiredScore': 'Pontuação necessária: {{score}}%',
  'player.completion.totalTime': 'Tempo total',
  'player.completion.blocksCompleted': 'Blocos concluídos',
  'player.completion.quizPassed': 'Questionários aprovados',
  'player.completion.successRate': 'Taxa de sucesso',
  'player.completion.blockCompleted': 'Concluído ✓',
  'player.completion.blockFailed': 'Reprovado ✗',
  'player.completion.blockSkipped': 'Ignorado',
  'player.completion.detail': '📊 Detalhe por bloco',
  'player.completion.type': 'Tipo',
  'player.completion.timeSpent': 'Tempo gasto',
  'player.completion.downloadCertificate': '📄 Baixar certificado',
  'player.completion.certificateSoon': 'O certificado estará disponível em breve.',
  'player.completion.restart': '🔄 Recomeçar',
  'player.completion.viewDetails': '📊 Ver detalhes',
  'player.completion.backToDashboard': '← Voltar ao painel',
  'player.completion.embedDismissHint':
    'Concluiu o módulo. Para sair, use o X no topo da janela ou o botão abaixo.',
  'player.completion.embedDismissButton': 'Fechar janela',
  'player.previewBanner': 'Modo de pré-visualização — esta vista reflete os dados reais da sua formação',
  'player.closePreview': 'Fechar pré-visualização',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Mostrar painel',
  'player.toggleSidebarHide': 'Ocultar painel',
  'player.sidebarExpand': 'Expandir painel',
  'player.sidebarCollapse': 'Recolher painel',
  'player.keyboardShortcuts': 'Atalhos de teclado',
  'player.keyboardShortcutPrev': 'Bloco anterior',
  'player.keyboardShortcutNext': 'Bloco seguinte',
  'player.keyboardShortcutsHint':
    'Ative « Navegação por teclado » no centro de acessibilidade (botão em baixo à direita). Inativo em campos de texto.',
  'player.a11yCenter': 'Centro de acessibilidade',
  'player.reviewSummary': 'Ver resumo',
  'player.viewResults': 'Ver resultados',
  'player.submitFirst': 'Enviar primeiro',
  'player.phraseRevealCompleteFirst': 'Veja todas as frases primeiro',
  'player.phraseReveal.emptyBlock': 'Nenhuma frase configurada para este bloco.',
  'player.phraseReveal.revealNextAria':
    'Clique ou pressione Espaço para revelar a frase seguinte',
  'player.phraseReveal.lastPhraseHint':
    'Última frase — use « Seguinte » para continuar o percurso.',
  'player.phraseReveal.morePhrasesHint': 'Clique, Espaço ou seta direita para a frase seguinte',
  'player.phraseReveal.previousPhrases': 'Frases anteriores ({{count}})',
  'player.certificateUnavailable': 'O certificado estará disponível em breve.',
  'player.browser.embeddedContentTitle': 'Conteúdo incorporado',
  'player.browser.noValidUrl': 'URL inválida',
  'player.browser.enterHttpsHint':
    'Introduza um endereço que comece por https:// (ou http://) nas definições do bloco.',
  'player.browser.loadingContent': 'A carregar conteúdo…',
  'player.browser.defaultPageTitle': 'Página web incorporada',
  'player.browser.continue': 'Continuar',
  'player.browser.stepSaved': 'Passo guardado',
  'player.browser.fullscreen': 'Ecrã inteiro',
  'player.browser.exitFullscreen': 'Sair do ecrã inteiro',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Episódio',
  'player.podcast.loadError': 'Não foi possível carregar o podcast.',
  'player.podcast.loadingEpisodes': 'A carregar episódios…',
  'player.podcast.playAll': 'Ouvir tudo',
  'player.podcast.nextEpisode': 'Episódio seguinte',
  'player.podcast.episodesHeading': 'Episódios',
  'player.podcast.noEpisodes': 'Sem episódios.',
  'player.podcast.noAudio': 'Sem áudio',
  'player.podcast.chainPlayback': 'Reprodução em sequência · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Selecione um episódio na lista ou inicie «Ouvir tudo».',
  'player.podcast.audioUnavailable': 'Áudio indisponível para este episódio.',
  'player.podcast.episodeCount_one': '{{count}} episódio',
  'player.podcast.episodeCount_other': '{{count}} episódios',
  'player.renderError': 'Erro ao renderizar o bloco: {{blockType}}',
  'player.blocks.defaultWelcome': 'Bem-vindo a esta formação',
  'player.blocks.defaultEndTitle': 'Parabéns!',
  'player.quiz.selectCorrectAnswer': 'Selecione a resposta correta:',
  'player.quiz.correctAnswer': '✓ Resposta correta!',
  'player.quiz.incorrectAnswer': '✗ Resposta incorreta.',
  'player.blockTimer.aria': 'Tempo para este bloco',
  'player.blockTimer.modeShortCountup': 'Cronómetro',
  'player.blockTimer.modeShortCountdown': 'Contagem decrescente',
  'player.form.successMessage': 'Obrigado pela sua resposta!',
  'player.form.noFieldsConfigured': 'Este formulário ainda não tem campos configurados.',
  'player.evaluation.commentsLabel': 'Comentários (opcional)',
  'player.evaluation.commentsPlaceholder': 'Partilhe a sua opinião...',
  'player.evaluation.submitted': 'Avaliação enviada',
  'player.evaluation.submit': 'Enviar avaliação',
  'player.time.lessThanMinute': 'Menos de um minuto',
  'player.time.oneMinute': '1 minuto',
  'player.time.minutes': '{{count}} minutos',
  'player.time.shortSeconds': '{{count}}s',
  'player.time.shortMinutes': '{{count}} min',
  'player.time.shortMinutesSeconds': '{{minutes}} min {{seconds}}s',
  'player.completion.scoreLabel': 'Pontuação',
  'accessibility.openCenter': 'Abrir o centro de acessibilidade',
  'accessibility.center': 'Acessibilidade',
  'accessibility.modalTitle': 'Centro de leitura adaptada',
  'accessibility.modalDescription': 'Ajuste a interface para baixa visão, dislexia e uma leitura mais confortável.',
  'accessibility.closeCenter': 'Fechar o centro de acessibilidade',
  'accessibility.accessPad.connected': 'Lyrra Access Pad ligado',
  'accessibility.accessPad.playGuide': 'Ouvir o guia LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Pausar o guia LyrraPad',
  'accessibility.presets': 'Perfis predefinidos',
  'accessibility.preset.standard.label': 'Padrão',
  'accessibility.preset.standard.desc': 'Apresentação predefinida da aplicação.',
  'accessibility.preset.lowVision.label': 'Baixa visão',
  'accessibility.preset.lowVision.desc': 'Mais contraste, texto maior e pistas visuais mais claras.',
  'accessibility.preset.dys.label': 'Dislexia',
  'accessibility.preset.dys.desc': 'Leitura mais fácil com mais espaço e uma fonte mais legível.',
  'accessibility.typography': 'Definições tipográficas',
  'accessibility.fontScale': 'Tamanho do texto',
  'accessibility.lineHeight': 'Entrelinhas',
  'accessibility.letterSpacing': 'Espaçamento entre letras',
  'accessibility.wordSpacing': 'Espaçamento entre palavras',
  'accessibility.visualAids': 'Ajuda visual',
  'accessibility.highContrast': 'Alto contraste',
  'accessibility.highContrastDesc': 'Reforça o contraste de leitura, as margens e as pistas visuais.',
  'accessibility.underlineLinks': 'Ligações sublinhadas',
  'accessibility.underlineLinksDesc': 'Torna as ligações e as áreas interativas mais visíveis.',
  'accessibility.dyslexiaFont': 'Tipo de letra de leitura facilitada',
  'accessibility.dyslexiaFontDesc': 'Usa um tipo de letra mais confortável para pessoas com dislexia e leitores cansados.',
  'accessibility.reducedMotion': 'Movimento reduzido',
  'accessibility.reducedMotionDesc': 'Reduz animações e transições para limitar o desconforto visual.',
  'accessibility.mouseMagnifier': 'Lupa de leitura',
  'accessibility.mouseMagnifierDesc':
    'Mostra uma lupa circular que segue o ponteiro e amplia o conteúdo do curso sob a área apontada (apenas leitura).',
  'accessibility.alwaysOn': 'Melhorias sempre ativas',
  'accessibility.alwaysOnFocus': 'Foco do teclado mais visível',
  'accessibility.alwaysOnLinks': 'Ligações e elementos interativos mais fáceis de detetar',
  'accessibility.alwaysOnSaved': 'Definições guardadas automaticamente neste dispositivo',
  'accessibility.navigation': 'Navegação',
  'accessibility.keyboardNavigation': 'Navegação por teclado',
  'accessibility.keyboardNavigationDesc':
    'Seta esquerda/cima para o bloco anterior, direita/baixo para o seguinte (como os botões em baixo).',
  // ── Injected from locales/pt-BR.json ──
  'producerAssignments.createInDesigner': 'Criar / abrir formação',
  'producerAssignments.draft': 'Rascunho',
  'producerAssignments.enterprise': 'Empresa',
  'producerAssignments.enterprisePaths': 'Trilhas empresariais',
  'producerAssignments.episodes': 'ep.',
  'producerAssignments.errorForbidden': 'Você não tem acesso a esta página.',
  'producerAssignments.errorGeneric': 'Não foi possível carregar suas missões.',
  'producerAssignments.errorNoInstitution': 'Você não está vinculado a nenhuma instituição.',
  'producerAssignments.formations': 'Fichas de formação',
  'producerAssignments.intro': 'Esta página reúne as fichas de formação e podcasts atribuídos pela direção de RH da sua instituição. Crie seus conteúdos no designer EduFlow ou no estúdio de podcasts e atualize o status de produção.',
  'producerAssignments.markCompleted': 'Marcar como concluído',
  'producerAssignments.markInProgress': 'Em produção',
  'producerAssignments.markReady': 'Pronto',
  'producerAssignments.modulesCatalog': '{{count}} módulo(s) catálogo · atualizado {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Nenhuma solicitação de trilha empresarial.',
  'producerAssignments.noneFormations': 'Nenhuma ficha atribuída.',
  'producerAssignments.nonePodcasts': 'Nenhum podcast atribuído.',
  'producerAssignments.openInDesigner': 'Abrir no designer',
  'producerAssignments.openPodcasts': 'Estúdio de podcast',
  'producerAssignments.planningDeadline': 'Prazo {{date}}',
  'producerAssignments.planningFrom': 'A partir de {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasts a produzir',
  'producerAssignments.productionStatus.completed': 'Concluído',
  'producerAssignments.productionStatus.in_progress': 'Em andamento',
  'producerAssignments.productionStatus.not_started': 'Não iniciado',
  'producerAssignments.productionStatus.published': 'Publicado',
  'producerAssignments.publishedCatalog': 'Publicado no catálogo',
  'producerAssignments.rhPlanning': 'Planejamento RH',
  'producerAssignments.subtitle': 'Formações a criar no designer e podcasts a produzir, atribuídos pelo RH.',
  'producerAssignments.title': 'Minhas missões de produção',
  'podcasts.actions.cancel': 'Cancelar',
  'podcasts.actions.close': 'Fechar',
  'podcasts.actions.create': 'Criar',
  'podcasts.actions.edit': 'Editar',
  'podcasts.actions.retry': 'Tentar novamente',
  'podcasts.actions.save': 'Salvar',
  'podcasts.actions.saving': 'Salvando…',
  'podcasts.actions.share': 'Compartilhar',
  'podcasts.category.ariaGroup': 'Categoria do podcast',
  'podcasts.category.emptyBanner': 'Adicione categorias no espaço Instituição para organizar melhor os podcasts.',
  'podcasts.category.emptyInstitutionHint': 'Ainda não há categorias da instituição — você pode publicar sem.',
  'podcasts.category.noneChip': 'Sem categoria',
  'podcasts.category.sectionTitle': 'Categoria',
  'podcasts.category.withCategoriesHint': 'Posicionamento no catálogo e filtros do lado da instituição.',
  'podcasts.cover.ariaDropzone': 'Escolher ou soltar uma imagem de capa',
  'podcasts.cover.browseFiles': 'procurar arquivos',
  'podcasts.cover.cancelNewFile': 'Cancelar o novo arquivo',
  'podcasts.cover.dragDrop': 'Arraste e solte uma imagem',
  'podcasts.cover.dropHere': 'Solte a imagem aqui',
  'podcasts.cover.errorMaxSize': 'A imagem não pode ultrapassar 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Formato de imagem não suportado.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · máx. 5 MB',
  'podcasts.cover.help': 'Formato quadrado recomendado. Arraste e solte um arquivo ou clique para procurar.',
  'podcasts.cover.or': 'ou',
  'podcasts.cover.replaceHint': 'Substituir a imagem',
  'podcasts.cover.sectionTitle': 'Imagem de capa',
  'podcasts.deleteDialog.body': '« {{title}} » será removido da lista (exclusão lógica). Os episódios não são apagados. Se sua conta estiver vinculada a uma instituição, um administrador pode restaurar o podcast a partir da lixeira do espaço Instituição.',
  'podcasts.deleteDialog.confirm': 'Mover para a lixeira',
  'podcasts.deleteDialog.title': 'Mover o podcast para a lixeira?',
  'podcasts.editPanel.createdOn': 'Criado em ',
  'podcasts.editPanel.headerTitle': 'Editar podcast',
  'podcasts.editPanel.saving': 'Salvando…',
  'podcasts.editPanel.statsEpisodes': 'Episódios: ',
  'podcasts.editPanel.statsSubscribers': 'Assinantes: ',
  'podcasts.editPanel.successSaved': 'Alterações salvas',
  'podcasts.episodeStatus.draft': 'Rascunho',
  'podcasts.episodeStatus.published': 'Publicado',
  'podcasts.episodesPanel.addEpisode': 'Adicionar episódio',
  'podcasts.episodesPanel.emptySubtitle': 'Adicione episódios a este podcast',
  'podcasts.episodesPanel.emptyTitle': 'Nenhum episódio',
  'podcasts.episodesPanel.episodeCount': '{{count}} episódio(s)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Nenhum episódio disponível',
  'podcasts.episodesPanel.remove': 'Remover',
  'podcasts.episodesPanel.searchPlaceholder': 'Buscar um episódio…',
  'podcasts.episodesPanel.title': 'Episódios do podcast',
  'podcasts.error.retry': 'Tentar novamente',
  'podcasts.filters.allCategories': 'Todas as categorias',
  'podcasts.filters.categoryLabel': 'Categoria',
  'podcasts.filters.searchLabel': 'Busca de texto',
  'podcasts.filters.searchPlaceholder': 'Título, descrição…',
  'podcasts.filters.viewCategories': 'Visualização por categorias',
  'podcasts.filters.viewList': 'Visualização em lista',
  'podcasts.form.descriptionLabel': 'Descrição',
  'podcasts.form.descriptionPlaceholder': 'Descreva seu podcast…',
  'podcasts.form.namePlaceholder': 'Nome do podcast',
  'podcasts.form.titleLabel': 'Título',
  'podcasts.language.ariaGroup': 'Idioma do podcast',
  'podcasts.language.help': 'Entre os 11 idiomas da plataforma — para alinhar o podcast ao mesmo locale do leitor e da interface do aprendiz.',
  'podcasts.language.label': 'Idioma',
  'podcasts.lightbox.ariaClose': 'Fechar',
  'podcasts.lightbox.ariaDialog': 'Pré-visualização da capa do podcast',
  'podcasts.list.actionActivate': 'Ativar',
  'podcasts.list.actionDeactivate': 'Desativar',
  'podcasts.list.actionDelete': 'Excluir',
  'podcasts.list.actionManageEpisodes': 'Gerenciar episódios',
  'podcasts.list.actionPreview': 'Pré-visualizar e ouvir',
  'podcasts.list.coverPreviewAria': 'Pré-visualização da capa do podcast {{title}}',
  'podcasts.list.createFirst': 'Criar um podcast',
  'podcasts.list.emptySubtitle': 'Crie seu primeiro podcast para começar',
  'podcasts.list.emptyTitle': 'Nenhum podcast',
  'podcasts.list.loadError': 'Não foi possível carregar os podcasts.',
  'podcasts.list.enterpriseBadge': 'Empresa',
  'podcasts.list.enterpriseNeedTitle': 'Necessidade definida pela sua empresa',
  'podcasts.list.episodesShort': '{{count}} ep.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} assin.',
  'podcasts.list.uncategorized': 'Sem categoria',
  'podcasts.modal.errorRequiredFields': 'Título e descrição são obrigatórios.',
  'podcasts.modal.errorSaveFailed': 'Erro ao salvar.',
  'podcasts.modal.subtitleNew': 'Preencha título, descrição e opções. Você poderá adicionar episódios depois.',
  'podcasts.modal.titleEdit': 'Editar podcast',
  'podcasts.modal.titleNew': 'Novo podcast',
  'podcasts.page.episodesLink': 'Episódios',
  'podcasts.page.newButton': 'Novo',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Página {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) no total',
  'podcasts.player.listen': 'Ouvir',
  'podcasts.player.noAudio': 'Sem áudio',
  'podcasts.player.pause': 'Pausa',
  'podcasts.preview.emptySubtitle': 'Este podcast ainda não tem episódios',
  'podcasts.preview.emptyTitle': 'Nenhum episódio',
  'podcasts.preview.episodeCount': '{{count}} episódio(s)',
  'podcasts.preview.fallbackDescription': 'Ouça os episódios de áudio disponíveis.',
  'podcasts.preview.playAll': 'Ouvir tudo',
  'podcasts.preview.title': 'Pré-visualização do podcast',
  'podcasts.share.assignedLearnersHeading': '{{count}} aprendiz(es) atribuído(s)',
  'podcasts.share.copied': 'Copiado',
  'podcasts.share.copyLink': 'Copiar o link',
  'podcasts.share.emailPlaceholder': 'E-mail *',
  'podcasts.share.errorCreateLearner': 'Não foi possível criar o aprendiz.',
  'podcasts.share.errorPublicLink': 'Não foi possível gerar o link público.',
  'podcasts.share.errorRemoveLearner': 'Não foi possível remover o aprendiz.',
  'podcasts.share.errorShare': 'Não foi possível compartilhar este podcast.',
  'podcasts.share.firstNamePlaceholder': 'Nome *',
  'podcasts.share.generatingLink': 'Gerando o link…',
  'podcasts.share.hideQuickCreate': 'Ocultar criação rápida',
  'podcasts.share.lastNamePlaceholder': 'Sobrenome *',
  'podcasts.share.loadingAssigned': 'Carregando…',
  'podcasts.share.loadingLearners': 'Carregando aprendizes...',
  'podcasts.share.messageAccessRemoved': 'Acesso removido para o aprendiz.',
  'podcasts.share.messageCreatedAndShared': 'Conta criada/vinculada, podcast compartilhado e e-mail de convite enviado.',
  'podcasts.share.messageShared': 'Podcast compartilhado e convite enviado ao aprendiz.',
  'podcasts.share.noAssignedLearners': 'Nenhum aprendiz atribuído a este podcast.',
  'podcasts.share.noLearnerAvailable': 'Nenhum aprendiz disponível.',
  'podcasts.share.panelTitle': 'Compartilhar o podcast',
  'podcasts.share.publicLinkSection': 'Link público (sem login)',
  'podcasts.share.removeAccessTitle': 'Remover o acesso',
  'podcasts.share.searchLearnerPlaceholder': 'Buscar um aprendiz (nome, sobrenome, e-mail)',
  'podcasts.share.selectExistingLearnerLabel': 'Selecionar um aprendiz existente',
  'podcasts.share.showQuickCreate': 'Criar novo aprendiz',
  'podcasts.share.submitCreateAndInvite': 'Criar, compartilhar e enviar convite',
  'podcasts.status.active': 'Ativo',
  'podcasts.status.inactive': 'Inativo',
  'learningPaths.card.contents': '{{count}} conteúdo(s)',
  'learningPaths.card.delete': 'Excluir',
  'learningPaths.card.edit': 'Editar',
  'learningPaths.card.learners': '{{count}} aluno(s)',
  'learningPaths.card.publish': 'Publicar',
  'learningPaths.card.unpublish': 'Despublicar',
  'learningPaths.card.updatedAt': 'Atualizado em {{date}}',
  'learningPaths.createPanel.cancel': 'Cancelar',
  'learningPaths.createPanel.description': 'Dê um nome à sua trilha; você poderá adicionar podcasts e formações.',
  'learningPaths.createPanel.descriptionLabel': 'Descrição',
  'learningPaths.createPanel.descriptionPlaceholder': 'Objetivo da trilha, público-alvo…',
  'learningPaths.createPanel.nameLabel': 'Nome da trilha',
  'learningPaths.createPanel.namePlaceholder': 'Ex.: Trilha de integração de RH…',
  'learningPaths.createPanel.submit': 'Criar trilha',
  'learningPaths.createPanel.title': 'Nova trilha',
  'learningPaths.deleteDialog.body': 'Você vai excluir « {{title}} ». Esta ação não pode ser desfeita.',
  'learningPaths.deleteDialog.cancel': 'Cancelar',
  'learningPaths.deleteDialog.confirm': 'Excluir',
  'learningPaths.deleteDialog.title': 'Excluir trilha',
  'learningPaths.empty.createFirst': 'Criar uma trilha',
  'learningPaths.empty.noPathsHint': 'Crie sua primeira trilha combinando podcasts e formações.',
  'learningPaths.empty.noPathsYet': 'Nenhuma trilha por enquanto',
  'learningPaths.empty.noResults': 'Nenhum resultado',
  'learningPaths.empty.noResultsHint': 'Altere seus filtros ou crie uma nova trilha.',
  'learningPaths.error.loadFailed': 'Não foi possível carregar as trilhas',
  'learningPaths.error.retry': 'Tentar novamente',
  'learningPaths.filters.allStatuses': 'Todos os status',
  'learningPaths.filters.draft': 'Rascunho',
  'learningPaths.filters.published': 'Publicado',
  'learningPaths.filters.searchPlaceholder': 'Buscar uma trilha…',
  'learningPaths.page.newButton': 'Nova trilha',
  'learningPaths.page.subtitle': 'Crie trilhas de formação ordenadas',
  'learningPaths.page.title': 'Trilhas',
  'learningPaths.page.totalCount': '{{count}} trilhas',
  'learningPaths.pagination.next': 'Próximo',
  'learningPaths.pagination.pageOf': 'Página {{page}} de {{total}} · {{count}} trilhas',
  'learningPaths.pagination.previous': 'Anterior',
  'learningPaths.status.draft': 'Rascunho',
  'learningPaths.status.published': 'Publicado',
  'webinars.card.code': 'Código {{code}}',
  'webinars.card.copyLink': 'Copiar link',
  'webinars.card.delete': 'Excluir',
  'webinars.card.edit': 'Editar',
  'webinars.card.openRoom': 'Abrir sala',
  'webinars.card.publicSignupBadge': 'Inscrição pública',
  'webinars.deleteDialog.body': 'Excluir « {{title}} »? Esta ação é irreversível.',
  'webinars.deleteDialog.cancel': 'Cancelar',
  'webinars.deleteDialog.confirm': 'Excluir',
  'webinars.deleteDialog.title': 'Excluir webinário',
  'webinars.eligibility.default': 'Os webinários não estão disponíveis para sua conta (verifique a conexão com a API ou entre em contato com o administrador).',
  'webinars.eligibility.moduleDisabled': 'O módulo de webinários ainda não está ativado para sua instituição. Um administrador deve marcar « Ativar webinários » em Instituição → Webinários.',
  'webinars.eligibility.noInstitution': 'Nenhuma instituição está associada à sua conta.',
  'webinars.eligibility.notAuthorized': 'Sua conta não está na lista de apresentadores autorizados. Peça à instituição para adicioná-la (Gestão de webinários → Apresentadores autorizados).',
  'webinars.eligibility.roleNotAllowed': 'Seu perfil não permite apresentar webinários com esta conta.',
  'webinars.empty.createFirst': 'Criar um webinário',
  'webinars.empty.noResults': 'Nenhum resultado',
  'webinars.empty.noResultsHint': 'Altere sua pesquisa.',
  'webinars.empty.noWebinarsHint': 'Crie uma sala e convide participantes pelo painel lateral.',
  'webinars.empty.noWebinarsYet': 'Nenhum webinário por enquanto',
  'webinars.externalInvite.closeButton': 'Fechar',
  'webinars.externalInvite.description': 'Pessoas que não constam na lista de aprendizes: cada endereço recebe um e-mail com o link da sala e um anexo para o calendário.',
  'webinars.externalInvite.done': 'Concluído.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} já convidado',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} já convidados',
  'webinars.externalInvite.emailsHint': 'Um endereço por linha, ou separados por vírgulas. Duplicatas e convites já enviados são ignorados.',
  'webinars.externalInvite.emailsLabel': 'Endereços de e-mail',
  'webinars.externalInvite.emailsPlaceholder': 'usuario@exemplo.com.br\noutro@dominio.org',
  'webinars.externalInvite.errorMax200': 'Máximo de 200 endereços por envio.',
  'webinars.externalInvite.errorMinOne': 'Digite pelo menos um endereço de e-mail válido.',
  'webinars.externalInvite.errorsLabel': 'erros: {{errors}}',
  'webinars.externalInvite.sendButton': 'Enviar convites',
  'webinars.externalInvite.sendFailed': 'Falha no envio',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail enviado',
  'webinars.externalInvite.sentCount_other': '{{count}} e-mails enviados',
  'webinars.externalInvite.skippedCount_one': '{{count}} ignorada',
  'webinars.externalInvite.skippedCount_other': '{{count}} ignoradas',
  'webinars.externalInvite.title': 'Convites por e-mail',
  'webinars.invite.duplicatesCount_one': '{{count}} já convidado',
  'webinars.invite.duplicatesCount_other': '{{count}} já convidados',
  'webinars.invite.errorsLabel': 'erros: {{errors}}',
  'webinars.invite.externalButton': 'Fora da lista (e-mail)',
  'webinars.invite.failed': 'Falha',
  'webinars.invite.groupSuccess': 'Convites em grupo processados (e-mails enviados aos membros).',
  'webinars.invite.inviteButton': 'Convidar',
  'webinars.invite.learnersHelp': 'Pesquise um aprendiz da sua instituição: um <strong>e-mail de convite</strong> (link + agenda) é enviado a cada convite.',
  'webinars.invite.learnersTitle': 'Aprendizes',
  'webinars.invite.noCandidates': 'Nenhum aprendiz para exibir (tente outra pesquisa ou use « Fora da lista » para e-mails externos).',
  'webinars.invite.noInvitations': 'Nenhum convite registrado.',
  'webinars.invite.searchLabel': 'Pesquisar aprendiz',
  'webinars.invite.searchPlaceholder': 'Nome ou e-mail…',
  'webinars.invite.searching': 'Pesquisando…',
  'webinars.invite.sentCount_one': '{{count}} convite enviado (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} convites enviados (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Convites enviados',
  'webinars.page.newButton': 'Novo webinário',
  'webinars.page.searchPlaceholder': 'Pesquisar…',
  'webinars.page.subtitle': 'Lista de suas salas — abra o painel para criar, editar ou convidar participantes.',
  'webinars.page.title': 'Webinários',
  'webinars.panel.backgroundHint': 'URL https (ou http) exibida atrás do conteúdo compartilhado para os participantes. Deixe em branco para usar o visual Lyrra integrado.',
  'webinars.panel.backgroundLabel': 'Imagem de fundo (visão do participante)',
  'webinars.panel.backgroundPlaceholder': 'https://… (vazio = fundo Lyrra padrão)',
  'webinars.panel.copyLink': 'Copiar link',
  'webinars.panel.createButton': 'Criar',
  'webinars.panel.createDescription': 'Preencha as informações e salve. Você poderá convidar participantes depois.',
  'webinars.panel.createTitle': 'Novo webinário',
  'webinars.panel.dateLabel': 'Data (opcional)',
  'webinars.panel.deleteButton': 'Excluir',
  'webinars.panel.descriptionLabel': 'Descrição',
  'webinars.panel.durationError': 'Informe a duração no formato horas:minutos (ex.: 1:00 ou 0:45).',
  'webinars.panel.durationHint': 'Obrigatório se houver data. Ex.: 1:30 para 1 h 30, 0:45 para 45 min.',
  'webinars.panel.durationLabel': 'Duração (h:mm)',
  'webinars.panel.editTitle': 'Webinário',
  'webinars.panel.loading': 'Carregando…',
  'webinars.panel.openRoom': 'Abrir sala',
  'webinars.panel.presentersLabel': 'Coapresentadores',
  'webinars.panel.roomCode': 'Código da sala:',
  'webinars.panel.timeHint': 'Se estiver vazio: 9:00.',
  'webinars.panel.timeLabel': 'Hora de início',
  'webinars.panel.titleLabel': 'Título',
  'webinars.panel.waitingHtmlLabel': 'HTML da sala de espera (opcional)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Bem-vindo…</p>',
  'webinars.panel.publicAccessTitle': 'Inscrição pública (formulário)',
  'webinars.panel.publicAccessHelp':
    'Os participantes que não são coapresentadores preenchem um formulário após iniciar sessão no Lyrra: e-mail, palavra-passe (à escolha deles) e os campos extra que adicionar abaixo.',
  'webinars.panel.publicFormFixedFieldsHint':
    'E-mail e palavra-passe (mín. 8 caracteres) são sempre pedidos; aqui adiciona outros campos opcionais ou obrigatórios.',
  'webinars.panel.publicFormAddFieldButton': 'Adicionar campo',
  'webinars.panel.publicFormFieldNumber': 'Campo {{n}}',
  'webinars.panel.publicFormFieldLabel': 'Rótulo apresentado',
  'webinars.panel.publicFormFieldType': 'Tipo',
  'webinars.panel.publicFormFieldTypeText': 'Texto curto',
  'webinars.panel.publicFormFieldTypeTextarea': 'Texto longo',
  'webinars.panel.publicFormFieldRequired': 'Obrigatório',
  'webinars.panel.publicFormFieldRemove': 'Remover',
  'webinars.panel.publicFormFieldIdHint': 'Identificador técnico:',
  'webinars.panel.publicFormFieldLabelRequired': 'Cada campo deve ter um rótulo.',
  'webinars.panel.publicAccessLoginLabel': 'Identificador do formulário de inscrição',
  'webinars.panel.publicAccessPasswordLabel': 'Palavra-passe do formulário',
  'webinars.panel.publicAccessPasswordPlaceholderNew': 'Pelo menos 8 caracteres',
  'webinars.panel.publicAccessPasswordPlaceholderKeep': 'Deixe em branco para manter a palavra-passe atual',
  'webinars.panel.publicAccessPasswordHint':
    'Na criação ou na primeira ativação: palavra-passe obrigatória (mín. 8). Depois: deixe em branco para não a alterar.',
  'webinars.panel.publicAccessLoginRequired': 'Indique um identificador para a inscrição pública.',
  'webinars.panel.publicAccessPasswordRequired': 'Defina uma palavra-passe com pelo menos 8 caracteres.',
  'webinars.panel.publicAccessPasswordMin': 'A palavra-passe pública deve ter pelo menos 8 caracteres.',
  'webinars.status.draft': 'Rascunho',
  'webinars.status.ended': 'Encerrado',
  'webinars.status.live': 'Ao vivo',
  'webinars.status.scheduled': 'Agendado',
  'videos.addPanel.cancel': 'Cancelar',
  'videos.addPanel.continue': 'Continuar',
  'videos.addPanel.description': 'Importe MP4, WebM ou MOV para usá-los em seus treinamentos.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Clique para escolher um vídeo',
  'videos.addPanel.title': 'Adicionar um vídeo',
  'videos.addPanel.titleLabel': 'Título',
  'videos.addPanel.titlePlaceholder': 'Título do vídeo…',
  'videos.card.delete': 'Excluir',
  'videos.card.edit': 'Editar',
  'videos.card.noThumbnail': 'Sem miniatura',
  'videos.card.publishedStudio': 'Publicado (editável no Studio)',
  'videos.deleteDialog.body': '« {{title}} » será excluído permanentemente.',
  'videos.deleteDialog.cancel': 'Cancelar',
  'videos.deleteDialog.confirm': 'Excluir',
  'videos.deleteDialog.title': 'Excluir o vídeo?',
  'videos.empty.addButton': 'Adicionar um vídeo',
  'videos.empty.description': 'Importe seu primeiro vídeo para gerenciá-lo e usá-lo em seus treinamentos.',
  'videos.empty.title': 'Nenhum vídeo',
  'videos.page.addButton': 'Adicionar um vídeo',
  'videos.page.count_one': '{{count}} vídeo na sua biblioteca',
  'videos.page.count_other': '{{count}} vídeos na sua biblioteca',
  'videos.page.title': 'Vídeos',
  'videos.preview.noFile': 'Nenhum arquivo disponível.',
  'videos.status.draft': 'Rascunho',
  'videos.status.published': 'Publicado',
  'sounds.card.delete': 'Excluir',
  'sounds.card.mute': 'Silenciar',
  'sounds.deleteDialog.body': '« {{title}} » será excluído permanentemente do servidor e da sua biblioteca.',
  'sounds.deleteDialog.cancel': 'Cancelar',
  'sounds.deleteDialog.confirm': 'Excluir',
  'sounds.deleteDialog.title': 'Excluir este som?',
  'sounds.empty.description': 'Importe seus arquivos: eles são registrados no banco e copiados para o servidor para que nada se perca.',
  'sounds.empty.importButton': 'Importar um som',
  'sounds.empty.title': 'Nenhum som importado',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Cancelar',
  'sounds.importPanel.changeFile': 'Trocar arquivo',
  'sounds.importPanel.createTag': 'Criar « {{tag}} »',
  'sounds.importPanel.description': 'Adicione um arquivo de áudio à sua biblioteca para seus percursos.',
  'sounds.importPanel.descriptionLabel': 'Descrição',
  'sounds.importPanel.descriptionPlaceholder': 'Descreva este som…',
  'sounds.importPanel.dropzoneBrowse': 'procure',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Arraste um arquivo de áudio ou',
  'sounds.importPanel.optional': '(opcional)',
  'sounds.importPanel.submit': 'Importar',
  'sounds.importPanel.submitting': 'Enviando…',
  'sounds.importPanel.tagsHint': 'Digite e pressione Enter · Novas tags são criadas automaticamente',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Adicionar tags…',
  'sounds.importPanel.title': 'Importar um som',
  'sounds.importPanel.titleLabel': 'Título',
  'sounds.importPanel.titlePlaceholder': 'Nome do som…',
  'sounds.importPanel.titleRequired': 'O título é obrigatório',
  'sounds.noResults': 'Nenhum som corresponde a « {{query}} »',
  'sounds.page.count_one': '{{count}} arquivo — salvo no banco de dados e no servidor',
  'sounds.page.count_other': '{{count}} arquivos — salvos no banco de dados e no servidor',
  'sounds.page.importButton': 'Importar',
  'sounds.page.searchPlaceholder': 'Título, descrição, tag…',
  'sounds.page.title': 'Sons',
  'documents.count_one': '{{count}} documento',
  'documents.count_other': '{{count}} documentos',
  'documents.delete.body': 'O documento « {{filename}} » será removido da sua biblioteca.',
  'documents.delete.cancel': 'Cancelar',
  'documents.delete.confirm': 'Excluir',
  'documents.delete.irreversible': 'Esta ação não pode ser desfeita.',
  'documents.delete.title': 'Excluir o documento?',
  'documents.importPanel.cancel': 'Cancelar',
  'documents.importPanel.chooseFile': 'Escolher um documento PDF',
  'documents.importPanel.description': 'O documento ficará disponível nos seus blocos PDF e nesta biblioteca.',
  'documents.importPanel.descriptionLabel': 'Descrição',
  'documents.importPanel.descriptionPlaceholder': 'Descrição (opcional)',
  'documents.importPanel.fileHint': 'O documento ficará disponível no seu bloco PDF e nesta biblioteca.',
  'documents.importPanel.submit': 'Importar',
  'documents.importPanel.title': 'Importar um PDF',
  'documents.importPanel.titleLabel': 'Título',
  'documents.importPanel.titlePlaceholder': 'Título do documento',
  'documents.importPanel.uploading': 'Enviando…',
  'documents.importPdf': 'Importar um PDF',
  'documents.loading': 'Carregando a biblioteca…',
  'documents.newDocument': 'Novo documento',
  'documents.noDocuments': 'Nenhum documento',
  'documents.noDocumentsHint': 'Importe seus PDFs aqui para reutilizá-los depois no bloco PDF.',
  'documents.noResults': 'Nenhum documento corresponde à sua busca.',
  'documents.pages_one': '{{count}} página',
  'documents.pages_other': '{{count}} páginas',
  'documents.previewTitle': 'Pré-visualização do documento',
  'documents.searchPlaceholder': 'Buscar um PDF...',
  'documents.share.accessRemoved': 'Acesso do aprendiz revogado.',
  'documents.share.assignedTitle': 'Aprendizes com acesso',
  'documents.share.close': 'Fechar',
  'documents.share.createAndShare': 'Criar, compartilhar e enviar o convite',
  'documents.share.createError': 'Não foi possível criar o aprendiz.',
  'documents.share.createLearner': 'Criar novo aprendiz',
  'documents.share.createdSuccess': 'Conta criada/vinculada, documento compartilhado e e-mail enviado.',
  'documents.share.creating': 'Enviando...',
  'documents.share.emailPlaceholder': 'E-mail *',
  'documents.share.firstNamePlaceholder': 'Nome *',
  'documents.share.hideCreateLearner': 'Ocultar criação rápida',
  'documents.share.lastNamePlaceholder': 'Sobrenome *',
  'documents.share.loadingAssigned': 'Carregando…',
  'documents.share.loadingLearners': 'Carregando aprendizes...',
  'documents.share.noAssigned': 'Nenhum aprendiz atribuído a este documento.',
  'documents.share.noLearners': 'Nenhum aprendiz disponível.',
  'documents.share.removeButton': 'Remover',
  'documents.share.removeError': 'Não foi possível revogar o acesso.',
  'documents.share.searchPlaceholder': 'Buscar aprendiz (nome, sobrenome, e-mail)',
  'documents.share.selectLearner': 'Selecionar um aprendiz existente',
  'documents.share.shareButton': 'Compartilhar',
  'documents.share.shareError': 'Não foi possível compartilhar o documento.',
  'documents.share.sharedSuccess': 'Documento compartilhado e convite enviado.',
  'documents.share.title': 'Compartilhar o documento',
  'documents.subtitle': 'Biblioteca de documentos PDF reutilizável no designer.',
  'documents.title': 'Documentos',
  'documents.toast.importError': 'Não foi possível importar',
  'documents.toast.importErrorDefault': 'A importação falhou.',
  'documents.toast.importSuccess': 'Documento importado',
  'documents.toast.importSuccessDesc': '{{filename}} foi adicionado à sua biblioteca de documentos.',
  'documents.updating': 'Atualizando…',
  'help.categories.analytics.description': 'Painéis, filtros e exportações para acompanhar o impacto dos treinamentos.',
  'help.categories.analytics.title': 'Análises e relatórios',
  'help.categories.apprenants.description': 'Acompanhar inscrições, coortes e a progressão dos participantes.',
  'help.categories.apprenants.title': 'Aprendizes, grupos e acompanhamento',
  'help.categories.boutique.description': 'Vender ou atribuir treinamentos quando a loja estiver configurada.',
  'help.categories.boutique.title': 'Loja e monetização',
  'help.categories.demarrage.description': 'Familiarize-se com a interface, seu perfil e a navegação diária no Lyrra.',
  'help.categories.demarrage.title': 'Início e conta',
  'help.categories.donnees.description': 'LGPD, conservação, direitos das pessoas e boas práticas de segurança.',
  'help.categories.donnees.title': 'Dados pessoais e segurança',
  'help.categories.eduflow.description': 'Conceber percursos pedagógicos com blocos, conexões e lógica de navegação.',
  'help.categories.eduflow.title': 'Percursos e designer EduFlow',
  'help.categories.evaluation.description': 'Questões de múltipla escolha, avaliações, enquetes e feedbacks para medir os conhecimentos adquiridos.',
  'help.categories.evaluation.title': 'Avaliação e pedagogia ativa',
  'help.categories.institution.description': 'Configurar sua estrutura, os colaboradores e a organização dos conteúdos.',
  'help.categories.institution.title': 'Instituição e equipe',
  'help.categories.marque.description': 'Alinhar cores, rótulos e percurso do aprendiz com a sua identidade.',
  'help.categories.marque.title': 'Marca e experiência do usuário',
  'help.categories.medias.description': 'Gerenciar biblioteca, importações e ferramentas de áudio/vídeo/imagem integradas ao Lyrra.',
  'help.categories.medias.title': 'Mídias e estúdio criativo',
  'help.categories.parcours-v2.description': 'Campanhas, competências, integrações: tudo que amplia o gerenciamento de treinamentos.',
  'help.categories.parcours-v2.title': 'Funções avançadas e roadmap',
  'help.categories.podcasts.description': 'Criar e distribuir conteúdos de áudio longos fora do percurso bloco a bloco.',
  'help.categories.podcasts.title': 'Podcasts e audiolivros',
  'help.categories.publication.description': 'Tornar um percurso disponível para os aprendizes e compartilhar os acessos corretos.',
  'help.categories.publication.title': 'Publicação e difusão',
  'help.categories.qualite.description': 'Referências de qualidade francesas: rastreabilidade, objetivos e provas para auditorias.',
  'help.categories.qualite.title': 'Qualidade, Qualiopi e conformidade pedagógica',
  'help.categories.support.description': 'Em caso de bloqueio técnico ou funcional, saber onde procurar e quem contatar.',
  'help.categories.support.title': 'Resolução de problemas e suporte',
  'help.configHint': 'Conteúdo editorial: conecte seus URLs, artigos ou base de conhecimento quando estiver pronto.',
  'help.description': 'Documentação e suporte',
  'help.hubIntro': 'Temas que abrangem o uso do Lyrra: em breve você poderá vincular cada ficha à sua documentação, tutoriais ou tickets de suporte.',
  'help.modal.expectedAnswer': 'Resposta esperada:',
  'help.modal.fallbackHighlight1': 'Ficha',
  'help.modal.fallbackHighlight2': 'Plataforma de treinamento',
  'help.modal.fallbackHighlight3': 'A completar',
  'help.modal.fallbackSectionHeading': 'Pontos importantes',
  'help.modal.fallbackSectionParagraph': 'O conteúdo detalhado desta ficha será enriquecido ou vinculado à sua documentação interna.',
  'help.modal.fallbackStep1Desc': 'Use o menu lateral ou o painel para acessar o módulo correspondente.',
  'help.modal.fallbackStep1Title': 'Abrir a funcionalidade',
  'help.modal.fallbackStep2Desc': 'As telas guiam você: salve frequentemente e teste na pré-visualização quando disponível.',
  'help.modal.fallbackStep2Title': 'Seguir o fluxo de trabalho',
  'help.modal.fallbackStep3Desc': 'Anote as especificidades da sua instituição (processos de RH, nomenclatura, identidade visual) para sua equipe.',
  'help.modal.fallbackStep3Title': 'Documentar internamente',
  'help.modal.fallbackTip': 'Em caso de dúvida, verifique seu papel (formador, admin, RH): os menus exibidos dependem dele.',
  'help.modal.quiz': 'Quiz de validação',
  'help.modal.related': 'Veja também',
  'help.modal.resources': 'Recursos',
  'help.modal.steps': 'Na prática',
  'help.modal.tips': 'Dicas',
  'help.noResults': 'Nenhum resultado para esta pesquisa.',
  'help.onboarding.ai-designer': 'Treinamento — Assistente IA do designer LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Treinamento — Tipos de blocos EduFlow (onboarding)',
  'help.onboarding.branches': 'Treinamento — Ramificações e condições EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Treinamento — Catálogo e visibilidade LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Treinamento — Certificados emitidos LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Treinamento — Painel do Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Treinamento — Visão geral do designer EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Treinamento — Inscrições e participantes LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Treinamento — Grupos e coortes LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Treinamento — Biblioteca de mídia LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Treinamento — Navegação e menu lateral do Lyrra (onboarding)',
  'help.onboarding.objectives': 'Treinamento — Objetivos pedagógicos EduFlow (onboarding)',
  'help.onboarding.profile': 'Treinamento — Perfil e preferências do Lyrra (onboarding)',
  'help.onboarding.progress': 'Treinamento — Progressão e tempo gasto LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Treinamento — Links de acesso e compartilhamento LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Treinamento — Rascunho, publicação e atualizações LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Treinamento — Funções e direitos de acesso do Lyrra (onboarding)',
  'help.onboarding.validation': 'Treinamento — Validação do percurso EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Treinamento não encontrado',
  'help.onboardingFlowMissingDesc': 'Execute os scripts db:seed:onboarding-eduflow e db:seed:onboarding-navigation-eduflow (backend), ou as variáveis VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Abrir a ficha',
  'help.searchPlaceholder': 'Pesquisar um tema…',
  'help.title': 'Central de ajuda',
  'help.topics.analytics-exports.description': 'Preparar as extrações para o relatório interno ou regulatório.',
  'help.topics.analytics-exports.title': 'Exportações e relatórios de RH',
  'help.topics.analytics-indicateurs.description': 'Taxa de conclusão, engajamento, distribuição por status e tempo nos blocos.',
  'help.topics.analytics-indicateurs.title': 'Indicadores-chave',
  'help.topics.analytics-vue.description': 'Personalizar gráficos, períodos e filtro por treinamento para analisar os dados.',
  'help.topics.analytics-vue.title': 'Visão geral analítica',
  'help.topics.app-certificats.description': 'Verificar as attestações geradas e as condições de aprovação associadas.',
  'help.topics.app-certificats.title': 'Certificados emitidos',
  'help.topics.app-groupes.description': 'Usar os grupos para segmentar os públicos e facilitar campanhas ou relatórios.',
  'help.topics.app-groupes.title': 'Grupos e coortes',
  'help.topics.app-inscriptions.description': 'Consultar a lista de aprendizes em um percurso, seus status e históricos de acesso.',
  'help.topics.app-inscriptions.title': 'Inscrições e participantes',
  'help.topics.app-progression.description': 'Interpretar a progressão, as conclusões e o tempo de engajamento nos blocos.',
  'help.topics.app-progression.title': 'Progressão e tempo gasto',
  'help.topics.boutique-offres.description': 'Vincular percursos a produtos ou códigos de acesso de acordo com seu modelo econômico.',
  'help.topics.boutique-offres.title': 'Ofertas e precificação',
  'help.topics.boutique-paiement.description': 'Entender o fluxo de compra pelo aprendiz e as confirmações pela organização.',
  'help.topics.boutique-paiement.title': 'Pagamentos e faturamento',
  'help.topics.demarrage-navigation.description': 'Encontrar os espaços Projetos, Treinamentos, Mídias, Análises e Configurações de acordo com seu papel.',
  'help.topics.demarrage-navigation.title': 'Navegação e menu lateral',
  'help.topics.demarrage-profil.description': 'Atualizar suas informações, o idioma da interface e as notificações.',
  'help.topics.demarrage-profil.title': 'Perfil e preferências',
  'help.topics.demarrage-roles.description': 'Entender as diferenças entre formador, administrador de instituição, RH e aprendiz.',
  'help.topics.demarrage-roles.title': 'Funções e direitos de acesso',
  'help.topics.demarrage-tableau-bord.description': 'Ler os indicadores-chave, acessar rapidamente os percursos recentes e os atalhos úteis.',
  'help.topics.demarrage-tableau-bord.title': 'Painel de treinamento',
  'help.topics.donnees-conservation.description': 'Princípios de prazo de arquivamento das progressões e das provas de treinamento.',
  'help.topics.donnees-conservation.title': 'Prazo de conservação',
  'help.topics.donnees-mots-de-passe.description': 'Boas práticas de senhas, sessões e delegação de acesso.',
  'help.topics.donnees-mots-de-passe.title': 'Contas e autenticação',
  'help.topics.donnees-rgpd.description': 'Finalidades dos dados coletados no Lyrra e responsabilidade do responsável pelo treinamento.',
  'help.topics.donnees-rgpd.title': 'LGPD e registro de tratamentos',
  'help.topics.eduflow-blocs.description': 'Escolher e configurar os blocos adequados: texto, vídeo, áudio, avaliação, ramificação, etc.',
  'help.topics.eduflow-blocs.title': 'Tipos de blocos (conteúdo, quiz, mídias…)',
  'help.topics.eduflow-branches.description': 'Gerenciar múltiplas saídas, a convergência de caminhos e as regras de navegação.',
  'help.topics.eduflow-branches.title': 'Ramificações e condições',
  'help.topics.eduflow-concept.description': 'Canvas, paleta de blocos, seleção, zoom e organização visual do percurso.',
  'help.topics.eduflow-concept.title': 'Visão geral do designer',
  'help.topics.eduflow-ia.description': 'Sugerir ou modificar blocos com a ajuda do assistente, com revisão humana sistemática.',
  'help.topics.eduflow-ia.title': 'Assistente IA no designer',
  'help.topics.eduflow-objectifs.description': 'Vincular objetivos aos blocos para estruturar o referencial do seu treinamento.',
  'help.topics.eduflow-objectifs.title': 'Objetivos pedagógicos',
  'help.topics.eduflow-validation.description': 'Usar o painel de Validação para detectar erros e avisos antes da publicação.',
  'help.topics.eduflow-validation.title': 'Validação do percurso',
  'help.topics.eval-auto-eval.description': 'Propor percursos adaptativos com base nos resultados.',
  'help.topics.eval-auto-eval.title': 'Autoavaliação e remediação',
  'help.topics.eval-ouverte.description': 'Organizar a coleta de respostas livres e a correção manual ou semiautomática.',
  'help.topics.eval-ouverte.title': 'Questões abertas e correção',
  'help.topics.eval-quiz.description': 'Configurar baremas, feedbacks e tentativas para questões de múltipla escolha eficientes.',
  'help.topics.eval-quiz.title': 'Quiz e questões de múltipla escolha',
  'help.topics.inst-collaborateurs.description': 'Adicionar formadores, administradores ou RH e gerenciar as permissões.',
  'help.topics.inst-collaborateurs.title': 'Usuários e convites',
  'help.topics.inst-structure.description': 'Definir o que é compartilhado no nível da instituição versus projetos pessoais.',
  'help.topics.inst-structure.title': 'Estrutura e perímetro',
  'help.topics.inst-trash.description': 'Encontrar treinamentos ou podcasts excluídos e restaurá-los se necessário.',
  'help.topics.inst-trash.title': 'Lixeira e restauração',
  'help.topics.marque-accessibilite.description': 'Contrastes, alternativas textuais e navegação por teclado nos conteúdos.',
  'help.topics.marque-accessibilite.title': 'Acessibilidade e legibilidade',
  'help.topics.marque-charte.description': 'Como a marca se reflete no player e nos espaços dos aprendizes.',
  'help.topics.marque-charte.title': 'Identidade visual e cores institucionais',
  'help.topics.marque-landing.description': 'Apresentar sua oferta de treinamento para o visitante quando o módulo estiver ativado.',
  'help.topics.marque-landing.title': 'Páginas públicas e catálogo',
  'help.topics.medias-bibliotheque.description': 'Importar, organizar e reutilizar vídeos, sons, imagens e documentos nos seus blocos.',
  'help.topics.medias-bibliotheque.title': 'Biblioteca de mídia',
  'help.topics.medias-documents.description': 'Anexar recursos para download e vincular a documentação ao percurso.',
  'help.topics.medias-documents.title': 'Documentos e PDF',
  'help.topics.medias-studio-audio.description': 'Gravar, mixar ou usar a síntese de voz para enriquecer seus conteúdos.',
  'help.topics.medias-studio-audio.title': 'Estúdio de áudio e voz',
  'help.topics.medias-video.description': 'Boas práticas de formato, peso e acessibilidade para os players integrados.',
  'help.topics.medias-video.title': 'Vídeos e integrações',
  'help.topics.podcasts-creation.description': 'Estruturar episódios, metadados e capas para sua oferta de áudio.',
  'help.topics.podcasts-creation.title': 'Criação de um podcast',
  'help.topics.podcasts-diffusion.description': 'Configurações de escuta, compartilhamento e acompanhamento de uso pela instituição.',
  'help.topics.podcasts-diffusion.title': 'Distribuição e aprendizes',
  'help.topics.pub-catalogue.description': 'Vincular um treinamento ao catálogo institucional ou a uma oferta quando ativado.',
  'help.topics.pub-catalogue.title': 'Catálogo e visibilidade',
  'help.topics.pub-liens.description': 'Gerar ou copiar os links para os aprendizes, apresentadores ou pré-visualização.',
  'help.topics.pub-liens.title': 'Links de acesso e compartilhamento',
  'help.topics.pub-statuts.description': 'Ciclo de vida de um treinamento: salvar, testar, publicar e versionar as alterações.',
  'help.topics.pub-statuts.title': 'Rascunho, publicação e atualizações',
  'help.topics.qualite-qualiopi.description': 'Usar os espaços dedicados para preparar os critérios e o dossiê de qualidade.',
  'help.topics.qualite-qualiopi.title': 'Pré-auditoria e indicadores Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Vincular objetivos, conteúdos e avaliações para documentar o percurso certificador.',
  'help.topics.qualite-tracabilite.title': 'Rastreabilidade pedagógica',
  'help.topics.support-contact.description': 'Canal de suporte, criação de ticket e informações a fornecer para um diagnóstico rápido.',
  'help.topics.support-contact.title': 'Contatar o suporte Lyrra',
  'help.topics.support-faq.description': 'Player que não carrega, mídia ausente, erro de publicação: primeiros reflexos.',
  'help.topics.support-faq.title': 'Problemas frequentes',
  'help.topics.support-navigateur.description': 'Versões recomendadas, cookies e extensões que podem interferir.',
  'help.topics.support-navigateur.title': 'Navegador e compatibilidade',
  'help.topics.v2-campagnes.description': 'Planejar a implantação de percursos junto a populações-alvo ao longo do tempo.',
  'help.topics.v2-campagnes.title': 'Campanhas e planos de treinamento',
  'help.topics.v2-competences.description': 'Mapear os blocos ou percursos às competências esperadas para o relatório profissional.',
  'help.topics.v2-competences.title': 'Referenciais de competências',
  'help.topics.v2-integrations.description': 'Intercâmbios com seu SIRH, LMS externo ou ferramentas de BI quando conectores estiverem disponíveis.',
  'help.topics.v2-integrations.title': 'Integrações e API',
  'pages.images.cancel': 'Cancelar',
  'pages.images.changeFile': 'Alterar arquivo',
  'pages.images.copyUrl': 'Copiar URL',
  'pages.images.count_one': '{{count}} imagem na sua biblioteca',
  'pages.images.count_other': '{{count}} imagens na sua biblioteca',
  'pages.images.delete': 'Excluir',
  'pages.images.deleteAction': 'Excluir',
  'pages.images.deleteConfirmHint': 'será excluída definitivamente.',
  'pages.images.deleteTitle': 'Excluir esta imagem?',
  'pages.images.descriptionLabel': 'Descrição (opcional)',
  'pages.images.descriptionPlaceholder': 'Descrição breve…',
  'pages.images.dropzoneBrowse': 'navegue',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Arraste uma imagem ou',
  'pages.images.emptyHint': 'Arraste e solte, clique para importar ou cole uma captura (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importar',
  'pages.images.importSubtitle': 'Adicione uma imagem à sua biblioteca para seus treinamentos.',
  'pages.images.importTitle': 'Importar uma imagem',
  'pages.images.lightboxBackdropClose': 'Fechar',
  'pages.images.lightboxClose': 'Fechar',
  'pages.images.lightboxNext': 'Próxima imagem',
  'pages.images.lightboxPrev': 'Imagem anterior',
  'pages.images.lightboxTitle': 'Pré-visualização da imagem',
  'pages.images.noImages': 'Nenhuma imagem importada',
  'pages.images.noResults': 'Nenhum resultado',
  'pages.images.pasteHint': 'Ou cole uma captura de tela (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Pesquisar uma imagem…',
  'pages.images.submit': 'Importar',
  'pages.images.title': 'Imagens',
  'pages.images.titleLabel': 'Título (opcional)',
  'pages.images.titlePlaceholder': 'Nome da imagem…',
  'pages.images.viewLarge': 'Ver em tamanho grande',
  'pages.learners.blockSessionInProgress': '(em curso)',
  'pages.learners.blockTimeOnBlock': '({{duration}} neste bloco)',
  'pages.learners.description': 'Acompanhe o progresso dos seus aprendizes',
  'pages.learners.detailedTraces': 'Atividade detalhada · {{flowTitle}}',
  'pages.learners.enrolledFormations': 'Cursos inscritos',
  'pages.learners.lastEvents': 'Eventos recentes',
  'pages.learners.notStarted': 'Não iniciado',
  'pages.learners.startedOn': 'Início {{date}}',
  'pages.learners.statsBlockSessions': 'Sessões por bloco: {{count}}',
  'pages.learners.statsEvents': 'Eventos: {{count}}',
  'pages.learners.statsResponses': 'Respostas: {{count}}',
  'pages.learners.timePerBlock': 'Tempo por bloco',
  'pages.learners.title': 'Aprendizes',
  'pages.learners.trackingEvent.block_enter': 'Entrada num bloco',
  'pages.learners.trackingEvent.block_exit': 'Saída de um bloco',
  'pages.learners.trackingEvent.progress_sync': 'Progresso sincronizado',
  'pages.learners.trackingEvent.response_submitted': 'Resposta enviada',
  'pages.learners.trackingEvent.training_completed': 'Curso concluído',
  'pages.learners.trackingEvent.training_enter': 'Curso aberto',
  'pages.learners.trackingEvent.training_exit': 'Curso fechado ou abandonado',
  'pages.learners.trackingEvent.unknown': 'Evento',
  'pages.learners.refresh': 'Atualizar',
  'pages.learners.exportCsv': 'Exportar CSV',
  'pages.learners.statTotal': 'Total de aprendizes',
  'pages.learners.statInProgress': 'Em curso',
  'pages.learners.statCompleted': 'Concluídos',
  'pages.learners.statAvgProgress': 'Progresso médio',
  'pages.learners.searchPlaceholder': 'Pesquisar por nome ou e-mail…',
  'pages.learners.filterAllFlows': 'Todos os cursos',
  'pages.learners.sortRecent': 'Mais recentes',
  'pages.learners.sortName': 'Nome A-Z',
  'pages.learners.sortProgress': 'Progresso',
  'pages.learners.colLearner': 'Aprendiz',
  'pages.learners.colFormations': 'Cursos',
  'pages.learners.colProgress': 'Progresso',
  'pages.learners.colCompleted': 'Concluídos',
  'pages.learners.colTime': 'Tempo',
  'pages.learners.colLastAccess': 'Último acesso',
  'pages.learners.colActions': 'Ações',
  'pages.learners.openProgressDashboard': 'Painel de progresso',
  'pages.learners.dashboardTitle': 'Progresso do aprendiz',
  'pages.learners.dashboardBack': 'Voltar à lista',
  'pages.learners.dashboardInvalidId': 'Identificador de aprendiz inválido',
  'pages.learners.dashboardNotFound': 'Aprendiz não encontrado ou acesso negado',
  'pages.learners.dashboardOverviewHint':
    'Vista unificada: cursos EduFlow do participante, percursos e podcasts atribuídos à conta Lyrra com o mesmo e-mail.',
  'pages.learners.tabOverview': 'Resumo',
  'pages.learners.tabFormations': 'Cursos',
  'pages.learners.tabParcours': 'Percursos',
  'pages.learners.tabPodcasts': 'Podcasts',
  'pages.learners.sectionFormations': 'Cursos',
  'pages.learners.sectionParcours': 'Percursos',
  'pages.learners.sectionPodcasts': 'Podcasts',
  'pages.learners.emptyParcours': 'Nenhum percurso atribuído a uma conta Lyrra com este e-mail.',
  'pages.learners.emptyPodcasts': 'Nenhum podcast atribuído a uma conta Lyrra com este e-mail.',
  'pages.learners.linkedAccountHint':
    'Percursos e podcasts aparecem se existir um utilizador Lyrra com o mesmo e-mail do participante.',
  'pages.learners.kpiEduflows': 'Cursos',
  'pages.learners.kpiAvgProgress': 'Progresso médio',
  'pages.learners.kpiPaths': 'Percursos',
  'pages.learners.kpiPodcasts': 'Podcasts',
  'pages.learners.kpiCompletedFormations': '{{count}} concluídos',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} atividades',
  'pages.learners.pathAssignedOn': 'Adicionado em {{date}}',
  'pages.learners.podcastCompleted': 'Concluído',
  'pages.learners.podcastInProgress': 'Em curso',
  'pages.learners.pathStatusPublished': 'Publicado',
  'pages.learners.pathStatusDraft': 'Rascunho',
  'pages.learners.openInDesigner': 'Abrir no designer',
  'pages.learners.openParcoursEditor': 'Abrir percurso',
  'pages.learners.viewPodcasts': 'Ver podcasts',
  'pages.learners.loading': 'A carregar aprendizes…',
  'pages.learners.loadError': 'Não foi possível carregar os aprendizes',
  'pages.learners.retry': 'Tentar novamente',
  'pages.learners.emptyFiltered': 'Nenhum aprendiz encontrado',
  'pages.learners.emptyFilteredHint': 'Tente alterar a pesquisa ou os filtros',
  'pages.learners.emptyNone': 'Nenhum aprendiz inscrito',
  'pages.learners.emptyNoneHint':
    'Os aprendizes aparecerão aqui quando se inscreverem nos seus cursos publicados',
  'pages.learners.pagination': 'Página {{page}} de {{totalPages}} · {{total}} aprendiz(es)',
  'pages.learners.prev': 'Anterior',
  'pages.learners.next': 'Seguinte',
  'pages.learners.csvLastName': 'Apelido',
  'pages.learners.csvFirstName': 'Nome próprio',
  'pages.learners.csvEmail': 'E-mail',
  'pages.learners.csvFormations': 'Cursos',
  'pages.learners.csvAvgProgress': 'Progresso médio',
  'pages.learners.csvTotalTime': 'Tempo total',
  'pages.learners.csvLastActivity': 'Última atividade',
  'pages.learners.statsAdaptiveChoice':
    'Escolhas assistidas — aceites: {{accepted}} · recusadas: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptações avaliadas: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Versões simplificadas utilizadas: {{count}}',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Modo de texto simplificado',
  'accessibility.simplifiedTextModeDesc':
    'Simplifica apenas os textos do percurso para facilitar a leitura e a compreensão.',
  'accessibility.imageHoverAudio': 'Modo de baixa visão: reprodução de áudio da imagem ao passar o cursor',
  'accessibility.imageHoverAudioDesc':
    'Quando a imagem é sobrevoada no player, a descrição em áudio inicia automaticamente.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Recolher',
  'admin.shell.collapseSidebar': 'Recolher barra lateral',
  'admin.shell.expandSidebar': 'Expandir barra lateral',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.login': 'Sign in',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.logout': 'Log out',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.register': 'Create one',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'btn.cancel': 'Annuler',
  'btn.close': 'Fermer',
  'btn.save': 'Enregistrer',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.copied': 'Copié !',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.loading': 'Chargement…',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Pedido de formação — conteúdos pedagógicos',
  'trainingRequest.intro': 'Descreva a necessidade. As equipas de RH aprovam ou recusam o pedido; se aprovado, passa para preparação de conteúdos.',
  'trainingRequest.fieldTitle': 'Título da formação',
  'trainingRequest.fieldDescription': 'Descrição da necessidade',
  'trainingRequest.fieldJustification': 'Justificação / objetivos',
  'trainingRequest.fieldDate': 'Data pretendida',
  'trainingRequest.datePlaceholder': 'ex. T2 2026',
  'trainingRequest.fieldFormateur': 'Formador da instituição',
  'trainingRequest.formateurPlaceholder': 'Escolher um formador…',
  'trainingRequest.formateursEmpty': 'Nenhuma conta de formador registada para a sua instituição.',
  'trainingRequest.titleRequired': 'Indique um título (mínimo 2 caracteres).',
  'trainingRequest.sent': 'Pedido registado. Será notificado por e-mail sobre a decisão.',
  'trainingRequest.sendError': 'Não foi possível registar o pedido. Verifique a sua ligação ou permissões.',
  'trainingRequest.submit': 'Enviar o pedido',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.total': 'au total',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.collaborativeBadge': 'Colaborativo',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.createFormation.error': 'Não foi possível criar o curso.',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duplicateFlowError': 'Não foi possível duplicar o curso.',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.list.title': 'Cursos',
  'eduflow.list.subtitle': 'Conceba experiências de aprendizagem interativas',
  'eduflow.list.searchPlaceholder': 'Pesquisar um curso…',
  'eduflow.list.import': 'Importar',
  'eduflow.list.importHint':
    'Ficheiro .lyrra (média incluída) ou exportação JSON antiga Lyrra V1.',
  'eduflow.list.importSuccess': 'Curso importado. A abrir o designer.',
  'eduflow.list.importError':
    'Importação impossível — verifique o ficheiro (.lyrra ou JSON válido, pelo menos um bloco).',
  'eduflow.list.filterAll': 'Todos',
  'eduflow.list.filterDraft': 'Rascunhos',
  'eduflow.list.filterPublished': 'Publicados',
  'eduflow.list.filterArchived': 'Arquivados',
  'eduflow.list.viewModeGrid': 'Vista em grelha',
  'eduflow.list.viewModeList': 'Vista em lista',
  'eduflow.list.viewByCategory': 'Vista por categorias',
  'eduflow.list.categoryBack': 'Todas as categorias',
  'eduflow.list.categoryBrowseHint': 'Escolha uma categoria para ver os seus cursos.',
  'eduflow.list.categoryFormationCount': '{{count}} curso(s)',
  'eduflow.list.categoryEmptyFiltered': 'Nenhum curso nesta vista (filtro ou pesquisa ativos).',
  'eduflow.list.uncategorizedCardHint': 'Cursos sem etiqueta de categoria.',
  'eduflow.list.uncategorized': 'Sem categoria',
  'eduflow.list.manageCategories': 'Gerir categorias',
  'eduflow.list.categoriesPanelTitle': 'Categorias do curso',
  'eduflow.list.noCategoryAvailable': 'Nenhuma categoria disponível.',
  'eduflow.list.categoriesSaveError': 'Não foi possível guardar as categorias.',
  'eduflow.list.emptyFilteredTitle': 'Sem resultados',
  'eduflow.list.emptyFilteredHint': 'Tente alterar a pesquisa ou os filtros.',
  'eduflow.list.emptyFirstTitle': 'Crie o seu primeiro curso',
  'eduflow.list.emptyFirstHint': 'Crie aprendizagem interativa com o designer visual.',
  'eduflow.list.emptyCreateCta': 'Criar o meu primeiro curso',
  'eduflow.list.deleteToTrashTitle': 'Mover este curso para o lixo?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » será retirado da sua lista (eliminação lógica). A sua organização pode restaurá-lo no lixo institucional.',
  'eduflow.list.deleteToTrashConfirm': 'Para o lixo',
  'eduflow.list.coverLabel': 'Imagem de capa',
  'eduflow.list.coverRemove': 'Remover capa',
  'eduflow.list.coverUpload': 'Escolher imagem',
  'eduflow.list.flowProperties': 'Título, descrição e capa',
  'eduflow.list.propertiesPanelTitle': 'Propriedades do curso',
  'eduflow.list.duplicatePanelTitle': 'Duplicar curso',
  'eduflow.list.duplicatePanelSubtitle': 'Curso original: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'cópia',
  'eduflow.list.duplicateSuccess': 'Curso duplicado.',
  'eduflow.list.duplicateCoverHint':
    'A imagem de capa e o conteúdo pedagógico do curso original serão reutilizados.',
  'eduflow.list.propertiesSaveError': 'Não foi possível guardar as propriedades.',
  'eduflow.list.propertiesSaved': 'Propriedades guardadas.',
  'eduflow.list.propertiesUploadError': 'Falha ao carregar a imagem.',
  'eduflow.list.blocksCount': '{{count}} blocos',
  'eduflow.list.noContent': 'Sem conteúdo',
  'eduflow.list.minutesShort': '{{count}} min',
  'eduflow.list.tooltip.openMissions': 'Abrir Minhas missões de produção',
  'eduflow.list.tooltip.edit': 'Editar',
  'eduflow.list.shellHint':
    'Ficha definida pela sua organização — crie o EduFlow em « Minhas missões de produção ».',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Seguinte',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Versão {{label}} ativada (pública)',
  'eduflow.versionHistory.activateTitle': 'Ativar {{label}}?',
  'eduflow.versionHistory.blocks': '{{count}} blocos',
  'eduflow.versionHistory.confirmActivate': 'Ativar',
  'eduflow.versionHistory.copyFailed': 'Falha ao copiar',
  'eduflow.versionHistory.copyHtml': 'Copiar HTML',
  'eduflow.versionHistory.copyPlain': 'Copiar apenas texto',
  'eduflow.versionHistory.copyPublicPlayer': 'Copiar link público',
  'eduflow.versionHistory.copyPublicPlayerError': 'Não foi possível copiar o link público',
  'eduflow.versionHistory.count': '{{count}} versão(ões)',
  'eduflow.versionHistory.createError': 'Erro ao criar a versão',
  'eduflow.versionHistory.createMajor': 'Versão principal',
  'eduflow.versionHistory.createMajorSuccess': 'Versão principal criada',
  'eduflow.versionHistory.createMinor': 'Versão secundária',
  'eduflow.versionHistory.createMinorSuccess': 'Versão secundária criada',
  'eduflow.versionHistory.daysAgo': 'há {{count}} dias',
  'eduflow.versionHistory.deleteError': 'Erro ao excluir',
  'eduflow.versionHistory.deleteSuccess': 'Versão excluída',
  'eduflow.versionHistory.deleteTitle': 'Excluir {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'Esta ação é irreversível.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplicar',
  'eduflow.versionHistory.duplicateError': 'Erro ao duplicar',
  'eduflow.versionHistory.duplicateMinor': 'Duplicar (secundária)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Duplicar {{label}}?',
  'eduflow.versionHistory.empty': 'Nenhuma versão criada.',
  'eduflow.versionHistory.emptyHint': 'Salve uma versão principal ou secundária para encontrá-la aqui.',
  'eduflow.versionHistory.hoursAgo': 'há {{count}} h',
  'eduflow.versionHistory.latest': 'Mais recente',
  'eduflow.versionHistory.minutesAgo': 'há {{count}} min',
  'eduflow.versionHistory.newFromCurrent': 'Nova versão a partir do conteúdo atual',
  'eduflow.versionHistory.openPublicPlayer': 'Abrir player público',
  'eduflow.versionHistory.preview': 'Pré-visualização',
  'eduflow.versionHistory.previewBlocks': '{{count}} blocos',
  'eduflow.versionHistory.previewEdges': '{{count}} conexões',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Pré-visualização {{label}}',
  'eduflow.versionHistory.public': 'Versão pública',
  'eduflow.versionHistory.publicActive': 'Ativa para aprendizes',
  'eduflow.versionHistory.setPublic': 'Ativar',
  'eduflow.versionHistory.startDescModalTitle': 'Descrição — bloco Início ({{version}})',
  'eduflow.versionHistory.yesterday': 'ontem',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.settings': 'Settings',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'A traduzir…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Caminho circular detectado',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} erro(s)',
  'designer.validation.warningCount': '{{count}} aviso(s)',
  'designer.validation.titleRequired': 'O bloco « {{type}} » deve ter um título.',
  'designer.validation.summary': 'Percurso: {{blocks}} bloco(s), {{edges}} ligação(ões).',
  'designer.validation.multipleStart': 'Vários blocos Início ({{count}}): só um é permitido.',
  'designer.validation.noOutgoing': 'O bloco « {{type}} » não tem ligação de saída para um bloco seguinte.',
  'designer.validation.tooManyOutputs': '« {{label}} »: demasiadas ligações de saída ({{count}}, máximo {{max}}).',
  'designer.validation.deadEndBranch': 'Ramo sem caminho para um bloco Fim (a partir de « {{label}} »).',
  'designer.validation.moreDeadEnds': '… e mais {{count}} ramo(s) sem saída para o fim.',
  'designer.validation.noConvergence': 'Os ramos a partir de « {{label}} » não convergem para um bloco comum.',
  'designer.validation.orphanBlock': 'Bloco isolado do grafo principal: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'O bloco Fim « {{title}} » ainda tem uma ligação de saída.',
  'designer.validation.noScoring': 'Parâmetros de pontuação não definidos para « {{title}} ».',
  'designer.validation.emptyText': 'O bloco de texto « {{title}} » está vazio.',
  'designer.validation.textTooLong':
    'O bloco de texto « {{title}} » está estimado em {{lines}} linhas. Recomendado: de {{min}} a {{max}} linhas.',
  'designer.validation.splitTextBlock': 'Separar',
  'designer.validation.textSplitNotNeeded':
    'Este bloco não excede o limite de leitura recomendado.',
  'designer.validation.textSplitUnable':
    'Não foi possível propor uma separação automática fiável para este bloco.',
  'designer.validation.textSplitComplexFlow':
    'Separação automática indisponível para este bloco (ramificações múltiplas/condicionais).',
  'designer.validation.textSplitDone': '{{count}} blocos de texto criados',
  'designer.validation.textSplitConfirmTitle':
    'Separar automaticamente este bloco de texto?',
  'designer.validation.textSplitConfirmMessage':
    'O bloco « {{title}} » está estimado em {{lines}} linhas. Vamos separá-lo em {{parts}} blocos (guiado pela pontuação) e posicioná-los logo abaixo, na ordem correta.',
  'designer.validation.textSplitConfirmAction': 'Separar bloco',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Voz padrão',
  'voicePanel.provider': 'Fornecedor: Mistral AI',
  'voicePanel.hint': 'Selecione a voz usada para a narração dos blocos.',
  'voicePanel.translationFrenchFallback':
    'Áudio traduzido: se não houver voz predefinida guardada aqui, para francês usa-se a voz « Marie - sad » (Mistral).',
  'voicePanel.search': 'Pesquisar…',
  'voicePanel.allLanguages': 'Todos os idiomas',
  'voicePanel.loading': 'Carregando vozes…',
  'voicePanel.loadError': 'Não foi possível carregar as vozes',
  'voicePanel.empty': 'Nenhuma voz disponível.',
  'voicePanel.emptyHint': 'Verifique a chave API Mistral em Administração → Integrações.',
  'voicePanel.noResults': 'Nenhuma voz corresponde à sua pesquisa.',
  'voicePanel.saved': 'Voz padrão salva',
  'voicePanel.saveError': 'Erro ao salvar',
  'voicePanel.default': 'Padrão',
  'voicePanel.female': 'Feminina',
  'voicePanel.male': 'Masculina',
  'voicePanel.playPreview': 'Testar a voz',
  'voicePanel.stopPreview': 'Parar',
  'voicePanel.noPreview': 'Pré-visualização não disponível',
  'voicePanel.previewLoading': 'A gerar…',
  'voicePanel.previewGenerateError': 'Não foi possível gerar a pré-visualização de áudio.',
  'voicePanel.previewPlayError': 'Falha na reprodução de áudio.',
  'voicePanel.filterNoVoicesForLang':
    'Nenhuma voz do catálogo corresponde a este idioma — experimente «Todos os idiomas».',
  'episodeEditor.voicePicker.blockVoice': 'Voz para este bloco',
  'episodeEditor.voicePicker.defaultVoice': 'Voz padrão',
  'episodeEditor.voicePicker.voices': 'vozes',
  'episodeEditor.voicePicker.searchPlaceholder': 'Buscar uma voz…',
  'episodeEditor.voicePicker.noVoiceFound': 'Nenhuma voz encontrada',
  'episodeEditor.voicePicker.female': 'Feminino',
  'episodeEditor.voicePicker.male': 'Masculino',
  'episodeEditor.voicePicker.stop': 'Parar',
  'episodeEditor.voicePicker.preview': 'Testar voz',
  'episodeEditor.voicePicker.stopPlayback': 'Parar reprodução',
  'episodeEditor.errors.notFound': 'Episódio não encontrado',
  'episodeEditor.errors.titleRequired': 'O título é obrigatório.',
  'episodeEditor.errors.saveFailed': 'Erro ao salvar.',
  'episodeEditor.errors.noVoicesAvailable': 'Nenhuma voz disponível. Insira a chave da API em Administração → Integrações, depois recarregue.',
  'episodeEditor.errors.selectVoiceFirst': 'Selecione uma voz na aba « Voz » antes de gerar a locução.',
  'episodeEditor.errors.invalidBlockVoice': 'Blocos com voz inválida. Abra o seletor de voz por bloco ou recarregue a lista.',
  'episodeEditor.errors.addTextFirst': 'Adicione texto antes de gerar o áudio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Geração de áudio impossível. {{message}} (duração = estimativa.)',
  'episodeEditor.errors.audioGenerationFailed': 'Erro na geração de áudio. Duração = estimativa.',
  'episodeEditor.success.published': 'Episódio publicado!',
  'episodeEditor.success.draftSaved': 'Rascunho salvo',
  'episodeEditor.success.audioGenerated': 'Áudio gerado com {{count}} bloco(s)',
  'episodeEditor.tabs.edit': 'Editar',
  'episodeEditor.tabs.chapters': 'Capítulos',
  'episodeEditor.tabs.voice': 'Voz',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Desfazer (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Refazer (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Gerando…',
  'episodeEditor.toolbar.generateAudio': 'Gerar áudio',
  'episodeEditor.toolbar.save': 'Salvar',
  'episodeEditor.toolbar.publish': 'Publicar',
  'episodeEditor.titlePlaceholder': 'Título do episódio…',
  'episodeEditor.status.published': 'Publicado',
  'episodeEditor.status.draft': 'Rascunho',
  'episodeEditor.warnings.mistralNotConfigured': 'A locução usa Mistral Voxtral TTS. Adicione a chave Mistral em Administração → Integrações, depois recarregue.',
  'episodeEditor.warnings.mistralNoVoices': 'Chave Mistral reconhecida mas a API não retornou vozes. Verifique os logs do backend.',
  'episodeEditor.warnings.voiceLoadFailed': 'Impossível carregar a lista de vozes. Verifique a configuração Mistral em Administração → Integrações.',
  'episodeEditor.warnings.voiceOffDisabled': 'Sem uma voz real, a geração de locução permanece desativada.',
  'episodeEditor.edit.voiceOff': 'Locução',
  'episodeEditor.edit.voiceDescription': 'Síntese via Mistral Voxtral TTS. Escolha a voz na aba « Voz » ou por bloco.',
  'episodeEditor.edit.coverImage': 'Imagem de capa',
  'episodeEditor.edit.coverAlt': 'Capa do episódio',
  'episodeEditor.edit.deleteCover': 'Excluir',
  'episodeEditor.edit.dropOrClickImage': 'Clique ou solte uma imagem',
  'episodeEditor.edit.selectedVoice': 'Voz selecionada',
  'episodeEditor.edit.selectVoice': 'Selecionar uma voz',
  'episodeEditor.edit.defaultVoiceByLang': 'Voz padrão (idioma do podcast)',
  'episodeEditor.edit.lastAudioGenerated': 'Último áudio gerado',
  'episodeEditor.edit.audioNotFound': 'Arquivo de áudio não encontrado — regenere a locução.',
  'episodeEditor.edit.generatingInProgress': 'Gerando…',
  'episodeEditor.edit.generateVoiceOff': 'Gerar locução',
  'episodeEditor.chapters.title': 'Capítulos',
  'episodeEditor.chapters.add': 'Adicionar',
  'episodeEditor.chapters.defaultChapterTitle': 'Introdução',
  'episodeEditor.chapters.newChapterTitle': 'Capítulo {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} bloco(s)',
  'episodeEditor.voiceTab.availableVoices': 'Vozes disponíveis',
  'episodeEditor.podcasts.linked': 'Podcasts vinculados',
  'episodeEditor.podcasts.assign': 'Atribuir',
  'episodeEditor.podcasts.noneAvailable': 'Nenhum podcast disponível',
  'episodeEditor.podcasts.close': 'Fechar',
  'episodeEditor.podcasts.noneLinked': 'Nenhum podcast vinculado',
  'episodeEditor.podcasts.assignHint': 'Atribua este episódio a um ou mais podcasts',
  'episodeEditor.podcasts.remove': 'Remover',
  'episodeEditor.editor.addTextBlock': 'Adicionar um bloco de texto',
  'episodeEditor.editor.scriptPlaceholder': 'Comece a escrever seu roteiro…',
  'episodeEditor.editor.addBlock': 'Adicionar um bloco',
  'episodeEditor.player.loop': 'Repetir',
  'episodeEditor.player.playbackSpeed': 'Velocidade de reprodução',
  'episodeEditor.player.collapse': 'Reduzir',
  'episodeEditor.player.expand': 'Expandir',
  'episodeEditor.player.waveformHidden': 'Forma de onda oculta',
  'podcast.episodes.listen': 'Ouvir',
  'podcast.episodes.noAudioShort': 'Sem áudio disponível',
  'podcast.episodes.edit': 'Editar',
  'podcast.episodes.delete': 'Excluir',
  'podcast.episodes.unmute': 'Ativar som',
  'podcast.episodes.mute': 'Silenciar',
  'podcast.episodes.totalEpisodes': '{{count}} episódio(s) no total',
  'eduflow.designer.toast.blockCopied': 'Bloco copiado',
  'eduflow.designer.toast.blockPasted': 'Bloco colado',
  'eduflow.designer.toast.blockDuplicated': 'Bloco duplicado',
  'eduflow.designer.toast.connectionUpdated': 'Conexão atualizada',
  'eduflow.designer.toast.flowExported': 'Curso exportado',
  'eduflow.designer.toast.flowImported': 'Curso importado',
  'eduflow.designer.toast.invalidFile': 'Arquivo inválido',
  'eduflow.designer.untitled': 'Sem título',
  'eduflow.designer.importedFlow': 'Curso importado',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'Utilizador',
  'eduflow.designer.collaborator.editor': 'Editor',
  'eduflow.designer.collaborator.viewer': 'Leitor',
  'eduflow.designer.resizePanel': 'Redimensionar painel',
  'eduflow.designer.published': 'Publicado',
  'eduflow.designer.draft': 'Rascunho',
  'eduflow.designer.quizOutput.success': 'Aprovado',
  'eduflow.designer.quizOutput.failure': 'Reprovado',
  'eduflow.designer.handle.default': 'Padrão',
  'eduflow.designer.genError': 'Erro durante a geração. Tente novamente.',
  'trainerTour.welcome.title': 'Bem-vindo ao seu espaço de formador',
  'trainerTour.welcome.description':
    'Esta visita breve apresenta o painel e o menu. Na primeira vez que abre cada página, uma dica explica o essencial. Pode fechar um passo com o X — a visita não volta a iniciar.',
  'trainerTour.dashboard.header.title': 'O seu painel',
  'trainerTour.dashboard.header.description':
    'Visão geral da sua atividade: números-chave e atalhos para criar conteúdo rapidamente.',
  'trainerTour.dashboard.actions.title': 'Criar conteúdo',
  'trainerTour.dashboard.actions.description':
    'Atalhos para um novo curso EduFlow, podcast ou percurso. Para iniciar um projeto sem passar pelo menu.',
  'trainerTour.dashboard.stats.title': 'Indicadores',
  'trainerTour.dashboard.stats.description':
    'Cursos, percursos publicados, aprendizes alcançados e podcasts: veja a dimensão do seu catálogo num relance.',
  'trainerTour.dashboard.activity.title': 'Atividade recente',
  'trainerTour.dashboard.activity.description':
    'Últimos conteúdos atualizados. Clique numa linha para reabrir o curso, podcast ou percurso correspondente.',
  'trainerTour.sidebar.title': 'Menu lateral',
  'trainerTour.sidebar.description':
    'Painel (início, aprendizes, análises, missões), criação (cursos, podcasts, percursos, webinários se ativos), biblioteca de media e ajuda. Abra cada secção — na primeira vez aparece uma dica contextual.',
  'trainerTour.routes.learners.title': 'Aprendizes',
  'trainerTour.routes.learners.description':
    'Pessoas inscritas nos seus conteúdos, progresso, exportações — o seu centro de acompanhamento pedagógico.',
  'trainerTour.routes.analytics.title': 'Análises',
  'trainerTour.routes.analytics.description':
    'Gráficos sobre envolvimento e conclusão. Personalize o layout para o que importa para si.',
  'trainerTour.routes.eduflow.title': 'Cursos (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Crie e gere cursos interativos, versões, publicação e partilha — o núcleo da conceção pedagógica.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Programas, episódios, visibilidade e partilha com grupos ou aprendizes da sua organização.',
  'trainerTour.routes.parcours.title': 'Percursos',
  'trainerTour.routes.parcours.description':
    'Sequências de cursos e recursos: estruture percursos longos e atribua-os aos seus públicos.',
  'trainerTour.routes.videos.title': 'Vídeos',
  'trainerTour.routes.videos.description':
    'Biblioteca de vídeos reutilizável nos cursos e documentos.',
  'trainerTour.routes.sounds.title': 'Áudio',
  'trainerTour.routes.sounds.description':
    'Ficheiros de áudio e etiquetas para podcasts e blocos sonoros nos cursos.',
  'trainerTour.routes.images.title': 'Imagens',
  'trainerTour.routes.images.description':
    'Visuais centralizados: importação, pastas e reutilização no conteúdo.',
  'trainerTour.routes.documents.title': 'Documentos',
  'trainerTour.routes.documents.description':
    'PDFs e anexos, com partilha direcionada a aprendizes se necessário.',
  'trainerTour.routes.help.title': 'Ajuda',
  'trainerTour.routes.help.description':
    'Artigos, tutoriais e guias sobre LyrraStudio. Volte aqui quando ficar bloqueado numa funcionalidade.',
  'trainerTour.routes.producerAssignments.title': 'Missões de produção',
  'trainerTour.routes.producerAssignments.description':
    'Quando os RH lhe atribuem conteúdos de catálogo para produzir, aparecem aqui com o progresso.',
  'trainerTour.routes.webinars.title': 'Webinários',
  'trainerTour.routes.webinars.description':
    'Agende sessões em direto, convites e ligações de difusão para os seus grupos.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Seguinte',
  'trainerTour.driver.prev': 'Anterior',
  'trainerTour.driver.done': 'Concluir',
  'trainerTour.driver.gotIt': 'Percebi',
  'player.lightMode': 'Modo claro',
  'player.darkMode': 'Modo escuro',
};

const lbTranslations: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Tableau de bord',
  'nav.projects': 'Projeten',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Audiobicher',
  'nav.audio_studio': 'Audio-Studio',
  'nav.store': 'Buttek',
  'nav.analytics': 'Analysen',
  'nav.users': 'Benotzer',
  'nav.settings': 'Astellungen',
  'nav.profile': 'Profil',
  'nav.notifications': 'Notifikatiounen',
  'nav.help': 'Hëllef',
  'nav.community': 'Gemeinschaft',
  'nav.library': 'Bibliothéik',
  'nav.certificates': 'Zertifikater',
  'nav.institution': 'Institutioun',
  'nav.groups': 'Gruppen',
  'nav.learners': 'Léierenden',
  'nav.myLearning': 'Meng Léieren',
  'learner.sidebar.tagline': 'Léierende',
  'nav.reports': 'Rapporten',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'Weiderbildungsplang',
  'nav.catalog': 'Katalog',
  'nav.competencies': 'Kompetenze',
  'nav.budget': 'Budget',
  'nav.campaigns': 'Kampagnen',
  'nav.home': 'Accueil',
  'nav.producerAssignments': 'Produktiouns-Missiounen',
  'nav.videos': 'Videoen',
  'nav.sounds': 'Kläng',
  'nav.images': 'Biller',
  'nav.documents': 'Dokumenter',
  'nav.podcasts': 'Podcasts',
  'nav.parcours': 'Parcoursen',
  'nav.eduflowList': 'Formatiounen',
  'eduflowList.status.draft': 'Brouillon',
  'eduflowList.status.published': 'Publizéiert',
  'eduflowList.status.archived': 'Archivéiert',
  'eduflowList.badge.enterpriseShell': 'Entrepris-Ufro',
  'eduflowList.badge.enterpriseLinked': 'Entreprise',
  'eduflowList.badge.toProduce': 'Ze produzéieren',
  'nav.section.pilotage': 'Steierung',
  'nav.section.creation': 'Kreatioun',
  'nav.section.medias': 'Medien',
  'nav.section.aide': 'Hëllef',
  'sidebar.brandTagline': 'Formatioun',
  'sidebar.logoInstitutionAlt': 'Logo vun der Organisatioun',
  'sidebar.expand': 'Menu vergréisseren',
  'sidebar.collapse': 'Menu verklengeren',
  'sidebar.expandAria': 'Säitenleescht vergréisseren',
  'sidebar.collapseAria': 'Säitenleescht verklengeren',
  'learner.sidebar.collapse': 'Verklengeren',
  'learner.sidebar.expandAria': 'Säitenleescht vergréisseren',
  'learner.sidebar.collapseAria': 'Säitenleescht verklengeren',
  'dashboard.title': 'Dashboard',
  'dashboard.welcome': 'Wëllkomm, {{name}}!',
  'dashboard.welcomeSub': 'Hei ass en Iwwerbléck vun Ärem Beräich.',
  'dashboard.projects': 'Projeten',
  'dashboard.subtitle': 'Gestioun vun Äre Formatiounen a Progressioun vun de Léierenden.',
  'dashboard.newProject': 'Nei Formatioun',
  'dashboard.newEduFlow': 'Neie Parcours',
  'dashboard.uploadMedia': 'Medie eroplueden',
  'dashboard.viewDocs': 'Dokumentatioun',
  'dashboard.revenue': 'Recetten',
  'dashboard.eduflows': 'Formatiounen',
  'dashboard.learners': 'Léierenden',
  'dashboard.recentActivity': 'Rezent Aktivitéit',
  'dashboard.recentProjects': 'Rezent Projeten',
  'dashboard.noRecentProjects': 'Keng rezent Projeten',
  'dashboard.activity': 'Aktivitéit',
  'dashboard.activityEmpty': 'Keng rezent Aktivitéit op Formatiounen oder Podcasts.',
  'dashboard.loadError': 'Dashboard-Donnéeë konnten net geluede ginn.',
  'dashboard.publishedFlows': 'Publizéiert Parcoursen',
  'dashboard.roleSpecific': 'Fir dës Roll gëtt et en eegene Dashboard.',
  'dashboard.kindPodcast': 'Podcast',
  'pages.analytics.title': 'Analysen',
  'pages.analytics.description': 'Dashboards a Statistiken',
  'pages.analytics.subtitle':
    'Filtert Donnéeën, organiséiert Graphiquen duerch Zéien a passt d’Gréisst vun de Widgets un Är Aarbechtsweis un.',
  'pages.analytics.filters.period': 'Zäitraum',
  'pages.analytics.filters.flow': 'Formatioun',
  'pages.analytics.filters.allFlows': 'All Formatiounen',
  'pages.analytics.period.7d': 'Lescht 7 Deeg',
  'pages.analytics.period.30d': 'Lescht 30 Deeg',
  'pages.analytics.period.90d': 'Lescht 90 Deeg',
  'pages.analytics.period.365d': 'Lescht 12 Méint',
  'pages.analytics.addChart': 'Graphique derbäisetzen',
  'pages.analytics.resetLayout': 'Layout zrécksetzen',
  'pages.analytics.dragHint': 'Zéien fir ze sortéieren',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Dëse Graphique ewechhuelen',
  'pages.analytics.kind.completion_line': 'Ofschloss iwwer d’Zäit',
  'pages.analytics.kind.learners_bar': 'Léierende pro Modul',
  'pages.analytics.kind.status_pie': 'Statusverdeelung',
  'pages.analytics.kind.engagement_area': 'Engagement-Zäit',
  'pages.analytics.series.completion': 'Ofschlussquot (%)',
  'pages.analytics.series.learners': 'Léierenden',
  'pages.analytics.series.hours': 'Stonnen',
  'pages.analytics.status.completed': 'Ofgeschloss',
  'pages.analytics.status.in_progress': 'Am Gaang',
  'pages.analytics.status.dropped': 'Ofgebrach',
  'pages.analytics.status.not_started': 'Nach net ugefaangen',
  'pages.analytics.loadError': 'Analysedate konnten net geluede ginn.',
  'pages.analytics.retry': 'Nach eng Kéier probéieren',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Mé',
  'pages.analytics.mock.wd1': 'Dë',
  'pages.analytics.mock.wd2': 'Më',
  'pages.analytics.mock.wd3': 'Do',
  'pages.analytics.mock.wd4': 'Fr',
  'pages.analytics.mock.wd5': 'Sa',
  'pages.analytics.mock.wd6': 'So',
  'pages.analytics.emptyCharts':
    'Setzt mindestens ee Graphique derbäi fir d’Donnéeën ze gesinn.',
  'pages.analytics.comingSoonDesc':
    'Kuckt Statistike vun Äre Formatiounen, Ofschlussquoten, Engagement vun de Léierenden an d’global Leeschtung.',
  'btn.cancel': 'Ofbriechen',
  'btn.delete': 'Läschen',
  'btn.edit': 'Änneren',
  'btn.create': 'Erstellen',
  'btn.add': 'Dobäisetzen',
  'btn.close': 'Zoumaachen',
  'btn.confirm': 'Confirméieren',
  'btn.back': 'Zréck',
  'btn.next': 'Weider',
  'btn.previous': 'Virdrun',
  'btn.submit': 'Ofschécken',
  'btn.search': 'Sichen',
  'btn.filter': 'Filteren',
  'btn.export': 'Exportéieren',
  'btn.import': 'Importéieren',
  'btn.download': 'Eroflueden',
  'btn.upload': 'Eroplueden',
  'btn.refresh': 'Aktualiséieren',
  'btn.retry': 'Nees probéieren',
  'btn.view': 'Ukucken',
  'btn.details': 'Detailer',
  'btn.publish': 'Publizéieren',
  'btn.unpublish': 'Depublizéieren',
  'btn.duplicate': 'Duplizéieren',
  'btn.archive': 'Archivéieren',
  'btn.restore': 'Restauréieren',
  'btn.send': 'Schécken',
  'btn.apply': 'Applizéieren',
  'btn.reset': 'Récksetzen',
  'btn.select_all': 'Alles auswielen',
  'btn.deselect_all': 'Alles ofwielen',
  'btn.load_more': 'Méi lueden',
  'btn.show_more': 'Méi weisen',
  'btn.show_less': 'Manner weisen',
  'btn.copy': 'Kopéieren',
  'btn.share': 'Deelen',
  'btn.preview': 'Virschau',
  'btn.continue': 'Weiderfueren',
  'btn.skip': 'Iwwersprangen',
  // Form labels
  'form.email': 'E-Mail-Adress',
  'form.password': 'Passwuert',
  'form.password_confirm': 'Passwuert bestätegen',
  'form.first_name': 'Virnumm',
  'form.last_name': 'Familljennumm',
  'form.name': 'Numm',
  'form.phone': 'Telefon',
  'form.address': 'Adress',
  'form.city': 'Stad',
  'form.zip': 'Postleitzuel',
  'form.country': 'Land',
  'form.title': 'Titel',
  'form.description': 'Beschreiwung',
  'form.category': 'Kategorie',
  'form.tags': 'Tags',
  'form.status': 'Status',
  'form.role': 'Rôle',
  'form.language': 'Sprooch',
  'form.date': 'Datum',
  'form.start_date': 'Ufanksdatum',
  'form.end_date': 'Enddatum',
  'form.duration': 'Dauer',
  'form.price': 'Präis',
  'form.avatar': 'Profilbild',
  'form.required': 'Obligatoresch',
  'form.optional': 'Fakultativ',
  'form.select_placeholder': 'Auswielen...',
  'form.search_placeholder': 'Sichen...',
  'form.no_results': 'Keng Resultater',
  'form.file_drop': 'Fichier hei erofdrécken oder klicken fir ze browsen',
  'form.max_size': 'Maximal Gréisst: {{size}}',
  'form.institution': 'Institutioun',
  'form.department': 'Departement',
  'form.group': 'Grupp',
  'form.level': 'Niveau',
  'form.notes': 'Noten',
  'form.url': 'URL',
  'form.content': 'Inhalt',
  'form.thumbnail': 'Virschaubild',
  // Validation errors
  'error.required': 'Dëst Feld ass obligatoresch',
  'error.email_invalid': 'Ongëlteg E-Mail-Adress',
  'error.password_min': 'D\'Passwuert muss mindestens {{min}} Zeechen hunn',
  'error.password_match': 'D\'Passwierder stëmmen net iwwereen',
  'error.min_length': 'Minimum {{min}} Zeechen',
  'error.max_length': 'Maximum {{max}} Zeechen',
  'error.min_value': 'Mindestwäert ass {{min}}',
  'error.max_value': 'Maximalwäert ass {{max}}',
  'error.invalid_url': 'Ongëlteg URL',
  'error.file_too_large': 'Fichier ass ze grouss',
  'error.file_type': 'Net ënnerstëtzte Fichiertyp',
  'error.generic': 'E Feeler ass opgetrueden',
  'error.network': 'Netzwierkverbindungsfeeler',
  'error.unauthorized': 'Dir musst ageloggt sinn',
  'error.forbidden': 'Zougang verweigert',
  'error.not_found': 'Ressource net fonnt',
  'error.server': 'Interner Serverfeeler',
  'error.conflict': 'Dës Ressource existéiert scho',
  'error.rate_limit': 'Ze vill Ufroen, w.e.g. waarden',
  // Auth
  'auth.login': 'Aloggen',
  'auth.logout': 'Ausloggen',
  'auth.register': 'Kont erstellen',
  'auth.forgot_password': 'Passwuert vergiesst?',
  'auth.reset_password': 'Passwuert récksetzen',
  'auth.otp_title': 'Aloggen mat Code',
  'auth.otp_sent': 'E Verifikatiounscode gouf u {{email}} geschéckt',
  'auth.otp_placeholder': 'Gitt den 6-Ziffer-Code an',
  'auth.google_login': 'Mat Google weiderfueren',
  'auth.remember_me': 'U mech erënneren',
  'auth.no_account': 'Nach kee Kont?',
  'auth.has_account': 'Schonn e Kont?',
  'auth.welcome_back': 'Wëllkomm zréck, {{name}}!',
  'auth.login_success': 'Aloggen erfollegräich',
  'auth.register_success': 'Kont erfollegräich erstallt',
  'auth.logout_success': 'Ausloggen erfollegräich',
  // Roles
  'role.teacher': 'Enseignant',
  'role.institution': 'Institutioun',
  'role.admin': 'Administrateur',
  'role.drh': 'DRH',
  'role.rh': 'HR-Responsabel',
  'role.manager': 'Manager',
  'role.collaborator': 'Mataarbechter',
  'role.learner': 'Léierenden',
  'dashboard.recent_projects': 'Rezent Projeten',
  'dashboard.recent_activity': 'Rezent Aktivitéit',
  'dashboard.quick_actions': 'Schnell Aktiounen',
  'dashboard.stats': 'Statistiken',
  'dashboard.total_projects': 'Total Projeten',
  'dashboard.total_learners': 'Total Léierenden',
  'dashboard.total_eduflows': 'Total formations',
  'dashboard.completion_rate': 'Ofschloussquot',
  'dashboard.active_users': 'Aktiv Benotzer',
  'dashboard.new_registrations': 'Nei Umeldungen',
  'dashboard.xp_earned': 'XP verdéngt',
  'dashboard.streak': 'Aktuell Serie',
  'dashboard.level': 'Niveau {{level}}',
  'dashboard.badges_earned': 'Badges kritt',
  'dashboard.next_level': 'Nächst Niveau',
  'dashboard.no_projects': 'Nach keng Projeten',
  'dashboard.create_first': 'Erstellt Äre éischte Projet',
  'dashboard.pending_reviews': 'Ausstehend Evaluatiounen',
  'dashboard.upcoming_deadlines': 'Kommend Fristen',
  // Podcast — Episoden
  'podcast.episodes.pageTitle': 'Episoden',
  'podcast.episodes.count': '{{count}} Episod(en)',
  'podcast.episodes.newEpisode': 'Nei Episod',
  'podcast.episodes.searchPlaceholder': 'No Titel sichen…',
  'podcast.episodes.filterAllStatuses': 'All Statuser',
  'podcast.episodes.filterDraft': 'Brouillon',
  'podcast.episodes.filterPublished': 'Publizéiert',
  'podcast.episodes.emptyTitle': 'Keng Episod fonnt',
  'podcast.episodes.emptyHint': 'Leet Är éischt Episod un',
  'podcast.episodes.emptyCreateCta': 'Episod uleeën',
  'podcast.episodes.backPodcasts': 'Podcasts',
  'podcast.episodes.paginationTotal': '{{total}} Episod(en) am Ganzen',
  'podcast.episodes.pageOf': 'Säit {{page}} / {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Episod läschen?',
  'podcast.episodes.deleteHint': '« {{title}} » gëtt definitiv geläscht.',
  'podcast.episodes.deleteCancel': 'Ofbriechen',
  'podcast.episodes.deleteConfirm': 'Läschen',
  'podcast.episodes.statusPublished': 'Publizéiert',
  'podcast.episodes.statusDraft': 'Brouillon',
  'podcast.episodes.noDescription': 'Keng Beschreiwung',
  'podcast.episodes.noAudio': 'Keen Audio',
  'podcast.episodes.retry': 'Nach eng Kéier',
  'podcast.createEpisode.panelTitle': 'Nei Episod',
  'podcast.createEpisode.panelSubtitle':
    'Gitt den Titel an, fakultativ eng Beschreiwung an e Cover. No der Validatioun gëtt d’Episod ugeleet an den Editor geet op.',
  'podcast.createEpisode.titleLabel': 'Titel',
  'podcast.createEpisode.descriptionLabel': 'Beschreiwung',
  'podcast.createEpisode.coverLabel': 'Cover-Bild (facultatif)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP oder GIF — max. 5 Mo.',
  'podcast.createEpisode.coverChoose': 'Bild wielen',
  'podcast.createEpisode.coverRemove': 'Bild ewechhuelen',
  'podcast.createEpisode.titlePlaceholder': 'z. B. Aféierung Sécherheetsmodul',
  'podcast.createEpisode.descriptionPlaceholder': 'Resumé, Ziler… (facultatif)',
  'podcast.createEpisode.validate': 'Uleeën an Editor opmaachen',
  'podcast.createEpisode.titleRequired': 'Den Titel ass obligatoresch.',
  'podcast.createEpisode.error': 'Episod konnt net ugeleet ginn.',
  'podcast.createEpisode.uploadCoverError': 'Episod ugeleet, mais d’Bild konnt net eropgeluede ginn.',
  'podcast.editor.defaultNewTitle': 'Nei Episod',
  // EduFlow
  'eduflow.title': 'EduFlow Editor',
  'eduflow.new_flow': 'Nei Formatioun',
  'eduflow.list.title': 'Formatiounen',
  'eduflow.list.subtitle': 'Entwéiert Är interaktiv Léierparcoursen',
  'eduflow.list.searchPlaceholder': 'Formatioun sichen…',
  'eduflow.list.filterAll': 'All',
  'eduflow.list.filterDraft': 'Brouillonen',
  'eduflow.list.filterPublished': 'Publizéiert',
  'eduflow.list.filterArchived': 'Archivéiert',
  'eduflow.list.viewModeGrid': 'Gitter-Visioun',
  'eduflow.list.viewModeList': 'Liëscht-Visioun',
  'eduflow.list.viewByCategory': 'Kategorie-Visioun',
  'eduflow.list.uncategorized': 'Ouni Kategorie',
  'eduflow.list.manageCategories': 'Kategorie verwalten',
  'eduflow.list.categoriesPanelTitle': 'Formatiouns-Kategorien',
  'eduflow.list.noCategoryAvailable': 'Keng Kategorie verfügbar.',
  'eduflow.list.categoriesSaveError': 'Kategorien konnten net gespäichert ginn.',
  'eduflow.list.emptyFilteredTitle': 'Kee Resultat',
  'eduflow.list.emptyFilteredHint': 'Ännert Är Sich oder Filter.',
  'eduflow.list.emptyFirstTitle': 'Leet Är éischt Formatioun un',
  'eduflow.list.emptyFirstHint': 'Mat dem visuellen Designer interaktiv Léieren opbauen.',
  'eduflow.list.emptyCreateCta': 'Meng éischt Formatioun uleeën',
  'eduflow.list.deleteToTrashTitle': 'Formatioun an d’Corbeille leeën?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » gëtt vun Ärer Lëscht ewechgeholl (logesch Läschung). Är Institutioun kann se aus der Institutiouns-Corbeille restauréieren.',
  'eduflow.list.deleteToTrashConfirm': 'An d’Corbeille',
  'eduflow.list.import': 'Importéieren',
  'eduflow.list.importHint': '.lyrra-Fichier (Medien dran) oder Lyrra-V1-JSON-Export.',
  'eduflow.list.importSuccess': 'Formatioun importéiert. De Designer geet op.',
  'eduflow.list.importError':
    'Import onméiglech — iwwerpréift d’Fichier (.lyrra oder valabele JSON-Export, mindestens ee Block).',
  'eduflow.list.categoryBack': 'All Kategorien',
  'eduflow.list.categoryBrowseHint': 'Wielt eng Kategorie fir hir Formatiounen ze weisen.',
  'eduflow.list.categoryFormationCount': '{{count}} Formatioun(en)',
  'eduflow.list.categoryEmptyFiltered':
    'Keng Formatioun an dëser Vue (aktive Filter oder Sich).',
  'eduflow.list.uncategorizedCardHint': 'Formatiounen ouni Kategorie-Etikett.',
  'eduflow.list.blocksCount': '{{count}} Bléck',
  'eduflow.list.minutesShort': '{{count}} Min.',
  'eduflow.list.tooltip.openMissions': 'Produktiouns-Missiounen opmaachen',
  'eduflow.list.tooltip.edit': 'Änneren',
  'eduflow.list.shellHint':
    'Fiche vun Ärer Organisatioun — kreéiert den EduFlow iwwer « Produktiouns-Missiounen ».',
  'eduflow.list.coverLabel': 'Cover-Bild',
  'eduflow.list.coverRemove': 'Cover ewechhuelen',
  'eduflow.list.coverUpload': 'Bild auswielen',
  'eduflow.list.flowProperties': 'Titel, Beschreiwung a Cover',
  'eduflow.list.propertiesPanelTitle': 'Eegeschafte vun der Formatioun',
  'eduflow.list.duplicatePanelTitle': 'Formatioun duplizéieren',
  'eduflow.list.duplicatePanelSubtitle': 'Original-Formatioun: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'Kopie',
  'eduflow.list.duplicateSuccess': 'Formatioun duplizéiert.',
  'eduflow.list.duplicateCoverHint':
    'Cover-Bild an didakteschen Inhalt vun der Original-Formatioun ginn iwwerdro.',
  'eduflow.list.propertiesSaveError': 'D’Eegeschaften konnten net gespäichert ginn.',
  'eduflow.list.propertiesSaved': 'Eegeschaften gespäichert.',
  'eduflow.list.propertiesUploadError': 'D’Bild konnt net eropgeluede ginn.',
  'eduflow.list.noContent': 'Keen Inhalt',
  'eduflow.collaborativeBadge': 'Kollaborativ',
  'eduflow.duplicateFlowError': 'D’Formatioun konnt net duplizéiert ginn.',
  'eduflow.createFormation.panelTitle': 'Nei Formatioun',
  'eduflow.createFormation.panelSubtitle':
    'Titel a Beschreiwung aginn. No der Validatioun gëtt d’Formatioun ugeluecht an de Designer geet op.',
  'eduflow.createFormation.titleLabel': 'Titel',
  'eduflow.createFormation.descriptionLabel': 'Beschreiwung',
  'eduflow.createFormation.titlePlaceholder': 'z. B. Aféierung Sécherheetsmodul',
  'eduflow.createFormation.descriptionPlaceholder': 'Ziler, Zilgrupp, asw. (facultatif)',
  'eduflow.createFormation.validate': 'Uleeën an Designer opmaachen',
  'eduflow.createFormation.titleRequired': 'Den Titel ass obligatoresch.',
  'eduflow.createFormation.error': 'Formatioun konnt net ugeleet ginn.',
  'eduflow.shareFormation.title': 'Formatioun deelen',
  'eduflow.shareFormation.publicLinkTitle': 'Ëffentleche Link (ouni Umeldung)',
  'eduflow.shareFormation.publicLinkCopy': 'Link kopéieren',
  'eduflow.shareFormation.publicLinkCopied': 'Kopéiert',
  'eduflow.shareFormation.publishRequired':
    'Publizéiert d’Formatioun fir e Link ouni Umeldung ze kréien.',
  'eduflow.shareFormation.embedTitle': 'Agebaute Knäppche a Modal-Lieser',
  'eduflow.shareFormation.embedHint':
    'Pecht dëse Blok op Är Säit. De Script nëmmen eemol pro Säit; fir aner Formatiounen duplizéiert just d’Knäppche-Linn an ännert d’URL (data-lyrra-eduflow-url). D’URL vun der Lieser huet sidebar=0 (Iwwersicht an der Modal zougeklappt).',
  'eduflow.shareFormation.embedCopy': 'HTML kopéieren',
  'eduflow.shareFormation.embedCopied': 'Kopéiert',
  'eduflow.shareFormation.embedCloseAria': 'Zoumaachen',
  'eduflow.shareFormation.embedButtonLabel': '{{title}} opmaachen',
  'eduflow.shareFormation.embedToggle': 'Websäit-Embedding',
  'eduflow.shareFormation.removeAccessSuccess': 'Zougang fir de Participant ewechgeholl.',
  'eduflow.shareFormation.removeAccessError': 'Zougang konnt net ewechgeholl ginn.',
  'eduflow.shareFormation.assignSuccess': 'Formatioun gedeelt an Invitatiouns-E-Mail geschéckt.',
  'eduflow.shareFormation.assignError': 'Dëse Participant konnt net zougewise ginn.',
  'eduflow.shareFormation.createAssignSuccess': 'Kont ugeluecht/verknëppt, Formatioun zougewisen an Invitatioun verschéckt.',
  'eduflow.shareFormation.createLearnerError': 'Participant konnt net ugeluecht ginn.',
  'eduflow.shareFormation.selectExistingLearner': 'Bestoende Participant auswielen',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Participant sichen (Numm, Virnumm, E-Mail)',
  'eduflow.shareFormation.loadingLearners': 'Participanten lueden...',
  'eduflow.shareFormation.noLearnerAvailable': 'Keng Participante verfügbar.',
  'eduflow.shareFormation.hideQuickCreate': 'Schnell-Erstellung verstoppen',
  'eduflow.shareFormation.createNewLearner': 'Neie Participant erstellen',
  'eduflow.shareFormation.firstNameRequired': 'Virnumm *',
  'eduflow.shareFormation.lastNameRequired': 'Numm *',
  'eduflow.shareFormation.emailRequired': 'E-Mail *',
  'eduflow.shareFormation.createShareInvite': 'Erstellen, deelen an Invitatioun schécken',
  'eduflow.shareFormation.assignedLearnersCount_one': '{{count}} Participant zougewisen',
  'eduflow.shareFormation.assignedLearnersCount_other': '{{count}} Participanten zougewisen',
  'eduflow.shareFormation.noAssignedLearner': 'Keen Participant fir dës Formatioun zougewisen.',
  'eduflow.shareFormation.removeAccessTitle': 'Zougang ewechhuelen',
  'eduflow.participant.flowTitle': 'Formatiounsparcours',
  'eduflow.participant.stepWelcome': 'Wëllkomm',
  'eduflow.participant.stepModule1': 'Modul 1',
  'eduflow.participant.stepEvaluation': 'Evaluatioun',
  'eduflow.participant.stepContentPlaceholder': 'Interaktiven Etappe-Contenu fir de Participant.',
  'eduflow.edit_flow': 'Parcours änneren',
  'eduflow.flow_name': 'Numm vum Parcours',
  'eduflow.block_palette': 'Block-Palette',
  'eduflow.canvas': 'Canvas',
  'eduflow.properties': 'Eegeschaften',
  'eduflow.publish': 'Publizéieren',
  'eduflow.publish_confirm': 'Sidd Dir sécher dass Dir dëse Parcours publizéiere wëllt?',
  'eduflow.save_draft': 'Entworf späicheren',
  'eduflow.preview': 'Virschau',
  'eduflow.versions': 'Versiounen',
  'eduflow.add_block': 'Block dobäisetzen',
  'eduflow.remove_block': 'Block läschen',
  'eduflow.connect_blocks': 'Blöcke verbannen',
  'eduflow.undo': 'Réckgängeg',
  'eduflow.redo': 'Erëmherstellen',
  'eduflow.zoom_in': 'Vergréisseren',
  'eduflow.zoom_out': 'Verklengeren',
  'eduflow.fit_view': 'Ukucken upassen',
  'eduflow.auto_layout': 'Automatesch Layout',
  'eduflow.unsaved_changes': 'Net gespäichert Ännerungen',
  'eduflow.blocks.quiz_mcq': 'MCQ',
  'eduflow.blocks.quiz_open': 'Oppent Quiz',
  'eduflow.blocks.video': 'Video',
  'eduflow.blocks.audio': 'Audio',
  'eduflow.blocks.text': 'Text',
  'eduflow.blocks.evaluation': 'Evaluatioun',
  'eduflow.blocks.form': 'Formular',
  'eduflow.blocks.mindmap': 'Mindmap',
  'eduflow.blocks.poll': 'Ëmfro',
  'eduflow.blocks.flashcard': 'Flashcard',
  'eduflow.blocks.timeline': 'Chronologie',
  'eduflow.blocks.pdf': 'PDF-Dokument',
  'eduflow.blocks.image': 'Bild',
  'eduflow.blocks.code': 'Code',
  'eduflow.blocks.whiteboard': 'Whiteboard',
  'eduflow.blocks.ai_chat': 'KI-Chat',
  'eduflow.blocks.certificate': 'Zertifikat',
  'eduflow.blocks.attendance': 'Präsenz',
  'eduflow.blocks.sortable': 'Ranking',
  'eduflow.blocks.pair_matching': 'Pair matching',
  'eduflow.blocks.drawing': 'Zeechnung',
  'eduflow.blocks.embed': 'Agebetteten Inhalt',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Konditioun',
  'eduflow.blocks.timer': 'Timer',
  'eduflow.blocks.randomizer': 'Zoufallsgenerator',
  'eduflow.blocks.grouping': 'Gruppéierung',
  'eduflow.blocks.debate': 'Debatt',
  'eduflow.blocks.roleplay': 'Rollespill',
  'eduflow.blocks.simulation': 'Simulatioun',
  'eduflow.blocks.scenario': 'Scénario',
  'eduflow.blocks.checklist': 'Checkliste',
  'eduflow.blocks.kanban': 'Kanban',
  'eduflow.blocks.peer_review': 'Peer Review',
  // Common
  'common.loading': 'Lueden...',
  'common.saving': 'Späicheren...',
  'common.saved': 'Gespäichert',
  'common.error': 'Feeler',
  'common.success': 'Erfolleg',
  'common.warning': 'Achtung',
  'common.info': 'Informatioun',
  'common.yes': 'Jo',
  'common.no': 'Nee',
  'common.or': 'oder',
  'common.and': 'an',
  'common.all': 'All',
  'common.none': 'Keen',
  'common.total': 'Total',
  'common.actions': 'Aktiounen',
  'common.created_at': 'Erstellt den',
  'common.updated_at': 'Geännert den',
  'common.sort_by': 'Sortéieren no',
  'common.per_page': 'Pro Säit',
  'common.page': 'Säit',
  'common.of': 'vun',
  'common.results': '{{count}} Resultat(er)',
  'common.no_data': 'Keng Daten',
  'common.coming_soon': 'Kënnt geschwënn',
  'common.beta': 'Beta',
  'common.new': 'Nei',
  'common.delete_confirm': 'Dës Aktioun ass irreversiibel. Weiderfueren?',
  'common.copied': 'Kopéiert!',
  'common.drag_drop': 'Drag-and-drop',
  'common.online': 'Online',
  'common.offline': 'Offline',
  'common.version': 'Versioun',
  // Player
  'player.next': 'Weider',
  'player.image.clickForAnnotations': "Klickt op d'Bild fir d'Annotatiounen ze gesinn",
  'player.previous': 'Zréck',
  'player.complete': 'Ofschléissen',
  'player.exit': 'Erausgoen',
  'player.exitConfirmTitle': 'D\'Formatioun verloossen?',
  'player.exitConfirmMessage': 'Äre Fortschrëtt gëtt gespäichert. Sidd Dir sécher dass Dir erausgoe wëllt?',
  'player.focusLostTitle': 'Formatioun pausséiert',
  'player.focusLostMessage': 'Dir hutt d\'Fenster vun der Formatioun verlooss. D\'Zäit ass pausséiert.',
  'player.focusLostResume': 'Klickt fir weiderzefueren',
  'player.focusLostRemaining': 'Verbleibend Austrëtter virum Blockéieren: {{remaining}}',
  'player.focusBlockedTitle': 'Formatioun blockéiert',
  'player.focusBlockedMessage':
    'Dir hutt déi erlaabten Zuel vun Austrëtter ({{max}}) iwwerschratt. Dir kënnt dës Formatioun net méi weiderféieren. Kontaktéiert Äre Formateur oder Referent.',
  'player.cancel': 'Ofbriechen',
  'player.summary': 'Zesummefaassung',
  'player.contents': 'Inhalt',
  'player.progress': 'Fortschrëtt',
  'player.block': 'Block',
  'player.parallelBranches': 'Parallel Zweige',
  'player.parallelBranchesTitle': 'Wat kënnt duerno?',
  'player.parallelBranchesHint': 'Déi aner Optiounen kommen duerno.',
  'player.parallelBranchShowFull': 'Méi weisen',
  'player.parallelBranchShowLess': 'Manner weisen',
  'player.parallelBranchChoose': 'Opmachen',
  'player.parallelBranchDismiss': 'Zoumaachen',
  'player.parallelBranchDismissHint':
    'Zréck op de Block. Mat « Weider » e Branche wielen.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Notizen maachen...',
  'player.noBlocks': 'Keng Blöcke fir ze weisen.',
  'player.contentTranslatingTitle': 'Contenu gëtt un Är Sprooch ugepasst…',
  'player.contentTranslatingHint':
    'Iwwersetzung leeft. Beim éischte Luede kann et e bëssi méi daueren.',
  'player.textBlockTipsHeading': 'Conseillen',
  'player.textBlockReadDone': 'Liesen ofgeschloss',
  'player.defaultFlowTitle': 'Formatioun',
  'player.docTitle.error': 'Formatioun net disponibel',
  'player.docTitle.completed': 'Formatioun ofgeschloss',
  'player.docTitle.loading': 'Formatioun gëtt gelueden',
  'player.docTitle.fallback': 'Formatioun',
  'player.initError.title': "D'Formatioun ass fir d'Eemol net disponibel",
  'player.initError.loadFailed': "D'Formatioun konnt elo net geluede ginn.",
  'player.initError.loadFailedWithDetail':
    "D'Formatioun konnt elo net geluede ginn.{{detail}} Probéiert et a kuerzer Zäit nach emol.",
  'player.initError.hintsIntro': 'Dir kënnt:',
  'player.initError.hintRefresh': "d'Säit aktualiséieren;",
  'player.initError.hintConnection': 'Är Internetverbindung iwwerpréiwen;',
  'player.initError.hintRetryLater': 'a puer Minutten nach emol probéieren.',
  'api.error.network': 'De Server ass net erreechbar. Iwwerpréift Är Verbindung.',
  'api.error.unexpected': 'En onerwaarte Feeler ass opgetrueden.',
  'api.error.badRequest': 'Ongülteg Ufro.',
  'api.error.forbidden': 'Zougang refuséiert.',
  'api.error.notFound': 'Ressource net fonnt.',
  'api.error.conflict': 'Konflikt — dës Ressource gëtt et schonn.',
  'api.error.unprocessable': 'Déi geschéckt Donnéeë sinn net valabel.',
  'api.error.tooManyRequests': 'Ze vill Ufroen. Probéiert et an e puer Sekonnen nach emol.',
  'api.error.server': 'Interne Serverfeeler.',
  'api.error.badGateway': 'Service fir d’Eemol net disponibel.',
  'api.error.maintenance': 'Service an der Maintenance.',
  'api.error.generic': 'Feeler {{status}}',
  'player.textBlockReadMinutes': '{{count}} Min. Lieszäit',
  'player.textBlockWordCount_one': '{{count}} Wuert',
  'player.textBlockWordCount_other': '{{count}} Wierder',
  'player.textBlockNoContent': 'Kee Contenu disponibel.',
  'player.blockUnknown': 'Onbekannte Block',
  'player.objectivesPanelTitle': 'Pedagogesch Ziler',
  'player.objectivesOpenLabel': 'Pedagogesch Ziler weisen',
  'player.objectivesCloseLabel': "Panell mat den Ziler zoumaachen",
  'player.objectivesAchievementRate': 'Erreechungsgrad',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} Ziler erreecht',
  'player.objectivesNoneDefined': 'Keng Ziler fir dës Formatioun definéiert.',
  'player.objectiveCategory.savoir': 'Wëssen',
  'player.objectiveCategory.savoirFaire': 'Kënnen',
  'player.objectiveCategory.savoirEtre': 'Sinn',
  'player.objectiveLevel.connaissance': 'Kënntnes',
  'player.objectiveLevel.comprehension': 'Verständnis',
  'player.objectiveLevel.application': 'Uwendung',
  'player.objectiveLevel.analyse': 'Analys',
  'player.objectiveLevel.synthese': 'Synthees',
  'player.objectiveLevel.evaluation': 'Bewertung',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Obligatoresch',
  'player.objectiveLinkedBlocks': 'Verbonne Blöck',
  'player.objectiveCriterionScore': 'Score ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Block ofgeschloss',
  'player.objectiveNoLinkedBlocks':
    'Kee verbonnene Block — de globale Fortschrëtt mooss dëst Zil net.',
  'player.completion.congratulations': '🎉 Felicitatiounen!',
  'player.completion.finished': '📋 Parcours ofgeschloss',
  'player.completion.youCompleted': 'Dir hutt ofgeschloss',
  'player.completion.passed': 'Bestanen ✓',
  'player.completion.failed': 'Net valideiert ✗',
  'player.completion.requiredScore': 'Erfuerderleche Score: {{score}}%',
  'player.completion.totalTime': 'Gesamtzäit',
  'player.completion.blocksCompleted': 'Ofgeschloss Blöcke',
  'player.completion.quizPassed': 'Bestanen Quiz',
  'player.completion.successRate': 'Erfollegsquot',
  'player.completion.blockCompleted': 'Ofgeschloss ✓',
  'player.completion.blockFailed': 'Échoué ✗',
  'player.completion.blockSkipped': 'Iwwersprangen',
  'player.completion.detail': '📊 Detailer pro Block',
  'player.completion.type': 'Typ',
  'player.completion.timeSpent': 'Verbrachte Zäit',
  'player.completion.downloadCertificate': '📄 Zertifikat eroflueden',
  'player.completion.certificateSoon': 'D\'Zertifikat gëtt geschwënn verfügbar.',
  'player.completion.restart': '🔄 Nees ufänken',
  'player.completion.viewDetails': '📊 Detailer weisen',
  'player.completion.backToDashboard': '← Zréck zum Dashboard',
  'player.completion.embedDismissHint':
    'Dir hutt d’Modul ofgeschloss. Zoumaachen mat der X uewen oder de Knäppche hei ënnen.',
  'player.completion.embedDismissButton': 'Fënster zoumaachen',
  'player.previewBanner': 'Virschau-Modus — dës Vue weist déi richteg Donnéeë vun Ärer Formatioun',
  'player.closePreview': 'Virschau zoumaachen',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Panel weisen',
  'player.toggleSidebarHide': 'Panel verstoppen',
  'player.sidebarExpand': 'Panel opmaachen',
  'player.sidebarCollapse': 'Panel zouklappen',
  'player.keyboardShortcuts': 'Tastaturkierzelen',
  'player.keyboardShortcutPrev': 'Viregen Block',
  'player.keyboardShortcutNext': 'Weider Block',
  'player.keyboardShortcutsHint':
    'Aktivéiert « Navigatioun mat der Tastatur » am Accessibilitéitszentrum (Knäppche riets ënnen). Aus an Textfelder.',
  'player.a11yCenter': 'Accessibilitéitszentrum',
  'player.reviewSummary': 'Resumé kucken',
  'player.viewResults': 'Resultater kucken',
  'player.submitFirst': 'Éischt ofschécken',
  'player.phraseRevealCompleteFirst': 'Kuckt fir d’éischt all Sätz un',
  'player.phraseReveal.emptyBlock': 'Fir dëse Block sinn keng Sätz configuréiert.',
  'player.phraseReveal.revealNextAria':
    'Klickt oder dréckt d’Espacetaast fir de nächste Satz ze weisen',
  'player.phraseReveal.lastPhraseHint':
    'Leschte Satz — benotzt « Weider » fir mam Parcours virunzefueren.',
  'player.phraseReveal.morePhrasesHint':
    'Klick, Espacetaast oder Feil riets fir de nächste Satz',
  'player.phraseReveal.previousPhrases': 'Vireg Sätz ({{count}})',
  'player.certificateUnavailable': 'De Certificat wäert geschwënn verfügbar sinn.',
  'player.browser.embeddedContentTitle': 'Agebett Inhalt',
  'player.browser.noValidUrl': 'Keng valabel URL',
  'player.browser.enterHttpsHint':
    'Gitt eng Adress an, déi mat https:// (oder http://) ufänkt, an den Astellunge vum Block.',
  'player.browser.loadingContent': 'Inhalt gëtt gelueden…',
  'player.browser.defaultPageTitle': 'Agebett Säit',
  'player.browser.continue': 'Weider',
  'player.browser.stepSaved': 'Schrëtt gespäichert',
  'player.browser.fullscreen': 'Vollbild',
  'player.browser.exitFullscreen': 'Vollbild verloossen',
  'player.podcast.defaultShowTitle': 'Podcast',
  'player.podcast.defaultEpisode': 'Episod',
  'player.podcast.loadError': 'De Podcast konnt net geluede ginn.',
  'player.podcast.loadingEpisodes': 'Episoden ginn gelueden…',
  'player.podcast.playAll': 'All ofspillen',
  'player.podcast.nextEpisode': 'Nächst Episod',
  'player.podcast.episodesHeading': 'Episoden',
  'player.podcast.noEpisodes': 'Keng Episoden.',
  'player.podcast.noAudio': 'Keen Audio',
  'player.podcast.chainPlayback': 'Ofspillung a Ketten · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Wielt eng Episod aus der Lëscht oder start «All ofspillen».',
  'player.podcast.audioUnavailable': 'Kee Audio fir dës Episod disponibel.',
  'player.podcast.episodeCount_one': '{{count}} Episod',
  'player.podcast.episodeCount_other': '{{count}} Episoden',
  'player.renderError': 'Feeler beim Renderen vum Block: {{blockType}}',
  'player.blocks.defaultWelcome': 'Wëllkomm bei dëser Formatioun',
  'player.blocks.defaultEndTitle': 'Gratulatioun!',
  'player.quiz.selectCorrectAnswer': 'Wielt déi richteg Äntwert:',
  'player.quiz.correctAnswer': '✓ Richteg Äntwert!',
  'player.quiz.incorrectAnswer': '✗ Falsch Äntwert.',
  'player.blockTimer.aria': 'Zäit fir dëse Block',
  'player.blockTimer.modeShortCountup': 'Zäitlech',
  'player.blockTimer.modeShortCountdown': 'Ofzielen',
  'player.form.successMessage': 'Merci fir Är Äntwert!',
  'player.form.noFieldsConfigured': 'Dëse Formulaire huet nach keng konfiguréiert Felder.',
  'player.evaluation.commentsLabel': 'Kommentaren (optional)',
  'player.evaluation.commentsPlaceholder': 'Deelt Är Meenung...',
  'player.evaluation.submitted': 'Bewäertung geschéckt',
  'player.evaluation.submit': 'Bewäertung ofschécken',
  'player.time.lessThanMinute': 'Manner wéi eng Minutt',
  'player.time.oneMinute': '1 Minutt',
  'player.time.minutes': '{{count}} Minutten',
  'player.time.shortSeconds': '{{count}} Sek.',
  'player.time.shortMinutes': '{{count}} Min.',
  'player.time.shortMinutesSeconds': '{{minutes}} Min. {{seconds}} Sek.',
  'player.completion.scoreLabel': 'Score',
  'accessibility.openCenter': 'Zenter fir Accessibilitéit opmaachen',
  'accessibility.center': 'Accessibilitéit',
  'accessibility.modalTitle': 'Zenter fir ugepasst Liesen',
  'accessibility.modalDescription': "Passt d'Interface fir Schwächt vum Gesinn, DYS-Schwierigkeeten a méi bequemt Liesen un.",
  'accessibility.closeCenter': 'Zenter fir Accessibilitéit zoumaachen',
  'accessibility.accessPad.connected': 'Lyrra Access Pad verbonnen',
  'accessibility.accessPad.playGuide': 'LyrraPad-Guide lauschteren',
  'accessibility.accessPad.pauseGuide': 'LyrraPad-Guide pausen',
  'accessibility.presets': 'Virkonfiguréiert Profiller',
  'accessibility.preset.standard.label': 'Standard',
  'accessibility.preset.standard.desc': 'Standard-Duerstellung vun der Applikatioun.',
  'accessibility.preset.lowVision.label': 'Schwächt vum Gesinn',
  'accessibility.preset.lowVision.desc': 'Méi staarke Kontrast, méi groussen Text a méi kloer visuell Hiweiser.',
  'accessibility.preset.dys.label': 'DYS',
  'accessibility.preset.dys.desc': 'Liichter Liesen mat méi Ofstand an enger méi liesbarer Schrëft.',
  'accessibility.typography': 'Typografie-Astellungen',
  'accessibility.fontScale': 'Textgréisst',
  'accessibility.lineHeight': 'Zeilenofstand',
  'accessibility.letterSpacing': 'Buschtawenofstand',
  'accessibility.wordSpacing': 'Wuertofstand',
  'accessibility.visualAids': 'Visuell Hëllefen',
  'accessibility.highContrast': 'Héije Kontrast',
  'accessibility.highContrastDesc': 'Verstäerkt Lieskontrast, Rummen a visuell Hiweiser.',
  'accessibility.underlineLinks': 'Ënnerstrach Linken',
  'accessibility.underlineLinksDesc': 'Mécht Linke a interaktiv Beräicher méi gutt ze gesinn.',
  'accessibility.dyslexiaFont': 'Liesfrëndlech Schrëft',
  'accessibility.dyslexiaFontDesc': 'Benotzt eng méi bequem Schrëft fir Leit mat DYS a midd Lieser.',
  'accessibility.reducedMotion': 'Reduzéiert Bewegung',
  'accessibility.reducedMotionDesc': 'Reduzéiert Animatiounen a Transitounen fir visuell Onbehuelechkeeten ze limitéieren.',
  'accessibility.mouseMagnifier': 'Lieslus',
  'accessibility.mouseMagnifierDesc':
    'Weist eng ronn Lupe déi der Ma follegt an den Inhalt ënnendrënner vergréissert (nëmme liesen).',
  'accessibility.alwaysOn': 'Permanent aktiv Verbesserungen',
  'accessibility.alwaysOnFocus': 'Siichtbareren Tastatur-Fokus',
  'accessibility.alwaysOnLinks': 'Linke a interaktiv Elementer méi einfach ze erkennen',
  'accessibility.alwaysOnSaved': 'Astellungen ginn automatesch op dësem Apparat gespäichert',
  'accessibility.navigation': 'Navigatioun',
  'accessibility.keyboardNavigation': 'Navigatioun mat der Tastatur',
  'accessibility.keyboardNavigationDesc':
    'Feil lénks/op fir de viregen Block, riets/ënne fir den nächsten (wéi d\'Knäppercher ënnen).',
  // ── Injected from locales/nl.json ──
  'producerAssignments.createInDesigner': 'Opleiding aanmaken / openen',
  'producerAssignments.draft': 'Concept',
  'producerAssignments.enterprise': 'Bedrijf',
  'producerAssignments.enterprisePaths': 'Bedrijfspaden',
  'producerAssignments.episodes': 'afl.',
  'producerAssignments.errorForbidden': 'U heeft geen toegang tot deze pagina.',
  'producerAssignments.errorGeneric': 'Kan uw opdrachten niet laden.',
  'producerAssignments.errorNoInstitution': 'U bent niet gekoppeld aan een instelling.',
  'producerAssignments.formations': 'Opleidingskaarten',
  'producerAssignments.intro': 'Deze pagina bevat de opleidingskaarten en podcasts die u zijn toegewezen door de HR-afdeling van uw instelling. Maak uw content in de EduFlow-designer of de podcaststudio en werk de productiestatus bij.',
  'producerAssignments.markCompleted': 'Markeren als voltooid',
  'producerAssignments.markInProgress': 'In productie',
  'producerAssignments.markReady': 'Klaar',
  'producerAssignments.modulesCatalog': '{{count}} catalogusmodule(s) · bijgewerkt {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Geen bedrijfspad-aanvragen.',
  'producerAssignments.noneFormations': 'Geen kaarten toegewezen.',
  'producerAssignments.nonePodcasts': 'Geen podcasts toegewezen.',
  'producerAssignments.openInDesigner': 'Openen in designer',
  'producerAssignments.openPodcasts': 'Podcaststudio',
  'producerAssignments.planningDeadline': 'Deadline {{date}}',
  'producerAssignments.planningFrom': 'Vanaf {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Te produceren podcasts',
  'producerAssignments.productionStatus.completed': 'Voltooid',
  'producerAssignments.productionStatus.in_progress': 'In uitvoering',
  'producerAssignments.productionStatus.not_started': 'Niet gestart',
  'producerAssignments.productionStatus.published': 'Gepubliceerd',
  'producerAssignments.publishedCatalog': 'Gepubliceerd in catalogus',
  'producerAssignments.rhPlanning': 'HR-planning',
  'producerAssignments.subtitle': 'Opleidingen om te maken in de designer en podcasts om te produceren, toegewezen door HR.',
  'producerAssignments.title': 'Mijn productieopdrachten',
  'podcasts.actions.cancel': 'Annuleren',
  'podcasts.actions.close': 'Sluiten',
  'podcasts.actions.create': 'Aanmaken',
  'podcasts.actions.edit': 'Bewerken',
  'podcasts.actions.retry': 'Opnieuw proberen',
  'podcasts.actions.save': 'Opslaan',
  'podcasts.actions.saving': 'Opslaan…',
  'podcasts.actions.share': 'Delen',
  'podcasts.category.ariaGroup': 'Categorie van de podcast',
  'podcasts.category.emptyBanner': 'Voeg categorieën toe in de Instellingsruimte om podcasts beter te organiseren.',
  'podcasts.category.emptyInstitutionHint': 'Nog geen instellingscategorieën — u kunt zonder publiceren.',
  'podcasts.category.noneChip': 'Geen categorie',
  'podcasts.category.sectionTitle': 'Categorie',
  'podcasts.category.withCategoriesHint': 'Plaatsing in de catalogus en filters aan de instellingszijde.',
  'podcasts.cover.ariaDropzone': 'Omslagafbeelding kiezen of neerzetten',
  'podcasts.cover.browseFiles': 'bestanden bladeren',
  'podcasts.cover.cancelNewFile': 'Nieuw bestand annuleren',
  'podcasts.cover.dragDrop': 'Sleep een afbeelding',
  'podcasts.cover.dropHere': 'Zet de afbeelding hier neer',
  'podcasts.cover.errorMaxSize': 'De afbeelding mag niet groter zijn dan 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Afbeeldingsformaat niet ondersteund.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max. 5 MB',
  'podcasts.cover.help': 'Vierkant formaat aanbevolen. Sleep een bestand of klik om te bladeren.',
  'podcasts.cover.or': 'of',
  'podcasts.cover.replaceHint': 'Afbeelding vervangen',
  'podcasts.cover.sectionTitle': 'Omslagafbeelding',
  'podcasts.deleteDialog.body': '« {{title}} » wordt uit de lijst verwijderd (logische verwijdering). Afleveringen worden niet gewist. Als uw account aan een instelling is gekoppeld, kan een beheerder de podcast herstellen vanuit de prullenbak van de Instellingsruimte.',
  'podcasts.deleteDialog.confirm': 'Naar prullenbak',
  'podcasts.deleteDialog.title': 'Podcast naar de prullenbak verplaatsen?',
  'podcasts.editPanel.createdOn': 'Aangemaakt op ',
  'podcasts.editPanel.headerTitle': 'Podcast bewerken',
  'podcasts.editPanel.saving': 'Opslaan…',
  'podcasts.editPanel.statsEpisodes': 'Afleveringen: ',
  'podcasts.editPanel.statsSubscribers': 'Abonnees: ',
  'podcasts.editPanel.successSaved': 'Wijzigingen opgeslagen',
  'podcasts.episodeStatus.draft': 'Concept',
  'podcasts.episodeStatus.published': 'Gepubliceerd',
  'podcasts.episodesPanel.addEpisode': 'Aflevering toevoegen',
  'podcasts.episodesPanel.emptySubtitle': 'Voeg afleveringen toe aan deze podcast',
  'podcasts.episodesPanel.emptyTitle': 'Geen afleveringen',
  'podcasts.episodesPanel.episodeCount': '{{count}} aflevering(en)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Geen aflevering beschikbaar',
  'podcasts.episodesPanel.remove': 'Verwijderen',
  'podcasts.episodesPanel.searchPlaceholder': 'Zoek een aflevering…',
  'podcasts.episodesPanel.title': 'Afleveringen van de podcast',
  'podcasts.error.retry': 'Opnieuw proberen',
  'podcasts.filters.allCategories': 'Alle categorieën',
  'podcasts.filters.categoryLabel': 'Categorie',
  'podcasts.filters.searchLabel': 'Tekstzoekopdracht',
  'podcasts.filters.searchPlaceholder': 'Titel, beschrijving…',
  'podcasts.filters.viewCategories': 'Categorieënweergave',
  'podcasts.filters.viewList': 'Lijstweergave',
  'podcasts.form.descriptionLabel': 'Beschrijving',
  'podcasts.form.descriptionPlaceholder': 'Beschrijf uw podcast…',
  'podcasts.form.namePlaceholder': 'Naam van de podcast',
  'podcasts.form.titleLabel': 'Titel',
  'podcasts.language.ariaGroup': 'Taal van de podcast',
  'podcasts.language.help': 'Onder de 10 platformtalen — om de podcast af te stemmen op dezelfde locale als de speler en de leerlinginterface.',
  'podcasts.language.label': 'Taal',
  'podcasts.lightbox.ariaClose': 'Sluiten',
  'podcasts.lightbox.ariaDialog': 'Omslagvoorbeeld podcast',
  'podcasts.list.actionActivate': 'Activeren',
  'podcasts.list.actionDeactivate': 'Deactiveren',
  'podcasts.list.actionDelete': 'Verwijderen',
  'podcasts.list.actionManageEpisodes': 'Afleveringen beheren',
  'podcasts.list.actionPreview': 'Voorbeeld en luisteren',
  'podcasts.list.coverPreviewAria': 'Omslagvoorbeeld podcast {{title}}',
  'podcasts.list.createFirst': 'Podcast aanmaken',
  'podcasts.list.emptySubtitle': 'Maak uw eerste podcast om te beginnen',
  'podcasts.list.emptyTitle': 'Geen podcasts',
  'podcasts.list.loadError': 'Kan podcasts niet laden.',
  'podcasts.list.enterpriseBadge': 'Bedrijf',
  'podcasts.list.enterpriseNeedTitle': 'Door uw bedrijf gedefinieerde behoefte',
  'podcasts.list.episodesShort': '{{count}} afl.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} abon.',
  'podcasts.list.uncategorized': 'Geen categorie',
  'podcasts.modal.errorRequiredFields': 'Titel en beschrijving zijn verplicht.',
  'podcasts.modal.errorSaveFailed': 'Fout bij het opslaan.',
  'podcasts.modal.subtitleNew': 'Vul titel, beschrijving en opties in. U kunt daarna afleveringen toevoegen.',
  'podcasts.modal.titleEdit': 'Podcast bewerken',
  'podcasts.modal.titleNew': 'Nieuwe podcast',
  'podcasts.page.episodesLink': 'Afleveringen',
  'podcasts.page.newButton': 'Nieuw',
  'podcasts.page.title': 'Podcasts',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Pagina {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': '{{count}} podcast(s) in totaal',
  'podcasts.player.listen': 'Luisteren',
  'podcasts.player.noAudio': 'Geen audio',
  'podcasts.player.pause': 'Pauze',
  'podcasts.preview.emptySubtitle': 'Deze podcast heeft nog geen afleveringen',
  'podcasts.preview.emptyTitle': 'Geen afleveringen',
  'podcasts.preview.episodeCount': '{{count}} aflevering(en)',
  'podcasts.preview.fallbackDescription': 'Luister naar de beschikbare audio-afleveringen.',
  'podcasts.preview.playAll': 'Alles afspelen',
  'podcasts.preview.title': 'Podcastvoorbeeld',
  'podcasts.share.assignedLearnersHeading': '{{count}} toegewezen leerling(en)',
  'podcasts.share.copied': 'Gekopieerd',
  'podcasts.share.copyLink': 'Link kopiëren',
  'podcasts.share.emailPlaceholder': 'E-mail *',
  'podcasts.share.errorCreateLearner': 'Kan de leerling niet aanmaken.',
  'podcasts.share.errorPublicLink': 'Kan de openbare link niet genereren.',
  'podcasts.share.errorRemoveLearner': 'Kan de leerling niet verwijderen.',
  'podcasts.share.errorShare': 'Kan deze podcast niet delen.',
  'podcasts.share.firstNamePlaceholder': 'Voornaam *',
  'podcasts.share.generatingLink': 'Link genereren…',
  'podcasts.share.hideQuickCreate': 'Snel aanmaken verbergen',
  'podcasts.share.lastNamePlaceholder': 'Achternaam *',
  'podcasts.share.loadingAssigned': 'Laden…',
  'podcasts.share.loadingLearners': 'Leerlingen laden...',
  'podcasts.share.messageAccessRemoved': 'Toegang voor de leerling ingetrokken.',
  'podcasts.share.messageCreatedAndShared': 'Account aangemaakt/gekoppeld, podcast gedeeld en uitnodigingsmail verzonden.',
  'podcasts.share.messageShared': 'Podcast gedeeld en uitnodiging naar de leerling verzonden.',
  'podcasts.share.noAssignedLearners': 'Geen leerling toegewezen aan deze podcast.',
  'podcasts.share.noLearnerAvailable': 'Geen leerling beschikbaar.',
  'podcasts.share.panelTitle': 'Podcast delen',
  'podcasts.share.publicLinkSection': 'Openbare link (zonder inloggen)',
  'podcasts.share.removeAccessTitle': 'Toegang intrekken',
  'podcasts.share.searchLearnerPlaceholder': 'Zoek een leerling (naam, voornaam, e-mail)',
  'podcasts.share.selectExistingLearnerLabel': 'Bestaande leerling selecteren',
  'podcasts.share.showQuickCreate': 'Nieuwe leerling aanmaken',
  'podcasts.share.submitCreateAndInvite': 'Aanmaken, delen en uitnodiging verzenden',
  'podcasts.status.active': 'Actief',
  'podcasts.status.inactive': 'Inactief',
  'learningPaths.card.contents': '{{count}} inhoud(s)',
  'learningPaths.card.delete': 'Verwijderen',
  'learningPaths.card.edit': 'Bewerken',
  'learningPaths.card.learners': '{{count}} deelnemer(s)',
  'learningPaths.card.publish': 'Publiceren',
  'learningPaths.card.unpublish': 'Publicatie intrekken',
  'learningPaths.card.updatedAt': 'Bijgewerkt op {{date}}',
  'learningPaths.createPanel.cancel': 'Annuleren',
  'learningPaths.createPanel.description': 'Geef je traject een naam; je kunt later podcasts en trainingen toevoegen.',
  'learningPaths.createPanel.descriptionLabel': 'Beschrijving',
  'learningPaths.createPanel.descriptionPlaceholder': 'Doel van het traject, doelgroep…',
  'learningPaths.createPanel.nameLabel': 'Naam van het traject',
  'learningPaths.createPanel.namePlaceholder': 'Bijv.: HR-onboardingtraject…',
  'learningPaths.createPanel.submit': 'Traject aanmaken',
  'learningPaths.createPanel.title': 'Nieuw traject',
  'learningPaths.deleteDialog.body': 'Je staat op het punt « {{title}} » te verwijderen. Deze actie kan niet ongedaan worden gemaakt.',
  'learningPaths.deleteDialog.cancel': 'Annuleren',
  'learningPaths.deleteDialog.confirm': 'Verwijderen',
  'learningPaths.deleteDialog.title': 'Traject verwijderen',
  'learningPaths.empty.createFirst': 'Traject aanmaken',
  'learningPaths.empty.noPathsHint': 'Maak je eerste traject door podcasts en trainingen te combineren.',
  'learningPaths.empty.noPathsYet': 'Nog geen trajecten',
  'learningPaths.empty.noResults': 'Geen resultaten',
  'learningPaths.empty.noResultsHint': 'Pas je filters aan of maak een nieuw traject.',
  'learningPaths.error.loadFailed': 'Trajecten konden niet worden geladen',
  'learningPaths.error.retry': 'Opnieuw proberen',
  'learningPaths.filters.allStatuses': 'Alle statussen',
  'learningPaths.filters.draft': 'Concept',
  'learningPaths.filters.published': 'Gepubliceerd',
  'learningPaths.filters.searchPlaceholder': 'Zoek een traject…',
  'learningPaths.page.newButton': 'Nieuw traject',
  'learningPaths.page.subtitle': 'Maak geordende opleidingstrajecten',
  'learningPaths.page.title': 'Leertrajecten',
  'learningPaths.page.totalCount': '{{count}} trajecten',
  'learningPaths.pagination.next': 'Volgende',
  'learningPaths.pagination.pageOf': 'Pagina {{page}} van {{total}} · {{count}} trajecten',
  'learningPaths.pagination.previous': 'Vorige',
  'learningPaths.status.draft': 'Concept',
  'learningPaths.status.published': 'Gepubliceerd',
  'webinars.card.code': 'Code {{code}}',
  'webinars.card.copyLink': 'Link kopiëren',
  'webinars.card.delete': 'Verwijderen',
  'webinars.card.edit': 'Bewerken',
  'webinars.card.openRoom': 'Zaal openen',
  'webinars.deleteDialog.body': '« {{title}} » verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
  'webinars.deleteDialog.cancel': 'Annuleren',
  'webinars.deleteDialog.confirm': 'Verwijderen',
  'webinars.deleteDialog.title': 'Webinar verwijderen',
  'webinars.eligibility.default': 'Webinars zijn niet beschikbaar voor uw account (controleer de API-verbinding of neem contact op met uw beheerder).',
  'webinars.eligibility.moduleDisabled': 'De webinar-module is voor uw instelling nog niet ingeschakeld. Een beheerder moet « Webinars inschakelen » aanvinken bij Instelling → Webinars.',
  'webinars.eligibility.noInstitution': 'Er is geen instelling gekoppeld aan uw account.',
  'webinars.eligibility.notAuthorized': 'Uw account staat niet op de lijst van geautoriseerde presentatoren. Vraag uw instelling u toe te voegen (Webinarbeheer → Geautoriseerde presentatoren).',
  'webinars.eligibility.roleNotAllowed': 'Uw rol staat niet toe webinars te geven met dit account.',
  'webinars.empty.createFirst': 'Webinar aanmaken',
  'webinars.empty.noResults': 'Geen resultaten',
  'webinars.empty.noResultsHint': 'Pas uw zoekopdracht aan.',
  'webinars.empty.noWebinarsHint': 'Maak een zaal aan en nodig deelnemers uit via het zijpaneel.',
  'webinars.empty.noWebinarsYet': 'Nog geen webinars',
  'webinars.externalInvite.closeButton': 'Sluiten',
  'webinars.externalInvite.description': 'Personen die niet op de leerlingenlijst staan: elk adres ontvangt een e-mail met de zaallink en een agenda-bijlage.',
  'webinars.externalInvite.done': 'Klaar.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} al uitgenodigd',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} al uitgenodigd',
  'webinars.externalInvite.emailsHint': 'Eén adres per regel, of gescheiden door komma\'s. Duplicaten en reeds verstuurde uitnodigingen worden genegeerd.',
  'webinars.externalInvite.emailsLabel': 'E-mailadressen',
  'webinars.externalInvite.emailsPlaceholder': 'gebruiker@voorbeeld.nl\nander@domein.org',
  'webinars.externalInvite.errorMax200': 'Maximaal 200 adressen per verzending.',
  'webinars.externalInvite.errorMinOne': 'Voer minstens één geldig e-mailadres in.',
  'webinars.externalInvite.errorsLabel': 'fouten: {{errors}}',
  'webinars.externalInvite.sendButton': 'Uitnodigingen versturen',
  'webinars.externalInvite.sendFailed': 'Verzenden mislukt',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail verstuurd',
  'webinars.externalInvite.sentCount_other': '{{count}} e-mails verstuurd',
  'webinars.externalInvite.skippedCount_one': '{{count}} overgeslagen',
  'webinars.externalInvite.skippedCount_other': '{{count}} overgeslagen',
  'webinars.externalInvite.title': 'Uitnodigingen per e-mail',
  'webinars.invite.duplicatesCount_one': '{{count}} al uitgenodigd',
  'webinars.invite.duplicatesCount_other': '{{count}} al uitgenodigd',
  'webinars.invite.errorsLabel': 'fouten: {{errors}}',
  'webinars.invite.externalButton': 'Buiten de lijst (e-mail)',
  'webinars.invite.failed': 'Mislukt',
  'webinars.invite.groupSuccess': 'Groepsuitnodigingen verwerkt (e-mails naar leden verstuurd).',
  'webinars.invite.inviteButton': 'Uitnodigen',
  'webinars.invite.learnersHelp': 'Zoek een leerling van uw instelling: bij elke uitnodiging wordt een <strong>uitnodigingsmail</strong> (link + agenda) verstuurd.',
  'webinars.invite.learnersTitle': 'Leerlingen',
  'webinars.invite.noCandidates': 'Geen leerlingen om te tonen (probeer een andere zoekopdracht of gebruik « Buiten de lijst » voor externe e-mailadressen).',
  'webinars.invite.noInvitations': 'Geen uitnodigingen geregistreerd.',
  'webinars.invite.searchLabel': 'Leerling zoeken',
  'webinars.invite.searchPlaceholder': 'Naam of e-mail…',
  'webinars.invite.searching': 'Zoeken…',
  'webinars.invite.sentCount_one': '{{count}} uitnodiging verstuurd (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} uitnodigingen verstuurd (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Verstuurde uitnodigingen',
  'webinars.page.newButton': 'Nieuwe webinar',
  'webinars.page.searchPlaceholder': 'Zoeken…',
  'webinars.page.subtitle': 'Lijst van uw zalen — open het zijpaneel om aan te maken, te bewerken of deelnemers uit te nodigen.',
  'webinars.page.title': 'Webinars',
  'webinars.panel.backgroundHint': 'https- (of http-)URL achter de gedeelde inhoud voor deelnemers. Laat leeg voor de ingebouwde Lyrra-stijl.',
  'webinars.panel.backgroundLabel': 'Achtergrondafbeelding (deelnemersweergave)',
  'webinars.panel.backgroundPlaceholder': 'https://… (leeg = standaard Lyrra-achtergrond)',
  'webinars.panel.copyLink': 'Link kopiëren',
  'webinars.panel.createButton': 'Aanmaken',
  'webinars.panel.createDescription': 'Vul de gegevens in en sla op. Daarna kunt u deelnemers uitnodigen.',
  'webinars.panel.createTitle': 'Nieuwe webinar',
  'webinars.panel.dateLabel': 'Dag (optioneel)',
  'webinars.panel.deleteButton': 'Verwijderen',
  'webinars.panel.descriptionLabel': 'Beschrijving',
  'webinars.panel.durationError': 'Geef een duur op als u:mm (bijv. 1:00 of 0:45).',
  'webinars.panel.durationHint': 'Verplicht als er een dag is ingevuld. Bijv. 1:30 voor 1 u 30, 0:45 voor 45 min.',
  'webinars.panel.durationLabel': 'Duur (u:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Laden…',
  'webinars.panel.openRoom': 'Zaal openen',
  'webinars.panel.presentersLabel': 'Medepresentatoren',
  'webinars.panel.roomCode': 'Zaalkode:',
  'webinars.panel.timeHint': 'Indien leeg: 9:00.',
  'webinars.panel.timeLabel': 'Starttijd',
  'webinars.panel.titleLabel': 'Titel',
  'webinars.panel.waitingHtmlLabel': 'HTML wachtzaal (optioneel)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Welkom…</p>',
  'webinars.status.draft': 'Concept',
  'webinars.status.ended': 'Beëindigd',
  'webinars.status.live': 'Live',
  'webinars.status.scheduled': 'Gepland',
  'videos.addPanel.cancel': 'Annuleren',
  'videos.addPanel.continue': 'Doorgaan',
  'videos.addPanel.description': 'Importeer MP4, WebM of MOV om ze in uw trainingen te gebruiken.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Klik om een video te kiezen',
  'videos.addPanel.title': 'Video toevoegen',
  'videos.addPanel.titleLabel': 'Titel',
  'videos.addPanel.titlePlaceholder': 'Videotitel…',
  'videos.card.delete': 'Verwijderen',
  'videos.card.edit': 'Bewerken',
  'videos.card.noThumbnail': 'Geen miniatuur',
  'videos.card.publishedStudio': 'Gepubliceerd (aanpasbaar in Studio)',
  'videos.deleteDialog.body': '« {{title}} » wordt definitief verwijderd.',
  'videos.deleteDialog.cancel': 'Annuleren',
  'videos.deleteDialog.confirm': 'Verwijderen',
  'videos.deleteDialog.title': 'Video verwijderen?',
  'videos.empty.addButton': 'Video toevoegen',
  'videos.empty.description': 'Importeer uw eerste video om deze te beheren en in uw trainingen te gebruiken.',
  'videos.empty.title': 'Geen video\'s',
  'videos.page.addButton': 'Video toevoegen',
  'videos.page.count_one': '{{count}} video in uw bibliotheek',
  'videos.page.count_other': '{{count}} video\'s in uw bibliotheek',
  'videos.page.title': 'Video\'s',
  'videos.preview.noFile': 'Geen bestand beschikbaar.',
  'videos.status.draft': 'Concept',
  'videos.status.published': 'Gepubliceerd',
  'sounds.card.delete': 'Verwijderen',
  'sounds.card.mute': 'Geluid uit',
  'sounds.deleteDialog.body': '« {{title}} » wordt permanent van de server en uit uw bibliotheek verwijderd.',
  'sounds.deleteDialog.cancel': 'Annuleren',
  'sounds.deleteDialog.confirm': 'Verwijderen',
  'sounds.deleteDialog.title': 'Dit geluid verwijderen?',
  'sounds.empty.description': 'Importeer uw bestanden: ze worden in de database opgeslagen en naar de server gekopieerd zodat u niets kwijtraakt.',
  'sounds.empty.importButton': 'Geluid importeren',
  'sounds.empty.title': 'Geen geluiden geïmporteerd',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Annuleren',
  'sounds.importPanel.changeFile': 'Ander bestand kiezen',
  'sounds.importPanel.createTag': '« {{tag}} » aanmaken',
  'sounds.importPanel.description': 'Voeg een audiobestand toe aan uw bibliotheek voor uw trajecten.',
  'sounds.importPanel.descriptionLabel': 'Beschrijving',
  'sounds.importPanel.descriptionPlaceholder': 'Beschrijf dit geluid…',
  'sounds.importPanel.dropzoneBrowse': 'blader',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Sleep een audiobestand of',
  'sounds.importPanel.optional': '(optioneel)',
  'sounds.importPanel.submit': 'Importeren',
  'sounds.importPanel.submitting': 'Verzenden…',
  'sounds.importPanel.tagsHint': 'Typ en druk op Enter · Nieuwe tags worden automatisch aangemaakt',
  'sounds.importPanel.tagsLabel': 'Tags',
  'sounds.importPanel.tagsPlaceholder': 'Tags toevoegen…',
  'sounds.importPanel.title': 'Geluid importeren',
  'sounds.importPanel.titleLabel': 'Titel',
  'sounds.importPanel.titlePlaceholder': 'Naam van het geluid…',
  'sounds.importPanel.titleRequired': 'Titel is verplicht',
  'sounds.noResults': 'Geen geluid komt overeen met « {{query}} »',
  'sounds.page.count_one': '{{count}} bestand — opgeslagen in database en op server',
  'sounds.page.count_other': '{{count}} bestanden — opgeslagen in database en op server',
  'sounds.page.importButton': 'Importeren',
  'sounds.page.searchPlaceholder': 'Titel, beschrijving, tag…',
  'sounds.page.title': 'Geluiden',
  'documents.count_one': '{{count}} document',
  'documents.count_other': '{{count}} documenten',
  'documents.delete.body': 'Het document « {{filename}} » wordt uit uw bibliotheek verwijderd.',
  'documents.delete.cancel': 'Annuleren',
  'documents.delete.confirm': 'Verwijderen',
  'documents.delete.irreversible': 'Deze actie kan niet ongedaan worden gemaakt.',
  'documents.delete.title': 'Document verwijderen?',
  'documents.importPanel.cancel': 'Annuleren',
  'documents.importPanel.chooseFile': 'Kies een PDF-document',
  'documents.importPanel.description': 'Het document is beschikbaar in uw PDF-blokken en in deze bibliotheek.',
  'documents.importPanel.descriptionLabel': 'Beschrijving',
  'documents.importPanel.descriptionPlaceholder': 'Beschrijving (optioneel)',
  'documents.importPanel.fileHint': 'Het document is beschikbaar in uw PDF-blok en in deze bibliotheek.',
  'documents.importPanel.submit': 'Importeren',
  'documents.importPanel.title': 'PDF importeren',
  'documents.importPanel.titleLabel': 'Titel',
  'documents.importPanel.titlePlaceholder': 'Documenttitel',
  'documents.importPanel.uploading': 'Uploaden…',
  'documents.importPdf': 'PDF importeren',
  'documents.loading': 'Bibliotheek laden…',
  'documents.newDocument': 'Nieuw document',
  'documents.noDocuments': 'Geen documenten',
  'documents.noDocumentsHint': 'Importeer hier uw PDF\'s om ze later opnieuw te gebruiken in het PDF-blok.',
  'documents.noResults': 'Geen documenten komen overeen met uw zoekopdracht.',
  'documents.pages_one': '{{count}} pagina',
  'documents.pages_other': '{{count}} pagina\'s',
  'documents.previewTitle': 'Documentvoorbeeld',
  'documents.searchPlaceholder': 'Zoek een PDF...',
  'documents.share.accessRemoved': 'Toegang voor de cursist ingetrokken.',
  'documents.share.assignedTitle': 'Cursisten met toegang',
  'documents.share.close': 'Sluiten',
  'documents.share.createAndShare': 'Aanmaken, delen en uitnodiging verzenden',
  'documents.share.createError': 'De cursist kon niet worden aangemaakt.',
  'documents.share.createLearner': 'Nieuwe cursist aanmaken',
  'documents.share.createdSuccess': 'Account aangemaakt/gekoppeld, document gedeeld en e-mail verzonden.',
  'documents.share.creating': 'Verzenden...',
  'documents.share.emailPlaceholder': 'E-mail *',
  'documents.share.firstNamePlaceholder': 'Voornaam *',
  'documents.share.hideCreateLearner': 'Snel aanmaken verbergen',
  'documents.share.lastNamePlaceholder': 'Achternaam *',
  'documents.share.loadingAssigned': 'Laden…',
  'documents.share.loadingLearners': 'Cursisten laden...',
  'documents.share.noAssigned': 'Geen cursist toegewezen voor dit document.',
  'documents.share.noLearners': 'Geen cursisten beschikbaar.',
  'documents.share.removeButton': 'Verwijderen',
  'documents.share.removeError': 'Toegang kon niet worden ingetrokken.',
  'documents.share.searchPlaceholder': 'Zoek een cursist (naam, voornaam, e-mail)',
  'documents.share.selectLearner': 'Bestaande cursist selecteren',
  'documents.share.shareButton': 'Delen',
  'documents.share.shareError': 'Het document kon niet worden gedeeld.',
  'documents.share.sharedSuccess': 'Document gedeeld en uitnodiging verzonden.',
  'documents.share.title': 'Document delen',
  'documents.subtitle': 'Herbruikbare PDF-bibliotheek voor gebruik in de designer.',
  'documents.title': 'Documenten',
  'documents.toast.importError': 'Importeren mislukt',
  'documents.toast.importErrorDefault': 'De import is mislukt.',
  'documents.toast.importSuccess': 'Document geïmporteerd',
  'documents.toast.importSuccessDesc': '{{filename}} is toegevoegd aan uw documentenbibliotheek.',
  'documents.updating': 'Bijwerken…',
  'help.categories.analytics.description': 'Dashboards, filters en exports om de impact van trainingen te monitoren.',
  'help.categories.analytics.title': 'Analytics & rapporten',
  'help.categories.apprenants.description': 'Inschrijvingen, cohorten en de voortgang van deelnemers opvolgen.',
  'help.categories.apprenants.title': 'Lerenden, groepen & opvolging',
  'help.categories.boutique.description': 'Trainingen verkopen of toewijzen wanneer de winkel is ingesteld.',
  'help.categories.boutique.title': 'Winkel & monetisering',
  'help.categories.demarrage.description': 'De interface, uw profiel en de dagelijkse navigatie in Lyrra leren kennen.',
  'help.categories.demarrage.title': 'Aan de slag & account',
  'help.categories.donnees.description': 'GDPR, bewaring, rechten van betrokkenen en goede beveiligingspraktijken.',
  'help.categories.donnees.title': 'Persoonsgegevens & beveiliging',
  'help.categories.eduflow.description': 'Leertrajecten ontwerpen met blokken, verbindingen en navigatielogica.',
  'help.categories.eduflow.title': 'Trajecten & EduFlow-designer',
  'help.categories.evaluation.description': 'Meerkeuze, evaluaties, enquêtes en feedback om de verworven kennis te meten.',
  'help.categories.evaluation.title': 'Evaluatie & actieve pedagogie',
  'help.categories.institution.description': 'Uw structuur, medewerkers en de organisatie van content instellen.',
  'help.categories.institution.title': 'Instelling & team',
  'help.categories.marque.description': 'Kleuren, labels en het leertraject afstemmen op uw identiteit.',
  'help.categories.marque.title': 'Merk & gebruikerservaring',
  'help.categories.medias.description': 'Bibliotheek, imports en geïntegreerde audio-/video-/beeldtools in Lyrra beheren.',
  'help.categories.medias.title': 'Media & creatieve studio',
  'help.categories.parcours-v2.description': 'Campagnes, competenties, integraties: alles wat het trainingsbeheer verrijkt.',
  'help.categories.parcours-v2.title': 'Geavanceerde functies & roadmap',
  'help.categories.podcasts.description': 'Lange audiocontent maken en verspreiden buiten het bloksgewijze traject.',
  'help.categories.podcasts.title': 'Podcasts & audioboeken',
  'help.categories.publication.description': 'Een traject beschikbaar maken voor lerenden en de juiste toegang delen.',
  'help.categories.publication.title': 'Publicatie & verspreiding',
  'help.categories.qualite.description': 'Franse kwaliteitskaders: traceerbaarheid, doelstellingen en bewijsmateriaal voor audits.',
  'help.categories.qualite.title': 'Kwaliteit, Qualiopi & pedagogische conformiteit',
  'help.categories.support.description': 'Bij een technische of functionele blokkade weten waar te zoeken en wie te contacteren.',
  'help.categories.support.title': 'Probleemoplossing & support',
  'help.configHint': 'Redactionele inhoud: koppel uw URL\'s, artikelen of kennisbank wanneer u klaar bent.',
  'help.description': 'Documentatie en ondersteuning',
  'help.hubIntro': 'Thema\'s over het gebruik van Lyrra: binnenkort kunt u elke fiche koppelen aan uw documentatie, tutorials of supporttickets.',
  'help.modal.expectedAnswer': 'Verwacht antwoord:',
  'help.modal.fallbackHighlight1': 'Fiche',
  'help.modal.fallbackHighlight2': 'Trainingsplatform',
  'help.modal.fallbackHighlight3': 'Aan te vullen',
  'help.modal.fallbackSectionHeading': 'Onthoud',
  'help.modal.fallbackSectionParagraph': 'De gedetailleerde inhoud van deze fiche wordt aangevuld of gekoppeld aan uw interne documentatie.',
  'help.modal.fallbackStep1Desc': 'Gebruik het zijmenu of het dashboard om de betreffende module te openen.',
  'help.modal.fallbackStep1Title': 'De functie openen',
  'help.modal.fallbackStep2Desc': 'De schermen begeleiden u: sla regelmatig op en test in de voorbeeldweergave wanneer beschikbaar.',
  'help.modal.fallbackStep2Title': 'De werkstroom volgen',
  'help.modal.fallbackStep3Desc': 'Noteer de specifieke kenmerken van uw instelling (HR-processen, naamgeving, huisstijl) voor uw team.',
  'help.modal.fallbackStep3Title': 'Intern documenteren',
  'help.modal.fallbackTip': 'Controleer bij twijfel uw rol (docent, beheerder, HR): de weergegeven menu\'s zijn daarvan afhankelijk.',
  'help.modal.quiz': 'Validatiequiz',
  'help.modal.related': 'Zie ook',
  'help.modal.resources': 'Bronnen',
  'help.modal.steps': 'In de praktijk',
  'help.modal.tips': 'Tips',
  'help.noResults': 'Geen resultaten voor deze zoekopdracht.',
  'help.onboarding.ai-designer': 'Training — AI-assistent van de LyrraStudio-designer (onboarding)',
  'help.onboarding.blocks': 'Training — Soorten blokken EduFlow (onboarding)',
  'help.onboarding.branches': 'Training — Vertakkingen & voorwaarden EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Training — Catalogus & zichtbaarheid LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Training — Afgegeven certificaten LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Training — Lyrra-dashboard (onboarding)',
  'help.onboarding.designer-overview': 'Training — Overzicht van de EduFlow-designer (onboarding)',
  'help.onboarding.enrollments': 'Training — Inschrijvingen & deelnemers LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Training — Groepen & cohorten LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Training — Mediabibliotheek LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Training — Navigatie & zijmenu Lyrra (onboarding)',
  'help.onboarding.objectives': 'Training — Leerdoelen EduFlow (onboarding)',
  'help.onboarding.profile': 'Training — Profiel & voorkeuren Lyrra (onboarding)',
  'help.onboarding.progress': 'Training — Voortgang & bestede tijd LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Training — Toegangslinks & delen LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Training — Concept, publicatie en updates LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Training — Rollen en toegangsrechten Lyrra (onboarding)',
  'help.onboarding.validation': 'Training — Trajectvalidatie EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Training niet gevonden',
  'help.onboardingFlowMissingDesc': 'Voer de scripts db:seed:onboarding-eduflow en db:seed:onboarding-navigation-eduflow (backend) uit, of stel de variabelen VITE_ONBOARDING_*_FLOW_ID in.',
  'help.openArticle': 'Fiche openen',
  'help.searchPlaceholder': 'Zoek een thema…',
  'help.title': 'Helpcentrum',
  'help.topics.analytics-exports.description': 'De extracties voorbereiden voor interne of wettelijke rapportage.',
  'help.topics.analytics-exports.title': 'Exports & HR-rapportage',
  'help.topics.analytics-indicateurs.description': 'Voltooiingspercentage, betrokkenheid, verdeling per status en tijd op de blokken.',
  'help.topics.analytics-indicateurs.title': 'Kernindicatoren',
  'help.topics.analytics-vue.description': 'Grafieken, periodes en filter per training aanpassen om de gegevens te analyseren.',
  'help.topics.analytics-vue.title': 'Analytisch overzicht',
  'help.topics.app-certificats.description': 'De gegenereerde attestaties en de bijbehorende slaagvoorwaarden controleren.',
  'help.topics.app-certificats.title': 'Afgegeven certificaten',
  'help.topics.app-groupes.description': 'Groepen gebruiken om doelgroepen te segmenteren en campagnes of rapporten te vergemakkelijken.',
  'help.topics.app-groupes.title': 'Groepen & cohorten',
  'help.topics.app-inscriptions.description': 'De lijst van lerenden op een traject raadplegen, hun statussen en toegangsgeschiedenis.',
  'help.topics.app-inscriptions.title': 'Inschrijvingen & deelnemers',
  'help.topics.app-progression.description': 'De voortgang, voltooiingen en betrokkenheidstijd in de blokken interpreteren.',
  'help.topics.app-progression.title': 'Voortgang & bestede tijd',
  'help.topics.boutique-offres.description': 'Trajecten koppelen aan producten of toegangscodes volgens uw bedrijfsmodel.',
  'help.topics.boutique-offres.title': 'Aanbiedingen & prijsstelling',
  'help.topics.boutique-paiement.description': 'De aankoopstroom voor de lerende en de bevestigingen voor de organisatie begrijpen.',
  'help.topics.boutique-paiement.title': 'Betalingen & facturering',
  'help.topics.demarrage-navigation.description': 'De ruimtes Projecten, Trainingen, Media, Analytics en Instellingen vinden op basis van uw rol.',
  'help.topics.demarrage-navigation.title': 'Navigatie & zijmenu',
  'help.topics.demarrage-profil.description': 'Uw gegevens, de interfacetaal en meldingen bijwerken.',
  'help.topics.demarrage-profil.title': 'Profiel & voorkeuren',
  'help.topics.demarrage-roles.description': 'De verschillen tussen docent, instellingsbeheerder, HR en lerende begrijpen.',
  'help.topics.demarrage-roles.title': 'Rollen en toegangsrechten',
  'help.topics.demarrage-tableau-bord.description': 'De kernindicatoren lezen, snel toegang krijgen tot recente trajecten en handige snelkoppelingen.',
  'help.topics.demarrage-tableau-bord.title': 'Trainingsdashboard',
  'help.topics.donnees-conservation.description': 'Principes voor de bewaarduur van voortgangsgegevens en opleidingsbewijzen.',
  'help.topics.donnees-conservation.title': 'Bewaartermijn',
  'help.topics.donnees-mots-de-passe.description': 'Goede praktijken voor wachtwoorden, sessies en gedelegeerde toegang.',
  'help.topics.donnees-mots-de-passe.title': 'Accounts & authenticatie',
  'help.topics.donnees-rgpd.description': 'Doeleinden van de in Lyrra verzamelde gegevens en verantwoordelijkheid van de opleidingsverantwoordelijke.',
  'help.topics.donnees-rgpd.title': 'GDPR & verwerkingsregister',
  'help.topics.eduflow-blocs.description': 'De juiste blokken kiezen en instellen: tekst, video, audio, evaluatie, vertakking, enz.',
  'help.topics.eduflow-blocs.title': 'Soorten blokken (inhoud, quiz, media…)',
  'help.topics.eduflow-branches.description': 'Meerdere uitgangen, padconvergentie en navigatieregels beheren.',
  'help.topics.eduflow-branches.title': 'Vertakkingen & voorwaarden',
  'help.topics.eduflow-concept.description': 'Canvas, blokpalet, selectie, zoom en visuele organisatie van het traject.',
  'help.topics.eduflow-concept.title': 'Overzicht van de designer',
  'help.topics.eduflow-ia.description': 'Blokken voorstellen of wijzigen met behulp van de assistent, met systematische menselijke controle.',
  'help.topics.eduflow-ia.title': 'AI-assistent in de designer',
  'help.topics.eduflow-objectifs.description': 'Doelen koppelen aan blokken om het referentiekader van uw training te structureren.',
  'help.topics.eduflow-objectifs.title': 'Leerdoelen',
  'help.topics.eduflow-validation.description': 'Het Validatiepaneel gebruiken om fouten en waarschuwingen vóór publicatie te detecteren.',
  'help.topics.eduflow-validation.title': 'Trajectvalidatie',
  'help.topics.eval-auto-eval.description': 'Adaptieve trajecten aanbieden op basis van de resultaten.',
  'help.topics.eval-auto-eval.title': 'Zelfevaluatie & remediëring',
  'help.topics.eval-ouverte.description': 'De verzameling van vrije antwoorden en de handmatige of semi-automatische beoordeling organiseren.',
  'help.topics.eval-ouverte.title': 'Open vragen & correctie',
  'help.topics.eval-quiz.description': 'Schalen, feedback en pogingen configureren voor een efficiënte meerkeuzetoets.',
  'help.topics.eval-quiz.title': 'Quiz & meerkeuze',
  'help.topics.inst-collaborateurs.description': 'Docenten, beheerders of HR toevoegen en rechten beheren.',
  'help.topics.inst-collaborateurs.title': 'Gebruikers & uitnodigingen',
  'help.topics.inst-structure.description': 'Bepalen wat gedeeld wordt op instellingsniveau versus persoonlijke projecten.',
  'help.topics.inst-structure.title': 'Structuur & bereik',
  'help.topics.inst-trash.description': 'Verwijderde trainingen of podcasts terugvinden en indien nodig herstellen.',
  'help.topics.inst-trash.title': 'Prullenbak & herstel',
  'help.topics.marque-accessibilite.description': 'Contrasten, tekstalternatieven en toetsenbordnavigatie in de content.',
  'help.topics.marque-accessibilite.title': 'Toegankelijkheid & leesbaarheid',
  'help.topics.marque-charte.description': 'Hoe het merk zich weerspiegelt in de speler en de lerenderuimtes.',
  'help.topics.marque-charte.title': 'Huisstijl & institutionele kleuren',
  'help.topics.marque-landing.description': 'Uw trainingsaanbod presenteren aan bezoekers wanneer de module is geactiveerd.',
  'help.topics.marque-landing.title': 'Openbare pagina\'s & catalogus',
  'help.topics.medias-bibliotheque.description': 'Video\'s, geluiden, afbeeldingen en documenten importeren, organiseren en hergebruiken in uw blokken.',
  'help.topics.medias-bibliotheque.title': 'Mediabibliotheek',
  'help.topics.medias-documents.description': 'Downloadbare bronnen bijvoegen en documentatie aan het traject koppelen.',
  'help.topics.medias-documents.title': 'Documenten & PDF',
  'help.topics.medias-studio-audio.description': 'Opnemen, mixen of tekst-naar-spraak gebruiken om uw content te verrijken.',
  'help.topics.medias-studio-audio.title': 'Audiostudio & stem',
  'help.topics.medias-video.description': 'Goede praktijken voor formaat, bestandsgrootte en toegankelijkheid voor geïntegreerde spelers.',
  'help.topics.medias-video.title': 'Video\'s & integraties',
  'help.topics.podcasts-creation.description': 'Afleveringen, metadata en omslagen structureren voor uw audioaanbod.',
  'help.topics.podcasts-creation.title': 'Een podcast maken',
  'help.topics.podcasts-diffusion.description': 'Luisterinstellingen, delen en gebruiksopvolging vanuit de instelling.',
  'help.topics.podcasts-diffusion.title': 'Verspreiding & lerenden',
  'help.topics.pub-catalogue.description': 'Een training koppelen aan de instellingscatalogus of een aanbod wanneer geactiveerd.',
  'help.topics.pub-catalogue.title': 'Catalogus & zichtbaarheid',
  'help.topics.pub-liens.description': 'Links genereren of kopiëren voor lerenden, presentatoren of voorbeeldweergave.',
  'help.topics.pub-liens.title': 'Toegangslinks & delen',
  'help.topics.pub-statuts.description': 'Levenscyclus van een training: opslaan, testen, publiceren en wijzigingen versiebeheren.',
  'help.topics.pub-statuts.title': 'Concept, publicatie en updates',
  'help.topics.qualite-qualiopi.description': 'De speciale ruimtes gebruiken om de criteria en het kwaliteitsdossier voor te bereiden.',
  'help.topics.qualite-qualiopi.title': 'Pre-audit & Qualiopi-indicatoren',
  'help.topics.qualite-tracabilite.description': 'Doelen, inhoud en evaluaties koppelen om het certificeringstraject te documenteren.',
  'help.topics.qualite-tracabilite.title': 'Pedagogische traceerbaarheid',
  'help.topics.support-contact.description': 'Supportkanaal, ticket aanmaken en benodigde informatie voor een snelle diagnose.',
  'help.topics.support-contact.title': 'Contact opnemen met Lyrra-support',
  'help.topics.support-faq.description': 'Speler laadt niet, ontbrekend mediabestand, publicatiefout: eerste stappen.',
  'help.topics.support-faq.title': 'Veelvoorkomende problemen',
  'help.topics.support-navigateur.description': 'Aanbevolen versies, cookies en extensies die kunnen storen.',
  'help.topics.support-navigateur.title': 'Browser & compatibiliteit',
  'help.topics.v2-campagnes.description': 'De uitrol van trajecten bij doelgroepen in de tijd plannen.',
  'help.topics.v2-campagnes.title': 'Campagnes & opleidingsplannen',
  'help.topics.v2-competences.description': 'Blokken of trajecten koppelen aan verwachte competenties voor bedrijfsrapportage.',
  'help.topics.v2-competences.title': 'Competentiekaders',
  'help.topics.v2-integrations.description': 'Uitwisseling met uw HR-systeem, extern LMS of BI-tools wanneer connectoren beschikbaar zijn.',
  'help.topics.v2-integrations.title': 'Integraties & API',
  'pages.images.cancel': 'Annuleren',
  'pages.images.changeFile': 'Bestand wijzigen',
  'pages.images.copyUrl': 'URL kopiëren',
  'pages.images.count_one': '{{count}} afbeelding in uw bibliotheek',
  'pages.images.count_other': '{{count}} afbeeldingen in uw bibliotheek',
  'pages.images.delete': 'Verwijderen',
  'pages.images.deleteAction': 'Verwijderen',
  'pages.images.deleteConfirmHint': 'wordt permanent verwijderd.',
  'pages.images.deleteTitle': 'Deze afbeelding verwijderen?',
  'pages.images.descriptionLabel': 'Beschrijving (optioneel)',
  'pages.images.descriptionPlaceholder': 'Korte beschrijving…',
  'pages.images.dropzoneBrowse': 'blader',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Sleep een afbeelding of',
  'pages.images.emptyHint': 'Sleep en zet neer, klik om te importeren of plak een schermafbeelding (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importeren',
  'pages.images.importSubtitle': 'Voeg een afbeelding toe aan uw bibliotheek voor uw trainingen.',
  'pages.images.importTitle': 'Afbeelding importeren',
  'pages.images.lightboxBackdropClose': 'Sluiten',
  'pages.images.lightboxClose': 'Sluiten',
  'pages.images.lightboxNext': 'Volgende afbeelding',
  'pages.images.lightboxPrev': 'Vorige afbeelding',
  'pages.images.lightboxTitle': 'Voorbeeldafbeelding',
  'pages.images.noImages': 'Geen afbeeldingen geïmporteerd',
  'pages.images.noResults': 'Geen resultaten',
  'pages.images.pasteHint': 'Of plak een schermafbeelding (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Zoek een afbeelding…',
  'pages.images.submit': 'Importeren',
  'pages.images.title': 'Afbeeldingen',
  'pages.images.titleLabel': 'Titel (optioneel)',
  'pages.images.titlePlaceholder': 'Naam van de afbeelding…',
  'pages.images.viewLarge': 'Groot bekijken',
  'pages.learners.blockSessionInProgress': '(am Gaang)',
  'pages.learners.blockTimeOnBlock': '({{duration}} op dësem Block)',
  'pages.learners.description': 'Verfollegt de Fortschrëtt vun Äre Léierenden',
  'pages.learners.detailedTraces': 'Detailléiert Aktivitéit · {{flowTitle}}',
  'pages.learners.enrolledFormations': 'Ingeschriwwe Formatiounen',
  'pages.learners.lastEvents': 'Lescht Evenementer',
  'pages.learners.notStarted': 'Nach net ugefaangen',
  'pages.learners.startedOn': 'Ufank {{date}}',
  'pages.learners.statsBlockSessions': 'Block-Sitzungen: {{count}}',
  'pages.learners.statsEvents': 'Evenementer: {{count}}',
  'pages.learners.statsResponses': 'Äntwerten: {{count}}',
  'pages.learners.timePerBlock': 'Zäit pro Block',
  'pages.learners.title': 'Léierenden',
  'pages.learners.trackingEvent.block_enter': 'Block betrieden',
  'pages.learners.trackingEvent.block_exit': 'Block verloossen',
  'pages.learners.trackingEvent.progress_sync': 'Fortschrëtt synchroniséiert',
  'pages.learners.trackingEvent.response_submitted': 'Äntwert geschéckt',
  'pages.learners.trackingEvent.training_completed': 'Formatioun ofgeschloss',
  'pages.learners.trackingEvent.training_enter': 'Formatioun opgemaach',
  'pages.learners.trackingEvent.training_exit': 'Formatioun verlooss oder zougemaach',
  'pages.learners.trackingEvent.unknown': 'Evenement',
  'pages.learners.refresh': 'Aktualiséieren',
  'pages.learners.exportCsv': 'CSV exportéieren',
  'pages.learners.statTotal': 'Léierenden am Ganzen',
  'pages.learners.statInProgress': 'Am Gaang',
  'pages.learners.statCompleted': 'Ofgeschloss',
  'pages.learners.statAvgProgress': 'Mëtt. Fortschrëtt',
  'pages.learners.searchPlaceholder': 'No Numm oder E-Mail sichen…',
  'pages.learners.filterAllFlows': 'All Formatiounen',
  'pages.learners.sortRecent': 'Am rezentsten',
  'pages.learners.sortName': 'Numm A-Z',
  'pages.learners.sortProgress': 'Fortschrëtt',
  'pages.learners.colLearner': 'Léierenden',
  'pages.learners.colFormations': 'Formatiounen',
  'pages.learners.colProgress': 'Fortschrëtt',
  'pages.learners.colCompleted': 'Ofgeschloss',
  'pages.learners.colTime': 'Zäit',
  'pages.learners.colLastAccess': 'Leschte Zougrëff',
  'pages.learners.colActions': 'Aktiounen',
  'pages.learners.openProgressDashboard': 'Fortschrëtt-Dashboard',
  'pages.learners.dashboardTitle': 'Fortschrëtt vum Léierenden',
  'pages.learners.dashboardBack': 'Zréck op d’Lëscht',
  'pages.learners.dashboardInvalidId': 'Ongëlteg Léierenden-ID',
  'pages.learners.dashboardNotFound': 'Léierende fonnt oder Zougrëff refuséiert',
  'pages.learners.dashboardOverviewHint':
    'Iwwersiicht: EduFlow-Formatiounen vum Participant, Weeër a Podcasten déi dem Lyrra-Kont mat der selwechter E-Mail zougedeelt sinn.',
  'pages.learners.tabOverview': 'Iwwersiicht',
  'pages.learners.tabFormations': 'Formatiounen',
  'pages.learners.tabParcours': 'Weeër',
  'pages.learners.tabPodcasts': 'Podcasten',
  'pages.learners.sectionFormations': 'Formatiounen',
  'pages.learners.sectionParcours': 'Weeër',
  'pages.learners.sectionPodcasts': 'Podcasten',
  'pages.learners.emptyParcours': 'Kee Wee fir e Lyrra-Kont mat dëser E-Mail.',
  'pages.learners.emptyPodcasts': 'Kee Podcast fir e Lyrra-Kont mat dëser E-Mail.',
  'pages.learners.linkedAccountHint':
    'Weeër a Podcaste ginn ugewisen wann e Lyrra-Benotzer mat der selwechter E-Mail wéi de Participant existéiert.',
  'pages.learners.kpiEduflows': 'Formatiounen',
  'pages.learners.kpiAvgProgress': 'Mëtt. Fortschrëtt',
  'pages.learners.kpiPaths': 'Weeër',
  'pages.learners.kpiPodcasts': 'Podcasten',
  'pages.learners.kpiCompletedFormations': '{{count}} ofgeschloss',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} Aktivitéiten',
  'pages.learners.pathAssignedOn': 'Derbäi gesat den {{date}}',
  'pages.learners.podcastCompleted': 'Ofgeschloss',
  'pages.learners.podcastInProgress': 'Am Gaang',
  'pages.learners.pathStatusPublished': 'Verëffentlecht',
  'pages.learners.pathStatusDraft': 'Brouillon',
  'pages.learners.openInDesigner': 'Am Designer opmaachen',
  'pages.learners.openParcoursEditor': 'Wee opmaachen',
  'pages.learners.viewPodcasts': 'Podcaste weisen',
  'pages.learners.loading': 'Léierende ginn gelueden…',
  'pages.learners.loadError': 'Léierend konnten net geluede ginn',
  'pages.learners.retry': 'Nach eng Kéier probéieren',
  'pages.learners.emptyFiltered': 'Kee Léierende fonnt',
  'pages.learners.emptyFilteredHint': 'Ännert Är Sich oder Filter',
  'pages.learners.emptyNone': 'Kee ageschriwwene Léierenden',
  'pages.learners.emptyNoneHint':
    'Léierende ginn hei ugewisen, wann si sech an Är verëffentlecht Formatiounen aschreiwen',
  'pages.learners.pagination': 'Säit {{page}} vu(n) {{totalPages}} · {{total}} Léierende',
  'pages.learners.prev': 'Zréck',
  'pages.learners.next': 'Weider',
  'pages.learners.csvLastName': 'Familljennumm',
  'pages.learners.csvFirstName': 'Virnumm',
  'pages.learners.csvEmail': 'E-Mail',
  'pages.learners.csvFormations': 'Formatiounen',
  'pages.learners.csvAvgProgress': 'Mëttel Fortschrëtt',
  'pages.learners.csvTotalTime': 'Totalzäit',
  'pages.learners.csvLastActivity': 'Lescht Aktivitéit',
  'pages.learners.statsAdaptiveChoice':
    'Hëllef bei der Choix — ugeholl: {{accepted}} · refuséiert: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Adaptatioune bewert: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Vereinfacht Versioune benotzt: {{count}}',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Vereinfachte Text-Modus',
  'accessibility.simplifiedTextModeDesc':
    "Simplifizéiert nëmmen d'Texter vum Parcours, fir d'Liesen an d'Verstoen ze vereinfachen.",
  'accessibility.imageHoverAudio': 'Modus fir schwaach Siicht: Audiobeschreiwung vum Bild beim Iwwerfueren',
  'accessibility.imageHoverAudioDesc':
    "Wann d'Bild am Player iwwertfuere gëtt, start d'Audiobeschreiwung automatesch.",
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Verklengeren',
  'admin.shell.collapseSidebar': 'Säitenleescht verklengeren',
  'admin.shell.expandSidebar': 'Säitenleescht vergréisseren',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Formatiounsufro — pedagogesch Inhalter',
  'trainingRequest.intro': 'Beschreift de Besoin. D\'HR-Equipe validéiert oder refuséiert d\'Ufro; wann akzeptéiert, gëtt se an d\'Inhaltsvirbereedung weidergeleet.',
  'trainingRequest.fieldTitle': 'Titel vun der Formatioun',
  'trainingRequest.fieldDescription': 'Beschreiwung vum Besoin',
  'trainingRequest.fieldJustification': 'Begrënnung / Objektiver',
  'trainingRequest.fieldDate': 'Gewënscht Datum',
  'trainingRequest.datePlaceholder': 'z.B. T2 2026',
  'trainingRequest.fieldFormateur': 'Formateur vun der Institutioun',
  'trainingRequest.formateurPlaceholder': 'E Formateur wielen…',
  'trainingRequest.formateursEmpty': 'Kee Formateurkont fir Är Institutioun registréiert.',
  'trainingRequest.titleRequired': 'Gitt w.e.g. en Titel un (mindestens 2 Zeechen).',
  'trainingRequest.sent': 'Ufro gespäichert. Dir gitt per E-Mail iwwer d\'Decisioun informéiert.',
  'trainingRequest.sendError': 'D\'Ufro konnt net gespäichert ginn. Préift Är Verbindung oder Rechter.',
  'trainingRequest.submit': 'Ufro schécken',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Weider',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Versioun {{label}} aktivéiert (ëffentlech)',
  'eduflow.versionHistory.activateTitle': '{{label}} aktivéieren?',
  'eduflow.versionHistory.blocks': '{{count}} Bléck',
  'eduflow.versionHistory.confirmActivate': 'Aktivéieren',
  'eduflow.versionHistory.copyFailed': 'Kopéieren gescheitert',
  'eduflow.versionHistory.copyHtml': 'HTML kopéieren',
  'eduflow.versionHistory.copyPlain': 'Nëmmen Text kopéieren',
  'eduflow.versionHistory.copyPublicPlayer': 'Ëffentleche Link kopéieren',
  'eduflow.versionHistory.copyPublicPlayerError': 'Ëffentleche Link konnt net kopéiert ginn',
  'eduflow.versionHistory.count': '{{count}} Versioun(en)',
  'eduflow.versionHistory.createError': 'Feeler beim Erstellen vun der Versioun',
  'eduflow.versionHistory.createMajor': 'Haaptversioun',
  'eduflow.versionHistory.createMajorSuccess': 'Haaptversioun erstallt',
  'eduflow.versionHistory.createMinor': 'Ënnerversioun',
  'eduflow.versionHistory.createMinorSuccess': 'Ënnerversioun erstallt',
  'eduflow.versionHistory.daysAgo': 'virun {{count}} Deeg',
  'eduflow.versionHistory.deleteError': 'Feeler beim Läschen',
  'eduflow.versionHistory.deleteSuccess': 'Versioun geläscht',
  'eduflow.versionHistory.deleteTitle': '{{label}} läschen?',
  'eduflow.versionHistory.deleteWarning': 'Dës Aktioun ass net réckgängeg ze maachen.',
  'eduflow.versionHistory.duplicateConfirm': 'Duplizéieren',
  'eduflow.versionHistory.duplicateError': 'Feeler beim Duplizéieren',
  'eduflow.versionHistory.duplicateMinor': 'Duplizéieren (Ënner-)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': '{{label}} duplizéieren?',
  'eduflow.versionHistory.empty': 'Keng Versiounen erstallt.',
  'eduflow.versionHistory.emptyHint': 'Späichert eng Haaptversioun oder Ënnerversioun fir se hei ze fannen.',
  'eduflow.versionHistory.hoursAgo': 'virun {{count}} Stonn',
  'eduflow.versionHistory.latest': 'Neist',
  'eduflow.versionHistory.minutesAgo': 'virun {{count}} Min.',
  'eduflow.versionHistory.newFromCurrent': 'Nei Versioun vum aktuelle Contenu',
  'eduflow.versionHistory.openPublicPlayer': 'Ëffentleche Player opmaachen',
  'eduflow.versionHistory.preview': 'Virschau',
  'eduflow.versionHistory.previewBlocks': '{{count}} Bléck',
  'eduflow.versionHistory.previewEdges': '{{count}} Verbindungen',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Virschau {{label}}',
  'eduflow.versionHistory.public': 'Ëffentlech Versioun',
  'eduflow.versionHistory.publicActive': 'Aktiv fir Léierend',
  'eduflow.versionHistory.setPublic': 'Aktivéieren',
  'eduflow.versionHistory.startDescModalTitle': 'Beschreiwung — Startblock ({{version}})',
  'eduflow.versionHistory.yesterday': 'gëschter',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Iwwersetzung leeft…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Kreesfërmege Wee erkannt',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} Feeler',
  'designer.validation.warningCount': '{{count}} Warnung(en)',
  'designer.validation.titleRequired': 'De Block « {{type}} » brauch en Titel.',
  'designer.validation.summary': 'Verlaf: {{blocks}} Block/Bléck, {{edges}} Verbindung(en).',
  'designer.validation.multipleStart': 'Méi Start-Bléck ({{count}}): nëmmen ee ass erlaabt.',
  'designer.validation.noOutgoing': 'De Block « {{type}} » huet keng ausgoend Verbindung zu engem nächste Block.',
  'designer.validation.tooManyOutputs': '« {{label}} »: ze vill ausgoend Verbindungen ({{count}}, Maximum {{max}}).',
  'designer.validation.deadEndBranch': 'Aascht ouni Wee zu engem Enn-Block (vu « {{label}} »).',
  'designer.validation.moreDeadEnds': '… an nach {{count}} aner(en) Aascht ouni Ausgang zum Enn.',
  'designer.validation.noConvergence': "D'Aascht vun « {{label}} » lafen net an engem gemengen Block zesummen.",
  'designer.validation.orphanBlock': 'Block vum Haaptgraph isoléiert: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Den Enn-Block « {{title}} » huet nach eng ausgoend Verbindung.',
  'designer.validation.noScoring': 'Bewäertungsastellungen fir « {{title}} » net definéiert.',
  'designer.validation.emptyText': 'Den Textblock « {{title}} » ass eidel.',
  'designer.validation.textTooLong':
    'Den Textblock « {{title}} » gëtt op {{lines}} Zeilen geschat. Recommandéiert: {{min}} bis {{max}} Zeilen.',
  'designer.validation.splitTextBlock': 'Trennen',
  'designer.validation.textSplitNotNeeded':
    'Dëse Block iwwerschreit den empfohlene Lies-Schwell net.',
  'designer.validation.textSplitUnable':
    'Keng zouverlässeg automatesch Trennung fir dëse Block méiglech.',
  'designer.validation.textSplitComplexFlow':
    'Automatesch Trennung net verfügbar fir dëse Block (méi/bedéngt Verzweigungen).',
  'designer.validation.textSplitDone': '{{count}} Textbléck erstallt',
  'designer.validation.textSplitConfirmTitle':
    'Dëse Textblock automatesch trennen?',
  'designer.validation.textSplitConfirmMessage':
    'De Block « {{title}} » gëtt op {{lines}} Zeilen geschat. Mir trennen en an {{parts}} Bléck (op Basis vu Punktuatioun) a setzen se direkt drënner an der richteger Reiefolleg.',
  'designer.validation.textSplitConfirmAction': 'Block trennen',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Standardstëmm',
  'voicePanel.provider': 'Fournisseur: Mistral AI',
  'voicePanel.hint': 'Wielt d\'Stëmm fir d\'Erzielung vun de Bléck.',
  'voicePanel.translationFrenchFallback':
    'Iwwersat Audio: ouni hei eng Standardstëmm ze späicheren, gëtt fir Franséisch d’Stëmm « Marie - sad » (Mistral) benotzt.',
  'voicePanel.search': 'Sichen…',
  'voicePanel.allLanguages': 'All Sproochen',
  'voicePanel.loading': 'Stëmme gi gelueden…',
  'voicePanel.loadError': 'Stëmme konnten net geluede ginn',
  'voicePanel.empty': 'Keng Stëmm verfügbar.',
  'voicePanel.emptyHint': 'Iwwerpréift de Mistral-API-Schlëssel ënner Administratioun → Integratiounen.',
  'voicePanel.noResults': 'Keng Stëmm passt zu Ärer Sich.',
  'voicePanel.saved': 'Standardstëmm gespäichert',
  'voicePanel.saveError': 'Feeler beim Späicheren',
  'voicePanel.default': 'Standard',
  'voicePanel.female': 'Weiblech',
  'voicePanel.male': 'Männlech',
  'voicePanel.playPreview': 'Stëmm testen',
  'voicePanel.stopPreview': 'Stoppen',
  'voicePanel.noPreview': 'Virschau net verfügbar',
  'voicePanel.previewLoading': 'Gëtt generéiert…',
  'voicePanel.previewGenerateError': "D'Audiovirschau konnt net generéiert ginn.",
  'voicePanel.previewPlayError': 'Ofspillen ass feelgeschloen.',
  'voicePanel.filterNoVoicesForLang':
    'Keng Stëmm am Katalog passt zu dëser Sprooch — probéiert «All Sproochen».',
  'episodeEditor.voicePicker.blockVoice': 'Voice for this block',
  'episodeEditor.voicePicker.defaultVoice': 'Default voice',
  'episodeEditor.voicePicker.voices': 'voices',
  'episodeEditor.voicePicker.searchPlaceholder': 'Search a voice…',
  'episodeEditor.voicePicker.noVoiceFound': 'No voice found',
  'episodeEditor.voicePicker.female': 'Female',
  'episodeEditor.voicePicker.male': 'Male',
  'episodeEditor.voicePicker.stop': 'Stop',
  'episodeEditor.voicePicker.preview': 'Preview voice',
  'episodeEditor.voicePicker.stopPlayback': 'Stop playback',
  'episodeEditor.errors.notFound': 'Episode not found',
  'episodeEditor.errors.titleRequired': 'Title is required.',
  'episodeEditor.errors.saveFailed': 'Error saving.',
  'episodeEditor.errors.noVoicesAvailable': 'No voices available. Enter the API key in Administration → Platform integrations, then reload the page.',
  'episodeEditor.errors.selectVoiceFirst': 'Select a voice in the "Voice" tab before generating voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'One or more blocks have an invalid voice. Open the per-block voice selector or reload the voice list.',
  'episodeEditor.errors.addTextFirst': 'Add text before generating audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audio generation failed. {{message}} (displayed duration = estimate.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error during audio generation. Displayed duration = estimate.',
  'episodeEditor.success.published': 'Episode published!',
  'episodeEditor.success.draftSaved': 'Draft saved',
  'episodeEditor.success.audioGenerated': 'Audio generated with {{count}} block(s)',
  'episodeEditor.tabs.edit': 'Edit',
  'episodeEditor.tabs.chapters': 'Chapters',
  'episodeEditor.tabs.voice': 'Voice',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Undo (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Redo (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generating…',
  'episodeEditor.toolbar.generateAudio': 'Generate audio',
  'episodeEditor.toolbar.save': 'Save',
  'episodeEditor.toolbar.publish': 'Publish',
  'episodeEditor.titlePlaceholder': 'Episode title…',
  'episodeEditor.status.published': 'Published',
  'episodeEditor.status.draft': 'Draft',
  'episodeEditor.warnings.mistralNotConfigured': 'Episode voiceover uses Mistral Voxtral TTS. Add the Mistral key in Administration → Platform integrations, then reload this page.',
  'episodeEditor.warnings.mistralNoVoices': 'The Mistral key is recognized but the API returned no voices. Check backend logs.',
  'episodeEditor.warnings.voiceLoadFailed': 'Unable to load voice list. Check Mistral configuration in Administration → Platform integrations.',
  'episodeEditor.warnings.voiceOffDisabled': 'Without a real voice, voiceover generation remains disabled.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthesis via Mistral Voxtral TTS. Choose the voice in the "Voice" tab or per block.',
  'episodeEditor.edit.coverImage': 'Cover image',
  'episodeEditor.edit.coverAlt': 'Episode cover',
  'episodeEditor.edit.deleteCover': 'Delete',
  'episodeEditor.edit.dropOrClickImage': 'Click or drop an image',
  'episodeEditor.edit.selectedVoice': 'Selected voice',
  'episodeEditor.edit.selectVoice': 'Select a voice',
  'episodeEditor.edit.defaultVoiceByLang': 'Default voice (podcast language)',
  'episodeEditor.edit.lastAudioGenerated': 'Last generated audio',
  'episodeEditor.edit.audioNotFound': 'Audio file not found — regenerate the voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generating…',
  'episodeEditor.edit.generateVoiceOff': 'Generate voiceover',
  'episodeEditor.chapters.title': 'Chapters',
  'episodeEditor.chapters.add': 'Add',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapter {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} block(s)',
  'episodeEditor.chapters.readByAi': 'Read chapter title',
  'episodeEditor.chapters.readByAiHint':
    'When off, the AI does not read this chapter title aloud. The chapter blocks stay included in the voiceover.',
  'episodeEditor.voiceTab.availableVoices': 'Available voices',
  'episodeEditor.podcasts.linked': 'Linked podcasts',
  'episodeEditor.podcasts.assign': 'Assign',
  'episodeEditor.podcasts.noneAvailable': 'No podcast available',
  'episodeEditor.podcasts.close': 'Close',
  'episodeEditor.podcasts.noneLinked': 'No linked podcast',
  'episodeEditor.podcasts.assignHint': 'Assign this episode to one or more podcasts',
  'episodeEditor.podcasts.remove': 'Remove',
  'episodeEditor.editor.addTextBlock': 'Add a text block',
  'episodeEditor.editor.scriptPlaceholder': 'Start writing your script…',
  'episodeEditor.editor.addBlock': 'Add a block',
  'episodeEditor.player.loop': 'Loop',
  'episodeEditor.player.playbackSpeed': 'Playback speed',
  'episodeEditor.player.collapse': 'Collapse',
  'episodeEditor.player.expand': 'Expand',
  'episodeEditor.player.waveformHidden': 'Waveform hidden',
  'podcast.episodes.listen': 'Listen',
  'podcast.episodes.noAudioShort': 'No audio available',
  'podcast.episodes.edit': 'Edit',
  'podcast.episodes.delete': 'Delete',
  'podcast.episodes.unmute': 'Unmute',
  'podcast.episodes.mute': 'Mute',
  'podcast.episodes.totalEpisodes': '{{count}} episode(s) total',
  'eduflow.designer.toast.blockCopied': 'Block kopéiert',
  'eduflow.designer.toast.blockPasted': 'Block agefüügt',
  'eduflow.designer.toast.blockDuplicated': 'Block duplizéiert',
  'eduflow.designer.toast.connectionUpdated': 'Verbindung aktualiséiert',
  'eduflow.designer.toast.flowExported': 'Parcours exportéiert',
  'eduflow.designer.toast.flowImported': 'Parcours importéiert',
  'eduflow.designer.toast.invalidFile': 'Ongëlteg Datei',
  'eduflow.designer.untitled': 'Ouni Titel',
  'eduflow.designer.importedFlow': 'Importéierte Parcours',
  'eduflow.designer.collaborator.online': 'Online',
  'eduflow.designer.collaborator.offline': 'Offline',
  'eduflow.designer.collaborator.defaultName': 'Benotzer',
  'eduflow.designer.collaborator.editor': 'Editeur',
  'eduflow.designer.collaborator.viewer': 'Lieeser',
  'eduflow.designer.resizePanel': 'Panel-Gréisst änneren',
  'eduflow.designer.published': 'Publizéiert',
  'eduflow.designer.draft': 'Brouillon',
  'eduflow.designer.quizOutput.success': 'Bestand',
  'eduflow.designer.quizOutput.failure': 'Net bestand',
  'eduflow.designer.handle.default': 'Standard',
  'eduflow.designer.genError': 'Feeler bei der Generéierung. Versicht et nach eng Kéier.',
  'trainerTour.welcome.title': 'Wëllkomm an Ärem Formateur-Beräich',
  'trainerTour.welcome.description':
    'Dës kuerz Visite weist den Dashboard an de Menu. Beim éischte Opmoden vun all Säit kritt Dir e Kuerz-Hiwäis mam Wichtegsten. Dir kënnt e Schrëtt mat der X zoumaachen — d’Visite fänkt net nees un.',
  'trainerTour.dashboard.header.title': 'Äre Dashboard',
  'trainerTour.dashboard.header.description':
    'Iwwerbléck iwwer Är Aktivitéit: Kennzuele a Kierzel fir schnell Inhalt ze erstellen.',
  'trainerTour.dashboard.actions.title': 'Inhalt erstellen',
  'trainerTour.dashboard.actions.description':
    'Kierzel fir en neie EduFlow, Podcast oder Parcours — ouni duerch de Menu ze fueren.',
  'trainerTour.dashboard.stats.title': 'Indicateuren',
  'trainerTour.dashboard.stats.description':
    'Formatiounen, publizéiert Parcoursen, Léierenden a Podcasts: Äre Katalog op e Bléck.',
  'trainerTour.dashboard.activity.title': 'Rezent Aktivitéit',
  'trainerTour.dashboard.activity.description':
    'Lescht geännert Inhalter. Klickt eng Zeil un fir d’Formatioun, de Podcast oder de Parcours nees opzemaachen.',
  'trainerTour.sidebar.title': 'Säitemenu',
  'trainerTour.sidebar.description':
    'Iwwerbléck (Accueil, Léierenden, Analysen, Missiounen), Kreatioun (Formatiounen, Podcasts, Parcoursen, Webinaren wann aktiv), Medie-Bibliothéik an Hëllef. Jiddwer Rubrik eemol opmaachen — beim éischte Kontakt gëtt e kontextuelle Hiweis gewisen.',
  'trainerTour.routes.learners.title': 'Léierenden',
  'trainerTour.routes.learners.description':
    'Leit déi an Äre Contenu ageschriwwen sinn, Fortschrëtt, Exporte — Äre Pilote fir den didaktesche Suivi.',
  'trainerTour.routes.analytics.title': 'Analysen',
  'trainerTour.routes.analytics.description':
    'Graphiquen iwwer Engagement an Ofschloss. Passt de Layout un, wat fir Iech comptéiert.',
  'trainerTour.routes.eduflow.title': 'Formatiounen (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Interaktiv Formatioune kreéieren a geréieren: Versiounen, Publizéierung an Deelen — d’Häerzstéck vun der didaktescher Gestaltung.',
  'trainerTour.routes.podcasts.title': 'Podcasts',
  'trainerTour.routes.podcasts.description':
    'Shows, Episoden, Siichtbarkeet an Deelen mat Gruppen oder Léierende vun Ärer Institutioun.',
  'trainerTour.routes.parcours.title': 'Parcoursen',
  'trainerTour.routes.parcours.description':
    'Oflaf vu Formatiounen a Ressourcen: laang Parcoursen strukturéieren an Zilgruppen zouweisen.',
  'trainerTour.routes.videos.title': 'Videoen',
  'trainerTour.routes.videos.description':
    'Video-Bibliothéik fir Formatiounen a Dokumenter.',
  'trainerTour.routes.sounds.title': 'Audio',
  'trainerTour.routes.sounds.description':
    'Audio-Fichieren a Taggen fir Podcasts a Sound-Bléck an de Parcoursen.',
  'trainerTour.routes.images.title': 'Biller',
  'trainerTour.routes.images.description':
    'Zentraliséiert Biller: Import, Dossieren a Wiederverwendung am Contenu.',
  'trainerTour.routes.documents.title': 'Dokumenter',
  'trainerTour.routes.documents.description':
    'PDFen an Annexen, mat zielgruppegerichtem Deelen un Léierend wann néideg.',
  'trainerTour.routes.help.title': 'Hëllef',
  'trainerTour.routes.help.description':
    'Artikelen, Tutorielle a Guide fir LyrraStudio. Kommt zréck wann eng Funktioun onklor ass.',
  'trainerTour.routes.producerAssignments.title': 'Produktiouns-Missiounen',
  'trainerTour.routes.producerAssignments.description':
    'Wann d’RH Iech Katalog-Inhalter fir d’Produktioun zouweist, stinn se hei mat dem Fortschrëtt.',
  'trainerTour.routes.webinars.title': 'Webinaren',
  'trainerTour.routes.webinars.description':
    'Live-Sessionen planen, Invitatiounen a Stream-Linken fir Är Gruppen.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Weider',
  'trainerTour.driver.prev': 'Zréck',
  'trainerTour.driver.done': 'Fäerdeg',
  'trainerTour.driver.gotIt': 'Verstanen',
  'player.lightMode': 'Hell Modus',
  'player.darkMode': 'Donkel Modus',
};

const ukTranslations: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Панель керування',
  'nav.projects': 'Проєкти',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Аудіокниги',
  'nav.audio_studio': 'Аудіостудія',
  'nav.store': 'Магазин',
  'nav.analytics': 'Аналітика',
  'nav.users': 'Користувачі',
  'nav.settings': 'Налаштування',
  'nav.profile': 'Профіль',
  'nav.notifications': 'Сповіщення',
  'nav.help': 'Допомога',
  'nav.community': 'Спільнота',
  'nav.library': 'Бібліотека',
  'nav.certificates': 'Сертифікати',
  'nav.institution': 'Установа',
  'nav.groups': 'Групи',
  'nav.learners': 'Учні',
  'nav.myLearning': 'Моє навчання',
  'learner.sidebar.tagline': 'Учень',
  'nav.reports': 'Звіти',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'План навчання',
  'nav.catalog': 'Каталог',
  'nav.competencies': 'Компетенції',
  'nav.budget': 'Бюджет',
  'nav.campaigns': 'Кампанії',
  'nav.home': 'Головна',
  'nav.producerAssignments': 'Продюсерські місії',
  'nav.videos': 'Відео',
  'nav.sounds': 'Аудіо',
  'nav.images': 'Зображення',
  'nav.documents': 'Документи',
  'nav.podcasts': 'Подкасти',
  'nav.parcours': 'Маршрути',
  'nav.eduflowList': 'Курси',
  'nav.section.pilotage': 'Керування',
  'nav.section.creation': 'Створення',
  'nav.section.medias': 'Медіа',
  'nav.section.aide': 'Допомога',
  'sidebar.brandTagline': 'Навчання',
  'sidebar.logoInstitutionAlt': 'Логотип організації',
  'sidebar.expand': 'Розгорнути меню',
  'sidebar.collapse': 'Згорнути меню',
  'sidebar.expandAria': 'Розгорнути бічну панель',
  'sidebar.collapseAria': 'Згорнути бічну панель',
  'learner.sidebar.collapse': 'Згорнути',
  'learner.sidebar.expandAria': 'Розгорнути бічну панель',
  'learner.sidebar.collapseAria': 'Згорнути бічну панель',
  'dashboard.title': 'Панель керування',
  'dashboard.welcome': 'Вітаємо, {{name}}!',
  'dashboard.welcomeSub': 'Ось огляд вашого робочого простору.',
  'dashboard.projects': 'Проєкти',
  'dashboard.subtitle': 'Керуйте курсами та відстежуйте прогрес учнів.',
  'dashboard.newProject': 'Новий курс',
  'dashboard.newEduFlow': 'Новий маршрут',
  'dashboard.uploadMedia': 'Завантажити медіа',
  'dashboard.viewDocs': 'Документація',
  'dashboard.revenue': 'Дохід',
  'dashboard.eduflows': 'Курси',
  'dashboard.learners': 'Учні',
  'dashboard.recentActivity': 'Остання активність',
  'dashboard.recentProjects': 'Останні проєкти',
  'dashboard.noRecentProjects': 'Немає останніх проєктів',
  'dashboard.activity': 'Активність',
  'dashboard.activityEmpty': 'Немає останньої активності у курсах або подкастах.',
  'dashboard.loadError': 'Не вдалося завантажити дані панелі керування.',
  'dashboard.publishedFlows': 'Опубліковані маршрути',
  'dashboard.roleSpecific': 'Для цієї ролі доступна окрема панель.',
  'dashboard.kindPodcast': 'Подкаст',
  'pages.analytics.title': 'Аналітика',
  'pages.analytics.description': 'Панелі та статистика',
  'pages.analytics.subtitle':
    'Фільтруйте дані, перетягуйте графіки для зміни порядку та змінюйте розмір віджетів під свій спосіб роботи.',
  'pages.analytics.filters.period': 'Період',
  'pages.analytics.filters.flow': 'Курс',
  'pages.analytics.filters.allFlows': 'Усі курси',
  'pages.analytics.period.7d': 'Останні 7 днів',
  'pages.analytics.period.30d': 'Останні 30 днів',
  'pages.analytics.period.90d': 'Останні 90 днів',
  'pages.analytics.period.365d': 'Останні 12 місяців',
  'pages.analytics.addChart': 'Додати діаграму',
  'pages.analytics.resetLayout': 'Скинути розташування',
  'pages.analytics.dragHint': 'Перетягніть, щоб змінити порядок',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Прибрати цю діаграму',
  'pages.analytics.kind.completion_line': 'Завершення з часом',
  'pages.analytics.kind.learners_bar': 'Учні за модулем',
  'pages.analytics.kind.status_pie': 'Розподіл за статусами',
  'pages.analytics.kind.engagement_area': 'Час залучення',
  'pages.analytics.series.completion': 'Рівень завершення (%)',
  'pages.analytics.series.learners': 'Учні',
  'pages.analytics.series.hours': 'Години',
  'pages.analytics.status.completed': 'Завершено',
  'pages.analytics.status.in_progress': 'Триває',
  'pages.analytics.status.dropped': 'Покинуто',
  'pages.analytics.status.not_started': 'Не розпочато',
  'pages.analytics.loadError': 'Не вдалося завантажити дані аналітики.',
  'pages.analytics.retry': 'Повторити',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'Пн',
  'pages.analytics.mock.wd1': 'Вт',
  'pages.analytics.mock.wd2': 'Ср',
  'pages.analytics.mock.wd3': 'Чт',
  'pages.analytics.mock.wd4': 'Пт',
  'pages.analytics.mock.wd5': 'Сб',
  'pages.analytics.mock.wd6': 'Нд',
  'pages.analytics.emptyCharts': 'Додайте хоча б одну діаграму, щоб бачити дані.',
  'pages.analytics.comingSoonDesc':
    'Переглядайте статистику навчання, рівень завершення, залученість учнів та загальні показники.',
  'project.status.draft': 'Чернетка',
  'project.status.published': 'Опубліковано',
  'project.status.archived': 'Архівовано',
  'eduflowList.status.draft': 'Чернетка',
  'eduflowList.status.published': 'Опубліковано',
  'eduflowList.status.archived': 'Архівовано',
  'eduflowList.badge.enterpriseShell': 'Запит організації',
  'eduflowList.badge.enterpriseLinked': 'Організація',
  'eduflowList.badge.toProduce': 'До виробництва',
  // Common buttons
  'btn.save': 'Зберегти',
  'btn.cancel': 'Скасувати',
  'btn.delete': 'Видалити',
  'btn.edit': 'Редагувати',
  'btn.create': 'Створити',
  'btn.add': 'Додати',
  'btn.close': 'Закрити',
  'btn.confirm': 'Підтвердити',
  'btn.back': 'Назад',
  'btn.next': 'Далі',
  'btn.previous': 'Назад',
  'btn.submit': 'Надіслати',
  'btn.search': 'Пошук',
  'btn.filter': 'Фільтр',
  'btn.export': 'Експорт',
  'btn.import': 'Імпорт',
  'btn.download': 'Завантажити',
  'btn.upload': 'Вивантажити',
  'btn.refresh': 'Оновити',
  'btn.retry': 'Повторити',
  'btn.view': 'Переглянути',
  'btn.details': 'Деталі',
  'btn.publish': 'Опублікувати',
  'btn.unpublish': 'Скасувати публікацію',
  'btn.duplicate': 'Дублювати',
  'btn.archive': 'Архівувати',
  'btn.restore': 'Відновити',
  'btn.send': 'Надіслати',
  'btn.apply': 'Застосувати',
  'btn.reset': 'Скинути',
  'btn.select_all': 'Вибрати все',
  'btn.deselect_all': 'Скасувати вибір',
  'btn.load_more': 'Завантажити більше',
  'btn.show_more': 'Показати більше',
  'btn.show_less': 'Показати менше',
  'btn.copy': 'Копіювати',
  'btn.share': 'Поділитися',
  'btn.preview': 'Попередній перегляд',
  'btn.continue': 'Продовжити',
  'btn.skip': 'Пропустити',
  // Form labels
  'form.email': 'Електронна пошта',
  'form.password': 'Пароль',
  'form.password_confirm': 'Підтвердити пароль',
  'form.first_name': 'Ім\'я',
  'form.last_name': 'Прізвище',
  'form.name': 'Ім\'я',
  'form.phone': 'Телефон',
  'form.address': 'Адреса',
  'form.city': 'Місто',
  'form.zip': 'Поштовий індекс',
  'form.country': 'Країна',
  'form.title': 'Заголовок',
  'form.description': 'Опис',
  'form.category': 'Категорія',
  'form.tags': 'Теги',
  'form.status': 'Статус',
  'form.role': 'Роль',
  'form.language': 'Мова',
  'form.date': 'Дата',
  'form.start_date': 'Дата початку',
  'form.end_date': 'Дата закінчення',
  'form.duration': 'Тривалість',
  'form.price': 'Ціна',
  'form.avatar': 'Фото профілю',
  'form.required': 'Обов\'язково',
  'form.optional': 'Необов\'язково',
  'form.select_placeholder': 'Вибрати...',
  'form.search_placeholder': 'Пошук...',
  'form.no_results': 'Немає результатів',
  'form.file_drop': 'Перетягніть файл сюди або натисніть для вибору',
  'form.max_size': 'Максимальний розмір: {{size}}',
  'form.institution': 'Установа',
  'form.department': 'Відділ',
  'form.group': 'Група',
  'form.level': 'Рівень',
  'form.notes': 'Нотатки',
  'form.url': 'URL',
  'form.content': 'Зміст',
  'form.thumbnail': 'Ескіз',
  // Validation errors
  'error.required': 'Це поле обов\'язкове',
  'error.email_invalid': 'Невірна адреса електронної пошти',
  'error.password_min': 'Пароль повинен містити щонайменше {{min}} символів',
  'error.password_match': 'Паролі не збігаються',
  'error.min_length': 'Мінімум {{min}} символів',
  'error.max_length': 'Максимум {{max}} символів',
  'error.min_value': 'Мінімальне значення: {{min}}',
  'error.max_value': 'Максимальне значення: {{max}}',
  'error.invalid_url': 'Невірний URL',
  'error.file_too_large': 'Файл занадто великий',
  'error.file_type': 'Непідтримуваний тип файлу',
  'error.generic': 'Сталася помилка',
  'error.network': 'Помилка мережевого з\'єднання',
  'error.unauthorized': 'Ви повинні бути авторизовані',
  'error.forbidden': 'Доступ заборонено',
  'error.not_found': 'Ресурс не знайдено',
  'error.server': 'Внутрішня помилка сервера',
  'error.conflict': 'Цей ресурс вже існує',
  'error.rate_limit': 'Забагато запитів, будь ласка, зачекайте',
  // Auth
  'auth.login': 'Увійти',
  'auth.logout': 'Вийти',
  'auth.register': 'Зареєструватися',
  'auth.forgot_password': 'Забули пароль?',
  'auth.reset_password': 'Скинути пароль',
  'auth.otp_title': 'Вхід за кодом',
  'auth.otp_sent': 'Код підтвердження надіслано на {{email}}',
  'auth.otp_placeholder': 'Введіть 6-значний код',
  'auth.google_login': 'Продовжити з Google',
  'auth.remember_me': 'Запам\'ятати мене',
  'auth.no_account': 'Ще немає акаунта?',
  'auth.has_account': 'Вже є акаунт?',
  'auth.welcome_back': 'З поверненням, {{name}}!',
  'auth.login_success': 'Вхід успішний',
  'auth.register_success': 'Акаунт успішно створено',
  'auth.logout_success': 'Вихід успішний',
  // Roles
  'role.teacher': 'Викладач',
  'role.institution': 'Установа',
  'role.admin': 'Адміністратор',
  'role.drh': 'Директор з персоналу',
  'role.rh': 'Менеджер з персоналу',
  'role.manager': 'Менеджер',
  'role.collaborator': 'Співробітник',
  'role.learner': 'Учень',
  'dashboard.recent_projects': 'Останні проєкти',
  'dashboard.recent_activity': 'Остання активність',
  'dashboard.quick_actions': 'Швидкі дії',
  'dashboard.stats': 'Статистика',
  'dashboard.total_projects': 'Усього проєктів',
  'dashboard.total_learners': 'Усього учнів',
  'dashboard.total_eduflows': 'Усього EduFlows',
  'dashboard.completion_rate': 'Відсоток завершення',
  'dashboard.active_users': 'Активні користувачі',
  'dashboard.new_registrations': 'Нові реєстрації',
  'dashboard.xp_earned': 'Зароблено XP',
  'dashboard.streak': 'Поточна серія',
  'dashboard.level': 'Рівень {{level}}',
  'dashboard.badges_earned': 'Отримані значки',
  'dashboard.next_level': 'Наступний рівень',
  'dashboard.no_projects': 'Поки що немає проєктів',
  'dashboard.create_first': 'Створіть свій перший проєкт',
  'dashboard.pending_reviews': 'Незавершені оцінювання',
  'dashboard.upcoming_deadlines': 'Майбутні терміни',
  // Podcast — епізоди
  'podcast.episodes.pageTitle': 'Епізоди',
  'podcast.episodes.count': '{{count}} епізод(ів)',
  'podcast.episodes.newEpisode': 'Новий епізод',
  'podcast.episodes.searchPlaceholder': 'Пошук за назвою…',
  'podcast.episodes.filterAllStatuses': 'Усі статуси',
  'podcast.episodes.filterDraft': 'Чернетка',
  'podcast.episodes.filterPublished': 'Опубліковано',
  'podcast.episodes.emptyTitle': 'Епізодів не знайдено',
  'podcast.episodes.emptyHint': 'Створіть перший епізод',
  'podcast.episodes.emptyCreateCta': 'Створити епізод',
  'podcast.episodes.backPodcasts': 'Подкасти',
  'podcast.episodes.paginationTotal': 'Усього {{total}} епізод(ів)',
  'podcast.episodes.pageOf': 'Сторінка {{page}} з {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Видалити епізод?',
  'podcast.episodes.deleteHint': '« {{title}} » буде видалено назавжди.',
  'podcast.episodes.deleteCancel': 'Скасувати',
  'podcast.episodes.deleteConfirm': 'Видалити',
  'podcast.episodes.statusPublished': 'Опубліковано',
  'podcast.episodes.statusDraft': 'Чернетка',
  'podcast.episodes.noDescription': 'Без опису',
  'podcast.episodes.noAudio': 'Без аудіо',
  'podcast.episodes.retry': 'Повторити',
  'podcast.createEpisode.panelTitle': 'Новий епізод',
  'podcast.createEpisode.panelSubtitle':
    'Вкажіть назву та за бажанням опис і обкладинку. Після підтвердження епізод створюється й відкриється редактор.',
  'podcast.createEpisode.titleLabel': 'Назва',
  'podcast.createEpisode.descriptionLabel': 'Опис',
  'podcast.createEpisode.coverLabel': 'Обкладинка (необов’язково)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP або GIF — до 5 МБ.',
  'podcast.createEpisode.coverChoose': 'Обрати зображення',
  'podcast.createEpisode.coverRemove': 'Прибрати зображення',
  'podcast.createEpisode.titlePlaceholder': 'напр. Вступ до модуля безпеки',
  'podcast.createEpisode.descriptionPlaceholder': 'Коротко про зміст… (необов’язково)',
  'podcast.createEpisode.validate': 'Створити й відкрити редактор',
  'podcast.createEpisode.titleRequired': 'Назва обов’язкова.',
  'podcast.createEpisode.error': 'Не вдалося створити епізод.',
  'podcast.createEpisode.uploadCoverError': 'Епізод створено, але завантаження обкладинки не вдалося.',
  'podcast.editor.defaultNewTitle': 'Новий епізод',
  // EduFlow
  'eduflow.title': 'Редактор EduFlow',
  'eduflow.new_flow': 'Новий курс',
  'eduflow.list.title': 'Курси',
  'eduflow.list.subtitle': 'Створюйте інтерактивні навчальні траєкторії',
  'eduflow.list.searchPlaceholder': 'Пошук курсу…',
  'eduflow.list.filterAll': 'Усі',
  'eduflow.list.filterDraft': 'Чернетки',
  'eduflow.list.filterPublished': 'Опубліковані',
  'eduflow.list.filterArchived': 'Архів',
  'eduflow.list.viewModeGrid': 'Плитковий вигляд',
  'eduflow.list.viewModeList': 'Список',
  'eduflow.list.viewByCategory': 'Перегляд за категоріями',
  'eduflow.list.import': 'Імпорт',
  'eduflow.list.importHint':
    'Файл .lyrra (медіа включено) або застарілий JSON-експорт Lyrra V1.',
  'eduflow.list.importSuccess': 'Курс імпортовано. Відкриваємо редактор.',
  'eduflow.list.importError':
    'Не вдалося імпортувати — перевірте файл (.lyrra або коректний JSON-експорт, принаймні один блок).',
  'eduflow.list.categoryBack': 'Усі категорії',
  'eduflow.list.categoryBrowseHint': 'Оберіть категорію, щоб побачити її курси.',
  'eduflow.list.categoryFormationCount': '{{count}} курс(ів)',
  'eduflow.list.categoryEmptyFiltered':
    'Немає курсів у цьому вигляді (активний фільтр або пошук).',
  'eduflow.list.uncategorizedCardHint': 'Курси без мітки категорії.',
  'eduflow.list.uncategorized': 'Без категорії',
  'eduflow.list.manageCategories': 'Керувати категоріями',
  'eduflow.list.categoriesPanelTitle': 'Категорії курсу',
  'eduflow.list.noCategoryAvailable': 'Немає доступних категорій.',
  'eduflow.list.categoriesSaveError': 'Не вдалося зберегти категорії.',
  'eduflow.list.emptyFilteredTitle': 'Нічого не знайдено',
  'eduflow.list.emptyFilteredHint': 'Змініть критерії пошуку або фільтри.',
  'eduflow.list.emptyFirstTitle': 'Створіть перший курс',
  'eduflow.list.emptyFirstHint': 'Інтерактивне навчання у візуальному редакторі.',
  'eduflow.list.emptyCreateCta': 'Створити перший курс',
  'eduflow.list.deleteToTrashTitle': 'Перемістити курс у кошик?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » буде прибрано зі списку (логічне видалення). Заклад зможе відновити його з інституційного кошика.',
  'eduflow.list.deleteToTrashConfirm': 'У кошик',
  'eduflow.list.blocksCount': '{{count}} блоків',
  'eduflow.list.noContent': 'Без вмісту',
  'eduflow.list.minutesShort': '{{count}} хв',
  'eduflow.list.tooltip.openMissions': 'Відкрити виробничі завдання',
  'eduflow.list.tooltip.edit': 'Редагувати',
  'eduflow.list.shellHint':
    'Картка визначена вашою організацією — створіть EduFlow у розділі виробничих завдань.',
  'eduflow.list.coverLabel': 'Зображення обкладинки',
  'eduflow.list.coverRemove': 'Прибрати обкладинку',
  'eduflow.list.coverUpload': 'Обрати зображення',
  'eduflow.list.flowProperties': 'Заголовок, опис та обкладинка',
  'eduflow.list.propertiesPanelTitle': 'Властивості курсу',
  'eduflow.list.duplicatePanelTitle': 'Дублювати курс',
  'eduflow.list.duplicatePanelSubtitle': 'Початковий курс: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'копія',
  'eduflow.list.duplicateSuccess': 'Курс дубльовано.',
  'eduflow.list.duplicateCoverHint':
    'Обкладинку й навчальний вміст початкового курсу буде перенесено.',
  'eduflow.list.propertiesSaveError': 'Не вдалося зберегти властивості.',
  'eduflow.list.propertiesSaved': 'Властивості збережено.',
  'eduflow.list.propertiesUploadError': 'Не вдалося завантажити зображення.',
  'eduflow.collaborativeBadge': 'Спільний',
  'eduflow.duplicateFlowError': 'Не вдалося дублювати курс.',
  'eduflow.createFormation.panelTitle': 'Новий курс',
  'eduflow.createFormation.panelSubtitle':
    'Введіть назву та опис. Після підтвердження курс буде створено і відкриється дизайнер потоку.',
  'eduflow.createFormation.titleLabel': 'Назва',
  'eduflow.createFormation.descriptionLabel': 'Опис',
  'eduflow.createFormation.titlePlaceholder': 'напр. Вступ до модуля безпеки',
  'eduflow.createFormation.descriptionPlaceholder': 'Цілі, аудиторія тощо (необов’язково)',
  'eduflow.createFormation.validate': 'Створити й відкрити дизайнер',
  'eduflow.createFormation.titleRequired': 'Назва обов’язкова.',
  'eduflow.createFormation.error': 'Не вдалося створити курс.',
  'eduflow.shareFormation.title': 'Поділитися курсом',
  'eduflow.shareFormation.publicLinkTitle': 'Публічне посилання (без входу)',
  'eduflow.shareFormation.publicLinkCopy': 'Копіювати посилання',
  'eduflow.shareFormation.publicLinkCopied': 'Скопійовано',
  'eduflow.shareFormation.publishRequired':
    'Опублікуйте курс, щоб отримати посилання без входу в систему.',
  'eduflow.shareFormation.embedTitle': 'Кнопка для вбудовування та модальний плеєр',
  'eduflow.shareFormation.embedHint':
    'Вставте цей блок на сайт. Скрипт додавайте лише один раз на сторінку; для інших курсів додайте кнопки, скопіювавши рядок кнопки та змінивши URL у data-lyrra-eduflow-url. URL плеєра містить sidebar=0 (зміст згорнуто в модальному вікні).',
  'eduflow.shareFormation.embedCopy': 'Копіювати HTML',
  'eduflow.shareFormation.embedCopied': 'Скопійовано',
  'eduflow.shareFormation.embedCloseAria': 'Закрити',
  'eduflow.shareFormation.embedButtonLabel': 'Відкрити: {{title}}',
  'eduflow.shareFormation.embedToggle': 'Вбудувати на сайт',
  'eduflow.shareFormation.removeAccessSuccess': 'Доступ для учня відкликано.',
  'eduflow.shareFormation.removeAccessError': 'Не вдалося відкликати доступ учня.',
  'eduflow.shareFormation.assignSuccess': 'Курс поширено, запрошення надіслано.',
  'eduflow.shareFormation.assignError': 'Не вдалося призначити цього учня.',
  'eduflow.shareFormation.createAssignSuccess': 'Акаунт створено/прив’язано, курс призначено, запрошення надіслано.',
  'eduflow.shareFormation.createLearnerError': 'Не вдалося створити учня.',
  'eduflow.shareFormation.selectExistingLearner': 'Виберіть існуючого учня',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Пошук учня (імʼя, прізвище, email)',
  'eduflow.shareFormation.loadingLearners': 'Завантаження учнів...',
  'eduflow.shareFormation.noLearnerAvailable': 'Немає доступних учнів.',
  'eduflow.shareFormation.hideQuickCreate': 'Приховати швидке створення',
  'eduflow.shareFormation.createNewLearner': 'Створити нового учня',
  'eduflow.shareFormation.firstNameRequired': 'Імʼя *',
  'eduflow.shareFormation.lastNameRequired': 'Прізвище *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': 'Створити, поділитися та надіслати запрошення',
  'eduflow.shareFormation.assignedLearnersCount_one': 'Призначено {{count}} учня',
  'eduflow.shareFormation.assignedLearnersCount_few': 'Призначено {{count}} учні',
  'eduflow.shareFormation.assignedLearnersCount_many': 'Призначено {{count}} учнів',
  'eduflow.shareFormation.assignedLearnersCount_other': 'Призначено {{count}} учня',
  'eduflow.shareFormation.noAssignedLearner': 'Для цього курсу ще не призначено учнів.',
  'eduflow.shareFormation.removeAccessTitle': 'Відкликати доступ',
  'eduflow.shareFormation.inviteEmailsTitle': 'Запросити електронною поштою (існуючий акаунт)',
  'eduflow.shareFormation.inviteEmailsHint':
    'Одна чи кілька адрес: лише облікові записи LyrraStudio (у т. ч. іншої установи). Без акаунта не додаються — скористайтеся «Створити нового учня».',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'По одній адресі в рядку або через кому\nexample@lyrra.fr, other@company.com',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Додати ці акаунти до курсу',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Введіть принаймні одну коректну адресу e-mail.',
  'eduflow.shareFormation.inviteEmailsError': 'Не вдалося додати адреси.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} акаунт(ів) додано.',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} без акаунта LyrraStudio.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} уже призначено.',
  'eduflow.participant.flowTitle': 'Навчальний маршрут',
  'eduflow.participant.stepWelcome': 'Ласкаво просимо',
  'eduflow.participant.stepModule1': 'Модуль 1',
  'eduflow.participant.stepEvaluation': 'Оцінювання',
  'eduflow.participant.stepContentPlaceholder': 'Інтерактивний контент етапу для учня.',
  'eduflow.edit_flow': 'Редагувати маршрут',
  'eduflow.flow_name': 'Назва маршруту',
  'eduflow.block_palette': 'Палітра блоків',
  'eduflow.canvas': 'Полотно',
  'eduflow.properties': 'Властивості',
  'eduflow.publish': 'Опублікувати',
  'eduflow.publish_confirm': 'Ви впевнені, що хочете опублікувати цей маршрут?',
  'eduflow.save_draft': 'Зберегти чернетку',
  'eduflow.preview': 'Попередній перегляд',
  'eduflow.versions': 'Версії',
  'eduflow.add_block': 'Додати блок',
  'eduflow.remove_block': 'Видалити блок',
  'eduflow.connect_blocks': 'З\'єднати блоки',
  'eduflow.undo': 'Скасувати',
  'eduflow.redo': 'Повторити',
  'eduflow.zoom_in': 'Збільшити',
  'eduflow.zoom_out': 'Зменшити',
  'eduflow.fit_view': 'Підібрати вигляд',
  'eduflow.auto_layout': 'Автоматичне розташування',
  'eduflow.unsaved_changes': 'Незбережені зміни',
  'eduflow.blocks.quiz_mcq': 'Тест із вибором',
  'eduflow.blocks.quiz_open': 'Відкрите запитання',
  'eduflow.blocks.video': 'Відео',
  'eduflow.blocks.audio': 'Аудіо',
  'eduflow.blocks.text': 'Текст',
  'eduflow.blocks.evaluation': 'Оцінювання',
  'eduflow.blocks.form': 'Форма',
  'eduflow.blocks.mindmap': 'Ментальна карта',
  'eduflow.blocks.poll': 'Опитування',
  'eduflow.blocks.flashcard': 'Флешкарта',
  'eduflow.blocks.timeline': 'Хронологія',
  'eduflow.blocks.pdf': 'PDF-документ',
  'eduflow.blocks.image': 'Зображення',
  'eduflow.blocks.code': 'Код',
  'eduflow.blocks.whiteboard': 'Дошка',
  'eduflow.blocks.ai_chat': 'Чат зі ШІ',
  'eduflow.blocks.certificate': 'Сертифікат',
  'eduflow.blocks.attendance': 'Відвідуваність',
  'eduflow.blocks.sortable': 'Ранжування',
  'eduflow.blocks.pair_matching': 'Зіставлення пар',
  'eduflow.blocks.drawing': 'Малювання',
  'eduflow.blocks.embed': 'Вбудований вміст',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Умова',
  'eduflow.blocks.timer': 'Таймер',
  'eduflow.blocks.randomizer': 'Рандомізатор',
  'eduflow.blocks.grouping': 'Групування',
  'eduflow.blocks.debate': 'Дебати',
  'eduflow.blocks.roleplay': 'Рольова гра',
  'eduflow.blocks.simulation': 'Симуляція',
  'eduflow.blocks.scenario': 'Сценарій',
  'eduflow.blocks.checklist': 'Контрольний список',
  'eduflow.blocks.kanban': 'Канбан',
  'eduflow.blocks.peer_review': 'Взаємна оцінка',
  // Common
  'common.loading': 'Завантаження...',
  'common.saving': 'Збереження...',
  'common.saved': 'Збережено',
  'common.error': 'Помилка',
  'common.success': 'Успіх',
  'common.warning': 'Увага',
  'common.info': 'Інформація',
  'common.yes': 'Так',
  'common.no': 'Ні',
  'common.or': 'або',
  'common.and': 'і',
  'common.all': 'Усі',
  'common.none': 'Жоден',
  'common.total': 'Усього',
  'common.actions': 'Дії',
  'common.created_at': 'Створено',
  'common.updated_at': 'Змінено',
  'common.sort_by': 'Сортувати за',
  'common.per_page': 'На сторінці',
  'common.page': 'Сторінка',
  'common.of': 'з',
  'common.results': '{{count}} результат(ів)',
  'common.no_data': 'Немає даних',
  'common.coming_soon': 'Незабаром',
  'common.beta': 'Бета',
  'common.new': 'Нове',
  'common.delete_confirm': 'Ця дія незворотна. Продовжити?',
  'common.copied': 'Скопійовано!',
  'common.drag_drop': 'Перетягування',
  'common.online': 'Онлайн',
  'common.offline': 'Офлайн',
  'common.version': 'Версія',
  // Player
  'player.next': 'Далі',
  'player.image.clickForAnnotations': 'Натисніть на зображення, щоб переглянути анотації',
  'player.previous': 'Назад',
  'player.complete': 'Завершити',
  'player.exit': 'Вийти',
  'player.exitConfirmTitle': 'Вийти з курсу?',
  'player.exitConfirmMessage': 'Ваш прогрес буде збережено. Ви впевнені, що хочете вийти?',
  'player.focusLostTitle': 'Курс призупинено',
  'player.focusLostMessage': 'Ви залишили вікно курсу. Час призупинено.',
  'player.focusLostResume': 'Натисніть, щоб продовжити',
  'player.focusLostRemaining': 'Залишилося виходів до блокування: {{remaining}}',
  'player.focusBlockedTitle': 'Курс заблоковано',
  'player.focusBlockedMessage':
    'Ви перевищили дозволену кількість виходів ({{max}}). Ви більше не можете продовжувати цей курс. Зверніться до свого тренера або куратора.',
  'player.cancel': 'Скасувати',
  'player.summary': 'Зміст',
  'player.contents': 'Матеріали',
  'player.progress': 'Прогрес',
  'player.block': 'Блок',
  'player.parallelBranches': 'Паралельні гілки',
  'player.parallelBranchesTitle': 'Що далі?',
  'player.parallelBranchesHint': 'Інші варіанти будуть запропоновані пізніше.',
  'player.parallelBranchShowFull': 'Більше',
  'player.parallelBranchShowLess': 'Менше',
  'player.parallelBranchChoose': 'Відкрити',
  'player.parallelBranchDismiss': 'Закрити',
  'player.parallelBranchDismissHint':
    'Повернутися до блоку. Натисніть «Далі», щоб обрати гілку.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Зробити нотатки...',
  'player.noBlocks': 'Немає блоків для відображення.',
  'player.contentTranslatingTitle': 'Адаптуємо вміст до вашої мови…',
  'player.contentTranslatingHint':
    'Триває переклад. Перше завантаження може тривати трохи довше.',
  'player.textBlockTipsHeading': 'Поради',
  'player.textBlockReadDone': 'Читання завершено',
  'player.defaultFlowTitle': 'Навчання',
  'player.docTitle.error': 'Навчання недоступне',
  'player.docTitle.completed': 'Навчання завершено',
  'player.docTitle.loading': 'Завантаження навчання',
  'player.docTitle.fallback': 'Навчання',
  'player.initError.title': 'Навчання тимчасово недоступне',
  'player.initError.loadFailed': 'Не вдалося завантажити навчання зараз.',
  'player.initError.loadFailedWithDetail':
    'Не вдалося завантажити навчання зараз.{{detail}} Спробуйте ще раз за кілька хвилин.',
  'player.initError.hintsIntro': 'Ви можете:',
  'player.initError.hintRefresh': 'оновити сторінку;',
  'player.initError.hintConnection': 'перевірити підключення до Інтернету;',
  'player.initError.hintRetryLater': 'повторити спробу за кілька хвилин.',
  'api.error.network': 'Не вдалося зв’язатися з сервером. Перевірте з’єднання.',
  'api.error.unexpected': 'Сталася неочікувана помилка.',
  'api.error.badRequest': 'Некоректний запит.',
  'api.error.forbidden': 'Доступ заборонено.',
  'api.error.notFound': 'Ресурс не знайдено.',
  'api.error.conflict': 'Конфлікт — такий ресурс уже існує.',
  'api.error.unprocessable': 'Надіслані дані недійсні.',
  'api.error.tooManyRequests': 'Забагато запитів. Спробуйте ще раз за мить.',
  'api.error.server': 'Внутрішня помилка сервера.',
  'api.error.badGateway': 'Сервіс тимчасово недоступний.',
  'api.error.maintenance': 'Сервіс на обслуговуванні.',
  'api.error.generic': 'Помилка {{status}}',
  'player.textBlockReadMinutes': '{{count}} хв читання',
  'player.textBlockWordCount_one': '{{count}} слово',
  'player.textBlockWordCount_other': '{{count}} слів',
  'player.textBlockNoContent': 'Немає доступного вмісту.',
  'player.blockUnknown': 'Невідомий блок',
  'player.objectivesPanelTitle': 'Навчальні цілі',
  'player.objectivesOpenLabel': 'Переглянути навчальні цілі',
  'player.objectivesCloseLabel': 'Закрити панель цілей',
  'player.objectivesAchievementRate': 'Рівень досягнення',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} цілей досягнуто',
  'player.objectivesNoneDefined': 'Для цього курсу не визначено цілей.',
  'player.objectiveCategory.savoir': 'Знання',
  'player.objectiveCategory.savoirFaire': 'Уміння',
  'player.objectiveCategory.savoirEtre': 'Ставлення',
  'player.objectiveLevel.connaissance': 'Знання',
  'player.objectiveLevel.comprehension': 'Розуміння',
  'player.objectiveLevel.application': 'Застосування',
  'player.objectiveLevel.analyse': 'Аналіз',
  'player.objectiveLevel.synthese': 'Синтез',
  'player.objectiveLevel.evaluation': 'Оцінювання',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Обов’язково',
  'player.objectiveLinkedBlocks': 'Пов’язані блоки',
  'player.objectiveCriterionScore': 'Бал ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Завершення блоку',
  'player.objectiveNoLinkedBlocks':
    'Немає пов’язаного блоку — загальний прогрес не вимірює цю ціль.',
  'player.completion.congratulations': '🎉 Вітаємо!',
  'player.completion.finished': '📋 Курс завершено',
  'player.completion.youCompleted': 'Ви завершили',
  'player.completion.passed': 'Пройдено ✓',
  'player.completion.failed': 'Не зараховано ✗',
  'player.completion.requiredScore': 'Необхідний бал: {{score}}%',
  'player.completion.totalTime': 'Загальний час',
  'player.completion.blocksCompleted': 'Завершено блоків',
  'player.completion.quizPassed': 'Тестів пройдено',
  'player.completion.successRate': 'Відсоток успіху',
  'player.completion.blockCompleted': 'Завершено ✓',
  'player.completion.blockFailed': 'Провалено ✗',
  'player.completion.blockSkipped': 'Пропущено',
  'player.completion.detail': '📊 Деталі по блоку',
  'player.completion.type': 'Тип',
  'player.completion.timeSpent': 'Витрачений час',
  'player.completion.downloadCertificate': '📄 Завантажити сертифікат',
  'player.completion.certificateSoon': 'Сертифікат буде доступний незабаром.',
  'player.completion.restart': '🔄 Почати знову',
  'player.completion.viewDetails': '📊 Переглянути деталі',
  'player.completion.backToDashboard': '← Повернутися до панелі',
  'player.completion.embedDismissHint':
    'Ви завершили модуль. Щоб вийти, натисніть × угорі вікна або кнопку нижче.',
  'player.completion.embedDismissButton': 'Закрити вікно',
  'player.previewBanner': 'Режим попереднього перегляду — це подання відображає реальні дані вашого навчання',
  'player.closePreview': 'Закрити попередній перегляд',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Показати панель',
  'player.toggleSidebarHide': 'Сховати панель',
  'player.sidebarExpand': 'Розгорнути панель',
  'player.sidebarCollapse': 'Згорнути панель',
  'player.keyboardShortcuts': 'Гарячі клавіші',
  'player.keyboardShortcutPrev': 'Попередній блок',
  'player.keyboardShortcutNext': 'Наступний блок',
  'player.keyboardShortcutsHint':
    'Увімкніть «Навігацію з клавіатури» в центрі доступності (кнопка внизу справа). Вимкнено в полях вводу.',
  'player.a11yCenter': 'Центр доступності',
  'player.reviewSummary': 'Переглянути підсумок',
  'player.viewResults': 'Переглянути результати',
  'player.submitFirst': 'Спочатку надішліть',
  'player.phraseRevealCompleteFirst': 'Спочатку перегляньте всі речення',
  'player.phraseReveal.emptyBlock': 'Для цього блоку не налаштовано жодного речення.',
  'player.phraseReveal.revealNextAria':
    'Натисніть або пробіл, щоб показати наступне речення',
  'player.phraseReveal.lastPhraseHint':
    'Останнє речення — натисніть «Далі», щоб продовжити шлях.',
  'player.phraseReveal.morePhrasesHint':
    'Клік, пробіл або стрілка вправо для наступного речення',
  'player.phraseReveal.previousPhrases': 'Попередні речення ({{count}})',
  'player.certificateUnavailable': 'Сертифікат скоро буде доступний.',
  'player.browser.embeddedContentTitle': 'Вбудований вміст',
  'player.browser.noValidUrl': 'Недійсна URL-адреса',
  'player.browser.enterHttpsHint':
    'Укажіть адресу, що починається з https:// (або http://), у налаштуваннях блоку.',
  'player.browser.loadingContent': 'Завантаження вмісту…',
  'player.browser.defaultPageTitle': 'Вбудована вебсторінка',
  'player.browser.continue': 'Далі',
  'player.browser.stepSaved': 'Крок збережено',
  'player.browser.fullscreen': 'На весь екран',
  'player.browser.exitFullscreen': 'Вийти з повноекранного режиму',
  'player.podcast.defaultShowTitle': 'Подкаст',
  'player.podcast.defaultEpisode': 'Епізод',
  'player.podcast.loadError': 'Не вдалося завантажити подкаст.',
  'player.podcast.loadingEpisodes': 'Завантаження епізодів…',
  'player.podcast.playAll': 'Слухати все',
  'player.podcast.nextEpisode': 'Наступний епізод',
  'player.podcast.episodesHeading': 'Епізоди',
  'player.podcast.noEpisodes': 'Немає епізодів.',
  'player.podcast.noAudio': 'Без аудіо',
  'player.podcast.chainPlayback': 'Плейлист · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Оберіть епізод у списку або натисніть «Слухати все».',
  'player.podcast.audioUnavailable': 'Аудіо для цього епізоду недоступне.',
  'player.podcast.episodeCount_one': '{{count}} епізод',
  'player.podcast.episodeCount_other': '{{count}} епізоди',
  'player.renderError': 'Помилка під час відображення блоку: {{blockType}}',
  'player.blocks.defaultWelcome': 'Ласкаво просимо до цього навчання',
  'player.blocks.defaultEndTitle': 'Вітаємо!',
  'player.quiz.selectCorrectAnswer': 'Виберіть правильну відповідь:',
  'player.quiz.correctAnswer': '✓ Правильна відповідь!',
  'player.quiz.incorrectAnswer': '✗ Неправильна відповідь.',
  'player.blockTimer.aria': 'Час для цього блоку',
  'player.blockTimer.modeShortCountup': 'Секундомір',
  'player.blockTimer.modeShortCountdown': 'Зворотний відлік',
  'player.form.successMessage': 'Дякуємо за вашу відповідь!',
  'player.form.noFieldsConfigured': 'У цій формі ще немає налаштованих полів.',
  'player.evaluation.commentsLabel': "Коментарі (необов'язково)",
  'player.evaluation.commentsPlaceholder': 'Поділіться своїм відгуком...',
  'player.evaluation.submitted': 'Оцінювання надіслано',
  'player.evaluation.submit': 'Надіслати оцінювання',
  'player.time.lessThanMinute': 'Менше хвилини',
  'player.time.oneMinute': '1 хвилина',
  'player.time.minutes': '{{count}} хвилин',
  'player.time.shortSeconds': '{{count}} с',
  'player.time.shortMinutes': '{{count}} хв',
  'player.time.shortMinutesSeconds': '{{minutes}} хв {{seconds}} с',
  'player.completion.scoreLabel': 'Оцінка',
  'accessibility.openCenter': 'Відкрити центр доступності',
  'accessibility.center': 'Доступність',
  'accessibility.modalTitle': 'Центр адаптованого читання',
  'accessibility.modalDescription': 'Налаштуйте інтерфейс для слабкого зору, дислексії та зручнішого читання.',
  'accessibility.closeCenter': 'Закрити центр доступності',
  'accessibility.accessPad.connected': 'Lyrra Access Pad підключено',
  'accessibility.accessPad.playGuide': 'Слухати посібник LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Призупинити посібник LyrraPad',
  'accessibility.presets': 'Готові профілі',
  'accessibility.preset.standard.label': 'Стандарт',
  'accessibility.preset.standard.desc': 'Типове відображення програми.',
  'accessibility.preset.lowVision.label': 'Слабкий зір',
  'accessibility.preset.lowVision.desc': 'Сильніший контраст, більший текст і чіткіші візуальні підказки.',
  'accessibility.preset.dys.label': 'Дислексія',
  'accessibility.preset.dys.desc': 'Легше читати з більшим інтервалом і зручнішим шрифтом.',
  'accessibility.typography': 'Налаштування шрифту',
  'accessibility.fontScale': 'Розмір тексту',
  'accessibility.lineHeight': 'Міжрядковий інтервал',
  'accessibility.letterSpacing': 'Інтервал між літерами',
  'accessibility.wordSpacing': 'Інтервал між словами',
  'accessibility.visualAids': 'Візуальні підказки',
  'accessibility.highContrast': 'Високий контраст',
  'accessibility.highContrastDesc': 'Посилює контраст тексту, межі та візуальні орієнтири.',
  'accessibility.underlineLinks': 'Підкреслені посилання',
  'accessibility.underlineLinksDesc': 'Робить посилання та інтерактивні зони помітнішими.',
  'accessibility.dyslexiaFont': 'Шрифт для зручного читання',
  'accessibility.dyslexiaFontDesc': 'Зручніший шрифт для людей з дислексією та втомлених читачів.',
  'accessibility.reducedMotion': 'Зменшення руху',
  'accessibility.reducedMotionDesc': 'Менше анімацій і переходів, щоб зменшити візуальний дискомфорт.',
  'accessibility.mouseMagnifier': 'Лупа для читання',
  'accessibility.mouseMagnifierDesc':
    'Кругла лупа, що слідує за вказівником і збільшує вміст під нею (лише для перегляду).',
  'accessibility.alwaysOn': 'Постійні покращення',
  'accessibility.alwaysOnFocus': 'Помітніший фокус клавіатури',
  'accessibility.alwaysOnLinks': 'Посилання та інтерактивні елементи легше помітити',
  'accessibility.alwaysOnSaved': 'Налаштування автоматично зберігаються на цьому пристрої',
  'accessibility.navigation': 'Навігація',
  'accessibility.keyboardNavigation': 'Навігація з клавіатури',
  'accessibility.keyboardNavigationDesc':
    'Стрілка вліво/вгору — попередній блок, вправо/вниз — наступний (як нижні кнопки).',
  // ── Injected from locales/pl.json ──
  'producerAssignments.createInDesigner': 'Utwórz / otwórz szkolenie',
  'producerAssignments.draft': 'Szkic',
  'producerAssignments.enterprise': 'Firma',
  'producerAssignments.enterprisePaths': 'Ścieżki firmowe',
  'producerAssignments.episodes': 'odc.',
  'producerAssignments.errorForbidden': 'Nie masz dostępu do tej strony.',
  'producerAssignments.errorGeneric': 'Nie udało się załadować Twoich zadań.',
  'producerAssignments.errorNoInstitution': 'Nie jesteś przypisany do żadnej instytucji.',
  'producerAssignments.formations': 'Karty szkoleń',
  'producerAssignments.intro': 'Ta strona zawiera karty szkoleń i podcasty przydzielone Ci przez dział HR Twojej instytucji. Twórz treści w designerze EduFlow lub studiu podcastów, a następnie aktualizuj status produkcji.',
  'producerAssignments.markCompleted': 'Oznacz jako ukończone',
  'producerAssignments.markInProgress': 'W produkcji',
  'producerAssignments.markReady': 'Gotowy',
  'producerAssignments.modulesCatalog': '{{count}} moduł(ów) katalogu · zaktualizowano {{date}}',
  'producerAssignments.noneEnterprisePaths': 'Brak zapytań o ścieżki firmowe.',
  'producerAssignments.noneFormations': 'Brak przydzielonych kart.',
  'producerAssignments.nonePodcasts': 'Brak przydzielonych podcastów.',
  'producerAssignments.openInDesigner': 'Otwórz w designerze',
  'producerAssignments.openPodcasts': 'Studio podcastów',
  'producerAssignments.planningDeadline': 'Termin {{date}}',
  'producerAssignments.planningFrom': 'Od {{date}}',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': 'Podcasty do wyprodukowania',
  'producerAssignments.productionStatus.completed': 'Ukończone',
  'producerAssignments.productionStatus.in_progress': 'W trakcie',
  'producerAssignments.productionStatus.not_started': 'Nie rozpoczęte',
  'producerAssignments.productionStatus.published': 'Opublikowane',
  'producerAssignments.publishedCatalog': 'Opublikowane w katalogu',
  'producerAssignments.rhPlanning': 'Planowanie HR',
  'producerAssignments.subtitle': 'Szkolenia do stworzenia w designerze i podcasty do wyprodukowania, przydzielone przez dział HR.',
  'producerAssignments.title': 'Moje zadania produkcyjne',
  'podcasts.actions.cancel': 'Anuluj',
  'podcasts.actions.close': 'Zamknij',
  'podcasts.actions.create': 'Utwórz',
  'podcasts.actions.edit': 'Edytuj',
  'podcasts.actions.retry': 'Spróbuj ponownie',
  'podcasts.actions.save': 'Zapisz',
  'podcasts.actions.saving': 'Zapisywanie…',
  'podcasts.actions.share': 'Udostępnij',
  'podcasts.category.ariaGroup': 'Kategoria podcastu',
  'podcasts.category.emptyBanner': 'Dodaj kategorie w przestrzeni Instytucja, aby lepiej organizować podcasty.',
  'podcasts.category.emptyInstitutionHint': 'Brak kategorii instytucji — możesz opublikować bez kategorii.',
  'podcasts.category.noneChip': 'Bez kategorii',
  'podcasts.category.sectionTitle': 'Kategoria',
  'podcasts.category.withCategoriesHint': 'Umiejscowienie w katalogu i filtry po stronie instytucji.',
  'podcasts.cover.ariaDropzone': 'Wybierz lub upuść obraz okładki',
  'podcasts.cover.browseFiles': 'przeglądaj pliki',
  'podcasts.cover.cancelNewFile': 'Anuluj nowy plik',
  'podcasts.cover.dragDrop': 'Przeciągnij i upuść obraz',
  'podcasts.cover.dropHere': 'Upuść obraz tutaj',
  'podcasts.cover.errorMaxSize': 'Obraz nie może przekraczać 5 MB.',
  'podcasts.cover.errorUnsupportedFormat': 'Nieobsługiwany format obrazu.',
  'podcasts.cover.formatsHint': 'PNG, JPG, WebP, GIF · max. 5 MB',
  'podcasts.cover.help': 'Zalecany format kwadratowy. Przeciągnij plik lub kliknij, aby przeglądać.',
  'podcasts.cover.or': 'lub',
  'podcasts.cover.replaceHint': 'Zamień obraz',
  'podcasts.cover.sectionTitle': 'Okładka',
  'podcasts.deleteDialog.body': '„{{title}}” zostanie usunięty z listy (miękkie usunięcie). Odcinki nie są kasowane. Jeśli konto jest powiązane z instytucją, administrator może przywrócić podcast z kosza przestrzeni Instytucja.',
  'podcasts.deleteDialog.confirm': 'Przenieś do kosza',
  'podcasts.deleteDialog.title': 'Przenieść podcast do kosza?',
  'podcasts.editPanel.createdOn': 'Utworzono ',
  'podcasts.editPanel.headerTitle': 'Edytuj podcast',
  'podcasts.editPanel.saving': 'Zapisywanie…',
  'podcasts.editPanel.statsEpisodes': 'Odcinki: ',
  'podcasts.editPanel.statsSubscribers': 'Subskrybenci: ',
  'podcasts.editPanel.successSaved': 'Zmiany zapisane',
  'podcasts.episodeStatus.draft': 'Szkic',
  'podcasts.episodeStatus.published': 'Opublikowany',
  'podcasts.episodesPanel.addEpisode': 'Dodaj odcinek',
  'podcasts.episodesPanel.emptySubtitle': 'Dodaj odcinki do tego podcastu',
  'podcasts.episodesPanel.emptyTitle': 'Brak odcinków',
  'podcasts.episodesPanel.episodeCount': '{{count}} odcinek(ów)',
  'podcasts.episodesPanel.noEpisodeAvailable': 'Brak dostępnego odcinka',
  'podcasts.episodesPanel.remove': 'Usuń',
  'podcasts.episodesPanel.searchPlaceholder': 'Szukaj odcinka…',
  'podcasts.episodesPanel.title': 'Odcinki podcastu',
  'podcasts.error.retry': 'Spróbuj ponownie',
  'podcasts.filters.allCategories': 'Wszystkie kategorie',
  'podcasts.filters.categoryLabel': 'Kategoria',
  'podcasts.filters.searchLabel': 'Wyszukiwanie tekstowe',
  'podcasts.filters.searchPlaceholder': 'Tytuł, opis…',
  'podcasts.filters.viewCategories': 'Widok kategorii',
  'podcasts.filters.viewList': 'Widok listy',
  'podcasts.form.descriptionLabel': 'Opis',
  'podcasts.form.descriptionPlaceholder': 'Opisz swój podcast…',
  'podcasts.form.namePlaceholder': 'Nazwa podcastu',
  'podcasts.form.titleLabel': 'Tytuł',
  'podcasts.language.ariaGroup': 'Język podcastu',
  'podcasts.language.help': 'Spośród 10 języków platformy — aby dopasować podcast do tej samej lokalizacji co odtwarzacz i interfejs ucznia.',
  'podcasts.language.label': 'Język',
  'podcasts.lightbox.ariaClose': 'Zamknij',
  'podcasts.lightbox.ariaDialog': 'Podgląd okładki podcastu',
  'podcasts.list.actionActivate': 'Aktywuj',
  'podcasts.list.actionDeactivate': 'Dezaktywuj',
  'podcasts.list.actionDelete': 'Usuń',
  'podcasts.list.actionManageEpisodes': 'Zarządzaj odcinkami',
  'podcasts.list.actionPreview': 'Podgląd i odsłuch',
  'podcasts.list.coverPreviewAria': 'Podgląd okładki podcastu {{title}}',
  'podcasts.list.createFirst': 'Utwórz podcast',
  'podcasts.list.emptySubtitle': 'Utwórz pierwszy podcast, aby zacząć',
  'podcasts.list.emptyTitle': 'Brak podcastów',
  'podcasts.list.loadError': 'Nie udało się załadować podcastów.',
  'podcasts.list.enterpriseBadge': 'Firma',
  'podcasts.list.enterpriseNeedTitle': 'Potrzeba zdefiniowana przez firmę',
  'podcasts.list.episodesShort': '{{count}} odc.',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} subskr.',
  'podcasts.list.uncategorized': 'Bez kategorii',
  'podcasts.modal.errorRequiredFields': 'Tytuł i opis są wymagane.',
  'podcasts.modal.errorSaveFailed': 'Błąd podczas zapisywania.',
  'podcasts.modal.subtitleNew': 'Podaj tytuł, opis i opcje. Odcinki dodasz później.',
  'podcasts.modal.titleEdit': 'Edytuj podcast',
  'podcasts.modal.titleNew': 'Nowy podcast',
  'podcasts.page.episodesLink': 'Odcinki',
  'podcasts.page.newButton': 'Nowy',
  'podcasts.page.title': 'Podcasty',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': 'Strona {{page}} / {{total}}',
  'podcasts.pagination.totalSummary': 'Łącznie {{count}} podcast(s)',
  'podcasts.player.listen': 'Słuchaj',
  'podcasts.player.noAudio': 'Brak audio',
  'podcasts.player.pause': 'Pauza',
  'podcasts.preview.emptySubtitle': 'Ten podcast nie ma jeszcze odcinków',
  'podcasts.preview.emptyTitle': 'Brak odcinków',
  'podcasts.preview.episodeCount': '{{count}} odcinek(ów)',
  'podcasts.preview.fallbackDescription': 'Posłuchaj dostępnych odcinków audio.',
  'podcasts.preview.playAll': 'Odtwórz wszystko',
  'podcasts.preview.title': 'Podgląd podcastu',
  'podcasts.share.assignedLearnersHeading': '{{count}} przypisany(ch) uczeń(ów)',
  'podcasts.share.copied': 'Skopiowano',
  'podcasts.share.copyLink': 'Kopiuj link',
  'podcasts.share.emailPlaceholder': 'E-mail *',
  'podcasts.share.errorCreateLearner': 'Nie można utworzyć ucznia.',
  'podcasts.share.errorPublicLink': 'Nie można wygenerować linku publicznego.',
  'podcasts.share.errorRemoveLearner': 'Nie można usunąć ucznia.',
  'podcasts.share.errorShare': 'Nie można udostępnić tego podcastu.',
  'podcasts.share.firstNamePlaceholder': 'Imię *',
  'podcasts.share.generatingLink': 'Generowanie linku…',
  'podcasts.share.hideQuickCreate': 'Ukryj szybkie tworzenie',
  'podcasts.share.lastNamePlaceholder': 'Nazwisko *',
  'podcasts.share.loadingAssigned': 'Ładowanie…',
  'podcasts.share.loadingLearners': 'Ładowanie uczniów...',
  'podcasts.share.messageAccessRemoved': 'Dostęp ucznia został cofnięty.',
  'podcasts.share.messageCreatedAndShared': 'Konto utworzone/połączone, podcast udostępniono i wysłano e-mail z zaproszeniem.',
  'podcasts.share.messageShared': 'Podcast udostępniono i wysłano zaproszenie do ucznia.',
  'podcasts.share.noAssignedLearners': 'Brak uczniów przypisanych do tego podcastu.',
  'podcasts.share.noLearnerAvailable': 'Brak dostępnego ucznia.',
  'podcasts.share.panelTitle': 'Udostępnij podcast',
  'podcasts.share.publicLinkSection': 'Link publiczny (bez logowania)',
  'podcasts.share.removeAccessTitle': 'Cofnij dostęp',
  'podcasts.share.searchLearnerPlaceholder': 'Szukaj ucznia (imię, nazwisko, e-mail)',
  'podcasts.share.selectExistingLearnerLabel': 'Wybierz istniejącego ucznia',
  'podcasts.share.showQuickCreate': 'Utwórz nowego ucznia',
  'podcasts.share.submitCreateAndInvite': 'Utwórz, udostępnij i wyślij zaproszenie',
  'podcasts.status.active': 'Aktywny',
  'podcasts.status.inactive': 'Nieaktywny',
  'learningPaths.card.contents': '{{count}} treści',
  'learningPaths.card.delete': 'Usuń',
  'learningPaths.card.edit': 'Edytuj',
  'learningPaths.card.learners': '{{count}} uczących się',
  'learningPaths.card.publish': 'Opublikuj',
  'learningPaths.card.unpublish': 'Cofnij publikację',
  'learningPaths.card.updatedAt': 'Zaktualizowano {{date}}',
  'learningPaths.createPanel.cancel': 'Anuluj',
  'learningPaths.createPanel.description': 'Nadaj nazwę ścieżce; później dodasz podcasty i szkolenia.',
  'learningPaths.createPanel.descriptionLabel': 'Opis',
  'learningPaths.createPanel.descriptionPlaceholder': 'Cel ścieżki, grupa docelowa…',
  'learningPaths.createPanel.nameLabel': 'Nazwa ścieżki',
  'learningPaths.createPanel.namePlaceholder': 'Np.: Ścieżka wdrożeniowa HR…',
  'learningPaths.createPanel.submit': 'Utwórz ścieżkę',
  'learningPaths.createPanel.title': 'Nowa ścieżka',
  'learningPaths.deleteDialog.body': 'Usuniesz „{{title}}”. Tej operacji nie można cofnąć.',
  'learningPaths.deleteDialog.cancel': 'Anuluj',
  'learningPaths.deleteDialog.confirm': 'Usuń',
  'learningPaths.deleteDialog.title': 'Usuń ścieżkę',
  'learningPaths.empty.createFirst': 'Utwórz ścieżkę',
  'learningPaths.empty.noPathsHint': 'Utwórz pierwszą ścieżkę, łącząc podcasty i szkolenia.',
  'learningPaths.empty.noPathsYet': 'Nie ma jeszcze żadnych ścieżek',
  'learningPaths.empty.noResults': 'Brak wyników',
  'learningPaths.empty.noResultsHint': 'Zmień filtry lub utwórz nową ścieżkę.',
  'learningPaths.error.loadFailed': 'Nie udało się wczytać ścieżek',
  'learningPaths.error.retry': 'Spróbuj ponownie',
  'learningPaths.filters.allStatuses': 'Wszystkie statusy',
  'learningPaths.filters.draft': 'Szkic',
  'learningPaths.filters.published': 'Opublikowano',
  'learningPaths.filters.searchPlaceholder': 'Szukaj ścieżki…',
  'learningPaths.page.newButton': 'Nowa ścieżka',
  'learningPaths.page.subtitle': 'Twórz uporządkowane ścieżki szkoleniowe',
  'learningPaths.page.title': 'Ścieżki szkoleniowe',
  'learningPaths.page.totalCount': '{{count}} ścieżek',
  'learningPaths.pagination.next': 'Następna',
  'learningPaths.pagination.pageOf': 'Strona {{page}} z {{total}} · {{count}} ścieżek',
  'learningPaths.pagination.previous': 'Poprzednia',
  'learningPaths.status.draft': 'Szkic',
  'learningPaths.status.published': 'Opublikowano',
  'webinars.card.code': 'Kod {{code}}',
  'webinars.card.copyLink': 'Kopiuj link',
  'webinars.card.delete': 'Usuń',
  'webinars.card.edit': 'Edytuj',
  'webinars.card.openRoom': 'Otwórz salę',
  'webinars.deleteDialog.body': 'Usunąć « {{title}} »? Tej operacji nie można cofnąć.',
  'webinars.deleteDialog.cancel': 'Anuluj',
  'webinars.deleteDialog.confirm': 'Usuń',
  'webinars.deleteDialog.title': 'Usuń webinar',
  'webinars.eligibility.default': 'Webinary nie są dostępne dla Twojego konta (sprawdź połączenie z API lub skontaktuj się z administratorem).',
  'webinars.eligibility.moduleDisabled': 'Moduł webinarów nie jest jeszcze włączony dla Twojej placówki. Administrator musi zaznaczyć « Włącz webinary » w Placówka → Webinary.',
  'webinars.eligibility.noInstitution': 'Do Twojego konta nie jest przypisana żadna placówka.',
  'webinars.eligibility.notAuthorized': 'Twoje konto nie znajduje się na liście uprawnionych prowadzących. Poproś placówkę o dodanie (Zarządzanie webinarami → Uprawnieni prowadzący).',
  'webinars.eligibility.roleNotAllowed': 'Twoja rola nie pozwala prowadzić webinarów z tego konta.',
  'webinars.empty.createFirst': 'Utwórz webinar',
  'webinars.empty.noResults': 'Brak wyników',
  'webinars.empty.noResultsHint': 'Zmień kryteria wyszukiwania.',
  'webinars.empty.noWebinarsHint': 'Utwórz salę i zaproś uczestników z panelu bocznego.',
  'webinars.empty.noWebinarsYet': 'Na razie brak webinarów',
  'webinars.externalInvite.closeButton': 'Zamknij',
  'webinars.externalInvite.description': 'Osoby spoza listy uczących się: każdy adres otrzyma e-mail z linkiem do sali i załącznikiem kalendarza.',
  'webinars.externalInvite.done': 'Gotowe.',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} już zaproszony',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} już zaproszonych',
  'webinars.externalInvite.emailsHint': 'Jeden adres w wierszu lub rozdzielone przecinkami. Duplikaty i wcześniej wysłane zaproszenia są pomijane.',
  'webinars.externalInvite.emailsLabel': 'Adresy e-mail',
  'webinars.externalInvite.emailsPlaceholder': 'uzytkownik@przyklad.pl\ninny@domena.org',
  'webinars.externalInvite.errorMax200': 'Maksymalnie 200 adresów na wysyłkę.',
  'webinars.externalInvite.errorMinOne': 'Wpisz co najmniej jeden prawidłowy adres e-mail.',
  'webinars.externalInvite.errorsLabel': 'błędy: {{errors}}',
  'webinars.externalInvite.sendButton': 'Wyślij zaproszenia',
  'webinars.externalInvite.sendFailed': 'Wysyłka nie powiodła się',
  'webinars.externalInvite.sentCount_one': '{{count}} e-mail wysłany',
  'webinars.externalInvite.sentCount_other': '{{count}} e-maili wysłanych',
  'webinars.externalInvite.skippedCount_one': '{{count}} pominięta',
  'webinars.externalInvite.skippedCount_other': '{{count}} pominiętych',
  'webinars.externalInvite.title': 'Zaproszenia e-mailem',
  'webinars.invite.duplicatesCount_one': '{{count}} już zaproszony',
  'webinars.invite.duplicatesCount_other': '{{count}} już zaproszonych',
  'webinars.invite.errorsLabel': 'błędy: {{errors}}',
  'webinars.invite.externalButton': 'Poza listą (e-mail)',
  'webinars.invite.failed': 'Niepowodzenie',
  'webinars.invite.groupSuccess': 'Zaproszenia grupowe przetworzone (e-maile wysłane do członków).',
  'webinars.invite.inviteButton': 'Zaproś',
  'webinars.invite.learnersHelp': 'Wyszukaj uczącego się w swojej placówce: przy każdym zaproszeniu wysyłany jest <strong>e-mail z zaproszeniem</strong> (link + kalendarz).',
  'webinars.invite.learnersTitle': 'Uczący się',
  'webinars.invite.noCandidates': 'Brak uczących się do wyświetlenia (spróbuj innego wyszukiwania lub użyj « Poza listą » dla zewnętrznych adresów).',
  'webinars.invite.noInvitations': 'Brak zapisanych zaproszeń.',
  'webinars.invite.searchLabel': 'Szukaj uczącego się',
  'webinars.invite.searchPlaceholder': 'Imię lub e-mail…',
  'webinars.invite.searching': 'Wyszukiwanie…',
  'webinars.invite.sentCount_one': '{{count}} zaproszenie wysłane (e-mail)',
  'webinars.invite.sentCount_other': '{{count}} zaproszeń wysłanych (e-mail)',
  'webinars.invite.sentInvitationsToggle': 'Wysłane zaproszenia',
  'webinars.page.newButton': 'Nowy webinar',
  'webinars.page.searchPlaceholder': 'Szukaj…',
  'webinars.page.subtitle': 'Lista Twoich sal — otwórz panel, aby utworzyć, edytować lub zaprosić uczestników.',
  'webinars.page.title': 'Webinary',
  'webinars.panel.backgroundHint': 'Adres URL https (lub http) wyświetlany za udostępnianą treścią dla uczestników. Pozostaw puste, aby użyć wbudowanego wyglądu Lyrra.',
  'webinars.panel.backgroundLabel': 'Obraz tła (widok uczestnika)',
  'webinars.panel.backgroundPlaceholder': 'https://… (puste = domyślne tło Lyrra)',
  'webinars.panel.copyLink': 'Kopiuj link',
  'webinars.panel.createButton': 'Utwórz',
  'webinars.panel.createDescription': 'Uzupełnij informacje i zapisz. Później zaprosisz uczestników.',
  'webinars.panel.createTitle': 'Nowy webinar',
  'webinars.panel.dateLabel': 'Dzień (opcjonalnie)',
  'webinars.panel.deleteButton': 'Usuń',
  'webinars.panel.descriptionLabel': 'Opis',
  'webinars.panel.durationError': 'Podaj czas trwania w formacie godziny:minuty (np. 1:00 lub 0:45).',
  'webinars.panel.durationHint': 'Wymagany, jeśli podano dzień. Np. 1:30 na 1 h 30, 0:45 na 45 min.',
  'webinars.panel.durationLabel': 'Czas trwania (g:mm)',
  'webinars.panel.editTitle': 'Webinar',
  'webinars.panel.loading': 'Ładowanie…',
  'webinars.panel.openRoom': 'Otwórz salę',
  'webinars.panel.presentersLabel': 'Współprowadzący',
  'webinars.panel.roomCode': 'Kod sali:',
  'webinars.panel.timeHint': 'Jeśli puste: 9:00.',
  'webinars.panel.timeLabel': 'Godzina rozpoczęcia',
  'webinars.panel.titleLabel': 'Tytuł',
  'webinars.panel.waitingHtmlLabel': 'HTML poczekalni (opcjonalnie)',
  'webinars.panel.waitingHtmlPlaceholder': '<p>Witamy…</p>',
  'webinars.status.draft': 'Szkic',
  'webinars.status.ended': 'Zakończony',
  'webinars.status.live': 'Na żywo',
  'webinars.status.scheduled': 'Zaplanowany',
  'videos.addPanel.cancel': 'Anuluj',
  'videos.addPanel.continue': 'Kontynuuj',
  'videos.addPanel.description': 'Importuj MP4, WebM lub MOV, aby używać ich w szkoleniach.',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'Kliknij, aby wybrać film',
  'videos.addPanel.title': 'Dodaj film',
  'videos.addPanel.titleLabel': 'Tytuł',
  'videos.addPanel.titlePlaceholder': 'Tytuł filmu…',
  'videos.card.delete': 'Usuń',
  'videos.card.edit': 'Edytuj',
  'videos.card.noThumbnail': 'Brak miniatury',
  'videos.card.publishedStudio': 'Opublikowane (edytowalne w Studio)',
  'videos.deleteDialog.body': '« {{title}} » zostanie trwale usunięty.',
  'videos.deleteDialog.cancel': 'Anuluj',
  'videos.deleteDialog.confirm': 'Usuń',
  'videos.deleteDialog.title': 'Usunąć film?',
  'videos.empty.addButton': 'Dodaj film',
  'videos.empty.description': 'Zaimportuj pierwszy film, aby nim zarządzać i używać go w szkoleniach.',
  'videos.empty.title': 'Brak filmów',
  'videos.page.addButton': 'Dodaj film',
  'videos.page.count_one': '{{count}} film w twojej bibliotece',
  'videos.page.count_other': '{{count}} filmów w twojej bibliotece',
  'videos.page.title': 'Filmy',
  'videos.preview.noFile': 'Brak dostępnego pliku.',
  'videos.status.draft': 'Szkic',
  'videos.status.published': 'Opublikowany',
  'sounds.card.delete': 'Usuń',
  'sounds.card.mute': 'Wycisz',
  'sounds.deleteDialog.body': '« {{title}} » zostanie trwale usunięty z serwera i z biblioteki.',
  'sounds.deleteDialog.cancel': 'Anuluj',
  'sounds.deleteDialog.confirm': 'Usuń',
  'sounds.deleteDialog.title': 'Usunąć ten dźwięk?',
  'sounds.empty.description': 'Zaimportuj pliki: są zapisywane w bazie i kopiowane na serwer, aby nic nie zginęło.',
  'sounds.empty.importButton': 'Importuj dźwięk',
  'sounds.empty.title': 'Brak zaimportowanych dźwięków',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'Anuluj',
  'sounds.importPanel.changeFile': 'Zmień plik',
  'sounds.importPanel.createTag': 'Utwórz « {{tag}} »',
  'sounds.importPanel.description': 'Dodaj plik audio do biblioteki na potrzeby ścieżek szkoleniowych.',
  'sounds.importPanel.descriptionLabel': 'Opis',
  'sounds.importPanel.descriptionPlaceholder': 'Opisz ten dźwięk…',
  'sounds.importPanel.dropzoneBrowse': 'przeglądaj',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'Przeciągnij plik audio lub',
  'sounds.importPanel.optional': '(opcjonalnie)',
  'sounds.importPanel.submit': 'Importuj',
  'sounds.importPanel.submitting': 'Wysyłanie…',
  'sounds.importPanel.tagsHint': 'Wpisz i naciśnij Enter · Nowe tagi tworzą się automatycznie',
  'sounds.importPanel.tagsLabel': 'Tagi',
  'sounds.importPanel.tagsPlaceholder': 'Dodaj tagi…',
  'sounds.importPanel.title': 'Importuj dźwięk',
  'sounds.importPanel.titleLabel': 'Tytuł',
  'sounds.importPanel.titlePlaceholder': 'Nazwa dźwięku…',
  'sounds.importPanel.titleRequired': 'Tytuł jest wymagany',
  'sounds.noResults': 'Żaden dźwięk nie pasuje do « {{query}} »',
  'sounds.page.count_one': '{{count}} plik — zapisany w bazie i na serwerze',
  'sounds.page.count_other': '{{count}} plików — zapisanych w bazie i na serwerze',
  'sounds.page.importButton': 'Importuj',
  'sounds.page.searchPlaceholder': 'Tytuł, opis, tag…',
  'sounds.page.title': 'Dźwięki',
  'documents.count_one': '{{count}} dokument',
  'documents.count_other': '{{count}} dokumentów',
  'documents.delete.body': 'Dokument „{{filename}}” zostanie usunięty z biblioteki.',
  'documents.delete.cancel': 'Anuluj',
  'documents.delete.confirm': 'Usuń',
  'documents.delete.irreversible': 'Tej operacji nie można cofnąć.',
  'documents.delete.title': 'Usunąć dokument?',
  'documents.importPanel.cancel': 'Anuluj',
  'documents.importPanel.chooseFile': 'Wybierz dokument PDF',
  'documents.importPanel.description': 'Dokument będzie dostępny w blokach PDF i w tej bibliotece.',
  'documents.importPanel.descriptionLabel': 'Opis',
  'documents.importPanel.descriptionPlaceholder': 'Opis (opcjonalnie)',
  'documents.importPanel.fileHint': 'Dokument będzie dostępny w bloku PDF i w tej bibliotece.',
  'documents.importPanel.submit': 'Importuj',
  'documents.importPanel.title': 'Importuj PDF',
  'documents.importPanel.titleLabel': 'Tytuł',
  'documents.importPanel.titlePlaceholder': 'Tytuł dokumentu',
  'documents.importPanel.uploading': 'Przesyłanie…',
  'documents.importPdf': 'Importuj PDF',
  'documents.loading': 'Ładowanie biblioteki…',
  'documents.newDocument': 'Nowy dokument',
  'documents.noDocuments': 'Brak dokumentów',
  'documents.noDocumentsHint': 'Importuj tutaj pliki PDF, aby później użyć ich w bloku PDF.',
  'documents.noResults': 'Żaden dokument nie pasuje do wyszukiwania.',
  'documents.pages_one': '{{count}} strona',
  'documents.pages_other': '{{count}} stron',
  'documents.previewTitle': 'Podgląd dokumentu',
  'documents.searchPlaceholder': 'Szukaj PDF...',
  'documents.share.accessRemoved': 'Dostęp uczestnika został odebrany.',
  'documents.share.assignedTitle': 'Uczestnicy z dostępem',
  'documents.share.close': 'Zamknij',
  'documents.share.createAndShare': 'Utwórz, udostępnij i wyślij zaproszenie',
  'documents.share.createError': 'Nie udało się utworzyć uczestnika.',
  'documents.share.createLearner': 'Utwórz nowego uczestnika',
  'documents.share.createdSuccess': 'Konto utworzone/połączone, dokument udostępniony, e-mail wysłany.',
  'documents.share.creating': 'Wysyłanie...',
  'documents.share.emailPlaceholder': 'E-mail *',
  'documents.share.firstNamePlaceholder': 'Imię *',
  'documents.share.hideCreateLearner': 'Ukryj szybkie tworzenie',
  'documents.share.lastNamePlaceholder': 'Nazwisko *',
  'documents.share.loadingAssigned': 'Ładowanie…',
  'documents.share.loadingLearners': 'Ładowanie uczestników...',
  'documents.share.noAssigned': 'Brak przypisanych uczestników do tego dokumentu.',
  'documents.share.noLearners': 'Brak dostępnych uczestników.',
  'documents.share.removeButton': 'Odbierz',
  'documents.share.removeError': 'Nie udało się odebrać dostępu.',
  'documents.share.searchPlaceholder': 'Szukaj uczestnika (imię, nazwisko, e-mail)',
  'documents.share.selectLearner': 'Wybierz istniejącego uczestnika',
  'documents.share.shareButton': 'Udostępnij',
  'documents.share.shareError': 'Nie udało się udostępnić dokumentu.',
  'documents.share.sharedSuccess': 'Dokument udostępniony, zaproszenie wysłane.',
  'documents.share.title': 'Udostępnij dokument',
  'documents.subtitle': 'Biblioteka dokumentów PDF do ponownego użycia w projektancie.',
  'documents.title': 'Dokumenty',
  'documents.toast.importError': 'Import niemożliwy',
  'documents.toast.importErrorDefault': 'Import się nie powiódł.',
  'documents.toast.importSuccess': 'Dokument zaimportowany',
  'documents.toast.importSuccessDesc': 'Plik {{filename}} został dodany do biblioteki dokumentów.',
  'documents.updating': 'Aktualizowanie…',
  'help.categories.analytics.description': 'Pulpity, filtry i eksporty do monitorowania wpływu szkoleń.',
  'help.categories.analytics.title': 'Analityka i raporty',
  'help.categories.apprenants.description': 'Śledzenie zapisów, kohort i postępów uczestników.',
  'help.categories.apprenants.title': 'Uczący się, grupy i śledzenie',
  'help.categories.boutique.description': 'Sprzedaż lub przydzielanie szkoleń po skonfigurowaniu sklepu.',
  'help.categories.boutique.title': 'Sklep i monetyzacja',
  'help.categories.demarrage.description': 'Zapoznaj się z interfejsem, swoim profilem i codzienną nawigacją w Lyrra.',
  'help.categories.demarrage.title': 'Rozpoczęcie i konto',
  'help.categories.donnees.description': 'RODO, przechowywanie, prawa osób i dobre praktyki bezpieczeństwa.',
  'help.categories.donnees.title': 'Dane osobowe i bezpieczeństwo',
  'help.categories.eduflow.description': 'Projektowanie ścieżek dydaktycznych z blokami, połączeniami i logiką nawigacji.',
  'help.categories.eduflow.title': 'Ścieżki i designer EduFlow',
  'help.categories.evaluation.description': 'Test wielokrotnego wyboru, oceny, ankiety i informacje zwrotne do pomiaru nabytej wiedzy.',
  'help.categories.evaluation.title': 'Ocenianie i aktywna pedagogika',
  'help.categories.institution.description': 'Konfiguracja struktury, współpracowników i organizacji treści.',
  'help.categories.institution.title': 'Placówka i zespół',
  'help.categories.marque.description': 'Dopasowanie kolorów, etykiet i ścieżki uczącego się do Twojej tożsamości.',
  'help.categories.marque.title': 'Marka i doświadczenie użytkownika',
  'help.categories.medias.description': 'Zarządzanie biblioteką, importem oraz zintegrowanymi narzędziami audio/wideo/graficznymi w Lyrra.',
  'help.categories.medias.title': 'Multimedia i studio kreatywne',
  'help.categories.parcours-v2.description': 'Kampanie, kompetencje, integracje: wszystko, co wzbogaca zarządzanie szkoleniami.',
  'help.categories.parcours-v2.title': 'Zaawansowane funkcje i plan rozwoju',
  'help.categories.podcasts.description': 'Tworzenie i dystrybucja długich treści audio poza ścieżką blok po bloku.',
  'help.categories.podcasts.title': 'Podcasty i audiobooki',
  'help.categories.publication.description': 'Udostępnianie ścieżki uczącym się i dzielenie się odpowiednimi dostępami.',
  'help.categories.publication.title': 'Publikacja i dystrybucja',
  'help.categories.qualite.description': 'Francuskie ramy jakości: identyfikowalność, cele i dowody na potrzeby audytów.',
  'help.categories.qualite.title': 'Jakość, Qualiopi i zgodność dydaktyczna',
  'help.categories.support.description': 'W przypadku blokady technicznej lub funkcjonalnej — gdzie szukać i z kim się kontaktować.',
  'help.categories.support.title': 'Rozwiązywanie problemów i wsparcie',
  'help.configHint': 'Treść redakcyjna: podłącz swoje adresy URL, artykuły lub bazę wiedzy, gdy będziesz gotowy.',
  'help.description': 'Dokumentacja i wsparcie',
  'help.hubIntro': 'Tematy dotyczące korzystania z Lyrra: wkrótce będziesz mógł powiązać każdą kartę z dokumentacją, samouczkami lub zgłoszeniami.',
  'help.modal.expectedAnswer': 'Oczekiwana odpowiedź:',
  'help.modal.fallbackHighlight1': 'Karta',
  'help.modal.fallbackHighlight2': 'Platforma szkoleniowa',
  'help.modal.fallbackHighlight3': 'Do uzupełnienia',
  'help.modal.fallbackSectionHeading': 'Do zapamiętania',
  'help.modal.fallbackSectionParagraph': 'Szczegółowa treść tej karty zostanie wzbogacona lub powiązana z Twoją wewnętrzną dokumentacją.',
  'help.modal.fallbackStep1Desc': 'Użyj menu bocznego lub pulpitu, aby przejść do odpowiedniego modułu.',
  'help.modal.fallbackStep1Title': 'Otwórz funkcjonalność',
  'help.modal.fallbackStep2Desc': 'Ekrany Cię prowadzą: zapisuj często i testuj w podglądzie, gdy jest dostępny.',
  'help.modal.fallbackStep2Title': 'Postępuj zgodnie z przepływem pracy',
  'help.modal.fallbackStep3Desc': 'Zapisz specyfikę Twojej placówki (procesy HR, nazewnictwo, identyfikacja wizualna) dla swojego zespołu.',
  'help.modal.fallbackStep3Title': 'Dokumentuj wewnętrznie',
  'help.modal.fallbackTip': 'W razie wątpliwości sprawdź swoją rolę (trener, admin, HR): wyświetlane menu zależą od niej.',
  'help.modal.quiz': 'Quiz walidacyjny',
  'help.modal.related': 'Zobacz także',
  'help.modal.resources': 'Zasoby',
  'help.modal.steps': 'W praktyce',
  'help.modal.tips': 'Wskazówki',
  'help.noResults': 'Brak wyników dla tego wyszukiwania.',
  'help.onboarding.ai-designer': 'Szkolenie — Asystent AI designera LyrraStudio (onboarding)',
  'help.onboarding.blocks': 'Szkolenie — Typy bloków EduFlow (onboarding)',
  'help.onboarding.branches': 'Szkolenie — Rozgałęzienia i warunki EduFlow (onboarding)',
  'help.onboarding.catalog-visibility': 'Szkolenie — Katalog i widoczność LyrraStudio (onboarding)',
  'help.onboarding.certificates': 'Szkolenie — Wydane certyfikaty LyrraStudio (onboarding)',
  'help.onboarding.dashboard': 'Szkolenie — Pulpit Lyrra (onboarding)',
  'help.onboarding.designer-overview': 'Szkolenie — Przegląd designera EduFlow (onboarding)',
  'help.onboarding.enrollments': 'Szkolenie — Zapisy i uczestnicy LyrraStudio (onboarding)',
  'help.onboarding.groups-cohorts': 'Szkolenie — Grupy i kohorty LyrraStudio (onboarding)',
  'help.onboarding.media-library': 'Szkolenie — Biblioteka multimediów LyrraStudio (onboarding)',
  'help.onboarding.navigation': 'Szkolenie — Nawigacja i menu boczne Lyrra (onboarding)',
  'help.onboarding.objectives': 'Szkolenie — Cele dydaktyczne EduFlow (onboarding)',
  'help.onboarding.profile': 'Szkolenie — Profil i preferencje Lyrra (onboarding)',
  'help.onboarding.progress': 'Szkolenie — Postęp i czas spędzony LyrraStudio (onboarding)',
  'help.onboarding.publication-links': 'Szkolenie — Linki dostępu i udostępnianie LyrraStudio (onboarding)',
  'help.onboarding.publication-status': 'Szkolenie — Szkic, publikacja i aktualizacje LyrraStudio (onboarding)',
  'help.onboarding.roles': 'Szkolenie — Role i prawa dostępu Lyrra (onboarding)',
  'help.onboarding.validation': 'Szkolenie — Walidacja ścieżki EduFlow (onboarding)',
  'help.onboardingFlowMissing': 'Szkolenie nie znalezione',
  'help.onboardingFlowMissingDesc': 'Uruchom skrypty db:seed:onboarding-eduflow i db:seed:onboarding-navigation-eduflow (backend) lub zmienne VITE_ONBOARDING_*_FLOW_ID.',
  'help.openArticle': 'Otwórz kartę',
  'help.searchPlaceholder': 'Szukaj tematu…',
  'help.title': 'Centrum pomocy',
  'help.topics.analytics-exports.description': 'Przygotowywanie wyciągów do raportowania wewnętrznego lub regulacyjnego.',
  'help.topics.analytics-exports.title': 'Eksporty i raportowanie HR',
  'help.topics.analytics-indicateurs.description': 'Wskaźnik ukończenia, zaangażowanie, rozkład według statusu i czas na blokach.',
  'help.topics.analytics-indicateurs.title': 'Kluczowe wskaźniki',
  'help.topics.analytics-vue.description': 'Dostosowywanie wykresów, okresów i filtrów według szkolenia do analizy danych.',
  'help.topics.analytics-vue.title': 'Przegląd analityczny',
  'help.topics.app-certificats.description': 'Weryfikacja wygenerowanych zaświadczeń i powiązanych warunków zaliczenia.',
  'help.topics.app-certificats.title': 'Wydane certyfikaty',
  'help.topics.app-groupes.description': 'Używanie grup do segmentacji odbiorców i ułatwiania kampanii lub raportów.',
  'help.topics.app-groupes.title': 'Grupy i kohorty',
  'help.topics.app-inscriptions.description': 'Przeglądanie listy uczących się na ścieżce, ich statusów i historii dostępu.',
  'help.topics.app-inscriptions.title': 'Zapisy i uczestnicy',
  'help.topics.app-progression.description': 'Interpretowanie postępu, ukończeń i czasu zaangażowania w blokach.',
  'help.topics.app-progression.title': 'Postęp i czas spędzony',
  'help.topics.boutique-offres.description': 'Powiązanie ścieżek z produktami lub kodami dostępu zgodnie z modelem biznesowym.',
  'help.topics.boutique-offres.title': 'Oferty i cennik',
  'help.topics.boutique-paiement.description': 'Zrozumienie przepływu zakupu po stronie uczącego się i potwierdzeń po stronie organizacji.',
  'help.topics.boutique-paiement.title': 'Płatności i fakturowanie',
  'help.topics.demarrage-navigation.description': 'Znajdowanie przestrzeni Projekty, Szkolenia, Multimedia, Analityka i Ustawienia zgodnie z Twoją rolą.',
  'help.topics.demarrage-navigation.title': 'Nawigacja i menu boczne',
  'help.topics.demarrage-profil.description': 'Aktualizacja danych, języka interfejsu i powiadomień.',
  'help.topics.demarrage-profil.title': 'Profil i preferencje',
  'help.topics.demarrage-roles.description': 'Zrozumienie różnic między trenerem, administratorem placówki, HR i uczącym się.',
  'help.topics.demarrage-roles.title': 'Role i prawa dostępu',
  'help.topics.demarrage-tableau-bord.description': 'Odczytywanie kluczowych wskaźników, szybki dostęp do ostatnich ścieżek i przydatnych skrótów.',
  'help.topics.demarrage-tableau-bord.title': 'Pulpit szkoleniowy',
  'help.topics.donnees-conservation.description': 'Zasady dotyczące okresu archiwizowania postępów i dowodów szkoleniowych.',
  'help.topics.donnees-conservation.title': 'Okres przechowywania',
  'help.topics.donnees-mots-de-passe.description': 'Dobre praktyki dotyczące haseł, sesji i delegowania dostępu.',
  'help.topics.donnees-mots-de-passe.title': 'Konta i uwierzytelnianie',
  'help.topics.donnees-rgpd.description': 'Cele zbieranych danych w Lyrra i odpowiedzialność osoby odpowiedzialnej za szkolenie.',
  'help.topics.donnees-rgpd.title': 'RODO i rejestr przetwarzania',
  'help.topics.eduflow-blocs.description': 'Wybieranie i konfigurowanie odpowiednich bloków: tekst, wideo, audio, ocena, rozgałęzienie itp.',
  'help.topics.eduflow-blocs.title': 'Typy bloków (treść, quiz, multimedia…)',
  'help.topics.eduflow-branches.description': 'Zarządzanie wieloma wyjściami, zbieżnością ścieżek i regułami nawigacji.',
  'help.topics.eduflow-branches.title': 'Rozgałęzienia i warunki',
  'help.topics.eduflow-concept.description': 'Płótno, paleta bloków, zaznaczanie, zoom i wizualna organizacja ścieżki.',
  'help.topics.eduflow-concept.title': 'Przegląd designera',
  'help.topics.eduflow-ia.description': 'Sugerowanie lub modyfikowanie bloków za pomocą asystenta, z systematyczną kontrolą człowieka.',
  'help.topics.eduflow-ia.title': 'Asystent AI w designerze',
  'help.topics.eduflow-objectifs.description': 'Powiązanie celów z blokami, aby ustrukturyzować ramy referencyjne szkolenia.',
  'help.topics.eduflow-objectifs.title': 'Cele dydaktyczne',
  'help.topics.eduflow-validation.description': 'Korzystanie z panelu Walidacja w celu wykrywania błędów i ostrzeżeń przed publikacją.',
  'help.topics.eduflow-validation.title': 'Walidacja ścieżki',
  'help.topics.eval-auto-eval.description': 'Proponowanie adaptacyjnych ścieżek na podstawie wyników.',
  'help.topics.eval-auto-eval.title': 'Samoocena i remediacja',
  'help.topics.eval-ouverte.description': 'Organizowanie zbierania odpowiedzi swobodnych i oceniania ręcznego lub półautomatycznego.',
  'help.topics.eval-ouverte.title': 'Pytania otwarte i ocenianie',
  'help.topics.eval-quiz.description': 'Konfigurowanie skali ocen, informacji zwrotnych i prób dla efektywnego testu wielokrotnego wyboru.',
  'help.topics.eval-quiz.title': 'Quiz i test wielokrotnego wyboru',
  'help.topics.inst-collaborateurs.description': 'Dodawanie trenerów, administratorów lub HR i zarządzanie uprawnieniami.',
  'help.topics.inst-collaborateurs.title': 'Użytkownicy i zaproszenia',
  'help.topics.inst-structure.description': 'Określanie tego, co jest współdzielone na poziomie placówki a projektów osobistych.',
  'help.topics.inst-structure.title': 'Struktura i zasięg',
  'help.topics.inst-trash.description': 'Znajdowanie usuniętych szkoleń lub podcastów i przywracanie ich w razie potrzeby.',
  'help.topics.inst-trash.title': 'Kosz i przywracanie',
  'help.topics.marque-accessibilite.description': 'Kontrasty, alternatywy tekstowe i nawigacja klawiaturą w treściach.',
  'help.topics.marque-accessibilite.title': 'Dostępność i czytelność',
  'help.topics.marque-charte.description': 'Jak marka odzwierciedla się w odtwarzaczu i przestrzeniach uczących się.',
  'help.topics.marque-charte.title': 'Identyfikacja wizualna i kolory instytucji',
  'help.topics.marque-landing.description': 'Prezentowanie oferty szkoleniowej odwiedzającym, gdy moduł jest aktywny.',
  'help.topics.marque-landing.title': 'Strony publiczne i katalog',
  'help.topics.medias-bibliotheque.description': 'Importowanie, organizowanie i ponowne wykorzystywanie filmów, dźwięków, obrazów i dokumentów w blokach.',
  'help.topics.medias-bibliotheque.title': 'Biblioteka multimediów',
  'help.topics.medias-documents.description': 'Dołączanie zasobów do pobrania i powiązanie dokumentacji ze ścieżką.',
  'help.topics.medias-documents.title': 'Dokumenty i PDF',
  'help.topics.medias-studio-audio.description': 'Nagrywanie, miksowanie lub korzystanie z syntezy mowy, aby wzbogacić treści.',
  'help.topics.medias-studio-audio.title': 'Studio audio i głos',
  'help.topics.medias-video.description': 'Dobre praktyki dotyczące formatu, rozmiaru pliku i dostępności dla wbudowanych odtwarzaczy.',
  'help.topics.medias-video.title': 'Filmy i integracje',
  'help.topics.podcasts-creation.description': 'Strukturyzowanie odcinków, metadanych i okładek dla oferty audio.',
  'help.topics.podcasts-creation.title': 'Tworzenie podcastu',
  'help.topics.podcasts-diffusion.description': 'Ustawienia odsłuchu, udostępnianie i śledzenie użycia po stronie placówki.',
  'help.topics.podcasts-diffusion.title': 'Dystrybucja i uczący się',
  'help.topics.pub-catalogue.description': 'Powiązanie szkolenia z katalogiem instytucji lub ofertą, gdy jest to aktywne.',
  'help.topics.pub-catalogue.title': 'Katalog i widoczność',
  'help.topics.pub-liens.description': 'Generowanie lub kopiowanie linków dla uczących się, prezenterów lub podglądu.',
  'help.topics.pub-liens.title': 'Linki dostępu i udostępnianie',
  'help.topics.pub-statuts.description': 'Cykl życia szkolenia: zapisywanie, testowanie, publikowanie i wersjonowanie zmian.',
  'help.topics.pub-statuts.title': 'Szkic, publikacja i aktualizacje',
  'help.topics.qualite-qualiopi.description': 'Korzystanie z dedykowanych przestrzeni do przygotowania kryteriów i dokumentacji jakościowej.',
  'help.topics.qualite-qualiopi.title': 'Pre-audyt i wskaźniki Qualiopi',
  'help.topics.qualite-tracabilite.description': 'Powiązanie celów, treści i ocen w celu dokumentowania ścieżki certyfikującej.',
  'help.topics.qualite-tracabilite.title': 'Identyfikowalność dydaktyczna',
  'help.topics.support-contact.description': 'Kanał wsparcia, tworzenie zgłoszenia i informacje potrzebne do szybkiej diagnozy.',
  'help.topics.support-contact.title': 'Kontakt ze wsparciem Lyrra',
  'help.topics.support-faq.description': 'Odtwarzacz się nie ładuje, brakujący plik multimedialny, błąd publikacji: pierwsze kroki.',
  'help.topics.support-faq.title': 'Częste problemy',
  'help.topics.support-navigateur.description': 'Zalecane wersje, pliki cookie i rozszerzenia, które mogą przeszkadzać.',
  'help.topics.support-navigateur.title': 'Przeglądarka i kompatybilność',
  'help.topics.v2-campagnes.description': 'Planowanie wdrażania ścieżek wśród grup docelowych w czasie.',
  'help.topics.v2-campagnes.title': 'Kampanie i plany szkoleniowe',
  'help.topics.v2-competences.description': 'Mapowanie bloków lub ścieżek na oczekiwane kompetencje dla raportowania zawodowego.',
  'help.topics.v2-competences.title': 'Ramy kompetencji',
  'help.topics.v2-integrations.description': 'Wymiana z systemem HR, zewnętrznym LMS lub narzędziami BI, gdy dostępne są konektory.',
  'help.topics.v2-integrations.title': 'Integracje i API',
  'pages.images.cancel': 'Anuluj',
  'pages.images.changeFile': 'Zmień plik',
  'pages.images.copyUrl': 'Kopiuj URL',
  'pages.images.count_one': '{{count}} obraz w bibliotece',
  'pages.images.count_other': '{{count}} obrazów w bibliotece',
  'pages.images.delete': 'Usuń',
  'pages.images.deleteAction': 'Usuń',
  'pages.images.deleteConfirmHint': 'zostanie trwale usunięty.',
  'pages.images.deleteTitle': 'Usunąć ten obraz?',
  'pages.images.descriptionLabel': 'Opis (opcjonalnie)',
  'pages.images.descriptionPlaceholder': 'Krótki opis…',
  'pages.images.dropzoneBrowse': 'przeglądaj',
  'pages.images.dropzoneFormats': 'PNG, JPG, WEBP, GIF',
  'pages.images.dropzoneLabel': 'Przeciągnij obraz lub',
  'pages.images.emptyHint': 'Przeciągnij i upuść, kliknij aby importować lub wklej zrzut ekranu (Ctrl+V / Cmd+V).',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'Importuj',
  'pages.images.importSubtitle': 'Dodaj obraz do biblioteki na potrzeby szkoleń.',
  'pages.images.importTitle': 'Importuj obraz',
  'pages.images.lightboxBackdropClose': 'Zamknij',
  'pages.images.lightboxClose': 'Zamknij',
  'pages.images.lightboxNext': 'Następny obraz',
  'pages.images.lightboxPrev': 'Poprzedni obraz',
  'pages.images.lightboxTitle': 'Podgląd obrazu',
  'pages.images.noImages': 'Brak zaimportowanych obrazów',
  'pages.images.noResults': 'Brak wyników',
  'pages.images.pasteHint': 'Lub wklej zrzut ekranu (Ctrl+V / Cmd+V)',
  'pages.images.searchPlaceholder': 'Szukaj obrazu…',
  'pages.images.submit': 'Importuj',
  'pages.images.title': 'Obrazy',
  'pages.images.titleLabel': 'Tytuł (opcjonalnie)',
  'pages.images.titlePlaceholder': 'Nazwa obrazu…',
  'pages.images.viewLarge': 'Zobacz w pełnym rozmiarze',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Режим спрощеного тексту',
  'accessibility.simplifiedTextModeDesc':
    'Спрощує лише тексти курсу, щоб полегшити читання та розуміння.',
  'accessibility.imageHoverAudio': 'Режим слабкого зору: аудіоопис зображення при наведенні',
  'accessibility.imageHoverAudioDesc':
    'Коли навести курсор на зображення у плеєрі, аудіоопис запускається автоматично.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Згорнути',
  'admin.shell.collapseSidebar': 'Згорнути бічну панель',
  'admin.shell.expandSidebar': 'Розгорнути бічну панель',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Запит на навчання — навчальний контент',
  'trainingRequest.intro': 'Опишіть потребу. Команда HR затверджує або відхиляє запит; у разі затвердження він передається на підготовку контенту.',
  'trainingRequest.fieldTitle': 'Назва навчання',
  'trainingRequest.fieldDescription': 'Опис потреби',
  'trainingRequest.fieldJustification': 'Обґрунтування / цілі',
  'trainingRequest.fieldDate': 'Бажана дата',
  'trainingRequest.datePlaceholder': 'напр. Q2 2026',
  'trainingRequest.fieldFormateur': 'Тренер закладу',
  'trainingRequest.formateurPlaceholder': 'Обрати тренера…',
  'trainingRequest.formateursEmpty': 'Жодного облікового запису тренера для вашого закладу.',
  'trainingRequest.titleRequired': 'Вкажіть назву (мінімум 2 символи).',
  'trainingRequest.sent': 'Запит зареєстровано. Вас повідомлять електронною поштою про рішення.',
  'trainingRequest.sendError': 'Не вдалося зберегти запит. Перевірте підключення або права доступу.',
  'trainingRequest.submit': 'Надіслати запит',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Далі',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Версію {{label}} активовано (публічна)',
  'eduflow.versionHistory.activateTitle': 'Активувати {{label}}?',
  'eduflow.versionHistory.blocks': '{{count}} блоків',
  'eduflow.versionHistory.confirmActivate': 'Активувати',
  'eduflow.versionHistory.copyFailed': 'Копіювання не вдалося',
  'eduflow.versionHistory.copyHtml': 'Скопіювати HTML',
  'eduflow.versionHistory.copyPlain': 'Скопіювати лише текст',
  'eduflow.versionHistory.copyPublicPlayer': 'Скопіювати публічне посилання',
  'eduflow.versionHistory.copyPublicPlayerError': 'Неможливо скопіювати публічне посилання',
  'eduflow.versionHistory.count': '{{count}} версія(й)',
  'eduflow.versionHistory.createError': 'Помилка створення версії',
  'eduflow.versionHistory.createMajor': 'Основна версія',
  'eduflow.versionHistory.createMajorSuccess': 'Основну версію створено',
  'eduflow.versionHistory.createMinor': 'Другорядна версія',
  'eduflow.versionHistory.createMinorSuccess': 'Другорядну версію створено',
  'eduflow.versionHistory.daysAgo': '{{count}} дн тому',
  'eduflow.versionHistory.deleteError': 'Помилка при видаленні',
  'eduflow.versionHistory.deleteSuccess': 'Версію видалено',
  'eduflow.versionHistory.deleteTitle': 'Видалити {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'Цю дію неможливо скасувати.',
  'eduflow.versionHistory.duplicateConfirm': 'Дублювати',
  'eduflow.versionHistory.duplicateError': 'Помилка при дублюванні',
  'eduflow.versionHistory.duplicateMinor': 'Дублювати (другорядна)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Дублювати {{label}}?',
  'eduflow.versionHistory.empty': 'Версій не створено.',
  'eduflow.versionHistory.emptyHint': 'Збережіть основну або другорядну версію, щоб знайти її тут.',
  'eduflow.versionHistory.hoursAgo': '{{count}} год тому',
  'eduflow.versionHistory.latest': 'Найновіша',
  'eduflow.versionHistory.minutesAgo': '{{count}} хв тому',
  'eduflow.versionHistory.newFromCurrent': 'Нова версія з поточного вмісту',
  'eduflow.versionHistory.openPublicPlayer': 'Відкрити публічний плеєр',
  'eduflow.versionHistory.preview': 'Попередній перегляд',
  'eduflow.versionHistory.previewBlocks': '{{count}} блоків',
  'eduflow.versionHistory.previewEdges': '{{count}} з\'єднань',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Попередній перегляд {{label}}',
  'eduflow.versionHistory.public': 'Публічна версія',
  'eduflow.versionHistory.publicActive': 'Активна для учнів',
  'eduflow.versionHistory.setPublic': 'Активувати',
  'eduflow.versionHistory.startDescModalTitle': 'Опис — блок Старт ({{version}})',
  'eduflow.versionHistory.yesterday': 'вчора',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Триває переклад…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Виявлено циклічний шлях',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} помилка(ок)',
  'designer.validation.warningCount': '{{count}} попередження',
  'designer.validation.titleRequired': 'Блок « {{type}} » має мати заголовок.',
  'designer.validation.summary': 'Маршрут: {{blocks}} блок(ів), {{edges}} зв’язок.',
  'designer.validation.multipleStart': 'Кілька блоків «Початок» ({{count}}): дозволено лише один.',
  'designer.validation.noOutgoing': 'Блок « {{type}} » не має вихідного зв’язку до наступного блоку.',
  'designer.validation.tooManyOutputs': '« {{label}} »: забагато вихідних зв’язків ({{count}}, максимум {{max}}).',
  'designer.validation.deadEndBranch': 'Гілка без шляху до блоку «Кінець» (від « {{label}} »).',
  'designer.validation.moreDeadEnds': '… і ще {{count}} гілок без виходу до кінця.',
  'designer.validation.noConvergence': 'Гілки від « {{label}} » не сходяться до спільного блоку.',
  'designer.validation.orphanBlock': 'Блок ізольовано від основного графа: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'Блок «Кінець» « {{title}} » ще має вихідний зв’язок.',
  'designer.validation.noScoring': 'Параметри оцінювання для « {{title}} » не визначено.',
  'designer.validation.emptyText': 'Текстовий блок « {{title}} » порожній.',
  'designer.validation.textTooLong':
    'Текстовий блок « {{title}} » оцінено у {{lines}} рядків. Рекомендовано: від {{min}} до {{max}} рядків.',
  'designer.validation.splitTextBlock': 'Розділити',
  'designer.validation.textSplitNotNeeded':
    'Цей блок не перевищує рекомендований поріг читання.',
  'designer.validation.textSplitUnable':
    'Не вдалося запропонувати надійний автоматичний поділ для цього блоку.',
  'designer.validation.textSplitComplexFlow':
    'Автоматичний поділ недоступний для цього блоку (кілька/умовні гілки).',
  'designer.validation.textSplitDone': 'Створено {{count}} текстових блоків',
  'designer.validation.textSplitConfirmTitle':
    'Автоматично розділити цей текстовий блок?',
  'designer.validation.textSplitConfirmMessage':
    'Блок « {{title}} » оцінено у {{lines}} рядків. Ми поділимо його на {{parts}} блоків (за розділовими знаками) та розмістимо нижче у правильному порядку.',
  'designer.validation.textSplitConfirmAction': 'Розділити блок',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Голос за замовчуванням',
  'voicePanel.provider': 'Постачальник: Mistral AI',
  'voicePanel.hint': 'Оберіть голос для озвучення блоків.',
  'voicePanel.translationFrenchFallback':
    'Переклад аудіо: якщо тут не збережено голос за замовчуванням, для французької використовується голос «Marie - sad» (Mistral).',
  'voicePanel.search': 'Пошук…',
  'voicePanel.allLanguages': 'Усі мови',
  'voicePanel.loading': 'Завантаження голосів…',
  'voicePanel.loadError': 'Не вдалося завантажити голоси',
  'voicePanel.empty': 'Голоси недоступні.',
  'voicePanel.emptyHint': 'Перевірте ключ API Mistral у Адміністрування → Інтеграції.',
  'voicePanel.noResults': 'Жоден голос не відповідає вашому пошуку.',
  'voicePanel.saved': 'Голос за замовчуванням збережено',
  'voicePanel.saveError': 'Помилка збереження',
  'voicePanel.default': 'За замовчуванням',
  'voicePanel.female': 'Жіночий',
  'voicePanel.male': 'Чоловічий',
  'voicePanel.playPreview': 'Прослухати голос',
  'voicePanel.stopPreview': 'Зупинити',
  'voicePanel.noPreview': 'Попередній перегляд недоступний',
  'voicePanel.previewLoading': 'Створення…',
  'voicePanel.previewGenerateError': 'Не вдалося згенерувати аудіопопередній перегляд.',
  'voicePanel.previewPlayError': 'Не вдалося відтворити аудіо.',
  'voicePanel.filterNoVoicesForLang':
    'У каталозі немає голосу для цієї мови — спробуйте «Усі мови».',
  'episodeEditor.voicePicker.blockVoice': 'Voice for this block',
  'episodeEditor.voicePicker.defaultVoice': 'Default voice',
  'episodeEditor.voicePicker.voices': 'voices',
  'episodeEditor.voicePicker.searchPlaceholder': 'Search a voice…',
  'episodeEditor.voicePicker.noVoiceFound': 'No voice found',
  'episodeEditor.voicePicker.female': 'Female',
  'episodeEditor.voicePicker.male': 'Male',
  'episodeEditor.voicePicker.stop': 'Stop',
  'episodeEditor.voicePicker.preview': 'Preview voice',
  'episodeEditor.voicePicker.stopPlayback': 'Stop playback',
  'episodeEditor.errors.notFound': 'Episode not found',
  'episodeEditor.errors.titleRequired': 'Title is required.',
  'episodeEditor.errors.saveFailed': 'Error saving.',
  'episodeEditor.errors.noVoicesAvailable': 'No voices available. Enter the API key in Administration → Platform integrations, then reload the page.',
  'episodeEditor.errors.selectVoiceFirst': 'Select a voice in the "Voice" tab before generating voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'One or more blocks have an invalid voice. Open the per-block voice selector or reload the voice list.',
  'episodeEditor.errors.addTextFirst': 'Add text before generating audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audio generation failed. {{message}} (displayed duration = estimate.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error during audio generation. Displayed duration = estimate.',
  'episodeEditor.success.published': 'Episode published!',
  'episodeEditor.success.draftSaved': 'Draft saved',
  'episodeEditor.success.audioGenerated': 'Audio generated with {{count}} block(s)',
  'episodeEditor.tabs.edit': 'Edit',
  'episodeEditor.tabs.chapters': 'Chapters',
  'episodeEditor.tabs.voice': 'Voice',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Undo (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Redo (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generating…',
  'episodeEditor.toolbar.generateAudio': 'Generate audio',
  'episodeEditor.toolbar.save': 'Save',
  'episodeEditor.toolbar.publish': 'Publish',
  'episodeEditor.titlePlaceholder': 'Episode title…',
  'episodeEditor.status.published': 'Published',
  'episodeEditor.status.draft': 'Draft',
  'episodeEditor.warnings.mistralNotConfigured': 'Episode voiceover uses Mistral Voxtral TTS. Add the Mistral key in Administration → Platform integrations, then reload this page.',
  'episodeEditor.warnings.mistralNoVoices': 'The Mistral key is recognized but the API returned no voices. Check backend logs.',
  'episodeEditor.warnings.voiceLoadFailed': 'Unable to load voice list. Check Mistral configuration in Administration → Platform integrations.',
  'episodeEditor.warnings.voiceOffDisabled': 'Without a real voice, voiceover generation remains disabled.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthesis via Mistral Voxtral TTS. Choose the voice in the "Voice" tab or per block.',
  'episodeEditor.edit.coverImage': 'Cover image',
  'episodeEditor.edit.coverAlt': 'Episode cover',
  'episodeEditor.edit.deleteCover': 'Delete',
  'episodeEditor.edit.dropOrClickImage': 'Click or drop an image',
  'episodeEditor.edit.selectedVoice': 'Selected voice',
  'episodeEditor.edit.selectVoice': 'Select a voice',
  'episodeEditor.edit.defaultVoiceByLang': 'Default voice (podcast language)',
  'episodeEditor.edit.lastAudioGenerated': 'Last generated audio',
  'episodeEditor.edit.audioNotFound': 'Audio file not found — regenerate the voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generating…',
  'episodeEditor.edit.generateVoiceOff': 'Generate voiceover',
  'episodeEditor.chapters.title': 'Chapters',
  'episodeEditor.chapters.add': 'Add',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapter {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} block(s)',
  'episodeEditor.chapters.readByAi': 'Read chapter title',
  'episodeEditor.chapters.readByAiHint':
    'When off, the AI does not read this chapter title aloud. The chapter blocks stay included in the voiceover.',
  'episodeEditor.voiceTab.availableVoices': 'Available voices',
  'episodeEditor.podcasts.linked': 'Linked podcasts',
  'episodeEditor.podcasts.assign': 'Assign',
  'episodeEditor.podcasts.noneAvailable': 'No podcast available',
  'episodeEditor.podcasts.close': 'Close',
  'episodeEditor.podcasts.noneLinked': 'No linked podcast',
  'episodeEditor.podcasts.assignHint': 'Assign this episode to one or more podcasts',
  'episodeEditor.podcasts.remove': 'Remove',
  'episodeEditor.editor.addTextBlock': 'Add a text block',
  'episodeEditor.editor.scriptPlaceholder': 'Start writing your script…',
  'episodeEditor.editor.addBlock': 'Add a block',
  'episodeEditor.player.loop': 'Loop',
  'episodeEditor.player.playbackSpeed': 'Playback speed',
  'episodeEditor.player.collapse': 'Collapse',
  'episodeEditor.player.expand': 'Expand',
  'episodeEditor.player.waveformHidden': 'Waveform hidden',
  'podcast.episodes.listen': 'Listen',
  'podcast.episodes.noAudioShort': 'No audio available',
  'podcast.episodes.edit': 'Edit',
  'podcast.episodes.delete': 'Delete',
  'podcast.episodes.unmute': 'Unmute',
  'podcast.episodes.mute': 'Mute',
  'podcast.episodes.totalEpisodes': '{{count}} episode(s) total',
  'eduflow.designer.toast.blockCopied': 'Блок скопійовано',
  'eduflow.designer.toast.blockPasted': 'Блок вставлено',
  'eduflow.designer.toast.blockDuplicated': 'Блок здубльовано',
  'eduflow.designer.toast.connectionUpdated': 'З\'єднання оновлено',
  'eduflow.designer.toast.flowExported': 'Курс експортовано',
  'eduflow.designer.toast.flowImported': 'Курс імпортовано',
  'eduflow.designer.toast.invalidFile': 'Невалідний файл',
  'eduflow.designer.untitled': 'Без назви',
  'eduflow.designer.importedFlow': 'Імпортований курс',
  'eduflow.designer.collaborator.online': 'Онлайн',
  'eduflow.designer.collaborator.offline': 'Офлайн',
  'eduflow.designer.collaborator.defaultName': 'Користувач',
  'eduflow.designer.collaborator.editor': 'Редактор',
  'eduflow.designer.collaborator.viewer': 'Переглядач',
  'eduflow.designer.resizePanel': 'Змінити розмір панелі',
  'eduflow.designer.published': 'Опубліковано',
  'eduflow.designer.draft': 'Чернетка',
  'eduflow.designer.quizOutput.success': 'Пройдено',
  'eduflow.designer.quizOutput.failure': 'Не пройдено',
  'eduflow.designer.handle.default': 'За замовчуванням',
  'eduflow.designer.genError': 'Помилка під час генерації. Спробуйте ще раз.',
  'trainerTour.welcome.title': 'Ласкаво просимо до простору викладача',
  'trainerTour.welcome.description':
    'Ця коротка екскурсія показує панель і меню. Уперше на кожній сторінці з’являється підказка з головним. Закрийте крок хрестиком — екскурсія більше не запуститься.',
  'trainerTour.dashboard.header.title': 'Ваша панель',
  'trainerTour.dashboard.header.description':
    'Огляд активності: ключові показники та швидкі дії для створення контенту.',
  'trainerTour.dashboard.actions.title': 'Створити контент',
  'trainerTour.dashboard.actions.description':
    'Ярлики для нового курсу EduFlow, подкасту чи траєкторії. Почніть проєкт без меню.',
  'trainerTour.dashboard.stats.title': 'Показники',
  'trainerTour.dashboard.stats.description':
    'Курси, опубліковані траєкторії, учні та подкасти — масштаб каталогу одним поглядом.',
  'trainerTour.dashboard.activity.title': 'Остання активність',
  'trainerTour.dashboard.activity.description':
    'Нещодавно змінений контент. Клацніть рядок, щоб відкрити курс, подкаст або траєкторію.',
  'trainerTour.sidebar.title': 'Бічне меню',
  'trainerTour.sidebar.description':
    'Огляд (головна, учні, аналітика, завдання), створення (курси, подкасти, траєкторії, вебінари якщо увімкнено), медіатека та довідка. Відкрийте кожний пункт — уперше з’явиться контекстна підказка.',
  'trainerTour.routes.learners.title': 'Учні',
  'trainerTour.routes.learners.description':
    'Записані на ваш контент, прогрес, експорт — центр навчального супроводу.',
  'trainerTour.routes.analytics.title': 'Аналітика',
  'trainerTour.routes.analytics.description':
    'Діаграми залучення та завершення. Налаштуйте вигляд під свої пріоритети.',
  'trainerTour.routes.eduflow.title': 'Курси (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Створюйте та керуйте інтерактивними курсами, версіями, публікацією та поширенням — ядро дизайну навчання.',
  'trainerTour.routes.podcasts.title': 'Подкасти',
  'trainerTour.routes.podcasts.description':
    'Шоу, епізоди, видимість і поширення для груп або учнів вашої установи.',
  'trainerTour.routes.parcours.title': 'Траєкторії',
  'trainerTour.routes.parcours.description':
    'Ланцюжки курсів і ресурсів: довгі шляхи навчання та призначення аудиторіям.',
  'trainerTour.routes.videos.title': 'Відео',
  'trainerTour.routes.videos.description':
    'Бібліотека відео для повторного використання в курсах і документах.',
  'trainerTour.routes.sounds.title': 'Аудіо',
  'trainerTour.routes.sounds.description':
    'Аудіофайли та теги для подкастів і звукових блоків у курсах.',
  'trainerTour.routes.images.title': 'Зображення',
  'trainerTour.routes.images.description':
    'Централізовані зображення: імпорт, теки та повторне використання.',
  'trainerTour.routes.documents.title': 'Документи',
  'trainerTour.routes.documents.description':
    'PDF та вкладення, за потреби з цільовим доступом для учнів.',
  'trainerTour.routes.help.title': 'Довідка',
  'trainerTour.routes.help.description':
    'Статті, посібники й матеріали про LyrraStudio. Повертайтеся, якщо щось незрозуміло.',
  'trainerTour.routes.producerAssignments.title': 'Виробничі завдання',
  'trainerTour.routes.producerAssignments.description':
    'Коли HR призначає виробництво позицій каталогу, вони з’являються тут зі статусом.',
  'trainerTour.routes.webinars.title': 'Вебінари',
  'trainerTour.routes.webinars.description':
    'Плануйте live-сесії, запрошення та посилання на трансляцію для ваших груп.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Далі',
  'trainerTour.driver.prev': 'Назад',
  'trainerTour.driver.done': 'Готово',
  'trainerTour.driver.gotIt': 'Зрозуміло',
  'pages.learners.title': 'Учні',
  'pages.learners.description': 'Відстежуйте прогрес своїх учнів',
  'pages.learners.enrolledFormations': 'Записані курси',
  'pages.learners.notStarted': 'Не розпочато',
  'pages.learners.startedOn': 'Початок {{date}}',
  'pages.learners.detailedTraces': 'Детальна активність · {{flowTitle}}',
  'pages.learners.statsEvents': 'Події: {{count}}',
  'pages.learners.statsResponses': 'Відповіді: {{count}}',
  'pages.learners.statsBlockSessions': 'Сесії блоків: {{count}}',
  'pages.learners.timePerBlock': 'Час на блок',
  'pages.learners.lastEvents': 'Останні події',
  'pages.learners.blockSessionInProgress': '(триває)',
  'pages.learners.blockTimeOnBlock': '({{duration}} на цьому блоці)',
  'pages.learners.trackingEvent.unknown': 'Подія',
  'pages.learners.trackingEvent.training_enter': 'Курс відкрито',
  'pages.learners.trackingEvent.training_exit': 'Курс закрито або покинуто',
  'pages.learners.trackingEvent.training_completed': 'Курс завершено',
  'pages.learners.trackingEvent.block_enter': 'Вхід у блок',
  'pages.learners.trackingEvent.block_exit': 'Вихід з блоку',
  'pages.learners.trackingEvent.response_submitted': 'Відповідь надіслано',
  'pages.learners.trackingEvent.progress_sync': 'Прогрес синхронізовано',
  'pages.learners.refresh': 'Оновити',
  'pages.learners.exportCsv': 'Експорт CSV',
  'pages.learners.statTotal': 'Учнів загалом',
  'pages.learners.statInProgress': 'Триває',
  'pages.learners.statCompleted': 'Завершено',
  'pages.learners.statAvgProgress': 'Сер. прогрес',
  'pages.learners.searchPlaceholder': 'Пошук за ім’ям або email…',
  'pages.learners.filterAllFlows': 'Усі курси',
  'pages.learners.sortRecent': 'Найновіші',
  'pages.learners.sortName': 'Ім’я А–Я',
  'pages.learners.sortProgress': 'Прогрес',
  'pages.learners.colLearner': 'Учень',
  'pages.learners.colFormations': 'Курси',
  'pages.learners.colProgress': 'Прогрес',
  'pages.learners.colCompleted': 'Завершено',
  'pages.learners.colTime': 'Час',
  'pages.learners.colLastAccess': 'Останній доступ',
  'pages.learners.colActions': 'Дії',
  'pages.learners.openProgressDashboard': 'Панель прогресу',
  'pages.learners.dashboardTitle': 'Прогрес учня',
  'pages.learners.dashboardBack': 'Назад до списку',
  'pages.learners.dashboardInvalidId': 'Некоректний ідентифікатор учня',
  'pages.learners.dashboardNotFound': 'Учня не знайдено або доступ заборонено',
  'pages.learners.dashboardOverviewHint':
    'Зведений перегляд: курси EduFlow учасника, шляхи та подкасти, призначені обліковому запису Lyrra з тією ж електронною поштою.',
  'pages.learners.tabOverview': 'Огляд',
  'pages.learners.tabFormations': 'Курси',
  'pages.learners.tabParcours': 'Шляхи',
  'pages.learners.tabPodcasts': 'Подкасти',
  'pages.learners.sectionFormations': 'Курси',
  'pages.learners.sectionParcours': 'Шляхи',
  'pages.learners.sectionPodcasts': 'Подкасти',
  'pages.learners.emptyParcours': 'Немає шляху, призначеного обліковому запису Lyrra з цією поштою.',
  'pages.learners.emptyPodcasts': 'Немає подкасту, призначеного обліковому запису Lyrra з цією поштою.',
  'pages.learners.linkedAccountHint':
    'Шляхи та подкасти з’являються, якщо існує користувач Lyrra з тією ж поштою, що й у учасника.',
  'pages.learners.kpiEduflows': 'Курси',
  'pages.learners.kpiAvgProgress': 'Сер. прогрес',
  'pages.learners.kpiPaths': 'Шляхи',
  'pages.learners.kpiPodcasts': 'Подкасти',
  'pages.learners.kpiCompletedFormations': 'Завершено: {{count}}',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} активностей',
  'pages.learners.pathAssignedOn': 'Додано {{date}}',
  'pages.learners.podcastCompleted': 'Завершено',
  'pages.learners.podcastInProgress': 'Триває',
  'pages.learners.pathStatusPublished': 'Опубліковано',
  'pages.learners.pathStatusDraft': 'Чернетка',
  'pages.learners.openInDesigner': 'Відкрити в редакторі',
  'pages.learners.openParcoursEditor': 'Відкрити шлях',
  'pages.learners.viewPodcasts': 'До подкастів',
  'pages.learners.loading': 'Завантаження учнів…',
  'pages.learners.loadError': 'Не вдалося завантажити учнів',
  'pages.learners.retry': 'Повторити',
  'pages.learners.emptyFiltered': 'Учнів не знайдено',
  'pages.learners.emptyFilteredHint': 'Спробуйте змінити критерії пошуку',
  'pages.learners.emptyNone': 'Немає записаних учнів',
  'pages.learners.emptyNoneHint':
    'Учні з’являться тут, коли запишуться на ваші опубліковані курси',
  'pages.learners.pagination': 'Сторінка {{page}} з {{totalPages}} · {{total}} учн.',
  'pages.learners.prev': 'Назад',
  'pages.learners.next': 'Далі',
  'pages.learners.csvLastName': 'Прізвище',
  'pages.learners.csvFirstName': 'Ім’я',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Курси',
  'pages.learners.csvAvgProgress': 'Середній прогрес',
  'pages.learners.csvTotalTime': 'Загальний час',
  'pages.learners.csvLastActivity': 'Остання активність',
  'pages.learners.statsAdaptiveChoice':
    'Підказки — прийнято: {{accepted}} · відхилено: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Оцінок адаптації: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Використано спрощених версій: {{count}}',
  'player.lightMode': 'Світла тема',
  'player.darkMode': 'Темна тема',
};

const ruTranslations: Record<string, string> = {
  // Navigation
  'nav.dashboard': 'Панель управления',
  'nav.projects': 'Проекты',
  'nav.eduflow': 'EduFlow',
  'nav.audiobooks': 'Аудиокниги',
  'nav.audio_studio': 'Аудиостудия',
  'nav.store': 'Магазин',
  'nav.analytics': 'Аналитика',
  'nav.users': 'Пользователи',
  'nav.settings': 'Настройки',
  'nav.profile': 'Профиль',
  'nav.notifications': 'Уведомления',
  'nav.help': 'Помощь',
  'nav.community': 'Сообщество',
  'nav.library': 'Библиотека',
  'nav.certificates': 'Сертификаты',
  'nav.institution': 'Учреждение',
  'nav.groups': 'Группы',
  'nav.learners': 'Учащиеся',
  'nav.myLearning': 'Моё обучение',
  'learner.sidebar.tagline': 'Учащийся',
  'nav.reports': 'Отчёты',
  'nav.qualiopi': 'Qualiopi',
  'nav.training_plan': 'План обучения',
  'nav.catalog': 'Каталог',
  'nav.competencies': 'Компетенции',
  'nav.budget': 'Бюджет',
  'nav.campaigns': 'Кампании',
  'nav.home': 'Главная',
  'nav.producerAssignments': 'Производственные миссии',
  'nav.videos': 'Видео',
  'nav.sounds': 'Аудио',
  'nav.images': 'Изображения',
  'nav.documents': 'Документы',
  'nav.podcasts': 'Подкасты',
  'nav.parcours': 'Маршруты',
  'nav.eduflowList': 'Курсы',
  'nav.section.pilotage': 'Управление',
  'nav.section.creation': 'Создание',
  'nav.section.medias': 'Медиа',
  'nav.section.aide': 'Помощь',
  'sidebar.brandTagline': 'Обучение',
  'sidebar.logoInstitutionAlt': 'Логотип организации',
  'sidebar.expand': 'Развернуть меню',
  'sidebar.collapse': 'Свернуть меню',
  'sidebar.expandAria': 'Развернуть боковую панель',
  'sidebar.collapseAria': 'Свернуть боковую панель',
  'learner.sidebar.collapse': 'Свернуть',
  'learner.sidebar.expandAria': 'Развернуть боковую панель',
  'learner.sidebar.collapseAria': 'Свернуть боковую панель',
  'dashboard.title': 'Панель управления',
  'dashboard.welcome': 'Добро пожаловать, {{name}}!',
  'dashboard.welcomeSub': 'Вот обзор вашего рабочего пространства.',
  'dashboard.projects': 'Проекты',
  'dashboard.subtitle': 'Управляйте курсами и отслеживайте прогресс учащихся.',
  'dashboard.newProject': 'Новый курс',
  'dashboard.newEduFlow': 'Новый маршрут',
  'dashboard.uploadMedia': 'Загрузить медиа',
  'dashboard.viewDocs': 'Документация',
  'dashboard.revenue': 'Доход',
  'dashboard.eduflows': 'Курсы',
  'dashboard.learners': 'Учащиеся',
  'dashboard.recentActivity': 'Недавняя активность',
  'dashboard.recentProjects': 'Последние проекты',
  'dashboard.noRecentProjects': 'Нет последних проектов',
  'dashboard.activity': 'Активность',
  'dashboard.activityEmpty': 'Нет недавней активности по курсам или подкастам.',
  'dashboard.loadError': 'Не удалось загрузить данные панели.',
  'dashboard.publishedFlows': 'Опубликованные маршруты',
  'dashboard.roleSpecific': 'Для этой роли доступна отдельная панель.',
  'dashboard.kindPodcast': 'Подкаст',
  'pages.analytics.title': 'Аналитика',
  'pages.analytics.description': 'Панели и статистика',
  'pages.analytics.subtitle':
    'Фильтруйте данные, перетаскивайте графики для изменения порядка и настраивайте размер виджетов.',
  'pages.analytics.filters.period': 'Период',
  'pages.analytics.filters.flow': 'Курс',
  'pages.analytics.filters.allFlows': 'Все курсы',
  'pages.analytics.period.7d': 'Последние 7 дней',
  'pages.analytics.period.30d': 'Последние 30 дней',
  'pages.analytics.period.90d': 'Последние 90 дней',
  'pages.analytics.period.365d': 'Последние 12 месяцев',
  'pages.analytics.addChart': 'Добавить диаграмму',
  'pages.analytics.resetLayout': 'Сбросить расположение',
  'pages.analytics.dragHint': 'Перетащите для изменения порядка',
  'pages.analytics.size.sm': 'S',
  'pages.analytics.size.md': 'M',
  'pages.analytics.size.lg': 'L',
  'pages.analytics.removeChart': 'Убрать эту диаграмму',
  'pages.analytics.kind.completion_line': 'Завершение со временем',
  'pages.analytics.kind.learners_bar': 'Учащиеся по модулю',
  'pages.analytics.kind.status_pie': 'Распределение по статусам',
  'pages.analytics.kind.engagement_area': 'Время вовлечённости',
  'pages.analytics.series.completion': 'Доля завершения (%)',
  'pages.analytics.series.learners': 'Учащиеся',
  'pages.analytics.series.hours': 'Часы',
  'pages.analytics.status.completed': 'Завершено',
  'pages.analytics.status.in_progress': 'В процессе',
  'pages.analytics.status.dropped': 'Брошено',
  'pages.analytics.status.not_started': 'Не начато',
  'pages.analytics.loadError': 'Не удалось загрузить данные аналитики.',
  'pages.analytics.retry': 'Повторить',
  'pages.analytics.axis.bucket': 'P. {{n}}',
  'pages.analytics.mock.wd0': 'пн',
  'pages.analytics.mock.wd1': 'вт',
  'pages.analytics.mock.wd2': 'ср',
  'pages.analytics.mock.wd3': 'чт',
  'pages.analytics.mock.wd4': 'пт',
  'pages.analytics.mock.wd5': 'сб',
  'pages.analytics.mock.wd6': 'вс',
  'pages.analytics.emptyCharts': 'Добавьте хотя бы одну диаграмму, чтобы увидеть данные.',
  'pages.analytics.comingSoonDesc':
    'Смотрите статистику обучения, долю завершения, вовлечённость учащихся и общие показатели.',
  'project.status.draft': 'Черновик',
  'project.status.published': 'Опубликовано',
  'project.status.archived': 'Архивировано',
  'eduflowList.status.draft': 'Черновик',
  'eduflowList.status.published': 'Опубликовано',
  'eduflowList.status.archived': 'Архивировано',
  'eduflowList.badge.enterpriseShell': 'Корпоративный запрос',
  'eduflowList.badge.enterpriseLinked': 'Организация',
  'eduflowList.badge.toProduce': 'К производству',
  // Common buttons
  'btn.save': 'Сохранить',
  'btn.cancel': 'Отмена',
  'btn.delete': 'Удалить',
  'btn.edit': 'Редактировать',
  'btn.create': 'Создать',
  'btn.add': 'Добавить',
  'btn.close': 'Закрыть',
  'btn.confirm': 'Подтвердить',
  'btn.back': 'Назад',
  'btn.next': 'Далее',
  'btn.previous': 'Назад',
  'btn.submit': 'Отправить',
  'btn.search': 'Поиск',
  'btn.filter': 'Фильтр',
  'btn.export': 'Экспорт',
  'btn.import': 'Импорт',
  'btn.download': 'Скачать',
  'btn.upload': 'Загрузить',
  'btn.refresh': 'Обновить',
  'btn.retry': 'Повторить',
  'btn.view': 'Просмотреть',
  'btn.details': 'Детали',
  'btn.publish': 'Опубликовать',
  'btn.unpublish': 'Снять с публикации',
  'btn.duplicate': 'Дублировать',
  'btn.archive': 'Архивировать',
  'btn.restore': 'Восстановить',
  'btn.send': 'Отправить',
  'btn.apply': 'Применить',
  'btn.reset': 'Сбросить',
  'btn.select_all': 'Выбрать все',
  'btn.deselect_all': 'Снять выбор',
  'btn.load_more': 'Загрузить больше',
  'btn.show_more': 'Показать больше',
  'btn.show_less': 'Показать меньше',
  'btn.copy': 'Копировать',
  'btn.share': 'Поделиться',
  'btn.preview': 'Предварительный просмотр',
  'btn.continue': 'Продолжить',
  'btn.skip': 'Пропустить',
  // Form labels
  'form.email': 'Электронная почта',
  'form.password': 'Пароль',
  'form.password_confirm': 'Подтвердить пароль',
  'form.first_name': 'Имя',
  'form.last_name': 'Фамилия',
  'form.name': 'Имя',
  'form.phone': 'Телефон',
  'form.address': 'Адрес',
  'form.city': 'Город',
  'form.zip': 'Почтовый индекс',
  'form.country': 'Страна',
  'form.title': 'Заголовок',
  'form.description': 'Описание',
  'form.category': 'Категория',
  'form.tags': 'Теги',
  'form.status': 'Статус',
  'form.role': 'Роль',
  'form.language': 'Язык',
  'form.date': 'Дата',
  'form.start_date': 'Дата начала',
  'form.end_date': 'Дата окончания',
  'form.duration': 'Продолжительность',
  'form.price': 'Цена',
  'form.avatar': 'Фото профиля',
  'form.required': 'Обязательно',
  'form.optional': 'Необязательно',
  'form.select_placeholder': 'Выбрать...',
  'form.search_placeholder': 'Поиск...',
  'form.no_results': 'Нет результатов',
  'form.file_drop': 'Перетащите файл сюда или нажмите для выбора',
  'form.max_size': 'Максимальный размер: {{size}}',
  'form.institution': 'Учреждение',
  'form.department': 'Отдел',
  'form.group': 'Группа',
  'form.level': 'Уровень',
  'form.notes': 'Заметки',
  'form.url': 'URL',
  'form.content': 'Содержание',
  'form.thumbnail': 'Миниатюра',
  // Validation errors
  'error.required': 'Это поле обязательно',
  'error.email_invalid': 'Неверный адрес электронной почты',
  'error.password_min': 'Пароль должен содержать не менее {{min}} символов',
  'error.password_match': 'Пароли не совпадают',
  'error.min_length': 'Минимум {{min}} символов',
  'error.max_length': 'Максимум {{max}} символов',
  'error.min_value': 'Минимальное значение: {{min}}',
  'error.max_value': 'Максимальное значение: {{max}}',
  'error.invalid_url': 'Неверный URL',
  'error.file_too_large': 'Файл слишком большой',
  'error.file_type': 'Неподдерживаемый тип файла',
  'error.generic': 'Произошла ошибка',
  'error.network': 'Ошибка сетевого подключения',
  'error.unauthorized': 'Необходима авторизация',
  'error.forbidden': 'Доступ запрещён',
  'error.not_found': 'Ресурс не найден',
  'error.server': 'Внутренняя ошибка сервера',
  'error.conflict': 'Этот ресурс уже существует',
  'error.rate_limit': 'Слишком много запросов, пожалуйста, подождите',
  // Auth
  'auth.login': 'Войти',
  'auth.logout': 'Выйти',
  'auth.register': 'Зарегистрироваться',
  'auth.forgot_password': 'Забыли пароль?',
  'auth.reset_password': 'Сбросить пароль',
  'auth.otp_title': 'Вход по коду',
  'auth.otp_sent': 'Код подтверждения отправлен на {{email}}',
  'auth.otp_placeholder': 'Введите 6-значный код',
  'auth.google_login': 'Продолжить с Google',
  'auth.remember_me': 'Запомнить меня',
  'auth.no_account': 'Нет аккаунта?',
  'auth.has_account': 'Уже есть аккаунт?',
  'auth.welcome_back': 'С возвращением, {{name}}!',
  'auth.login_success': 'Вход выполнен успешно',
  'auth.register_success': 'Аккаунт успешно создан',
  'auth.logout_success': 'Выход выполнен успешно',
  // Roles
  'role.teacher': 'Преподаватель',
  'role.institution': 'Учреждение',
  'role.admin': 'Администратор',
  'role.drh': 'Директор по персоналу',
  'role.rh': 'Менеджер по персоналу',
  'role.manager': 'Менеджер',
  'role.collaborator': 'Сотрудник',
  'role.learner': 'Учащийся',
  'dashboard.recent_projects': 'Последние проекты',
  'dashboard.recent_activity': 'Последняя активность',
  'dashboard.quick_actions': 'Быстрые действия',
  'dashboard.stats': 'Статистика',
  'dashboard.total_projects': 'Всего проектов',
  'dashboard.total_learners': 'Всего учащихся',
  'dashboard.total_eduflows': 'Всего EduFlows',
  'dashboard.completion_rate': 'Процент завершения',
  'dashboard.active_users': 'Активные пользователи',
  'dashboard.new_registrations': 'Новые регистрации',
  'dashboard.xp_earned': 'Заработано XP',
  'dashboard.streak': 'Текущая серия',
  'dashboard.level': 'Уровень {{level}}',
  'dashboard.badges_earned': 'Полученные значки',
  'dashboard.next_level': 'Следующий уровень',
  'dashboard.no_projects': 'Пока нет проектов',
  'dashboard.create_first': 'Создайте свой первый проект',
  'dashboard.pending_reviews': 'Ожидающие оценки',
  'dashboard.upcoming_deadlines': 'Предстоящие сроки',
  // Podcast — эпизоды
  'podcast.episodes.pageTitle': 'Эпизоды',
  'podcast.episodes.count': '{{count}} эпизод(ов)',
  'podcast.episodes.newEpisode': 'Новый эпизод',
  'podcast.episodes.searchPlaceholder': 'Поиск по названию…',
  'podcast.episodes.filterAllStatuses': 'Все статусы',
  'podcast.episodes.filterDraft': 'Черновик',
  'podcast.episodes.filterPublished': 'Опубликован',
  'podcast.episodes.emptyTitle': 'Эпизоды не найдены',
  'podcast.episodes.emptyHint': 'Создайте первый эпизод',
  'podcast.episodes.emptyCreateCta': 'Создать эпизод',
  'podcast.episodes.backPodcasts': 'Подкасты',
  'podcast.episodes.paginationTotal': 'Всего {{total}} эпизод(ов)',
  'podcast.episodes.pageOf': 'Страница {{page}} из {{totalPages}}',
  'podcast.episodes.deleteTitle': 'Удалить эпизод?',
  'podcast.episodes.deleteHint': '« {{title}} » будет удалён безвозвратно.',
  'podcast.episodes.deleteCancel': 'Отмена',
  'podcast.episodes.deleteConfirm': 'Удалить',
  'podcast.episodes.statusPublished': 'Опубликован',
  'podcast.episodes.statusDraft': 'Черновик',
  'podcast.episodes.noDescription': 'Нет описания',
  'podcast.episodes.noAudio': 'Нет аудио',
  'podcast.episodes.retry': 'Повторить',
  'podcast.createEpisode.panelTitle': 'Новый эпизод',
  'podcast.createEpisode.panelSubtitle':
    'Укажите название и при желании описание и обложку. После подтверждения эпизод создаётся и откроется редактор.',
  'podcast.createEpisode.titleLabel': 'Название',
  'podcast.createEpisode.descriptionLabel': 'Описание',
  'podcast.createEpisode.coverLabel': 'Обложка (необязательно)',
  'podcast.createEpisode.coverHint': 'JPEG, PNG, WebP или GIF — до 5 МБ.',
  'podcast.createEpisode.coverChoose': 'Выбрать изображение',
  'podcast.createEpisode.coverRemove': 'Убрать изображение',
  'podcast.createEpisode.titlePlaceholder': 'напр. Введение в модуль безопасности',
  'podcast.createEpisode.descriptionPlaceholder': 'Кратко о содержании… (необязательно)',
  'podcast.createEpisode.validate': 'Создать и открыть редактор',
  'podcast.createEpisode.titleRequired': 'Название обязательно.',
  'podcast.createEpisode.error': 'Не удалось создать эпизод.',
  'podcast.createEpisode.uploadCoverError': 'Эпизод создан, но загрузка обложки не удалась.',
  'podcast.editor.defaultNewTitle': 'Новый эпизод',
  // EduFlow
  'eduflow.title': 'Редактор EduFlow',
  'eduflow.new_flow': 'Новый курс',
  'eduflow.list.title': 'Курсы',
  'eduflow.list.subtitle': 'Создавайте интерактивные учебные траектории',
  'eduflow.list.searchPlaceholder': 'Поиск курса…',
  'eduflow.list.filterAll': 'Все',
  'eduflow.list.filterDraft': 'Черновики',
  'eduflow.list.filterPublished': 'Опубликованные',
  'eduflow.list.filterArchived': 'Архив',
  'eduflow.list.viewModeGrid': 'Плитка',
  'eduflow.list.viewModeList': 'Список',
  'eduflow.list.viewByCategory': 'Вид по категориям',
  'eduflow.list.import': 'Импорт',
  'eduflow.list.importHint':
    'Файл .lyrra (медиа включены) или устаревший JSON-экспорт Lyrra V1.',
  'eduflow.list.importSuccess': 'Курс импортирован. Открываем редактор.',
  'eduflow.list.importError':
    'Импорт не удался — проверьте файл (.lyrra или корректный JSON-экспорт, хотя бы один блок).',
  'eduflow.list.categoryBack': 'Все категории',
  'eduflow.list.categoryBrowseHint': 'Выберите категорию, чтобы увидеть её курсы.',
  'eduflow.list.categoryFormationCount': '{{count}} курс(ов)',
  'eduflow.list.categoryEmptyFiltered':
    'Нет курсов в этом виде (активный фильтр или поиск).',
  'eduflow.list.uncategorizedCardHint': 'Курсы без метки категории.',
  'eduflow.list.uncategorized': 'Без категории',
  'eduflow.list.manageCategories': 'Управлять категориями',
  'eduflow.list.categoriesPanelTitle': 'Категории курса',
  'eduflow.list.noCategoryAvailable': 'Нет доступных категорий.',
  'eduflow.list.categoriesSaveError': 'Не удалось сохранить категории.',
  'eduflow.list.emptyFilteredTitle': 'Ничего не найдено',
  'eduflow.list.emptyFilteredHint': 'Измените критерии поиска или фильтры.',
  'eduflow.list.emptyFirstTitle': 'Создайте первый курс',
  'eduflow.list.emptyFirstHint': 'Интерактивное обучение в визуальном редакторе.',
  'eduflow.list.emptyCreateCta': 'Создать первый курс',
  'eduflow.list.deleteToTrashTitle': 'Переместить курс в корзину?',
  'eduflow.list.deleteToTrashBody':
    '« {{title}} » будет убран из списка (логическое удаление). Организация сможет восстановить его из корзины учреждения.',
  'eduflow.list.deleteToTrashConfirm': 'В корзину',
  'eduflow.list.blocksCount': '{{count}} блоков',
  'eduflow.list.noContent': 'Нет содержимого',
  'eduflow.list.minutesShort': '{{count}} мин',
  'eduflow.list.tooltip.openMissions': 'Открыть производственные миссии',
  'eduflow.list.tooltip.edit': 'Редактировать',
  'eduflow.list.shellHint':
    'Карточка определена вашей организацией — создайте EduFlow в разделе производственных миссий.',
  'eduflow.list.coverLabel': 'Обложка',
  'eduflow.list.coverRemove': 'Убрать обложку',
  'eduflow.list.coverUpload': 'Выбрать изображение',
  'eduflow.list.flowProperties': 'Название, описание и обложка',
  'eduflow.list.propertiesPanelTitle': 'Свойства курса',
  'eduflow.list.duplicatePanelTitle': 'Дублировать курс',
  'eduflow.list.duplicatePanelSubtitle': 'Исходный курс: {{name}}',
  'eduflow.list.duplicateTitleSuffix': 'копия',
  'eduflow.list.duplicateSuccess': 'Курс дублирован.',
  'eduflow.list.duplicateCoverHint':
    'Будут скопированы обложка и учебный контент исходного курса.',
  'eduflow.list.propertiesSaveError': 'Не удалось сохранить свойства.',
  'eduflow.list.propertiesSaved': 'Свойства сохранены.',
  'eduflow.list.propertiesUploadError': 'Не удалось загрузить изображение.',
  'eduflow.collaborativeBadge': 'Совместный',
  'eduflow.duplicateFlowError': 'Не удалось дублировать курс.',
  'eduflow.createFormation.panelTitle': 'Новый курс',
  'eduflow.createFormation.panelSubtitle':
    'Укажите название и описание. После подтверждения курс будет создан и откроется дизайнер потока.',
  'eduflow.createFormation.titleLabel': 'Название',
  'eduflow.createFormation.descriptionLabel': 'Описание',
  'eduflow.createFormation.titlePlaceholder': 'напр. Введение в модуль безопасности',
  'eduflow.createFormation.descriptionPlaceholder': 'Цели, аудитория и т.д. (необязательно)',
  'eduflow.createFormation.validate': 'Создать и открыть дизайнер',
  'eduflow.createFormation.titleRequired': 'Название обязательно.',
  'eduflow.createFormation.error': 'Не удалось создать курс.',
  'eduflow.shareFormation.title': 'Поделиться курсом',
  'eduflow.shareFormation.publicLinkTitle': 'Публичная ссылка (без входа)',
  'eduflow.shareFormation.publicLinkCopy': 'Копировать ссылку',
  'eduflow.shareFormation.publicLinkCopied': 'Скопировано',
  'eduflow.shareFormation.publishRequired':
    'Опубликуйте курс, чтобы получить ссылку без входа в систему.',
  'eduflow.shareFormation.embedTitle': 'Кнопка для встраивания и модальный плеер',
  'eduflow.shareFormation.embedHint':
    'Вставьте этот блок на сайт. Скрипт указывайте только один раз на страницу; для других курсов добавьте кнопки, скопировав строку кнопки и изменив URL в data-lyrra-eduflow-url. В URL плеера добавлен sidebar=0 (оглавление свёрнуто в модальном окне).',
  'eduflow.shareFormation.embedCopy': 'Копировать HTML',
  'eduflow.shareFormation.embedCopied': 'Скопировано',
  'eduflow.shareFormation.embedCloseAria': 'Закрыть',
  'eduflow.shareFormation.embedButtonLabel': 'Открыть: {{title}}',
  'eduflow.shareFormation.embedToggle': 'Встроить на сайт',
  'eduflow.shareFormation.removeAccessSuccess': 'Доступ для обучающегося отозван.',
  'eduflow.shareFormation.removeAccessError': 'Не удалось отозвать доступ обучающегося.',
  'eduflow.shareFormation.assignSuccess': 'Курс отправлен, приглашение по email отправлено.',
  'eduflow.shareFormation.assignError': 'Не удалось назначить этого обучающегося.',
  'eduflow.shareFormation.createAssignSuccess': 'Аккаунт создан/привязан, курс назначен, приглашение отправлено.',
  'eduflow.shareFormation.createLearnerError': 'Не удалось создать обучающегося.',
  'eduflow.shareFormation.selectExistingLearner': 'Выберите существующего обучающегося',
  'eduflow.shareFormation.searchLearnerPlaceholder': 'Поиск обучающегося (имя, фамилия, email)',
  'eduflow.shareFormation.loadingLearners': 'Загрузка обучающихся...',
  'eduflow.shareFormation.noLearnerAvailable': 'Нет доступных обучающихся.',
  'eduflow.shareFormation.hideQuickCreate': 'Скрыть быстрое создание',
  'eduflow.shareFormation.createNewLearner': 'Создать нового обучающегося',
  'eduflow.shareFormation.firstNameRequired': 'Имя *',
  'eduflow.shareFormation.lastNameRequired': 'Фамилия *',
  'eduflow.shareFormation.emailRequired': 'Email *',
  'eduflow.shareFormation.createShareInvite': 'Создать, назначить и отправить приглашение',
  'eduflow.shareFormation.assignedLearnersCount_one': 'Назначен {{count}} обучающийся',
  'eduflow.shareFormation.assignedLearnersCount_few': 'Назначено {{count}} обучающихся',
  'eduflow.shareFormation.assignedLearnersCount_many': 'Назначено {{count}} обучающихся',
  'eduflow.shareFormation.assignedLearnersCount_other': 'Назначено {{count}} обучающихся',
  'eduflow.shareFormation.noAssignedLearner': 'К этому курсу пока никто не назначен.',
  'eduflow.shareFormation.removeAccessTitle': 'Отозвать доступ',
  'eduflow.shareFormation.inviteEmailsTitle': 'Пригласить по email (существующий аккаунт)',
  'eduflow.shareFormation.inviteEmailsHint':
    'Один или несколько адресов: только аккаунты LyrraStudio (в т. ч. другой организации). Без аккаунта не добавляются — используйте «Создать нового обучающегося».',
  'eduflow.shareFormation.inviteEmailsPlaceholder':
    'По одному адресу в строке или через запятую\nexample@lyrra.fr, other@company.com',
  'eduflow.shareFormation.inviteEmailsSubmit': 'Добавить эти аккаунты к курсу',
  'eduflow.shareFormation.inviteEmailsInvalid': 'Введите хотя бы один корректный email.',
  'eduflow.shareFormation.inviteEmailsError': 'Не удалось добавить адреса.',
  'eduflow.shareFormation.inviteEmailsSummaryOk': '{{count}} аккаунт(ов) добавлено.',
  'eduflow.shareFormation.inviteEmailsSummaryNoAccount': '{{count}} без аккаунта LyrraStudio.',
  'eduflow.shareFormation.inviteEmailsSummaryAlready': '{{count}} уже назначено.',
  'eduflow.participant.flowTitle': 'Учебный маршрут',
  'eduflow.participant.stepWelcome': 'Добро пожаловать',
  'eduflow.participant.stepModule1': 'Модуль 1',
  'eduflow.participant.stepEvaluation': 'Оценка',
  'eduflow.participant.stepContentPlaceholder': 'Интерактивный контент шага для обучающегося.',
  'eduflow.edit_flow': 'Редактировать маршрут',
  'eduflow.flow_name': 'Название маршрута',
  'eduflow.block_palette': 'Палитра блоков',
  'eduflow.canvas': 'Холст',
  'eduflow.properties': 'Свойства',
  'eduflow.publish': 'Опубликовать',
  'eduflow.publish_confirm': 'Вы уверены, что хотите опубликовать этот маршрут?',
  'eduflow.save_draft': 'Сохранить черновик',
  'eduflow.preview': 'Предварительный просмотр',
  'eduflow.versions': 'Версии',
  'eduflow.add_block': 'Добавить блок',
  'eduflow.remove_block': 'Удалить блок',
  'eduflow.connect_blocks': 'Соединить блоки',
  'eduflow.undo': 'Отменить',
  'eduflow.redo': 'Повторить',
  'eduflow.zoom_in': 'Увеличить',
  'eduflow.zoom_out': 'Уменьшить',
  'eduflow.fit_view': 'Подобрать вид',
  'eduflow.auto_layout': 'Авторасположение',
  'eduflow.unsaved_changes': 'Несохранённые изменения',
  'eduflow.blocks.quiz_mcq': 'Тест с выбором',
  'eduflow.blocks.quiz_open': 'Открытый вопрос',
  'eduflow.blocks.video': 'Видео',
  'eduflow.blocks.audio': 'Аудио',
  'eduflow.blocks.text': 'Текст',
  'eduflow.blocks.evaluation': 'Оценка',
  'eduflow.blocks.form': 'Форма',
  'eduflow.blocks.mindmap': 'Ментальная карта',
  'eduflow.blocks.poll': 'Опрос',
  'eduflow.blocks.flashcard': 'Флеш-карта',
  'eduflow.blocks.timeline': 'Хронология',
  'eduflow.blocks.pdf': 'PDF-документ',
  'eduflow.blocks.image': 'Изображение',
  'eduflow.blocks.code': 'Код',
  'eduflow.blocks.whiteboard': 'Доска',
  'eduflow.blocks.ai_chat': 'Чат с ИИ',
  'eduflow.blocks.certificate': 'Сертификат',
  'eduflow.blocks.attendance': 'Посещаемость',
  'eduflow.blocks.sortable': 'Ранжирование',
  'eduflow.blocks.pair_matching': 'Сопоставление пар',
  'eduflow.blocks.drawing': 'Рисование',
  'eduflow.blocks.embed': 'Встроенный контент',
  'eduflow.blocks.h5p': 'H5P',
  'eduflow.blocks.scorm': 'SCORM',
  'eduflow.blocks.conditional': 'Условие',
  'eduflow.blocks.timer': 'Таймер',
  'eduflow.blocks.randomizer': 'Рандомизатор',
  'eduflow.blocks.grouping': 'Группировка',
  'eduflow.blocks.debate': 'Дебаты',
  'eduflow.blocks.roleplay': 'Ролевая игра',
  'eduflow.blocks.simulation': 'Симуляция',
  'eduflow.blocks.scenario': 'Сценарий',
  'eduflow.blocks.checklist': 'Контрольный список',
  'eduflow.blocks.kanban': 'Канбан',
  'eduflow.blocks.peer_review': 'Взаимная оценка',
  // Common
  'common.loading': 'Загрузка...',
  'common.saving': 'Сохранение...',
  'common.saved': 'Сохранено',
  'common.error': 'Ошибка',
  'common.success': 'Успех',
  'common.warning': 'Внимание',
  'common.info': 'Информация',
  'common.yes': 'Да',
  'common.no': 'Нет',
  'common.or': 'или',
  'common.and': 'и',
  'common.all': 'Все',
  'common.none': 'Никто',
  'common.total': 'Итого',
  'common.actions': 'Действия',
  'common.created_at': 'Создано',
  'common.updated_at': 'Изменено',
  'common.sort_by': 'Сортировать по',
  'common.per_page': 'На странице',
  'common.page': 'Страница',
  'common.of': 'из',
  'common.results': '{{count}} результат(ов)',
  'common.no_data': 'Нет данных',
  'common.coming_soon': 'Скоро',
  'common.beta': 'Бета',
  'common.new': 'Новое',
  'common.delete_confirm': 'Это действие необратимо. Продолжить?',
  'common.copied': 'Скопировано!',
  'common.drag_drop': 'Перетаскивание',
  'common.online': 'Онлайн',
  'common.offline': 'Офлайн',
  'common.version': 'Версия',
  // Player
  'player.next': 'Далее',
  'player.image.clickForAnnotations': 'Нажмите на изображение, чтобы просмотреть аннотации',
  'player.previous': 'Назад',
  'player.complete': 'Завершить',
  'player.exit': 'Выйти',
  'player.exitConfirmTitle': 'Выйти из курса?',
  'player.exitConfirmMessage': 'Ваш прогресс будет сохранён. Вы уверены, что хотите выйти?',
  'player.focusLostTitle': 'Курс приостановлен',
  'player.focusLostMessage': 'Вы покинули окно курса. Время приостановлено.',
  'player.focusLostResume': 'Нажмите, чтобы продолжить',
  'player.focusLostRemaining': 'Осталось выходов до блокировки: {{remaining}}',
  'player.focusBlockedTitle': 'Курс заблокирован',
  'player.focusBlockedMessage':
    'Вы превысили допустимое число выходов ({{max}}). Вы больше не можете продолжить этот курс. Обратитесь к своему тренеру или куратору.',
  'player.cancel': 'Отмена',
  'player.summary': 'Содержание',
  'player.contents': 'Материалы',
  'player.progress': 'Прогресс',
  'player.block': 'Блок',
  'player.parallelBranches': 'Параллельные ветки',
  'player.parallelBranchesTitle': 'Что дальше?',
  'player.parallelBranchesHint': 'Остальные варианты будут доступны позже.',
  'player.parallelBranchShowFull': 'Ещё',
  'player.parallelBranchShowLess': 'Свернуть',
  'player.parallelBranchChoose': 'Открыть',
  'player.parallelBranchDismiss': 'Закрыть',
  'player.parallelBranchDismissHint':
    'Вернуться к блоку. Нажмите «Далее», чтобы выбрать ветку.',
  'player.parallelBranchesPickTitle': 'Choose your next path',
  'player.parallelBranchesPickHint':
    'Paths you already took are marked “Completed”. Finish each remaining branch before the shared part of the course.',
  'player.parallelBranchFromBlock': 'From: {{title}}',
  'player.parallelBranchesProgress': '{{done}} / {{total}} paths completed',
  'player.parallelBranchesOverview': 'Progress',
  'player.parallelBranchStatusDone': 'Completed',
  'player.parallelBranchStatusTodo': 'Pending',
  'player.parallelBranchStatusNext': 'Up next',
  'player.parallelBranchAction': 'Take this path',
  'player.notes': 'Сделать заметку...',
  'player.noBlocks': 'Нет блоков для отображения.',
  'player.contentTranslatingTitle': 'Подстраиваем содержание под ваш язык…',
  'player.contentTranslatingHint':
    'Выполняется перевод. Первая загрузка может занять чуть больше времени.',
  'player.textBlockTipsHeading': 'Советы',
  'player.textBlockReadDone': 'Чтение завершено',
  'player.defaultFlowTitle': 'Курс',
  'player.docTitle.error': 'Курс недоступен',
  'player.docTitle.completed': 'Курс завершён',
  'player.docTitle.loading': 'Загрузка курса',
  'player.docTitle.fallback': 'Курс',
  'player.initError.title': 'Курс временно недоступен',
  'player.initError.loadFailed': 'Не удалось загрузить курс сейчас.',
  'player.initError.loadFailedWithDetail':
    'Не удалось загрузить курс сейчас.{{detail}} Повторите попытку через несколько минут.',
  'player.initError.hintsIntro': 'Вы можете:',
  'player.initError.hintRefresh': 'обновить страницу;',
  'player.initError.hintConnection': 'проверить подключение к Интернету;',
  'player.initError.hintRetryLater': 'повторить попытку через несколько минут.',
  'api.error.network': 'Не удалось связаться с сервером. Проверьте подключение.',
  'api.error.unexpected': 'Произошла непредвиденная ошибка.',
  'api.error.badRequest': 'Некорректный запрос.',
  'api.error.forbidden': 'Доступ запрещён.',
  'api.error.notFound': 'Ресурс не найден.',
  'api.error.conflict': 'Конфликт — такой ресурс уже существует.',
  'api.error.unprocessable': 'Отправленные данные недействительны.',
  'api.error.tooManyRequests': 'Слишком много запросов. Повторите попытку через мгновение.',
  'api.error.server': 'Внутренняя ошибка сервера.',
  'api.error.badGateway': 'Сервис временно недоступен.',
  'api.error.maintenance': 'Сервис на обслуживании.',
  'api.error.generic': 'Ошибка {{status}}',
  'player.textBlockReadMinutes': '{{count}} мин чтения',
  'player.textBlockWordCount_one': '{{count}} слово',
  'player.textBlockWordCount_few': '{{count}} слова',
  'player.textBlockWordCount_many': '{{count}} слов',
  'player.textBlockWordCount_other': '{{count}} слов',
  'player.textBlockNoContent': 'Нет доступного содержимого.',
  'player.blockUnknown': 'Неизвестный блок',
  'player.objectivesPanelTitle': 'Учебные цели',
  'player.objectivesOpenLabel': 'Показать учебные цели',
  'player.objectivesCloseLabel': 'Закрыть панель целей',
  'player.objectivesAchievementRate': 'Уровень достижения',
  'player.objectivesAchievedCount': '{{achieved}}/{{total}} целей достигнуто',
  'player.objectivesNoneDefined': 'Для этого курса цели не заданы.',
  'player.objectiveCategory.savoir': 'Знание',
  'player.objectiveCategory.savoirFaire': 'Умение',
  'player.objectiveCategory.savoirEtre': 'Отношение',
  'player.objectiveLevel.connaissance': 'Знание',
  'player.objectiveLevel.comprehension': 'Понимание',
  'player.objectiveLevel.application': 'Применение',
  'player.objectiveLevel.analyse': 'Анализ',
  'player.objectiveLevel.synthese': 'Синтез',
  'player.objectiveLevel.evaluation': 'Оценка',
  'player.objectiveBloom': 'Bloom · {{level}}',
  'player.objectiveRequired': 'Обязательно',
  'player.objectiveLinkedBlocks': 'Связанные блоки',
  'player.objectiveCriterionScore': 'Балл ≥ {{min}}%',
  'player.objectiveCriterionCompletion': 'Завершение блока',
  'player.objectiveNoLinkedBlocks':
    'Нет связанного блока — общий прогресс не учитывает эту цель.',
  'player.completion.congratulations': '🎉 Поздравляем!',
  'player.completion.finished': '📋 Курс завершён',
  'player.completion.youCompleted': 'Вы завершили',
  'player.completion.passed': 'Пройдено ✓',
  'player.completion.failed': 'Не засчитано ✗',
  'player.completion.requiredScore': 'Необходимый балл: {{score}}%',
  'player.completion.totalTime': 'Общее время',
  'player.completion.blocksCompleted': 'Завершено блоков',
  'player.completion.quizPassed': 'Тестов пройдено',
  'player.completion.successRate': 'Процент успеха',
  'player.completion.blockCompleted': 'Завершено ✓',
  'player.completion.blockFailed': 'Провалено ✗',
  'player.completion.blockSkipped': 'Пропущено',
  'player.completion.detail': '📊 Детали по блоку',
  'player.completion.type': 'Тип',
  'player.completion.timeSpent': 'Затраченное время',
  'player.completion.downloadCertificate': '📄 Скачать сертификат',
  'player.completion.certificateSoon': 'Сертификат будет доступен в ближайшее время.',
  'player.completion.restart': '🔄 Начать заново',
  'player.completion.viewDetails': '📊 Просмотреть детали',
  'player.completion.backToDashboard': '← Вернуться к панели',
  'player.completion.embedDismissHint':
    'Вы завершили модуль. Чтобы выйти, нажмите × вверху окна или кнопку ниже.',
  'player.completion.embedDismissButton': 'Закрыть окно',
  'player.previewBanner': 'Режим предпросмотра — этот экран показывает реальные данные вашего обучения',
  'player.closePreview': 'Закрыть предпросмотр',
  'player.flowPreviewMap': 'Flow map',
  'player.flowPreviewTitle': 'Flow preview',
  'player.flowPreviewHint':
    'Read-only overview (same layout as in the designer). The orange frame shows the block currently shown in the player.',
  'player.flowPreviewLegendCurrent': 'Current block',
  'player.flowPreviewClose': 'Close',
  'player.toggleSidebarShow': 'Показать панель',
  'player.toggleSidebarHide': 'Скрыть панель',
  'player.sidebarExpand': 'Развернуть панель',
  'player.sidebarCollapse': 'Свернуть панель',
  'player.keyboardShortcuts': 'Сочетания клавиш',
  'player.keyboardShortcutPrev': 'Предыдущий блок',
  'player.keyboardShortcutNext': 'Следующий блок',
  'player.keyboardShortcutsHint':
    'Включите «Навигацию с клавиатуры» в центре доступности (кнопка внизу справа). Отключено в полях ввода.',
  'player.a11yCenter': 'Центр доступности',
  'player.reviewSummary': 'Посмотреть итог',
  'player.viewResults': 'Посмотреть результаты',
  'player.submitFirst': 'Сначала отправьте',
  'player.phraseRevealCompleteFirst': 'Сначала просмотрите все фразы',
  'player.phraseReveal.emptyBlock': 'Для этого блока не настроены фразы.',
  'player.phraseReveal.revealNextAria':
    'Нажмите или пробел, чтобы показать следующую фразу',
  'player.phraseReveal.lastPhraseHint':
    'Последняя фраза — нажмите «Далее», чтобы продолжить прохождение.',
  'player.phraseReveal.morePhrasesHint':
    'Щёлкните, пробел или стрелка вправо для следующей фразы',
  'player.phraseReveal.previousPhrases': 'Предыдущие фразы ({{count}})',
  'player.certificateUnavailable': 'Сертификат скоро будет доступен.',
  'player.browser.embeddedContentTitle': 'Встроенное содержимое',
  'player.browser.noValidUrl': 'Недействительный URL',
  'player.browser.enterHttpsHint':
    'Укажите адрес, начинающийся с https:// (или http://), в настройках блока.',
  'player.browser.loadingContent': 'Загрузка содержимого…',
  'player.browser.defaultPageTitle': 'Встроенная веб-страница',
  'player.browser.continue': 'Продолжить',
  'player.browser.stepSaved': 'Шаг сохранён',
  'player.browser.fullscreen': 'Полный экран',
  'player.browser.exitFullscreen': 'Выйти из полноэкранного режима',
  'player.podcast.defaultShowTitle': 'Подкаст',
  'player.podcast.defaultEpisode': 'Эпизод',
  'player.podcast.loadError': 'Не удалось загрузить подкаст.',
  'player.podcast.loadingEpisodes': 'Загрузка эпизодов…',
  'player.podcast.playAll': 'Слушать всё',
  'player.podcast.nextEpisode': 'Следующий эпизод',
  'player.podcast.episodesHeading': 'Эпизоды',
  'player.podcast.noEpisodes': 'Нет эпизодов.',
  'player.podcast.noAudio': 'Нет аудио',
  'player.podcast.chainPlayback': 'Плейлист · {{current}}/{{total}}',
  'player.podcast.selectOrPlayAll':
    'Выберите эпизод в списке или нажмите «Слушать всё».',
  'player.podcast.audioUnavailable': 'Аудио для этого эпизода недоступно.',
  'player.podcast.episodeCount_one': '{{count}} эпизод',
  'player.podcast.episodeCount_other': '{{count}} эпизодов',
  'player.renderError': 'Ошибка при отображении блока: {{blockType}}',
  'player.blocks.defaultWelcome': 'Добро пожаловать на это обучение',
  'player.blocks.defaultEndTitle': 'Поздравляем!',
  'player.quiz.selectCorrectAnswer': 'Выберите правильный ответ:',
  'player.quiz.correctAnswer': '✓ Правильный ответ!',
  'player.quiz.incorrectAnswer': '✗ Неверный ответ.',
  'player.blockTimer.aria': 'Время для этого блока',
  'player.blockTimer.modeShortCountup': 'Секундомер',
  'player.blockTimer.modeShortCountdown': 'Обратный отсчёт',
  'player.form.successMessage': 'Спасибо за ваш ответ!',
  'player.form.noFieldsConfigured': 'В этой форме пока нет настроенных полей.',
  'player.evaluation.commentsLabel': 'Комментарии (необязательно)',
  'player.evaluation.commentsPlaceholder': 'Поделитесь своим отзывом...',
  'player.evaluation.submitted': 'Оценка отправлена',
  'player.evaluation.submit': 'Отправить оценку',
  'player.time.lessThanMinute': 'Меньше минуты',
  'player.time.oneMinute': '1 минута',
  'player.time.minutes': '{{count}} минут',
  'player.time.shortSeconds': '{{count}} с',
  'player.time.shortMinutes': '{{count}} мин',
  'player.time.shortMinutesSeconds': '{{minutes}} мин {{seconds}} с',
  'player.completion.scoreLabel': 'Баллы',
  'accessibility.openCenter': 'Открыть центр доступности',
  'accessibility.center': 'Доступность',
  'accessibility.modalTitle': 'Центр адаптированного чтения',
  'accessibility.modalDescription': 'Настройте интерфейс для слабовидящих, дислексии и более комфортного чтения.',
  'accessibility.closeCenter': 'Закрыть центр доступности',
  'accessibility.accessPad.connected': 'Lyrra Access Pad подключён',
  'accessibility.accessPad.playGuide': 'Слушать руководство LyrraPad',
  'accessibility.accessPad.pauseGuide': 'Приостановить руководство LyrraPad',
  'accessibility.presets': 'Готовые профили',
  'accessibility.preset.standard.label': 'Стандарт',
  'accessibility.preset.standard.desc': 'Обычное отображение приложения.',
  'accessibility.preset.lowVision.label': 'Слабое зрение',
  'accessibility.preset.lowVision.desc': 'Более сильный контраст, крупный текст и более чёткие визуальные подсказки.',
  'accessibility.preset.dys.label': 'Дислексия',
  'accessibility.preset.dys.desc': 'Более лёгкое чтение с большим интервалом и удобным шрифтом.',
  'accessibility.typography': 'Настройки шрифта',
  'accessibility.fontScale': 'Размер текста',
  'accessibility.lineHeight': 'Межстрочный интервал',
  'accessibility.letterSpacing': 'Интервал между буквами',
  'accessibility.wordSpacing': 'Интервал между словами',
  'accessibility.visualAids': 'Визуальные подсказки',
  'accessibility.highContrast': 'Высокий контраст',
  'accessibility.highContrastDesc': 'Усиливает контраст текста, границы и визуальные ориентиры.',
  'accessibility.underlineLinks': 'Подчёркнутые ссылки',
  'accessibility.underlineLinksDesc': 'Делает ссылки и интерактивные области заметнее.',
  'accessibility.dyslexiaFont': 'Шрифт для удобного чтения',
  'accessibility.dyslexiaFontDesc': 'Более комфортный шрифт для людей с дислексией и уставших читателей.',
  'accessibility.reducedMotion': 'Сокращение движения',
  'accessibility.reducedMotionDesc': 'Меньше анимаций и переходов, чтобы снизить визуальный дискомфорт.',
  'accessibility.mouseMagnifier': 'Лупа для чтения',
  'accessibility.mouseMagnifierDesc':
    'Круглая лупа, следует за курсором и увеличивает содержимое под ней (только просмотр).',
  'accessibility.alwaysOn': 'Постоянные улучшения',
  'accessibility.alwaysOnFocus': 'Более заметный фокус клавиатуры',
  'accessibility.alwaysOnLinks': 'Ссылки и интерактивные элементы легче заметить',
  'accessibility.alwaysOnSaved': 'Настройки автоматически сохраняются на этом устройстве',
  'accessibility.navigation': 'Навигация',
  'accessibility.keyboardNavigation': 'Навигация с клавиатуры',
  'accessibility.keyboardNavigationDesc':
    'Стрелка влево/вверх — предыдущий блок, вправо/вниз — следующий (как нижние кнопки).',
  // ── Injected from locales/ja.json ──
  'producerAssignments.createInDesigner': '研修を作成/開く',
  'producerAssignments.draft': '下書き',
  'producerAssignments.enterprise': '企業',
  'producerAssignments.enterprisePaths': '企業パス',
  'producerAssignments.episodes': '話',
  'producerAssignments.errorForbidden': 'このページにアクセスする権限がありません。',
  'producerAssignments.errorGeneric': 'ミッションを読み込めませんでした。',
  'producerAssignments.errorNoInstitution': '所属する機関がありません。',
  'producerAssignments.formations': '研修カード',
  'producerAssignments.intro': 'このページには、所属機関の人事部から割り当てられた研修カードとポッドキャストが表示されます。EduFlowデザイナーまたはポッドキャストスタジオでコンテンツを作成し、制作ステータスを更新してください。',
  'producerAssignments.markCompleted': '完了としてマーク',
  'producerAssignments.markInProgress': '制作中',
  'producerAssignments.markReady': '完了',
  'producerAssignments.modulesCatalog': '{{count}}カタログモジュール · 更新日 {{date}}',
  'producerAssignments.noneEnterprisePaths': '企業パスのリクエストはありません。',
  'producerAssignments.noneFormations': '割り当てられたカードはありません。',
  'producerAssignments.nonePodcasts': '割り当てられたポッドキャストはありません。',
  'producerAssignments.openInDesigner': 'デザイナーで開く',
  'producerAssignments.openPodcasts': 'ポッドキャストスタジオ',
  'producerAssignments.planningDeadline': '期限 {{date}}',
  'producerAssignments.planningFrom': '{{date}}から',
  'producerAssignments.planningRange': '{{start}} → {{end}}',
  'producerAssignments.podcasts': '制作するポッドキャスト',
  'producerAssignments.productionStatus.completed': '完了',
  'producerAssignments.productionStatus.in_progress': '進行中',
  'producerAssignments.productionStatus.not_started': '未開始',
  'producerAssignments.productionStatus.published': '公開済み',
  'producerAssignments.publishedCatalog': 'カタログに公開済み',
  'producerAssignments.rhPlanning': '人事計画',
  'producerAssignments.subtitle': 'デザイナーで作成する研修とプロデュースするポッドキャスト（人事部より割り当て）',
  'producerAssignments.title': '制作ミッション',
  'podcasts.actions.cancel': 'キャンセル',
  'podcasts.actions.close': '閉じる',
  'podcasts.actions.create': '作成',
  'podcasts.actions.edit': '編集',
  'podcasts.actions.retry': '再試行',
  'podcasts.actions.save': '保存',
  'podcasts.actions.saving': '保存中…',
  'podcasts.actions.share': '共有',
  'podcasts.category.ariaGroup': 'ポッドキャストのカテゴリ',
  'podcasts.category.emptyBanner': '組織スペースでカテゴリを追加すると、ポッドキャストを整理しやすくなります。',
  'podcasts.category.emptyInstitutionHint': 'まだ組織カテゴリはありません。カテゴリなしで公開できます。',
  'podcasts.category.noneChip': 'カテゴリなし',
  'podcasts.category.sectionTitle': 'カテゴリ',
  'podcasts.category.withCategoriesHint': 'カタログでの分類と組織側のフィルターに使われます。',
  'podcasts.cover.ariaDropzone': 'カバー画像を選択またはドロップ',
  'podcasts.cover.browseFiles': 'ファイルを参照',
  'podcasts.cover.cancelNewFile': '新しいファイルをキャンセル',
  'podcasts.cover.dragDrop': '画像をドラッグ＆ドロップ',
  'podcasts.cover.dropHere': 'ここに画像をドロップ',
  'podcasts.cover.errorMaxSize': '画像は5MBを超えてはいけません。',
  'podcasts.cover.errorUnsupportedFormat': '非対応の画像形式です。',
  'podcasts.cover.formatsHint': 'PNG、JPG、WebP、GIF · 最大5MB',
  'podcasts.cover.help': '正方形を推奨。ファイルをドラッグ＆ドロップするか、クリックして参照してください。',
  'podcasts.cover.or': 'または',
  'podcasts.cover.replaceHint': '画像を差し替え',
  'podcasts.cover.sectionTitle': 'カバー画像',
  'podcasts.deleteDialog.body': '「{{title}}」は一覧から削除されます（論理削除）。エピソード自体は消えません。アカウントが組織に紐づいている場合、管理者は組織スペースのゴミ箱からポッドキャストを復元できます。',
  'podcasts.deleteDialog.confirm': 'ゴミ箱へ移動',
  'podcasts.deleteDialog.title': 'ポッドキャストをゴミ箱に移動しますか？',
  'podcasts.editPanel.createdOn': '作成日 ',
  'podcasts.editPanel.headerTitle': 'ポッドキャストを編集',
  'podcasts.editPanel.saving': '保存中…',
  'podcasts.editPanel.statsEpisodes': 'エピソード：',
  'podcasts.editPanel.statsSubscribers': '購読者：',
  'podcasts.editPanel.successSaved': '変更を保存しました',
  'podcasts.episodeStatus.draft': '下書き',
  'podcasts.episodeStatus.published': '公開',
  'podcasts.episodesPanel.addEpisode': 'エピソードを追加',
  'podcasts.episodesPanel.emptySubtitle': 'このポッドキャストにエピソードを追加してください',
  'podcasts.episodesPanel.emptyTitle': 'エピソードがありません',
  'podcasts.episodesPanel.episodeCount': '{{count}} エピソード',
  'podcasts.episodesPanel.noEpisodeAvailable': '利用できるエピソードがありません',
  'podcasts.episodesPanel.remove': '削除',
  'podcasts.episodesPanel.searchPlaceholder': 'エピソードを検索…',
  'podcasts.episodesPanel.title': 'ポッドキャストのエピソード',
  'podcasts.error.retry': '再試行',
  'podcasts.filters.allCategories': 'すべてのカテゴリ',
  'podcasts.filters.categoryLabel': 'カテゴリ',
  'podcasts.filters.searchLabel': 'テキスト検索',
  'podcasts.filters.searchPlaceholder': 'タイトル、説明…',
  'podcasts.filters.viewCategories': 'カテゴリ表示',
  'podcasts.filters.viewList': 'リスト表示',
  'podcasts.form.descriptionLabel': '説明',
  'podcasts.form.descriptionPlaceholder': 'ポッドキャストの内容を説明…',
  'podcasts.form.namePlaceholder': 'ポッドキャスト名',
  'podcasts.form.titleLabel': 'タイトル',
  'podcasts.language.ariaGroup': 'ポッドキャストの言語',
  'podcasts.language.help': 'プラットフォームの10言語のうちから選びます。学習者のプレーヤーとインターフェースと同じロケールにポッドキャストを合わせます。',
  'podcasts.language.label': '言語',
  'podcasts.lightbox.ariaClose': '閉じる',
  'podcasts.lightbox.ariaDialog': 'ポッドキャストカバーのプレビュー',
  'podcasts.list.actionActivate': '有効化',
  'podcasts.list.actionDeactivate': '無効化',
  'podcasts.list.actionDelete': '削除',
  'podcasts.list.actionManageEpisodes': 'エピソードを管理',
  'podcasts.list.actionPreview': 'プレビューと再生',
  'podcasts.list.coverPreviewAria': 'ポッドキャスト「{{title}}」のカバープレビュー',
  'podcasts.list.createFirst': 'ポッドキャストを作成',
  'podcasts.list.emptySubtitle': '最初のポッドキャストを作成して始めましょう',
  'podcasts.list.emptyTitle': 'ポッドキャストがありません',
  'podcasts.list.loadError': 'ポッドキャストを読み込めませんでした。',
  'podcasts.list.enterpriseBadge': '企業',
  'podcasts.list.enterpriseNeedTitle': '企業が定義したニーズ',
  'podcasts.list.episodesShort': '{{count}} 話',
  'podcasts.list.groupPodcastCount': '{{count}} podcast(s)',
  'podcasts.list.subscribersShort': '{{count}} 購読',
  'podcasts.list.uncategorized': 'カテゴリなし',
  'podcasts.modal.errorRequiredFields': 'タイトルと説明は必須です。',
  'podcasts.modal.errorSaveFailed': '保存中にエラーが発生しました。',
  'podcasts.modal.subtitleNew': 'タイトル、説明、オプションを入力してください。エピソードは後から追加できます。',
  'podcasts.modal.titleEdit': 'ポッドキャストを編集',
  'podcasts.modal.titleNew': '新しいポッドキャスト',
  'podcasts.page.episodesLink': 'エピソード',
  'podcasts.page.newButton': '新規',
  'podcasts.page.title': 'ポッドキャスト',
  'podcasts.page.totalCount': '{{count}} podcast(s)',
  'podcasts.pagination.pageOf': '{{total}} ページ中 {{page}} ページ目',
  'podcasts.pagination.totalSummary': '合計 {{count}} podcast(s)',
  'podcasts.player.listen': '再生',
  'podcasts.player.noAudio': '音声なし',
  'podcasts.player.pause': '一時停止',
  'podcasts.preview.emptySubtitle': 'このポッドキャストにはまだエピソードがありません',
  'podcasts.preview.emptyTitle': 'エピソードがありません',
  'podcasts.preview.episodeCount': '{{count}} エピソード',
  'podcasts.preview.fallbackDescription': '利用可能な音声エピソードを聴く。',
  'podcasts.preview.playAll': 'すべて再生',
  'podcasts.preview.title': 'ポッドキャストのプレビュー',
  'podcasts.share.assignedLearnersHeading': '割り当て済み学習者 {{count}} 名',
  'podcasts.share.copied': 'コピーしました',
  'podcasts.share.copyLink': 'リンクをコピー',
  'podcasts.share.emailPlaceholder': 'メール *',
  'podcasts.share.errorCreateLearner': '学習者を作成できません。',
  'podcasts.share.errorPublicLink': '公開リンクを生成できません。',
  'podcasts.share.errorRemoveLearner': '学習者を解除できません。',
  'podcasts.share.errorShare': 'このポッドキャストを共有できません。',
  'podcasts.share.firstNamePlaceholder': '名 *',
  'podcasts.share.generatingLink': 'リンクを生成中…',
  'podcasts.share.hideQuickCreate': 'クイック作成を非表示',
  'podcasts.share.lastNamePlaceholder': '姓 *',
  'podcasts.share.loadingAssigned': '読み込み中…',
  'podcasts.share.loadingLearners': '学習者を読み込み中...',
  'podcasts.share.messageAccessRemoved': '学習者のアクセスを解除しました。',
  'podcasts.share.messageCreatedAndShared': 'アカウントを作成／紐づけ、ポッドキャストを共有し、招待メールを送信しました。',
  'podcasts.share.messageShared': 'ポッドキャストを共有し、学習者に招待を送信しました。',
  'podcasts.share.noAssignedLearners': 'このポッドキャストに割り当てられた学習者はいません。',
  'podcasts.share.noLearnerAvailable': '利用できる学習者がいません。',
  'podcasts.share.panelTitle': 'ポッドキャストを共有',
  'podcasts.share.publicLinkSection': '公開リンク（ログイン不要）',
  'podcasts.share.removeAccessTitle': 'アクセスを解除',
  'podcasts.share.searchLearnerPlaceholder': '学習者を検索（姓、名、メール）',
  'podcasts.share.selectExistingLearnerLabel': '既存の学習者を選択',
  'podcasts.share.showQuickCreate': '新しい学習者を作成',
  'podcasts.share.submitCreateAndInvite': '作成、共有、招待メールを送信',
  'podcasts.status.active': '有効',
  'podcasts.status.inactive': '無効',
  'learningPaths.card.contents': '{{count}} 件のコンテンツ',
  'learningPaths.card.delete': '削除',
  'learningPaths.card.edit': '編集',
  'learningPaths.card.learners': '{{count}} 名の受講者',
  'learningPaths.card.publish': '公開',
  'learningPaths.card.unpublish': '非公開にする',
  'learningPaths.card.updatedAt': '{{date}} に更新',
  'learningPaths.createPanel.cancel': 'キャンセル',
  'learningPaths.createPanel.description': 'パスに名前を付けます。後からポッドキャストと研修を追加できます。',
  'learningPaths.createPanel.descriptionLabel': '説明',
  'learningPaths.createPanel.descriptionPlaceholder': 'パスの目的、対象者…',
  'learningPaths.createPanel.nameLabel': 'パス名',
  'learningPaths.createPanel.namePlaceholder': '例：人事オンボーディングパス…',
  'learningPaths.createPanel.submit': 'パスを作成',
  'learningPaths.createPanel.title': '新しいパス',
  'learningPaths.deleteDialog.body': '「{{title}}」を削除します。この操作は取り消せません。',
  'learningPaths.deleteDialog.cancel': 'キャンセル',
  'learningPaths.deleteDialog.confirm': '削除',
  'learningPaths.deleteDialog.title': 'パスを削除',
  'learningPaths.empty.createFirst': 'パスを作成',
  'learningPaths.empty.noPathsHint': 'ポッドキャストと研修を組み合わせて最初のパスを作成しましょう。',
  'learningPaths.empty.noPathsYet': 'まだパスがありません',
  'learningPaths.empty.noResults': '結果がありません',
  'learningPaths.empty.noResultsHint': 'フィルターを変更するか、新しいパスを作成してください。',
  'learningPaths.error.loadFailed': 'パスを読み込めませんでした',
  'learningPaths.error.retry': '再試行',
  'learningPaths.filters.allStatuses': 'すべてのステータス',
  'learningPaths.filters.draft': '下書き',
  'learningPaths.filters.published': '公開済み',
  'learningPaths.filters.searchPlaceholder': 'パスを検索…',
  'learningPaths.page.newButton': '新しいパス',
  'learningPaths.page.subtitle': '順序付けられた研修パスを作成',
  'learningPaths.page.title': 'ラーニングパス',
  'learningPaths.page.totalCount': '{{count}} 件のパス',
  'learningPaths.pagination.next': '次へ',
  'learningPaths.pagination.pageOf': 'ページ {{page}} / {{total}} · {{count}} 件のパス',
  'learningPaths.pagination.previous': '前へ',
  'learningPaths.status.draft': '下書き',
  'learningPaths.status.published': '公開済み',
  'webinars.card.code': 'コード {{code}}',
  'webinars.card.copyLink': 'リンクをコピー',
  'webinars.card.delete': '削除',
  'webinars.card.edit': '編集',
  'webinars.card.openRoom': 'ルームを開く',
  'webinars.deleteDialog.body': '「{{title}}」を削除しますか？この操作は取り消せません。',
  'webinars.deleteDialog.cancel': 'キャンセル',
  'webinars.deleteDialog.confirm': '削除',
  'webinars.deleteDialog.title': 'ウェビナーを削除',
  'webinars.eligibility.default': 'お客様のアカウントではウェビナーを利用できません（API接続を確認するか、管理者にお問い合わせください）。',
  'webinars.eligibility.moduleDisabled': 'お客様の施設ではウェビナーモジュールがまだ有効になっていません。管理者が「ウェビナーを有効にする」を施設 → ウェビナーでオンにする必要があります。',
  'webinars.eligibility.noInstitution': 'お客様のアカウントに施設が紐づいていません。',
  'webinars.eligibility.notAuthorized': 'お客様のアカウントは許可された司会者リストに含まれていません。施設に依頼して追加してもらってください（ウェビナー管理 → 許可された司会者）。',
  'webinars.eligibility.roleNotAllowed': 'お客様のロールでは、このアカウントでウェビナーを司会できません。',
  'webinars.empty.createFirst': 'ウェビナーを作成',
  'webinars.empty.noResults': '該当なし',
  'webinars.empty.noResultsHint': '検索条件を変えてください。',
  'webinars.empty.noWebinarsHint': 'ルームを作成し、サイドパネルから参加者を招待してください。',
  'webinars.empty.noWebinarsYet': 'まだウェビナーはありません',
  'webinars.externalInvite.closeButton': '閉じる',
  'webinars.externalInvite.description': '学習者リストにいない方：各アドレスにルームリンクとカレンダー用添付付きのメールが届きます。',
  'webinars.externalInvite.done': '完了しました。',
  'webinars.externalInvite.duplicatesCount_one': '{{count}} 件は既に招待済み',
  'webinars.externalInvite.duplicatesCount_other': '{{count}} 件は既に招待済み',
  'webinars.externalInvite.emailsHint': '1 行に 1 アドレス、またはカンマ区切り。重複と既に送信済みの招待は無視されます。',
  'webinars.externalInvite.emailsLabel': 'メールアドレス',
  'webinars.externalInvite.emailsPlaceholder': 'user@example.co.jp\nother@domain.org',
  'webinars.externalInvite.errorMax200': '1 回の送信は最大 200 件までです。',
  'webinars.externalInvite.errorMinOne': '有効なメールアドレスを 1 件以上入力してください。',
  'webinars.externalInvite.errorsLabel': 'エラー: {{errors}}',
  'webinars.externalInvite.sendButton': '招待を送信',
  'webinars.externalInvite.sendFailed': '送信に失敗しました',
  'webinars.externalInvite.sentCount_one': '{{count}} 通送信しました',
  'webinars.externalInvite.sentCount_other': '{{count}} 通送信しました',
  'webinars.externalInvite.skippedCount_one': '{{count}} 件をスキップ',
  'webinars.externalInvite.skippedCount_other': '{{count}} 件をスキップ',
  'webinars.externalInvite.title': 'メールでの招待',
  'webinars.invite.duplicatesCount_one': '{{count}} 件は既に招待済み',
  'webinars.invite.duplicatesCount_other': '{{count}} 件は既に招待済み',
  'webinars.invite.errorsLabel': 'エラー: {{errors}}',
  'webinars.invite.externalButton': 'リスト外（メール）',
  'webinars.invite.failed': '失敗',
  'webinars.invite.groupSuccess': 'グループ招待を処理しました（メンバーにメールを送信しました）。',
  'webinars.invite.inviteButton': '招待',
  'webinars.invite.learnersHelp': '施設の学習者を検索：招待のたびに<strong>招待メール</strong>（リンク＋カレンダー）が送信されます。',
  'webinars.invite.learnersTitle': '学習者',
  'webinars.invite.noCandidates': '表示する学習者がありません（別の検索を試すか、外部メールには「リスト外」をご利用ください）。',
  'webinars.invite.noInvitations': '記録された招待はありません。',
  'webinars.invite.searchLabel': '学習者を検索',
  'webinars.invite.searchPlaceholder': '氏名またはメール…',
  'webinars.invite.searching': '検索中…',
  'webinars.invite.sentCount_one': '{{count}} 件の招待を送信しました（メール）',
  'webinars.invite.sentCount_other': '{{count}} 件の招待を送信しました（メール）',
  'webinars.invite.sentInvitationsToggle': '送信済み招待',
  'webinars.page.newButton': '新規ウェビナー',
  'webinars.page.searchPlaceholder': '検索…',
  'webinars.page.subtitle': 'お客様のルーム一覧です — パネルを開いて作成・編集・参加者の招待ができます。',
  'webinars.page.title': 'ウェビナー',
  'webinars.panel.backgroundHint': '参加者向け共有コンテンツの背後に表示する https（または http）の URL。空欄にすると Lyrra 組み込みのビジュアルが使われます。',
  'webinars.panel.backgroundLabel': '背景画像（参加者ビュー）',
  'webinars.panel.backgroundPlaceholder': 'https://…（空欄＝Lyrra デフォルト背景）',
  'webinars.panel.copyLink': 'リンクをコピー',
  'webinars.panel.createButton': '作成',
  'webinars.panel.createDescription': '情報を入力して保存してください。その後、参加者を招待できます。',
  'webinars.panel.createTitle': '新規ウェビナー',
  'webinars.panel.dateLabel': '日付（任意）',
  'webinars.panel.deleteButton': '削除',
  'webinars.panel.descriptionLabel': '説明',
  'webinars.panel.durationError': '時間:分の形式で入力してください（例：1:00 または 0:45）。',
  'webinars.panel.durationHint': '日付を入力した場合は必須。例：1 時間 30 分は 1:30、45 分は 0:45。',
  'webinars.panel.durationLabel': '所要時間（h:mm）',
  'webinars.panel.editTitle': 'ウェビナー',
  'webinars.panel.loading': '読み込み中…',
  'webinars.panel.openRoom': 'ルームを開く',
  'webinars.panel.presentersLabel': '共同司会者',
  'webinars.panel.roomCode': 'ルームコード：',
  'webinars.panel.timeHint': '空欄の場合は 9:00。',
  'webinars.panel.timeLabel': '開始時刻',
  'webinars.panel.titleLabel': 'タイトル',
  'webinars.panel.waitingHtmlLabel': '待機室 HTML（任意）',
  'webinars.panel.waitingHtmlPlaceholder': '<p>ようこそ…</p>',
  'webinars.status.draft': '下書き',
  'webinars.status.ended': '終了',
  'webinars.status.live': 'ライブ',
  'webinars.status.scheduled': '予定',
  'videos.addPanel.cancel': 'キャンセル',
  'videos.addPanel.continue': '続ける',
  'videos.addPanel.description': '研修で使うために MP4、WebM、MOV をインポートしてください。',
  'videos.addPanel.dropzoneFormats': 'MP4, WebM, MOV',
  'videos.addPanel.dropzoneLabel': 'クリックして動画を選択',
  'videos.addPanel.title': '動画を追加',
  'videos.addPanel.titleLabel': 'タイトル',
  'videos.addPanel.titlePlaceholder': '動画のタイトル…',
  'videos.card.delete': '削除',
  'videos.card.edit': '編集',
  'videos.card.noThumbnail': 'サムネイルなし',
  'videos.card.publishedStudio': '公開済み（Studioで編集可能）',
  'videos.deleteDialog.body': '「{{title}}」は完全に削除されます。',
  'videos.deleteDialog.cancel': 'キャンセル',
  'videos.deleteDialog.confirm': '削除',
  'videos.deleteDialog.title': '動画を削除しますか？',
  'videos.empty.addButton': '動画を追加',
  'videos.empty.description': '最初の動画をインポートして管理し、研修で利用しましょう。',
  'videos.empty.title': '動画はありません',
  'videos.page.addButton': '動画を追加',
  'videos.page.count_one': 'ライブラリに動画が{{count}}本あります',
  'videos.page.count_other': 'ライブラリに動画が{{count}}本あります',
  'videos.page.title': '動画',
  'videos.preview.noFile': '利用できるファイルがありません。',
  'videos.status.draft': '下書き',
  'videos.status.published': '公開済み',
  'sounds.card.delete': '削除',
  'sounds.card.mute': 'ミュート',
  'sounds.deleteDialog.body': '「{{title}}」はサーバーとライブラリから完全に削除されます。',
  'sounds.deleteDialog.cancel': 'キャンセル',
  'sounds.deleteDialog.confirm': '削除',
  'sounds.deleteDialog.title': 'このサウンドを削除しますか？',
  'sounds.empty.description': 'ファイルをインポートしてください。データベースに記録され、サーバーにコピーされるため、データを失いません。',
  'sounds.empty.importButton': 'サウンドをインポート',
  'sounds.empty.title': 'インポートされたサウンドはありません',
  'sounds.fileSize.bytes': '{{size}} B',
  'sounds.fileSize.kb': '{{size}} KB',
  'sounds.fileSize.mb': '{{size}} MB',
  'sounds.importPanel.cancel': 'キャンセル',
  'sounds.importPanel.changeFile': 'ファイルを変更',
  'sounds.importPanel.createTag': '「{{tag}}」を作成',
  'sounds.importPanel.description': 'パス用のライブラリにオーディオファイルを追加します。',
  'sounds.importPanel.descriptionLabel': '説明',
  'sounds.importPanel.descriptionPlaceholder': 'このサウンドを説明…',
  'sounds.importPanel.dropzoneBrowse': '参照',
  'sounds.importPanel.dropzoneFormats': 'MP3, WAV, OGG, M4A, AAC, FLAC',
  'sounds.importPanel.dropzoneLabel': 'オーディオファイルをドラッグするか',
  'sounds.importPanel.optional': '（任意）',
  'sounds.importPanel.submit': 'インポート',
  'sounds.importPanel.submitting': '送信中…',
  'sounds.importPanel.tagsHint': '入力して Enter · 新しいタグは自動で作成されます',
  'sounds.importPanel.tagsLabel': 'タグ',
  'sounds.importPanel.tagsPlaceholder': 'タグを追加…',
  'sounds.importPanel.title': 'サウンドをインポート',
  'sounds.importPanel.titleLabel': 'タイトル',
  'sounds.importPanel.titlePlaceholder': 'サウンド名…',
  'sounds.importPanel.titleRequired': 'タイトルは必須です',
  'sounds.noResults': '「{{query}}」に一致するサウンドはありません',
  'sounds.page.count_one': '{{count}} 件のファイル — データベースとサーバーに保存されています',
  'sounds.page.count_other': '{{count}} 件のファイル — データベースとサーバーに保存されています',
  'sounds.page.importButton': 'インポート',
  'sounds.page.searchPlaceholder': 'タイトル、説明、タグ…',
  'sounds.page.title': 'サウンド',
  'documents.count_one': '{{count}}件の文書',
  'documents.count_other': '{{count}}件の文書',
  'documents.delete.body': '文書「{{filename}}」はライブラリから削除されます。',
  'documents.delete.cancel': 'キャンセル',
  'documents.delete.confirm': '削除',
  'documents.delete.irreversible': 'この操作は取り消せません。',
  'documents.delete.title': 'ドキュメントを削除しますか？',
  'documents.importPanel.cancel': 'キャンセル',
  'documents.importPanel.chooseFile': 'PDFドキュメントを選択',
  'documents.importPanel.description': 'このドキュメントはPDFブロックとこのライブラリで利用できます。',
  'documents.importPanel.descriptionLabel': '説明',
  'documents.importPanel.descriptionPlaceholder': '説明（任意）',
  'documents.importPanel.fileHint': 'このドキュメントはPDFブロックとこのライブラリで利用できます。',
  'documents.importPanel.submit': 'インポート',
  'documents.importPanel.title': 'PDFをインポート',
  'documents.importPanel.titleLabel': 'タイトル',
  'documents.importPanel.titlePlaceholder': 'ドキュメントのタイトル',
  'documents.importPanel.uploading': 'アップロード中',
  'documents.importPdf': 'PDFをインポート',
  'documents.loading': 'ライブラリを読み込み中…',
  'documents.newDocument': '新しいドキュメント',
  'documents.noDocuments': 'ドキュメントがありません',
  'documents.noDocumentsHint': 'PDFをここにインポートすると、PDFブロックで再利用できます。',
  'documents.noResults': '検索に一致するドキュメントがありません。',
  'documents.pages_one': '{{count}}ページ',
  'documents.pages_other': '{{count}}ページ',
  'documents.previewTitle': 'ドキュメントのプレビュー',
  'documents.searchPlaceholder': 'PDFを検索...',
  'documents.share.accessRemoved': '学習者のアクセスを解除しました。',
  'documents.share.assignedTitle': 'アクセス権のある学習者',
  'documents.share.close': '閉じる',
  'documents.share.createAndShare': '作成、共有、招待メールを送信',
  'documents.share.createError': '学習者を作成できませんでした。',
  'documents.share.createLearner': '新しい学習者を作成',
  'documents.share.createdSuccess': 'アカウントを作成／紐づけ、ドキュメントを共有し、メールを送信しました。',
  'documents.share.creating': '送信中...',
  'documents.share.emailPlaceholder': 'メール *',
  'documents.share.firstNamePlaceholder': '名 *',
  'documents.share.hideCreateLearner': 'クイック作成を非表示',
  'documents.share.lastNamePlaceholder': '姓 *',
  'documents.share.loadingAssigned': '読み込み中…',
  'documents.share.loadingLearners': '学習者を読み込み中...',
  'documents.share.noAssigned': 'このドキュメントに割り当てられた学習者はいません。',
  'documents.share.noLearners': '利用できる学習者がいません。',
  'documents.share.removeButton': '解除',
  'documents.share.removeError': 'アクセスを解除できませんでした。',
  'documents.share.searchPlaceholder': '学習者を検索（姓、名、メール）',
  'documents.share.selectLearner': '既存の学習者を選択',
  'documents.share.shareButton': '共有',
  'documents.share.shareError': 'ドキュメントを共有できませんでした。',
  'documents.share.sharedSuccess': 'ドキュメントを共有し、招待を送信しました。',
  'documents.share.title': 'ドキュメントを共有',
  'documents.subtitle': 'デザイナーで再利用できるPDFライブラリ。',
  'documents.title': 'ドキュメント',
  'documents.toast.importError': 'インポートできません',
  'documents.toast.importErrorDefault': 'インポートに失敗しました。',
  'documents.toast.importSuccess': 'ドキュメントをインポートしました',
  'documents.toast.importSuccessDesc': '{{filename}} をドキュメントライブラリに追加しました。',
  'documents.updating': '更新中…',
  'help.categories.analytics.description': 'ダッシュボード、フィルター、エクスポートで研修の効果を分析します。',
  'help.categories.analytics.title': 'アナリティクスとレポート',
  'help.categories.apprenants.description': '登録、コホート、参加者の進捗を追跡します。',
  'help.categories.apprenants.title': '学習者、グループ、フォローアップ',
  'help.categories.boutique.description': 'ストア設定後に研修を販売または割り当てます。',
  'help.categories.boutique.title': 'ストアと収益化',
  'help.categories.demarrage.description': 'Lyrraのインターフェース、プロフィール、日常的なナビゲーションに慣れましょう。',
  'help.categories.demarrage.title': '開始とアカウント',
  'help.categories.donnees.description': 'GDPR、保存期間、個人の権利、セキュリティのベストプラクティス。',
  'help.categories.donnees.title': '個人データとセキュリティ',
  'help.categories.eduflow.description': 'ブロック、接続、ナビゲーションロジックを使って学習パスを設計します。',
  'help.categories.eduflow.title': 'パスとEduFlowデザイナー',
  'help.categories.evaluation.description': '多肢選択、評価、アンケート、フィードバックで習得度を測定します。',
  'help.categories.evaluation.title': '評価とアクティブラーニング',
  'help.categories.institution.description': '組織構造、メンバー、コンテンツの編成を設定します。',
  'help.categories.institution.title': '機関とチーム',
  'help.categories.marque.description': '色、ラベル、学習者パスをブランドアイデンティティに合わせます。',
  'help.categories.marque.title': 'ブランドとユーザー体験',
  'help.categories.medias.description': 'Lyrraに統合されたライブラリ、インポート、音声/動画/画像ツールを管理します。',
  'help.categories.medias.title': 'メディアとクリエイティブスタジオ',
  'help.categories.parcours-v2.description': 'キャンペーン、コンピテンシー、統合：研修管理を拡充するすべての機能。',
  'help.categories.parcours-v2.title': '高度な機能とロードマップ',
  'help.categories.podcasts.description': 'ブロック単位のパス外で長尺のオーディオコンテンツを作成・配信します。',
  'help.categories.podcasts.title': 'ポッドキャストとオーディオブック',
  'help.categories.publication.description': 'パスを学習者が利用できるようにし、適切なアクセスを共有します。',
  'help.categories.publication.title': '公開と配信',
  'help.categories.qualite.description': 'フランスの品質基準：トレーサビリティ、目標、監査のためのエビデンス。',
  'help.categories.qualite.title': '品質、Qualiopiと教育コンプライアンス',
  'help.categories.support.description': '技術的または機能的な問題が発生した場合に、どこを確認し誰に連絡するか。',
  'help.categories.support.title': 'トラブルシューティングとサポート',
  'help.configHint': '編集コンテンツ：準備ができたらURL、記事、またはナレッジベースを接続してください。',
  'help.description': 'ドキュメントとサポート',
  'help.hubIntro': 'Lyrraの使い方に関するテーマ：近日中に各カードをドキュメント、チュートリアル、またはサポートチケットにリンクできるようになります。',
  'help.modal.expectedAnswer': '期待される回答：',
  'help.modal.fallbackHighlight1': 'カード',
  'help.modal.fallbackHighlight2': '研修プラットフォーム',
  'help.modal.fallbackHighlight3': '要補完',
  'help.modal.fallbackSectionHeading': 'ポイント',
  'help.modal.fallbackSectionParagraph': 'このカードの詳細コンテンツは今後充実させるか、内部ドキュメントにリンクされます。',
  'help.modal.fallbackStep1Desc': 'サイドメニューまたはダッシュボードから該当モジュールにアクセスします。',
  'help.modal.fallbackStep1Title': '機能を開く',
  'help.modal.fallbackStep2Desc': '画面がガイドします。こまめに保存し、プレビューが利用できる場合はテストしてください。',
  'help.modal.fallbackStep2Title': 'ワークフローに従う',
  'help.modal.fallbackStep3Desc': 'チーム向けに施設固有の情報（人事プロセス、命名規則、ブランドガイドライン）を記録しましょう。',
  'help.modal.fallbackStep3Title': '内部でドキュメント化する',
  'help.modal.fallbackTip': '迷った場合は自分のロール（トレーナー、管理者、人事）を確認してください。表示されるメニューはロールによって異なります。',
  'help.modal.quiz': '確認クイズ',
  'help.modal.related': '関連項目',
  'help.modal.resources': 'リソース',
  'help.modal.steps': '実践',
  'help.modal.tips': 'アドバイス',
  'help.noResults': 'この検索に一致する結果はありません。',
  'help.onboarding.ai-designer': 'トレーニング — LyrraStudioデザイナーAIアシスタント (onboarding)',
  'help.onboarding.blocks': 'トレーニング — EduFlowブロックタイプ (onboarding)',
  'help.onboarding.branches': 'トレーニング — EduFlow分岐と条件 (onboarding)',
  'help.onboarding.catalog-visibility': 'トレーニング — LyrraStudioカタログと表示設定 (onboarding)',
  'help.onboarding.certificates': 'トレーニング — LyrraStudio発行済み証明書 (onboarding)',
  'help.onboarding.dashboard': 'トレーニング — Lyrraダッシュボード (onboarding)',
  'help.onboarding.designer-overview': 'トレーニング — EduFlowデザイナー概要 (onboarding)',
  'help.onboarding.enrollments': 'トレーニング — LyrraStudio登録と参加者 (onboarding)',
  'help.onboarding.groups-cohorts': 'トレーニング — LyrraStudioグループとコホート (onboarding)',
  'help.onboarding.media-library': 'トレーニング — LyrraStudioメディアライブラリ (onboarding)',
  'help.onboarding.navigation': 'トレーニング — ナビゲーションとサイドメニュー Lyrra (onboarding)',
  'help.onboarding.objectives': 'トレーニング — EduFlow学習目標 (onboarding)',
  'help.onboarding.profile': 'トレーニング — プロフィールと設定 Lyrra (onboarding)',
  'help.onboarding.progress': 'トレーニング — LyrraStudio進捗と所要時間 (onboarding)',
  'help.onboarding.publication-links': 'トレーニング — LyrraStudioアクセスリンクと共有 (onboarding)',
  'help.onboarding.publication-status': 'トレーニング — LyrraStudio下書き・公開・更新 (onboarding)',
  'help.onboarding.roles': 'トレーニング — ロールとアクセス権 Lyrra (onboarding)',
  'help.onboarding.validation': 'トレーニング — EduFlowパス検証 (onboarding)',
  'help.onboardingFlowMissing': 'トレーニングが見つかりません',
  'help.onboardingFlowMissingDesc': 'スクリプト db:seed:onboarding-eduflow と db:seed:onboarding-navigation-eduflow（バックエンド）、または変数 VITE_ONBOARDING_*_FLOW_ID を実行してください。',
  'help.openArticle': 'カードを開く',
  'help.searchPlaceholder': 'テーマを検索…',
  'help.title': 'ヘルプセンター',
  'help.topics.analytics-exports.description': '内部または規制レポート用のデータ抽出を準備します。',
  'help.topics.analytics-exports.title': 'エクスポートと人事レポート',
  'help.topics.analytics-indicateurs.description': '完了率、エンゲージメント、ステータス別分布、ブロック滞在時間。',
  'help.topics.analytics-indicateurs.title': '主要指標',
  'help.topics.analytics-vue.description': 'グラフ、期間、研修ごとのフィルターをカスタマイズしてデータを分析します。',
  'help.topics.analytics-vue.title': 'アナリティクス概要',
  'help.topics.app-certificats.description': '生成された証明書と関連する合格条件を確認します。',
  'help.topics.app-certificats.title': '発行済み証明書',
  'help.topics.app-groupes.description': 'グループを使って対象者をセグメント化し、キャンペーンやレポートを容易にします。',
  'help.topics.app-groupes.title': 'グループとコホート',
  'help.topics.app-inscriptions.description': 'パスの学習者リスト、ステータス、アクセス履歴を確認します。',
  'help.topics.app-inscriptions.title': '登録と参加者',
  'help.topics.app-progression.description': '進捗、完了、ブロックへのエンゲージメント時間を解釈します。',
  'help.topics.app-progression.title': '進捗と所要時間',
  'help.topics.boutique-offres.description': 'ビジネスモデルに応じてパスを商品やアクセスコードに関連付けます。',
  'help.topics.boutique-offres.title': 'オファーと価格設定',
  'help.topics.boutique-paiement.description': '学習者側の購入フローと組織側の確認を理解します。',
  'help.topics.boutique-paiement.title': '決済と請求',
  'help.topics.demarrage-navigation.description': 'ロールに応じてプロジェクト、研修、メディア、アナリティクス、設定の各スペースを見つけます。',
  'help.topics.demarrage-navigation.title': 'ナビゲーションとサイドメニュー',
  'help.topics.demarrage-profil.description': '個人情報、インターフェース言語、通知を更新します。',
  'help.topics.demarrage-profil.title': 'プロフィールと設定',
  'help.topics.demarrage-roles.description': 'トレーナー、機関管理者、人事、学習者の違いを理解します。',
  'help.topics.demarrage-roles.title': 'ロールとアクセス権',
  'help.topics.demarrage-tableau-bord.description': '主要指標の確認、最近のパスや便利なショートカットへのクイックアクセス。',
  'help.topics.demarrage-tableau-bord.title': '研修ダッシュボード',
  'help.topics.donnees-conservation.description': '進捗データと研修エビデンスのアーカイブ期間の原則。',
  'help.topics.donnees-conservation.title': '保存期間',
  'help.topics.donnees-mots-de-passe.description': 'パスワード、セッション、アクセス委任のベストプラクティス。',
  'help.topics.donnees-mots-de-passe.title': 'アカウントと認証',
  'help.topics.donnees-rgpd.description': 'Lyrraで収集されるデータの目的と研修責任者の責任。',
  'help.topics.donnees-rgpd.title': 'GDPRと処理台帳',
  'help.topics.eduflow-blocs.description': 'テキスト、動画、音声、評価、分岐など適切なブロックを選択・設定します。',
  'help.topics.eduflow-blocs.title': 'ブロックタイプ（コンテンツ、クイズ、メディア…）',
  'help.topics.eduflow-branches.description': '複数の出口、パスの合流、ナビゲーションルールを管理します。',
  'help.topics.eduflow-branches.title': '分岐と条件',
  'help.topics.eduflow-concept.description': 'キャンバス、ブロックパレット、選択、ズーム、パスの視覚的な構成。',
  'help.topics.eduflow-concept.title': 'デザイナー概要',
  'help.topics.eduflow-ia.description': 'アシスタントを使ってブロックを提案・修正し、人間による体系的なレビューを行います。',
  'help.topics.eduflow-ia.title': 'デザイナーのAIアシスタント',
  'help.topics.eduflow-objectifs.description': '目標をブロックに関連付けて研修のフレームワークを構築します。',
  'help.topics.eduflow-objectifs.title': '学習目標',
  'help.topics.eduflow-validation.description': '公開前に検証パネルを使ってエラーや警告を検出します。',
  'help.topics.eduflow-validation.title': 'パスの検証',
  'help.topics.eval-auto-eval.description': '結果に基づいてアダプティブパスを提案します。',
  'help.topics.eval-auto-eval.title': '自己評価とリメディエーション',
  'help.topics.eval-ouverte.description': '自由回答の収集と手動または半自動の採点を整理します。',
  'help.topics.eval-ouverte.title': '自由記述問題と採点',
  'help.topics.eval-quiz.description': '効果的な多肢選択テストのための配点、フィードバック、試行回数を設定します。',
  'help.topics.eval-quiz.title': 'クイズと多肢選択',
  'help.topics.inst-collaborateurs.description': 'トレーナー、管理者、人事を追加し、権限を管理します。',
  'help.topics.inst-collaborateurs.title': 'ユーザーと招待',
  'help.topics.inst-structure.description': '機関レベルで共有されるものと個人プロジェクトの境界を定義します。',
  'help.topics.inst-structure.title': '組織構造と範囲',
  'help.topics.inst-trash.description': '削除された研修やポッドキャストを見つけて必要に応じて復元します。',
  'help.topics.inst-trash.title': 'ゴミ箱と復元',
  'help.topics.marque-accessibilite.description': 'コンテンツのコントラスト、テキスト代替、キーボードナビゲーション。',
  'help.topics.marque-accessibilite.title': 'アクセシビリティと読みやすさ',
  'help.topics.marque-charte.description': 'プレーヤーと学習者スペースにブランドがどのように反映されるか。',
  'help.topics.marque-charte.title': 'ブランドガイドラインと機関カラー',
  'help.topics.marque-landing.description': 'モジュールが有効な場合に訪問者向けに研修オファーを提示します。',
  'help.topics.marque-landing.title': '公開ページとカタログ',
  'help.topics.medias-bibliotheque.description': '動画、音声、画像、ドキュメントをブロックにインポート、整理、再利用します。',
  'help.topics.medias-bibliotheque.title': 'メディアライブラリ',
  'help.topics.medias-documents.description': 'ダウンロード可能なリソースを添付し、ドキュメントをパスにリンクします。',
  'help.topics.medias-documents.title': 'ドキュメントとPDF',
  'help.topics.medias-studio-audio.description': '録音、ミキシング、音声合成を使ってコンテンツを充実させます。',
  'help.topics.medias-studio-audio.title': 'オーディオスタジオと音声',
  'help.topics.medias-video.description': '統合プレーヤー向けのフォーマット、ファイルサイズ、アクセシビリティのベストプラクティス。',
  'help.topics.medias-video.title': '動画と統合',
  'help.topics.podcasts-creation.description': 'オーディオコンテンツのエピソード、メタデータ、カバーを構成します。',
  'help.topics.podcasts-creation.title': 'ポッドキャストの作成',
  'help.topics.podcasts-diffusion.description': 'リスニング設定、共有、機関側の利用状況追跡。',
  'help.topics.podcasts-diffusion.title': '配信と学習者',
  'help.topics.pub-catalogue.description': '有効化されている場合に研修を機関カタログやオファーに関連付けます。',
  'help.topics.pub-catalogue.title': 'カタログと表示設定',
  'help.topics.pub-liens.description': '学習者、プレゼンター、プレビュー用のリンクを生成またはコピーします。',
  'help.topics.pub-liens.title': 'アクセスリンクと共有',
  'help.topics.pub-statuts.description': '研修のライフサイクル：保存、テスト、公開、変更のバージョン管理。',
  'help.topics.pub-statuts.title': '下書き、公開、更新',
  'help.topics.qualite-qualiopi.description': '専用スペースを使って基準と品質ファイルを準備します。',
  'help.topics.qualite-qualiopi.title': '事前監査とQualiopi指標',
  'help.topics.qualite-tracabilite.description': '目標、コンテンツ、評価を関連付けて認定パスを文書化します。',
  'help.topics.qualite-tracabilite.title': '教育トレーサビリティ',
  'help.topics.support-contact.description': 'サポートチャネル、チケット作成、迅速な診断に必要な情報。',
  'help.topics.support-contact.title': 'Lyrraサポートへの連絡',
  'help.topics.support-faq.description': 'プレーヤーが読み込まない、メディアが見つからない、公開エラー：最初の対処法。',
  'help.topics.support-faq.title': 'よくある問題',
  'help.topics.support-navigateur.description': '推奨バージョン、Cookie、干渉する可能性のある拡張機能。',
  'help.topics.support-navigateur.title': 'ブラウザと互換性',
  'help.topics.v2-campagnes.description': 'ターゲットグループへのパス展開を時間軸で計画します。',
  'help.topics.v2-campagnes.title': 'キャンペーンと研修計画',
  'help.topics.v2-competences.description': 'ブロックやパスを期待されるコンピテンシーにマッピングし、業務レポートに活用します。',
  'help.topics.v2-competences.title': 'コンピテンシーフレームワーク',
  'help.topics.v2-integrations.description': 'コネクタが利用可能な場合の人事システム、外部LMS、BIツールとの連携。',
  'help.topics.v2-integrations.title': '統合とAPI',
  'pages.images.cancel': 'キャンセル',
  'pages.images.changeFile': 'ファイルを変更',
  'pages.images.copyUrl': 'URLをコピー',
  'pages.images.count_one': 'ライブラリに画像が {{count}} 枚あります',
  'pages.images.count_other': 'ライブラリに画像が {{count}} 枚あります',
  'pages.images.delete': '削除',
  'pages.images.deleteAction': '削除',
  'pages.images.deleteConfirmHint': 'は完全に削除されます。',
  'pages.images.deleteTitle': 'この画像を削除しますか？',
  'pages.images.descriptionLabel': '説明（任意）',
  'pages.images.descriptionPlaceholder': '簡単な説明…',
  'pages.images.dropzoneBrowse': '参照',
  'pages.images.dropzoneFormats': 'PNG、JPG、WEBP、GIF',
  'pages.images.dropzoneLabel': '画像をドラッグするか',
  'pages.images.emptyHint': 'ドラッグ＆ドロップ、クリックでインポート、またはスクリーンショットを貼り付け（Ctrl+V / Cmd+V）。',
  'pages.images.fileSize.bytes': '{{size}} B',
  'pages.images.fileSize.kb': '{{size}} KB',
  'pages.images.fileSize.mb': '{{size}} MB',
  'pages.images.fileSizeKb': '{{size}} KB',
  'pages.images.importButton': 'インポート',
  'pages.images.importSubtitle': '研修用にライブラリへ画像を追加します。',
  'pages.images.importTitle': '画像をインポート',
  'pages.images.lightboxBackdropClose': '閉じる',
  'pages.images.lightboxClose': '閉じる',
  'pages.images.lightboxNext': '次の画像',
  'pages.images.lightboxPrev': '前の画像',
  'pages.images.lightboxTitle': '画像プレビュー',
  'pages.images.noImages': 'インポートされた画像はありません',
  'pages.images.noResults': '該当なし',
  'pages.images.pasteHint': 'またはスクリーンショットを貼り付け（Ctrl+V / Cmd+V）',
  'pages.images.searchPlaceholder': '画像を検索…',
  'pages.images.submit': 'インポート',
  'pages.images.title': '画像',
  'pages.images.titleLabel': 'タイトル（任意）',
  'pages.images.titlePlaceholder': '画像名…',
  'pages.images.viewLarge': '拡大表示',
  // ── Auto-injected missing t() fallbacks ──
  'a11y.skipToContent': 'Skip to main content',
  'a11y.skipToNav': 'Skip to navigation',
  'accessibility.simplifiedTextMode': 'Режим упрощенного текста',
  'accessibility.simplifiedTextModeDesc':
    'Упрощает только тексты курса, чтобы облегчить чтение и понимание.',
  'accessibility.imageHoverAudio': 'Режим слабовидения: аудиоописание изображения при наведении',
  'accessibility.imageHoverAudioDesc':
    'Когда курсор наводится на изображение в плеере, аудиоописание запускается автоматически.',
  'admin.dashboard.monthlyRevenue': 'Revenus factures payées (€)',
  'admin.dashboard.recentActivity': 'Activité récente',
  'admin.dashboard.revenue': 'Revenus (mois)',
  'admin.dashboard.storageUsed': 'Stockage estimé',
  'admin.dashboard.systemHealth': 'Santé système',
  'admin.dashboard.totalUsers': 'Utilisateurs',
  'admin.dashboard.userGrowth': 'Nouveaux utilisateurs / mois',
  'admin.dashboard.vsLastMonth': 'vs mois dernier',
  'admin.shell.collapse': 'Свернуть',
  'admin.shell.collapseSidebar': 'Свернуть боковую панель',
  'admin.shell.expandSidebar': 'Развернуть боковую панель',
  'admin.shell.title': 'Administration',
  'admin.smtp.pageHint': 'Serveur sortant pour les emails transactionnels de la plateforme.',
  'admin.smtp.pageTitle': 'Configuration SMTP',
  'auth.acceptTerms': 'I accept the',
  'auth.accessDenied': 'Access Denied',
  'auth.and': 'and',
  'auth.backToLogin': 'Back to login',
  'auth.checkInbox': 'Check your inbox for a password reset link. It may take a few minutes.',
  'auth.confirmPassword': 'Confirm password',
  'auth.confirmRequired': 'Please confirm your password',
  'auth.createAccount': 'Create your account',
  'auth.email': 'Email',
  'auth.emailInvalid': 'Invalid email address',
  'auth.emailPassword': 'Email & Password',
  'auth.emailRequired': 'Email is required',
  'auth.emailSent': 'Email sent!',
  'auth.firstName': 'First name',
  'auth.firstNameRequired': 'Required',
  'auth.forgotError': 'Failed to send reset email',
  'auth.forgotPassword': 'Forgot password?',
  'auth.forgotPasswordTitle': 'Forgot password?',
  'auth.goBack': 'Go back',
  'auth.goToLogin': 'Go to login',
  'auth.hasAccount': 'Already have an account?',
  'auth.hidePassword': 'Hide password',
  'auth.institutionRegister': 'Inscription Institution',
  'auth.invalidLink': 'Invalid reset link',
  'auth.invalidLinkDesc': 'This password reset link is invalid or has expired.',
  'auth.invalidToken': 'Invalid or expired reset token',
  'auth.lastName': 'Last name',
  'auth.lastNameRequired': 'Required',
  'auth.loginError': 'Login failed. Please check your credentials.',
  'auth.loginOTP': 'Login with OTP',
  'auth.loginSubtitle': 'Sign in to your LyrraStudio account',
  'auth.newPassword': 'New password',
  'auth.otpSubtitle': 'We sent a 6-digit code to your email',
  'auth.otpTitle': 'Enter verification code',
  'auth.password': 'Password',
  'auth.passwordMin': 'Minimum 8 characters',
  'auth.passwordMismatch': 'Passwords do not match',
  'auth.passwordRequired': 'Password is required',
  'auth.passwordReset': 'Password reset!',
  'auth.passwordResetDesc': 'Your password has been successfully updated.',
  'auth.privacy': 'Privacy Policy',
  'auth.registerError': 'Registration failed',
  'auth.registerSubtitle': 'Start creating with LyrraStudio',
  'auth.requestNewLink': 'Request a new link',
  'auth.resendCode': 'Resend code',
  'auth.resendIn': 'Resend code in',
  'auth.resetError': 'Failed to reset password',
  'auth.resetPassword': 'Reset password',
  'auth.resetPasswordSubtitle': 'Choose a strong password for your account',
  'auth.resetPasswordTitle': 'Set new password',
  'auth.sendOTP': 'Send OTP code',
  'auth.sendResetLink': 'Send reset link',
  'auth.showPassword': 'Show password',
  'auth.terms': 'Terms of Service',
  'auth.termsRequired': 'You must accept the terms',
  'auth.welcomeBack': 'Welcome back',
  'breadcrumb.home': 'Home',
  'breadcrumb.label': 'Breadcrumb',
  'campaign.analytics.avgProgress': 'Progression moy.',
  'campaign.analytics.avgProgressChart': 'Progression moyenne',
  'campaign.analytics.completion': 'Taux de complétion',
  'campaign.analytics.completionPct': 'Complétion %',
  'campaign.analytics.completionRate': 'Taux de complétion',
  'campaign.analytics.now': 'Actuel',
  'campaign.analytics.totalParticipants': 'Participants',
  'campaign.budget.none': 'Aucun budget renseigné.',
  'campaign.budget.title': 'Budget prévu',
  'campaign.col.participant': 'Participant',
  'campaign.col.progress': 'Progression',
  'campaign.col.status': 'Statut',
  'campaign.content.placeholderTitle': 'Contenus pédagogiques',
  'campaign.detail.actionError': 'Action impossible.',
  'campaign.detail.back': 'Retour aux campagnes',
  'campaign.detail.cancel': 'Annuler',
  'campaign.detail.complete': 'Clôturer',
  'campaign.detail.description': 'Description',
  'campaign.detail.launch': 'Lancer',
  'campaign.detail.loadError': 'Impossible de charger la campagne.',
  'campaign.detail.missingId': 'Identifiant manquant.',
  'campaign.detail.noDescription': 'Aucune description.',
  'campaign.detail.timeline': 'Calendrier',
  'campaign.participants.empty': 'Aucun participant assigné.',
  'campaign.participants.unknown': 'Inconnu',
  'campaign.stats.completed': 'Terminés',
  'campaign.stats.enrolled': 'Inscrits',
  'campaign.stats.inProgress': 'En cours',
  'campaign.stats.notStarted': 'Non démarrés',
  'campaign.tab.analytics': 'Analytique',
  'campaign.tab.budget': 'Budget',
  'campaign.tab.content': 'Contenu',
  'campaign.tab.overview': 'Vue d’ensemble',
  'campaign.tab.participants': 'Participants',
  'campaign.timeline.end': 'Fin prévue',
  'campaign.timeline.start': 'Début de campagne',
  'campaigns.col.budget': 'Budget',
  'campaigns.col.completion': 'Completion',
  'campaigns.col.enrolled': 'Enrolled',
  'campaigns.col.name': 'Name',
  'campaigns.col.period': 'Period',
  'campaigns.col.status': 'Status',
  'campaigns.create.budget': 'Budget (€)',
  'campaigns.create.budgetPlaceholder': '25000',
  'campaigns.create.departments': 'Departments',
  'campaigns.create.description': 'Description',
  'campaigns.create.endDate': 'End date',
  'campaigns.create.error': 'Could not create campaign.',
  'campaigns.create.individualSearchPlaceholder': 'Search people...',
  'campaigns.create.individuals': 'Individuals',
  'campaigns.create.launch': 'Launch campaign',
  'campaigns.create.launchNote': 'Launching notifies all participants and starts the campaign on the selected start date.',
  'campaigns.create.name': 'Campaign name',
  'campaigns.create.namePlaceholder': 'e.g. Leadership 2025',
  'campaigns.create.review': 'Review',
  'campaigns.create.reviewFieldBudget': 'Budget',
  'campaigns.create.reviewFieldName': 'Name',
  'campaigns.create.reviewFieldPeriod': 'Period',
  'campaigns.create.reviewContentItems': 'Training content',
  'campaigns.create.contentSelected': '{{count}} selected',
  'campaigns.create.selectContent': 'Select training content',
  'campaigns.create.startDate': 'Start date',
  'campaigns.create.steps.audience': 'Target audience',
  'campaigns.create.steps.basic': 'Basic information',
  'campaigns.create.steps.content': 'Content',
  'campaigns.create.steps.review': 'Review & launch',
  'campaigns.create.title': 'Create campaign',
  'campaigns.dept.engineering': 'Engineering',
  'campaigns.dept.product': 'Product',
  'campaigns.dept.sales': 'Sales',
  'campaigns.dept.marketing': 'Marketing',
  'campaigns.dept.finance': 'Finance',
  'campaigns.dept.legal': 'Legal',
  'campaigns.dept.hr': 'Human resources',
  'campaigns.list.create': 'New campaign',
  'campaigns.list.empty': 'No campaigns found.',
  'campaigns.list.loadError': 'Unable to load campaigns.',
  'campaigns.list.results': 'campaigns',
  'campaigns.list.search': 'Search campaigns...',
  'campaigns.list.title': 'Campaigns',
  'campaigns.page.documentTitle': 'Campaigns - LyrraStudio',
  'campaigns.sort.completion': 'Completion',
  'campaigns.sort.enrolled': 'Enrolled',
  'campaigns.sort.name': 'Name',
  'campaigns.sort.startDate': 'Start date',
  'campaigns.status.active': 'Active',
  'campaigns.status.all': 'All',
  'campaigns.status.cancelled': 'Cancelled',
  'campaigns.status.completed': 'Completed',
  'campaigns.status.draft': 'Draft',
  'campaigns.status.paused': 'Paused',
  'campaigns.status.scheduled': 'Scheduled',
  'campaigns.validation.budgetRequired': 'Budget is required.',
  'campaigns.validation.endDateRequired': 'End date is required.',
  'campaigns.validation.nameRequired': 'Campaign name is required.',
  'campaigns.validation.startDateRequired': 'Start date is required.',
  'catalog.backToCatalog': 'Retour au catalogue',
  'catalog.categories': 'Catégories',
  'catalog.category': 'Catégorie',
  'catalog.detailLoadError': 'Impossible de charger la formation.',
  'catalog.enrollError': 'Inscription impossible.',
  'catalog.enrollOk': 'Inscription enregistrée.',
  'catalog.enrollments': 'insc.',
  'catalog.flowFromTeacher': 'Parcours formateur',
  'catalog.format': 'Format',
  'catalog.language': 'Langue',
  'catalog.loadError': 'Impossible de charger le catalogue.',
  'catalog.missingId': 'Identifiant manquant.',
  'catalog.noObjectives': 'Aucune compétence liée.',
  'catalog.noResults': 'Aucune formation ne correspond à vos critères.',
  'catalog.objectives': 'Objectifs / compétences',
  'catalog.prerequisites': 'Prérequis',
  'catalog.prerequisitesPlaceholder': 'Les prérequis détaillés seront affichés lorsque renseignés sur la fiche.',
  'catalog.related': 'Formations populaires',
  'catalog.results': 'formation(s)',
  'catalog.search': 'Rechercher une formation…',
  'catalog.sortDuration': 'Durée',
  'catalog.sortName': 'Nom',
  'catalog.sortPopular': 'Popularité',
  'catalog.sortRating': 'Note',
  'certificate.completed': 'has successfully completed',
  'certificate.date': 'Date',
  'certificate.download': 'Download PDF',
  'certificate.title': 'Certificate of Completion',
  'certificate.verification': 'Verification',
  'certification.completeToUnlock': 'Complétez toutes les étapes pour débloquer votre certificat.',
  'certification.download': 'Télécharger',
  'certification.earned': 'Certification obtenue',
  'certification.locked': 'Verrouillé',
  'certification.milestones': 'étapes',
  'certification.preview': 'Certificat',
  'certification.requirements': 'Prérequis à valider :',
  'certification.subtitle': 'Suivez votre progression vers vos certifications professionnelles.',
  'certification.title': 'Parcours de certification',
  'codir.card.budgetAllocated': 'Budget alloué (année)',
  'codir.card.campaigns': 'Campagnes actives',
  'codir.card.completionYtd': 'Taux complétion (année)',
  'codir.card.costPerUser': 'Coût / utilisateur (estim.)',
  'codir.card.used': 'utilisé',
  'codir.dashboard.budgetOverview': 'Budget (année en cours)',
  'codir.dashboard.competencyGaps': 'Écarts compétences critiques',
  'codir.dashboard.completionTrends': 'Achèvements par mois',
  'codir.dashboard.completionsPerMonth': 'Achèvements / mois',
  'codir.dashboard.deptPerformance': 'Budget par direction',
  'codir.dashboard.loadError': 'Impossible de charger le tableau de bord.',
  'codir.dashboard.noBudget': 'Non renseigné',
  'codir.dashboard.noDeptData': 'Aucune ligne budgétaire',
  'codir.dashboard.noGaps': 'Aucun écart critique recensé.',
  'codir.dashboard.remaining': 'Restant',
  'codir.dashboard.spent': 'Consommé',
  'codir.dashboard.strategicKPIs': 'Indicateurs clés',
  'codir.dashboard.subtitle': 'Indicateurs formation et budget',
  'codir.dashboard.title': 'Tableau de bord CODIR',
  'codir.dashboard.totalBudget': 'Budget total',
  'codir.dashboard.utilization': 'Utilisation %',
  'codir.kpi.budget': 'Budget consommé',
  'codir.kpi.completion': 'Complétion',
  'codir.kpi.recentCompletions': 'Achèvements 30 j.',
  'codir.kpi.satisfaction': 'Satisfaction',
  'collab.certificates.active': 'actif(s)',
  'collab.certificates.empty': 'Aucun certificat pour ce filtre.',
  'collab.certificates.expires': 'Expire le',
  'collab.certificates.issued': 'Délivré le',
  'collab.certificates.title': 'Mes certificats',
  'collab.certificates.verify': 'Vérifier',
  'collab.dashboard.activeTrainings': 'Formations en cours',
  'collab.dashboard.certificates': 'Certificats',
  'collab.dashboard.completed': 'Terminées',
  'collab.dashboard.level': 'Niveau',
  'collab.dashboard.noActiveTrainings': 'Aucune formation en cours.',
  'collab.dashboard.noCompleted': 'Aucune formation terminée.',
  'collab.dashboard.noUpcoming': 'Aucune formation à venir.',
  'collab.dashboard.title': 'Mes formations',
  'collab.dashboard.totalXp': 'XP Total',
  'collab.dashboard.upcoming': 'À venir',
  'collab.page.documentTitle': 'My training - LyrraStudio',
  'collab.page.subtitle': 'Track your progress and manage your training',
  'collab.page.tabs.certificates': 'Certificates',
  'collab.page.tabs.dashboard': 'Dashboard',
  'collab.page.tabs.request': 'Make a request',
  'collab.page.tabs.skills': 'My skills',
  'collab.page.title': 'My training',
  'collab.request.history': 'Historique de mes demandes',
  'collab.request.justification': 'Justification',
  'collab.request.justificationPlaceholder': 'Expliquez pourquoi cette formation est nécessaire (min. 10 caractères)…',
  'collab.request.newRequest': 'Nouvelle demande',
  'collab.request.noHistory': 'Aucune demande pour le moment.',
  'collab.request.preferredDate': 'Date souhaitée',
  'collab.request.searchCatalog': 'Rechercher dans le catalogue…',
  'collab.request.submit': 'Envoyer la demande',
  'collab.request.title': 'Demander une formation',
  'collab.request.training': 'Formation',
  'collab.skills.current': 'Niveau actuel',
  'collab.skills.empty': 'Aucune compétence renseignée.',
  'collab.skills.emptyHint': 'Vos compétences apparaîtront ici une fois ajoutées.',
  'collab.skills.radar': 'Radar de compétences',
  'collab.skills.selfAssess': 'Auto-évaluation',
  'collab.skills.target': 'Objectif',
  'collab.skills.title': 'Mes compétences',
  'common.back': 'Back',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.collaborator': 'Collaborateur',
  'common.comingSoon': 'En construction',
  'common.comment': 'Commentaire',
  'common.comments': 'Comments',
  'common.complete': 'Complete',
  'common.confirm': 'Confirmer',
  'common.cost': 'Coût',
  'common.current': 'Current',
  'common.date': 'Date souhaitée',
  'common.delete': 'Supprimer',
  'common.description': 'Description',
  'common.download': 'Télécharger',
  'common.invalidEmail': 'Invalid email',
  'common.justification': 'Justification',
  'common.managerComment': 'Commentaire manager',
  'common.moveDown': 'Descendre',
  'common.moveUp': 'Monter',
  'common.next': 'Suivant',
  'common.noExpiry': 'Sans expiration',
  'common.noResults': 'Aucun résultat',
  'common.overall': 'Global',
  'common.preview': 'Preview',
  'common.previous': 'Précédent',
  'common.publish': 'Publish',
  'common.read': 'read',
  'common.refresh': 'Actualiser',
  'common.remove': 'Supprimer',
  'common.required': 'Required',
  'common.restore': 'Restore',
  'common.retry': 'Réessayer',
  'common.save': 'Save',
  'common.search': 'Rechercher…',
  'common.seeAll': 'Voir tout',
  'common.select': 'Select...',
  'common.sending': 'Envoi…',

  // Training request panel
  'trainingRequest.panelTitle': 'Запрос на обучение — учебный контент',
  'trainingRequest.intro': 'Опишите потребность. Команда HR одобряет или отклоняет запрос; в случае одобрения он передаётся на подготовку контента.',
  'trainingRequest.fieldTitle': 'Название обучения',
  'trainingRequest.fieldDescription': 'Описание потребности',
  'trainingRequest.fieldJustification': 'Обоснование / цели',
  'trainingRequest.fieldDate': 'Желаемая дата',
  'trainingRequest.datePlaceholder': 'напр. Q2 2026',
  'trainingRequest.fieldFormateur': 'Тренер учреждения',
  'trainingRequest.formateurPlaceholder': 'Выбрать тренера…',
  'trainingRequest.formateursEmpty': 'Нет учётной записи тренера для вашего учреждения.',
  'trainingRequest.titleRequired': 'Укажите название (минимум 2 символа).',
  'trainingRequest.sent': 'Запрос зарегистрирован. Вы получите уведомление по электронной почте о решении.',
  'trainingRequest.sendError': 'Не удалось сохранить запрос. Проверьте подключение или права доступа.',
  'trainingRequest.submit': 'Отправить запрос',
  'common.share': 'Partager',
  'common.startedOn': 'Débuté le',
  'common.status': 'Statut',
  'common.submit': 'Submit',
  'common.submittedAt': 'Soumis le',
  'common.title': 'Title',
  'common.training': 'Formation',
  'common.view': 'Consulter',
  'common.words': 'words',
  'compliance.download': 'Télécharger',
  'compliance.generate': 'Générer',
  'compliance.generating': 'Génération…',
  'compliance.lastGenerated': 'Généré le',
  'compliance.subtitle': 'Générez et gérez vos documents réglementaires et pédagogiques.',
  'compliance.title': 'Documents de conformité',
  'compliance.uploadEvidence': 'Importer un justificatif',
  'confirmDialog.cancel': 'Cancel',
  'confirmDialog.confirm': 'Confirm',
  'dashboard.activeUsersPeriod': 'Utilisateurs actifs (période)',
  'dashboard.analytics': 'Analytics',
  'dashboard.analyticsComingSoon': 'Analytics coming soon',
  'dashboard.collaborators': 'Collaborateurs',
  'dashboard.completionPercent': 'Completion %',
  'dashboard.home': 'Home',
  'dashboard.period.month': 'Month',
  'dashboard.period.quarter': 'Quarter',
  'dashboard.period.week': 'Week',
  'dashboard.publishedTraining': 'Formations publiées',
  'dashboard.settings': 'Settings',
  'dashboard.settingsComingSoon': 'Settings coming soon',
  'dashboard.topEduFlows': 'Meilleures formations',
  'dashboard.totalLearners': 'Total apprenants',
  'dysModule.font': 'OpenDyslexic',
  'dysModule.letter': 'Aa',
  'dysModule.ruler': 'Ruler',
  'dysModule.settings': 'Dyslexia settings',
  'dysModule.word': 'W',
  'eduflow.adaptiveReplacement.assist': 'Assisté',
  'eduflow.adaptiveReplacement.auto': 'Auto',
  'eduflow.adaptiveReplacement.force': 'Forcer la version jeu simplifiée pour ce bloc',
  'eduflow.adaptiveReplacement.manual': 'Manuel',
  'eduflow.adaptiveReplacement.mode': 'Mode',
  'eduflow.adaptiveReplacement.title': 'Remplacement adaptatif',
  'eduflow.answerSubmitted': '✓ Answer submitted successfully',
  'eduflow.blocks': 'Blocks',
  'eduflow.contents': 'Contents',
  'eduflow.correct': '✓ Correct!',
  'eduflow.designer.autoLayoutDone': 'Disposition automatique appliquée',
  'eduflow.designer.autoLayoutFailed': 'Impossible de calculer la disposition.',
  'eduflow.designer.autoLayoutNoBlocks': 'Ajoutez au moins un bloc pour lancer la disposition.',
  'eduflow.designer.blockLabel.summary': 'Récapitulatif',
  'eduflow.designer.newFlowDefaultTitle': 'Nouvelle formation',
  'eduflow.designer.paletteCategory.institution': 'Blocs institution',
  'eduflow.duration': 'Duration (seconds)',
  'eduflow.evaluation': 'Evaluation',
  'eduflow.evaluationSubmitted': 'Evaluation submitted',
  'eduflow.exportFlow': 'Export Flow',
  'eduflow.flowSettings': 'Flow Settings',
  'eduflow.form': 'Form',
  'eduflow.formSubmitted': 'Form submitted',
  'eduflow.incorrect': '✗ Incorrect. Try again.',
  'eduflow.mindmap': 'Mindmap',
  'eduflow.next': 'Далее',
  'eduflow.noExtraConfig': 'No additional configuration for this block type.',
  'eduflow.openQuestion': 'Open Question',
  'eduflow.phraseReveal.addPhrase': 'Ajouter une phrase',
  'eduflow.phraseReveal.autoScroll': 'Défilement automatique',
  'eduflow.phraseReveal.displayDuration': 'Durée par phrase (s)',
  'eduflow.phraseReveal.emptyPhrase': 'Phrase vide',
  'eduflow.phraseReveal.noPhrases': 'Aucune phrase pour l’instant.',
  'eduflow.phraseReveal.phraseContent': 'Contenu de la phrase',
  'eduflow.phraseReveal.phrasePlaceholder': 'Saisissez le texte de cette étape…',
  'eduflow.phraseReveal.showHistory': 'Afficher l’historique des phrases',
  'eduflow.quiz': 'Quiz',
  'eduflow.ragChatbot.addDocument': 'Ajouter un document (PDF, TXT, MD)',
  'eduflow.ragChatbot.confirmDelete': 'Supprimer ce document et son index ?',
  'eduflow.ragChatbot.deleteError': 'Suppression impossible.',
  'eduflow.ragChatbot.deleted': 'Document supprimé.',
  'eduflow.ragChatbot.dialogTitle': 'Assistant formation',
  'eduflow.ragChatbot.disabledOk': 'Chatbot désactivé.',
  'eduflow.ragChatbot.emptyDocs': 'Aucun document. Le chatbot utilisera uniquement le texte des blocs du parcours.',
  'eduflow.ragChatbot.enabledOk': 'Chatbot activé pour le player.',
  'eduflow.ragChatbot.loadError': 'Impossible de charger la configuration du chatbot.',
  'eduflow.ragChatbot.open': 'Ouvrir l’assistant',
  'eduflow.ragChatbot.panelTitle': 'Chatbot formation (RAG)',
  'eduflow.ragChatbot.placeholder': 'Votre question…',
  'eduflow.ragChatbot.playerError': 'Impossible d’obtenir une réponse. Réessayez plus tard.',
  'eduflow.ragChatbot.playerToggle': 'Afficher dans le player',
  'eduflow.ragChatbot.playerWelcome': 'Posez une question sur le cours ou les documents fournis.',
  'eduflow.ragChatbot.send': 'Envoyer',
  'eduflow.ragChatbot.toggleError': 'Mise à jour impossible.',
  'eduflow.ragChatbot.uploadError': 'Échec de l’upload ou de l’indexation.',
  'eduflow.ragChatbot.uploadOk': 'Document indexé.',
  'eduflow.readComplete': 'Read complete',
  'eduflow.selfAssessment.addBadge': 'Ajouter',
  'eduflow.selfAssessment.addCriterion': 'Ajouter',
  'eduflow.selfAssessment.badgeInputPlaceholder': 'Ex. Souvent, Jamais, Parfois…',
  'eduflow.selfAssessment.badgeOptionsLabel': 'Propositions (badges)',
  'eduflow.selfAssessment.criteriaSection': 'Critères',
  'eduflow.selfAssessment.criterionDescription': 'Description (optionnelle)',
  'eduflow.selfAssessment.criterionTitle': 'Intitulé',
  'eduflow.selfAssessment.moveDown': 'Descendre',
  'eduflow.selfAssessment.moveUp': 'Monter',
  'eduflow.selfAssessment.panelTitle': 'Auto-évaluation',
  'eduflow.selfAssessment.removeBadge': 'Retirer',
  'eduflow.selfAssessment.removeCriterion': 'Supprimer ce critère',
  'eduflow.selfAssessment.responseTypeLabel': 'Type de réponse',
  'eduflow.selfAssessment.scaleHint': 'De 1 à N (ex. 5 = Likert)',
  'eduflow.selfAssessment.scaleLabel': 'Échelle de notation',
  'eduflow.selfAssessment.starCountHint': 'Souvent 5',
  'eduflow.selfAssessment.starCountLabel': 'Nombre d’étoiles',
  'eduflow.selfAssessment.typeNumeric': 'Chiffres',
  'eduflow.selfAssessment.typeNumericHint': 'Boutons 1 à N',
  'eduflow.selfAssessment.typeStars': 'Étoiles',
  'eduflow.selfAssessment.typeStarsHint': 'Notation visuelle',
  'eduflow.selfAssessment.typeText': 'Texte libre',
  'eduflow.selfAssessment.typeTextHint': 'Réponse rédigée par critère',
  'eduflow.summary.addItem': 'Ajouter un point',
  'eduflow.summary.emptyList': 'Aucun point pour l’instant. Ajoutez-en un.',
  'eduflow.summary.itemPlaceholder': 'Ex. : Les risques incendie en entreprise',
  'eduflow.takeNotes': 'Take notes...',
  'eduflow.timeline.addEvent': 'Événement',
  'eduflow.timeline.alternatingSides': 'Côtés alternés',
  'eduflow.timeline.animatedReveal': 'Révélation animée',
  'eduflow.timeline.colorHint': 'Couleur du point (optionnel)',
  'eduflow.timeline.colorLabel': 'Couleur',
  'eduflow.timeline.datePlaceholder': 'Date ou période (ex. 1789, janv. 2024)',
  'eduflow.timeline.descPlaceholder': 'Description (affichée au développement)',
  'eduflow.timeline.events': 'Événements',
  'eduflow.timeline.horizontal': 'Horizontale',
  'eduflow.timeline.orientation': 'Orientation',
  'eduflow.timeline.settings': 'Paramètres',
  'eduflow.timeline.showConnectors': 'Lignes de connexion',
  'eduflow.timeline.untitled': 'Sans titre',
  'eduflow.timeline.vertical': 'Verticale',
  'eduflow.typeAnswer': 'Type your answer...',
  'eduflow.typeConfig': 'Type Configuration',
  'eduflow.unsaved': 'Unsaved changes',
  'eduflow.versionHistory': 'Version History',
  'eduflow.versionHistory.activateSuccess': 'Версия {{label}} активирована (публичная)',
  'eduflow.versionHistory.activateTitle': 'Активировать {{label}}?',
  'eduflow.versionHistory.blocks': '{{count}} блоков',
  'eduflow.versionHistory.confirmActivate': 'Активировать',
  'eduflow.versionHistory.copyFailed': 'Копирование не удалось',
  'eduflow.versionHistory.copyHtml': 'Скопировать HTML',
  'eduflow.versionHistory.copyPlain': 'Скопировать только текст',
  'eduflow.versionHistory.copyPublicPlayer': 'Скопировать публичную ссылку',
  'eduflow.versionHistory.copyPublicPlayerError': 'Не удалось скопировать публичную ссылку',
  'eduflow.versionHistory.count': '{{count}} версия(й)',
  'eduflow.versionHistory.createError': 'Ошибка при создании версии',
  'eduflow.versionHistory.createMajor': 'Основная версия',
  'eduflow.versionHistory.createMajorSuccess': 'Основная версия создана',
  'eduflow.versionHistory.createMinor': 'Второстепенная версия',
  'eduflow.versionHistory.createMinorSuccess': 'Второстепенная версия создана',
  'eduflow.versionHistory.daysAgo': '{{count}} дн назад',
  'eduflow.versionHistory.deleteError': 'Ошибка при удалении',
  'eduflow.versionHistory.deleteSuccess': 'Версия удалена',
  'eduflow.versionHistory.deleteTitle': 'Удалить {{label}}?',
  'eduflow.versionHistory.deleteWarning': 'Это действие необратимо.',
  'eduflow.versionHistory.duplicateConfirm': 'Дублировать',
  'eduflow.versionHistory.duplicateError': 'Ошибка при дублировании',
  'eduflow.versionHistory.duplicateMinor': 'Дублировать (второстепенная)',
  'eduflow.versionHistory.duplicateSuccess': 'Version dupliquée — contenu restauré dans l’éditeur',
  'eduflow.versionHistory.duplicateTitle': 'Дублировать {{label}}?',
  'eduflow.versionHistory.empty': 'Версии не созданы.',
  'eduflow.versionHistory.emptyHint': 'Сохраните основную или второстепенную версию, чтобы найти её здесь.',
  'eduflow.versionHistory.hoursAgo': '{{count}} ч назад',
  'eduflow.versionHistory.latest': 'Последняя',
  'eduflow.versionHistory.minutesAgo': '{{count}} мин назад',
  'eduflow.versionHistory.newFromCurrent': 'Новая версия из текущего содержимого',
  'eduflow.versionHistory.openPublicPlayer': 'Открыть публичный плеер',
  'eduflow.versionHistory.preview': 'Предпросмотр',
  'eduflow.versionHistory.previewBlocks': '{{count}} блоков',
  'eduflow.versionHistory.previewEdges': '{{count}} соединений',
  'eduflow.versionHistory.previewError': 'Impossible de charger l’aperçu',
  'eduflow.versionHistory.previewTitle': 'Предпросмотр {{label}}',
  'eduflow.versionHistory.public': 'Публичная версия',
  'eduflow.versionHistory.publicActive': 'Активна для учащихся',
  'eduflow.versionHistory.setPublic': 'Активировать',
  'eduflow.versionHistory.startDescModalTitle': 'Описание — блок Старт ({{version}})',
  'eduflow.versionHistory.yesterday': 'вчера',
  'eduflow.videoCompleted': 'Video watched',
  'footer.accessibility': 'Accessibility',
  'footer.forEducation': 'for education',
  'footer.legal': 'Legal links',
  'footer.madeWith': 'Made with',
  'footer.privacy': 'Privacy',
  'footer.terms': 'Terms',
  'gamification.active': 'actives',
  'gamification.badges': 'Badges',
  'gamification.bestStreak': 'Meilleur streak',
  'gamification.claim': 'Récupérer',
  'gamification.completed': 'Terminées',
  'gamification.days': 'jours',
  'gamification.leaderboard': 'Classement',
  'gamification.level': 'Niv.',
  'gamification.maxLevel': '✨ Niveau max atteint !',
  'gamification.missions': 'Missions',
  'gamification.name': 'Nom',
  'gamification.streakActive': 'Streak en cours 🔥',
  'gamification.streakInactive': 'Pas de streak actif',
  'gamification.xpRemaining': '${formatXp(xpForNextLevel - currentXp)} XP restants',
  'header.language': 'Language',
  'header.logout': 'Logout',
  'header.profile': 'Profile',
  'header.search': 'Search',
  'header.settings': 'Settings',
  'header.toggleMenu': 'Toggle menu',
  'header.toggleTheme': 'Toggle theme',
  'institution.categories.color': 'Couleur',
  'institution.categories.coverLabel': 'Image de couverture',
  'institution.categories.coverUploadError': 'Échec du téléversement de la couverture.',
  'institution.categories.removeCover': 'Retirer l’image',
  'institution.categories.descShortPh': 'Décrivez l’usage de la catégorie',
  'institution.categories.edit': 'Modifier la catégorie',
  'institution.categories.episodes': 'épisode(s)',
  'institution.categories.learners': 'apprenant(s)',
  'institution.categories.namePlaceholder': 'Nom de la catégorie',
  'institution.categories.new': 'Nouvelle catégorie',
  'institution.categories.preview': 'Aperçu',
  'institution.categories.previewApiModeOff': 'Prévisualisation indisponible (mode local).',
  'institution.categories.previewDesc': 'Podcasts et parcours reliés à cette catégorie.',
  'institution.categories.previewError': 'Erreur lors du chargement de l’aperçu.',
  'institution.categories.previewFormations': 'Parcours / formations',
  'institution.categories.previewLoading': 'Chargement de l’aperçu…',
  'institution.categories.previewNoFlows': 'Aucune formation rattachée à cette catégorie.',
  'institution.categories.previewNoPodcasts': 'Aucun podcast rattaché à cette catégorie.',
  'institution.categories.previewPodcasts': 'Podcasts',
  'institution.categories.saveError': 'Erreur lors de la sauvegarde.',
  'institution.categories.subscribers': 'abonné(s)',
  'institution.categories.usageItems': 'élément(s)',
  'institution.common.add': 'Ajouter',
  'institution.common.cancel': 'Annuler',
  'institution.common.close': 'Fermer',
  'institution.common.delete': 'Supprimer',
  'institution.common.description': 'Description',
  'institution.common.email': 'Email',
  'institution.common.loading': 'Chargement…',
  'institution.common.name': 'Nom',
  'institution.common.refresh': 'Actualiser',
  'institution.common.required': 'Requis',
  'institution.common.retry': 'Réessayer',
  'institution.common.save': 'Enregistré',
  'institution.contentPipeline.confirmDeletePath': 'Supprimer ce parcours ?',
  'institution.contentPipeline.createPath': 'Créer le parcours',
  'institution.contentPipeline.createPodcast': 'Créer le podcast',
  'institution.contentPipeline.createShell': 'Créer la fiche',
  'institution.contentPipeline.descPh': 'Description / objectifs',
  'institution.contentPipeline.newPath': 'Nouveau parcours',
  'institution.contentPipeline.newPodcastShell': 'Pré-créer un podcast',
  'institution.contentPipeline.newShell': 'Nouvelle fiche formation',
  'institution.contentPipeline.openDesigner': 'Ouvrir dans le designer',
  'institution.contentPipeline.openStudio': 'Épisodes',
  'institution.contentPipeline.pathDescLabel': 'Description',
  'institution.contentPipeline.pathPlannedLaunch': 'Date cible de lancement',
  'institution.contentPipeline.pathTitlePh': 'Titre du parcours',
  'institution.contentPipeline.pickProducer': 'Assigner producteur…',
  'institution.contentPipeline.pickTeacher': 'Assigner…',
  'institution.contentPipeline.plannedDeadline': 'Échéance',
  'institution.contentPipeline.plannedStart': 'Début prévu',
  'institution.contentPipeline.planningColumn': 'Planning création',
  'institution.contentPipeline.planningOptional': 'Planning de création (optionnel)',
  'institution.contentPipeline.podDescPh': 'Brief / description pour le formateur',
  'institution.contentPipeline.savePlanning': 'Enregistrer le planning',
  'institution.contentPipeline.statFormations': 'Fiches catalogue',
  'institution.contentPipeline.statPaths': 'Parcours certifiants',
  'institution.contentPipeline.statPodcasts': 'Podcasts',
  'institution.contentPipeline.tabFormations': 'Fiches formations',
  'institution.contentPipeline.tabPaths': 'Parcours',
  'institution.contentPipeline.tabPodcasts': 'Podcasts',
  'institution.contentPipeline.title': 'Préparation du contenu',
  'institution.contentPipeline.titlePh': 'Titre',
  'institution.learnerAnalytics.chartActivity': 'Activité et complétions (8 dernières semaines)',
  'institution.learnerAnalytics.chartProgress': 'Progression globale (par apprenant)',
  'institution.learnerAnalytics.chartStatus': 'Répartition par statut',
  'institution.learnerAnalytics.chartTopFlows': 'Formations les plus suivies',
  'institution.learnerAnalytics.colDone': 'Terminé',
  'institution.learnerAnalytics.colFlow': 'Formation',
  'institution.learnerAnalytics.colLastAccess': 'Dernier accès',
  'institution.learnerAnalytics.colName': 'Apprenant',
  'institution.learnerAnalytics.colTime': 'Temps',
  'institution.learnerAnalytics.count': 'Effectif',
  'institution.learnerAnalytics.done': 'Terminés',
  'institution.learnerAnalytics.enrolled': 'Inscrits',
  'institution.learnerAnalytics.kpiEnrollments': 'Inscriptions',
  'institution.learnerAnalytics.kpiLearners': 'Apprenants',
  'institution.learnerAnalytics.kpiLearnersSub': 'Participants enregistrés',
  'institution.learnerAnalytics.kpiRate': 'Taux de complétion',
  'institution.learnerAnalytics.kpiRateSub': 'Sur l’ensemble des inscriptions',
  'institution.learnerAnalytics.kpiTime': 'Temps cumulé',
  'institution.learnerAnalytics.kpiTimeSub': 'Temps passé sur les parcours',
  'institution.learnerAnalytics.loadError': 'Impossible de charger les analyses.',
  'institution.learnerAnalytics.noData': 'Pas encore de données.',
  'institution.learnerAnalytics.seriesActivity': 'Accès (inscriptions)',
  'institution.learnerAnalytics.seriesCompletions': 'Complétions',
  'institution.learnerAnalytics.tableEmpty': 'Aucune ligne à afficher.',
  'institution.learnerAnalytics.tableSearch': 'Rechercher nom, email, formation…',
  'institution.learnerAnalytics.tableTitle': 'Détail des inscriptions',
  'institution.learnerAnalytics.tableTruncated': 'Affichage limité à 500 lignes — utilisez l’export pour la liste complète.',
  'institution.learnerAnalytics.title': 'Analyse & suivi des apprenants',
  'institution.learnerAnalytics.xapiDataSourceTitle': 'Source des données & xAPI',
  'institution.learners.flowDone': 'Terminé',
  'institution.learners.inProgress': 'En cours',
  'institution.menuBlocked.title': 'Fonctionnalité non disponible',
  'institution.rhModule.pickFromMenu': 'Choisissez un outil dans le menu Formation & RH.',
  'institution.sirh.apiKey': 'Clé API',
  'institution.sirh.apiKeyStored': 'déjà enregistrée — laissez vide pour ne pas changer',
  'institution.sirh.apiSecret': 'Secret / second jeton (optionnel)',
  'institution.sirh.apiSecretHint': 'Envoyé en en-tête X-API-Secret si renseigné.',
  'institution.sirh.baseUrl': 'URL de base de l’API',
  'institution.sirh.baseUrlHint': 'Lyrra appellera GET {URL}/employees avec Authorization: Bearer &lt;clé API&gt;.',
  'institution.sirh.enabled': 'Synchronisation activée',
  'institution.sirh.formatTitle': 'Format attendu pour GET /employees',
  'institution.sirh.loadError': 'Impossible de charger la configuration SIRH.',
  'institution.sirh.mappingJob': 'Poste / fonction (jobTitle ou ex. position)',
  'institution.sirh.mappingTitle': 'Correspondance des champs (JSON SIRH)',
  'institution.sirh.name': 'Libellé interne',
  'institution.sirh.saveError': 'Enregistrement impossible.',
  'institution.sirh.saved': 'Configuration enregistrée.',
  'institution.sirh.secretStored': 'déjà enregistré',
  'institution.sirh.syncDone': 'Synchronisation : ${s.created} créés, ${s.updated} mis à jour, ${s.errors} erreurs, ${s.skipped} ignorés (autre établissement).',
  'institution.sirh.syncError': 'Synchronisation impossible.',
  'institution.sirh.syncNow': 'Synchroniser les collaborateurs',
  'institution.sirh.syncOk': 'Synchronisation terminée.',
  'institution.sirh.test': 'Tester la connexion',
  'institution.sirh.testError': 'Test impossible.',
  'institution.sirh.title': 'SIRH — connexion API',
  'institution.sirh.type': 'Type / famille',
  'institution.sirh.typeCustom': 'API HTTP personnalisée',
  'institution.ssoOidc.autoProvision': 'Créer automatiquement les comptes à la première connexion (si pris en charge)',
  'institution.ssoOidc.clientId': 'Client ID',
  'institution.ssoOidc.clientSecret': 'Client secret',
  'institution.ssoOidc.copied': 'Copié',
  'institution.ssoOidc.copy': 'Copier',
  'institution.ssoOidc.copyError': 'Copie impossible.',
  'institution.ssoOidc.defaultRole': 'Rôle par défaut (provisionnement)',
  'institution.ssoOidc.enableSsoGeneric': 'Activer le SSO pour cet établissement',
  'institution.ssoOidc.flagsError': 'Mise à jour des options impossible.',
  'institution.ssoOidc.flagsSuccess': 'Options SSO mises à jour.',
  'institution.ssoOidc.formTitle': 'Identifiants OpenID Connect',
  'institution.ssoOidc.issuer': 'Issuer (URL)',
  'institution.ssoOidc.loadError': 'Impossible de charger la configuration SSO.',
  'institution.ssoOidc.optionsTitle': 'Activation et provisionnement',
  'institution.ssoOidc.pageTitle': 'SSO — OpenID Connect & SAML 2.0',
  'institution.ssoOidc.redirectTitle': 'URL de redirection (callback)',
  'institution.ssoOidc.saveCredentials': 'Enregistrer les identifiants',
  'institution.ssoOidc.saveError': 'Enregistrement impossible.',
  'institution.ssoOidc.saveOptions': 'Enregistrer les options',
  'institution.ssoOidc.saveSuccess': 'Configuration OpenID Connect enregistrée.',
  'institution.ssoOidc.scopes': 'Scopes (séparés par un espace)',
  'institution.ssoOidc.secretOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoOidc.secretRequired': 'Indiquez le secret client (première configuration).',
  'institution.ssoOidc.tablistAria': 'Protocole SSO',
  'institution.ssoSaml.acsTitle': 'URL Assertion Consumer Service (ACS)',
  'institution.ssoSaml.certOptional': 'laisser vide pour conserver l’actuel',
  'institution.ssoSaml.certificate': 'Certificat de signature IdP (PEM)',
  'institution.ssoSaml.copied': 'Copié',
  'institution.ssoSaml.copy': 'Copier',
  'institution.ssoSaml.copyError': 'Copie impossible.',
  'institution.ssoSaml.downloadError': 'Téléchargement des métadonnées SP impossible.',
  'institution.ssoSaml.downloadSp': 'Métadonnées SP (XML)',
  'institution.ssoSaml.entityId': 'Entity ID (Issuer IdP)',
  'institution.ssoSaml.manualTitle': 'Paramètres IdP (manuel)',
  'institution.ssoSaml.metadataEmpty': 'Collez le XML des métadonnées IdP.',
  'institution.ssoSaml.metadataError': 'Import des métadonnées impossible.',
  'institution.ssoSaml.metadataFormTitle': 'Importer les métadonnées IdP (XML)',
  'institution.ssoSaml.metadataStored': 'Un fichier de métadonnées IdP est déjà stocké.',
  'institution.ssoSaml.metadataSuccess': 'Métadonnées SAML IdP enregistrées.',
  'institution.ssoSaml.metadataUrl': 'URL des métadonnées IdP (optionnel)',
  'institution.ssoSaml.saveError': 'Enregistrement SAML impossible.',
  'institution.ssoSaml.saveManual': 'Enregistrer la configuration SAML',
  'institution.ssoSaml.saveSuccess': 'Configuration SAML enregistrée.',
  'institution.ssoSaml.sloUrl': 'URL de déconnexion SLO (optionnel)',
  'institution.ssoSaml.ssoUrl': 'URL de connexion SSO (IdP)',
  'institution.ssoSaml.uploadMetadata': 'Enregistrer le XML IdP',
  'institution.templates.blockType': 'Type de bloc',
  'institution.templates.create': 'Créer un modèle',
  'institution.templates.createDrawerTitle': 'Créer un modèle de bloc',
  'institution.templates.createNameRequired': 'Veuillez renseigner le titre du modèle.',
  'institution.templates.editDrawerTitle': 'Modifier le modèle',
  'institution.templates.modelDescPh': 'Description du modèle',
  'institution.templates.modelNamePh': 'Titre du modèle',
  'institution.templates.previewDrawerTitle': 'Aperçu du modèle',
  'institution.templates.publish': 'Publier',
  'institution.templates.published': 'Publié',
  'institution.templates.typeConfig': 'Configuration du bloc',
  'institution.templates.unpublish': 'Dépublier',
  'institution.xapi.accessToken': 'Jeton d’accès (Bearer)',
  'institution.xapi.actorField': 'Champ acteur (référence)',
  'institution.xapi.authBasic': 'HTTP Basic (utilisateur / mot de passe)',
  'institution.xapi.authBearer': 'Bearer / jeton OAuth',
  'institution.xapi.authType': 'Authentification LRS',
  'institution.xapi.enable': 'Activer l’envoi des statements vers le LRS',
  'institution.xapi.formTitle': 'Paramètres LRS',
  'institution.xapi.loadError': 'Impossible de charger la configuration xAPI.',
  'institution.xapi.loadStatements': 'Afficher 15 statements (JSON)',
  'institution.xapi.lrsReadTitle': 'Lecture distante (diagnostic)',
  'institution.xapi.lrsUrl': 'URL de base du LRS',
  'institution.xapi.optionalKeep': 'optionnel pour conserver l’actuel',
  'institution.xapi.password': 'Mot de passe / secret',
  'institution.xapi.payloadOptions': 'Données incluses dans les statements (parcours)',
  'institution.xapi.profile': 'Profil / usage',
  'institution.xapi.save': 'Enregistrer',
  'institution.xapi.saveError': 'Enregistrement impossible.',
  'institution.xapi.saveSuccess': 'Configuration xAPI enregistrée.',
  'institution.xapi.sendCompletion': 'Envoyer complétion',
  'institution.xapi.sendDuration': 'Envoyer durée',
  'institution.xapi.sendScore': 'Envoyer score',
  'institution.xapi.statementsError': 'Impossible de charger les statements.',
  'institution.xapi.sync': 'Lire un lot (aperçu)',
  'institution.xapi.syncError': 'Lecture LRS impossible.',
  'institution.xapi.test': 'Tester le LRS',
  'institution.xapi.testError': 'Test impossible.',
  'institution.xapi.testFail': 'Test LRS échoué.',
  'institution.xapi.testOk': 'LRS joignable.',
  'institution.xapi.title': 'xAPI — Learning Record Store (LRS)',
  'institution.xapi.username': 'Utilisateur (Basic Auth)',
  'loading.label': 'Loading…',
  'manager.dashboard.loadError': 'Chargement impossible.',
  'manager.dashboard.noReports': 'Aucun collaborateur rattaché comme manager dans le SIRH.',
  'manager.dashboard.pendingCount': 'en attente',
  'manager.dashboard.pendingWorkflowHint': 'Les workflows visibles sont ceux dont vous êtes le demandeur.',
  'manager.dashboard.pendingWorkflowTitle': 'Demandes en circuit',
  'manager.dashboard.title': 'Tableau de bord Manager',
  'manager.dashboard.workflowBox': 'Validation',
  'manager.kpi.activeTrainings': 'Formations actives',
  'manager.kpi.completionRate': 'Taux de complétion',
  'manager.kpi.pendingWorkflow': 'Workflows en attente',
  'manager.nav.requests': 'Demandes de formation',
  'manager.nav.reviews': 'Évaluations',
  'manager.page.documentTitle': 'Manager workspace - LyrraStudio',
  'manager.page.subtitle': 'Manage training plans and your team progress',
  'manager.page.tabs.dashboard': 'Dashboard',
  'manager.page.tabs.requests': 'Requests',
  'manager.page.tabs.reviews': 'Reviews',
  'manager.page.tabs.team': 'Team',
  'manager.page.title': 'Manager workspace',
  'manager.requests.commentPlaceholder': 'Ajouter un commentaire…',
  'manager.requests.empty': 'Aucune demande pour ce filtre.',
  'manager.requests.title': 'Demandes de formation',
  'manager.reviews.commentPlaceholder': 'Commentaire sur cette compétence…',
  'manager.reviews.lastAssessment': 'Dernière éval.',
  'manager.reviews.selectMember': 'Sélectionnez un collaborateur pour évaluer',
  'manager.reviews.submitted': 'Évaluation soumise',
  'manager.reviews.target': 'Cible',
  'manager.reviews.title': 'Évaluations de compétences',
  'manager.team.loadError': 'Chargement impossible.',
  'nav.webinarsEnterprise': 'Webinaires — pilotage',
  'notifications.empty': 'No notifications yet',
  'notifications.label': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.markRead': 'Mark as read',
  'notifications.title': 'Notifications',
  'notifications.viewAll': 'View all notifications',
  'pages.branding.comingSoonDesc': 'Configurez les couleurs, logos et la charte graphique de votre espace de formation.',
  'pages.branding.title': 'Identité visuelle',
  'pages.budget.comingSoonDesc': 'Planifiez et suivez votre budget de formation, gérez les enveloppes et les dépenses par département.',
  'pages.budget.description': 'Gestion du budget de formation',
  'pages.budget.title': 'Budget',
  'pages.campaigns.description': 'Gestion des campagnes de formation',
  'pages.campaigns.title': 'Campagnes',
  'pages.catalog.comingSoonDesc': 'Parcourez le catalogue de formations disponibles, recherchez par compétence et inscrivez-vous aux sessions.',
  'pages.catalog.description': 'Catalogue des formations',
  'pages.catalog.title': 'Catalogue',
  'pages.certificates.comingSoonDesc': 'Consultez et téléchargez vos certificats de formation, attestations et badges obtenus.',
  'pages.certificates.description': 'Vos certifications et attestations',
  'pages.certificates.title': 'Certificats',
  'pages.competencies.comingSoonDesc': 'Gérez votre référentiel de compétences, cartographiez les savoir-faire et identifiez les besoins en formation.',
  'pages.competencies.description': 'Référentiel de compétences',
  'pages.competencies.title': 'Compétences',
  'pages.compliance.comingSoonDesc': 'Suivez les formations obligatoires, les certifications et la conformité réglementaire de votre organisation.',
  'pages.compliance.description': 'Suivi de la conformité réglementaire',
  'pages.compliance.title': 'Conformité',
  'pages.integrations.comingSoonDesc': 'Connectez votre plateforme à vos outils RH, SIRH, LMS et autres services pour synchroniser vos données.',
  'pages.integrations.description': 'Connexions aux services externes',
  'pages.integrations.title': 'Intégrations',
  'pages.reports.comingSoonDesc': 'Générez des rapports détaillés sur les activités de formation, les budgets et les indicateurs de performance.',
  'pages.reports.description': 'Rapports et bilans de formation',
  'pages.reports.title': 'Rapports',
  'pages.requests.description': 'Gestion des demandes de formation',
  'pages.requests.title': 'Demandes',
  'pages.reviews.comingSoonDesc': 'Gérez les entretiens annuels, les évaluations de compétences et le suivi de progression de votre équipe.',
  'pages.reviews.description': 'Entretiens et évaluations',
  'pages.reviews.title': 'Évaluations',
  'pages.settings.description': 'Configuration de votre compte et préférences',
  'pages.settings.title': 'Paramètres',
  'pages.skills.description': 'Votre profil de compétences',
  'pages.skills.title': 'Mes compétences',
  'pages.team.comingSoonDesc': 'Visualisez votre équipe, suivez les compétences et les formations de chaque collaborateur.',
  'pages.team.description': 'Gestion de votre équipe',
  'pages.team.title': 'Mon équipe',
  'pages.training.comingSoonDesc': 'Accédez à vos formations en cours, suivez votre progression et reprenez là où vous vous êtes arrêté.',
  'pages.training.description': 'Vos parcours de formation en cours',
  'pages.training.title': 'Mes formations',
  'pages.webhooks.comingSoonDesc': 'Configurez des webhooks pour connecter votre plateforme à des services externes et automatiser vos flux.',
  'pages.webhooks.description': 'Configuration des webhooks et intégrations',
  'pages.webhooks.title': 'Webhooks',
  'player.adaptiveGame.done': 'Objectif valide en mode simplifie',
  'player.adaptiveGame.next': 'Etape suivante',
  'player.adaptiveGame.step': 'Etape {{index}} / {{total}}',
  'player.adaptiveGame.subtitle': 'Une alternative plus accessible pour valider cet objectif.',
  'player.adaptiveGame.title': 'Version simplifiee interactive',
  'player.adaptiveGame.validate': 'Valider',
  'player.adaptivePrompt.keepOriginal': 'Garder le bloc standard',
  'player.adaptivePrompt.subtitle': 'Souhaitez-vous utiliser une version interactive plus accessible ?',
  'player.adaptivePrompt.title': 'Version simplifiee disponible pour ce bloc',
  'player.adaptivePrompt.useSimplified': 'Utiliser la version simplifiee',
  'player.descriptionTeleprompter': 'Mode téléprompteur',
  'player.descriptionTeleprompterOff': 'Afficher le bloc',
  'player.descriptionVoiceover': 'Voix off — description du bloc',
  'player.podcast.hideEpisodeText': 'Masquer le texte',
  'player.podcast.showEpisodeText': 'Afficher le texte',
  'player.selfAssessment.commentLabel': 'Commentaire (optionnel)',
  'player.selfAssessment.continue': 'Continuer',
  'player.selfAssessment.criterionFallback': 'critère',
  'player.selfAssessment.empty': 'Aucun critère n’est défini pour cette auto-évaluation.',
  'player.selfAssessment.pick': 'Choisissez une note',
  'player.selfAssessment.pickBadges': 'Sélectionnez une ou plusieurs propositions :',
  'player.selfAssessment.rateAria': 'Note {{n}} : {{label}}',
  'player.selfAssessment.submit': 'Valider mon auto-évaluation',
  'player.selfAssessment.submitted': 'Réponses enregistrées.',
  'player.subflow.blockTitle': 'Sous-formation',
  'player.subflow.defaultTitle': 'Sous-formation',
  'player.subflow.loadError': 'La sous-formation n’a pas pu être chargée.',
  'player.subflow.loading': 'Chargement…',
  'player.subflow.missingId': 'Aucune sous-formation n’est liée à ce bloc.',
  'player.subflow.noContext': 'Impossible de charger la sous-formation (contexte lecteur manquant).',
  'player.subflow.start': 'Commencer la sous-formation',
  'player.summary.badge': 'Synthèse',
  'player.summary.noItems': 'Aucun point de récapitulatif n’a été défini pour ce bloc.',
  'player.summary.blockTranslating': 'Идёт перевод…',
  'player.teleprompterNextParagraph': 'Paragraphe suivant',
  'player.teleprompterPrevParagraph': 'Paragraphe précédent',
  'podcast.episodes.chapters': 'Chapitres',
  'podcast.episodes.hideText': 'Masquer le texte',
  'podcast.episodes.noChapters': 'Aucun chapitre disponible.',
  'podcast.episodes.previewEpisodeTitle': 'Épisode',
  'podcast.episodes.showText': 'Afficher le texte',
  'qualiopi.actions': 'Actions',
  'qualiopi.completion': 'Conformité',
  'qualiopi.evidence': 'Documents justificatifs',
  'qualiopi.noDocuments': 'Aucun document',
  'qualiopi.notes': 'Notes',
  'qualiopi.notesPlaceholder': 'Ajouter une note…',
  'quickActions.hint': 'Press',
  'quickActions.hintToggle': 'to toggle',
  'quickActions.title': 'Quick Actions',
  'rh.achievements': 'Achievements',
  'rh.addComment': 'Add a comment...',
  'rh.addCompetency': 'Add Competency',
  'rh.afterApproval': 'After Approval',
  'rh.alert.forecast': 'Prévision fin d’année',
  'rh.alert.workflow': 'Workflows',
  'rh.all': 'All',
  'rh.allCompetencies': 'All Competencies',
  'rh.allDepartments': 'All Departments',
  'rh.allocated': 'Alloué',
  'rh.approvalHistory': 'Approval History',
  'rh.approve': 'Approve',
  'rh.approveConfirm': 'Are you sure you want to approve this training request?',
  'rh.approveRequest': 'Approve Request',
  'rh.approveSelected': 'Approve',
  'rh.approved': 'Approved',
  'rh.auditOverdue': 'Audit overdue',
  'rh.avgGap': 'Avg Gap Size',
  'rh.avgLevel': 'Avg Level',
  'rh.avgOverall': 'Avg Overall',
  'rh.budget.subtitle': 'Suivi budgétaire et ROI',
  'rh.budget.title': 'Budget formation',
  'rh.budgetAlerts': 'Alertes',
  'rh.budgetDashboard': 'Budget formation',
  'rh.budgetLoadError': 'Impossible de charger le budget.',
  'rh.budgetRemaining': 'Budget Remaining',
  'rh.byDepartment': 'Budget par direction',
  'rh.competencies': 'competencies',
  'rh.competencyMap': 'Competency Heatmap',
  'rh.competencyRadar': 'Competency Radar',
  'rh.completion': 'Completion',
  'rh.complianceCritical': 'Critical: Several criteria are below acceptable thresholds.',
  'rh.complianceGood': 'Your organization meets Qualiopi standards. Keep up the good work.',
  'rh.compliancePage.documentTitle': 'Compliance & Qualiopi - LyrraStudio',
  'rh.compliancePage.subtitle': 'Manage regulatory compliance and Qualiopi certification',
  'rh.compliancePage.tabs.dashboard': 'Dashboard',
  'rh.compliancePage.tabs.qualiopi': 'Qualiopi',
  'rh.compliancePage.title': 'Compliance & Qualiopi',
  'rh.complianceWarning': 'Some criteria need attention before the next audit.',
  'rh.compliant': 'compliant',
  'rh.confirm': 'Confirm Approval',
  'rh.confirmReject': 'Confirm Rejection',
  'rh.consumed': 'Consommé',
  'rh.costInfo': 'Cost Info',
  'rh.critical': 'critical',
  'rh.criticalGaps': 'Critical (High)',
  'rh.current': 'Current',
  'rh.currentLevel': 'Current Level',
  'rh.date': 'Date',
  'rh.daysRemaining': 'days remaining',
  'rh.department': 'Department',
  'rh.departmentBreakdown': 'Détail par direction',
  'rh.departments': 'departments',
  'rh.description': 'Description',
  'rh.edit': 'Edit',
  'rh.employee': 'Employee',
  'rh.employeeInfo': 'Employee Info',
  'rh.evolution': 'Evolution',
  'rh.evolutionTimeline': 'Evolution Timeline',
  'rh.exportReport': 'Exporter',
  'rh.gapAnalysis': 'Gap Analysis',
  'rh.growth': 'Growth this year',
  'rh.lastUpdated': 'Last updated',
  'rh.level': 'Level',
  'rh.manager': 'Manager',
  'rh.needsWork': 'needs work',
  'rh.nextAudit': 'Next Audit',
  'rh.noAlerts': 'Aucune alerte.',
  'rh.noAllocations': 'Aucune ligne budgétaire pour cette année.',
  'rh.noDeptData': 'Aucune donnée',
  'rh.noGaps': 'No gaps found for this filter.',
  'rh.of': 'of',
  'rh.ofTotal': 'du total',
  'rh.overallCompliance': 'Overall Compliance Score',
  'rh.page.documentTitle': 'Pilotage RH - LyrraStudio',
  'rh.pending': 'Pending',
  'rh.provider': 'Provider',
  'rh.qualiopiCompliance': 'Qualiopi Compliance',
  'rh.radar': 'Radar',
  'rh.reject': 'Reject',
  'rh.rejectConfirm': 'Are you sure you want to reject this training request?',
  'rh.rejectRequest': 'Reject Request',
  'rh.rejectSelected': 'Reject',
  'rh.rejected': 'Rejected',
  'rh.remaining': 'Restant',
  'rh.requestInfo': 'Request Info',
  'rh.requests.subtitle': 'Workflows de demandes et validations',
  'rh.requests.title': 'Demandes de formation',
  'rh.required': 'Required',
  'rh.rh': 'RH',
  'rh.saveAssessment': 'Save Assessment',
  'rh.searchCompetencies': 'Search competencies…',
  'rh.selfAssess': 'Self',
  'rh.showing': 'Showing',
  'rh.skillsSelfAssessment': 'Skills & Self-Assessment',
  'rh.spending': 'Dépenses',
  'rh.spendingTimeline': 'Dépenses par année',
  'rh.status': 'Status',
  'rh.targetLevel': 'Target Level',
  'rh.totalBudget': 'Budget total',
  'rh.totalGaps': 'Total Gaps',
  'rh.training': 'Training',
  'rh.trainingCost': 'Training Cost',
  'rh.trainingRequests': 'Training Requests',
  'search.close': 'to close',
  'search.input': 'Search input',
  'search.navigate': 'to navigate',
  'search.placeholder': 'Search pages, projects, flows...',
  'search.recent': 'Recent',
  'search.select': 'to select',
  'search.title': 'Search',
  'status.completed': 'Terminé',
  'status.inProgress': 'En cours',
  'status.notStarted': 'Non commencé',
  'store.noResults': 'Aucun résultat trouvé',
  'store.search': 'Rechercher...',
  'store.subtitle': 'Découvrez des formations, templates et ressources créés par la communauté',
  'store.title': 'Store',
  'support.chat.empty': 'Écrivez un message pour démarrer la conversation.',
  'support.chat.fromSupport': 'Support',
  'support.chat.fromYou': 'Vous',
  'support.chat.open': 'Ouvrir le chat support',
  'support.chat.placeholder': 'Votre message…',
  'support.chat.send': 'Envoyer',
  'support.chat.subtitle': 'Posez votre question : notre équipe vous répond ici.',
  'support.chat.title': 'Support',
  'webinar.room.chat': 'Chat',
  'webinar.room.closeParticipantCamerasView': 'Fermer',
  'webinar.room.hideParticipantCameras': 'Fermer la vue caméras',
  'webinar.room.hideParticipantsPanel': 'Réduire le panneau',
  'webinar.room.participantCamerasViewTitle': 'Caméras des participants',
  'webinar.room.participants': 'Participants',
  'webinar.room.showParticipantCameras': 'Voir les caméras des participants',
  'webinar.room.showParticipantsPanel': 'Afficher le panneau Participants',
  'webinar.room.sidePanelTabs': 'Participants et chat',
  'webinar.room.you': 'vous',
  // ── Auto-injected missing t() fallbacks ──
  'designer.ctx.addBlock': 'Ajouter un bloc',
  'designer.ctx.blockInfo': 'Info du bloc',
  'designer.ctx.changeColor': 'Changer la couleur',
  'designer.ctx.copy': 'Copier',
  'designer.ctx.delete': 'Supprimer',
  'designer.ctx.duplicate': 'Dupliquer',
  'designer.ctx.edit': 'Modifier',
  'designer.ctx.fitView': 'Ajuster la vue',
  'designer.ctx.lock': 'Verrouiller',
  'designer.ctx.paste': 'Coller',
  'designer.ctx.toggleGrid': 'Grille ON/OFF',
  'designer.ctx.toggleMinimap': 'Minimap ON/OFF',
  'designer.ctx.unlock': 'Déverrouiller',
  'designer.edge.cancel': 'Annuler',
  'designer.edge.color': 'Couleur',
  'designer.edge.name': 'Nom',
  'designer.edge.namePlaceholder': 'ex. Réussi, Branche A…',
  'designer.edge.save': 'Enregistrer',
  'designer.edge.title': 'Connexion',
  'designer.gamification.badgeName': 'Nom du badge',
  'designer.gamification.badges': 'Badges',
  'designer.gamification.blocksGte': 'Blocs ≥',
  'designer.gamification.bonusPerfect': 'Bonus score parfait (quiz)',
  'designer.gamification.condition': 'Condition',
  'designer.gamification.conditionPlaceholder': 'ex: quiz_completed >= 3',
  'designer.gamification.description': 'Description',
  'designer.gamification.enable': 'Activer la gamification',
  'designer.gamification.enableLeaderboard': 'Activer le classement',
  'designer.gamification.icon': 'Icône',
  'designer.gamification.leaderboard': 'Classement',
  'designer.gamification.missionDesc': 'Description (ex: Terminer 3 quiz)',
  'designer.gamification.missionTitle': 'Titre de la mission',
  'designer.gamification.missions': 'Missions',
  'designer.gamification.noBadges': 'Aucun badge défini',
  'designer.gamification.noMissions': 'Aucune mission définie',
  'designer.gamification.points': 'Points',
  'designer.gamification.pointsPerBlock': 'Points par bloc complété',
  'designer.gamification.pointsPerSecond': 'Points par seconde gagnée',
  'designer.gamification.publicLeaderboard': 'Classement public',
  'designer.gamification.reward': 'Récompense',
  'designer.gamification.scoreGte': 'Score ≥',
  'designer.gamification.timeBonus': 'Bonus de rapidité',
  'designer.gamification.title': 'Gamification',
  'designer.gamification.topN': 'Afficher le top N',
  'designer.objectives.add': 'Ajouter',
  'designer.objectives.addTitle': 'Ajouter un objectif',
  'designer.objectives.cancel': 'Annuler',
  'designer.objectives.category': 'Catégorie',
  'designer.objectives.completed': 'Complété',
  'designer.objectives.empty': 'Aucun objectif défini.',
  'designer.objectives.emptyHint': 'Ajoutez vos premiers objectifs pédagogiques.',
  'designer.objectives.errorToast': 'Erreur lors de la sauvegarde',
  'designer.objectives.level': 'Niveau (Bloom)',
  'designer.objectives.linkedBlocks': 'Blocs liés',
  'designer.objectives.noContentBlocks': 'Aucun bloc de contenu disponible.',
  'designer.objectives.required': 'Obligatoire',
  'designer.objectives.save': 'Sauvegarder',
  'designer.objectives.savedToast': 'Objectifs sauvegardés',
  'designer.objectives.scoreGte': 'Score ≥',
  'designer.objectives.title': 'Objectifs pédagogiques',
  'designer.objectives.update': 'Mettre à jour',
  'designer.validation.circularPath': 'Обнаружен циклический путь',
  'designer.validation.duplicateStart': 'Bloc Départ en doublon',
  'designer.validation.invalid': 'Invalide',
  'designer.validation.noEnd': 'Aucun bloc Fin trouvé. Il doit y en avoir au moins 1.',
  'designer.validation.noIssues': 'Aucun problème détecté',
  'designer.validation.noStart': 'Il doit y avoir exactement un bloc Départ (aucun trouvé).',
  'designer.validation.title': 'Validation',
  'designer.validation.untitled': 'Sans titre',
  'designer.validation.valid': 'Valide',
  'designer.validation.errorCount': '{{count}} ошибка(ок)',
  'designer.validation.warningCount': '{{count}} предупреждение(й)',
  'designer.validation.titleRequired': 'Блок « {{type}} » должен иметь заголовок.',
  'designer.validation.summary': 'Маршрут: {{blocks}} блок(ов), {{edges}} связей.',
  'designer.validation.multipleStart': 'Несколько блоков «Старт» ({{count}}): разрешён только один.',
  'designer.validation.noOutgoing': 'У блока « {{type}} » нет исходящей связи к следующему блоку.',
  'designer.validation.tooManyOutputs': '« {{label}} »: слишком много исходящих связей ({{count}}, максимум {{max}}).',
  'designer.validation.deadEndBranch': 'Ветка без пути к блоку «Конец» (от « {{label}} »).',
  'designer.validation.moreDeadEnds': '… и ещё {{count}} веток без выхода к концу.',
  'designer.validation.noConvergence': 'Ветки от « {{label}} » не сходятся к общему блоку.',
  'designer.validation.orphanBlock': 'Блок изолирован от основного графа: « {{title}} ».',
  'designer.validation.endHasOutgoing': 'У блока «Конец» « {{title}} » всё ещё есть исходящая связь.',
  'designer.validation.noScoring': 'Параметры оценки для « {{title}} » не заданы.',
  'designer.validation.emptyText': 'Текстовый блок « {{title}} » пуст.',
  'designer.validation.textTooLong':
    'Текстовый блок « {{title}} » оценен в {{lines}} строк. Рекомендуется: от {{min}} до {{max}} строк.',
  'designer.validation.splitTextBlock': 'Разделить',
  'designer.validation.textSplitNotNeeded':
    'Этот блок не превышает рекомендуемый порог чтения.',
  'designer.validation.textSplitUnable':
    'Не удалось предложить надёжное автоматическое разделение для этого блока.',
  'designer.validation.textSplitComplexFlow':
    'Автоматическое разделение недоступно для этого блока (множественные/условные ветки).',
  'designer.validation.textSplitDone': 'Создано текстовых блоков: {{count}}',
  'designer.validation.textSplitConfirmTitle':
    'Автоматически разделить этот текстовый блок?',
  'designer.validation.textSplitConfirmMessage':
    'Блок « {{title}} » оценен в {{lines}} строк. Мы разделим его на {{parts}} блоков (по знакам препинания) и разместим ниже в правильном порядке.',
  'designer.validation.textSplitConfirmAction': 'Разделить блок',
  'designer.variables.camelCaseError': 'Le nom doit être en camelCase (ex: maVariable)',
  'designer.variables.copy': 'Copier',
  'designer.variables.custom': 'Variables personnalisées',
  'designer.variables.defaultValue': 'Valeur par défaut',
  'designer.variables.howToTitle': 'Comment utiliser les variables :',
  'designer.variables.noCustom': 'Aucune variable personnalisée',
  'designer.variables.system': 'Variables système',
  'designer.variables.systemHint': 'Lecture seule — disponibles dans tous les blocs',
  'designer.variables.title': 'Variables',
  'designer.variables.type': 'Type',
  'voicePanel.title': 'Голос по умолчанию',
  'voicePanel.provider': 'Провайдер: Mistral AI',
  'voicePanel.hint': 'Выберите голос для озвучивания блоков.',
  'voicePanel.translationFrenchFallback':
    'Перевод аудио: если голос по умолчанию здесь не сохранён, для французского используется голос «Marie - sad» (Mistral).',
  'voicePanel.search': 'Поиск…',
  'voicePanel.allLanguages': 'Все языки',
  'voicePanel.loading': 'Загрузка голосов…',
  'voicePanel.loadError': 'Не удалось загрузить голоса',
  'voicePanel.empty': 'Голоса недоступны.',
  'voicePanel.emptyHint': 'Проверьте ключ API Mistral в Администрирование → Интеграции.',
  'voicePanel.noResults': 'Ни один голос не соответствует вашему запросу.',
  'voicePanel.saved': 'Голос по умолчанию сохранён',
  'voicePanel.saveError': 'Ошибка сохранения',
  'voicePanel.default': 'По умолчанию',
  'voicePanel.female': 'Женский',
  'voicePanel.male': 'Мужской',
  'voicePanel.playPreview': 'Прослушать голос',
  'voicePanel.stopPreview': 'Остановить',
  'voicePanel.noPreview': 'Предварительный просмотр недоступен',
  'voicePanel.previewLoading': 'Создание…',
  'voicePanel.previewGenerateError': 'Не удалось создать аудиопредпросмотр.',
  'voicePanel.previewPlayError': 'Не удалось воспроизвести аудио.',
  'voicePanel.filterNoVoicesForLang':
    'В каталоге нет голоса для этого языка — попробуйте «Все языки».',
  'episodeEditor.voicePicker.blockVoice': 'Voice for this block',
  'episodeEditor.voicePicker.defaultVoice': 'Default voice',
  'episodeEditor.voicePicker.voices': 'voices',
  'episodeEditor.voicePicker.searchPlaceholder': 'Search a voice…',
  'episodeEditor.voicePicker.noVoiceFound': 'No voice found',
  'episodeEditor.voicePicker.female': 'Female',
  'episodeEditor.voicePicker.male': 'Male',
  'episodeEditor.voicePicker.stop': 'Stop',
  'episodeEditor.voicePicker.preview': 'Preview voice',
  'episodeEditor.voicePicker.stopPlayback': 'Stop playback',
  'episodeEditor.errors.notFound': 'Episode not found',
  'episodeEditor.errors.titleRequired': 'Title is required.',
  'episodeEditor.errors.saveFailed': 'Error saving.',
  'episodeEditor.errors.noVoicesAvailable': 'No voices available. Enter the API key in Administration → Platform integrations, then reload the page.',
  'episodeEditor.errors.selectVoiceFirst': 'Select a voice in the "Voice" tab before generating voiceover.',
  'episodeEditor.errors.invalidBlockVoice': 'One or more blocks have an invalid voice. Open the per-block voice selector or reload the voice list.',
  'episodeEditor.errors.addTextFirst': 'Add text before generating audio.',
  'episodeEditor.errors.audioGenerationFailedApi': 'Audio generation failed. {{message}} (displayed duration = estimate.)',
  'episodeEditor.errors.audioGenerationFailed': 'Error during audio generation. Displayed duration = estimate.',
  'episodeEditor.success.published': 'Episode published!',
  'episodeEditor.success.draftSaved': 'Draft saved',
  'episodeEditor.success.audioGenerated': 'Audio generated with {{count}} block(s)',
  'episodeEditor.tabs.edit': 'Edit',
  'episodeEditor.tabs.chapters': 'Chapters',
  'episodeEditor.tabs.voice': 'Voice',
  'episodeEditor.tabs.podcasts': 'Podcasts',
  'episodeEditor.toolbar.undo': 'Undo (Ctrl+Z)',
  'episodeEditor.toolbar.redo': 'Redo (Ctrl+Y)',
  'episodeEditor.toolbar.generating': 'Generating…',
  'episodeEditor.toolbar.generateAudio': 'Generate audio',
  'episodeEditor.toolbar.save': 'Save',
  'episodeEditor.toolbar.publish': 'Publish',
  'episodeEditor.titlePlaceholder': 'Episode title…',
  'episodeEditor.status.published': 'Published',
  'episodeEditor.status.draft': 'Draft',
  'episodeEditor.warnings.mistralNotConfigured': 'Episode voiceover uses Mistral Voxtral TTS. Add the Mistral key in Administration → Platform integrations, then reload this page.',
  'episodeEditor.warnings.mistralNoVoices': 'The Mistral key is recognized but the API returned no voices. Check backend logs.',
  'episodeEditor.warnings.voiceLoadFailed': 'Unable to load voice list. Check Mistral configuration in Administration → Platform integrations.',
  'episodeEditor.warnings.voiceOffDisabled': 'Without a real voice, voiceover generation remains disabled.',
  'episodeEditor.edit.voiceOff': 'Voiceover',
  'episodeEditor.edit.voiceDescription': 'Synthesis via Mistral Voxtral TTS. Choose the voice in the "Voice" tab or per block.',
  'episodeEditor.edit.coverImage': 'Cover image',
  'episodeEditor.edit.coverAlt': 'Episode cover',
  'episodeEditor.edit.deleteCover': 'Delete',
  'episodeEditor.edit.dropOrClickImage': 'Click or drop an image',
  'episodeEditor.edit.selectedVoice': 'Selected voice',
  'episodeEditor.edit.selectVoice': 'Select a voice',
  'episodeEditor.edit.defaultVoiceByLang': 'Default voice (podcast language)',
  'episodeEditor.edit.lastAudioGenerated': 'Last generated audio',
  'episodeEditor.edit.audioNotFound': 'Audio file not found — regenerate the voiceover.',
  'episodeEditor.edit.generatingInProgress': 'Generating…',
  'episodeEditor.edit.generateVoiceOff': 'Generate voiceover',
  'episodeEditor.chapters.title': 'Chapters',
  'episodeEditor.chapters.add': 'Add',
  'episodeEditor.chapters.defaultChapterTitle': 'Introduction',
  'episodeEditor.chapters.newChapterTitle': 'Chapter {{n}}',
  'episodeEditor.chapters.blockCount': '{{count}} block(s)',
  'episodeEditor.chapters.readByAi': 'Read chapter title',
  'episodeEditor.chapters.readByAiHint':
    'When off, the AI does not read this chapter title aloud. The chapter blocks stay included in the voiceover.',
  'episodeEditor.voiceTab.availableVoices': 'Available voices',
  'episodeEditor.podcasts.linked': 'Linked podcasts',
  'episodeEditor.podcasts.assign': 'Assign',
  'episodeEditor.podcasts.noneAvailable': 'No podcast available',
  'episodeEditor.podcasts.close': 'Close',
  'episodeEditor.podcasts.noneLinked': 'No linked podcast',
  'episodeEditor.podcasts.assignHint': 'Assign this episode to one or more podcasts',
  'episodeEditor.podcasts.remove': 'Remove',
  'episodeEditor.editor.addTextBlock': 'Add a text block',
  'episodeEditor.editor.scriptPlaceholder': 'Start writing your script…',
  'episodeEditor.editor.addBlock': 'Add a block',
  'episodeEditor.player.loop': 'Loop',
  'episodeEditor.player.playbackSpeed': 'Playback speed',
  'episodeEditor.player.collapse': 'Collapse',
  'episodeEditor.player.expand': 'Expand',
  'episodeEditor.player.waveformHidden': 'Waveform hidden',
  'podcast.episodes.listen': 'Listen',
  'podcast.episodes.noAudioShort': 'No audio available',
  'podcast.episodes.edit': 'Edit',
  'podcast.episodes.delete': 'Delete',
  'podcast.episodes.unmute': 'Unmute',
  'podcast.episodes.mute': 'Mute',
  'podcast.episodes.totalEpisodes': '{{count}} episode(s) total',
  'eduflow.designer.toast.blockCopied': 'Блок скопирован',
  'eduflow.designer.toast.blockPasted': 'Блок вставлен',
  'eduflow.designer.toast.blockDuplicated': 'Блок продублирован',
  'eduflow.designer.toast.connectionUpdated': 'Соединение обновлено',
  'eduflow.designer.toast.flowExported': 'Курс экспортирован',
  'eduflow.designer.toast.flowImported': 'Курс импортирован',
  'eduflow.designer.toast.invalidFile': 'Недопустимый файл',
  'eduflow.designer.untitled': 'Без названия',
  'eduflow.designer.importedFlow': 'Импортированный курс',
  'eduflow.designer.collaborator.online': 'Онлайн',
  'eduflow.designer.collaborator.offline': 'Офлайн',
  'eduflow.designer.collaborator.defaultName': 'Пользователь',
  'eduflow.designer.collaborator.editor': 'Редактор',
  'eduflow.designer.collaborator.viewer': 'Просмотрщик',
  'eduflow.designer.resizePanel': 'Изменить размер панели',
  'eduflow.designer.published': 'Опубликовано',
  'eduflow.designer.draft': 'Черновик',
  'eduflow.designer.quizOutput.success': 'Пройдено',
  'eduflow.designer.quizOutput.failure': 'Не пройдено',
  'eduflow.designer.handle.default': 'По умолчанию',
  'eduflow.designer.genError': 'Ошибка при генерации. Попробуйте ещё раз.',
  'trainerTour.welcome.title': 'Добро пожаловать в пространство преподавателя',
  'trainerTour.welcome.description':
    'Этот короткий тур показывает панель и меню. При первом открытии каждой страницы появляется подсказка с главным. Закройте шаг крестиком — тур не запустится снова.',
  'trainerTour.dashboard.header.title': 'Ваша панель',
  'trainerTour.dashboard.header.description':
    'Обзор активности: ключевые показатели и быстрые действия для создания контента.',
  'trainerTour.dashboard.actions.title': 'Создать контент',
  'trainerTour.dashboard.actions.description':
    'Ярлыки для нового курса EduFlow, подкаста или траектории. Начните проект без меню.',
  'trainerTour.dashboard.stats.title': 'Показатели',
  'trainerTour.dashboard.stats.description':
    'Курсы, опубликованные траектории, учащиеся и подкасты — масштаб каталога с первого взгляда.',
  'trainerTour.dashboard.activity.title': 'Недавняя активность',
  'trainerTour.dashboard.activity.description':
    'Недавно изменённый контент. Щёлкните строку, чтобы снова открыть курс, подкаст или траекторию.',
  'trainerTour.sidebar.title': 'Боковое меню',
  'trainerTour.sidebar.description':
    'Обзор (главная, учащиеся, аналитика, задания), создание (курсы, подкасты, траектории, вебинары если включены), медиатека и справка. Откройте каждый пункт — при первом посещении появится контекстная подсказка.',
  'trainerTour.routes.learners.title': 'Учащиеся',
  'trainerTour.routes.learners.description':
    'Записанные на ваш контент, прогресс, выгрузки — центр педагогического сопровождения.',
  'trainerTour.routes.analytics.title': 'Аналитика',
  'trainerTour.routes.analytics.description':
    'Диаграммы вовлечённости и завершения. Настройте вид под свои приоритеты.',
  'trainerTour.routes.eduflow.title': 'Курсы (EduFlow)',
  'trainerTour.routes.eduflow.description':
    'Создание и управление интерактивными курсами, версиями, публикацией и доступом — ядро учебного дизайна.',
  'trainerTour.routes.podcasts.title': 'Подкасты',
  'trainerTour.routes.podcasts.description':
    'Шоу, эпизоды, видимость и доступ для групп или учащихся вашей организации.',
  'trainerTour.routes.parcours.title': 'Траектории',
  'trainerTour.routes.parcours.description':
    'Цепочки курсов и ресурсов: длинные учебные пути и назначение аудиториям.',
  'trainerTour.routes.videos.title': 'Видео',
  'trainerTour.routes.videos.description':
    'Библиотека видео для повторного использования в курсах и документах.',
  'trainerTour.routes.sounds.title': 'Аудио',
  'trainerTour.routes.sounds.description':
    'Аудиофайлы и теги для подкастов и звуковых блоков в курсах.',
  'trainerTour.routes.images.title': 'Изображения',
  'trainerTour.routes.images.description':
    'Централизованные изображения: импорт, папки и повторное использование.',
  'trainerTour.routes.documents.title': 'Документы',
  'trainerTour.routes.documents.description':
    'PDF и вложения, при необходимости с целевой выдачей учащимся.',
  'trainerTour.routes.help.title': 'Справка',
  'trainerTour.routes.help.description':
    'Статьи, руководства и материалы по LyrraStudio. Возвращайтесь, если что-то непонятно.',
  'trainerTour.routes.producerAssignments.title': 'Производственные миссии',
  'trainerTour.routes.producerAssignments.description':
    'Когда HR назначает производство позиций каталога, они отображаются здесь со статусом.',
  'trainerTour.routes.webinars.title': 'Вебинары',
  'trainerTour.routes.webinars.description':
    'Планируйте live-сессии, приглашения и ссылки на трансляцию для ваших групп.',
  'trainerTour.driver.progress': '{{current}} / {{total}}',
  'trainerTour.driver.next': 'Далее',
  'trainerTour.driver.prev': 'Назад',
  'trainerTour.driver.done': 'Готово',
  'trainerTour.driver.gotIt': 'Понятно',
  'pages.learners.title': 'Учащиеся',
  'pages.learners.description': 'Отслеживайте прогресс ваших учащихся',
  'pages.learners.enrolledFormations': 'Записанные курсы',
  'pages.learners.notStarted': 'Не начато',
  'pages.learners.startedOn': 'Начало {{date}}',
  'pages.learners.detailedTraces': 'Подробная активность · {{flowTitle}}',
  'pages.learners.statsEvents': 'События: {{count}}',
  'pages.learners.statsResponses': 'Ответы: {{count}}',
  'pages.learners.statsBlockSessions': 'Сессии блоков: {{count}}',
  'pages.learners.timePerBlock': 'Время по блоку',
  'pages.learners.lastEvents': 'Последние события',
  'pages.learners.blockSessionInProgress': '(в процессе)',
  'pages.learners.blockTimeOnBlock': '({{duration}} на этом блоке)',
  'pages.learners.trackingEvent.unknown': 'Событие',
  'pages.learners.trackingEvent.training_enter': 'Курс открыт',
  'pages.learners.trackingEvent.training_exit': 'Курс закрыт или покинут',
  'pages.learners.trackingEvent.training_completed': 'Курс завершён',
  'pages.learners.trackingEvent.block_enter': 'Вход в блок',
  'pages.learners.trackingEvent.block_exit': 'Выход из блока',
  'pages.learners.trackingEvent.response_submitted': 'Ответ отправлен',
  'pages.learners.trackingEvent.progress_sync': 'Прогресс синхронизирован',
  'pages.learners.refresh': 'Обновить',
  'pages.learners.exportCsv': 'Экспорт CSV',
  'pages.learners.statTotal': 'Всего учащихся',
  'pages.learners.statInProgress': 'В процессе',
  'pages.learners.statCompleted': 'Завершено',
  'pages.learners.statAvgProgress': 'Сред. прогресс',
  'pages.learners.searchPlaceholder': 'Поиск по имени или email…',
  'pages.learners.filterAllFlows': 'Все курсы',
  'pages.learners.sortRecent': 'Сначала новые',
  'pages.learners.sortName': 'Имя А–Я',
  'pages.learners.sortProgress': 'Прогресс',
  'pages.learners.colLearner': 'Учащийся',
  'pages.learners.colFormations': 'Курсы',
  'pages.learners.colProgress': 'Прогресс',
  'pages.learners.colCompleted': 'Завершено',
  'pages.learners.colTime': 'Время',
  'pages.learners.colLastAccess': 'Последний доступ',
  'pages.learners.colActions': 'Действия',
  'pages.learners.openProgressDashboard': 'Панель прогресса',
  'pages.learners.dashboardTitle': 'Прогресс учащегося',
  'pages.learners.dashboardBack': 'Назад к списку',
  'pages.learners.dashboardInvalidId': 'Недействительный идентификатор учащегося',
  'pages.learners.dashboardNotFound': 'Учащийся не найден или доступ запрещён',
  'pages.learners.dashboardOverviewHint':
    'Сводный вид: курсы EduFlow участника, траектории и подкасты, назначенные учётной записи Lyrra с тем же email.',
  'pages.learners.tabOverview': 'Обзор',
  'pages.learners.tabFormations': 'Курсы',
  'pages.learners.tabParcours': 'Траектории',
  'pages.learners.tabPodcasts': 'Подкасты',
  'pages.learners.sectionFormations': 'Курсы',
  'pages.learners.sectionParcours': 'Траектории',
  'pages.learners.sectionPodcasts': 'Подкасты',
  'pages.learners.emptyParcours': 'Нет траектории, назначенной учётной записи Lyrra с этим email.',
  'pages.learners.emptyPodcasts': 'Нет подкаста, назначенного учётной записи Lyrra с этим email.',
  'pages.learners.linkedAccountHint':
    'Траектории и подкасты отображаются, если есть пользователь Lyrra с тем же email, что у участника.',
  'pages.learners.kpiEduflows': 'Курсы',
  'pages.learners.kpiAvgProgress': 'Сред. прогресс',
  'pages.learners.kpiPaths': 'Траектории',
  'pages.learners.kpiPodcasts': 'Подкасты',
  'pages.learners.kpiCompletedFormations': 'Завершено: {{count}}',
  'pages.learners.pathProgressLabel': '{{done}} / {{total}} активностей',
  'pages.learners.pathAssignedOn': 'Добавлено {{date}}',
  'pages.learners.podcastCompleted': 'Завершено',
  'pages.learners.podcastInProgress': 'В процессе',
  'pages.learners.pathStatusPublished': 'Опубликовано',
  'pages.learners.pathStatusDraft': 'Черновик',
  'pages.learners.openInDesigner': 'Открыть в редакторе',
  'pages.learners.openParcoursEditor': 'Открыть траекторию',
  'pages.learners.viewPodcasts': 'К подкастам',
  'pages.learners.loading': 'Загрузка учащихся…',
  'pages.learners.loadError': 'Не удалось загрузить учащихся',
  'pages.learners.retry': 'Повторить',
  'pages.learners.emptyFiltered': 'Учащиеся не найдены',
  'pages.learners.emptyFilteredHint': 'Измените условия поиска или фильтры',
  'pages.learners.emptyNone': 'Нет записанных учащихся',
  'pages.learners.emptyNoneHint':
    'Учащиеся появятся здесь, когда запишутся на ваши опубликованные курсы',
  'pages.learners.pagination': 'Страница {{page}} из {{totalPages}} · {{total}} учащ.',
  'pages.learners.prev': 'Назад',
  'pages.learners.next': 'Далее',
  'pages.learners.csvLastName': 'Фамилия',
  'pages.learners.csvFirstName': 'Имя',
  'pages.learners.csvEmail': 'Email',
  'pages.learners.csvFormations': 'Курсы',
  'pages.learners.csvAvgProgress': 'Средний прогресс',
  'pages.learners.csvTotalTime': 'Общее время',
  'pages.learners.csvLastActivity': 'Последняя активность',
  'pages.learners.statsAdaptiveChoice':
    'Подсказки — принято: {{accepted}} · отклонено: {{declined}}',
  'pages.learners.statsAdaptiveEvaluated': 'Оценок адаптации: {{count}}',
  'pages.learners.statsAdaptiveReplaced': 'Использовано упрощённых версий: {{count}}',
  'player.lightMode': 'Светлая тема',
  'player.darkMode': 'Тёмная тема',
};

const SUPPORTED_LNGS = ['fr', 'en', 'es', 'de', 'it', 'pt', 'lb', 'uk', 'ru', 'ar', 'tr'] as const;

function readStoredLanguageCode(): string {
  if (typeof window === 'undefined') return 'en';
  const code = resolveInitialLanguage();
  return (SUPPORTED_LNGS as readonly string[]).includes(code) ? code : 'en';
}

function persistLanguageChange(lng: string): void {
  const code = lng.split('-')[0];
  if (!(SUPPORTED_LNGS as readonly string[]).includes(code)) return;
  try {
    localStorage.setItem('locale', code);
    localStorage.setItem('app_language', code);
    applyDocumentLocale(code);
    useLanguageStore.setState({ language: code as Language });
  } catch {
    /* ignore quota / private mode */
  }
}

i18n.use(initReactI18next).init({
  resources: {
    fr: {
      translation: withInstitution(
        { ...frTranslations, ...playerVisualParallelLocales.fr },
        'fr',
      ),
    },
    en: {
      translation: withInstitution(
        { ...enTranslations, ...playerVisualParallelLocales.en },
        'en',
      ),
    },
    es: {
      translation: withInstitution(
        withEnglishFallback({
          ...esTranslations,
          ...esGapTranslations,
          ...playerVisualParallelLocales.es,
        }),
        'es',
      ),
    },
    de: {
      translation: withInstitution(
        withEnglishFallback({
          ...deTranslations,
          ...deGapTranslations,
          ...playerVisualParallelLocales.de,
        }),
        'de',
      ),
    },
    it: {
      translation: withInstitution(
        withEnglishFallback({
          ...itTranslations,
          ...itGapTranslations,
          ...playerVisualParallelLocales.it,
        }),
        'it',
      ),
    },
    pt: {
      translation: withInstitution(
        withEnglishFallback({
          ...ptTranslations,
          ...ptGapTranslations,
          ...playerVisualParallelLocales.pt,
        }),
        'pt',
      ),
    },
    lb: {
      translation: withInstitution(
        withEnglishFallback({
          ...lbTranslations,
          ...lbGapTranslations,
          ...playerVisualParallelLocales.lb,
        }),
        'lb',
      ),
    },
    uk: {
      translation: withInstitution(
        withEnglishFallback({
          ...ukTranslations,
          ...ukGapTranslations,
          ...playerVisualParallelLocales.uk,
        }),
        'uk',
      ),
    },
    ru: {
      translation: withInstitution(
        withEnglishFallback({
          ...ruTranslations,
          ...ruGapTranslations,
          ...playerVisualParallelLocales.ru,
        }),
        'ru',
      ),
    },
    ar: {
      translation: withInstitution(
        withEnglishFallback({
          ...arTranslations,
          ...arHelpTranslations,
          ...arPlayerTranslations,
          ...arGapTranslations,
          ...playerVisualParallelLocales.ar,
        }),
        'ar',
      ),
    },
    tr: {
      translation: withInstitution(
        withEnglishFallback({
          ...trTranslations,
          ...trGapTranslations,
          ...playerVisualParallelLocales.tr,
        }),
        'tr',
      ),
    },
  },
  lng: readStoredLanguageCode(),
  fallbackLng: {
    ar: ['en'],
    default: ['en'],
  },
  supportedLngs: [...SUPPORTED_LNGS],
  interpolation: {
    escapeValue: false,
  },
  react: {
    useSuspense: false,
  },
});

i18n.on('languageChanged', (lng) => {
  persistLanguageChange(lng);
});

if (typeof document !== 'undefined') {
  applyDocumentLocale(i18n.language);
}

if (typeof window !== 'undefined') {
  (window as unknown as { __i18n: typeof i18n }).__i18n = i18n;
  /** Aligne i18next sur la même résolution que le store (évite un premier rendu FR puis AR). */
  queueMicrotask(() => {
    try {
      const want = resolveInitialLanguage();
      const have = (i18n.language || '').split('-')[0].toLowerCase();
      if (want && have !== want && (SUPPORTED_LNGS as readonly string[]).includes(want)) {
        void i18n.changeLanguage(want);
      }
    } catch {
      /* noop */
    }
  });
}

export default i18n;
